import React, { useState, useEffect, useRef, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";
import {
  buildScanLedgerEntry,
  mergeScanLedgerEntry,
  findPreviousLedgerEntry,
  detectChangedIndicators,
  buildSignalChangeEntry,
  appendSignalChange,
  processActiveSignals,
  buildActiveSignalDisplay,
  ledgerChangeFlag,
  summarizeTickerLedger,
  entryTimestamp as scanLedgerEntryTimestamp,
} from "./scanLedger.js";
import {
  SECTOR_DASHBOARD_ETFS,
  SECTOR_TICKER_PLAYS,
  SECTOR_CACHE_MS,
  STAGE_DISPLAY,
  STAGE_KEYS,
  buildSectorEtfMetrics,
  detectRotationStage,
  sortSectorsByStage,
  buildRotationAlerts,
  buildSectorTradeRecommendation,
  attachSectorContextToResult,
  getSectorRotationActionSuffix,
  getSectorRotationHeadwindWarning,
  formatStageAlertMessage,
  pickSectorAlertTickers,
  buildRotationScanBannerMessage,
  formatSectorAlertStageLabel,
  readLastScanResultsCache,
  SECTOR_EMOJI_BY_ETF,
  getTickerRotationSectorInfo,
} from "./sectorRotation.js";
import {
  computeMeasurementScoreBonus,
  emaAlignmentDisplay,
  consolidationActionLabel,
  consolidationSignalBadge,
} from "./scanMeasurements.js";
import { computeFibRetracementScore } from "./fibRetracementScore.js";
import { computeContextAwareRsiDivergence, calcRsiMidlineScore } from "./rsiScoring.js";
import {
  rsiDisplayColor,
  formatRsiWithDivergence,
  rsiDisplayTooltip,
  rsiWatchlistSubline,
  rsiCellTooltipExtended,
  RSI_COLUMN_HEADER_TIP,
} from "./rsiDisplay.js";
import { AnalysisTabPanel, SignalTimelinePanel } from "./analysisTabUI.jsx";
import { SignalGuidePanel } from "./signalGuideUI.jsx";
import {
  SCORE_TIP,
  CMF_TIP,
  RS_SPY_TIP,
  ACTION_COLUMN_TIP,
  CONFLUENCE_TIPS,
  signalVerdictTooltip,
  confluenceCellTooltip,
  confluenceSignalBridge,
  BB_TIGHT_BANDS_LABEL,
  BB_TIGHT_BANDS_SHORT,
  BB_TIGHT_BANDS_TIP,
} from "./signalTooltips.js";
import { vixImpliedDailyMove } from "./vixExpectedMove.js";
import { BriefingParagraph, TodaysPrimeSetupsBlock } from "./briefingTextUI.jsx";
import {
  resolveScannerTickerList,
  getScannerThemeOptions,
  symbolsNeedingRescan,
  buildScannerCoreTickers,
} from "./scannerUniverse.js";
import {
  pickTopScannerPicks,
  buildSignalOfDayFromPick,
  scannerDisplayVerdict,
} from "./scannerFilters.js";
import { evaluateConfluence, watchlistConfluenceCell } from "./confluenceScore.js";
import {
  attachPrimeSetup,
  PRIME_STAR_TOOLTIP,
  getPrimeWatchlistActionText,
} from "./primeSetupFlag.js";
import { calcGate, assertGateScoreConsistency } from "./gateCalc.js";
import {
  computeCoreBuyScoreV2,
  applyAddonCap,
  clampScore,
} from "./scoringV2.js";
import { computeTradeLevelsV2 } from "./targetsV2.js";
import { evaluateOptionsChecks } from "./optionsChecks.js";
import {
  attachScannerAction,
  isScoringV2Result,
  normalizeScanResult,
  v2WatchlistActionShort,
} from "./resolveScannerAction.js";
import { attachScanLanes } from "./scanLanes.js";
import {
  buildMarketContext,
  computeBreadthPct20,
  marketContextBanners,
} from "./marketContext.js";
import { LongTermScannerTable, MarketContextBanners } from "./longTermScannerUI.jsx";
import { TickerMaChart } from "./tickerMaChart.jsx";
import { MaStackPills } from "./maStackUI.jsx";
import { runAnalysisV2 } from "./runAnalysisV2.js";
import {
  SignalSystemBanner,
  SignalReasonsSection,
  DrawerOverviewStack,
  PriceStorySection,
  OrderFlowSummary,
  Module4RecommendationCard,
  OptionsSignalPanel,
  formatModuleSignalLabel,
  getWatchlistActionUi,
} from "./signalSystemUI.jsx";
import {
  DEFAULT_SCANNER_FILTERS,
  SECTOR_SCAN_PRESET,
  ScannerFilterPanel,
  ScannerCompactFilterBar,
  ScannerResultsTable,
  ScannerResultsBar,
  applyQuickFilter,
  matchesScannerFilters,
  countBySignalType,
  sortScannerResults,
  useScannerCompanyNames,
  getScannerDisplayBadge,
  getSetupExplanationLine,
  getScannerGateCell,
  EarningsRowPill,
  YesterdayMoversPanel,
} from "./marketScannerUI.jsx";
import { getEarningsDrawerLine, getEarningsRowPill, earningsContextForAi, getEarningsDays } from "./earningsDisplay.js";
import {
  computeMoveMetrics,
  generatePostMoveAssessment,
  getWatchlistAssessmentPill,
  PostMoveAssessmentPanel,
  refreshPostMoveAssessment,
} from "./postMoveAssessment.jsx";
import { resolveDisplayVerdict, displayVerdictChipStyle } from "./resolveDisplayVerdict.js";
import { resetAppShellCache } from "./registerSW.js";

/** Bump when verifying deploy — log in console: [Auctus] client build … */
export const AUCTUS_CLIENT_BUILD = "20260329-ai-fix-4";

// ─── SUPABASE ─────────────────────────────────────────────────────────────────
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || "",
  process.env.REACT_APP_SUPABASE_ANON_KEY || "",
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: window.localStorage,
    }
  }
);
let _uid = null; // current user id — updated on auth state change
const WATCHLIST_BACKUP_KEY = "auctus_watchlist_backup";
let _watchlistLoadedFromSupabase = false;
let _vixData = null;       // current VIX snapshot — updated by fetchVIX, read by calcVerdict
/** Flip on when Polygon IV Rank / CSP columns have live data. */
const POLYGON_OPTIONS_LIVE = false;
let _sectorETFData = {};  // cached sector ETF prices { XLK: price, XLE: price, ... }

// Sector mapping: ticker → sector ETF
const TICKER_SECTOR_MAP = {
  // Tech
  AAPL:"XLK",MSFT:"XLK",NVDA:"XLK",AMD:"XLK",INTC:"XLK",AVGO:"XLK",QCOM:"XLK",TXN:"XLK",
  KLAC:"XLK",LRCX:"XLK",AMAT:"XLK",MRVL:"XLK",SNPS:"XLK",CDNS:"XLK",MPWR:"XLK",ON:"XLK",
  SMCI:"XLK",AMKR:"XLK",ONTO:"XLK",SOXX:"XLK",SMH:"XLK",
  // Mega cap tech / internet
  GOOGL:"XLC",GOOG:"XLC",META:"XLC",NFLX:"XLC",DIS:"XLC",WBD:"XLC",NYT:"XLC",
  // E-commerce / consumer internet  
  AMZN:"XLY",TSLA:"XLY",BURL:"XLY",TJX:"XLY",NKE:"XLY",COST:"XLY",WMT:"XLY",TGT:"XLY",
  LOW:"XLY",HD:"XLY",CMG:"XLY",SBUX:"XLY",MCD:"XLY",DPZ:"XLY",
  // Software/SaaS
  TWLO:"IGV",PLTR:"IGV",DDOG:"IGV",SNOW:"IGV",OKTA:"IGV",NET:"IGV",HUBS:"IGV",
  BILL:"IGV",PAYC:"IGV",PCTY:"IGV",CDNS:"IGV",
  // Fintech / payments
  COIN:"XLF",PYPL:"XLF",MSTR:"XLF",GBTC:"XLF",
  // Financials
  JPM:"XLF",BAC:"XLF",MS:"XLF",GS:"XLF",C:"XLF",WFC:"XLF",SCHW:"XLF",
  BX:"XLF",KKR:"XLF",ARES:"XLF",APO:"XLF",BEN:"XLF",
  // Healthcare
  JNJ:"XLV",LLY:"XLV",AMGN:"XLV",REGN:"XLV",GILD:"XLV",DHR:"XLV",TMO:"XLV",
  BSX:"XLV",MDT:"XLV",SYK:"XLV",ISRG:"XLV",DXCM:"XLV",PODD:"XLV",
  // Energy
  XOM:"XLE",CVX:"XLE",SLB:"XLE",KMI:"XLE",WMB:"XLE",ET:"XLE",OXY:"XLE",
  COP:"XLE",DVN:"XLE",HAL:"XLE",BKR:"XLE",APA:"XLE",EOG:"XLE",MPC:"XLE",
  PSX:"XLE",FANG:"XLE",RRC:"XLE",EQT:"XLE",
  // Industrials
  DE:"XLI",GD:"XLI",LHX:"XLI",AXON:"XLI",CACI:"XLI",TT:"XLI",BWXT:"XLI",
  // Real estate
  EQIX:"XLRE",COIN:"XLRE",BURL:"XLRE",
  // Crypto/Mining
  MARA:"XLK",CORZ:"XLK",HUT:"XLK",IREN:"XLK",BTBT:"XLK",CLSK:"XLK",
};
const SECTOR_ETFS = [...new Set(Object.values(TICKER_SECTOR_MAP))];
let _vixHistory = [];     // last 5 readings for trend detection — rising/falling/flat
let _btcData = null;       // current BTC price/trend — updated by fetchBTC, used for crypto RS signal
let _fearGreedData = null; // current Fear & Greed score — updated by fetchFearGreed, used for crypto flags

// ─── ELECTRON BRIDGE ──────────────────────────────────────────────────────────
const IS_ELECTRON = typeof window !== "undefined" && !!window.electronAPI;
const eAPI = IS_ELECTRON ? window.electronAPI : null;

// ─── STORAGE — Supabase when logged in, localStorage fallback ─────────────────
// ─── STORAGE — API route when logged in, localStorage fallback ────────────────
function getAuthToken() {
  try {
    const raw = localStorage.getItem('sb-sqnxzmzxenniyljjtbaa-auth-token');
    return raw ? JSON.parse(raw)?.access_token : null;
  } catch { return null; }
}

/** Full scan payloads (OHLC arrays) exceed Vercel/Supabase limits — keep on device only. */
const CLOUD_SYNC_SKIP_KEYS = new Set([
  "auctus_last_scanner_results",
  "scannerResults",
  "lastScanResults",
  "cryptoScanCoins",
  "cryptoScanEquities",
]);
const CLOUD_SYNC_MAX_BYTES = 350_000;

async function load(key) {
  if (CLOUD_SYNC_SKIP_KEYS.has(key)) {
    try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
  }
  try {
    let token = null;
    try {
      const { data: { session } } = await supabase.auth.getSession();
      token = session?.access_token ?? null;
    } catch {}
    if (!token) token = getAuthToken();
    if (token) {
      const res = await fetch(`/api/userdata?key=${encodeURIComponent(key)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.value != null) {
          try { localStorage.setItem(key, JSON.stringify(data.value)); } catch {}
          return data.value;
        }
      }
    }
  } catch {}
  // Fallback to localStorage
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : null; } catch { return null; }
}

async function save(key, val) {
  // Always write to localStorage first
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
  try {
    const token = getAuthToken();
    if (!token) return;
    if (CLOUD_SYNC_SKIP_KEYS.has(key)) return;
    if (key === "watchlist" && !_watchlistLoadedFromSupabase && (!val || !Array.isArray(val) || val.length === 0)) {
      return;
    }
    const body = JSON.stringify({ key, value: val });
    if (body.length > CLOUD_SYNC_MAX_BYTES) {
      console.warn(`[save] skipping cloud sync — ${key} is ${(body.length / 1024).toFixed(0)}KB (max ${CLOUD_SYNC_MAX_BYTES / 1024}KB). Data stays in localStorage.`);
      return;
    }
    const res = await fetch('/api/userdata', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = err.error || res.statusText || `HTTP ${res.status}`;
      console.error('[save] API error:', key, msg);
    } else if (key === "watchlist") {
      try { localStorage.setItem(WATCHLIST_BACKUP_KEY, JSON.stringify(val)); } catch {}
    }
  } catch (e) { console.error('[save] exception:', key, e); }
}

const AI_TICKER_CACHE_PREFIXES = ["aiSummary_", "aiTriggers_", "aiVerdict_", "auctus_aiSummary_"];

async function clearTickerAiCache(symbol) {
  const sym = String(symbol || "").toUpperCase();
  if (!sym) return;
  for (const prefix of AI_TICKER_CACHE_PREFIXES) {
    const key = `${prefix}${sym}`;
    try { localStorage.removeItem(key); } catch {}
    try {
      const token = getAuthToken();
      if (token) {
        await fetch("/api/userdata", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ key, value: null }),
        });
      }
    } catch {}
  }
}

function safeDisplayVerdict(result, opts) {
  try {
    return resolveDisplayVerdict(result, opts);
  } catch (e) {
    console.warn("[safeDisplayVerdict]", e?.message || e);
    return {
      verdict: typeof result?.signal === "string" ? result.signal : "—",
      actionText: typeof result?.signal === "string" ? result.signal : "—",
    };
  }
}

function readActionHistory(symbol) {
  try {
    const raw = localStorage.getItem(`actionHistory_${String(symbol).toUpperCase()}`);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter(Boolean) : [];
  } catch {
    return [];
  }
}

function appendActionHistory(symbol, result) {
  try {
    const sym = String(symbol).toUpperCase();
    const prev = readActionHistory(sym);
    const next = [
      {
        t: Date.now(),
        buyScore: typeof result?.buyScore === "number" ? result.buyScore : null,
        price: typeof result?.price === "number" ? result.price : null,
        cmf: typeof result?.cmf === "number" ? result.cmf : null,
        ema21: typeof result?.ema21 === "number" ? result.ema21 : null,
        atr: typeof result?.atr === "number" ? result.atr : null,
        callsReady: !!(result?.callsSignal?.fullSignal && !result?.catchingKnivesCallsBlock),
        putsReady: !!(result?.putsSignal?.fullSignal),
        rsVsSpy10: typeof result?.rsVsSpy10 === "number" ? result.rsVsSpy10 : null,
      },
      ...prev,
    ].slice(0, 5);
    localStorage.setItem(`actionHistory_${sym}`, JSON.stringify(next));
  } catch {}
}

// ─── PER-USER SIGNAL HISTORY (user_data.signal_history) ────────────────────
// Auto-logs strong CALLS (score ≥ +4) / PUTS (score ≤ −4) after each scan; dedupes OPEN rows per ticker+side within 7d.
function classifySignalHistorySide(result) {
  const bs = result?.buyScore;
  if (bs == null) return null;
  if (bs >= 4) return "CALLS";
  if (bs <= -4) return "PUTS";
  return null;
}

/** True when calls side should show as active (momentum full/partial or bounce-play-only). */
function callsSignalDisplayActive(result) {
  const c = result?.callsSignal;
  return !!(c?.fullSignal || c?.partialSignal || c?.bouncePlayOnly);
}

function deriveSignalHistoryPhase(result) {
  const ew = result?.earlyWarning;
  const full = result?.callsSignal?.fullSignal || result?.putsSignal?.fullSignal;
  if (full) return "PHASE3";
  if (ew?.phase2Any) return "PHASE2";
  if (ew?.phase1Any) return "PHASE1";
  return "PHASE3";
}

function buildSignalHistoryIndicators(result, verdict) {
  const r = result;
  const wr = r.williamsR;
  const obv = r.obv;
  let obvStr = "";
  if (obv) {
    if (obv.bullishDiv) obvStr = "BULL_DIV";
    else if (obv.bearishDiv) obvStr = "BEAR_DIV";
    else if (obv.confirming) obvStr = "CONFIRM";
    else obvStr = obv.label || "NEUTRAL";
  }
  return {
    cmf: r.cmf ?? 0,
    rsi: r.rsi ?? 0,
    macd: r.macd?.bullish ? "BULL" : "BEAR",
    ema21: r.ema21 != null ? String(r.ema21.toFixed(2)) : "",
    ema21_slope: r.ema21SlopeLabel || "",
    obv: obvStr,
    golden_cross: r.goldenCross ? "YES" : "NO",
    williams_r: wr?.value ?? 0,
    bb_squeeze: !!r.bb?.isSqueeze,
    rvol: r.rvol?.ratio ?? 0,
    adx: r.adx?.adx ?? 0,
    stochastic: r.stoch ? `${(r.stoch.k ?? 0).toFixed(1)}/${(r.stoch.d ?? 0).toFixed(1)}` : "",
    psar: r.psar?.label ?? "",
    vwap: r.vwap != null && r.price != null ? (r.price > r.vwap ? "ABOVE" : "BELOW") : "",
    rs_vs_spy: r.rs?.spread ?? 0,
    weekly_trend: r.weeklyTrend?.label ?? "",
    volume_breakout: !!r.volBreakout?.isBreakout,
    rsi_divergence: r.rsiDiv?.bullish ? "BULLISH" : r.rsiDiv?.bearish ? "BEARISH" : "NONE",
    volume_climax: r.volClimax?.uiLabel ?? "Neutral",
    sector_rs: r.rsVsSector?.spread ?? 0,
    key_level_proximity: r.keyLevelProximity?.score ?? 0,
    multi_tf_alignment: !!(verdict?.multiTF),
    vbc: !!r.volBreakout?.volConfirmed,
    sma200_structure: r.deathCross ? "DEATH_CROSS" : r.goldenCross ? "GOLDEN_CROSS" : "MIXED",
    oversold_reversal: !!r.wrRecovery?.active,
    volume_reversal: !!(r.volDiv?.bullish || r.volDiv?.bearish),
    vwap_recovery: !!(r.vwap && r.price > r.vwap && r.cmf != null && r.cmf > 0.05),
  };
}

function deriveSignalTierFromScore(score) {
  if (typeof score !== "number") return "—";
  if (score >= 12) return "POWER PLAY";
  if (score >= 6) return "STANDARD LONG";
  if (score >= 4) return "SPECULATIVE";
  if (score <= -12) return "BREAKDOWN";
  if (score <= -6) return "STANDARD SHORT";
  if (score <= -4) return "SCALP";
  return "—";
}

function formatPct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Math.round(v * 10) / 10;
  return `${n >= 0 ? "+" : ""}${n.toFixed(1)}%`;
}

function computeResolvedOutcomePct(e) {
  if (!e || !e.outcome || e.outcome === "STILL_OPEN") return null;
  const entry = typeof e.price_at_scan === "number" ? e.price_at_scan : null;
  if (!entry || entry <= 0) return null;
  const isPuts = e.signal_type === "PUTS";
  const t1 = typeof e.t1_price === "number" ? e.t1_price : null;
  const t2 = typeof e.t2_price === "number" ? e.t2_price : null;
  const stop = typeof e.stop_price === "number" ? e.stop_price : null;
  const exit = e.outcome === "T2_HIT" ? t2 : e.outcome === "T1_HIT" ? t1 : e.outcome === "STOP_HIT" ? stop : null;
  if (exit == null) return null;
  const pct = isPuts ? ((entry - exit) / entry) * 100 : ((exit - entry) / entry) * 100;
  return Math.round(pct * 100) / 100;
}

const SIGNAL_HISTORY_SETUP_TYPES = ["MOMENTUM", "VCP", "GAP_AND_GO", "GAP_FILL", "EARNINGS_DRIFT", "BULL_FLAG", "RANGE_BREAKOUT", "SUPPORT_BOUNCE", "DEAD_CAT_BOUNCE"];

function downloadCsv(filename, rows) {
  try {
    const esc = (s) => {
      const v = s == null ? "" : String(s);
      const needs = /[",\n]/.test(v);
      return needs ? `"${v.replace(/"/g, '""')}"` : v;
    };
    const header = Object.keys(rows[0] || {});
    const csv = [
      header.join(","),
      ...rows.map(r => header.map(k => esc(r[k])).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    console.warn("CSV export failed:", e?.message || e);
  }
}

function AdminSignalDashboard({ T, F, onClose }) {
  const [rows, setRows] = React.useState(null); // null = not loaded, [] = empty
  const [scanLedger, setScanLedger] = React.useState([]);
  const [signalChanges, setSignalChanges] = React.useState([]);
  const [activeSignals, setActiveSignals] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = React.useState(null);

  const [outcomeFilter, setOutcomeFilter] = React.useState("ALL");
  const [signalFilter, setSignalFilter] = React.useState("ALL");
  const [setupFilter, setSetupFilter] = React.useState("ALL");
  const [tierFilter, setTierFilter] = React.useState("ALL");
  const [tickerSearch, setTickerSearch] = React.useState("");
  const [timeframe, setTimeframe] = React.useState("ALL"); // 7D/30D/90D/ALL
  const [sortCol, setSortCol] = React.useState("scan_date");
  const [sortDir, setSortDir] = React.useState(-1);
  const [page, setPage] = React.useState(1);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, ledger, changes, active] = await Promise.all([
        load("signal_history"),
        load("scan_ledger"),
        load("signal_changes"),
        load("active_signals"),
      ]);
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      setScanLedger(Array.isArray(ledger) ? ledger : []);
      setSignalChanges(Array.isArray(changes) ? changes : []);
      setActiveSignals(Array.isArray(active) ? active : []);
      setLastUpdatedAt(Date.now());
      console.log("[AdminSignalDashboard] fetch signal_history", { count: list.length });
    } catch (e) {
      setRows([]);
      setScanLedger([]);
      setSignalChanges([]);
      setActiveSignals([]);
      setError(e?.message || "Fetch failed");
      console.log("[AdminSignalDashboard] fetch error", e);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    console.log("[AdminSignalDashboard] mount");
    refresh();
  }, [refresh]);

  React.useEffect(() => {
    console.log("[AdminSignalDashboard] render", { rows: rows?.length ?? null, loading, error });
  });

  const ageSec = lastUpdatedAt ? Math.max(0, Math.floor((Date.now() - lastUpdatedAt) / 1000)) : null;

  const normalized = React.useMemo(() => {
    const list = Array.isArray(rows) ? rows : [];
    return list.filter(Boolean);
  }, [rows]);

  const filtered = React.useMemo(() => {
    const now = Date.now();
    const cutoff = timeframe === "7D" ? now - 7 * 86400000
      : timeframe === "30D" ? now - 30 * 86400000
      : timeframe === "90D" ? now - 90 * 86400000
      : null;
    const q = tickerSearch.trim().toUpperCase();
    return normalized.filter(e => {
      if (cutoff != null) {
        const t = new Date(e.scan_date).getTime();
        if (!Number.isFinite(t) || t < cutoff) return false;
      }
      if (outcomeFilter !== "ALL" && e.outcome !== outcomeFilter) return false;
      if (signalFilter !== "ALL" && e.signal_type !== signalFilter) return false;
      if (setupFilter !== "ALL" && (e.setup_type || "MOMENTUM") !== setupFilter) return false;
      const tier = deriveSignalTierFromScore(e.score);
      if (tierFilter !== "ALL" && tier !== tierFilter) return false;
      if (q && !(e.ticker || "").toUpperCase().includes(q)) return false;
      return true;
    });
  }, [normalized, outcomeFilter, signalFilter, setupFilter, tierFilter, tickerSearch, timeframe]);

  const sorted = React.useMemo(() => {
    const dir = sortDir;
    const col = sortCol;
    const getVal = (e) => {
      if (col === "tier") return deriveSignalTierFromScore(e.score);
      if (col === "setup_type") return e.setup_type || "MOMENTUM";
      if (col === "date") return new Date(e.scan_date).getTime();
      return e[col];
    };
    return [...filtered].sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      const sa = va == null ? "" : String(va);
      const sb = vb == null ? "" : String(vb);
      return sa.localeCompare(sb) * dir;
    });
  }, [filtered, sortCol, sortDir]);

  React.useEffect(() => { setPage(1); }, [outcomeFilter, signalFilter, setupFilter, tierFilter, tickerSearch, timeframe]);

  const pageSize = 50;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize);

  const stats = React.useMemo(() => {
    const all = normalized;
    const open = all.filter(e => e.outcome === "STILL_OPEN");
    const resolved = all.filter(e => e.outcome && e.outcome !== "STILL_OPEN");
    const t1 = resolved.filter(e => e.outcome === "T1_HIT" || e.outcome === "T2_HIT");
    const t2 = resolved.filter(e => e.outcome === "T2_HIT");
    const stop = resolved.filter(e => e.outcome === "STOP_HIT");
    const outcomePcts = resolved.map(computeResolvedOutcomePct).filter(v => typeof v === "number");
    const winners = resolved.filter(e => e.outcome === "T1_HIT" || e.outcome === "T2_HIT").map(computeResolvedOutcomePct).filter(v => typeof v === "number");
    const losers = resolved.filter(e => e.outcome === "STOP_HIT").map(computeResolvedOutcomePct).filter(v => typeof v === "number");
    const avg = (arr) => arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null;
    const ev = avg(outcomePcts);
    return {
      total: all.length,
      resolved: resolved.length,
      open: open.length,
      t1WinRate: resolved.length ? (t1.length / resolved.length) * 100 : null,
      t2Rate: resolved.length ? (t2.length / resolved.length) * 100 : null,
      stopRate: resolved.length ? (stop.length / resolved.length) * 100 : null,
      avgWinner: avg(winners),
      avgLoser: avg(losers),
      ev,
      overallWinRate: resolved.length ? (t1.length / resolved.length) * 100 : 0,
    };
  }, [normalized]);

  const tierTable = React.useMemo(() => {
    const tiers = ["POWER PLAY", "STANDARD LONG", "SPECULATIVE", "STANDARD SHORT", "BREAKDOWN", "SCALP"];
    const resolved = normalized.filter(e => e.outcome && e.outcome !== "STILL_OPEN");
    const byTier = (tier) => normalized.filter(e => deriveSignalTierFromScore(e.score) === tier);
    const avg = (arr) => arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null;
    return tiers.map(tier => {
      const all = byTier(tier);
      const res = resolved.filter(e => deriveSignalTierFromScore(e.score) === tier);
      const t1 = res.filter(e => e.outcome === "T1_HIT" || e.outcome === "T2_HIT");
      const t2 = res.filter(e => e.outcome === "T2_HIT");
      const stop = res.filter(e => e.outcome === "STOP_HIT");
      const winners = res.filter(e => e.outcome === "T1_HIT" || e.outcome === "T2_HIT").map(computeResolvedOutcomePct).filter(v => typeof v === "number");
      const losers = res.filter(e => e.outcome === "STOP_HIT").map(computeResolvedOutcomePct).filter(v => typeof v === "number");
      const ev = avg(res.map(computeResolvedOutcomePct).filter(v => typeof v === "number"));
      return {
        tier,
        count: all.length,
        t1: res.length ? (t1.length / res.length) * 100 : null,
        t2: res.length ? (t2.length / res.length) * 100 : null,
        stop: res.length ? (stop.length / res.length) * 100 : null,
        avgGain: avg(winners),
        avgLoss: avg(losers),
        ev,
      };
    });
  }, [normalized]);

  const setupTypeTable = React.useMemo(() => {
    const setups = SIGNAL_HISTORY_SETUP_TYPES;
    const resolved = normalized.filter(e => e.outcome && e.outcome !== "STILL_OPEN");
    const bySetup = (s) => normalized.filter(e => (e.setup_type || "MOMENTUM") === s);
    const avg = (arr) => arr.length ? arr.reduce((a, v) => a + v, 0) / arr.length : null;
    return setups.map(setup => {
      const all = bySetup(setup);
      const res = resolved.filter(e => (e.setup_type || "MOMENTUM") === setup);
      const t1 = res.filter(e => e.outcome === "T1_HIT" || e.outcome === "T2_HIT");
      const t2 = res.filter(e => e.outcome === "T2_HIT");
      const stop = res.filter(e => e.outcome === "STOP_HIT");
      const winners = res.filter(e => e.outcome === "T1_HIT" || e.outcome === "T2_HIT").map(computeResolvedOutcomePct).filter(v => typeof v === "number");
      const losers = res.filter(e => e.outcome === "STOP_HIT").map(computeResolvedOutcomePct).filter(v => typeof v === "number");
      const ev = avg(res.map(computeResolvedOutcomePct).filter(v => typeof v === "number"));
      return {
        setup,
        count: all.length,
        t1: res.length ? (t1.length / res.length) * 100 : null,
        t2: res.length ? (t2.length / res.length) * 100 : null,
        stop: res.length ? (stop.length / res.length) * 100 : null,
        avgGain: avg(winners),
        avgLoss: avg(losers),
        ev,
      };
    });
  }, [normalized]);

  const indicatorTable = React.useMemo(() => {
    const resolved = normalized.filter(e => e.outcome && e.outcome !== "STILL_OPEN");
    const wins = resolved.filter(e => e.outcome === "T1_HIT" || e.outcome === "T2_HIT");
    const losses = resolved.filter(e => e.outcome === "STOP_HIT");
    const overall = stats.overallWinRate || 0;
    const keys = new Set();
    normalized.forEach(e => Object.keys(e.indicators || {}).forEach(k => keys.add(k)));
    const isActive = (val) => {
      if (typeof val === "boolean") return val;
      if (typeof val === "number") return val !== 0 && !Number.isNaN(val);
      if (typeof val === "string") {
        const s = val.trim();
        if (!s) return false;
        const low = s.toLowerCase();
        return !["none", "neutral", "—", "n/a"].includes(low);
      }
      return !!val;
    };
    const rows = [...keys].map(k => {
      const w = wins.filter(e => isActive(e.indicators?.[k])).length;
      const l = losses.filter(e => isActive(e.indicators?.[k])).length;
      const tot = w + l;
      const winRate = tot ? (w / tot) * 100 : null;
      const edge = winRate == null ? null : winRate - overall;
      const rating = edge == null ? "—" : edge > 15 ? "STRONG" : edge > 5 ? "POSITIVE" : edge > -5 ? "NEUTRAL" : "NEGATIVE";
      return { indicator: k, wins: w, losses: l, winRate, edge, rating };
    }).filter(r => (r.wins + r.losses) >= 5); // avoid tiny samples
    return rows.sort((a, b) => (b.edge ?? -999) - (a.edge ?? -999));
  }, [normalized, stats.overallWinRate]);

  const tickerLedgerQuery = tickerSearch.trim().toUpperCase();
  const tickerLedgerEntries = React.useMemo(() => {
    if (!tickerLedgerQuery) return [];
    return (scanLedger || [])
      .filter(e => (e.ticker || "").toUpperCase() === tickerLedgerQuery)
      .sort((a, b) => scanLedgerEntryTimestamp(b) - scanLedgerEntryTimestamp(a));
  }, [scanLedger, tickerLedgerQuery]);

  const tickerActiveSignal = React.useMemo(() => {
    if (!tickerLedgerQuery) return null;
    return (activeSignals || []).find(e => (e.ticker || "").toUpperCase() === tickerLedgerQuery) || null;
  }, [activeSignals, tickerLedgerQuery]);

  const tickerLedgerSummary = React.useMemo(() => {
    if (!tickerLedgerQuery || !tickerLedgerEntries.length) return null;
    return summarizeTickerLedger(tickerLedgerEntries, tickerActiveSignal);
  }, [tickerLedgerQuery, tickerLedgerEntries, tickerActiveSignal]);

  const ScoreSparkline = ({ scores }) => {
    const pts = (scores || []).filter(v => typeof v === "number" && Number.isFinite(v));
    if (pts.length < 2) return <span style={{ color: T.textDim, fontSize: 11 }}>—</span>;
    const chron = [...pts].reverse();
    const w = 120, h = 28, pad = 2;
    const min = Math.min(...chron, -14);
    const max = Math.max(...chron, 14);
    const range = max - min || 1;
    const yFor = (v) => pad + (h - pad * 2) * (1 - (v - min) / range);
    const xFor = (i) => pad + (i / (chron.length - 1)) * (w - pad * 2);
    const d = chron.map((v, i) => `${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`).join(" ");
    const last = chron[chron.length - 1];
    const stroke = last >= 6 ? T.green : last <= -6 ? T.red : last >= 0 ? "#94a3b8" : T.orange;
    return (
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "inline-block", verticalAlign: "middle" }}>
        <line x1={pad} y1={yFor(0)} x2={w - pad} y2={yFor(0)} stroke={T.border} strokeWidth={1} strokeDasharray="2,2" />
        <polyline points={d} stroke={stroke} strokeWidth={1.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  };

  const contextTable = React.useMemo(() => {
    const resolved = normalized.filter(e => e.outcome && e.outcome !== "STILL_OPEN");
    const isWin = (e) => e.outcome === "T1_HIT" || e.outcome === "T2_HIT";
    const winRate = (arr) => arr.length ? (arr.filter(isWin).length / arr.length) * 100 : null;
    const tideBand = (s) => s >= 2 ? "Bull Tide" : s <= -2 ? "Bear Tide" : "Neutral";
    const macroBand = (s) => s >= 55 ? "Macro Positive" : s <= 45 ? "Macro Negative" : "Neutral";
    const phaseBand = (p) => p === "PHASE1" ? "1→2→3" : p === "PHASE2" ? "2→3" : "3 only";
    const groupRates = (keyFn) => {
      const groups = {};
      resolved.forEach(e => {
        const k = keyFn(e);
        groups[k] = (groups[k] || []).concat(e);
      });
      return Object.entries(groups).map(([k, arr]) => ({ k, n: arr.length, wr: winRate(arr) }));
    };
    return {
      tide: groupRates(e => tideBand(e.market_tide_score ?? 0)),
      macro: groupRates(e => macroBand(e.macro_score ?? 0)),
      phase: groupRates(e => phaseBand(e.phase)),
      setup: groupRates(e => e.setup_type || "MOMENTUM"),
    };
  }, [normalized]);

  const Card = ({ title, value, sub }) => (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, minWidth: 140 }}>
      <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textDim, fontWeight: 800 }}>{title}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginTop: 6 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  const Section = ({ title, children, right }) => (
    <div style={{ background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: T.text }}>{title}</div>
        {right}
      </div>
      {children}
    </div>
  );

  const OutcomeBadge = ({ outcome }) => {
    const o = outcome || "—";
    const map = {
      T1_HIT: { bg: `${T.green}22`, fg: T.green, label: "✅ T1 HIT" },
      T2_HIT: { bg: "#f59e0b22", fg: "#f59e0b", label: "🎯 T2 HIT" },
      STOP_HIT: { bg: `${T.red}22`, fg: T.red, label: "❌ STOP HIT" },
      STILL_OPEN: { bg: `${T.textDim}22`, fg: T.textDim, label: "⏳ STILL OPEN" },
    };
    const v = map[o] || { bg: `${T.textDim}22`, fg: T.textDim, label: o };
    return <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: v.bg, color: v.fg, whiteSpace: "nowrap" }}>{v.label}</span>;
  };

  const th = (label, col) => (
    <th
      onClick={() => {
        if (!col) return;
        if (sortCol === col) setSortDir(d => -d);
        else { setSortCol(col); setSortDir(col === "date" ? -1 : -1); }
      }}
      style={{ padding: "8px 8px", fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.08em", textAlign: "left", cursor: col ? "pointer" : "default", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}
    >
      {label}{col && sortCol === col ? (sortDir === -1 ? " ▼" : " ▲") : ""}
    </th>
  );

  const btn = (active) => ({
    padding: "7px 10px",
    background: active ? `${T.cyan}18` : T.surface,
    border: `1px solid ${active ? `${T.cyan}55` : T.border}`,
    color: active ? T.cyan : T.textSub,
    borderRadius: 9,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
  });

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9999, background: T.bg, display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "0 14px", borderBottom: `1px solid ${T.border}`, background: T.surface }}>
        <button onClick={onClose} style={{ padding: "7px 10px", background: `${T.purple}18`, border: `1px solid ${T.purple}44`, color: T.purple, borderRadius: 10, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>← Back to App</button>
        <div style={{ minWidth: 0, textAlign: "center" }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: T.text }}>Auctus Signal Accuracy Dashboard</div>
          <div style={{ fontSize: 11, color: T.textDim }}>Last updated: {ageSec == null ? "—" : `${ageSec}s ago`}{loading ? " · refreshing…" : ""}{error ? ` · ${error}` : ""}</div>
        </div>
        <button onClick={refresh} style={{ padding: "7px 10px", background: `${T.cyan}18`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 10, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>🔄 Refresh</button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: "auto", padding: 14 }}>
        {/* Empty state */}
        {(!rows || rows.length === 0) ? (
          <div style={{ height: "calc(100vh - 56px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
            <div style={{ maxWidth: 520, textAlign: "center", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: 22 }}>
              <div style={{ fontSize: 14, fontWeight: 900, color: T.text }}>No signals logged yet.</div>
              <div style={{ fontSize: 12, color: T.textSub, marginTop: 8, lineHeight: 1.5 }}>
                Run a scan to start tracking accuracy.<br />
                Every CALLS/PUTS signal will be automatically logged and tracked here.
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* Summary */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
              <Card title="Total Signals" value={stats.total} />
              <Card title="Win Rate %" value={stats.t1WinRate == null ? "—" : `${stats.t1WinRate.toFixed(1)}%`} sub={`${stats.resolved} resolved`} />
              <Card title="T2 Hit Rate %" value={stats.t2Rate == null ? "—" : `${stats.t2Rate.toFixed(1)}%`} />
              <Card title="Stop Hit Rate %" value={stats.stopRate == null ? "—" : `${stats.stopRate.toFixed(1)}%`} />
              <Card title="Avg Winner %" value={stats.avgWinner == null ? "—" : formatPct(stats.avgWinner)} />
              <Card title="Avg Loser %" value={stats.avgLoser == null ? "—" : formatPct(stats.avgLoser)} />
              <Card title="Expected Value" value={stats.ev == null ? "—" : formatPct(stats.ev)} />
              <Card title="Open Signals" value={stats.open} />
            </div>

            {/* Verdict tier table */}
            <Section title="Verdict Tier Table">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                  <thead>
                    <tr>
                      {th("Verdict", null)}
                      {th("Count", null)}
                      {th("T1 Win%", null)}
                      {th("T2 Win%", null)}
                      {th("Stop%", null)}
                      {th("Avg Gain", null)}
                      {th("Avg Loss", null)}
                      {th("EV", null)}
                    </tr>
                  </thead>
                  <tbody>
                    {tierTable.map(r => (
                      <tr key={r.tier}>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.text, fontWeight: 800, whiteSpace: "nowrap" }}>{r.tier}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.count}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.t1 == null ? "—" : `${r.t1.toFixed(1)}%`}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.t2 == null ? "—" : `${r.t2.toFixed(1)}%`}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.stop == null ? "—" : `${r.stop.toFixed(1)}%`}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.avgGain == null ? "—" : formatPct(r.avgGain)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.avgLoss == null ? "—" : formatPct(r.avgLoss)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.ev == null ? "—" : formatPct(r.ev)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="Setup Type Table">
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                  <thead>
                    <tr>
                      {th("Setup Type", null)}
                      {th("Count", null)}
                      {th("T1 Win%", null)}
                      {th("T2 Win%", null)}
                      {th("Stop%", null)}
                      {th("Avg Gain", null)}
                      {th("Avg Loss", null)}
                      {th("EV", null)}
                    </tr>
                  </thead>
                  <tbody>
                    {setupTypeTable.map(r => (
                      <tr key={r.setup}>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.text, fontWeight: 800, whiteSpace: "nowrap", fontSize: 11 }}>{r.setup}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.count}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.t1 == null ? "—" : `${r.t1.toFixed(1)}%`}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.t2 == null ? "—" : `${r.t2.toFixed(1)}%`}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.stop == null ? "—" : `${r.stop.toFixed(1)}%`}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.avgGain == null ? "—" : formatPct(r.avgGain)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.avgLoss == null ? "—" : formatPct(r.avgLoss)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.ev == null ? "—" : formatPct(r.ev)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* Indicator accuracy */}
            <Section title="Indicator Accuracy Table" right={<div style={{ fontSize: 11, color: T.textDim }}>Sorted by Edge</div>}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
                  <thead>
                    <tr>
                      {th("Indicator", null)}
                      {th("Wins Active", null)}
                      {th("Losses Active", null)}
                      {th("Win Rate", null)}
                      {th("Edge", null)}
                      {th("Rating", null)}
                    </tr>
                  </thead>
                  <tbody>
                    {indicatorTable.map(r => {
                      const edge = r.edge ?? 0;
                      const color = r.edge == null ? T.textDim : edge > 15 ? T.green : edge > 5 ? "#eab308" : edge > -5 ? T.textDim : T.red;
                      return (
                        <tr key={r.indicator}>
                          <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.text, fontWeight: 700, whiteSpace: "nowrap" }}>{r.indicator}</td>
                          <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.wins}</td>
                          <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.losses}</td>
                          <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{r.winRate == null ? "—" : `${r.winRate.toFixed(1)}%`}</td>
                          <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color }}>{r.edge == null ? "—" : `${edge >= 0 ? "+" : ""}${edge.toFixed(1)}%`}</td>
                          <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color }}>{r.rating}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* Market context */}
            <Section title="Market Context Table">
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: T.text, marginBottom: 8 }}>Win rate by Tide</div>
                  {contextTable.tide.map(r => (
                    <div key={r.k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.textSub, padding: "4px 0" }}>
                      <span>{r.k}</span>
                      <span>{r.wr == null ? "—" : `${r.wr.toFixed(1)}%`} <span style={{ color: T.textDim }}>({r.n})</span></span>
                    </div>
                  ))}
                </div>
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: T.text, marginBottom: 8 }}>Win rate by Macro</div>
                  {contextTable.macro.map(r => (
                    <div key={r.k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.textSub, padding: "4px 0" }}>
                      <span>{r.k}</span>
                      <span>{r.wr == null ? "—" : `${r.wr.toFixed(1)}%`} <span style={{ color: T.textDim }}>({r.n})</span></span>
                    </div>
                  ))}
                </div>
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: T.text, marginBottom: 8 }}>Win rate by Phase</div>
                  {contextTable.phase.map(r => (
                    <div key={r.k} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: T.textSub, padding: "4px 0" }}>
                      <span>{r.k}</span>
                      <span>{r.wr == null ? "—" : `${r.wr.toFixed(1)}%`} <span style={{ color: T.textDim }}>({r.n})</span></span>
                    </div>
                  ))}
                </div>
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: T.text, marginBottom: 8 }}>Win rate by Setup Type</div>
                  {contextTable.setup.map(r => (
                    <div key={r.k} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: T.textSub, padding: "4px 0" }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{r.k}</span>
                      <span>{r.wr == null ? "—" : `${r.wr.toFixed(1)}%`} <span style={{ color: T.textDim }}>({r.n})</span></span>
                    </div>
                  ))}
                </div>
              </div>
            </Section>

            {/* Signal history */}
            <Section
              title="Signal History"
              right={
                <button
                  onClick={() => {
                    const csvRows = sorted.map(e => ({
                      Date: e.scan_date,
                      Ticker: e.ticker,
                      Score: e.score,
                      Tier: deriveSignalTierFromScore(e.score),
                      Verdict: e.verdict,
                      Signal: e.signal_type,
                      SetupType: e.setup_type || "MOMENTUM",
                      Phase: e.phase,
                      Entry: e.price_at_scan,
                      T1: e.t1_price,
                      T2: e.t2_price,
                      Stop: e.stop_price,
                      Tide: e.market_tide_score,
                      Macro: e.macro_score,
                      MaxGainPct: e.max_gain_pct,
                      MaxDrawdownPct: e.max_drawdown_pct,
                      Days: e.days_to_resolution,
                      Outcome: e.outcome,
                    }));
                    downloadCsv("signal_history.csv", csvRows);
                  }}
                  style={{ padding: "7px 10px", background: T.surface, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 10, fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Export CSV
                </button>
              }
            >
              {/* Filters */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <select value={outcomeFilter} onChange={e => setOutcomeFilter(e.target.value)} style={{ ...btn(false), padding: "7px 10px" }}>
                  {["ALL", "STILL_OPEN", "T1_HIT", "T2_HIT", "STOP_HIT"].map(o => <option key={o} value={o}>{o === "ALL" ? "Outcome: ALL" : `Outcome: ${o}`}</option>)}
                </select>
                <select value={signalFilter} onChange={e => setSignalFilter(e.target.value)} style={{ ...btn(false), padding: "7px 10px" }}>
                  {["ALL", "CALLS", "PUTS"].map(s => <option key={s} value={s}>{s === "ALL" ? "Signal: ALL" : `Signal: ${s}`}</option>)}
                </select>
                <select value={setupFilter} onChange={e => setSetupFilter(e.target.value)} style={{ ...btn(false), padding: "7px 10px", maxWidth: 220 }}>
                  {["ALL", ...SIGNAL_HISTORY_SETUP_TYPES].map(s => <option key={s} value={s}>{s === "ALL" ? "Setup: ALL" : `Setup: ${s}`}</option>)}
                </select>
                <select value={tierFilter} onChange={e => setTierFilter(e.target.value)} style={{ ...btn(false), padding: "7px 10px" }}>
                  {["ALL", "POWER PLAY", "STANDARD LONG", "SPECULATIVE", "STANDARD SHORT", "BREAKDOWN", "SCALP"].map(t => <option key={t} value={t}>{t === "ALL" ? "Verdict: ALL" : `Verdict: ${t}`}</option>)}
                </select>
                <input value={tickerSearch} onChange={e => setTickerSearch(e.target.value)} placeholder="Ticker search"
                  style={{ padding: "8px 10px", borderRadius: 10, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
                <div style={{ display: "flex", gap: 6 }}>
                  {["7D", "30D", "90D", "ALL"].map(tf => (
                    <button key={tf} onClick={() => setTimeframe(tf)} style={btn(timeframe === tf)}>{tf}</button>
                  ))}
                </div>
                <div style={{ marginLeft: "auto", fontSize: 11, color: T.textDim }}>
                  Showing {sorted.length} rows
                </div>
              </div>

              {tickerLedgerQuery && tickerLedgerEntries.length > 0 && (
                <div style={{ marginBottom: 14, padding: 12, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 900, color: T.text }}>{tickerLedgerQuery} — Scan Ledger Timeline</div>
                      {tickerLedgerSummary && (
                        <div style={{ fontSize: 11, color: T.textSub, marginTop: 6, lineHeight: 1.6 }}>
                          {tickerLedgerQuery} fired buy signal {tickerLedgerSummary.buyFires} time{tickerLedgerSummary.buyFires !== 1 ? "s" : ""}
                          {" · "}Average days signal held: {tickerLedgerSummary.avgHold ?? "—"}
                          {" · "}Signal flipped to CHOP {tickerLedgerSummary.chopFlips} time{tickerLedgerSummary.chopFlips !== 1 ? "s" : ""}
                          {" · "}Current status: {tickerLedgerSummary.status}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 10, color: T.textDim, marginBottom: 4 }}>Score trend ({tickerLedgerEntries.length} scans)</div>
                      <ScoreSparkline scores={tickerLedgerEntries.slice().reverse().map(e => e.score)} />
                    </div>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1100 }}>
                      <thead>
                        <tr>
                          {["Date", "Time", "Price", "Score", "Verdict", "Action", "Signal", "Setup", "CMF", "RSI", "Market Tide", "Change"].map(h => (
                            <th key={h} style={{ padding: "6px 8px", fontSize: 10, color: T.textDim, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tickerLedgerEntries.map((e, i) => {
                          const prev = tickerLedgerEntries[i + 1];
                          const hardExit = i === 0 && tickerActiveSignal?.hard_exit_triggered;
                          const flag = ledgerChangeFlag(prev, e, hardExit);
                          return (
                            <tr key={e.id || i}>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontSize: 11 }}>{e.date}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontSize: 11 }}>{e.time}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: T.text, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>${(e.price ?? 0).toFixed(2)}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: (e.score ?? 0) >= 0 ? T.green : T.red, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{e.score > 0 ? "+" : ""}{e.score}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontSize: 10, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.verdict}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontSize: 10 }}>{e.action || "—"}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: e.signal_type === "CALLS" ? T.green : e.signal_type === "PUTS" ? T.red : T.textSub, fontSize: 10, fontWeight: 800 }}>{e.signal_type}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontSize: 10 }}>{e.setup_type || "—"}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>{e.cmf?.toFixed(3) ?? "—"}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontSize: 11 }}>{e.rsi?.toFixed(1) ?? "—"}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontSize: 11 }}>{e.market_tide ?? 0}</td>
                              <td style={{ padding: "6px 8px", borderBottom: `1px solid ${T.border}`, fontSize: 14, textAlign: "center" }} title={flag.title}>
                                <span style={{ color: flag.color }}>{flag.icon}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {tickerLedgerQuery && tickerLedgerEntries.length === 0 && (
                <div style={{ marginBottom: 10, padding: 10, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 11, color: T.textSub }}>
                  No scan ledger entries for {tickerLedgerQuery}. Run a scan on this ticker to populate the ledger.
                </div>
              )}

              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1250 }}>
                  <thead>
                    <tr>
                      {th("Date", "date")}
                      {th("Ticker", "ticker")}
                      {th("Score", "score")}
                      {th("Verdict", "tier")}
                      {th("Signal", "signal_type")}
                      {th("Setup", "setup_type")}
                      {th("Phase", "phase")}
                      {th("Entry$", "price_at_scan")}
                      {th("T1$", "t1_price")}
                      {th("T2$", "t2_price")}
                      {th("Stop$", "stop_price")}
                      {th("Tide", "market_tide_score")}
                      {th("Max Gain%", "max_gain_pct")}
                      {th("Max DD%", "max_drawdown_pct")}
                      {th("Days", "days_to_resolution")}
                      {th("Outcome", "outcome")}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(e => (
                      <tr key={e.id}>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, whiteSpace: "nowrap" }}>
                          {e.scan_date ? new Date(e.scan_date).toLocaleString() : "—"}
                        </td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.text, fontWeight: 900 }}>{e.ticker}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: e.score >= 0 ? T.green : T.red, fontFamily: "'JetBrains Mono', monospace" }}>
                          {e.score >= 0 ? "+" : ""}{e.score}
                        </td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, whiteSpace: "nowrap" }}>
                          <div style={{ fontWeight: 800, color: T.text }}>{deriveSignalTierFromScore(e.score)}</div>
                          <div style={{ fontSize: 10, color: T.textDim, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.verdict}</div>
                        </td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: e.signal_type === "CALLS" ? T.green : T.red, fontWeight: 900 }}>{e.signal_type}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap" }}>{e.setup_type || "MOMENTUM"}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{e.phase}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>${(e.price_at_scan ?? 0).toFixed(2)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>${(e.t1_price ?? 0).toFixed(2)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>${(e.t2_price ?? 0).toFixed(2)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>${(e.stop_price ?? 0).toFixed(2)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub, fontFamily: "'JetBrains Mono', monospace" }}>{e.market_tide_score ?? 0}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{formatPct(e.max_gain_pct ?? 0)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{formatPct(e.max_drawdown_pct ?? 0)}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}`, color: T.textSub }}>{e.days_to_resolution == null ? "—" : e.days_to_resolution}</td>
                        <td style={{ padding: "8px 8px", borderBottom: `1px solid ${T.border}` }}><OutcomeBadge outcome={e.outcome} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, gap: 10, flexWrap: "wrap" }}>
                <div style={{ fontSize: 11, color: T.textDim }}>Page {page} / {totalPages}</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ ...btn(false), opacity: page <= 1 ? 0.5 : 1, cursor: page <= 1 ? "default" : "pointer" }}>Prev</button>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} style={{ ...btn(false), opacity: page >= totalPages ? 0.5 : 1, cursor: page >= totalPages ? "default" : "pointer" }}>Next</button>
                </div>
              </div>
            </Section>
          </div>
        )}
      </div>
    </div>
  );
}

function buildSignalHistoryEntry(sym, result, verdict) {
  const signal_type = classifySignalHistorySide(result);
  if (!signal_type) return null;
  const st = result.activeSetupType || "MOMENTUM";
  if (st === "GAP_FILL" && signal_type === "CALLS") return null;
  if (st === "GAP_AND_GO" && signal_type === "PUTS") return null;
  if (st === "VCP" && signal_type === "PUTS") return null;
  if (st === "EARNINGS_DRIFT" && result.tradeSetups?.earningsDrift?.drift_failed) return null;
  if (st === "SUPPORT_BOUNCE" && result.sm200 != null && result.price != null && result.price < result.sm200) return null;
  const isPuts = signal_type === "PUTS";
  const atr = result.atr;
  const price = result.price;
  let stop_price = isPuts
    ? (atr ? price + 2 * atr : price * 1.08)
    : (atr ? price - 2 * atr : price * 0.92);
  const atrOk = typeof atr === "number" && atr > 0;
  const fallbackStop = atrOk ? 1.5 * atr : null;
  if (isPuts && stop_price <= price) {
    stop_price = fallbackStop != null ? price + fallbackStop : price * 1.08;
  } else if (!isPuts && stop_price >= price) {
    stop_price = fallbackStop != null ? price - fallbackStop : price * 0.92;
  }
  const t1_price = isPuts ? (result.d1 ?? price * 0.92) : (result.t1 ?? price * 1.08);
  const t2_price = isPuts ? (result.d2 ?? price * 0.85) : (result.t2 ?? price * 1.15);
  const v = verdict ?? calcVerdict({ result, aiCaution: false });
  const dcb = result.deadCatBounce;
  const bouncePhase = dcb?.bouncePhaseKey && dcb.bouncePhaseKey !== "NONE" ? dcb.bouncePhaseKey : null;
  const bounceT1 = !isPuts && bouncePhase && dcb?.bounceT1 != null ? dcb.bounceT1 : null;
  const bounceT2 = !isPuts && bouncePhase && dcb?.bounceT2 != null ? dcb.bounceT2 : null;
  const actionLabel = resolveDisplayVerdict(result, { inPosition: result?.inPosition, activeSignal: result?.activeSignal }).actionText ?? (result.signal ?? "");
  return {
    id: `${sym}_${Date.now()}`,
    ticker: sym,
    scan_date: new Date().toISOString(),
    first_fired_at: new Date().toISOString(),
    price_at_scan: price,
    score: result.buyScore,
    verdict: v?.label ?? v?.short ?? result.signal ?? "",
    signal: actionLabel,
    signal_type,
    phase: deriveSignalHistoryPhase(result),
    bounce_phase: bouncePhase,
    t1_price,
    t2_price,
    stop_price,
    market_tide_score: typeof result.marketTide?.breadthScore === "number" ? result.marketTide.breadthScore : 0,
    macro_score: typeof _fearGreedData?.score === "number" ? _fearGreedData.score : 0,
    indicators: buildSignalHistoryIndicators(result, v),
    t1_hit: false,
    t1_hit_date: null,
    t2_hit: false,
    t2_hit_date: null,
    stop_hit: false,
    stop_hit_date: null,
    bounce_t1_price: bounceT1,
    bounce_t2_price: bounceT2,
    bounce_t1_hit: false,
    bounce_t1_hit_date: null,
    bounce_t2_hit: false,
    bounce_t2_hit_date: null,
    max_gain_pct: 0,
    max_drawdown_pct: 0,
    outcome: "STILL_OPEN",
    days_to_resolution: null,
    setup_type: st,
  };
}

async function mergeSignalHistoryAfterScan(sym, result) {
  try {
    const scanSide = classifySignalHistorySide(result);
    const scanPrice = result?.price;
    if (scanPrice == null) return;
    const verdict = calcVerdict({ result, aiCaution: false });
    const existing = (await load("signal_history")) || [];
    const list = Array.isArray(existing) ? existing : [];

    // Outcome auto-update on every rescan of this ticker (for any STILL_OPEN entries)
    const nowIso = new Date().toISOString();
    const updatedForOutcome = list.map(e => {
      if (!e || e.ticker !== sym || e.outcome !== "STILL_OPEN") return e;
      const isPuts = e.signal_type === "PUTS";
      const entry = typeof e.price_at_scan === "number" ? e.price_at_scan : null;
      if (!entry || entry <= 0) return e;

      const fireAt = e.first_fired_at || e.scan_date;
      const staleAge = computeSignalAge({ price: scanPrice }, normalizeOpenSignalRow(e), scanPrice);

      const profitPct = isPuts
        ? ((entry - scanPrice) / entry) * 100
        : ((scanPrice - entry) / entry) * 100;
      const ddPct = isPuts
        ? ((entry - scanPrice) / entry) * 100 // negative when price rises above entry
        : ((scanPrice - entry) / entry) * 100; // negative when price falls below entry

      const max_gain_pct = Math.max(typeof e.max_gain_pct === "number" ? e.max_gain_pct : 0, profitPct);
      const max_drawdown_pct = Math.min(typeof e.max_drawdown_pct === "number" ? e.max_drawdown_pct : 0, ddPct);

      let outcome = e.outcome;
      let t1_hit = !!e.t1_hit;
      let t2_hit = !!e.t2_hit;
      let stop_hit = !!e.stop_hit;
      let t1_hit_date = e.t1_hit_date ?? null;
      let t2_hit_date = e.t2_hit_date ?? null;
      let stop_hit_date = e.stop_hit_date ?? null;
      let bounce_t1_hit = !!e.bounce_t1_hit;
      let bounce_t2_hit = !!e.bounce_t2_hit;
      let bounce_t1_hit_date = e.bounce_t1_hit_date ?? null;
      let bounce_t2_hit_date = e.bounce_t2_hit_date ?? null;

      const t1 = typeof e.t1_price === "number" ? e.t1_price : null;
      const t2 = typeof e.t2_price === "number" ? e.t2_price : null;
      const stop = typeof e.stop_price === "number" ? e.stop_price : null;
      const bt1 = typeof e.bounce_t1_price === "number" ? e.bounce_t1_price : null;
      const bt2 = typeof e.bounce_t2_price === "number" ? e.bounce_t2_price : null;

      if (isPuts) {
        if (!stop_hit && stop != null && scanPrice >= stop) {
          stop_hit = true; stop_hit_date = nowIso; outcome = "STOP_HIT";
        } else if (!t2_hit && t2 != null && scanPrice <= t2) {
          t2_hit = true; t2_hit_date = nowIso; outcome = "T2_HIT";
        } else if (!t1_hit && t1 != null && scanPrice <= t1) {
          t1_hit = true; t1_hit_date = nowIso; outcome = "T1_HIT";
        }
      } else {
        if (!stop_hit && stop != null && scanPrice <= stop) {
          stop_hit = true; stop_hit_date = nowIso; outcome = "STOP_HIT";
        } else if (!t2_hit && t2 != null && scanPrice >= t2) {
          t2_hit = true; t2_hit_date = nowIso; outcome = "T2_HIT";
        } else if (!t1_hit && t1 != null && scanPrice >= t1) {
          t1_hit = true; t1_hit_date = nowIso; outcome = "T1_HIT";
        }
        if (bt2 != null && !bounce_t2_hit && scanPrice >= bt2) {
          bounce_t2_hit = true; bounce_t2_hit_date = nowIso;
        }
        if (bt1 != null && !bounce_t1_hit && scanPrice >= bt1) {
          bounce_t1_hit = true; bounce_t1_hit_date = nowIso;
        }
      }

      let days_to_resolution = e.days_to_resolution ?? null;
      if (outcome === "STILL_OPEN" && staleAge?.isSevereStale) {
        outcome = "STALE";
        days_to_resolution = staleAge.ageDays;
      } else if (outcome !== "STILL_OPEN") {
        const start = new Date(fireAt).getTime();
        if (!Number.isNaN(start)) {
          const days = tradingDaysBetween(fireAt, nowIso) ?? Math.max(0, Math.round((Date.now() - start) / 86400000));
          days_to_resolution = days;
        }
      }

      return {
        ...e,
        first_fired_at: e.first_fired_at || e.scan_date,
        resolution_price: outcome === "STALE" ? scanPrice : e.resolution_price ?? null,
        resolved_at: outcome === "STALE" ? nowIso : e.resolved_at ?? null,
        max_gain_pct: Math.round(max_gain_pct * 100) / 100,
        max_drawdown_pct: Math.round(max_drawdown_pct * 100) / 100,
        t1_hit,
        t1_hit_date,
        t2_hit,
        t2_hit_date,
        stop_hit,
        stop_hit_date,
        bounce_t1_hit,
        bounce_t1_hit_date,
        bounce_t2_hit,
        bounce_t2_hit_date,
        outcome,
        days_to_resolution,
      };
    });

    // If the current scan qualifies as a new/updated signal, merge it in with dedup.
    if (!scanSide) {
      // Even if it doesn't qualify, we still persist any outcome updates above.
      await save("signal_history", updatedForOutcome.slice(0, 500));
      return;
    }

    const verdictText = `${verdict?.label ?? ""} ${verdict?.short ?? ""} ${result?.signal ?? ""}`.toUpperCase();
    const bs = result?.buyScore;
    const skipLog =
      verdictText.includes("BLOCKED")
      || (verdictText.includes("BEARISH") && scanSide === "CALLS")
      || (verdictText.includes("BULLISH") && scanSide === "PUTS")
      || scanPrice < 1
      || (typeof bs === "number" && bs >= -3 && bs <= 3);
    if (skipLog) {
      await save("signal_history", updatedForOutcome.slice(0, 500));
      return;
    }

    const fresh = buildSignalHistoryEntry(sym, result, verdict);
    if (!fresh) {
      await save("signal_history", updatedForOutcome.slice(0, 500));
      return;
    }
    const seven = 7 * 86400000;
    const idx = updatedForOutcome.findIndex(
      e => e && e.ticker === sym && e.signal_type === fresh.signal_type && e.outcome === "STILL_OPEN"
        && (Date.now() - new Date(e.scan_date).getTime()) < seven
    );
    if (idx >= 0) {
      const prev = updatedForOutcome[idx];
      const merged = {
        ...fresh,
        setup_type: fresh.setup_type ?? prev.setup_type,
        id: prev.id,
        scan_date: fresh.scan_date,
        first_fired_at: prev.first_fired_at || prev.scan_date,
        t1_hit: prev.t1_hit,
        t1_hit_date: prev.t1_hit_date,
        t2_hit: prev.t2_hit,
        t2_hit_date: prev.t2_hit_date,
        stop_hit: prev.stop_hit,
        stop_hit_date: prev.stop_hit_date,
        bounce_t1_hit: prev.bounce_t1_hit,
        bounce_t1_hit_date: prev.bounce_t1_hit_date,
        bounce_t2_hit: prev.bounce_t2_hit,
        bounce_t2_hit_date: prev.bounce_t2_hit_date,
        max_gain_pct: prev.max_gain_pct,
        max_drawdown_pct: prev.max_drawdown_pct,
        outcome: prev.outcome,
        days_to_resolution: prev.days_to_resolution,
      };
      const next = [...updatedForOutcome];
      next[idx] = merged;
      // Cap at 500 — prefer keeping STILL_OPEN entries and newest resolved
      const stillOpen = next.filter(e => e?.outcome === "STILL_OPEN");
      const resolved = next.filter(e => e?.outcome && e.outcome !== "STILL_OPEN")
        .sort((a, b) => new Date(b.scan_date).getTime() - new Date(a.scan_date).getTime());
      const capped = [...stillOpen, ...resolved].slice(0, 500);
      await save("signal_history", capped);
      return;
    }
    {
      const next = [fresh, ...updatedForOutcome];
      const stillOpen = next.filter(e => e?.outcome === "STILL_OPEN");
      const resolved = next.filter(e => e?.outcome && e.outcome !== "STILL_OPEN")
        .sort((a, b) => new Date(b.scan_date).getTime() - new Date(a.scan_date).getTime());
      const capped = [...stillOpen, ...resolved].slice(0, 500);
      await save("signal_history", capped);
    }
  } catch (e) {
    console.warn("signal_history merge failed:", e?.message || e);
  }
}

async function mergeScanLedgerAfterScan(sym, result) {
  try {
    if (!result || result.price == null) return result;
    attachSectorContextToResult(sym, result, _sectorDashboardCache, TICKER_SECTOR_MAP);
    const verdict = calcVerdict({ result, aiCaution: false });
    const signalAction = resolveDisplayVerdict(result, { inPosition: result?.inPosition, activeSignal: result?.activeSignal }).actionText ?? result.signal ?? "";
    const macroScore = typeof _fearGreedData?.score === "number" ? _fearGreedData.score : 0;
    const entry = buildScanLedgerEntry(sym, result, verdict, {
      signalAction,
      sector: result.sector,
      sectorStage: result.sectorStage,
      sectorEtf: result.sectorEtf,
      sectorVsSpy5d: result.sectorVsSpy5d,
      macroScore,
    });

    const [ledgerRaw, changesRaw, activeRaw] = await Promise.all([
      load("scan_ledger"),
      load("signal_changes"),
      load("active_signals"),
    ]);

    const { list: ledger, entry: mergedEntry, updated, previous } = mergeScanLedgerEntry(ledgerRaw, entry);

    let changes = Array.isArray(changesRaw) ? changesRaw : [];
    const comparePrev = updated ? previous : findPreviousLedgerEntry(ledger, sym, mergedEntry.id);
    if (comparePrev && comparePrev.verdict !== mergedEntry.verdict) {
      const changedIndicators = detectChangedIndicators(comparePrev, mergedEntry);
      changes = appendSignalChange(changes, buildSignalChangeEntry(sym, comparePrev, mergedEntry, changedIndicators));
    }

    const { activeSignals, activeSignal } = processActiveSignals(activeRaw, sym, result, verdict, mergedEntry, comparePrev);

    await Promise.all([
      save("scan_ledger", ledger),
      save("signal_changes", changes),
      save("active_signals", activeSignals),
    ]);

    result.activeSignal = activeSignal;
    result.activeSignalDisplay = buildActiveSignalDisplay(activeSignal, result);
    result.previousScanCmf = comparePrev?.cmf ?? null;
    return result;
  } catch (e) {
    console.warn("scan_ledger merge failed:", e?.message || e);
    return result;
  }
}

// ─── SIGNAL ACCURACY — ANONYMOUS AGGREGATE TRACKING ─────────────────────────
// Writes anonymous signal events to signal_accuracy table.
// No user_id — purely for aggregate accuracy measurement across all users.
// Window: accum = 365 days, all other signals = 45 days.
async function logSignalAccuracy(result, verdict) {
  try {
    if (!verdict) return;
    const loggableLayers = ["buy", "recov", "accum", "bearish"];
    const loggableWait   = verdict.layer === "wait" && result.buyScore >= 9;
    if (!loggableLayers.includes(verdict.layer) && !loggableWait) return;

    const windowDays = verdict.layer === "accum" ? 365 : 45;
    const dedupDays  = 3;
    const cutoff     = new Date(Date.now() - dedupDays * 86400000).toISOString();
    const isBearish  = verdict.layer === "bearish";

    // For bearish signals: stop is 2×ATR ABOVE entry, targets are D1/D2 (below entry)
    const stopPrice = isBearish
      ? (result.atr ? result.price + 2 * result.atr : result.price * 1.08)
      : (result.atr ? result.price - 2 * result.atr : result.price * 0.92);
    const t1 = isBearish ? (result.d1 || null) : (result.t1 || null);
    const t2 = isBearish ? (result.d2 || null) : (result.t2 || null);

    const { data: existing } = await supabase
      .from("signal_accuracy")
      .select("id")
      .eq("symbol", result.symbol || "")
      .eq("verdict_short", verdict.short || verdict.label)
      .gte("logged_at", cutoff)
      .maybeSingle();
    if (existing) return;

    await supabase.from("signal_accuracy").insert({
      symbol:        result.symbol || "UNKNOWN",
      verdict:       verdict.label,
      verdict_short: verdict.short || verdict.label,
      verdict_layer: verdict.layer,
      score:         result.buyScore,
      gate:          result.gate?.label || null,
      entry_price:   result.price,
      stop_price:    stopPrice,
      t1,
      t2,
      rsi:           result.rsi || null,
      cmf:           result.cmf || null,
      squeeze:       result.bb?.isSqueeze || false,
      window_days:   windowDays,
      outcome:       "OPEN",
    });
  } catch {}
}

// Background outcome checker — runs on app load, checks OPEN signals >= 5 days old.
// Fetches current price via /api/quote, marks T1/T2 HIT, STOPPED OUT, or EXPIRED.
async function checkAndUpdateSignalAccuracy() {
  try {
    const checkAfterDays = 5;
    const cutoff = new Date(Date.now() - checkAfterDays * 86400000).toISOString();

    const { data: open } = await supabase
      .from("signal_accuracy")
      .select("*")
      .eq("outcome", "OPEN")
      .lt("logged_at", cutoff)
      .limit(30); // process max 30 per session — avoids hammering the API

    if (!open?.length) return;

    for (const row of open) {
      try {
        const res = await fetch(`/api/quote?symbol=${encodeURIComponent(row.symbol)}`);
        if (!res.ok) continue;
        const q = await res.json();
        const price = q.price;
        if (!price) continue;

        const daysElapsed = (Date.now() - new Date(row.logged_at).getTime()) / 86400000;
        const windowExpired = daysElapsed >= row.window_days;
        const isBearish = row.verdict_layer === "bearish";

        let outcome = "OPEN";
        let outcomePrice = price;
        let outcomePct = row.entry_price
          ? isBearish
            ? ((row.entry_price - price) / row.entry_price * 100) // short: profit when price falls
            : ((price - row.entry_price) / row.entry_price * 100)
          : null;

        if (isBearish) {
          // Short signal: stop = price above entry, targets = price below entry (D1/D2)
          if (row.stop_price && price >= row.stop_price) {
            outcome = "STOPPED OUT";
            outcomePrice = row.stop_price;
            outcomePct = row.entry_price ? ((row.entry_price - row.stop_price) / row.entry_price * 100) : null;
          } else if (row.t2 && price <= row.t2) {
            outcome = "D2 HIT ✓✓";
            outcomePrice = row.t2;
            outcomePct = row.entry_price ? ((row.entry_price - row.t2) / row.entry_price * 100) : null;
          } else if (row.t1 && price <= row.t1) {
            outcome = "D1 HIT ✓";
            outcomePrice = row.t1;
            outcomePct = row.entry_price ? ((row.entry_price - row.t1) / row.entry_price * 100) : null;
          } else if (windowExpired) {
            outcome = outcomePct >= 0 ? "EXPIRED — POSITIVE" : "EXPIRED — NEGATIVE";
          }
        } else {
          // Long signal: stop below entry, T1/T2 above entry
          if (row.stop_price && price <= row.stop_price) {
            outcome = "STOPPED OUT";
            outcomePrice = row.stop_price;
            outcomePct = row.entry_price ? ((row.stop_price - row.entry_price) / row.entry_price * 100) : null;
          } else if (row.t2 && price >= row.t2) {
            outcome = "T2 HIT ✓✓";
            outcomePrice = row.t2;
            outcomePct = row.entry_price ? ((row.t2 - row.entry_price) / row.entry_price * 100) : null;
          } else if (row.t1 && price >= row.t1) {
            outcome = "T1 HIT ✓";
            outcomePrice = row.t1;
            outcomePct = row.entry_price ? ((row.t1 - row.entry_price) / row.entry_price * 100) : null;
          } else if (windowExpired) {
            outcome = outcomePct >= 0 ? "EXPIRED — POSITIVE" : "EXPIRED — NEGATIVE";
          }
        }

        if (outcome !== "OPEN") {
          await supabase.from("signal_accuracy")
            .update({ outcome, outcome_pct: outcomePct, outcome_price: outcomePrice, outcome_checked_at: new Date().toISOString() })
            .eq("id", row.id);
        }

        await new Promise(r => setTimeout(r, 200)); // gentle rate limit
      } catch {} // skip bad rows
    }
  } catch {} // fully non-blocking
}

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
// ─── DUAL THEME SYSTEM ───────────────────────────────────────────────────────
const THEMES = {
  light: {
    bg:        "#d8d1ec",
    surface:   "#e2daf4",
    surfaceHi: "#d0c8e6",
    border:    "#b8b0d4",
    borderHi:  "#a49cc4",
    text:      "#0f172a",
    textSub:   "#475569",
    textDim:   "#64748b",
    green:     "#16a34a",
    greenDim:  "#dcfce7",
    red:       "#dc2626",
    redDim:    "#fee2e2",
    yellow:    "#d97706",
    yellowDim: "#fef3c7",
    blue:      "#2563eb",
    cyan:      "#7c3aed",   // violet — primary brand accent
    purple:    "#d946ef",   // pink — secondary brand accent
    orange:    "#ea580c",
  },
  dark: {
    bg:        "#000000",   // pure black — Option A
    surface:   "#0a0a0a",
    surfaceHi: "#111111",
    border:    "#1c1c1c",
    borderHi:  "#2a2a2a",
    text:      "#f0f0f0",
    textSub:   "#888888",
    textDim:   "#444444",
    green:     "#22c55e",
    greenDim:  "#14532d",
    red:       "#ef4444",
    redDim:    "#7f1d1d",
    yellow:    "#eab308",
    yellowDim: "#713f12",
    blue:      "#3b82f6",
    cyan:      "#a855f7",   // violet — brand accent only
    purple:    "#e879f9",   // pink — brand accent only
    orange:    "#f97316",
  },
};

// T is set dynamically in App — default export for non-component use
let T = THEMES.light;

// ─── FONT SIZES (more compact) ───────────────────────────────────────────────
const F = {
  xs:   11,
  sm:   12,
  base: 13,
  md:   14,
  lg:   16,
  xl:   20,
  xxl:  24,
};

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const makeGlobalCSS = (isDark) => `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; overflow: hidden; background: ${isDark ? "#000000" : "#d8d1ec"}; }
  body { font-family: 'Inter', system-ui, sans-serif; font-size: 13px; color: ${isDark ? "#f0f2ff" : "#0f172a"}; -webkit-font-smoothing: antialiased; }
  #root { background-image: ${isDark ? "radial-gradient(circle at 1px 1px, #1e2235 1px, transparent 0)" : "none"}; background-size: 32px 32px; }
  input, button { font-family: inherit; }
  input { outline: none !important; }
  input::placeholder { color: ${T.textDim}; }
  input.watchlist-add-ticker-input::placeholder { color: ${T.textSub}; opacity: 0.95; }
  ::-webkit-scrollbar { width: 6px; height: 6px; background: ${T.surface}; }
  ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
  @keyframes phasePulse { 0%,100%{opacity:1; box-shadow:0 0 0 0 currentColor} 50%{opacity:.85; box-shadow:0 0 6px 1px currentColor} }
  @media (max-width: 767px) {
    html, body, #root { overflow: auto; height: auto; min-height: 100%; }
    body { font-size: 14px; }
  }
  @keyframes fadeIn { from{opacity:0;transform:translateY(-6px)} to{opacity:1;transform:translateY(0)} }
  tr { border-bottom: 1px solid ${T.border}; }
  tr:hover td { background: ${T.surfaceHi} !important; }
`;

// ─── MATH ENGINE ──────────────────────────────────────────────────────────────
const sma = (arr, n) => arr.length < n ? null : arr.slice(0, n).reduce((s, v) => s + v, 0) / n;

function emaArr(arr, n) {
  const rev = [...arr].reverse();
  if (rev.length < n) return arr.map(() => null);
  const k = 2 / (n + 1), out = Array(n - 1).fill(null);
  let e = rev.slice(0, n).reduce((s, v) => s + v, 0) / n; out.push(e);
  for (let i = n; i < rev.length; i++) { e = rev[i] * k + e * (1 - k); out.push(e); }
  return out.reverse();
}

function calcRSI(closes, n = 14) {
  const rev = [...closes].reverse();
  if (rev.length < n + 1) return null;
  let ag = 0, al = 0;
  for (let i = 1; i <= n; i++) { const d = rev[i] - rev[i - 1]; if (d > 0) ag += d; else al -= d; }
  ag /= n; al /= n;
  for (let i = n + 1; i < rev.length; i++) {
    const d = rev[i] - rev[i - 1];
    ag = (ag * (n - 1) + Math.max(d, 0)) / n;
    al = (al * (n - 1) + Math.max(-d, 0)) / n;
  }
  return al === 0 ? 100 : 100 - 100 / (1 + ag / al);
}

function calcMACD(closes) {
  const e12 = emaArr(closes, 12), e26 = emaArr(closes, 26);
  const ml = closes.map((_, i) => e12[i] != null && e26[i] != null ? e12[i] - e26[i] : null).filter(v => v != null);
  if (ml.length < 9) return null;
  const sig = emaArr(ml, 9);
  const hist0 = ml[0] - sig[0];
  const hist1 = ml[1] != null && sig[1] != null ? ml[1] - sig[1] : null;
  const hist2 = ml[2] != null && sig[2] != null ? ml[2] - sig[2] : null;
  // Histogram momentum: growing = accelerating in current direction, shrinking = losing steam
  const histGrowing  = hist1 != null && Math.abs(hist0) > Math.abs(hist1); // magnitude expanding
  const histShrinking = hist1 != null && Math.abs(hist0) < Math.abs(hist1); // magnitude contracting
  const histAccelBull = hist0 > 0 && histGrowing;   // bullish and getting stronger
  const histAccelBear = hist0 < 0 && histGrowing;   // bearish and getting stronger
  const histFadingBull = hist0 > 0 && histShrinking; // bullish but losing momentum
  const histFadingBear = hist0 < 0 && histShrinking; // bearish but losing momentum
  return {
    macd: ml[0], signal: sig[0], histogram: hist0,
    bullish: ml[0] > sig[0], crossover: ml[0] > sig[0] && ml[1] <= sig[1],
    crossunder: ml[0] < sig[0] && ml[1] >= sig[1],
    histGrowing, histShrinking, histAccelBull, histAccelBear, histFadingBull, histFadingBear,
  };
}

function calcBB(closes, n = 20) {
  if (closes.length < 60) return null;
  const bwOf = sl => { const m = sl.reduce((s, v) => s + v, 0) / sl.length; const sd = Math.sqrt(sl.reduce((s, v) => s + (v - m) ** 2, 0) / sl.length); return (4 * sd) / m; };
  const days = Math.min(closes.length, 126), bws = [];
  for (let i = 0; i + n <= days; i++) bws.push(bwOf(closes.slice(i, i + n)));
  const cur = bws[0], mn = Math.min(...bws), mx = Math.max(...bws);
  const m = sma(closes, n), sd = Math.sqrt(closes.slice(0, n).reduce((s, v) => s + (v - m) ** 2, 0) / n);

  // IV Rank proxy — where current BB width sits in its 126-day range (0-100)
  // Low ivRank = volatility compressed = cheap options = good time to buy
  // High ivRank = volatility expanded = expensive options = avoid buying premium
  const ivRank = mx > mn ? Math.round(((cur - mn) / (mx - mn)) * 100) : 50;
  const ivLabel = ivRank <= 20 ? "LOW" : ivRank <= 40 ? "BELOW AVG" : ivRank <= 60 ? "AVERAGE" : ivRank <= 80 ? "ELEVATED" : "HIGH";
  const ivColor = ivRank <= 20 ? "#22c55e" : ivRank <= 40 ? "#86efac" : ivRank <= 60 ? "#94a3b8" : ivRank <= 80 ? "#f97316" : "#ef4444";
  const goodForBuying = ivRank <= 35; // IV on sale — good to buy calls/puts
  const avoidBuying   = ivRank >= 70; // IV too expensive — consider spreads instead

  return {
    upper: m + 2 * sd, lower: m - 2 * sd, middle: m,
    isSqueeze: cur <= mn * 1.12, wasSqueezing: bws[1] != null && bws[1] <= mn * 1.12,
    ivRank, ivLabel, ivColor, goodForBuying, avoidBuying, curWidth: cur, minWidth: mn, maxWidth: mx,
  };
}

function calcCMF(h, l, c, v, n = 20) {
  if (c.length < n) return null;
  let mfv = 0, vol = 0;
  for (let i = 0; i < n; i++) { const hl = h[i] - l[i]; if (!hl) continue; mfv += ((c[i] - l[i]) - (h[i] - c[i])) / hl * v[i]; vol += v[i]; }
  return vol ? mfv / vol : 0;
}

function calcCMFDiv(h, l, c, v, lookback = 14) {
  // Need enough bars to compare two CMF windows
  if (c.length < lookback * 2) return null;

  // Calculate CMF for recent window (bars 0..lookback-1) and prior window (bars lookback..lookback*2-1)
  const recentCMF  = calcCMF(h.slice(0, lookback), l.slice(0, lookback), c.slice(0, lookback), v.slice(0, lookback), lookback);
  const priorCMF   = calcCMF(h.slice(lookback, lookback * 2), l.slice(lookback, lookback * 2), c.slice(lookback, lookback * 2), v.slice(lookback, lookback * 2), lookback);
  if (recentCMF == null || priorCMF == null) return null;

  // Price: compare recent high/low vs prior high/low to determine trend
  const recentHigh = Math.max(...c.slice(0, lookback));
  const priorHigh  = Math.max(...c.slice(lookback, lookback * 2));
  const recentLow  = Math.min(...c.slice(0, lookback));
  const priorLow   = Math.min(...c.slice(lookback, lookback * 2));

  // Min delta to avoid noise — price must move at least 1%, CMF at least 0.04
  const priceDelta = 0.01;
  const cmfDelta   = 0.04;

  const priceHigher = recentHigh > priorHigh * (1 + priceDelta);
  const priceLower  = recentLow  < priorLow  * (1 - priceDelta);
  const cmfHigher   = recentCMF  > priorCMF  + cmfDelta;
  const cmfLower    = recentCMF  < priorCMF  - cmfDelta;

  // Bearish divergence: price higher high but CMF lower high — rally losing fuel
  const bearishDiv = priceHigher && cmfLower && recentCMF < 0.1;

  // Bullish divergence: price lower low but CMF higher low — sellers exhausted
  const bullishDiv = priceLower && cmfHigher && recentCMF > -0.1;

  if (!bearishDiv && !bullishDiv) return null;

  return {
    bullishDiv,
    bearishDiv,
    recentCMF: parseFloat(recentCMF.toFixed(3)),
    priorCMF:  parseFloat(priorCMF.toFixed(3)),
    label: bullishDiv
      ? `CMF Bullish Divergence — price made lower low but CMF improved (${priorCMF.toFixed(3)} → ${recentCMF.toFixed(3)}). Sellers exhausting.`
      : `CMF Bearish Divergence — price made higher high but CMF weakened (${priorCMF.toFixed(3)} → ${recentCMF.toFixed(3)}). Rally losing fuel.`,
    tooltip: bullishDiv
      ? `Price hit a lower low but money flow improved — institutions buying into weakness. Early reversal signal.`
      : `Price hit a higher high but institutional buying is fading. Breakout may not be supported by real volume.`,
  };
}

function calcVWAP(h, l, c, v, n = 20) {
  let tpv = 0, vol = 0;
  for (let i = 0; i < Math.min(n, c.length); i++) { tpv += ((h[i] + l[i] + c[i]) / 3) * v[i]; vol += v[i]; }
  return vol ? tpv / vol : null;
}

function calcATR(highs, lows, closes, n = 14) {
  if (closes.length < n + 1) return null;
  const trs = [];
  for (let i = 0; i < n && i < closes.length - 1; i++)
    trs.push(Math.max(highs[i] - lows[i], Math.abs(highs[i] - closes[i + 1]), Math.abs(lows[i] - closes[i + 1])));
  if (trs.length < n) return null;
  return trs.reduce((s, v) => s + v, 0) / n;
}

function calcVolDiv(closes, volumes) {
  if (closes.length < 12 || volumes.length < 12) return null;
  const rc = closes.slice(0, 5), oc = closes.slice(5, 10);
  const rv = volumes.slice(0, 5), ov = volumes.slice(5, 10);
  const priceUp = Math.max(...rc) >= Math.max(...oc) * 0.98;
  const avgNew = rv.reduce((s, v) => s + v, 0) / rv.length;
  const avgOld = ov.reduce((s, v) => s + v, 0) / ov.length;
  const ratio = avgNew / avgOld;
  return { bearish: priceUp && ratio < 0.80, bullish: !priceUp && ratio > 1.20, ratio };
}

function calcFibs(closes, n = 63, highs = null, lows = null) {
  const len = Math.min(n, closes.length);
  const high = highs?.length ? Math.max(...highs.slice(0, len)) : Math.max(...closes.slice(0, len));
  const low = lows?.length ? Math.min(...lows.slice(0, len)) : Math.min(...closes.slice(0, len));
  const range = high - low;
  if (!range) return null;
  return {
    high, low, range,
    fib236: high - range * 0.236,
    fib382: high - range * 0.382,
    fib500: high - range * 0.500,
    fib618: high - range * 0.618,
    fib786: high - range * 0.786,
  };
}

function calcPivots(highs, lows, closes, n = 20) {
  if (highs.length < n) return null;
  const h = Math.max(...highs.slice(0, n)), l = Math.min(...lows.slice(0, n)), c = sma(closes, n);
  if (!c) return null;
  const p = (h + l + c) / 3;
  return { pivot: p, r1: 2 * p - l, r2: p + (h - l), s1: 2 * p - h, s2: p - (h - l) };
}

function calcRSIDiv(closes, highs = null, lows = null, context = {}) {
  return computeContextAwareRsiDivergence(closes, highs || closes, lows || closes, context);
}

function calc52w(closes) {
  const yr = closes.slice(0, Math.min(252, closes.length));
  if (yr.length < 20) return null;
  const high = Math.max(...yr), low = Math.min(...yr), price = closes[0], range = high - low;
  const score = range > 0 ? Math.round(((price - low) / range) * 100) : 50;
  return { high, low, score, nearLow: score <= 20, nearHigh: score >= 80, distFromLow: ((price - low) / low) * 100, distFromHigh: ((price - high) / high) * 100 };
}

// 52W Breakout Signal — replaces legacy "near 52W high" proximity logic.
// Returns a score adjustment ONLY; all other scoring logic stays unchanged.
function calc52wBreakoutSignalScore(range52w, rvol) {
  const pct = range52w?.score;
  if (pct == null) return 0;
  const ratio = rvol?.ratio;
  if (ratio == null) {
    // If we don't have volume context, keep neutral unless deep in range (<50%).
    return pct < 50 ? -1 : 0;
  }

  // Price AT 52W high (98–100%) rules (override the generic >90% band)
  if (pct >= 98) {
    if (ratio > 1.5) return 3;      // breakout in progress, highest bonus
    if (ratio < 1.0) return -1;     // stalling at high, no conviction
    if (ratio >= 1.0 && ratio <= 1.5) return 1.5; // approaching, building
    return 0;
  }

  // Price > 90% of 52W range rules
  if (pct > 90) {
    if (ratio > 1.5) return 2.5;    // confirmed breakout setup
    if (ratio >= 1.0 && ratio <= 1.5) return 1.5; // approaching, building
    return 0;                       // < 1.0x = no volume conviction
  }

  // Mid-range and deep-range structure rules
  if (pct < 50) return -1;          // deep in range, weak structure
  return 0;                         // 50–90% neutral
}

function calc52wBreakoutSetup({ range52w, rvol, cmf, buyScore, rsi, counterTrendWeeklyCut }) {
  const pct = range52w?.score;
  const ratio = rvol?.ratio;
  const has = {
    inTop10: pct != null && pct > 90,
    rvolConfirmed: ratio != null && ratio > 1.5,
    cmfConfirmed: cmf != null && cmf > 0.05,
    scoreConfirmed: buyScore != null && buyScore > 6,
    rsiOk: rsi != null && rsi >= 45 && rsi <= 75,
    weeklyOk: !counterTrendWeeklyCut,
  };
  const active = Object.values(has).every(Boolean);
  const topPct = pct != null ? Math.max(0, 100 - pct) : null; // "top X% of range"
  return { active, pct, topPct, ratio, checks: has };
}

function calc3Day(closes) {
  if (closes.length < 6) return { postDrop: null, breakout: null, earningsGap: null, gapFillWatch: null };
  let postDrop = null;
  for (let i = 0; i < 3; i++) {
    const d = ((closes[i] - closes[i + 1]) / closes[i + 1]) * 100;
    if (d <= -4) { postDrop = { daysAgo: i + 1, cooldownLeft: 3 - i, dropPct: d, active: (3 - i) > 0 }; break; }
  }
  let breakout = null;
  if (closes.length >= 25) {
    const res = Math.max(...closes.slice(1, 21));
    if (closes[0] > res) {
      const streak = [0, 1, 2].filter(i => closes[i] > res).length;
      breakout = { level: res, streak, confirmed: streak >= 3 };
    }
  }
  let earningsGap = null;
  for (let i = 0; i < 3; i++) {
    const g = ((closes[i] - closes[i + 1]) / closes[i + 1]) * 100;
    if (Math.abs(g) >= 5) { earningsGap = { daysAgo: i + 1, pct: g, active: (3 - i) > 0, stabilLeft: 3 - i }; break; }
  }
  // Gap Fill Watch — bullish earnings gap (5%+) within last 5 days
  // Stocks that gap up on earnings typically drift higher for 3-5 sessions before retracing
  // Optimal entry: gap day close or first pullback to gap day close price
  let gapFillWatch = null;
  for (let i = 0; i < 5; i++) {
    if (i + 1 >= closes.length) break;
    const g = ((closes[i] - closes[i + 1]) / closes[i + 1]) * 100;
    if (g >= 5) {
      const daysAgo = i + 1;
      const gapPrice = closes[i + 1]; // price before gap (entry zone)
      const currentPrice = closes[0];
      const driftPct = ((currentPrice - closes[i]) / closes[i]) * 100; // drift since gap close
      const windowOpen = daysAgo <= 5; // still in drift window
      gapFillWatch = {
        daysAgo,
        gapPct: g,
        gapPrice,       // support level — the pre-gap close
        driftPct,       // how much it's moved since the gap
        windowOpen,     // still within 5-session drift window
        active: windowOpen && driftPct > -3, // not if it's already retracing hard
      };
      break;
    }
  }
  return { postDrop, breakout, earningsGap, gapFillWatch };
}

// ADX — Average Directional Index (trend strength, 14-period)
function calcADX(highs, lows, closes, n = 14) {
  if (closes.length < n + 2) return null;
  const trs = [], pdms = [], ndms = [];
  for (let i = 0; i < closes.length - 1; i++) {
    const h = highs[i], l = lows[i], ph = highs[i + 1], pl = lows[i + 1], pc = closes[i + 1];
    trs.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
    pdms.push(h - ph > pl - l && h - ph > 0 ? h - ph : 0);
    ndms.push(pl - l > h - ph && pl - l > 0 ? pl - l : 0);
  }
  const atr14 = trs.slice(0, n).reduce((s, v) => s + v, 0) / n;
  const pdi14 = (pdms.slice(0, n).reduce((s, v) => s + v, 0) / n) / atr14 * 100;
  const ndi14 = (ndms.slice(0, n).reduce((s, v) => s + v, 0) / n) / atr14 * 100;
  const dx = Math.abs(pdi14 - ndi14) / (pdi14 + ndi14) * 100;
  // Approximate ADX as smoothed DX
  const dxArr = [];
  for (let i = 0; i + n < trs.length; i++) {
    const atr = trs.slice(i, i + n).reduce((s, v) => s + v, 0) / n;
    const pdi = (pdms.slice(i, i + n).reduce((s, v) => s + v, 0) / n) / atr * 100;
    const ndi = (ndms.slice(i, i + n).reduce((s, v) => s + v, 0) / n) / atr * 100;
    dxArr.push(Math.abs(pdi - ndi) / (pdi + ndi + 0.001) * 100);
  }
  const adx = dxArr.slice(0, n).reduce((s, v) => s + v, 0) / n;
  return {
    adx, pdi: pdi14, ndi: ndi14,
    strong: adx >= 25,
    trending: adx >= 20,
    ranging: adx < 20,
    label: adx >= 40 ? "VERY STRONG" : adx >= 25 ? "STRONG TREND" : adx >= 20 ? "DEVELOPING" : "RANGING / CHOPPY",
    color: adx >= 25 ? T.green : adx >= 20 ? T.yellow : T.red,
  };
}

// Stochastic Oscillator (14, 3, 3)
function calcStoch(highs, lows, closes, kPeriod = 14, dPeriod = 3) {
  if (closes.length < kPeriod + dPeriod) return null;
  const kArr = [];
  for (let i = 0; i + kPeriod <= closes.length; i++) {
    const hSlice = highs.slice(i, i + kPeriod);
    const lSlice = lows.slice(i, i + kPeriod);
    const h = Math.max(...hSlice), l = Math.min(...lSlice);
    kArr.push(h === l ? 50 : ((closes[i] - l) / (h - l)) * 100);
  }
  const kSmooth = kArr.slice(0, dPeriod).reduce((s, v) => s + v, 0) / dPeriod; // %K smoothed
  const dLine = kArr.slice(0, dPeriod * 2).reduce((s, v) => s + v, 0) / (dPeriod * 2); // %D approx
  const prevK = kArr.slice(1, dPeriod + 1).reduce((s, v) => s + v, 0) / dPeriod;
  return {
    k: kSmooth, d: dLine,
    overbought: kSmooth > 80,
    oversold: kSmooth < 20,
    bullCross: kSmooth > dLine && prevK <= dLine,
    bearCross: kSmooth < dLine && prevK >= dLine,
    label: kSmooth > 80 ? "OVERBOUGHT" : kSmooth < 20 ? "OVERSOLD" : "NEUTRAL",
  };
}

// On Balance Volume
function calcOBV(closes, volumes) {
  if (closes.length < 10) return null;
  let obv = 0;
  const obvArr = [0];
  for (let i = 0; i < closes.length - 1; i++) {
    if (closes[i] > closes[i + 1]) obv += volumes[i];
    else if (closes[i] < closes[i + 1]) obv -= volumes[i];
    obvArr.push(obv);
  }
  // OBV trend: is OBV rising while price rises? (confirmation) or diverging?
  const recentOBV = obvArr.slice(0, 5).reduce((s, v) => s + v, 0) / 5;
  const priorOBV = obvArr.slice(5, 15).reduce((s, v) => s + v, 0) / 10;
  const recentPrice = closes.slice(0, 5).reduce((s, v) => s + v, 0) / 5;
  const priorPrice = closes.slice(5, 15).reduce((s, v) => s + v, 0) / 10;
  const obvRising = recentOBV > priorOBV;
  const priceRising = recentPrice > priorPrice;
  return {
    value: obv,
    rising: obvRising,
    confirming: obvRising === priceRising,
    bullishDiv: !priceRising && obvRising,   // price down, OBV up = hidden strength
    bearishDiv: priceRising && !obvRising,   // price up, OBV down = weak move
    label: obvRising ? "RISING" : "FALLING",
  };
}

function calcParabolicSAR(highs, lows, closes, af = 0.02, maxAF = 0.2) {
  if (closes.length < 10) return null;
  const rev = { h: [...highs].reverse(), l: [...lows].reverse(), c: [...closes].reverse() };
  let bull = rev.c[1] < rev.c[0]; // initial trend
  let sar = bull ? rev.l[0] : rev.h[0];
  let ep = bull ? rev.h[0] : rev.l[0];
  let curAF = af;

  for (let i = 1; i < Math.min(rev.c.length - 1, 30); i++) {
    sar = sar + curAF * (ep - sar);
    if (bull) {
      if (rev.l[i] < sar) { bull = false; sar = ep; ep = rev.l[i]; curAF = af; }
      else { if (rev.h[i] > ep) { ep = rev.h[i]; curAF = Math.min(curAF + af, maxAF); } }
    } else {
      if (rev.h[i] > sar) { bull = true; sar = ep; ep = rev.h[i]; curAF = af; }
      else { if (rev.l[i] < ep) { ep = rev.l[i]; curAF = Math.min(curAF + af, maxAF); } }
    }
  }
  return {
    sar, bullish: bull,
    label: bull ? "BULLISH (SAR below price)" : "BEARISH (SAR above price)",
    stopLevel: sar,
  };
}

// Relative Volume (RVOL) — today vs 20-day avg
function calcRVOL(volumes, n = 20) {
  if (volumes.length < n + 1) return null;
  const avgVol = volumes.slice(1, n + 1).reduce((s, v) => s + v, 0) / n;
  const todayVol = volumes[0];
  const ratio = avgVol > 0 ? todayVol / avgVol : 1;
  return {
    today: todayVol, avg: avgVol, ratio,
    surge: ratio >= 2.0,
    high: ratio >= 1.5,
    normal: ratio >= 0.7 && ratio < 1.5,
    low: ratio < 0.7,
    label: ratio >= 2.0 ? "SURGE (2x+)" : ratio >= 1.5 ? "HIGH (1.5x+)" : ratio >= 0.7 ? "NORMAL" : "LOW",
    color: ratio >= 2.0 ? T.green : ratio >= 1.5 ? T.cyan : ratio < 0.7 ? T.red : T.textSub,
  };
}


// Williams %R — momentum oscillator, excellent for mean reversion -80 to -20 transitions
function calcWilliamsR(highs, lows, closes, n = 14) {
  if (closes.length < n) return null;
  const highN = Math.max(...highs.slice(0, n));
  const lowN  = Math.min(...lows.slice(0, n));
  const range = highN - lowN;
  if (range === 0) return null;
  const R = ((highN - closes[0]) / range) * -100; // 0 to -100
  // Previous reading for transition detection
  const highP = highs.length >= n + 1 ? Math.max(...highs.slice(1, n + 1)) : highN;
  const lowP  = lows.length  >= n + 1 ? Math.min(...lows.slice(1, n + 1))  : lowN;
  const rangeP = highP - lowP;
  const Rprev  = rangeP > 0 ? ((highP - closes[1]) / rangeP) * -100 : R;
  const oversold   = R <= -80;
  const overbought = R >= -20;
  const prevOversold   = Rprev <= -80;
  const prevOverbought = Rprev >= -20;
  // Key transitions: crossing OUT of oversold (-80→-20) = bullish, OUT of overbought = bearish
  const bullCross = prevOversold && !oversold;   // crossed above -80 from below (mean reversion buy)
  const bearCross = prevOverbought && !overbought; // crossed below -20 from above (reversal sell)
  return {
    value: Math.round(R * 10) / 10,
    oversold, overbought, bullCross, bearCross,
    label: oversold ? "OVERSOLD" : overbought ? "OVERBOUGHT" : "NEUTRAL",
    color: oversold ? T.green : overbought ? T.red : T.textSub,
    tooltip: `Williams %R: ${Math.round(R)}. Range -100 to 0. Below -80 = oversold. Above -20 = overbought. The most powerful signal is the TRANSITION — when %R crosses from -80 back toward -20, price has found a floor and momentum is recovering.`,
  };
}

// Volume Breakout Confirmation — validates breakouts vs fake-outs
function calcVolumeBreakout(closes, highs, lows, volumes, bb, ema21) {
  if (!volumes || volumes.length < 20 || !closes.length) return null;
  const price = closes[0];
  const avgVol20 = volumes.slice(1, 21).reduce((s, v) => s + v, 0) / 20;
  const todayVol = volumes[0];
  const volRatio = avgVol20 > 0 ? todayVol / avgVol20 : 1;
  // Detect breakout scenarios
  const breakAboveBB    = bb && price > bb.upper;
  const breakAboveEMA21 = ema21 && closes[1] < ema21 && price >= ema21; // yesterday below, today above
  const breakAbove52W20 = highs.length >= 20 && price >= Math.max(...highs.slice(1, 20)); // new 20-day high
  const isBreakout      = breakAboveBB || breakAboveEMA21 || breakAbove52W20;
  // Volume thresholds for breakout validation
  const volConfirmed = isBreakout && volRatio >= 1.5;  // breakout WITH volume — valid
  const volFakeout   = isBreakout && volRatio < 0.85;  // breakout WITHOUT volume — suspect
  const volSurgeNoBreakout = !isBreakout && volRatio >= 2.0; // volume surge but no breakout — watch
  return {
    isBreakout, volConfirmed, volFakeout, volSurgeNoBreakout,
    breakType: breakAboveEMA21 ? "EMA21 Cross" : breakAboveBB ? "BB Upper Break" : breakAbove52W20 ? "20D High Break" : null,
    volRatio: Math.round(volRatio * 100) / 100,
    label: volConfirmed ? "CONFIRMED" : volFakeout ? "FAKEOUT RISK" : volSurgeNoBreakout ? "VOL SURGE" : isBreakout ? "BREAKOUT" : "—",
    color: volConfirmed ? T.green : volFakeout ? T.red : volSurgeNoBreakout ? T.yellow : T.textSub,
    tooltip: `Volume breakout validation. ${isBreakout ? `Breakout detected (${breakAboveEMA21 ? "EMA21 cross" : breakAboveBB ? "above BB upper" : "20-day high"}). Volume is ${volRatio.toFixed(1)}x average — ${volConfirmed ? "CONFIRMED: volume supports the breakout." : "FAKEOUT RISK: low volume on breakout is a warning sign — often fails."}` : volSurgeNoBreakout ? `Volume spike ${volRatio.toFixed(1)}x average without a breakout — potential accumulation or news.` : `No breakout. Volume ${volRatio.toFixed(1)}x normal.`}`,
  };
}

// Higher Highs / Higher Lows — trend structure
function calcTrendStructure(highs, lows, closes, n = 10) {
  if (closes.length < n * 2) return null;
  const recent = { h: Math.max(...highs.slice(0, n)), l: Math.min(...lows.slice(0, n)) };
  const prior = { h: Math.max(...highs.slice(n, n * 2)), l: Math.min(...lows.slice(n, n * 2)) };
  const hh = recent.h > prior.h; // higher high
  const hl = recent.l > prior.l; // higher low
  const lh = recent.h < prior.h; // lower high
  const ll = recent.l < prior.l; // lower low
  let structure, color;
  if (hh && hl) { structure = "UPTREND (HH+HL)"; color = T.green; }
  else if (lh && ll) { structure = "DOWNTREND (LH+LL)"; color = T.red; }
  else if (hh && ll) { structure = "EXPANDING"; color = T.yellow; }
  else if (lh && hl) { structure = "CONTRACTING"; color = T.cyan; }
  else { structure = "MIXED"; color = T.textSub; }
  return { hh, hl, lh, ll, structure, color, bullish: hh && hl, bearish: lh && ll };
}

const isPureCrypto = sym => typeof sym === "string" && sym.endsWith("-USD"); // spot crypto only — no earnings, no SPY RS


// ─── WEEKLY TREND (resampled from daily bars — no extra API call) ──────────────
// Takes daily closes array and computes weekly EMA21 + trend direction
// Used to filter out setups where daily is bullish but weekly is bearish
function calcWeeklyTrend(closes) {
  if (!closes || closes.length < 40) return null;
  // Resample: take every 5th bar as weekly close (approx)
  const weekly = [];
  for (let i = 0; i < closes.length - 4; i += 5) {
    weekly.push(closes[i]); // most recent close of each ~week
  }
  if (weekly.length < 10) return null;
  const wEma21 = emaArr(weekly, Math.min(21, weekly.length - 1))[0];
  const wEma8  = emaArr(weekly, Math.min(8,  weekly.length - 1))[0];
  const wPrice = weekly[0];
  if (!wEma21 || !wEma8) return null;
  const aboveEma21 = wPrice > wEma21;
  const aboveEma8  = wPrice > wEma8;
  const ema8AboveEma21 = wEma8 > wEma21;
  // Weekly trend strength
  const strongUptrend  = aboveEma21 && aboveEma8 && ema8AboveEma21;
  const weakUptrend    = aboveEma21 && !aboveEma8;
  const downtrend      = !aboveEma21;
  const label = strongUptrend ? "STRONG UP" : weakUptrend ? "WEAK UP" : "DOWN";
  const wMacd = weekly.length >= 35 ? calcMACD(weekly) : null;
  const weeklyBullish = aboveEma21;
  const weeklyBearish = !aboveEma21 && downtrend;
  const weeklyNeutral = !weeklyBullish && !weeklyBearish;
  return { wPrice, wEma21, wEma8, aboveEma21, aboveEma8, ema8AboveEma21, strongUptrend, weakUptrend, downtrend, label, wMacd, weeklyBullish, weeklyBearish, weeklyNeutral };
}

function bbBandwidthAtOffset(closes, offset, n = 20) {
  const sl = closes.slice(offset, offset + n);
  if (sl.length < n) return null;
  const m = sl.reduce((s, v) => s + v, 0) / n;
  const sd = Math.sqrt(sl.reduce((s, v) => s + (v - m) ** 2, 0) / n);
  return m > 0 ? (4 * sd) / m : null;
}

function cmfAtOffset(highs, lows, closes, volumes, offset, win = 20) {
  if (!highs || !lows || !closes || !volumes) return null;
  return calcCMF(highs.slice(offset), lows.slice(offset), closes.slice(offset), volumes.slice(offset), win);
}

function obvCumulativeLF(closes, volumes) {
  if (!closes?.length || !volumes?.length) return null;
  const c = [...closes].reverse();
  const v = [...volumes].reverse();
  let cum = 0;
  const cumArr = [cum];
  for (let i = 1; i < c.length; i++) {
    if (c[i] > c[i - 1]) cum += v[i] || 0;
    else if (c[i] < c[i - 1]) cum -= v[i] || 0;
    cumArr.push(cum);
  }
  return cumArr.reverse();
}

function rvolAtOffset(volumes, offset, n = 20) {
  if (!volumes || volumes.length < offset + n + 1) return null;
  const avg = volumes.slice(offset + 1, offset + 1 + n).reduce((s, x) => s + (x || 0), 0) / n;
  const t = volumes[offset] || 0;
  return avg > 0 ? t / avg : null;
}

const SECTOR_ETF_NAMES = { XLK: "Tech", XLC: "Comm Services", XLY: "Discretionary", IGV: "Software", XLF: "Financials", XLV: "Healthcare", XLE: "Energy", XLI: "Industrials", XLRE: "Real Estate", SMH: "Semis", SOXX: "Semis" };

function computeEarlyWarningPhases(ctx) {
  const {
    closes, highs, lows, volumes, bb, pivots, range52w, cmf, obv, rvol, rs, rsVsSector,
    symbol, optionsFlow, phaseContext, pureCrypto, price, stockReturn63,
  } = ctx;

  const empty = {
    phaseScore: 0,
    phase1Bull: false,
    phase1Bear: false,
    phase2Bull: false,
    phase2Bear: false,
    phase1Any: false,
    phase2Any: false,
    labels: [],
    timeline: [],
    timelineLines: [],
    signals: {},
    sectorLeadBull: false,
    sectorLeadBear: false,
    progressionNote: null,
  };
  if (pureCrypto || !closes || closes.length < 65) return empty;

  const labels = [];
  let score = 0;
  const timeline = [];
  const signals = {};
  const breakdown = {
    accumulation: 0,
    rsLeader: 0,
    distribution: 0,
    preBreakout: 0,
    optionsFlowShift: 0,
    preBreakdown: 0,
    sectorRotation: 0,
  };

  const cmf0 = cmf;
  const cmf1 = cmfAtOffset(highs, lows, closes, volumes, 1);
  const cmf2 = cmfAtOffset(highs, lows, closes, volumes, 2);
  const cmf3 = cmfAtOffset(highs, lows, closes, volumes, 3);
  const cmfRising3 = cmf0 != null && cmf1 != null && cmf2 != null && cmf3 != null && cmf0 > cmf1 && cmf1 > cmf2 && cmf2 > cmf3;
  const cmfFalling3 = cmf0 != null && cmf1 != null && cmf2 != null && cmf3 != null && cmf0 < cmf1 && cmf1 < cmf2 && cmf2 < cmf3;

  const obvLF = obvCumulativeLF(closes, volumes);
  const obvHigherLows7 = (() => {
    if (!obvLF || obvLF.length < 15) return false;
    const a = Math.min(...obvLF.slice(0, 7));
    const b = Math.min(...obvLF.slice(7, 14));
    return a > b;
  })();
  const obvLowerHighs7 = (() => {
    if (!obvLF || obvLF.length < 15) return false;
    const a = Math.max(...obvLF.slice(0, 7));
    const b = Math.max(...obvLF.slice(7, 14));
    return a < b;
  })();

  const price5d = closes[5] ? ((closes[0] - closes[5]) / closes[5]) * 100 : null;
  const price3d = closes[3] ? ((closes[0] - closes[3]) / closes[3]) * 100 : null;
  const priceFlatOrDown = price5d != null && price5d <= 0.6;
  const priceFlatOrUp = price5d != null && price5d >= -0.6;

  let upVol = 0, downVol = 0;
  for (let i = 0; i < 7; i++) {
    const c0 = closes[i], c1 = closes[i + 1];
    const v0 = volumes[i] || 0;
    if (c0 < c1) downVol += v0;
    else if (c0 > c1) upVol += v0;
  }
  const volBullRatio = downVol > 0 ? upVol / downVol : (upVol > 0 ? 99 : 0);
  const volBearRatio = upVol > 0 ? downVol / upVol : (downVol > 0 ? 99 : 0);

  const bws = [];
  for (let i = 0; i < 25; i++) bws.push(bbBandwidthAtOffset(closes, i, 20));
  const bbNarrow5 = bws[0] != null && bws[1] != null && bws[2] != null && bws[3] != null && bws[4] != null
    && bws[0] < bws[1] && bws[1] < bws[2] && bws[2] < bws[3] && bws[3] < bws[4];
  const bbNarrow5Bear = bbNarrow5;

  const accumulation = cmfRising3 && cmf0 < 0.05 && obvHigherLows7 && priceFlatOrDown && volBullRatio > 1.3 && bbNarrow5;
  if (accumulation) {
    score += 2;
    breakdown.accumulation += 2;
    labels.push("🔍 ACCUMULATION DETECTED");
    signals.accumulation = true;
    timeline.push({ phase: 1, text: "Phase 1: ✅ Accumulation pattern active", ok: true });
  } else {
    timeline.push({ phase: 1, text: "Phase 1: ⏳ Accumulation not fully aligned (CMF/OBV/vol/BB width)", ok: false });
    signals.accumulation = false;
  }

  const spy5d = phaseContext?.spy5dPct;
  const stock5dPct = closes[5] ? ((closes[0] - closes[5]) / closes[5]) * 100 : null;
  const spy5dOld = phaseContext?.spy5dPctAt5 ?? null;
  const rsSpreadNow = stock5dPct != null && spy5d != null ? stock5dPct - spy5d : null;
  const rsSpreadThen = stock5dPct != null && spy5dOld != null ? (closes[5] && closes[10] ? (((closes[5] - closes[10]) / closes[10]) * 100) - spy5dOld : null) : null;
  const rsImproving5 = rsSpreadNow != null && rsSpreadThen != null && rsSpreadNow > rsSpreadThen + 0.15;
  const sector3d = phaseContext?.sector3dPct;
  const sectorFlatOrDown = sector3d == null || sector3d <= 0.35;
  const rsLeader = rsImproving5 && stock5dPct != null && stock5dPct <= 0.4 && sectorFlatOrDown;
  if (rsLeader) {
    score += 2;
    breakdown.rsLeader += 2;
    labels.push("💪 RS LEADER — Holding vs sector weakness");
    signals.rsLeader = true;
  } else {
    signals.rsLeader = false;
  }

  const distribution = cmfFalling3 && cmf0 > -0.05 && obvLowerHighs7 && priceFlatOrUp && volBearRatio > 1.3 && bbNarrow5Bear;
  if (distribution) {
    score -= 2;
    breakdown.distribution -= 2;
    labels.push("🔍 DISTRIBUTION DETECTED");
    signals.distribution = true;
    timeline.push({ phase: 1, text: "Phase 1: ✅ Distribution pressure building", ok: true });
  } else {
    signals.distribution = false;
    if (!accumulation) timeline.push({ phase: 1, text: "Phase 1: ⏳ No distribution cluster", ok: false });
  }

  const bws20 = bws.slice(0, 20).filter(v => v != null);
  const tightestSqueeze = bws20.length >= 10 && bws[0] != null && Math.abs(bws[0] - Math.min(...bws20)) < 1e-8;

  const resistance = pivots?.r1 && range52w?.high
    ? (Math.abs(price - pivots.r1) / pivots.r1 < Math.abs(price - range52w.high) / range52w.high ? pivots.r1 : range52w.high)
    : (pivots?.r1 || range52w?.high);
  const nearRes3 = resistance ? [0, 1, 2].every(i => {
    const p = closes[i];
    return Math.abs(p - resistance) / resistance < 0.011;
  }) : false;

  const rvolTrendUp5 = [0, 1, 2, 3, 4].every(i => {
    const a = rvolAtOffset(volumes, i);
    const b = rvolAtOffset(volumes, i + 1);
    return a != null && b != null && a > b * 0.998;
  });
  const cmfCrossUp3 = cmf0 != null && cmf0 > 0 && [1, 2, 3].some(off => {
    const prev = cmfAtOffset(highs, lows, closes, volumes, off);
    return prev != null && prev <= 0;
  });

  const preBreakout = !!(tightestSqueeze && nearRes3 && rvolTrendUp5 && cmfCrossUp3 && bb?.isSqueeze);
  if (preBreakout) {
    score += 3;
    breakdown.preBreakout += 3;
    labels.push("⚡ PRE-BREAKOUT COILING");
    signals.preBreakout = true;
    timeline.push({ phase: 2, text: "Phase 2: ✅ Pre-breakout coiling", ok: true });
  } else {
    signals.preBreakout = false;
    timeline.push({ phase: 2, text: "Phase 2: ⏳ Pre-breakout coiling not active", ok: false });
  }

  const of = optionsFlow;
  const optionsFlowBull = !!(of?.eligible !== false && of?.putCallOiRatio != null && of.putCallOiRatio < 0.72 && (of.otmCallOiShare ?? 0) >= 0.34);
  if (optionsFlowBull) {
    score += 2;
    breakdown.optionsFlowShift += 2;
    labels.push("📊 CALL FLOW INCREASING");
    signals.optionsFlow = true;
  } else {
    signals.optionsFlow = false;
  }

  const supportLvl = pivots?.s1 ?? (highs.length >= 15 ? Math.min(...lows.slice(0, 15)) : null);
  const nearSup3 = supportLvl ? [0, 1, 2].every(i => {
    const p = closes[i];
    return Math.abs(p - supportLvl) / supportLvl < 0.011;
  }) : false;
  const rvolDownDaysUp = (() => {
    let ok = true, cnt = 0;
    for (let i = 0; i < 5; i++) {
      if (closes[i] < closes[i + 1]) {
        const a = rvolAtOffset(volumes, i);
        const b = rvolAtOffset(volumes, i + 1);
        if (a != null && b != null && a > b) cnt++;
      }
    }
    return cnt >= 3;
  })();
  const cmfCrossDown3 = cmf0 != null && cmf0 < 0 && [1, 2, 3].some(off => {
    const prev = cmfAtOffset(highs, lows, closes, volumes, off);
    return prev != null && prev >= 0;
  });
  const preBreakdown = !!(tightestSqueeze && nearSup3 && rvolDownDaysUp && cmfCrossDown3 && bb?.isSqueeze && (price5d ?? 0) < 0);
  if (preBreakdown) {
    score -= 3;
    breakdown.preBreakdown -= 3;
    labels.push("⚡ PRE-BREAKDOWN COILING");
    signals.preBreakdown = true;
    timeline.push({ phase: 2, text: "Phase 2: ✅ Pre-breakdown coiling", ok: true });
  } else {
    signals.preBreakdown = false;
  }

  const spy3d = phaseContext?.spy3dPct;
  const stock3dFlat = price3d != null && Math.abs(price3d) < 0.85;
  const sectorOutperf = spy3d != null && sector3d != null && (sector3d - spy3d) > 1.5;
  const sectorLag = spy3d != null && sector3d != null && (spy3d - sector3d) > 1.5;
  const etfName = SECTOR_ETF_NAMES[phaseContext?.sectorEtf] || phaseContext?.sectorEtf || "Sector";
  if (sectorOutperf && stock3dFlat) {
    score += 2;
    breakdown.sectorRotation += 2;
    labels.push(`🏦 SECTOR LEADING — ${etfName} rotating in`);
    signals.sectorLeadingBull = true;
    signals.sectorLeadBull = true;
  } else if (sectorLag && stock3dFlat && price5d != null && price5d >= -0.5) {
    score -= 2;
    breakdown.sectorRotation -= 2;
    labels.push(`🏦 SECTOR LEADING — ${etfName} rotating out`);
    signals.sectorLeadingBear = true;
    signals.sectorLeadBear = true;
  } else {
    signals.sectorLeadingBull = false;
    signals.sectorLeadingBear = false;
  }

  score = Math.max(-9, Math.min(9, score));

  const phase1Any = accumulation || rsLeader || distribution;
  const phase2Any = preBreakout || optionsFlowBull || preBreakdown || signals.sectorLeadingBull || signals.sectorLeadingBear;

  const timelineLines = [
    ...timeline.map(t => t.text),
    `Phase 3: ⏳ Entry requires existing calls/puts confirmation (CMF, gate, etc.)`,
  ];

  return {
    phaseScore: score,
    phase1Bull: accumulation || rsLeader,
    phase1Bear: distribution,
    phase2Bull: preBreakout || optionsFlowBull || !!signals.sectorLeadingBull,
    phase2Bear: preBreakdown || !!signals.sectorLeadingBear,
    phase1Any,
    phase2Any,
    labels,
    timeline,
    timelineLines,
    signals,
    breakdown,
    sectorLeadBull: !!signals.sectorLeadingBull,
    sectorLeadBear: !!signals.sectorLeadingBear,
    progressionNote: null,
  };
}

/** Short label appended after direction-aware phase badge when a trade setup type is active */
function setupTypeSuffix(result, phaseKind) {
  const t = result?.activeSetupType;
  const map = {
    BULL_FLAG: "🚩 BULL FLAG",
    DEAD_CAT_BOUNCE: "🐱 DEAD CAT BOUNCE",
    RANGE_BREAKOUT: "💥 RANGE BREAKOUT",
    SUPPORT_BOUNCE: "🔄 SUPPORT BOUNCE",
    GAP_AND_GO: "⬆ GAP AND GO",
    GAP_FILL: "↩ GAP FILL",
    EARNINGS_DRIFT: "📈 EARNINGS DRIFT",
  };
  if (t === "VCP") {
    return result?.tradeSetups?.vcp?.breakout_confirmed ? "💥 VCP BREAKOUT" : "🔄 VCP FORMING";
  }
  if (t && map[t]) return map[t];
  if (t === "MOMENTUM" && result?.signal === "BREAKDOWN" && phaseKind === "PUTS_READY") return "🔻 BREAKDOWN";
  return "";
}

function derivePhaseUi(result) {
  if (!result?.earlyWarning) return null;
  const s = typeof result.buyScore === "number" ? result.buyScore : null;
  const inChop = s != null && s >= -3 && s <= 3;
  // Phase column should be empty in CHOP / blocked.
  if (inChop) return null;
  if (typeof result.price === "number" && result.price < 1) return null;
  const rsi = typeof result.rsi === "number" ? result.rsi : null;
  const cmf = typeof result.cmf === "number" ? result.cmf : null;
  const hardCallsBlocked = cmf != null && cmf < -0.10;
  const hardPutsBlocked = (rsi != null && rsi <= 20) || (cmf != null && cmf > 0.10);
  // Direction gating: only show CALLS phases when score > +3, only PUTS phases when score < -3.
  const allowCalls = (s != null && s > 3) && !hardCallsBlocked;
  const allowPuts = (s != null && s < -3) && !hardPutsBlocked;
  if (!allowCalls && !allowPuts) return null;
  const ew = result.earlyWarning;
  const cs = result.callsSignal;
  const ps = result.putsSignal;
  const ck = !!result.catchingKnivesCallsBlock;
  const dcbActive = result?.deadCatBounce?.eligible === true && (result.deadCatBounce.phase1 || result.deadCatBounce.phase2 || result.deadCatBounce.phase3 || result.deadCatBounce.bounce_trigger);

  const build = (row) => {
    const suf = setupTypeSuffix(result, row.kind);
    return {
      ...row,
      label: row.label,
      setupSuffix: suf || "",
    };
  };

  if (allowCalls && cs?.fullSignal && !ck) {
    return build({ kind: "CALLS_READY", label: "🚀 CALLS READY", short: "READY", flash: true, pulse: false, color: T.green, border: T.green });
  }
  if (allowCalls && cs?.bouncePlayOnly) {
    return build({ kind: "CALLS_READY", label: "🚀 CALLS READY", short: "READY", flash: true, pulse: false, color: T.green, border: T.green });
  }
  if (allowPuts && ps?.fullSignal) {
    return build({ kind: "PUTS_READY", label: "💀 PUTS READY", short: "PUTS", flash: true, pulse: false, color: T.red, border: T.red });
  }
  if (allowCalls && ew.phase2Bull) {
    return build({ kind: "CALLS_SETUP", label: "⚡ CALLS SETUP", short: "SETUP", flash: false, pulse: true, color: "#ca8a04", border: "#eab308" });
  }
  if (allowPuts && ew.phase2Bear) {
    return build({ kind: "PUTS_SETUP", label: "🔻 PUTS SETUP", short: "SETUP", flash: false, pulse: true, color: "#b91c1c", border: "#ef4444" });
  }
  if (allowPuts && ew.phase1Bear) {
    return build({ kind: "PUTS_WATCH", label: "👁 PUTS WATCH", short: "WATCH", flash: false, pulse: false, color: "#ea580c", border: "#fb923c" });
  }
  if (allowCalls && ew.phase1Bull) {
    return build({ kind: "CALLS_WATCH", label: dcbActive ? "👁 CALLS WATCH — DCB" : "👁 CALLS WATCH", short: "WATCH", flash: false, pulse: false, color: "#2563eb", border: "#60a5fa" });
  }
  return null;
}

/** First checklist condition that failed; used for GATED pill tooltip (display-only). */
function firstFailedOptionsCondition(signal) {
  return signal?.conditions?.find(c => !c.pass) ?? null;
}

/** True when full signal is off because pre-score hard gates failed (first failure is Hard gates line). */
function hardGatesBlockedReason(signal) {
  if (!signal || signal.fullSignal) return null;
  const fail = firstFailedOptionsCondition(signal);
  if (!fail?.label?.includes("Hard gates")) return null;
  return fail.label;
}

function calcActionSignalUi({ symbol, item, result, price, bp, atrStop }) {
  if (!result) return null;
  if (result.price != null && result.price < 1) {
    return { kind: "HARD_BLOCK", label: "⛔ Penny stock", color: T.textDim, bg: `${T.border}22`, border: T.border, flash: false, reason: "⚠ PENNY STOCK — price below $1 (signals suppressed)" };
  }
  const d = resolveDisplayVerdict(result, { inPosition: !!item?.inPosition, activeSignal: result?.activeSignal });
  const s = displayVerdictChipStyle(d.verdictStyle);
  return {
    kind: "DISPLAY",
    label: d.actionText,
    color: s.color,
    bg: s.bg,
    border: s.border,
    flash: false,
    reason: `${d.verdict} — ${d.subtitle}`,
  };
}

function calcSignalActionCombinedUi({ result, inPosition = false }) {
  if (!result) return null;
  const price = typeof result.price === "number" ? result.price : null;
  if (price != null && price < 1) {
    return { text: "⛔ Penny Stock", color: T.textDim, bg: `${T.border}22`, border: T.border, tip: "Penny stock filter — signals suppressed under $1.", showPill: false, isDash: false };
  }
  if (isScoringV2Result(result)) {
    normalizeScanResult(result);
    const short = v2WatchlistActionShort(result);
    const s = displayVerdictChipStyle(
      short.tone === "go" ? "bullish"
      : short.tone === "blocked" ? "blocked"
      : short.tone === "pending" || short.tone === "warn" ? "warning"
      : "neutral"
    );
    return {
      text: short.text,
      isDash: false,
      tip: short.tip,
      color: s.color,
      bg: s.bg,
      border: s.border,
      flash: false,
      pulse: false,
      showPill: false,
      setupLabel: null,
      setupPillStyle: null,
    };
  }
  const phase = derivePhaseUi(result);
  const cf = watchlistConfluenceCell(result);
  const gate = result.gate?.label;
  const setupType = result.activeSetupType || result.confluence?.setupType;
  const setupLabel = phase?.setupSuffix?.replace(/^ · /, "") || setupType || null;

  let text = "—";
  let color = T.textDim;
  let bg = `${T.border}22`;
  let border = T.border;
  let flash = false;
  let pulse = false;
  let tip = ACTION_COLUMN_TIP;

  if (phase) {
    text = phase.label;
    color = phase.color;
    bg = `${phase.color}18`;
    border = phase.border;
    flash = !!phase.flash;
    pulse = !!phase.pulse;
    tip = `${phase.label} — options phase timing.${setupLabel ? ` Setup: ${setupLabel}.` : ""}`;
  } else if (cf?.text && cf.text !== "—") {
    text = cf.text;
    color = cf.tone === "warn" ? T.orange : cf.tone === "breakout" ? T.cyan : T.green;
    bg = `${color}18`;
    border = `${color}55`;
    tip = confluenceCellTooltip(cf);
  } else if (gate && gate !== "—") {
    text = gate === "GO" ? "✓ GATE GO" : `GATE ${gate}`;
    color = gate === "GO" ? T.green : T.orange;
    bg = `${color}18`;
    border = `${color}44`;
    tip = result.gate?.reason || "Entry gate status — GO means timing filters passed.";
  }

  const isDash = text === "—";
  return {
    text,
    isDash,
    tip,
    color,
    bg,
    border,
    flash,
    pulse,
    showPill: !!setupLabel && !!phase,
    setupLabel,
    setupPillStyle: setupLabel ? { color: T.textSub, bg: `${T.border}33`, border: T.border } : null,
  };
}

/** Watchlist / scanner filters: match rows that show a bullish phase badge */
function matchCallsPhaseFilter(result, readyOnly) {
  const d = resolveDisplayVerdict(result, { inPosition: result?.inPosition, activeSignal: result?.activeSignal });
  if (!d.showCallsBadge) return false;
  const ui = derivePhaseUi(result);
  if (!ui) return false;
  if (readyOnly) return ui.kind === "CALLS_READY";
  return ui.kind === "CALLS_WATCH" || ui.kind === "CALLS_SETUP" || ui.kind === "CALLS_READY";
}

/** Watchlist / scanner filters: match rows that show a bearish phase badge */
function matchPutsPhaseFilter(result, readyOnly) {
  const d = resolveDisplayVerdict(result, { inPosition: result?.inPosition, activeSignal: result?.activeSignal });
  if (!d.showPutsBadge) return false;
  const ui = derivePhaseUi(result);
  if (!ui) return false;
  if (readyOnly) return ui.kind === "PUTS_READY";
  return ui.kind === "PUTS_WATCH" || ui.kind === "PUTS_SETUP" || ui.kind === "PUTS_READY";
}

function buildDeadCatPriceStory(symbol, dcb, earnDays) {
  if (!dcb || !symbol) return "";
  const sym = symbol.toUpperCase();
  const pct = dcb.declinePct != null ? Math.abs(dcb.declinePct).toFixed(1) : "—";
  const hi = dcb.high20 != null ? dcb.high20.toFixed(2) : "—";
  const nDays = dcb.daysFromHigh ?? 20;
  let text = `${sym} has fallen ${pct}% from its ${nDays}-day high of $${hi} over the last ${nDays} days.\n\n`;
  if (dcb.exhaustion_detected && dcb.climax_ratio != null) {
    text += `Volume spiked ${dcb.climax_ratio.toFixed(1)} times normal on a recent session, which can mean sellers are running out of steam.\n\n`;
  }
  if (dcb.bullish_divergence) {
    text += `Oversold momentum is making higher lows while price made a lower low — often an early sign a bounce may be building.\n\n`;
  }
  if (dcb.wick_rejection) {
    text += `The last few sessions show buyers pushing price up from the lows (long lower shadows).\n\n`;
  }
  if (dcb.bounce_trigger && dcb.rvol0 != null) {
    text += `A bounce-style session appeared recently with ${dcb.rvol0.toFixed(1)} times normal volume on an up day.\n\n`;
  }
  const hasSetup = dcb.phase1 || dcb.phase2 || dcb.phase3;
  if (hasSetup && dcb.bounceT1 != null && dcb.bounceT2 != null && dcb.bounceStop != null) {
    text += `SETUP: Possible bounce toward $${dcb.bounceT1.toFixed(2)} (Bounce T1, first recovery zone) or $${dcb.bounceT2.toFixed(2)} (Bounce T2, deeper recovery). A stop could sit near $${dcb.bounceStop.toFixed(2)}.\n\n`;
  } else {
    text += `No bounce-style setup is active right now.\n\n`;
  }
  if (typeof earnDays === "number" && earnDays >= 0 && earnDays <= 7) {
    text += `Earnings are in about ${earnDays} day${earnDays === 1 ? "" : "s"} — many traders wait until after the report for clarity.\n\n`;
  }
  if (dcb.warnings?.includes?.("cmf_penalty")) {
    text += `Money flow is still weak, so any bounce score was pulled back slightly.\n\n`;
  }
  if (dcb.warnings?.includes?.("rsi_phase1_block")) {
    text += `The dip is not deep enough on the main oversold reading to count the early bounce phase toward the score.\n\n`;
  }
  if (dcb.blocked && (dcb.warnings?.includes?.("earnings") || dcb.warnings?.includes?.("tide"))) {
    text += `Bounce-related score was not added — market or calendar conditions blocked it.\n\n`;
  }
  if (Array.isArray(dcb.labels) && dcb.labels.length > 0) {
    text += `${dcb.labels.join("\n")}\n\n`;
  }
  return text.trim();
}

/** Additive trade setups from raw.tradeSetups (api/scan.js + tradeSetups.js). Single winner by priority; bonus applied to buyScore. */
function applyTradeSetupsMerge(raw, ctx) {
  const {
    buyScore: bsIn, tideScore, cmf, rsi, earnDays, momentumBeforeDeadCat,
    deadCatBounce, pureCrypto, rawDcb, price, range52w,
  } = ctx;
  const t = raw?.tradeSetups;
  if (!t || pureCrypto) {
    return {
      buyScore: bsIn,
      activeSetupType: "MOMENTUM",
      setupBonus: 0,
      setupLabels: [],
      setupStory: "",
      tradeSetups: t || null,
    };
  }
  const dcb = rawDcb || raw?.deadCatBounce;
  const deadAny = !!(dcb && (dcb.phase1 || dcb.phase2 || dcb.phase3));

  const gap = t.gap || {};
  const ed = t.earningsDrift || {};
  const bf = t.bullFlag || {};
  const rb = t.rangeBreakout || {};
  const sb = t.supportBounce || {};
  const vcp = t.vcp || {};

  const earn = typeof earnDays === "number" ? earnDays : null;

  const gapGoBlock = rsi != null && rsi > 80
    || (cmf != null && cmf < 0)
    || (typeof tideScore === "number" && tideScore < -2)
    || (earn != null && earn >= 0 && earn <= 1);
  const gapFillBlock = (cmf != null && cmf > 0)
    || (rsi != null && rsi < 40)
    || (typeof tideScore === "number" && tideScore > 3);

  const edBlock = (cmf != null && cmf < 0)
    || (typeof tideScore === "number" && tideScore < -2)
    || ed.drift_failed;

  const bfBlockE = earn != null && earn >= 0 && earn <= 7;
  const bfBlockT = typeof tideScore === "number" && tideScore < -2;
  const bfBreakBlockCmf = bf.breakout_confirmed && cmf != null && cmf < 0;

  const rbBlockT = typeof tideScore === "number" && tideScore < -1;
  let rbPenalty = 0;
  if (earn != null && earn >= 0 && earn <= 14) rbPenalty = -2;

  const sbBlock = (typeof tideScore === "number" && tideScore < -3)
    || (cmf != null && cmf < -0.05)
    || (rsi != null && rsi < 35)
    || deadAny
    || !sb.in_uptrend;

  const candidates = [];

  if (vcp.active) {
    const vb = Math.min(10, typeof vcp.scoreProvisional === "number" ? vcp.scoreProvisional : 0);
    candidates.push({
      type: "VCP",
      priority: 0,
      bonus: vb,
      labels: vcp.breakout_confirmed ? ["💥 VCP BREAKOUT"] : ["🔄 VCP FORMING"],
    });
  }
  if (gap.gap_and_go && !gapGoBlock) {
    let b = 4;
    if (earn != null && earn >= 0 && earn <= 3) b += 2;
    candidates.push({ type: "GAP_AND_GO", priority: 1, bonus: b, labels: gap.labels || [] });
  }
  if (gap.gap_fill_setup && !gapFillBlock) {
    candidates.push({ type: "GAP_FILL", priority: 2, bonus: gap.scoreProvisional || -4, labels: gap.labels || [] });
  }
  if (ed.active && !ed.drift_failed && !edBlock) {
    candidates.push({ type: "EARNINGS_DRIFT", priority: 3, bonus: ed.scoreProvisional || 0, labels: ed.labels || [] });
  }
  if (bf.breakout_confirmed && !bfBlockE && !bfBlockT && !bfBreakBlockCmf) {
    candidates.push({ type: "BULL_FLAG", priority: 4, bonus: bf.scoreProvisional || 0, labels: bf.labels || [] });
  }
  if (rb.range_breakout_confirmed && !rbBlockT) {
    candidates.push({ type: "RANGE_BREAKOUT", priority: 5, bonus: (rb.scoreProvisional || 0) + rbPenalty, labels: rb.labels || [] });
  }
  if (sb.bounce_trigger && sb.active && !sbBlock) {
    candidates.push({ type: "SUPPORT_BOUNCE", priority: 6, bonus: sb.scoreProvisional || 0, labels: sb.labels || [] });
  }
  if (bf.valid_pole && bf.valid_flag && !bf.breakout_confirmed && !bfBlockE && !bfBlockT) {
    candidates.push({ type: "BULL_FLAG", priority: 7, bonus: Math.min(3, bf.scoreProvisional || 0), labels: bf.labels?.filter(l => l.includes("FORMING")) || [] });
  }
  if (rb.tight_range && rb.long_consolidation && !rb.range_breakout_confirmed && !rbBlockT) {
    const b = (rb.scoreProvisional || 0) + rbPenalty;
    if (b !== 0) candidates.push({ type: "RANGE_BREAKOUT", priority: 8, bonus: b, labels: rb.labels || [] });
  }
  if (sb.active && !sb.bounce_trigger && !sbBlock) {
    candidates.push({ type: "SUPPORT_BOUNCE", priority: 9, bonus: Math.min(4, sb.scoreProvisional || 0), labels: sb.labels || [] });
  }

  candidates.sort((a, b) => a.priority - b.priority);
  const win = candidates[0];
  let bonus = win ? win.bonus : 0;
  let activeSetupType = win ? win.type : "MOMENTUM";
  let setupLabels = win ? win.labels : [];

  if (momentumBeforeDeadCat >= 6 && ["BULL_FLAG", "RANGE_BREAKOUT", "SUPPORT_BOUNCE"].includes(activeSetupType)) {
    bonus = 0;
    activeSetupType = "MOMENTUM";
    setupLabels = [];
  }

  let buyScore = Math.round((bsIn + bonus) * 2) / 2;
  buyScore = Math.max(-15, Math.min(15, buyScore));

  const setupStory = buildTradeSetupWinnerStory(ctx.symbol || "", t, activeSetupType, { price, range52w, rsi, cmf, earnDays: earn });

  return {
    buyScore,
    activeSetupType,
    setupBonus: bonus,
    setupLabels,
    setupStory,
    tradeSetups: { ...t, _winner: win || null },
  };
}

function buildTradeSetupWinnerStory(symbol, t, type, o) {
  if (!type || type === "MOMENTUM") return "";
  const sym = (symbol || "").toUpperCase();
  const g = t.gap || {};
  const ed = t.earningsDrift || {};
  const bf = t.bullFlag || {};
  const rb = t.rangeBreakout || {};
  const sb = t.supportBounce || {};
  const vcp = t.vcp || {};
  if (type === "VCP") {
    const tag = vcp.breakout_confirmed ? "Volatility contraction resolved to the upside with volume." : "Volatility is contracting and volume is drying up — classic VCP coiling.";
    const pivot = vcp.pivotR1 != null ? `$${vcp.pivotR1}` : "the pivot band";
    return `${sym} — ${tag} Coil high/low (20D) near $${vcp.coilHigh20 ?? "—"} / $${vcp.coilLow20 ?? "—"}. Pivot area ${pivot}. ${vcp.breakout_confirmed ? `Measured targets near $${vcp.vcpT1 ?? "—"} / $${vcp.vcpT2 ?? "—"}` : `Watch for a high-volume close through the coil top; targets on confirmation near $${vcp.vcpT1 ?? "—"} / $${vcp.vcpT2 ?? "—"}`}, stop idea near $${vcp.vcpStop ?? "—"}.`;
  }
  if (type === "GAP_AND_GO") {
    return `${sym} gapped up about ${g.gap_pct != null ? Math.abs(g.gap_pct).toFixed(1) : "—"}% at the open on elevated volume. The gap lines up with the trend and money flow is supportive. Targets near $${g.gapT1 ?? "—"} and $${g.gapT2 ?? "—"} with a stop idea near $${g.gapStop ?? "—"} below the gap area.`;
  }
  if (type === "GAP_FILL") {
    return `${sym} gapped up but the move looks weak (light volume, against the larger trend). Traders often watch for a move back toward the prior close near $${g.yesterdayClose ?? "—"}.`;
  }
  if (type === "EARNINGS_DRIFT") {
    return `${sym} recently reported earnings; estimates were beaten and price has held above the report-day area. Flow remains positive (CMF ${o.cmf != null ? o.cmf.toFixed(2) : "—"}). Drift targets near $${ed.driftT1 ?? "—"} / $${ed.driftT2 ?? "—"}, stop near $${ed.driftStop ?? "—"}.`;
  }
  if (type === "BULL_FLAG") {
    return `${sym} showed a sharp rally (the pole) then a tight pullback. ${bf.breakout_confirmed ? "Price broke the flag with volume — follow-through levels near $" + (bf.flagT1 ?? "—") + " and $" + (bf.flagT2 ?? "—") + "." : "The flag is still forming — watch for a breakout with volume."} Stop idea near $${bf.flagStop ?? "—"}.`;
  }
  if (type === "RANGE_BREAKOUT") {
    return `${sym} compressed in a tight range (${rb.narrowDays ?? "—"} narrow sessions) then ${rb.range_breakout_confirmed ? "broke out on strong volume" : "is still coiling"}. Range targets near $${rb.rangeT1 ?? "—"} / $${rb.rangeT2 ?? "—"}, stop near $${rb.rangeStop ?? "—"}.`;
  }
  if (type === "SUPPORT_BOUNCE") {
    return `${sym} pulled back about ${sb.pullback_pct ?? "—"}% in an uptrend and is testing support (${sb.support_count ?? 0} nearby levels). ${sb.bounce_trigger ? "A reclaim with volume fired." : "Watch for a clean reclaim."} Targets $${sb.sbT1 ?? "—"} / $${sb.sbT2 ?? "—"}, stop $${sb.sbStop ?? "—"}.`;
  }
  return "";
}

function buildUnifiedPriceStory(symbol, deadCatStory, tradeSetup, unifiedDefault) {
  if (tradeSetup?.setupStory) return tradeSetup.setupStory;
  if (deadCatStory) return deadCatStory;
  return unifiedDefault || "";
}

function ScoreBreakdownSection({ result, verdict: drawerVerdict = null, inPosition = false, position = null, currentPrice = null }) {
  const sb = result?.scoreBreakdown;
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  const fmt = (v, d = 2) => (typeof v === "number" && Number.isFinite(v) ? v.toFixed(d) : "—");
  const fmtPct = (v, d = 1) => (typeof v === "number" && Number.isFinite(v) ? `${v >= 0 ? "+" : ""}${v.toFixed(d)}%` : "—");
  const fmtScore = (v) => (typeof v === "number" && Number.isFinite(v) ? `${v > 0 ? "+" : ""}${v}` : "—");
  const scoreColor = (v) => (typeof v !== "number" || !Number.isFinite(v)) ? T.textDim : v > 0 ? T.green : v < 0 ? T.red : T.textDim;
  const multColor = T.yellow;

  const table = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 11,
  };
  const th = {
    textAlign: "left",
    color: T.textDim,
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: "0.08em",
    padding: "6px 8px",
    borderBottom: `1px solid ${T.border}`,
  };
  const td = {
    padding: "6px 8px",
    borderBottom: `1px solid ${T.border}`,
    color: T.textSub,
    verticalAlign: "top",
  };

  const gates = result?.gates;
  const hardBlocks = (() => {
    const out = [];
    if (result?.catchingKnivesCallsBlock?.active) out.push({ name: "Catching Knives", reason: result.catchingKnivesCallsBlock.reason || "High downside momentum / knife-catching risk" });
    if (result?.threeDayRule?.postDrop?.active) out.push({ name: "Post-drop cooldown", reason: `Drop ${result.threeDayRule.postDrop.dropPct?.toFixed(1) ?? "—"}% — cooldown ${result.threeDayRule.postDrop.cooldownLeft ?? "—"} session(s)` });
    if (result?.threeDayRule?.earningsGap?.active) out.push({ name: "Earnings gap stabilization", reason: `${result.threeDayRule.earningsGap.stabilLeft ?? "—"} day(s) remaining` });
    if (result?.volClimax?.blockCalls) out.push({ name: "Volume climax", reason: result.volClimax.why || "Climax selling risk" });
    return out;
  })();

  const verdict = (() => {
    const s = result?.buyScore;
    if (typeof s !== "number") return { label: "—", color: T.textDim, bg: T.surface };
    if (s >= 12) return { label: "POWER PLAY", color: T.yellow, bg: `${T.yellow}12` };
    if (s >= 6) return { label: "STANDARD LONG", color: T.green, bg: `${T.green}12` };
    if (s >= 4) return { label: "SPECULATIVE", color: T.cyan, bg: `${T.cyan}10` };
    if (s <= -12) return { label: "BREAKDOWN", color: T.orange, bg: `${T.orange}12` };
    if (s <= -6) return { label: "STANDARD SHORT", color: T.red, bg: `${T.red}12` };
    if (s <= -4) return { label: "SCALP ONLY", color: "#fb923c", bg: "#fb923c12" };
    return { label: "CHOP / MIXED", color: T.textDim, bg: T.surface };
  })();

  return (
    <details open style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12 }}>
      <summary style={{ listStyle: "none", cursor: "pointer", padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 900, color: T.text, letterSpacing: "0.02em" }}>📊 Score Breakdown</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ ...mono, fontSize: 12, fontWeight: 900, color: scoreColor(result?.buyScore) }}>{fmtScore(result?.buyScore)}</span>
          <span style={{ fontSize: 10, fontWeight: 900, color: verdict.color, background: verdict.bg, border: `1px solid ${verdict.color}55`, padding: "3px 8px", borderRadius: 999, letterSpacing: "0.06em" }}>{verdict.label}</span>
        </div>
      </summary>

      <div style={{ padding: "0 14px 12px 14px" }}>
        {/* SECTION 1 — CORE INDICATORS */}
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", margin: "8px 0 6px 0" }}>CORE INDICATORS</div>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Indicator</th>
                <th style={th}>Value</th>
                <th style={{ ...th, textAlign: "right" }}>Score</th>
              </tr>
            </thead>
            <tbody>
              {[
                { k: "MACD (display only)", v: result?.macd ? (result.macd.crossover ? "Bull cross ▲" : result.macd.crossunder ? "Bear cross ▼" : result.macd.bullish ? "Bull ▲" : "Bear ▼") : "—", s: null },
                { k: "RSI", v: result?.rsi != null ? result.rsi.toFixed(1) : "—", s: sb?.core?.rsiCore },
                { k: "CMF Strength", v: result?.cmf != null ? result.cmf.toFixed(3) : "—", s: sb?.core?.cmfCore },
                { k: "RS vs SPY", v: fmtPct(result?.rsVsSpy10), s: sb?.core?.rsSpyCore },
                { k: "BB Squeeze", v: result?.bb?.isSqueeze ? "Active" : "None", s: sb?.core?.bbCore },
                { k: "Sector Rotation", v: (typeof sb?.core?.sectorRotationCore === "number" ? (sb.core.sectorRotationCore > 0 ? "Hot" : sb.core.sectorRotationCore < 0 ? "Cold" : "Neutral") : "—"), s: sb?.core?.sectorRotationCore },
                { k: "OBV Divergence", v: result?.obv?.bullishDiv ? "Bullish" : result?.obv?.bearishDiv ? "Bearish" : "None", s: sb?.core?.obvDivScore },
                { k: "Weekly Trend", v: result?.weeklyTrend?.weeklyBearish ? "Bearish" : result?.weeklyTrend?.weeklyBullish ? "Bullish" : (result?.weeklyTrend?.label || "—"), mult: result?.weeklyTrendMultiplier },
              ].map((r) => (
                <tr key={r.k}>
                  <td style={{ ...td, color: T.text }}>{r.k}</td>
                  <td style={td}>{r.v ?? "—"}</td>
                  <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: r.mult != null ? multColor : scoreColor(r.s) }}>
                    {r.mult != null ? `×${fmt(r.mult, 2)}` : fmtScore(r.s)}
                  </td>
                </tr>
              ))}
              {(result?.rsiDiv?.bullish || result?.rsiDiv?.bearish) && (
                <tr>
                  <td style={{ ...td, color: T.text }}>RSI Divergence</td>
                  <td style={td}>
                    {result.rsiDiv.bullish ? "✅ Bullish divergence" : "⚠ Bearish divergence"}
                  </td>
                  <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: scoreColor(sb?.core?.rsiDivScore) }}>
                    {fmtScore(sb?.core?.rsiDivScore)}
                  </td>
                </tr>
              )}
              {result?.confluence?.active && result.confluence.bonus !== 0 && (
                <tr>
                  <td style={{ ...td, color: T.text }}>Confluence</td>
                  <td style={td}>{result.confluence.label || result.confluence.setupType}</td>
                  <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: scoreColor(sb?.core?.confluenceScore) }}>
                    {fmtScore(sb?.core?.confluenceScore)}
                  </td>
                </tr>
              )}
              {result?.fibLevel != null && (
                <tr>
                  <td style={{ ...td, color: T.text }}>Fibonacci</td>
                  <td style={td}>
                    {result.fibLevel}
                    {result.fibBounce ? " · bounce ✓" : ""}
                  </td>
                  <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: scoreColor(sb?.core?.fibonacciScore) }}>
                    {fmtScore(sb?.core?.fibonacciScore)}
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ ...td, borderBottom: "none", color: T.textDim, fontWeight: 800 }} colSpan={2}>Core Score subtotal</td>
                <td style={{ ...td, ...mono, borderBottom: "none", textAlign: "right", fontWeight: 900, color: scoreColor(sb?.core?.subtotal) }}>{fmtScore(sb?.core?.subtotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* SECTION 2 — PHASE SYSTEM */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", margin: "8px 0 6px 0" }}>PHASE SYSTEM</div>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Signal</th>
                <th style={{ ...th, textAlign: "right" }}>Score</th>
              </tr>
            </thead>
            <tbody>
              {[
                { k: "Accumulation Detection", s: sb?.phaseSystem?.accumulation },
                { k: "RS Leader", s: sb?.phaseSystem?.rsLeader },
                { k: "Pre-Breakout Coiling", s: sb?.phaseSystem?.preBreakout },
                { k: "Options Flow Shift", s: sb?.phaseSystem?.optionsFlowShift },
                { k: "Phase Progression Bonus", s: sb?.phaseSystem?.phaseProgressionBonus },
              ].map((r) => (
                <tr key={r.k}>
                  <td style={{ ...td, color: T.text }}>{r.k}</td>
                  <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: scoreColor(r.s) }}>{fmtScore(r.s)}</td>
                </tr>
              ))}
              <tr>
                <td style={{ ...td, borderBottom: "none", color: T.textDim, fontWeight: 800 }}>Phase Subtotal</td>
                <td style={{ ...td, ...mono, borderBottom: "none", textAlign: "right", fontWeight: 900, color: scoreColor(sb?.phaseSystem?.subtotal) }}>{fmtScore(sb?.phaseSystem?.subtotal)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* SECTION 3 — SETUP DETECTION */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", margin: "8px 0 6px 0" }}>SETUP DETECTION</div>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Setup</th>
                <th style={{ ...th, textAlign: "right" }}>Score</th>
              </tr>
            </thead>
            <tbody>
              {sb?.setup?.activeSetupType ? (
                <>
                  <tr>
                    <td style={{ ...td, color: T.text }}>{sb.setup.activeSetupType}</td>
                    <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: scoreColor(sb?.setup?.setupBonus) }}>{fmtScore(sb?.setup?.setupBonus)}</td>
                  </tr>
                  <tr>
                    <td style={{ ...td, color: T.textSub }}>
                      {(sb?.setup?.setupLabels?.length ? sb.setup.setupLabels.join(" · ") : "Component breakdown —")}
                    </td>
                    <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: T.textDim }}>—</td>
                  </tr>
                </>
              ) : (
                <tr>
                  <td style={{ ...td, color: T.textDim }}>No setup detected</td>
                  <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: T.textDim }}>—</td>
                </tr>
              )}
              <tr>
                <td style={{ ...td, borderBottom: "none", color: T.textDim, fontWeight: 800 }}>Setup Subtotal</td>
                <td style={{ ...td, ...mono, borderBottom: "none", textAlign: "right", fontWeight: 900, color: scoreColor(sb?.setup?.setupBonus) }}>{sb?.setup?.activeSetupType ? fmtScore(sb?.setup?.setupBonus) : "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* SECTION — SCAN MEASUREMENTS (consolidation / EMA / RS) */}
        {sb?.measurements && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", margin: "8px 0 6px 0" }}>SCAN MEASUREMENTS</div>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>System</th>
                  <th style={th}>Detail</th>
                  <th style={{ ...th, textAlign: "right" }}>Score</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={td}>Consolidation</td>
                  <td style={td}>{result?.consolidation?.active ? `${result.consolidation.type} · ${result.consolidation.days}d` : "None"}</td>
                  <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: scoreColor(sb.measurements.consolidation) }}>{fmtScore(sb.measurements.consolidation)}</td>
                </tr>
                <tr>
                  <td style={td}>8/20 EMA</td>
                  <td style={td}>{result?.emaAlignment?.alignment || "—"}{result?.emaAlignment ? ` · ${emaAlignmentDisplay(result.emaAlignment).text}` : ""}</td>
                  <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: scoreColor(sb.measurements.ema) }}>{fmtScore(sb.measurements.ema)}</td>
                </tr>
                <tr>
                  <td style={td}>Relative Strength</td>
                  <td style={td}>
                    {result?.relativeStrength?.rs_rating != null ? `RS ${result.relativeStrength.rs_rating}` : "—"}
                    {result?.relativeStrength?.rs_line_new_high ? " · ⭐ RS HIGH" : result?.relativeStrength?.rs_line_trend ? ` · ${result.relativeStrength.rs_line_trend}` : ""}
                  </td>
                  <td style={{ ...td, ...mono, textAlign: "right", fontWeight: 900, color: scoreColor(sb.measurements.rs) }}>{fmtScore(sb.measurements.rs)}</td>
                </tr>
                <tr>
                  <td style={{ ...td, borderBottom: "none", fontWeight: 800, color: T.textDim }} colSpan={2}>Measurements subtotal</td>
                  <td style={{ ...td, ...mono, borderBottom: "none", textAlign: "right", fontWeight: 900, color: scoreColor(sb.measurements.subtotal) }}>{fmtScore(sb.measurements.subtotal)}</td>
                </tr>
              </tbody>
            </table>
            {result?.relativeStrength && (
              <div style={{ marginTop: 8, padding: "10px 10px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 11, lineHeight: 1.7 }}>
                <div style={{ fontWeight: 800, color: T.textSub, marginBottom: 4 }}>Relative Strength detail</div>
                {[
                  ["3d vs SPY", result.relativeStrength.vs_spy_3d],
                  ["10d vs SPY", result.relativeStrength.vs_spy_10d],
                  ["21d vs SPY", result.relativeStrength.vs_spy_21d],
                  ["63d vs SPY", result.relativeStrength.vs_spy_63d],
                  [`Sector (${result.relativeStrength.sector_etf || "—"})`, result.relativeStrength.vs_sector_5d],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <span style={{ color: T.textDim }}>{label}</span>
                    <span style={{ ...mono, fontWeight: 700, color: val != null && val >= 0 ? T.green : val != null ? T.red : T.textDim }}>
                      {val != null ? `${val >= 0 ? "+" : ""}${val.toFixed(1)}%` : "—"}
                    </span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 4 }}>
                  <span style={{ color: T.textDim }}>RS Rating</span>
                  <span style={{ ...mono, fontWeight: 800 }}>{result.relativeStrength.rs_rating ?? "—"} / 99</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ color: T.textDim }}>RS Line</span>
                  <span style={{ fontWeight: 800, color: result.relativeStrength.rs_line_new_high ? "#fbbf24" : T.textSub }}>
                    {result.relativeStrength.rs_line_new_high ? "⭐ AT NEW HIGH" : (result.relativeStrength.rs_line_trend || "—")}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* SECTION 4 — MULTIPLIERS */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", margin: "8px 0 6px 0" }}>MULTIPLIERS (STEP BY STEP)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "6px 10px", padding: "10px 10px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 11 }}>
            <div style={{ color: T.textSub }}>Score before weekly</div>
            <div style={{ ...mono, fontWeight: 900, color: scoreColor(sb?.multipliers?.buyScoreBeforeWeekly) }}>{fmtScore(sb?.multipliers?.buyScoreBeforeWeekly)}</div>

            <div style={{ color: T.textSub }}>Weekly multiplier</div>
            <div style={{ ...mono, fontWeight: 900, color: multColor }}>×{fmt(sb?.multipliers?.weeklyTrendMultiplier, 2)}</div>

            {sb?.multipliers?.weeklyBearishTideOverrideLabel && (
              <>
                <div style={{ color: T.textDim, fontSize: 10, gridColumn: "1 / span 2" }}>{sb.multipliers.weeklyBearishTideOverrideLabel}</div>
              </>
            )}

            <div style={{ color: T.textSub }}>Score after weekly</div>
            <div style={{ ...mono, fontWeight: 900, color: scoreColor(sb?.multipliers?.buyScoreAfterWeekly) }}>{fmtScore(sb?.multipliers?.buyScoreAfterWeekly)}</div>

            <div style={{ color: T.textSub, marginTop: 6 }}>Score before tide</div>
            <div style={{ ...mono, fontWeight: 900, color: scoreColor(sb?.multipliers?.buyScoreBeforeTide), marginTop: 6 }}>{fmtScore(sb?.multipliers?.buyScoreBeforeTide)}</div>

            <div style={{ color: T.textSub }}>Tide multiplier</div>
            <div style={{ ...mono, fontWeight: 900, color: multColor }}>×{fmt(sb?.multipliers?.tideMultiplier, 2)}</div>

            <div style={{ color: T.textDim, fontSize: 10, gridColumn: "1 / span 2" }}>
              ({sb?.multipliers?.tideBand || "—"} breadthScore: {sb?.multipliers?.breadthScore ?? "—"})
            </div>

            <div style={{ color: T.textSub }}>Score after tide</div>
            <div style={{ ...mono, fontWeight: 900, color: scoreColor(sb?.multipliers?.buyScoreAfterTide) }}>{fmtScore(sb?.multipliers?.buyScoreAfterTide)}</div>

            <div style={{ color: T.textSub, marginTop: 6 }}>Macro adjustment</div>
            <div style={{ ...mono, fontWeight: 900, color: T.textDim, marginTop: 6 }}>—</div>

            <div style={{ color: T.textDim, fontSize: 10, gridColumn: "1 / span 2" }}>
              (Sector RS: {sb?.multipliers?.sectorEtf || "—"} — {sb?.multipliers?.sectorLabel || "—"} · display only, not scored)
            </div>
          </div>
        </div>

        {/* SECTION 5 — FINAL SCORE */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", margin: "8px 0 6px 0" }}>FINAL SCORE</div>
          <div style={{ padding: "12px 12px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 900, color: T.text, letterSpacing: "0.04em" }}>FINAL SCORE</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ ...mono, fontSize: 18, fontWeight: 900, color: scoreColor(result?.buyScore) }}>{fmtScore(result?.buyScore)}</div>
              <div style={{ fontSize: 10, fontWeight: 900, color: verdict.color, background: verdict.bg, border: `1px solid ${verdict.color}55`, padding: "4px 10px", borderRadius: 999, letterSpacing: "0.08em" }}>
                {verdict.label}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 6 — PRE-TRADE CHECKLIST */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", margin: "8px 0 2px 0" }}>PRE-TRADE CHECKLIST</div>
          <div style={{ fontSize: 10, color: T.textDim, marginBottom: 6 }}>Review before entering position</div>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Gate</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                const st = computeSectorTide(result, result.symbol);
                const sectorTideRow = (() => {
                  if (!st) {
                    const sym = result.symbol?.toUpperCase();
                    const skipReason = SECTOR_TIDE_SKIP.has(sym)
                      ? "Index ETF — sector tide N/A (this is the market benchmark)"
                      : isCrypto(result.symbol)
                      ? "Crypto — no sector tide concept"
                      : !TICKER_SECTOR_MAP[sym]
                      ? "No sector mapping for this ticker"
                      : !result.phaseContext
                      ? "Phase context missing — rescan to populate sector tide"
                      : "Sector ETF data unavailable";
                    return { k: "Sector Tide", icon: "—", status: "N/A", detail: skipReason, color: T.textDim };
                  }
                  const pctStr = `${st.sectorETF} ${st.pct5d >= 0 ? "+" : ""}${st.pct5d.toFixed(1)}% over ${st.timeframe === "5d" ? "5" : "3"} sessions`;
                  if (st.band === "NEUTRAL") {
                    return { k: "Sector Tide", icon: "✅", status: "NEUTRAL", detail: pctStr, color: T.green };
                  }
                  if (st.band === "HEADWIND" || st.band === "TAILWIND_AGAINST_SHORTS") {
                    return { k: "Sector Tide", icon: "⚠", status: st.band.replace(/_/g, " "), detail: pctStr, color: "#fbbf24" };
                  }
                  return { k: "Sector Tide", icon: "✗", status: st.band.replace(/_/g, " "), detail: pctStr, color: T.red };
                })();
                const earningsWindowRow = (() => {
                  const line = getEarningsDrawerLine(result, result?.symbol);
                  const pill = getEarningsRowPill(result, result?.symbol);
                  if (pill) {
                    const icon = pill.level === "red" ? "⚠" : "⏱";
                    const color = pill.level === "red" ? T.red : "#fbbf24";
                    return { k: "Earnings", icon, status: "Info", detail: pill.text, color };
                  }
                  if (line) {
                    return { k: "Earnings", icon: "ℹ", status: "Info", detail: line.text, color: T.textDim };
                  }
                  return { k: "Earnings", icon: "—", status: "N/A", detail: "No upcoming earnings date (equity)", color: T.textDim };
                })();
                const signalAgeRow = (() => {
                  const sa = result?.signalAge;
                  if (!sa) {
                    return { k: "Signal Age", icon: "—", status: "N/A", detail: "No open signal logged for this ticker", color: T.textDim, preStale: null };
                  }
                  const n = sa.ageDays;
                  const pct = sa.pctStr ?? `${sa.pctMove >= 0 ? "+" : ""}${sa.pctMove?.toFixed(1)}%`;
                  const base = `${n} session${n === 1 ? "" : "s"}, ${pct} from entry`;
                  if (n < 5) {
                    return { k: "Signal Age", icon: "✅", status: "Fresh", detail: base, color: T.green, preStale: drawerVerdict?.preStaleVerdictLabel ?? null };
                  }
                  if (n <= 6) {
                    return { k: "Signal Age", icon: "⏱", status: "Aging", detail: base, color: T.textDim, preStale: drawerVerdict?.preStaleVerdictLabel ?? null };
                  }
                  if (sa.moveConfirmed) {
                    return { k: "Signal Age", icon: "✅", status: "Confirmed", detail: `${base} — move confirmed`, color: T.green, preStale: drawerVerdict?.preStaleVerdictLabel ?? null };
                  }
                  if (n >= 10) {
                    return { k: "Signal Age", icon: "❌", status: "SEVERE STALE", detail: base, color: T.red, preStale: drawerVerdict?.preStaleVerdictLabel ?? null };
                  }
                  return { k: "Signal Age", icon: "⚠", status: "MILD STALE", detail: `${base}, no 3%+ move`, color: "#fbbf24", preStale: drawerVerdict?.preStaleVerdictLabel ?? null };
                })();
                const phase1DetectionRow = (() => {
                  const ew = result?.earlyWarning;
                  if (!ew) {
                    return { k: "Phase 1 Detection", icon: "—", status: "N/A", detail: "No phase signals", color: T.textDim };
                  }
                  const phase3Bull = !!(result.callsSignal?.fullSignal || result.callsSignal?.bouncePlayOnly);
                  const phase3Bear = !!result.putsSignal?.fullSignal;
                  if ((ew.phase2Bull || ew.phase2Bear || phase3Bull || phase3Bear) && (ew.phase1Bull || ew.phase1Bear)) {
                    return { k: "Phase 1 Detection", icon: "✅", status: "Promoted", detail: "Phase 1 confirmed — promoted to Phase 2/3", color: T.green };
                  }
                  if (ew.phase1Bull) {
                    return { k: "Phase 1 Detection", icon: "✅", status: "Accumulation", detail: "Phase 1 active — institutional accumulation detected", color: "#06b6d4" };
                  }
                  if (ew.phase1Bear) {
                    return { k: "Phase 1 Detection", icon: "✅", status: "Distribution", detail: "Phase 1 active — institutional distribution detected", color: "#a855f7" };
                  }
                  return { k: "Phase 1 Detection", icon: "—", status: "N/A", detail: "No phase signals", color: T.textDim };
                })();
                const exitStatusRow = (() => {
                  if (!inPosition) {
                    return { k: "Exit Status", icon: "—", status: "N/A", detail: "No open position", color: T.textDim };
                  }
                  const price = currentPrice ?? result?.price;
                  const exitSignal = computeExitSignal(result, position, price, {
                    inPosition: true,
                    priorBuySignal: result?.activeSignal?.entry_signal === "SWING_LONG"
                      || result?.activeSignal?.entry_signal === "BOTTOM_CONFIRMED",
                  });
                  if (!exitSignal) {
                    return { k: "Exit Status", icon: "✅", status: "Clear", detail: "Position open, no exit triggers active", color: T.green };
                  }
                  const typeLabel = exitSignal.type.replace(/_/g, " ");
                  return { k: "Exit Status", icon: "✗", status: `${typeLabel} active`, detail: "See Position tab for action", color: T.red };
                })();
                return [
                  { k: "Market Tide", g: gates?.callsContext?.gate1 },
                  sectorTideRow,
                  earningsWindowRow,
                  signalAgeRow,
                  phase1DetectionRow,
                  exitStatusRow,
                  { k: "Stage 2 Trend", g: gates?.callsContext?.gate2 },
                  { k: "Trend Template", g: gates?.callsContext?.gate3 },
                  { k: "Institutional Support", g: gates?.callsContext?.gate4 },
                ];
              })().map((row) => (
                <React.Fragment key={row.k}>
                <tr>
                  <td style={{ ...td, color: T.text }} title={row.detail && (row.k === "Sector Tide" || row.k === "Earnings Window" || row.k === "Signal Age" || row.k === "Phase 1 Detection") && (row.status === "N/A" || row.status === "Clear" || row.status === "Fresh") ? row.detail : undefined}>{row.k}</td>
                  <td style={{ ...td, color: row.g ? (row.g.pass ? T.green : row.g.pass === false ? "#f97316" : T.textDim) : row.color }}>
                    {row.g ? (
                      <>
                        {(row.g.pass === true ? "✅" : row.g.pass === false ? "⚠" : "—")}{" "}
                        {row.g.pass === true ? "Confirmed" : row.g.pass === false ? "Not fully confirmed" : "—"}
                        {row.g.detail ? ` (${row.g.detail})` : ""}
                      </>
                    ) : (
                      <>
                        {row.icon}{" "}
                        {row.status}
                        {row.detail && row.status !== "N/A" ? ` — ${row.detail}` : row.status === "N/A" ? ` (${row.detail})` : ""}
                      </>
                    )}
                  </td>
                </tr>
                {row.k === "Signal Age" && row.preStale && (
                  <tr>
                    <td colSpan={2} style={{ ...td, fontSize: 10, color: T.textDim, paddingTop: 0, borderBottom: `1px solid ${T.border}` }}>
                      Would have been: {row.preStale}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              <tr>
                <td style={{ ...td, borderBottom: "none", color: T.textDim, fontWeight: 800 }}>Note</td>
                <td style={{ ...td, borderBottom: "none", color: T.textDim }}>
                  Soft gates are advisory. Hard blocks (RSI/CMF extremes + CHOP + penny stocks) suppress calls/puts signals.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* SECTION 7 — HARD BLOCKS */}
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", margin: "8px 0 6px 0" }}>HARD BLOCKS</div>
          <div style={{ padding: "10px 10px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 11, color: T.textSub, lineHeight: 1.7 }}>
            {hardBlocks.length ? (
              hardBlocks.map((b, idx) => (
                <div key={idx} style={{ color: T.red }}>
                  ⛔ {b.name} — <span style={{ color: T.textSub }}>{b.reason}</span>
                </div>
              ))
            ) : (
              <div style={{ color: T.green }}>✅ No hard blocks active</div>
            )}
          </div>
        </div>
      </div>
    </details>
  );
}

const EARN_SOON_DAYS = 14;
const EARN_WINDOW_MIN = -5;
const EARN_WINDOW_MAX = 90;

/** Canonical earnings timing from scan payload (daysAway only until UI enrich). */
function buildEarningsState(daysAway) {
  if (typeof daysAway !== "number" || daysAway < EARN_WINDOW_MIN || daysAway > EARN_WINDOW_MAX) {
    return { daysAway: null, date: null, soon: false, within7: false, warning: null, avoidOptions: false, source: null };
  }
  const soon = daysAway >= 0 && daysAway <= EARN_SOON_DAYS;
  const within7 = daysAway >= 0 && daysAway <= 7;
  return {
    daysAway,
    date: null,
    soon,
    within7,
    warning: soon ? `⚠ Earnings in ${daysAway} day${daysAway === 1 ? "" : "s"} — avoid options` : null,
    avoidOptions: soon,
    source: "scan",
  };
}

/** Add display fields (date) without changing daysAway from scan. */
function enrichEarningsState(state, { date } = {}) {
  if (!state || state.daysAway == null) return state;
  const resolvedDate = date ?? state.date;
  return {
    ...state,
    date: resolvedDate,
    enriched: !!resolvedDate,
    source: state.source === "scan" ? "enriched" : (state.source || "enriched"),
  };
}

/** Legacy UI shape — result.earnings (by design, not used for scoring). */
function earningsStateToLegacyUI(state) {
  if (!state || state.daysAway == null) return null;
  return {
    date: state.date,
    daysAway: state.daysAway,
    soon: state.soon,
    warning: state.warning,
  };
}

function callsContextPasses(gates) {
  const c = gates?.callsContext;
  if (!c) return true;
  return [c.gate1, c.gate2, c.gate3, c.gate4].every(g => g?.pass !== false);
}

function putsContextPasses(gates) {
  const p = gates?.putsContext;
  if (!p) return true;
  return [p.gate1, p.gate2, p.gate3, p.gate4].every(g => g?.pass !== false);
}

// ─── Phase C: trading-day signal age / stale modifier ───────────────────────
const US_MARKET_HOLIDAYS = new Set([
  "2025-01-01", "2025-01-20", "2025-02-17", "2025-04-18", "2025-05-26",
  "2025-06-19", "2025-07-04", "2025-09-01", "2025-11-27", "2025-12-25",
  "2026-01-01", "2026-01-19", "2026-02-16", "2026-04-03", "2026-05-25",
  "2026-06-19", "2026-07-03", "2026-09-07", "2026-11-26", "2026-12-25",
  "2027-01-01", "2027-01-18", "2027-02-15", "2027-03-26", "2027-05-31",
  "2027-06-18", "2027-07-05", "2027-09-06", "2027-11-25", "2027-12-24",
]);

function tradingDaysBetween(start, end) {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  if (s > e) return 0;

  let count = 0;
  const cur = new Date(s);
  cur.setHours(0, 0, 0, 0);
  const endDay = new Date(e);
  endDay.setHours(0, 0, 0, 0);

  while (cur < endDay) {
    cur.setDate(cur.getDate() + 1);
    const day = cur.getDay();
    const iso = cur.toISOString().slice(0, 10);
    if (day !== 0 && day !== 6 && !US_MARKET_HOLIDAYS.has(iso)) count++;
  }
  return count;
}

function isOpenSignalHistoryRow(row) {
  if (!row) return false;
  return row.outcome == null || row.outcome === "STILL_OPEN" || row.outcome === "OPEN";
}

/** Normalize local signal_history row to openSignal contract (first_fired_at alias). */
function normalizeOpenSignalRow(row) {
  if (!row || !isOpenSignalHistoryRow(row)) return null;
  return {
    ...row,
    first_fired_at: row.first_fired_at || row.scan_date || null,
    price_at_scan: typeof row.price_at_scan === "number" ? row.price_at_scan : null,
  };
}

function findOpenSignalForTicker(historyList, ticker, result = null) {
  const list = Array.isArray(historyList) ? historyList : [];
  const sym = (ticker || "").toUpperCase();
  const openRows = list
    .filter(e => e && (e.ticker || "").toUpperCase() === sym && isOpenSignalHistoryRow(e))
    .map(normalizeOpenSignalRow)
    .filter(Boolean);
  if (!openRows.length) return null;
  const side = result ? classifySignalHistorySide(result) : null;
  if (side) {
    const match = openRows.find(e => e.signal_type === side);
    if (match) return match;
  }
  return openRows.sort((a, b) =>
    new Date(a.first_fired_at).getTime() - new Date(b.first_fired_at).getTime()
  )[0];
}

async function attachOpenSignalToResult(sym, result, historyCache = null) {
  if (!result) return result;
  const history = historyCache ?? (await load("signal_history")) ?? [];
  result.openSignal = findOpenSignalForTicker(history, sym, result);
  return result;
}

function enrichResultsWithOpenSignals(resultsBySymbol, historyList) {
  const out = {};
  for (const [sym, r] of Object.entries(resultsBySymbol || {})) {
    if (!r) continue;
    out[sym] = { ...r, openSignal: findOpenSignalForTicker(historyList, sym, r) };
  }
  return out;
}

function enrichResultsWithActiveSignals(resultsBySymbol, activeList) {
  const list = Array.isArray(activeList) ? activeList : [];
  const out = {};
  for (const [sym, r] of Object.entries(resultsBySymbol || {})) {
    if (!r) continue;
    const active = list.find(e => (e.ticker || "").toUpperCase() === String(sym).toUpperCase()) || null;
    out[sym] = {
      ...r,
      activeSignal: active,
      activeSignalDisplay: buildActiveSignalDisplay(active, r),
    };
  }
  return out;
}

function computeSignalAge(result, openSignalRow, currentPrice) {
  if (!openSignalRow) return null;
  if (!openSignalRow.first_fired_at) return null;
  if (openSignalRow.price_at_scan == null) return null;
  if (currentPrice == null) return null;

  const ageDays = tradingDaysBetween(openSignalRow.first_fired_at, new Date());
  if (ageDays == null) return null;

  const entryPrice = parseFloat(openSignalRow.price_at_scan);
  if (!Number.isFinite(entryPrice) || entryPrice <= 0) return null;
  const pctMove = ((currentPrice - entryPrice) / entryPrice) * 100;

  const verdict = (openSignalRow.verdict || "").toUpperCase();
  const isBullish = /POWER PLAY|STANDARD LONG|SPECULATIVE|STARTER — 25% ACCUMULATION|WR RECOVERY|OVERSOLD RECOVERY|ACCUMULATION ZONE|EMERGING RECOVERY/.test(verdict);
  const isBearish = /STRONG SHORT|BREAKDOWN|BEARISH|STANDARD SHORT|SCALP|STARTER SHORT — 25% DISTRIBUTION/.test(verdict);

  const moveConfirmed = isBullish ? pctMove >= 3 : isBearish ? pctMove <= -3 : false;
  const isMildStale = ageDays >= 7 && !moveConfirmed;
  const isSevereStale = ageDays >= 10 && !moveConfirmed;

  return {
    ageDays,
    entryPrice,
    currentPrice,
    pctMove,
    moveConfirmed,
    direction: isBullish ? "bullish" : isBearish ? "bearish" : null,
    isMildStale,
    isSevereStale,
    originalVerdict: openSignalRow.verdict,
  };
}

function enrichSignalAgeForUi(signalAge) {
  if (!signalAge) return null;
  const { ageDays, pctMove, entryPrice } = signalAge;
  const pctStr = `${pctMove >= 0 ? "+" : ""}${pctMove.toFixed(1)}%`;
  let badgeLabel = null;
  let badgeColor = null;
  if (ageDays >= 10 && !signalAge.moveConfirmed) {
    badgeLabel = "⏱STALE";
    badgeColor = T.red;
  } else if (ageDays >= 7 && !signalAge.moveConfirmed) {
    badgeLabel = `⏱${ageDays}d`;
    badgeColor = "#fbbf24";
  } else if (ageDays >= 5) {
    badgeLabel = `⏱${ageDays}d`;
    badgeColor = T.textDim;
  }
  return {
    ...signalAge,
    pctStr,
    badgeLabel,
    badgeColor,
    tooltip: `Signal fired ${ageDays} session${ageDays === 1 ? "" : "s"} ago, ${pctStr} from entry ($${entryPrice?.toFixed(2) ?? "—"}).`,
  };
}

function applyStaleSignalModifier(verdict, signalAge) {
  if (!signalAge) return null;
  if (!verdict || !verdict.label) return null;
  if (verdict.layer === "exit") return null;

  const { ageDays, pctMove, isMildStale, isSevereStale, direction } = signalAge;

  const NON_ACTIONABLE = /(CHOP|BLOCKED|WAIT|NEARLY GO|HOLD|MONITOR|EXIT|TRAIL HIT|STOP HIT|T1 HIT|T2 HIT|POSITION HEALTHY)/i;
  if (NON_ACTIONABLE.test(verdict.label)) {
    if (/HOLD|MONITOR/i.test(verdict.label) && isMildStale) {
      return {
        ...verdict,
        reason: `${verdict.reason} ⏱ Position open ${ageDays} sessions, ${pctMove >= 0 ? "+" : ""}${pctMove.toFixed(1)}% from entry.`,
        action: `${verdict.action}\n\n⏱ STALE POSITION: ${ageDays} sessions since entry, no significant move (${pctMove >= 0 ? "+" : ""}${pctMove.toFixed(1)}%). Capital may be productive elsewhere — consider trimming and redeploying to a fresher setup.`,
        staleFlag: "STALE_POSITION",
        signalAgeDays: ageDays,
      };
    }
    return null;
  }

  const ACTIONABLE = /(POWER PLAY|STANDARD LONG|SPECULATIVE|STARTER — 25% ACCUMULATION|WR RECOVERY|OVERSOLD RECOVERY|ACCUMULATION ZONE|EMERGING RECOVERY|STRONG SHORT|BREAKDOWN|BEARISH|STARTER SHORT — 25% DISTRIBUTION)/i;
  if (!ACTIONABLE.test(verdict.label)) return null;

  const pctStr = `${pctMove >= 0 ? "+" : ""}${pctMove.toFixed(1)}%`;

  if (isSevereStale) {
    return {
      ...verdict,
      label: "WATCH — STALE SETUP",
      short: "WATCH ⏱",
      color: "#9ca3af",
      bg: "#1f2937",
      darkColor: "#d1d5db",
      darkBg: "#37415133",
      reason: `Signal fired ${ageDays} sessions ago at $${signalAge.entryPrice.toFixed(2)}; current ${pctStr} — setup is structurally stale.`,
      action: `Do not enter this setup. ${ageDays} trading sessions with no ${direction === "bullish" ? "3%+ move higher" : direction === "bearish" ? "3%+ move lower" : "meaningful progress"} means the conditions that triggered the signal have decayed. Wait for a fresh setup or remove from active watch.`,
      layer: "watch",
      staleFlag: "SEVERE_STALE",
      signalAgeDays: ageDays,
      preStaleVerdictLabel: verdict.label,
    };
  }

  if (isMildStale) {
    return {
      ...verdict,
      label: `${verdict.label} — STALE`,
      reason: `${verdict.reason} ⏱ Setup waiting ${ageDays} sessions, ${pctStr} from trigger.`,
      action: `${verdict.action}\n\n⏱ MILD STALE: Signal first fired ${ageDays} trading sessions ago at $${signalAge.entryPrice.toFixed(2)} and price has moved ${pctStr}. Setup is aging — tighten stop to 1×ATR and reduce position size by 25%. If no progress in 3 more sessions, exit and redeploy capital.`,
      staleFlag: "MILD_STALE",
      signalAgeDays: ageDays,
    };
  }

  return null;
}

function SignalAgeBadge({ signalAge }) {
  if (!signalAge?.badgeLabel) return null;
  return (
    <span
      title={signalAge.tooltip}
      style={{
        fontSize: 9,
        fontWeight: 800,
        padding: "2px 6px",
        borderRadius: 999,
        border: `1px solid ${signalAge.badgeColor}55`,
        color: signalAge.badgeColor,
        background: `${signalAge.badgeColor}14`,
        whiteSpace: "nowrap",
        cursor: "help",
        letterSpacing: "0.02em",
      }}
    >
      {signalAge.badgeLabel}
    </span>
  );
}

const SECTOR_TIDE_SKIP = new Set(["SPY", "QQQ", "DIA", "IWM", "VTI", "VOO", "IVV"]);

function getSectorFromMap(ticker) {
  const etf = TICKER_SECTOR_MAP[ticker?.toUpperCase()];
  if (!etf) return null;
  return SECTOR_ETF_NAMES[etf] || etf;
}

function getSectorETF(sectorOrTicker) {
  if (SECTOR_ETF_NAMES[sectorOrTicker]) return sectorOrTicker;
  const byName = Object.entries(SECTOR_ETF_NAMES).find(([, name]) => name === sectorOrTicker);
  if (byName) return byName[0];
  return TICKER_SECTOR_MAP[sectorOrTicker?.toUpperCase()] || null;
}

function computeSectorTide(result, ticker) {
  const phaseContext = result?.phaseContext;
  if (!phaseContext) return null;

  const sym = ticker?.toUpperCase();
  if (SECTOR_TIDE_SKIP.has(sym)) return null;
  if (isCrypto(ticker)) return null;

  const sector = getSectorFromMap(ticker);
  if (!sector) return null;

  const pct = phaseContext.sector5dPct ?? phaseContext.sector3dPct;
  if (pct == null) return null;

  const spyPct = phaseContext.spy5dPct ?? phaseContext.spy3dPct ?? 0;
  const sectorETF = getSectorETF(ticker) || phaseContext.sectorEtf || null;

  const band =
    pct <= -5 ? "SEVERE_HEADWIND" :
    pct <= -3 ? "HEADWIND" :
    pct >= 5 ? "SEVERE_TAILWIND_AGAINST_SHORTS" :
    pct >= 3 ? "TAILWIND_AGAINST_SHORTS" :
    "NEUTRAL";

  return {
    sectorName: sector,
    sectorETF,
    pct5d: pct,
    spyPct5d: spyPct,
    relativePct: pct - spyPct,
    band,
    timeframe: phaseContext.sector5dPct != null ? "5d" : "3d",
  };
}

function isMomentumBullishVerdict(label) {
  if (!label || isMeanReversionBullishVerdict(label)) return false;
  return /POWER PLAY|STANDARD LONG|SPECULATIVE|STARTER — 25% ACCUMULATION/.test(label);
}

function isMeanReversionBullishVerdict(label) {
  if (!label) return false;
  return /WR RECOVERY|OVERSOLD RECOVERY|ACCUMULATION ZONE|EMERGING RECOVERY/.test(label);
}

function isBearishVerdictForSectorTide(label) {
  if (!label) return false;
  // BEARISH — AVOID LONGS is a long-warning, not a short-entry recommendation
  if (/BEARISH — AVOID LONGS/i.test(label)) return false;
  return /STRONG SHORT|BREAKDOWN|STANDARD SHORT|STARTER SHORT — 25% DISTRIBUTION/.test(label);
}

function applySectorTideModifier(verdict, sectorTide, buyScore) {
  if (verdict?.layer === "exit") return null;
  const { band, sectorName, pct5d, sectorETF, timeframe } = sectorTide;
  const pctDisplay = pct5d.toFixed(1);
  const tfLabel = timeframe === "5d" ? "5 sessions" : "3 sessions";
  const absPct = Math.abs(pct5d).toFixed(1);

  if (band === "SEVERE_HEADWIND" &&
      (isMomentumBullishVerdict(verdict.label) || isMeanReversionBullishVerdict(verdict.label))) {
    return {
      ...verdict,
      label: "WATCH — SECTOR HEADWIND",
      short: "WATCH ⚠",
      color: "#d97706",
      bg: "#fef3c7",
      darkColor: "#fbbf24",
      darkBg: "#92400e22",
      reason: `${sectorName} (${sectorETF}) down ${absPct}% over ${tfLabel} — sector breakdown overrides individual setup.`,
      action: `Do not enter long. Even +${buyScore} scores fail when the sector tide is severely against them. Wait for sector to stabilize (positive 1-day return AND ${sectorETF} CMF turning positive) before considering this ticker.`,
      layer: "watch",
      sectorTideFlag: band,
    };
  }

  if (band === "HEADWIND" && isMomentumBullishVerdict(verdict.label)) {
    return {
      ...verdict,
      label: `${verdict.label} — SECTOR HEADWIND`,
      reason: `${verdict.reason} ⚠ ${sectorName} (${sectorETF}) down ${absPct}% over ${tfLabel}.`,
      action: "Take HALF normal size. Tighten stop to 1×ATR (not 2×). Sector headwind reduces follow-through probability — confirm intraday sector strength before adding the second half.",
      sectorTideFlag: band,
    };
  }

  if (band === "SEVERE_TAILWIND_AGAINST_SHORTS" && isBearishVerdictForSectorTide(verdict.label)) {
    return {
      ...verdict,
      label: "WATCH — SECTOR TAILWIND AGAINST",
      short: "WATCH ⚠",
      color: "#d97706",
      bg: "#fef3c7",
      darkColor: "#fbbf24",
      darkBg: "#92400e22",
      reason: `${sectorName} (${sectorETF}) up ${pctDisplay}% over ${tfLabel} — sector strength overrides individual breakdown.`,
      action: `Do not short. Wait for sector to roll over (negative 1-day return AND ${sectorETF} CMF turning negative) before considering puts.`,
      layer: "watch",
      sectorTideFlag: band,
    };
  }

  if (band === "TAILWIND_AGAINST_SHORTS" && isBearishVerdictForSectorTide(verdict.label)) {
    return {
      ...verdict,
      label: `${verdict.label} — SECTOR AGAINST`,
      reason: `${verdict.reason} ⚠ ${sectorName} (${sectorETF}) up ${pctDisplay}% over ${tfLabel}.`,
      action: "Take HALF normal size on puts. Tighten stop to just above the recent swing high. Reassess if sector closes negative on the day.",
      sectorTideFlag: band,
    };
  }

  return null;
}

function applyEarningsWindowModifier(verdict, earningsState, buyScore, ticker) {
  return null;
  if (verdict?.layer === "exit") return null;
  if (!earningsState || !earningsState.within7) return null;
  if (earningsState.daysAway == null || earningsState.daysAway < 0) return null;

  const daysAway = earningsState.daysAway;
  const dayWord = daysAway === 0 ? "today" :
                  daysAway === 1 ? "1 session" :
                  `${daysAway} sessions`;

  const HOLD_PATTERNS = [
    /HOLD/i,
    /^\s*[⏸◼️]\s*HOLD/i,
  ];
  const isHoldLayer = verdict.layer === "hold" ||
                      HOLD_PATTERNS.some(p => p.test(verdict.label || ""));

  if (isHoldLayer) {
    return {
      ...verdict,
      reason: `${verdict.reason} ⚠ Earnings ${dayWord === "today" ? "today" : "in " + dayWord}.`,
      action: `${verdict.action}\n\n⚠ EARNINGS ${dayWord === "today" ? "TODAY" : "IN " + dayWord.toUpperCase()}: Consider trimming to 25–50% size before the print. Earnings is a binary event — do not hold full size through it unless thesis warrants the gamble.`,
      earningsWindowFlag: "WITHIN_7_HOLD",
      earningsDaysAway: daysAway,
    };
  }

  const BULLISH_MOMENTUM = /(POWER PLAY|STANDARD LONG|SPECULATIVE|STARTER — 25% ACCUMULATION)/i;
  const BULLISH_MEAN_REV = /(WR RECOVERY|OVERSOLD RECOVERY|ACCUMULATION ZONE|EMERGING RECOVERY)/i;
  const BEARISH = /(STRONG SHORT|BREAKDOWN|BEARISH|STARTER SHORT — 25% DISTRIBUTION)/i;

  const isModifiable = BULLISH_MOMENTUM.test(verdict.label) ||
                       BULLISH_MEAN_REV.test(verdict.label) ||
                       BEARISH.test(verdict.label);

  if (!isModifiable) return null;

  const wasBearish = BEARISH.test(verdict.label);
  const sym = ticker || "This ticker";
  const reasonText = wasBearish
    ? `Short setup is technically valid, but ${sym} reports earnings ${dayWord === "today" ? "today" : "in " + dayWord} — binary upside risk overrides the setup.`
    : `Setup is technically strong (+${buyScore}), but ${sym} reports earnings ${dayWord === "today" ? "today" : "in " + dayWord} — binary risk overrides the setup.`;

  const actionText = wasBearish
    ? "Do not enter short. Wait for earnings to pass, then reassess. If the print confirms weakness (gap down, weak guide), enter post-print on confirmation. If the print is neutral-to-strong, the short thesis is invalidated."
    : "Do not enter long. Wait for earnings to pass, then reassess. If the print confirms strength (beat + raise, positive reaction), enter post-print on confirmation. Trading the print itself is gambling, not investing.";

  return {
    ...verdict,
    label: "WAIT — EARNINGS WINDOW",
    short: "WAIT ⚠",
    color: "#d97706",
    bg: "#fef3c7",
    darkColor: "#fbbf24",
    darkBg: "#92400e22",
    reason: reasonText,
    action: actionText,
    layer: "wait",
    earningsWindowFlag: "WITHIN_7_DOWNGRADE",
    earningsDaysAway: daysAway,
    preEarningsVerdictLabel: verdict.label,
  };
}

function applyAiCautionModifier(verdict, aiCaution) {
  if (!aiCaution) return null;
  if (!verdict || !verdict.label) return null;
  if (verdict.layer === "exit") return null;

  const NON_ACTIONABLE = /(CHOP|BLOCKED|WAIT|WATCH|NEARLY GO|HOLD|MONITOR|EXIT|TRAIL HIT|STOP HIT|T1 HIT|T2 HIT|POSITION HEALTHY)/i;
  if (NON_ACTIONABLE.test(verdict.label)) return null;

  const ACTIONABLE = /(POWER PLAY|STANDARD LONG|SPECULATIVE|STARTER — 25% ACCUMULATION|WR RECOVERY|OVERSOLD RECOVERY|ACCUMULATION ZONE|EMERGING RECOVERY|STRONG SHORT|BREAKDOWN|BEARISH|STARTER SHORT — 25% DISTRIBUTION)/i;
  if (!ACTIONABLE.test(verdict.label)) return null;

  return {
    ...verdict,
    label: `${verdict.label} — AI CAUTION`,
    reason: `${verdict.reason} ⚠ AI summary flagged caution.`,
    action: `${verdict.action}\n\n⚠ AI CAUTION: The AI summary for this ticker flagged concern (keywords like "avoid", "wait for", "extended", etc.). Open the Analysis tab and review the AI summary before entering. Consider half-size or wait for confirmation.`,
    aiCautionFlag: true,
  };
}

function computeExitSignal(result, position, currentPrice, opts = {}) {
  const { inPosition = false, priorBuySignal = false } = opts;
  if (typeof result?.buyScore === "number" && result.buyScore >= 4) return null;
  const hadBuyContext = inPosition || priorBuySignal
    || result?.signal === "SWING_LONG" || result?.signal === "BOTTOM_CONFIRMED"
    || result?.activeSignal?.entry_signal === "SWING_LONG"
    || result?.activeSignal?.entry_signal === "BOTTOM_CONFIRMED";
  if (!hadBuyContext) return null;
  if (!result || !position || !position.buyPrice || currentPrice == null) return null;

  const entryPrice = parseFloat(position.buyPrice);
  if (!entryPrice || entryPrice <= 0) return null;

  const atr = result.atr14 ?? result.atr ?? null;
  if (!atr || atr <= 0) return null;

  const macdHistogram = result.macd?.histogram ?? result.macdHistogram ?? null;
  const ema21 = result.ema21 ?? null;
  const maxCloseSinceEntry = position.maxCloseSinceEntry ?? currentPrice;

  const pctMove = ((currentPrice - entryPrice) / entryPrice) * 100;
  const dollarMove = currentPrice - entryPrice;
  const atrMove = dollarMove / atr;

  const stopPrice = entryPrice - (2 * atr);
  const t1Price = entryPrice + (1.5 * atr);
  const t2Price = entryPrice + (3 * atr);

  if (currentPrice < stopPrice) {
    return { type: "STOP_LOSS", priority: 1, level: stopPrice, atrMove, pctMove };
  }

  if (ema21 != null && currentPrice < ema21 && result.cmf != null && result.cmf < -0.05) {
    return { type: "MOMENTUM_LOSS", priority: 2, pctMove, atrMove };
  }

  if (currentPrice >= t2Price) {
    return { type: "T2_HIT", priority: 3, level: t2Price, atrMove, pctMove };
  }

  if (
    maxCloseSinceEntry >= entryPrice + (2 * atr) &&
    ema21 != null && currentPrice < ema21
  ) {
    return { type: "TRAILING_STOP", priority: 4, pctMove, atrMove };
  }

  if (currentPrice >= t1Price) {
    return { type: "T1_PARTIAL", priority: 5, level: t1Price, atrMove, pctMove };
  }

  return null;
}

function applyExitModifier(verdict, exitSignal, position, ticker, currentPrice) {
  if (!exitSignal) return null;
  if (!verdict || !verdict.label) return null;

  const { type, level, atrMove, pctMove } = exitSignal;
  const pctStr = `${pctMove >= 0 ? "+" : ""}${pctMove.toFixed(1)}%`;
  const entryPrice = parseFloat(position.buyPrice);
  const dollarStr = level ? `$${level.toFixed(2)}` : "";
  const priceStr = currentPrice != null ? `$${currentPrice.toFixed(2)}` : "—";

  switch (type) {
    case "STOP_LOSS":
      return {
        ...verdict,
        label: "STOP LOSS HIT — EXIT",
        short: "STOP HIT",
        color: "#dc2626",
        bg: "#fecaca",
        darkColor: "#fca5a5",
        darkBg: "#7f1d1d44",
        reason: `${ticker || "Ticker"} closed at ${priceStr} — below 2×ATR stop at ${dollarStr} (${pctStr} from entry $${entryPrice.toFixed(2)}).`,
        action: "Exit the full position immediately. Do not average down. The 2×ATR stop is the hard exit rule — honor it. Reassess the setup later if conditions reset; this position is closed.",
        layer: "exit",
        exitFlag: "STOP_LOSS",
      };

    case "MOMENTUM_LOSS":
      return {
        ...verdict,
        label: "MOMENTUM LOSS — EXIT",
        short: "EXIT",
        color: "#ea580c",
        bg: "#fed7aa",
        darkColor: "#fdba74",
        darkBg: "#9a340244",
        reason: `Three concurrent momentum losses on ${ticker || "this ticker"}: MACD histogram negative, close below EMA21, CMF turned negative. Position is at ${pctStr} from entry.`,
        action: "Exit the position regardless of P&L. The technical thesis that brought you in is no longer valid — MACD has rolled over, price has broken EMA21 support, and institutional flow (CMF) has turned. Stop hoping for a bounce; redeploy capital to a fresher setup.",
        layer: "exit",
        exitFlag: "MOMENTUM_LOSS",
      };

    case "T2_HIT":
      return {
        ...verdict,
        label: "T2 HIT — FULL EXIT OR TRAIL",
        short: "T2 HIT",
        color: "#059669",
        bg: "#a7f3d0",
        darkColor: "#6ee7b7",
        darkBg: "#06402144",
        reason: `${ticker || "Ticker"} hit T2 (3×ATR profit) at ${dollarStr}, currently ${pctStr} from entry — a successful swing.`,
        action: "You have two choices: (1) close the remaining position here and lock the full gain, or (2) convert to a trailing stop at EMA21 to let it run further. Both are valid — the easy money is already booked. Don't let a winner turn into a loser.",
        layer: "exit",
        exitFlag: "T2_HIT",
      };

    case "TRAILING_STOP":
      return {
        ...verdict,
        label: "TRAILING STOP — EXIT",
        short: "TRAIL HIT",
        color: "#f59e0b",
        bg: "#fef3c7",
        darkColor: "#fbbf24",
        darkBg: "#92400e44",
        reason: `Position ran to 2×ATR+ profit but is now closing below EMA21. Momentum is rolling over. Currently ${pctStr} from entry.`,
        action: "Exit and lock in the gain. The position has done its job — it ran in the right direction, hit a meaningful profit level, and is now showing momentum decay. Don't give back gains hoping for a continuation.",
        layer: "exit",
        exitFlag: "TRAILING_STOP",
      };

    case "T1_PARTIAL":
      return {
        ...verdict,
        label: "T1 HIT — TAKE 50%",
        short: "T1 HIT",
        color: "#eab308",
        bg: "#fef9c3",
        darkColor: "#facc15",
        darkBg: "#71370844",
        reason: `${ticker || "Ticker"} hit T1 (1.5×ATR profit) at ${dollarStr}, currently ${pctStr} from entry.`,
        action: `Sell 50% of the position to lock in partial gains. Move the stop on the remaining 50% to breakeven ($${entryPrice.toFixed(2)}). Let the remaining half run toward T2 with no downside risk. If you've already taken T1, ignore this and hold the runner.`,
        layer: "exit",
        exitFlag: "T1_PARTIAL",
      };

    default:
      return null;
  }
}

function buildExitChecklist(result, position, currentPrice) {
  if (!result || !position?.buyPrice || currentPrice == null) return null;
  const entryPrice = parseFloat(position.buyPrice);
  const atr = result.atr14 ?? result.atr ?? null;
  if (!entryPrice || !atr) return null;

  const stopPrice = entryPrice - 2 * atr;
  const t1Price = entryPrice + 1.5 * atr;
  const t2Price = entryPrice + 3 * atr;
  const pctMove = ((currentPrice - entryPrice) / entryPrice) * 100;
  const macdHistogram = result.macd?.histogram ?? null;
  const ema21 = result.ema21 ?? null;
  const maxCloseSinceEntry = position.maxCloseSinceEntry ?? currentPrice;

  const dist = (level) => {
    const d = level - currentPrice;
    return d >= 0 ? `$${Math.abs(d).toFixed(2)} above` : `$${Math.abs(d).toFixed(2)} below`;
  };

  const stopHit = currentPrice < stopPrice;
  const t1Hit = currentPrice >= t1Price;
  const t2Hit = currentPrice >= t2Price;
  const trailReady = maxCloseSinceEntry >= entryPrice + 2 * atr;
  const trailHit = trailReady && ema21 != null && currentPrice < ema21;
  const momentumLoss = ema21 != null && currentPrice < ema21 && result.cmf != null && result.cmf < -0.05;

  const exitSignal = computeExitSignal(result, position, currentPrice, {
    inPosition: true,
    priorBuySignal: result?.activeSignal?.entry_signal === "SWING_LONG"
      || result?.activeSignal?.entry_signal === "BOTTOM_CONFIRMED",
  });

  return {
    entryPrice,
    currentPrice,
    pctMove,
    atr,
    atr2x: 2 * atr,
    stopPrice,
    t1Price,
    t2Price,
    exitSignal,
    rows: [
      {
        key: "stop",
        icon: stopHit ? "✗" : "⏳",
        iconColor: stopHit ? T.red : T.textDim,
        label: "STOP LOSS",
        detail: stopHit
          ? `Sell below $${stopPrice.toFixed(2)} (2×ATR stop) — HIT`
          : `Sell below $${stopPrice.toFixed(2)} (2×ATR stop) — ${dist(stopPrice)}`,
      },
      {
        key: "t1",
        icon: t1Hit ? "✓" : "⏳",
        iconColor: t1Hit ? T.green : T.textDim,
        label: "T1 HIT",
        detail: t1Hit
          ? `at $${t1Price.toFixed(2)} — sell 50%, move stop to breakeven`
          : `at $${t1Price.toFixed(2)} — ${dist(t1Price)}`,
      },
      {
        key: "t2",
        icon: t2Hit ? "✓" : t1Hit ? "⏳" : "⏸",
        iconColor: t2Hit ? T.green : T.textDim,
        label: t2Hit ? "T2 HIT" : "T2 PENDING",
        detail: t2Hit
          ? `at $${t2Price.toFixed(2)} — full exit or trail`
          : t1Hit
          ? `at $${t2Price.toFixed(2)} — ${dist(t2Price)}`
          : `at $${t2Price.toFixed(2)} — pending T1`,
      },
      {
        key: "trail",
        icon: trailHit ? "✗" : trailReady ? "⏳" : "⏸",
        iconColor: trailHit ? T.orange : T.textDim,
        label: "TRAILING STOP",
        detail: trailHit
          ? "Triggered — max close at 2×ATR+ profit, now below EMA21"
          : trailReady
          ? "Pending — ran 2×ATR+ but still above EMA21"
          : "Pending (max close not yet at 2×ATR profit)",
      },
      {
        key: "momentum",
        icon: momentumLoss ? "✗" : "⏳",
        iconColor: momentumLoss ? T.orange : T.textDim,
        label: "MOMENTUM LOSS",
        detail: momentumLoss
          ? "Three concurrent technical losses active — exit"
          : "Pending (no concurrent technical losses)",
      },
    ],
  };
}

const AI_CAUTION_KEYWORDS = ["do not buy", "don't buy", "avoid", "wait for", "not yet", "overbought", "extended", "caution", "pullback first", "risky entry"];
const AI_CAUTION_KEYWORDS_BEARISH = [
  "cover shorts", "cover puts", "covering shorts", "oversold bounce", "oversold reversal",
  "support holding", "ready to bounce", "reversal", "capitulation", "squeezed",
  "short squeeze risk", "short squeeze",
];

function summaryTriggersAiCaution(text) {
  if (!text) return false;
  const lower = text.toLowerCase();
  return AI_CAUTION_KEYWORDS.some(w => lower.includes(w)) ||
         AI_CAUTION_KEYWORDS_BEARISH.some(w => lower.includes(w));
}

/** Shared AI caution flag from cached summary keywords + aiVerdict caution. */
function useEffectiveAiCaution(symbol) {
  const [effectiveAiCaution, setEffectiveAiCaution] = React.useState(false);
  React.useEffect(() => {
    if (!symbol) { setEffectiveAiCaution(false); return; }
    let alive = true;
    (async () => {
      let caution = false;
      try {
        const cached = await load(`aiSummary_${symbol}`);
        if (cached?.text) {
          caution = summaryTriggersAiCaution(cached.text);
        }
        const v = await load(`aiVerdict_${symbol}`);
        if (v?.caution === true) caution = true;
      } catch {}
      if (alive) setEffectiveAiCaution(caution);
    })();
    return () => { alive = false; };
  }, [symbol]);
  return effectiveAiCaution;
}

/** Verdict with shared AI caution from cache (row, drawer, mobile parity). */
function useVerdictForTicker(result, symbol, opts = {}) {
  const { inPosition = false, position = null, currentPrice = null } = opts;
  const effectiveAiCaution = useEffectiveAiCaution(symbol);
  return React.useMemo(
    () => (result ? calcVerdict({ result, aiCaution: effectiveAiCaution, inPosition, position, currentPrice, symbol }) : null),
    [result, effectiveAiCaution, inPosition, position, currentPrice, symbol]
  );
}

function MobileVerdictCell({ result, symbol, isDark, inPosition = false, position = null, currentPrice = null }) {
  if (!result) return <span style={{ color: T.textDim, fontSize: 11 }}>—</span>;
  const display = resolveDisplayVerdict(result, { inPosition, activeSignal: result?.activeSignal });
  const chip = displayVerdictChipStyle(display.verdictStyle);
  return (
    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 3, maxWidth: 160 }}>
      <span
        title={`${display.verdict} — ${display.subtitle}`}
        style={{
          display: "inline-block",
          maxWidth: 150,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          padding: "3px 6px",
          borderRadius: 5,
          background: chip.bg,
          border: `1px solid ${chip.border}`,
          color: chip.color,
          fontSize: 9,
          fontWeight: 800,
        }}
      >
        {display.verdict}
      </span>
      <SignalAgeBadge signalAge={result?.signalAge} />
    </div>
  );
}

function PositionCard({ item, isDark, livePrice, onOpen, getHealth }) {
  const { symbol, result } = item;
  const { health, color, flags } = getHealth(result);
  const live = livePrice[symbol];
  const displayPrice = live?.price || result?.price;
  const verdict = useVerdictForTicker(result, symbol, {
    inPosition: item.inPosition,
    position: item.position,
    currentPrice: displayPrice,
  });
  const vCol = verdict ? (isDark ? verdict.darkColor : verdict.color) : T.textDim;
  const displayPct = live?.changePct ?? result?.pctChange;
  const goods = result ? [
    result.goldenCross && "Golden cross",
    result.cmf > 0.05 && `CMF +${result.cmf.toFixed(3)}`,
    result.price > result.ema21 && (result.ema21SlopeLabel || "Above EMA21"),
  ].filter(Boolean) : [];

  return (
    <div
      onClick={() => onOpen(item)}
      style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderLeft: `3px solid ${color}`, borderRadius: "0 10px 10px 0", padding: "10px 14px", cursor: "pointer", transition: "background 0.1s" }}
      onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
      onMouseLeave={e => e.currentTarget.style.background = T.surface}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 5 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>{symbol}</span>
          {displayPrice != null && (
            <span style={{ fontSize: 12, color: T.textSub, fontFamily: "'JetBrains Mono', monospace" }}>${displayPrice.toFixed(2)}</span>
          )}
          {displayPct != null && (
            <span style={{ fontSize: 11, color: displayPct >= 0 ? T.green : T.red }}>{displayPct >= 0 ? "+" : ""}{displayPct.toFixed(2)}%</span>
          )}
          {live && <span style={{ fontSize: 9, color: T.cyan }}>● LIVE</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 7, flexShrink: 0 }}>
          {verdict && (
            <span
              title={verdict.label}
              style={{
                fontSize: 9,
                fontWeight: 800,
                padding: "2px 7px",
                borderRadius: 4,
                background: isDark ? verdict.darkBg : verdict.bg,
                border: `1px solid ${vCol}44`,
                color: vCol,
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {verdict.layer === "exit"
                ? `${/STOP LOSS|MOMENTUM LOSS|TRAILING STOP/i.test(verdict.label) ? "▼ " : "✓ "}${verdict.label}`
                : verdict.label}
            </span>
          )}
          <SignalAgeBadge signalAge={result?.signalAge} />
          <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: `${color}18`, color, border: `1px solid ${color}33` }}>{health}</span>
          {result?.buyScore != null && <span style={{ fontSize: 16, fontWeight: 800, color: result.buyScore >= 7 ? T.green : result.buyScore >= 4 ? "#86efac" : result.buyScore <= -4 ? T.red : T.textSub, fontFamily: "'JetBrains Mono', monospace" }}>{result.buyScore > 0 ? "+" : ""}{result.buyScore}</span>}
        </div>
      </div>
      {result && (
        <div style={{ display: "flex", gap: 12, marginBottom: 5 }}>
          <span style={{ fontSize: 11, color: T.textDim }}>Stop <span style={{ color: result.price < result.ema21 ? T.red : T.green, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }}>${result.ema21?.toFixed(2) ?? "—"}</span></span>
          <span style={{ fontSize: 11, color: T.textDim }}>RSI <span style={{ color: rsiDisplayColor(result.rsi, T), fontFamily: "'JetBrains Mono', monospace", fontWeight: 700 }}>{result.rsi != null ? formatRsiWithDivergence(result.rsi, result.rsiDiv, 0) : "—"}</span></span>
          <span style={{ fontSize: 11, color: T.textDim }}>CMF <span style={{ color: result.cmf > 0.05 ? T.green : result.cmf < -0.05 ? T.red : T.textSub, fontFamily: "'JetBrains Mono', monospace" }}>{result.cmf?.toFixed(3) ?? "—"}</span></span>
          {result.t1 && <span style={{ fontSize: 11, color: T.textDim }}>T1 <span style={{ color: T.purple, fontFamily: "'JetBrains Mono', monospace" }}>${result.t1.toFixed(2)}</span></span>}
        </div>
      )}
      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
        {flags.length === 0
          ? goods.slice(0, 3).map(g => <span key={g} style={{ fontSize: 10, padding: "1px 7px", borderRadius: 8, background: `${T.green}12`, color: T.green, border: `1px solid ${T.green}28` }}>✓ {g}</span>)
          : flags.map(f => <span key={f} style={{ fontSize: 10, padding: "1px 7px", borderRadius: 8, background: `${color}12`, color, border: `1px solid ${color}28` }}>⚠ {f}</span>)
        }
        {!result && <span style={{ fontSize: 10, color: T.textDim }}>Scan to load data</span>}
      </div>
    </div>
  );
}

function runAnalysis(raw, spyReturn = null, cryptoMode = false, symbol = null, marketTide = null, marketCtx = null) {
  return runAnalysisV2(raw, {
    symbol,
    spyReturn,
    cryptoMode,
    marketTide,
    marketCtx,
    btcData: _btcData,
    fearGreedData: _fearGreedData,
    sectorETFData: _sectorETFData,
    tickerSectorMap: TICKER_SECTOR_MAP,
  });
}

function calcExits(r) {
  const alerts = [];
  if (r.atrStop2x && r.price < r.atrStop2x) alerts.push({ level: "CRITICAL", msg: "Price broke ATR stop — exit rule triggered", color: T.red });
  if (r.sellCount >= 4) alerts.push({ level: "CRITICAL", msg: `${r.sellCount}/5 sell conditions active`, color: T.red });
  if (r.rsiDiv?.bearish) alerts.push({ level: "HIGH", msg: "RSI bearish divergence", color: T.orange });
  if (r.volDiv?.bearish) alerts.push({ level: "HIGH", msg: `Volume divergence — ${Math.round(r.volDiv.ratio * 100)}% of avg`, color: T.orange });
  if (r.bb && r.price > r.bb.upper) alerts.push({ level: "MEDIUM", msg: "Extended above BB upper", color: T.yellow });
  if (r.cmf < -0.10) alerts.push({ level: "MEDIUM", msg: "Heavy CMF distribution", color: T.yellow });
  const level = alerts.some(a => a.level === "CRITICAL") ? "CRITICAL" : alerts.some(a => a.level === "HIGH") ? "HIGH" : alerts.some(a => a.level === "MEDIUM") ? "MEDIUM" : "CLEAR";
  return { alerts, level };
}

// ─── THREE-LAYER VERDICT SYNTHESIZER ─────────────────────────────────────────
// Layer 1: Setup quality (score)
// Layer 2: Entry timing (gate + extension + AI caution)
// Layer 3: Position management (exits — only relevant if you own it)
function buildRiskFlags(result) {
  const flags = [];
  const isBullishLayer = result._verdictLayer && ["buy", "watch", "wait"].includes(result._verdictLayer);
  if (!isBullishLayer) return flags;

  // VIX risk-off
  if (_vixData) {
    const vixTrendStr = _vixData.trend === "rising" ? " ↑ rising" : _vixData.trend === "falling" ? " ↓ falling" : "";
    if (_vixData.value >= 25) {
      flags.push({ id: "high_fear", label: `High Market Fear (VIX ${_vixData.value?.toFixed(1)}${vixTrendStr})`, desc: `VIX at ${_vixData.value?.toFixed(1)}${_vixData.trend === "rising" ? " and rising — conditions deteriorating" : _vixData.trend === "falling" ? " but falling — fear is peaking, conditions improving" : ""}. Swing trade success rates drop significantly in elevated VIX environments.` });
    } else if (_vixData.value >= 20) {
      flags.push({ id: "market_risk_off", label: `Market Risk-Off (VIX ${_vixData.value?.toFixed(1)}${vixTrendStr})`, desc: `VIX elevated at ${_vixData.value?.toFixed(1)}${_vixData.trend === "rising" ? " and still rising — risk increasing" : _vixData.trend === "falling" ? " but falling — risk-off conditions easing" : ""}. Confirm entry timing carefully.` });
    } else if (_vixData.value >= 15 && _vixData.trend === "rising") {
      flags.push({ id: "vix_rising", label: `VIX Rising (${_vixData.value?.toFixed(1)} ↑)`, desc: `VIX is below 20 but trending higher — early warning of deteriorating conditions. Monitor closely.` });
    }
  }

  // Earnings proximity
  if (result.earnings?.soon) {
    const d = result.earnings.daysAway;
    flags.push({ id: "earnings", label: `Earnings in ${d} Day${d === 1 ? "" : "s"}`, desc: `Earnings on ${result.earnings.date}. Hold risk is elevated — avoid options or close before announcement.` });
  }

  // Low volume breakout
  if (result.rvol && result.rvol.ratio < 0.85) {
    flags.push({ id: "low_volume", label: `Low Volume (${(result.rvol.ratio * 100).toFixed(0)}% of avg)`, desc: `Volume is well below average. Moves on thin volume are less reliable and easier to reverse.` });
  }

  // Lagging SPY meaningfully
  if (result.rs?.outperforming === false && result.rs?.spread != null && result.rs.spread < -8) {
    flags.push({ id: "rs_weak", label: `Lagging SPY (${result.rs.spread.toFixed(1)}%)`, desc: `Stock underperforming SPY by ${Math.abs(result.rs.spread).toFixed(1)}% over 3 months. Relative weakness reduces conviction even on strong technical setups.` });
  }

  // Crypto-specific flags
  if (result.pureCrypto || isPureCrypto(result.symbol)) {
    // Extreme Greed = elevated reversal risk
    if (_fearGreedData && _fearGreedData.score >= 80) {
      flags.push({ id: 'fg_extreme_greed', label: `Extreme Greed (F&G: ${_fearGreedData.score})`, desc: `Fear & Greed at ${_fearGreedData.score}/100. Crypto markets are historically prone to sharp reversals from Extreme Greed. Consider smaller size.` });
    }
    // BTC down sharply on the day while altcoin shows buy
    if (_btcData && _btcData.changePct != null && _btcData.changePct < -3) {
      flags.push({ id: 'btc_dumping', label: `BTC Down ${Math.abs(_btcData.changePct).toFixed(1)}% Today`, desc: 'BTC is selling off sharply. Alt setups during BTC drops have high failure rates — wait for BTC to stabilize first.' });
    }
  }

  return flags;
}

function describePhase1Evidence(result) {
  const sig = result?.earlyWarning?.signals || {};
  const parts = [];
  if (sig.accumulation) {
    parts.push("CMF rising 3 sessions with OBV higher lows, flat/down price, and volume favoring up days");
  }
  if (sig.rsLeader) {
    parts.push("RS leader vs SPY while sector is flat or down");
  }
  return parts.length ? parts.join(", ") : "Institutional accumulation cluster";
}

function describePhase1BearEvidence(result) {
  const sig = result?.earlyWarning?.signals || {};
  const parts = [];
  if (sig.distribution) {
    parts.push("CMF falling 3 sessions with OBV lower highs, flat/up price, and volume favoring down days");
  }
  return parts.length ? parts.join(", ") : "Institutional distribution cluster";
}

function calcVerdict({ result, aiCaution = false, inPosition = false, position = null, currentPrice = null, symbol = null }) {
  if (!result) return null;
  const { gate, buyScore, signal, rsi, bb, price, exits, callsSignal, putsSignal, deathCross } = result;
  // Minimum price filter (penny stocks): show warning verdict, no signals/logging.
  if (typeof price === "number" && price < 1) {
    return {
      label: "⚠ PENNY STOCK",
      short: "PENNY",
      layer: "penny",
      color: "#fbbf24",
      action: "Penny stocks are high-risk (low liquidity, wide spreads). Signals are suppressed under $1. Score is still shown for reference.",
    };
  }
  const cmf          = result.cmf ?? null;
  const wrRecovery   = result.wrRecovery ?? null;
  const volBreakout  = result.volBreakout ?? null;
  const nineEmaEntry = result.nineEmaEntry ?? false;
  const ema9         = result.ema9 ?? null;
  const rvol         = result.rvol ?? null;
  const atrPctVal    = result.atr && result.price ? (result.atr / result.price * 100) : null;
  const vwap         = result.vwap ?? null;
  const gateGo      = gate?.label === "GO";
  const gateClose   = gate?.label === "CLOSE";
  const gateNear    = gate?.label === "NEAR";
  const gateBlocked = gate?.label === "BLOCKED" || gate?.label === "NOT YET";
  const extended    = bb && price > bb.upper;
  const bull        = buyScore >= 5;
  const strongBull  = buyScore >= 9;
  const bear        = buyScore <= -5;
  const strongBear  = buyScore <= -9;
  const exitCritical = exits?.level === "CRITICAL" || exits?.level === "HIGH";
  const hasCallsPlay = callsSignalDisplayActive(result);
  const hasPutsPlay  = putsSignal?.fullSignal  || putsSignal?.partialSignal;
  const cmfConflict  = cmf != null && cmf < -0.05;
  const hasFakeout   = volBreakout?.volFakeout === true;
  const hasVolConfirm = volBreakout?.volConfirmed === true;
  const gateDistance = (() => {
    if (!gate) return "";
    const critCount = gate.critFails?.length ?? 0;
    const advisoryCount = gate.advisoryFails?.length ?? 0;
    if (critCount > 0) return critCount === 1 ? "Blocked by 1 critical condition" : `Blocked by ${critCount} critical conditions`;
    return advisoryCount <= 1 ? "1 advisory condition from GO" : `${advisoryCount} advisory conditions from GO`;
  })();

  const withFlags = (v) => {
    result._verdictLayer = v.layer;
    v.riskFlags = buildRiskFlags(result);
    // Multi-timeframe confirmation — daily AND weekly both bullish
    const dailyBull = v.layer === "buy" || v.layer === "watch" || v.layer === "wait" || v.layer === "accum" || v.layer === "recov";
    v.multiTF = dailyBull && result.weeklyTrend?.strongUptrend === true;
    // Nearly GO — gate is CLOSE (all criticals pass, 1-2 advisory remaining) with a strong score
    // These are the setups to watch most closely — when gate flips to GO the move is often fast
    v.nearlyGo = !gateGo && gateClose && buyScore >= 7 && (gate?.critFails?.length ?? 0) === 0;
    v.nearlyGoConditions = v.nearlyGo ? (gate?.advisoryFails ?? []).slice(0, 2) : [];
    // Bubble entry detection — barely above threshold in a risk-off market
    const marginalScore = v.layer === "buy" && buyScore >= 7 && buyScore < 9;
    const vixLevel = _vixData?.value || 0;
    const vixRising = _vixData?.trend === "rising";
    const vixFalling = _vixData?.trend === "falling";
    const vixRiskOff = (vixLevel >= 20 && !vixFalling) || (vixLevel >= 15 && vixRising);
    const macroWeak = result.rs?.outperforming === false;
    v.bubbleEntry = marginalScore && (vixRiskOff || macroWeak);
    v.vixLevel = _vixData?.value || null;
    // ── VOLATILITY TIER — always attached to every verdict ──────────────────
    const atrPct = result.atr && result.price ? (result.atr / result.price) * 100 : null;
    v.atrPct = atrPct;
    v.volTier = atrPct == null ? null
      : atrPct > 5 ? "SPEC"
      : atrPct > 3 ? "HIGH"
      : atrPct > 1.5 ? "MOD"
      : "LOW";
    // Extended and volatile — near 52W high on a high-volatility name = double caution
    v.extendedAndVolatile = result.range52w?.score >= 70 && atrPct != null && atrPct > 3;
    // ── CMF CONFLICT — bullish signal with negative institutional flow ───────
    v.cmfConflict = v.layer === "buy" && result.cmf != null && result.cmf < -0.05;
    v._result = result; // attach result so VerdictBadge can calculate confidence

    // ── MARKET TIDE CONTEXT (labels + bearish confirmation gate) ────────────
    const tideBand = result.tideBand || "NEUTRAL";
    v.tideBand = tideBand;
    v.marketTide = result.marketTide ?? null;

    // Verdict label updates (tide context)
    if (v.short === "STD LONG") {
      if (tideBand === "BULL" || tideBand === "STRONG_BULL") {
        v.label = "✅ STANDARD LONG — Tide Confirmed";
      } else if (tideBand === "BEAR" || tideBand === "STRONG_BEAR") {
        v.label = "⚠ STANDARD LONG — Fighting Bear Tide";
      }
    }

    // Confirmed Bearish Gate — metadata only; cascade label preserved for Phase A–E modifiers
    if (v.layer === "bearish") {
      const checks = [
        { id: "score", pass: (result.buyScore ?? 0) <= -6 },
        { id: "rs_spy_10d", pass: result.rsVsSpy10 != null && result.rsVsSpy10 <= -5 },
        { id: "cmf", pass: result.cmf != null && result.cmf < 0 },
        { id: "below_vwap_200", pass: (result.vwap != null && result.price < result.vwap) && (result.sm200 != null && result.price < result.sm200) },
        { id: "sector_rs", pass: (result.rsVsSector?.spread != null && result.rsVsSector.spread < 0) || result.rsVsSector?.label === "LAGGING SECTOR" },
        { id: "down_vol", pass: result.downVsUpVol?.downGtUp === true },
      ];
      const met = checks.filter(c => c.pass).length;
      const gateLabel = met < 4
        ? "WEAK BEARISH — WAIT FOR CONFIRMATION"
        : met < 6
        ? "BEARISH — BUILDING"
        : "CONFIRMED BEARISH — High conviction";
      v.bearishGate = { met, total: checks.length, gateLabel, checks };
    }

    // Phase A: sector tide verdict modifier
    const sectorTide = computeSectorTide(result, result.symbol || null);
    if (sectorTide) result.sectorTide = sectorTide;
    if (sectorTide && sectorTide.band !== "NEUTRAL") {
      const modified = applySectorTideModifier(v, sectorTide, buyScore);
      if (modified) Object.assign(v, modified);
    }

    // Phase B: earnings window verdict modifier (runs after sector tide — earnings wins)
    const earningsState = result?.earningsState;
    if (earningsState && earningsState.within7) {
      const modified = applyEarningsWindowModifier(v, earningsState, buyScore, result.symbol || null);
      if (modified) Object.assign(v, modified);
    }

    // Phase C: signal staleness (runs after earnings, before AI caution)
    const signalAgeRaw = computeSignalAge(result, result?.openSignal, result?.price);
    result.signalAge = signalAgeRaw ? enrichSignalAgeForUi(signalAgeRaw) : null;
    if (signalAgeRaw && (signalAgeRaw.isMildStale || signalAgeRaw.isSevereStale)) {
      const modified = applyStaleSignalModifier(v, signalAgeRaw);
      if (modified) Object.assign(v, modified);
    }

    // AI Caution: verdict modifier (runs after sector tide, earnings, and stale)
    if (aiCaution) {
      const modified = applyAiCautionModifier(v, aiCaution);
      if (modified) Object.assign(v, modified);
    }

    // Phase E: EXIT modifier — last, supersedes everything else when in position
    if (inPosition && position?.buyPrice) {
      const exitPrice = currentPrice ?? result?.price;
      const exitSignal = computeExitSignal(result, position, exitPrice, {
        inPosition: true,
        priorBuySignal: result?.activeSignal?.entry_signal === "SWING_LONG"
          || result?.activeSignal?.entry_signal === "BOTTOM_CONFIRMED",
      });
      if (exitSignal) {
        const preExitLabel = v.label;
        const modified = applyExitModifier(v, exitSignal, position, symbol || result?.symbol || null, exitPrice);
        if (modified) {
          modified.preExitVerdictLabel = preExitLabel;
          Object.assign(v, modified);
        }
      } else {
        const healthData = calcPositionHealth({ result, position });
        const health = healthData?.health ?? null;
        if (health === "green" && /HOLD/i.test(v.label || "")) {
          v.label = "HOLD — POSITION HEALTHY";
          v.color = "#059669";
          v.short = "HOLD ✓";
        }
      }
    }

    return v;
  };

  // ── WR RECOVERY — 5-factor mean reversion — fires BEFORE BUY NOW verdicts ──
  if (wrRecovery?.active && !deathCross && !extended && (gateGo || gateClose)) {
    return withFlags({
      label: "WR RECOVERY — MEAN REVERSION", short: "WR RECOV",
      color: "#0891b2", bg: "#ecfeff",
      darkColor: "#22d3ee", darkBg: "#0e749022",
      reason: wrRecovery.reasons.join(" · "),
      action: "Mean reversion entry — shorter duration than trend trades. Size 75% of normal. Stop at 1×ATR (tighter than standard). Exit when RSI reaches 55-60 OR Williams %R reaches -30, whichever comes first. Do NOT hold through earnings. This is a bounce trade, not a trend continuation.",
      layer: "recov",
    });
  }

  // ── ACCUMULATION ZONE — quality name at depressed price with INSTITUTIONAL CONFIRMATION ────
  // STRICT REQUIREMENTS:
  // 1. Near 52W low (score ≤ 25)
  // 2. RSI oversold (≤ 38)
  // 3. CMF MUST be positive (> 0) — institutions must be stepping in, not just slowing selling
  //    Retail should not accumulate before institutional money is confirmed flowing IN
  // 4. Not in total freefall (score ≥ -6)
  // 5. At least one confirming momentum signal (RSI divergence OR volume drying on down days)
  const accum52wLow    = result.range52w?.score != null && result.range52w.score <= 25;
  const accumRsiOS     = rsi != null && rsi <= 38;
  const accumCMFpos    = result.cmf != null && result.cmf > 0;  // STRICT: CMF must be POSITIVE — institutions buying
  const accumNotCrash  = buyScore >= -6;                         // not in total freefall
  const accumRsiDiv    = result.rsiDiv?.bullish === true;        // price lower, RSI higher = momentum improving
  const accumVolDry    = result.volDiv?.bullish === true;        // volume dry on down days = sellers exhausted
  const accumOBVflat   = result.obv && !result.obv.bearishDiv;  // OBV not making new lows
  // Core: all 4 strict requirements must pass
  const accumCore = accum52wLow && accumRsiOS && accumCMFpos && accumNotCrash;
  // Confirm: at least one behavioral signal showing the bottom is forming
  const accumConfirm = accumRsiDiv || accumVolDry || accumOBVflat;
  const isAccumulationZone = accumCore && accumConfirm;

  if (isAccumulationZone) {
    // Override putsSignal — cannot recommend puts on a stock showing institutional accumulation
    // Suppress any puts signal to prevent contradictory directional guidance
    if (result.putsSignal) {
      result.putsSignal = { ...result.putsSignal, fullSignal: false, partialSignal: false,
        suppressedBy: "accumulation_zone" };
    }
    return withFlags({
      label: "ACCUMULATION ZONE", short: "ACCUM",
      color: "#0891b2", bg: "#ecfeff",
      darkColor: "#22d3ee", darkBg: "#0e7490" + "22",
      reason: `52W score ${result.range52w?.score}/100 — near annual low. RSI ${rsi?.toFixed(1)} oversold. CMF ${result.cmf?.toFixed(3)} positive — institutional money is flowing IN at these levels. ${accumRsiDiv ? "RSI bullish divergence detected — momentum improving before price." : ""} ${accumVolDry ? "Volume drying on down days — sellers are exhausting." : ""}`,
      action: "This is NOT a swing trade entry. Institutional money has begun accumulating at these levels — that is the key confirmation. Suitable for patient investors with a 3-6 month horizon. Use minimum position size (1% max). Verify fundamental health independently. Set a stop below the recent low.",
      layer: "accum",
    });
  }

  // ── OVERSOLD RECOVERY SETUP ──────────────────────────────────────────────
  // Distinct from Accumulation Zone:
  //   Accum Zone = near 52W LOW, positive CMF, long-term bottom thesis
  //   Oversold Recovery = quality name (Calmar ≥1) in a TEMPORARY DIP, no death cross
  //   Trigger: RSI ≤35, CMF not distributing (>-0.03), Calmar ≥1, score ≥-3, no death cross
  //   Thesis: proven name that got oversold in a selloff — mean reversion, not bottom fishing
  const calmarForRecovery = result.calmar2y ?? (() => {
    const ann63 = result.rs?.stockReturn != null ? result.rs.stockReturn * (252 / 63) : null;
    const rawDD = result.range52w?.distFromHigh != null ? Math.abs(result.range52w.distFromHigh) : null;
    if (ann63 == null || rawDD == null || rawDD < 8) return null;
    return Math.min(Math.abs(ann63 / rawDD), 9.9);
  })();
  const isOversoldRecovery = (
    !deathCross &&                                        // macro structure not broken
    rsi != null && rsi <= 35 &&                          // genuinely oversold
    result.cmf != null && result.cmf > -0.03 &&          // not actively distributing
    calmarForRecovery != null && calmarForRecovery >= 1 && // quality name — proven recovery history
    buyScore >= -3                                        // not deeply bearish
  );
  if (isOversoldRecovery) return withFlags({
    label: "OVERSOLD RECOVERY SETUP", short: "OB RECOV",
    color: "#0891b2", bg: "#ecfeff",
    darkColor: "#22d3ee", darkBg: "#0e749022",
    reason: `RSI ${rsi?.toFixed(1)} is oversold while the macro structure remains intact (200 SMA structure OK). CMF ${result.cmf?.toFixed(3)} shows selling is not institutional distribution — the bleed is slowing. Calmar ${calmarForRecovery.toFixed(2)} confirms this is a quality name with a proven track record of recovering from drawdowns. Score ${buyScore > 0 ? "+" : ""}${buyScore} — not deeply bearish. This is a temporary dip on a quality name, not a structural breakdown.`,
    action: `This is a mean-reversion setup, not a momentum entry. Watch for RSI to turn back above 35–40 as the entry confirmation. CMF crossing back above 0 is the strongest confirmation — institutional money returning. Do NOT chase — wait for the turn signal. Consider a starter position only, with a stop below the recent swing low. This setup has higher success probability on liquid, large-cap names (Mag 7, mega-cap ETFs) than on speculative stocks.`,
    layer: "recov",
  });

  // ── BEARISH VERDICTS ──────────────────────────────────────────────────────

  // EMERGING RECOVERY — death cross present but strong score + positive CMF
  // Institutional money flowing IN despite macro structure being bearish
  // AMZN pattern: death cross lagging while underlying momentum already recovering
  const emergingRecovery = deathCross && buyScore >= 7 && result.cmf != null && result.cmf > 0.08;
  if (emergingRecovery) return withFlags({
    label: "EMERGING RECOVERY", short: "EMERGING",
    color: "#7c3aed", bg: "#f5f3ff",
    darkColor: "#a78bfa", darkBg: "#4c1d9522",
    reason: `200 SMA structure still bearish (SMA50 below SMA200) but score is +${buyScore} and CMF is ${result.cmf?.toFixed(3)} — institutions are buying aggressively at current prices. The moving average cross is a lagging indicator and momentum has already shifted. This is a pre-golden-cross setup — the structure is improving faster than the averages can confirm.`,
    action: `No full position yet — 200 SMA structure is technically still bearish. Concrete trigger to act: golden cross confirmation (SMA50 crossing above SMA200). Consider a starter position (0.5% max) only if CMF stays above ${result.cmf != null ? (result.cmf + 0.02).toFixed(2) : "0.10"} and score holds above +5. Your stop if you enter: 2×ATR below current price ($${result.atr ? (result.price - 2 * result.atr).toFixed(2) : "—"}). A golden cross from this score and CMF level would be a very high-priority full entry.`,
    layer: "watch",
  });

  // 💀 BREAKDOWN — score -12 or lower, CMF negative (more severe than STRONG SHORT / AVOID LONGS)
  if (buyScore <= -12 && (deathCross || buyScore <= -10) && cmf != null && cmf < -0.05) {
    return withFlags({
      label: "💀 BREAKDOWN",
      short: "BREAKDOWN",
      color: "#ef4444",
      bg: "#fee2e2",
      darkColor: "#f87171",
      darkBg: "#7f1d1d33",
      reason: `Score ${buyScore} — maximum bearish conviction. CMF ${cmf.toFixed(3)} confirms heavy institutional distribution. ${result.bearCount} checklist indicators bearish. Structure, momentum, and confirmation all collapsed. This is a high-conviction breakdown.`,
      action: `High conviction puts. All three layers aligned against this name: institutional distribution (CMF ${cmf.toFixed(3)}), structural failure, momentum confirmed bearish. Size puts at 75% of normal — shorts carry squeeze risk. Enter on relief rallies that fail at resistance. Target D1${result.d1 ? ` ($${result.d1.toFixed(2)})` : ""}, ride to D2${result.d2 ? ` ($${result.d2.toFixed(2)})` : ""} with trailing stop. Stop: price closing above 2×ATR (${result.atr ? "$" + (result.price + 2 * result.atr).toFixed(2) : "—"}).`,
      layer: "bearish",
    });
  }

  // STRONG SHORT requires CMF negative — positive CMF means institutions buying = not a short
  const cmfConfirmsShort = result.cmf == null || result.cmf < 0.05; // must not be accumulating
  if (deathCross && strongBear && cmfConfirmsShort) return withFlags({
    label: "STRONG SHORT SETUP", short: "SHORT",
    color: "#ef4444", bg: "#fee2e2",
    darkColor: "#f87171", darkBg: "#7f1d1d22",
    reason: `200 SMA structure bearish confirmed with strongly bearish indicators (score ${buyScore}, ${result.bearCount ?? "—"} bearish signals). CMF ${result.cmf != null ? result.cmf.toFixed(3) : "—"} confirms institutional distribution. Structural breakdown.`,
      action: `Standard Short — 200 SMA structure bearish confirmed with ${result.bearCount ?? "—"} bearish signals aligned. ENTRY RULE: only enter puts after a relief rally fails — require price to close below VWAP${result.vwap ? ` ($${result.vwap.toFixed(2)})` : ""} on the rejection bar with RVOL ≥ 1.2x${result.rvol ? ` (current: ${result.rvol.ratio.toFixed(1)}x)` : ""}. Low RVOL on a rejection = no real selling pressure, likely a slow grind that can reverse. Don't chase downside at new lows — enter on the failed rally. Target D1${result.d1 ? ` ($${result.d1.toFixed(2)})` : ""}, D2${result.d2 ? ` ($${result.d2.toFixed(2)})` : ""}. Stop: price closing above 2×ATR ($${result.atr ? (result.price + 2 * result.atr).toFixed(2) : "—"}). Size 50–75% of normal — shorts carry squeeze risk. No long entries.`,
    layer: "bearish",
  });
  // Death cross + strong bear but CMF conflicts → downgrade to EMERGING RECOVERY or BEARISH
  if (deathCross && strongBear && !cmfConfirmsShort) {
    const conflictReason = `CMF ${result.cmf?.toFixed(3)} is positive — institutions are buying at current prices despite the bearish score. This conflicts with the short thesis.`;
    if (result.cmf != null && result.cmf > 0.08 && buyScore >= -4) return withFlags({
      label: "EMERGING RECOVERY", short: "EMERGING",
      color: "#7c3aed", bg: "#f5f3ff",
      darkColor: "#a78bfa", darkBg: "#4c1d9522",
      reason: `200 SMA structure bearish but ${conflictReason} Score ${buyScore}. Conflicting signals — not a clean short.`,
      action: "Do not short — institutional accumulation conflicts with the bearish technical structure. Watch for golden cross.",
      layer: "watch",
    });
    return withFlags({
      label: "BEARISH — AVOID LONGS", short: "BEARISH",
      color: "#dc2626", bg: "#fee2e2",
      darkColor: "#f87171", darkBg: "#7f1d1d22",
      reason: `200 SMA structure bearish with strongly negative score, but ${conflictReason} Not a clean short setup.`,
      action: "No long entries. Do not short — conflicting signals reduce conviction. Monitor for signal alignment.",
      layer: "bearish",
    });
  }
  if (deathCross || strongBear) return withFlags({
    label: "BEARISH — AVOID LONGS", short: "BEARISH",
    color: "#dc2626", bg: "#fee2e2",
    darkColor: "#f87171", darkBg: "#7f1d1d22",
    reason: deathCross && buyScore > 0
      ? `200 SMA structure bearish — SMA50 below SMA200. Score is ${buyScore > 0 ? "+" : ""}${buyScore} (some bullish signals present) but bearish 200 SMA structure overrides long entries. Monitor for improving CMF and golden cross.`
      : deathCross
      ? "200 SMA structure bearish — SMA50 crossed below SMA200. Macro trend is bearish. No long entries."
      : "Majority of indicators are bearish.",
    action: deathCross && buyScore > 0
      ? "No long entries until 200 SMA structure clears. If CMF turns positive (>0.08) watch for EMERGING RECOVERY upgrade."
      : buyScore <= -6
      ? `Standard Short — structural bearishness confirmed. Score ${buyScore} with ${result.bearCount ?? "—"} bearish signals. ENTRY RULE: wait for a relief rally to fail — require close below VWAP${result.vwap ? ` ($${result.vwap.toFixed(2)})` : ""} with RVOL ≥ 1.2x${result.rvol ? ` (current: ${result.rvol.ratio.toFixed(1)}x)` : ""} on the rejection. Low RVOL on a rejection = not real selling, potential trap. Target D1${result.d1 ? ` ($${result.d1.toFixed(2)})` : ""}. Stop above 2×ATR ($${result.atr ? (result.price + 2 * result.atr).toFixed(2) : "—"}).`
      : "Scalp Only — score in -1 to -5 range. Momentum is bearish but fighting a bullish structural backdrop. Likely a pullback, not a reversal. Puts for short-term only with strict stop management. High risk of reversal — do not hold overnight.",
    layer: "bearish",
  });

  // ── PHASE D — STARTER (Phase 1 institutional signal before score confirms) ──
  const ew = result?.earlyWarning;
  const phase1Bull = ew?.phase1Bull === true;
  const phase1Bear = ew?.phase1Bear === true;
  const phase2Bull = ew?.phase2Bull === true;
  const phase2Bear = ew?.phase2Bear === true;
  const phase3Bull = !!(callsSignal?.fullSignal || callsSignal?.bouncePlayOnly);
  const phase3Bear = !!putsSignal?.fullSignal;
  const postDropCooldown = !!result.threeDayRule?.postDrop?.active;
  const nearHighWeakCmf = result.range52w?.score > 90 && cmf != null && cmf < 0.02;
  const goldenCrossActive = !!result.goldenCross;
  const postRallyExhaustion = rsi != null && rsi > 75 && cmf != null && cmf > 0.08;

  const starterEligibleBull = (
    phase1Bull &&
    !phase2Bull &&
    !phase3Bull &&
    buyScore >= -2 && buyScore <= 4 &&
    gate?.label !== "BLOCKED" &&
    !gateBlocked &&
    !deathCross &&
    !postDropCooldown &&
    !nearHighWeakCmf
  );

  if (starterEligibleBull) {
    return withFlags({
      label: "STARTER — 25% ACCUMULATION",
      short: "STARTER 25%",
      color: "#06b6d4",
      bg: "#cffafe",
      darkColor: "#67e8f9",
      darkBg: "#0e749022",
      reason: `Phase 1 institutional accumulation detected. ${describePhase1Evidence(result)}. Full confirmation has not fired — but this is the institutional entry point.`,
      action: `Enter at 25% of normal position size now. Add the remaining 75% when Phase 2 (pre-breakout coiling) or Phase 3 (breakout confirmation) fires. Use a stop 2×ATR below entry ($${result.atr ? (result.price - 2 * result.atr).toFixed(2) : "—"}). If Phase 1 fades without progressing to Phase 2 within 10 trading sessions, exit the starter and free up the capital.`,
      layer: "buy",
      starterFlag: "BULL_PHASE_1",
    });
  }

  const starterEligibleBear = (
    phase1Bear &&
    !phase2Bear &&
    !phase3Bear &&
    buyScore >= -4 && buyScore <= 2 &&
    gate?.label !== "BLOCKED" &&
    !gateBlocked &&
    !goldenCrossActive &&
    !postRallyExhaustion
  );

  if (starterEligibleBear) {
    return withFlags({
      label: "STARTER SHORT — 25% DISTRIBUTION",
      short: "STARTER SHORT",
      color: "#a855f7",
      bg: "#f3e8ff",
      darkColor: "#d8b4fe",
      darkBg: "#581c8722",
      reason: `Phase 1 institutional distribution detected. ${describePhase1BearEvidence(result)}. Full confirmation has not fired — but this is the institutional exit point.`,
      action: `Enter short at 25% of normal position size now. Add the remaining 75% when Phase 2 (pre-breakdown coiling) or Phase 3 (breakdown confirmation) fires. Use a stop 2×ATR above entry ($${result.atr ? (result.price + 2 * result.atr).toFixed(2) : "—"}). If Phase 1 fades without progressing within 10 trading sessions, exit the starter.`,
      layer: "bearish",
      starterFlag: "BEAR_PHASE_1",
    });
  }

  // ── CHOP ZONE — score -3 to +3 = no edge, options signals blocked ──────────
  if (buyScore >= -3 && buyScore <= 3 && !deathCross && !isAccumulationZone) return withFlags({
    label: "CHOP — NO EDGE",
    short: "CHOP",
    color: "#64748b", bg: "#f1f5f9",
    darkColor: "#94a3b8", darkBg: "#1e293b",
    reason: `Score ${buyScore > 0 ? "+" : ""}${buyScore} — in the chop zone (-3 to +3). Signals are mixed with no directional conviction. Options premiums decay fast in choppy conditions — theta kills positions when there's no momentum.`,
    action: "Stay in cash on this name. Chop destroys options premium through theta decay. Wait for score to move decisively above +4 (bullish) or below -4 (bearish) before considering any position.",
    layer: "neutral",
  });

  // ── NEUTRAL / WATCH ──────────────────────────────────────────────────────
  if (!bull && !bear) {
    const watching = buyScore >= 2;
    // Watch zone — entry price range worth watching for WATCH verdicts
    const watchZoneLow  = result.atr && result.price ? parseFloat((result.price - 1.0 * result.atr).toFixed(2)) : null;
    const watchZoneHigh = result.atr && result.price ? parseFloat((result.price - 0.2 * result.atr).toFixed(2)) : null;
    const watchZoneNote = watching && watchZoneLow && watchZoneHigh
      ? ` Watch zone: $${watchZoneLow}–$${watchZoneHigh} (0.2–1× ATR pullback from current price). If price reaches this range, reassess for entry. If it breaks above without you, the setup has changed.`
      : "";
    return withFlags({
      label: watching ? "WATCH — BUILDING" : "HOLD — MONITOR",
      short: watching ? "WATCH" : "HOLD",
      color: watching ? "#d97706" : "#94a3b8",
      bg: watching ? "#fef3c7" : "#f1f5f9",
      darkColor: watching ? "#fbbf24" : "#9b8ec4",
      darkBg: watching ? "#92400e22" : "#1a1528",
      reason: watching
        ? `Score is ${buyScore > 0 ? "+" : ""}${buyScore} — bullish signals are building but not yet strong enough for entry. Watch for momentum to develop.${watchZoneNote}`
        : "Indicators are mixed with no clear directional edge.",
      action: watching
        ? `Speculative — score +${buyScore} is in the +1 to +5 range. Momentum signals present but structural backing is incomplete. High fake-out risk. If entering: small size only (25–50% of normal), tight stop at 1×ATR. Wait for score to cross +6 and gate to open for a Standard Long. Do not use options here — theta decay will punish you if momentum stalls.${watchZoneLow ? ` Watch zone: $${watchZoneLow}–$${watchZoneHigh} for lower-risk entry.` : ""}`
        : "No action recommended.",
      layer: "neutral",
    });
  }

  // ── BULLISH PATH — tiered by conviction ─────────────────────────────────

  // ⚡ POWER PLAY — score +12 or higher, gate GO, CMF positive, full stack aligned
  if (gateGo && buyScore >= 12 && !extended && cmf != null && cmf > 0.05) {
    return withFlags({
      label: "⚡ POWER PLAY",
      short: "⚡ POWER PLAY",
      color: "#22c55e",
      bg: "#dcfce7",
      darkColor: "#4ade80",
      darkBg: "#14532d33",
      reason: `Score +${buyScore} — maximum conviction. Gate GO, CMF ${cmf.toFixed(3)} confirms institutional accumulation. ${result.bullCount} checklist indicators bullish. Structural + momentum + confirmation stack fully aligned. This is the highest-probability setup the system can identify.`,
      action: `⚡ POWER PLAY — enter full size. All three layers fully aligned: institutional accumulation (CMF +${cmf.toFixed(3)}), gate open, maximum momentum conviction. This is the highest-probability setup the system produces. Size at your full normal allocation. Set stop at 2×ATR ($${result.atr ? (result.price - 2 * result.atr).toFixed(2) : "—"}). Take half off at T1${result.t1 ? ` ($${result.t1.toFixed(2)})` : ""}, let the rest run to T2${result.t2 ? ` ($${result.t2.toFixed(2)})` : ""} with a trailing stop. ${nineEmaEntry ? "✓ Price is at the EMA9 pullback zone — ideal limit order entry, minimal slippage risk." : ema9 && price > ema9 ? `⚠ Price is extended from EMA9 ($${ema9.toFixed(2)}) — use a limit order near EMA9 or wait for the first pullback. Chasing with a market order here compresses R:R.` : ""} ${callsSignal?.fullSignal ? "Calls signal active — check Options tab." : ""}`,
      layer: "buy",
    });
  }

  // ✅ STANDARD LONG — score +6 to +11, gate GO, not extended
  if (gateGo && buyScore >= 6 && buyScore < 12 && !extended) {
    // RSI bearish divergence — downgrade
    if (result.rsiDiv?.bearish) return withFlags({
      label: "WATCH — RSI DIVERGENCE", short: "WATCH ⚠",
      color: "#d97706", bg: "#fef3c7",
      darkColor: "#fbbf24", darkBg: "#92400e22",
      reason: `Gate GO and score +${buyScore} — setup looks strong. BUT RSI is making lower highs while price makes higher highs. Bearish divergence means momentum is weakening into the move — high-conviction reversal warning.`,
      action: "Do not enter full size. Bearish RSI divergence on an otherwise strong setup = buying late-stage momentum. Half size max with 1×ATR stop. Wait for RSI to reset below 50 before adding.",
      layer: "watch",
    });
    // CMF conflict — downgrade
    if (cmfConflict) return withFlags({
      label: "BUY ⚠ — CMF CONFLICT", short: "BUY ⚠",
      color: "#d97706", bg: "#fef3c7",
      darkColor: "#fbbf24", darkBg: "#92400e22",
      reason: `Gate GO, score +${buyScore} — technically aligned. BUT CMF ${cmf?.toFixed(3)} — smart money is distributing into price strength. The most important warning in the system.`,
      action: "Half size only. Do not add until CMF crosses above 0. Exit if CMF stays negative 3+ days while holding.",
      layer: "buy",
    });
    const tier = buyScore >= 9 ? "Standard Long (upper range)" : "Standard Long";
    return withFlags({
      label: hasCallsPlay && hasVolConfirm ? "✅ STANDARD LONG · CALLS + VOL" : hasCallsPlay ? "✅ STANDARD LONG · CALLS READY" : hasVolConfirm ? "✅ STANDARD LONG · VOL CONFIRMED" : "✅ STANDARD LONG",
      short: "STD LONG",
      color: "#16a34a", bg: "#dcfce7",
      darkColor: "#22c55e", darkBg: "#16653422",
      reason: `Score +${buyScore} — ${tier}. ${result.bullCount} checklist indicators bullish. Trend and structure confirmed.${hasVolConfirm ? ` Breakout confirmed with ${volBreakout?.volRatio?.toFixed(1)}x volume.` : ""} ${cmf != null && cmf > 0.05 ? `CMF ${cmf.toFixed(3)} — institutional accumulation active.` : ""}`,
      action: `Standard Long — enter at normal position size. Trend healthy, structure confirmed. Scale in on pullback to EMA21 for best entry.${nineEmaEntry ? " ✓ Price at EMA9 pullback — ideal limit order zone." : ema9 && price > ema9 * 1.015 ? ` Price is extended from EMA9 ($${ema9.toFixed(2)}) — use a limit order near EMA9 or wait for pullback to avoid chasing.` : ""} Set stop at 2×ATR ($${result.atr ? (result.price - 2 * result.atr).toFixed(2) : "—"}${atrPctVal ? ` · ${atrPctVal.toFixed(1)}% of price` : ""}). ${atrPctVal && atrPctVal > 4 ? "⚠ High-volatility name (ATR > 4%) — stop is wider than average, size accordingly." : ""} Target T1${result.t1 ? ` ($${result.t1.toFixed(2)})` : ""}, T2${result.t2 ? ` ($${result.t2.toFixed(2)})` : ""}.${hasCallsPlay ? " Calls play available — check Options tab." : ""}`,
      layer: "buy",
    });
  }

  // ⚠ SPECULATIVE — score +4 to +5, gate open or close
  if ((gateGo || gateClose) && buyScore >= 4 && buyScore < 6 && !extended) return withFlags({
    label: "⚠ SPECULATIVE",
    short: "SPEC",
    color: "#d97706", bg: "#fef3c7",
    darkColor: "#fbbf24", darkBg: "#92400e22",
    reason: `Score +${buyScore} — Speculative tier (+4 to +5). Momentum signals present but structural backing is not fully confirmed. Counter-trend or early-stage setup — elevated fake-out risk.`,
    action: `Speculative — small size only (25–50% of normal). Momentum is building but structural layers aren't fully aligned. Tight stop at 1×ATR ($${result.atr ? (result.price - 1 * result.atr).toFixed(2) : "—"}${atrPctVal ? ` · ${atrPctVal.toFixed(1)}% of price` : ""}). Note: stop is calculated from 14-day ATR on the daily chart — if trading intraday, recalculate ATR on your actual timeframe as this stop may be too wide. Do not use options here — theta decay punishes you if momentum stalls. Wait for score to reach +6 and gate to be GO for a Standard Long entry.`,
    layer: "watch",
  });

  // WATCH — EXTENDED on strong setups (timing issue, not setup issue)
  if ((gateGo || gateClose) && strongBull && extended) return withFlags({
    label: "WATCH — EXTENDED",
    short: "WATCH",
    color: "#d97706", bg: "#fef3c7",
    darkColor: "#fbbf24", darkBg: "#92400e22",
    reason: `Strong setup (+${buyScore}) but price is above the BB upper band — entry timing is extended. This is a TIMING issue, not a setup issue.`,
    action: "Set an alert for price to pull back to EMA21. Do NOT chase. The setup is real — wait 1–3 days for a better entry. If price runs away without a pullback, let it go.",
    layer: "watch",
  });

  // WATCH — STRUCTURE: gate close, CMF conflict or fakeout — structural issue requires resolution
  if ((gateGo || gateClose) && bull && (cmfConflict || hasFakeout)) return withFlags({
    label: cmfConflict ? "WATCH — CMF CONFLICT" : "WATCH — FAKEOUT RISK",
    short: "WATCH ⚠",
    color: "#d97706", bg: "#fef3c7",
    darkColor: "#fbbf24", darkBg: "#92400e22",
    reason: cmfConflict
      ? `Score +${buyScore} with gate ${gate?.label} — setup looks bullish. BUT CMF is ${cmf?.toFixed(3)} — institutions are distributing, not buying. This conflict means smart money is not behind this move yet.`
      : `Breakout detected (${volBreakout?.breakType}) but volume is only ${volBreakout?.volRatio.toFixed(1)}x average. Low-volume breakouts fail 65%+ of the time. This is a STRUCTURAL issue.`,
    action: cmfConflict
      ? "Do not enter yet. Watch for CMF to cross above 0 — that is your entry trigger. If CMF stays negative for 5+ days while price holds, the structural divergence is warning you. Check back daily."
      : "Do not enter. Wait for a retest of the breakout level with volume above 1.3× average. If price falls back below the level, the setup has failed — move on. If it consolidates and retests with volume, a new signal will fire.",
    layer: "watch",
  });

  // NEARLY GO — all criticals pass, score strong, 1-2 advisory conditions from GO
  // These are the highest-priority setups to watch — gate flip often triggers fast moves
  if (gateClose && strongBull) return withFlags({
    label: "NEARLY GO — WATCH DAILY",
    short: "NEARLY GO",
    color: "#0891b2", bg: "#ecfeff",
    darkColor: "#22d3ee", darkBg: "#0e749022",
    reason: `Score +${buyScore} — all critical gate conditions pass. ${gateDistance}. This is a high-priority watchlist item — when the gate opens this could move fast.`,
    action: `Scan daily. Missing: ${gate?.advisoryFails?.slice(0,2).map(c => c.label).join(" · ") || "see gate"}. Set a price alert at EMA21 ($${result.ema21?.toFixed(2) || "—"}) — a close above with volume confirms the setup.`,
    layer: "wait",
  });

  // WAIT — BLOCKED: strong score but critical condition blocking
  if (gateBlocked && strongBull) return withFlags({
    label: "STRONG SETUP — BLOCKED",
    short: "BLOCKED",
    color: "#ea580c", bg: "#ffedd5",
    darkColor: "#fb923c", darkBg: "#9a341222",
    reason: `Score +${buyScore} — technically very strong. BUT a critical gate condition is blocking entry: ${gate?.critFails?.map(c => c.label).join(", ") || "see gate"}. ${gate?.critFails?.[0]?.why || ""}`,
    action: `Do not enter regardless of score. A critical block is in place for a specific reason. ${gate?.critFails?.some(c => c.label.includes("200 SMA")) ? "200 SMA structure bearish — no longs until macro structure repairs." : gate?.critFails?.some(c => c.label.includes("extended")) ? "Wait for RSI to reset below 60 and price to pull back to EMA21." : gate?.critFails?.some(c => c.label.includes("fakeout")) ? "Breakout failed volume test. Wait for retest with volume ≥1.3×." : "Resolve the critical block before considering entry."}`,
    layer: "wait",
  });

  // WAIT — NEAR/FAR: moderate-strong setup, gate not close
  if ((gateNear || !gateGo) && bull) return withFlags({
    label: strongBull ? "STRONG SETUP — WAIT" : "SETUP — WAIT",
    short: "WAIT",
    color: "#ea580c", bg: "#ffedd5",
    darkColor: "#fb923c", darkBg: "#9a341222",
    reason: `Score +${buyScore} — ${strongBull ? "technically strong" : "some bullish signals"} but entry gate is not open. ${gateDistance}.`,
    action: `Do not enter. ${gateBlocked ? `Critical block: ${gate?.critFails?.map(c => c.label).join(", ")}.` : `${gate?.advisoryFails?.length || 0} advisory conditions from GO — ${gate?.advisoryFails?.length > 3 ? "not close enough to act on yet" : "monitor daily"}.`}`,
    layer: "wait",
  });

  // Fallback
  return withFlags({
    label: "HOLD — MONITOR",
    short: "MONITOR",
    color: "#94a3b8", bg: "#f1f5f9",
    darkColor: "#9b8ec4", darkBg: "#1a1528",
    reason: "Indicators are mixed with no clear directional edge. No actionable setup in either direction.",
    action: "No action. To turn bullish: score needs to reach +4 with gate GO or CLOSE. Watch for MACD crossover and CMF crossing above 0 as the two most reliable triggers. To turn bearish: score breaking below -4 with bearish 200 SMA structure would open a puts setup. Check back after next scan.",
    layer: "neutral",
  });
}
// ─── COMPANY NAMES ────────────────────────────────────────────────────────────
const COMPANY_NAMES = {
  // Mega Cap Tech
  "AAPL":"Apple","MSFT":"Microsoft","GOOGL":"Alphabet (Google)","GOOG":"Alphabet (Google)",
  "META":"Meta Platforms","AMZN":"Amazon","TSLA":"Tesla","NVDA":"NVIDIA","AMD":"Advanced Micro Devices",
  "ORCL":"Oracle","CRM":"Salesforce","NOW":"ServiceNow","SNOW":"Snowflake","ADBE":"Adobe",
  "INTU":"Intuit","NFLX":"Netflix","UBER":"Uber","ABNB":"Airbnb","DASH":"DoorDash",
  "SHOP":"Shopify","DELL":"Dell Technologies","IBM":"IBM","HPQ":"HP Inc","CSCO":"Cisco",
  // AI / Semiconductors
  "AVGO":"Broadcom","ARM":"Arm Holdings","QCOM":"Qualcomm","MRVL":"Marvell Technology",
  "INTC":"Intel","TSM":"Taiwan Semiconductor","AMAT":"Applied Materials","KLAC":"KLA Corporation",
  "LRCX":"Lam Research","ASML":"ASML Holding","TXN":"Texas Instruments","NXPI":"NXP Semiconductors",
  "ON":"ON Semiconductor","MCHP":"Microchip Technology","SMCI":"Super Micro Computer",
  "MU":"Micron Technology","WDC":"Western Digital","MPWR":"Monolithic Power Systems",
  "ENTG":"Entegris","ONTO":"Onto Innovation","WOLF":"Wolfspeed","SWKS":"Skyworks Solutions",
  "QRVO":"Qorvo","TER":"Teradyne","ACLS":"Axcelis Technologies","AMBA":"Ambarella",
  "CRUS":"Cirrus Logic","SOUN":"SoundHound AI","SNPS":"Synopsys","CDNS":"Cadence Design",
  "ANSS":"ANSYS",
  // Cloud / SaaS
  "NET":"Cloudflare","DDOG":"Datadog","ZS":"Zscaler","CRWD":"CrowdStrike",
  "PANW":"Palo Alto Networks","FTNT":"Fortinet","OKTA":"Okta","GTLB":"GitLab",
  "MDB":"MongoDB","ESTC":"Elastic","TEAM":"Atlassian","DOCU":"DocuSign",
  "ZM":"Zoom","BOX":"Box","TWLO":"Twilio","HUBS":"HubSpot","BILL":"Bill.com",
  "SMAR":"Smartsheet","CFLT":"Confluent","DOCN":"DigitalOcean","VEEV":"Veeva Systems",
  "WDAY":"Workday","PCTY":"Paylocity","PAYC":"Paycom","BRZE":"Braze",
  "PATH":"UiPath","AI":"C3.ai","MNDY":"Monday.com","TOST":"Toast","TTD":"The Trade Desk",
  // Fintech / Payments
  "V":"Visa","MA":"Mastercard","PYPL":"PayPal","SQ":"Block (Square)","COIN":"Coinbase",
  "HOOD":"Robinhood","SOFI":"SoFi Technologies","AFRM":"Affirm","UPST":"Upstart",
  "LC":"LendingClub","MSTR":"MicroStrategy","NU":"Nu Holdings","PAYO":"Payoneer",
  "WEX":"WEX Inc","GPN":"Global Payments","FIS":"Fidelity National Information Services",
  "FISV":"Fiserv","AXP":"American Express","COF":"Capital One","DFS":"Discover Financial",
  "MQ":"Marqeta","FICO":"Fair Isaac","WU":"Western Union","RELY":"Remitly","DAVE":"Dave Inc",
  // Financials / Banks
  "JPM":"JPMorgan Chase","GS":"Goldman Sachs","MS":"Morgan Stanley","BAC":"Bank of America",
  "WFC":"Wells Fargo","C":"Citigroup","BLK":"BlackRock","SCHW":"Charles Schwab",
  "RJF":"Raymond James","IBKR":"Interactive Brokers","USB":"U.S. Bancorp",
  "PNC":"PNC Financial","TFC":"Truist Financial","MTB":"M&T Bank","CFG":"Citizens Financial",
  "FITB":"Fifth Third Bancorp","HBAN":"Huntington Bancshares","KEY":"KeyCorp",
  "RF":"Regions Financial","ZION":"Zions Bancorporation","BX":"Blackstone",
  "KKR":"KKR & Co","APO":"Apollo Global Management","CG":"Carlyle Group","ARES":"Ares Management",
  "STT":"State Street","AMP":"Ameriprise Financial","TROW":"T. Rowe Price","BEN":"Franklin Templeton",
  // Healthcare / Biotech
  "LLY":"Eli Lilly","UNH":"UnitedHealth","JNJ":"Johnson & Johnson","PFE":"Pfizer",
  "ABBV":"AbbVie","MRK":"Merck","AMGN":"Amgen","GILD":"Gilead Sciences",
  "REGN":"Regeneron","VRTX":"Vertex Pharmaceuticals","MRNA":"Moderna","BNTX":"BioNTech",
  "BIIB":"Biogen","ILMN":"Illumina","IDXX":"IDEXX Laboratories","DXCM":"Dexcom",
  "ISRG":"Intuitive Surgical","SYK":"Stryker","BSX":"Boston Scientific","ZBH":"Zimmer Biomet",
  "TMO":"Thermo Fisher Scientific","DHR":"Danaher","MDT":"Medtronic","EW":"Edwards Lifesciences",
  "BDX":"Becton Dickinson","PODD":"Insulet","INSP":"Inspire Medical","ALNY":"Alnylam",
  "SRPT":"Sarepta Therapeutics","RARE":"Ultragenyx",
  // Consumer / Retail
  "WMT":"Walmart","TGT":"Target","COST":"Costco","HD":"Home Depot","LOW":"Lowe's",
  "TJX":"TJX Companies","ROST":"Ross Stores","LULU":"Lululemon","NKE":"Nike",
  "SBUX":"Starbucks","MCD":"McDonald's","YUM":"Yum! Brands","DPZ":"Domino's Pizza",
  "CMG":"Chipotle","WING":"Wingstop","ULTA":"Ulta Beauty","ELF":"e.l.f. Beauty",
  "ONON":"On Holding","DECK":"Deckers Outdoor","CROX":"Crocs","DG":"Dollar General",
  "DLTR":"Dollar Tree","BURL":"Burlington","CHWY":"Chewy","W":"Wayfair",
  "ETSY":"Etsy","CPNG":"Coupang","RH":"RH (Restoration Hardware)","TPR":"Tapestry",
  // Media / Entertainment
  "DIS":"Walt Disney","CMCSA":"Comcast","WBD":"Warner Bros. Discovery",
  "PARA":"Paramount Global","SPOT":"Spotify","SNAP":"Snap","PINS":"Pinterest",
  "RDDT":"Reddit","MTCH":"Match Group","FOXA":"Fox Corporation","FOX":"Fox Corporation",
  "NYT":"New York Times","LYV":"Live Nation","IMAX":"IMAX Corporation",
  "AMC":"AMC Entertainment","RBLX":"Roblox","TTWO":"Take-Two Interactive",
  "EA":"Electronic Arts","DKNG":"DraftKings","PENN":"PENN Entertainment",
  "MGM":"MGM Resorts","CZR":"Caesars Entertainment","WYNN":"Wynn Resorts","LVS":"Las Vegas Sands",
  // Energy
  "XOM":"ExxonMobil","CVX":"Chevron","COP":"ConocoPhillips","EOG":"EOG Resources",
  "PXD":"Pioneer Natural Resources","MPC":"Marathon Petroleum","PSX":"Phillips 66",
  "VLO":"Valero Energy","OXY":"Occidental Petroleum","DVN":"Devon Energy",
  "FANG":"Diamondback Energy","HAL":"Halliburton","SLB":"SLB (Schlumberger)",
  "BKR":"Baker Hughes","MRO":"Marathon Oil","APA":"APA Corporation",
  "CTRA":"Coterra Energy","SM":"SM Energy","RRC":"Range Resources","EQT":"EQT Corporation",
  "TRGP":"Targa Resources","KMI":"Kinder Morgan","WMB":"Williams Companies","ET":"Energy Transfer",
  "ENPH":"Enphase Energy","FSLR":"First Solar","RUN":"Sunrun",
  // EV / Clean Energy
  "RIVN":"Rivian","LCID":"Lucid Motors","F":"Ford Motor","GM":"General Motors",
  "NIO":"NIO","XPEV":"XPeng","LI":"Li Auto","BLNK":"Blink Charging",
  "CHPT":"ChargePoint","EVGO":"EVgo","ARRY":"Array Technologies","SEDG":"SolarEdge",
  "PLUG":"Plug Power","BE":"Bloom Energy","STEM":"Stem Inc","NOVA":"Sunnova Energy",
  "SPCE":"Virgin Galactic","RKLB":"Rocket Lab","ASTS":"AST SpaceMobile",
  "JOBY":"Joby Aviation","ACHR":"Archer Aviation","KTOS":"Kratos Defense",
  // ETFs
  "SPY":"S&P 500 ETF","QQQ":"Nasdaq 100 ETF","IWM":"Russell 2000 ETF",
  "DIA":"Dow Jones ETF","VTI":"Vanguard Total Market ETF","VOO":"Vanguard S&P 500 ETF",
  "SOXX":"iShares Semiconductor ETF","SMH":"VanEck Semiconductor ETF",
  "XLK":"Technology Select SPDR","XLF":"Financial Select SPDR","XLE":"Energy Select SPDR",
  "XLV":"Health Care Select SPDR","XLY":"Consumer Discretionary SPDR",
  "XLI":"Industrial Select SPDR","XLC":"Communication Services SPDR",
  "ARKK":"ARK Innovation ETF","GLD":"SPDR Gold ETF","SLV":"iShares Silver ETF",
  "TLT":"iShares 20+ Year Treasury","HYG":"iShares High Yield Bond",
  "SQQQ":"ProShares UltraPro Short QQQ","TQQQ":"ProShares UltraPro QQQ",
  "SOXL":"Direxion Daily Semiconductors Bull 3x","SOXS":"Direxion Daily Semiconductors Bear 3x",
  "UVXY":"ProShares Ultra VIX Short-Term Futures","XBI":"SPDR S&P Biotech ETF",
  "IBB":"iShares Biotechnology ETF","GDX":"VanEck Gold Miners ETF",
  "GDXJ":"VanEck Junior Gold Miners ETF","USO":"United States Oil Fund",
  // Real Estate / REITs
  "AMT":"American Tower","PLD":"Prologis","EQIX":"Equinix","CCI":"Crown Castle",
  "SBAC":"SBA Communications","DLR":"Digital Realty","PSA":"Public Storage",
  "EXR":"Extra Space Storage","AVB":"AvalonBay Communities","EQR":"Equity Residential",
  "MAA":"Mid-America Apartment","UDR":"UDR Inc","VTR":"Ventas","WELL":"Welltower",
  "PEAK":"Healthpeak Properties","O":"Realty Income","NNN":"NNN REIT",
  "SPG":"Simon Property Group","BXP":"BXP Inc","VNO":"Vornado Realty",
  "HST":"Host Hotels","IRM":"Iron Mountain","COLD":"Americold Realty",
  // Industrials / Defense
  "CAT":"Caterpillar","DE":"Deere & Company","HON":"Honeywell","GE":"GE Aerospace",
  "BA":"Boeing","RTX":"RTX Corporation","LMT":"Lockheed Martin","NOC":"Northrop Grumman",
  "GD":"General Dynamics","LHX":"L3Harris Technologies","MMM":"3M","EMR":"Emerson Electric",
  "ETN":"Eaton","PH":"Parker Hannifin","ROK":"Rockwell Automation","AME":"AMETEK",
  "CARR":"Carrier Global","OTIS":"Otis Worldwide","TT":"Trane Technologies",
  "IR":"Ingersoll Rand","AXON":"Axon Enterprise","LDOS":"Leidos","SAIC":"SAIC",
  "CACI":"CACI International","HII":"Huntington Ingalls","TDG":"TransDigm Group",
  // Crypto / Web3
  "MARA":"Marathon Digital Holdings","RIOT":"Riot Platforms","CLSK":"CleanSpark",
  "HUT":"Hut 8 Corp","BTBT":"Bit Digital","CIFR":"Cipher Mining","IREN":"Iris Energy",
  "CORZ":"Core Scientific","WGMI":"Valkyrie Bitcoin Miners ETF",
  "IBIT":"iShares Bitcoin Trust","FBTC":"Fidelity Wise Origin Bitcoin Fund",
  "GBTC":"Grayscale Bitcoin Trust","BITB":"Bitwise Bitcoin ETF",
  // Small Cap / High Momentum
  "APP":"AppLovin","DUOL":"Duolingo","CELH":"Celsius Holdings","GRAB":"Grab Holdings",
  "RXRX":"Recursion Pharmaceuticals","DOCS":"Doximity","GENI":"Genius Sports",
  "MAPS":"WM Technology","FLNC":"Fluence Energy","OPEN":"Opendoor Technologies",
  "HIMS":"Hims & Hers Health","LMND":"Lemonade","ROOT":"Root Insurance",
  // Retail Favorites
  "GME":"GameStop","PLTR":"Palantir","BB":"BlackBerry","NKLA":"Nikola",
  "GOEV":"Canoo","FSR":"Fisker","WISH":"ContextLogic","KOSS":"Koss Corporation",
  "MELI":"MercadoLibre","NU":"Nu Holdings","ACMR":"ACM Research",
  // Crypto
  "BTC-USD":"Bitcoin","ETH-USD":"Ethereum","SOL-USD":"Solana","BNB-USD":"BNB",
  "XRP-USD":"XRP","DOGE-USD":"Dogecoin","ADA-USD":"Cardano","AVAX-USD":"Avalanche",
  "LINK-USD":"Chainlink","DOT-USD":"Polkadot",
  // Other common
  "BRK.B":"Berkshire Hathaway","BRK.A":"Berkshire Hathaway",
  "BABA":"Alibaba","JD":"JD.com","PDD":"PDD Holdings","BIDU":"Baidu","NTES":"NetEase",
  "SE":"Sea Limited","BILI":"Bilibili","GRAB":"Grab Holdings",
  "T":"AT&T","VZ":"Verizon","TMUS":"T-Mobile","LBRDK":"Liberty Broadband",
  "NEE":"NextEra Energy","DUK":"Duke Energy","SO":"Southern Company",
  "PG":"Procter & Gamble","KO":"Coca-Cola","PEP":"PepsiCo","MNST":"Monster Beverage",
  "PM":"Philip Morris","MO":"Altria","BTI":"British American Tobacco",
  "UPS":"UPS","FDX":"FedEx","DAL":"Delta Air Lines","UAL":"United Airlines",
  "AAL":"American Airlines","LUV":"Southwest Airlines","ABNB":"Airbnb",
  "ANET":"Arista Networks","ZI":"ZoomInfo","NCNO":"nCino","ALTR":"Altair Engineering",
};

const companyCache = {};

async function getCompanyName(symbol) {
  if (COMPANY_NAMES[symbol]) return COMPANY_NAMES[symbol];
  if (companyCache[symbol]) return companyCache[symbol];
  // Try Yahoo Finance server-side via our quote API for unknown tickers
  try {
    const res = await fetch(`/api/quote?symbol=${encodeURIComponent(symbol)}`);
    if (res.ok) {
      const data = await res.json();
      if (data.name) { companyCache[symbol] = data.name; return data.name; }
    }
  } catch {}
  return symbol;
}

// ─── CRYPTO TICKERS — adjusted RSI thresholds, wider stops, no options signals ─
// Pure spot crypto coins — Yahoo Finance format
const PURE_CRYPTO_COINS = [
  "BTC-USD","ETH-USD","SOL-USD","BNB-USD","XRP-USD","DOGE-USD","ADA-USD","AVAX-USD",
  "LINK-USD","DOT-USD","MATIC-USD","UNI-USD","LTC-USD","BCH-USD","ATOM-USD","FIL-USD",
  "ALGO-USD","NEAR-USD","ICP-USD","APT-USD","OP-USD","ARB-USD","INJ-USD","SUI-USD",
  "SEI-USD","TIA-USD","RNDR-USD","FET-USD","GRT-USD","SAND-USD","MANA-USD","AXS-USD",
  "IMX-USD","BLUR-USD","LDO-USD","AAVE-USD","MKR-USD","CRV-USD","SNX-USD","COMP-USD",
];

// Crypto equity proxies — stocks that trade like crypto
const CRYPTO_EQUITIES = [
  "COIN","MARA","RIOT","CLSK","HUT","BTBT","CIFR","IREN","CORZ","WULF","MSTR",
  "IBIT","FBTC","GBTC","BITB","ARKB","BITO","BRRR","HODL","EZBC",
  "SQ","PYPL","HOOD","NVDA","TSLA",
];

const CRYPTO_TICKERS = new Set([...PURE_CRYPTO_COINS, ...CRYPTO_EQUITIES]);
const isCrypto     = sym => CRYPTO_TICKERS.has(sym);

// ─── ENTRY ZONE CALCULATOR ────────────────────────────────────────────────────
function calcEntryZone(result, symbol) {
  if (!result) return null;
  const { price, ema21, vwap, atr, rsi, bb, gate, rvol } = result;
  if (!price || !atr) return null;

  const crypto = isCrypto(symbol);
  const atrMult = crypto ? 0.75 : 0.4; // wider for crypto
  const chaseBuffer = crypto ? 1.5 : 0.6; // don't chase above this many ATRs

  let idealLow, idealHigh, dontChaseAbove, basis, note;

  // Scenario A: BB Squeeze firing — momentum entry, act near current price
  if (bb?.isSqueeze && rvol?.ratio >= 1.2) {
    idealLow = price - (atrMult * atr);
    idealHigh = price + (atrMult * 0.5 * atr);
    dontChaseAbove = price + (chaseBuffer * atr);
    basis = "BB Squeeze breakout";
    note = "Momentum entry — act quickly or set limit at current price. Squeeze breakouts don't wait.";
  }
  // Scenario B: RSI reset, price near EMA21 — ideal pullback entry
  else if (rsi < (crypto ? 50 : 50) && price <= ema21 * 1.015) {
    idealLow = Math.min(ema21, vwap || ema21) - (atrMult * atr * 0.5);
    idealHigh = Math.max(ema21, vwap || ema21) + (atrMult * atr * 0.3);
    dontChaseAbove = idealHigh + (chaseBuffer * atr);
    basis = "EMA21 / VWAP confluence";
    note = "Price is at support. Set a buy limit within this zone and let it come to you.";
  }
  // Scenario C: Price above EMA21 but RSI not extended — wait for pullback
  else if (rsi < (crypto ? 65 : 60) && price > ema21) {
    idealLow = ema21 - (atrMult * atr * 0.3);
    idealHigh = ema21 + (atrMult * atr * 0.5);
    dontChaseAbove = price + (atrMult * atr);
    basis = "EMA21 pullback target";
    note = "Don't chase current price. Set a limit at EMA21 and wait for the pullback.";
  }
  // Scenario D: Extended — wait for significant reset
  else if (rsi > (crypto ? 72 : 65)) {
    const pullbackTarget = ema21;
    idealLow = pullbackTarget - (atrMult * atr);
    idealHigh = pullbackTarget + (atrMult * atr * 0.5);
    dontChaseAbove = price; // don't enter at current price
    basis = "EMA21 reset target (extended)";
    note = "Stock is extended. Wait for RSI to reset below " + (crypto ? "60" : "55") + " and price to pull back to EMA21 before entering.";
  }
  // Default: near current price with ATR buffer
  else {
    idealLow = price - (atrMult * atr);
    idealHigh = price + (atrMult * atr * 0.3);
    dontChaseAbove = price + (chaseBuffer * atr);
    basis = "Current price ± ATR";
    note = "Price is reasonably positioned. A limit order within this zone gives you a cushion vs. market order.";
  }

  return {
    idealLow:  Math.max(0, idealLow),
    idealHigh: Math.max(0, idealHigh),
    dontChaseAbove: Math.max(0, dontChaseAbove),
    basis,
    note,
    isCrypto: crypto,
  };
}

const SECTORS = {
  "AI / Semis": ["NVDA", "AMD", "AVGO", "ARM", "QCOM", "MRVL", "INTC", "TSM", "AMAT", "KLAC"],
  "Mega Cap Tech": ["AAPL", "MSFT", "GOOGL", "META", "AMZN", "TSLA", "NFLX", "CRM", "ORCL"],
  "EV / Auto": ["TSLA", "RIVN", "GM", "F", "LCID", "NIO"],
  "ETFs": ["SPY", "QQQ", "SOXX", "SMH", "XLK", "ARKK", "IWM", "DIA", "GLD"],
  "Biotech": ["MRNA", "BNTX", "REGN", "BIIB", "VRTX", "GILD", "AMGN"],
  "Financials": ["JPM", "GS", "MS", "BAC", "V", "MA", "PYPL", "COIN"],
  "High Growth": ["PLTR", "SNOW", "NET", "DDOG", "ZS", "CRWD", "SHOP", "MELI"],
};

// ─── FORMATTERS ───────────────────────────────────────────────────────────────
const ff = (n, d = 2) => n != null && !isNaN(n) ? Number(n).toFixed(d) : "—";
const fp = n => n != null && !isNaN(n) ? `${n >= 0 ? "+" : ""}${Number(n).toFixed(2)}%` : "—";
const fc = n => n != null && !isNaN(n) ? `$${Number(n).toFixed(2)}` : "—";

// Reusable Calmar ratio calculator from a scan result
function calcCalmar(result) {
  // Prefer the proper 2-year Calmar calculated inside runAnalysis
  if (result?.calmar2y != null) return result.calmar2y;
  // Fallback for cached results that predate calmar2y
  const stockReturn63 = result?.rs?.stockReturn ?? null;
  const annualReturn63 = stockReturn63 != null ? stockReturn63 * (252 / 63) : null;
  const rawDrawdown = result?.range52w?.distFromHigh != null
    ? Math.abs(result.range52w.distFromHigh) : null;
  if (annualReturn63 == null || rawDrawdown == null || rawDrawdown < 8) return null;
  return Math.min(Math.abs(annualReturn63 / rawDrawdown), 9.9);
}
function isPrimeSetup(result, verdict) {
  if (!result) return false;
  if (typeof result.isPrime === "boolean") return result.isPrime;
  if (!verdict) return false;
  if (verdict.layer !== "buy") return false;
  if (result.gate?.label !== "GO" && result.gate?.label !== "CLOSE") return false;
  const calmar = calcCalmar(result);
  if (calmar == null || calmar < 1.5) return false;
  const price = result.price, t1 = result.t1, atr = result.atr;
  if (!price || !t1 || !atr) return false;
  const stop = price - 2 * atr;
  if (stop >= price) return false;
  const rr = Math.abs((t1 - price) / (price - stop));
  return rr >= 2.0;
}

function primeMessage(result, verdict) {
  const vixLevel = verdict?.vixLevel ?? 0;
  const riskOff = vixLevel >= 20;
  const calmar = calcCalmar(result) ?? 0;
  const cmfPos = result?.cmf != null && result.cmf > 0.05;
  if (riskOff && cmfPos) return `★ PRIME SETUP — Institutional money flowing in during a risk-off market (VIX ${vixLevel.toFixed(0)}). Highest-conviction contrarian setup. Extended hold recommended — 3–5 weeks. Don't cut on normal pullbacks.`;
  if (riskOff) return `★ PRIME SETUP — Valid signal in a risk-off market (VIX ${vixLevel.toFixed(0)}). Expect a longer hold than usual (3–5 weeks vs 1–2) and more chop on the way up. Give the thesis room — don't cut early on normal pullbacks.`;
  return `★ PRIME SETUP — R:R and risk quality both confirmed (Calmar ${calmar.toFixed(1)}). Highest conviction entry. All layers aligned.`;
}

function getPutsSnapshot(symbol) {
  try { return JSON.parse(localStorage.getItem(`putsSnapshot_${symbol}`) || "null"); } catch { return null; }
}
function setPutsSnapshot(symbol, rsi, price, cmf) {
  try { localStorage.setItem(`putsSnapshot_${symbol}`, JSON.stringify({ rsi, price, cmf, date: Date.now() })); } catch {}
}
function clearPutsSnapshot(symbol) {
  try { localStorage.removeItem(`putsSnapshot_${symbol}`); } catch {}
}
function calcCoverPuts(result, symbol) {
  // Returns null (no cover signal), "COVER" (close now), or "WATCH" (take partial)
  if (!result?.putsSignal?.fullSignal && !result?.putsSignal?.partialSignal) return null;
  const rsi = result.rsi;
  const cmf = result.cmf;
  const price = result.price;
  const d1 = result.d1; // downside target 1
  const snap = getPutsSnapshot(symbol);
  const rsiFall = snap ? (snap.rsi - rsi) : null; // how many points RSI has fallen since signal
  const priceFall = snap ? ((snap.price - price) / snap.price * 100) : null; // % price drop since signal

  // COVER — strong exit signal, multiple conditions
  const rsiOversold = rsi != null && rsi < 28;
  const bigRsiFall = rsiFall != null && rsiFall > 25; // RSI fell 25+ points since signal fired
  const hitTarget = d1 != null && price <= d1;
  const cmfTurned = snap && snap.cmf < 0 && cmf != null && cmf > 0.05; // CMF was negative, now positive
  const bigPriceDrop = priceFall != null && priceFall > 15; // stock dropped 15%+ since puts signal

  const coverReasons = [];
  if (rsiOversold) coverReasons.push(`RSI ${rsi?.toFixed(0)} — deeply oversold`);
  if (bigRsiFall && snap) coverReasons.push(`RSI fell ${rsiFall.toFixed(0)} pts since signal (${snap.rsi.toFixed(0)} → ${rsi?.toFixed(0)})`);
  if (hitTarget) coverReasons.push(`hit D1 target $${d1.toFixed(2)}`);
  if (cmfTurned) coverReasons.push(`CMF turned positive (${cmf?.toFixed(3)}) — institutions buying`);
  if (bigPriceDrop && snap) coverReasons.push(`down ${priceFall.toFixed(1)}% since signal`);

  // bigPriceDrop alone is not sufficient — trade is working, don't interrupt it
  // Only count it toward COVER when combined with another condition
  const significantReasons = coverReasons.filter(r => !r.includes('% since signal') || coverReasons.length >= 2);
  if (significantReasons.length >= 2 || hitTarget || cmfTurned) return { level: "COVER", reasons: coverReasons, snap };
  if (significantReasons.length === 1 && !coverReasons[0]?.includes('% since signal')) return { level: "WATCH", reasons: coverReasons, snap };
  return null;
}

function getCallsSnapshot(symbol) {
  try { return JSON.parse(localStorage.getItem(`callsSnapshot_${symbol}`) || "null"); } catch { return null; }
}
function setCallsSnapshot(symbol, rsi, price, cmf) {
  try { localStorage.setItem(`callsSnapshot_${symbol}`, JSON.stringify({ rsi, price, cmf, date: Date.now() })); } catch {}
}
function clearCallsSnapshot(symbol) {
  try { localStorage.removeItem(`callsSnapshot_${symbol}`); } catch {}
}
function calcCoverCalls(result, symbol) {
  // Returns null | { level: "COVER"|"WATCH", reasons, snap }
  if (!result?.callsSignal?.fullSignal && !result?.callsSignal?.partialSignal) return null;
  const rsi = result.rsi;
  const cmf = result.cmf;
  const price = result.price;
  const t1 = result.t1; // upside target 1
  const snap = getCallsSnapshot(symbol);
  const rsiRise = snap ? (rsi - snap.rsi) : null; // points RSI has risen since signal
  const priceRise = snap ? ((price - snap.price) / snap.price * 100) : null; // % price gain since signal
  const rsiBearishDiv = result?.rsiDiv?.bearish === true;
  const bigRsiRise = rsiRise != null && rsiRise > 25; // RSI ran 25+ pts since signal
  const hitTarget = t1 != null && price >= t1;
  const cmfTurned = snap && snap.cmf > 0 && cmf != null && cmf < -0.05; // CMF was positive, now distributing
  const bigPriceRise = priceRise != null && priceRise > 15; // up 15%+ since signal
  const coverReasons = [];
  if (rsiBearishDiv) coverReasons.push("RSI bearish divergence — distribution signal");
  if (bigRsiRise && snap) coverReasons.push(`RSI ran ${rsiRise.toFixed(0)} pts since signal (${snap.rsi.toFixed(0)} → ${rsi?.toFixed(0)})`);
  if (hitTarget) coverReasons.push(`hit T1 target $${t1.toFixed(2)}`);
  if (cmfTurned) coverReasons.push(`CMF turned negative (${cmf?.toFixed(3)}) — institutions distributing`);
  if (bigPriceRise && snap) coverReasons.push(`up ${priceRise.toFixed(1)}% since signal`);

  const strongSingle = rsiBearishDiv || hitTarget || cmfTurned;
  if (coverReasons.length >= 2 || hitTarget || cmfTurned) return { level: "COVER", reasons: coverReasons, snap };
  if (strongSingle && coverReasons.length === 1) return { level: "WATCH", reasons: coverReasons, snap };
  if (coverReasons.length >= 2) return { level: "WATCH", reasons: coverReasons, snap };
  return null;
}

function calmarColor(v) {
  if (v == null) return null;
  return v >= 3 ? "#22c55e" : v >= 1 ? "#86efac" : v >= 0.5 ? "#eab308" : "#ef4444";
}
function calmarLabel(v) {
  if (v == null) return "—";
  return v >= 3 ? "Strong" : v >= 1 ? "Accept." : v >= 0.5 ? "Weak" : "Poor";
}

function signalColor(s) {
  if (!s) return T.textSub;
  const u = s.toUpperCase();
  if (u.includes("STRONG BUY")) return T.green;
  if (u.includes("BUY")) return T.green;
  if (u.includes("DEATH") || u.includes("200 SMA STRUCTURE") || u.includes("STRONG SELL") || u.includes("SELL NOW")) return T.red;
  if (u.includes("SELL") || u.includes("TRIM")) return T.red;
  if (u.includes("WAIT") || u.includes("EXTENDED")) return T.orange;
  if (u.includes("CAUTION") || u.includes("WATCH")) return T.yellow;
  return T.textSub;
}

// ─── VERDICT BADGE ────────────────────────────────────────────────────────────
// ─── COMPANY NAME HOVER ───────────────────────────────────────────────────────
function TickerLabel({ symbol }) {
  const [name, setName] = useState(COMPANY_NAMES[symbol] || null);

  useEffect(() => {
    if (!name) {
      getCompanyName(symbol).then(n => { if (n && n !== symbol) setName(n); });
    }
  }, [symbol]);

  const shortName = name && name !== symbol
    ? name
        .replace(/ Inc\.?$/i, "").replace(/ Corp\.?$/i, "").replace(/ Corporation$/i, "")
        .replace(/ Limited$/i, "").replace(/ Ltd\.?$/i, "").replace(/ Holdings$/i, "")
        .replace(/ Technologies$/i, " Tech").replace(/ International$/i, " Intl")
        .trim().slice(0, 22)
    : null;

  return (
    <span style={{ display: "inline-flex", flexDirection: "column", gap: 1 }}>
      <span style={{ fontWeight: 800, letterSpacing: "0.04em" }}>{symbol}</span>
      {shortName && (
        <span style={{ fontSize: 9, fontWeight: 400, color: T.textDim, letterSpacing: "0.01em", lineHeight: 1, whiteSpace: "nowrap" }}>
          {shortName}
        </span>
      )}
    </span>
  );
}

function VerdictBadge({ verdict, isDark, compact, hasAI, displayLabel = null }) {
  const [show, setShow] = React.useState(false);
  const ref = React.useRef(null);
  if (!verdict) return null;
  const bg  = isDark ? verdict.darkBg  : verdict.bg;
  const col = isDark ? verdict.darkColor : verdict.color;
  const flags = verdict.riskFlags || [];
  const warnColor = "#f59e0b";

  // Timing note — only for confirmed buy entries
  const timingNote = verdict.layer === "buy"
    ? "Swing trade — expect 5-20 trading days to play out. Give it time unless your stop is hit."
    : verdict.layer === "recov"
    ? "Mean reversion — shorter hold, 5-10 days. Exit when RSI hits 55-60 or Williams %R reaches -30."
    : null;

  const pill = compact
    ? <span style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "3px 9px", borderRadius: 5, fontSize: 10, fontWeight: 700, color: col, background: bg, border: `1px solid ${col}44`, cursor: "help", letterSpacing: "0.03em" }}>
        {verdict.layer === "exit" ? (/STOP LOSS|MOMENTUM LOSS|TRAILING STOP/i.test(verdict.label) ? "▼ " : "✓ ") : ""}{verdict.short}
        {flags.length > 0 && <span style={{ fontSize: 9, fontWeight: 800, color: warnColor, marginLeft: 1 }}>⚠</span>}
        {!hasAI && <span style={{ width: 5, height: 5, borderRadius: "50%", background: col, opacity: 0.4, flexShrink: 0 }} />}
      </span>
    : <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, color: col, background: bg, border: `1px solid ${col}55`, cursor: "help", letterSpacing: "0.02em" }}>
        {verdict.layer === "exit"
          ? (/STOP LOSS|MOMENTUM LOSS|TRAILING STOP/i.test(verdict.label) ? "▼" : "✓")
          : verdict.layer === "buy" ? "▲" : verdict.layer === "bearish" ? "▼" : verdict.layer === "watch" ? "◆" : verdict.layer === "wait" ? "◈" : verdict.layer === "accum" ? "⟳" : verdict.layer === "recov" ? "↩" : "–"}
        {" "}{displayLabel || verdict.label}
        {verdict.layer === "accum" && <span style={{ fontSize: 9, marginLeft: 4, opacity: 0.8 }}>LT</span>}
        {verdict.bubbleEntry && <span style={{ fontSize: 9, marginLeft: 4, color: "#fbbf24" }}>⚡</span>}
        {flags.length > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 4, padding: "1px 6px", borderRadius: 4, background: `${warnColor}22`, border: `1px solid ${warnColor}55`, fontSize: 10, fontWeight: 800, color: warnColor }}>
            ⚠ {flags.length}
          </span>
        )}
      </div>;

  return (
    <span ref={ref} style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      {pill}
      {show && ref.current && (
        <div style={{
          position: "fixed",
          zIndex: 9999,
          width: 320,
          background: T.surface,
          border: `1px solid ${col}66`,
          borderRadius: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          overflow: "hidden",
          pointerEvents: "none",
          top: (() => { try { return ref.current.getBoundingClientRect().bottom + 8; } catch { return 100; } })(),
          left: (() => { try { const r = ref.current.getBoundingClientRect(); return Math.min(r.left, window.innerWidth - 330); } catch { return 0; } })(),
        }}>
          {/* Header — verdict label + confidence */}
          {(() => {
            const conf = calcSignalConfidence(verdict._result, verdict);
            return (
              <div style={{ padding: "10px 14px 8px", background: `${col}18`, borderBottom: `1px solid ${col}33` }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: conf ? 6 : 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 900, color: col, letterSpacing: "0.06em", flex: 1 }}>
                    {verdict.layer === "buy" ? "▲" : verdict.layer === "bearish" ? "▼" : verdict.layer === "watch" ? "◆" : verdict.layer === "wait" ? "◈" : verdict.layer === "accum" ? "⟳" : verdict.layer === "recov" ? "↩" : "–"} {verdict.label}
                  </div>
                  {conf && (
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: conf.color, letterSpacing: "0.08em" }}>{conf.label}</div>
                      <div style={{ fontSize: 11, fontWeight: 900, color: conf.color, fontFamily: "monospace" }}>{conf.pct}%</div>
                    </div>
                  )}
                </div>
                {conf && (
                  <div style={{ position: "relative", height: 4, borderRadius: 2, background: `${conf.color}22`, overflow: "hidden" }}>
                    <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${conf.pct}%`, background: conf.color, borderRadius: 2, transition: "width 0.4s" }} />
                  </div>
                )}
              </div>
            );
          })()}
          {/* Signal explanation */}
          <div style={{ padding: "10px 14px 6px" }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 5 }}>WHAT THIS MEANS</div>
            <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.7 }}>{verdict.reason}</div>
          </div>
          {/* User action — the important part */}
          <div style={{ margin: "0 14px 10px", padding: "8px 10px", background: `${col}12`, border: `1px solid ${col}33`, borderRadius: 7 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: col, letterSpacing: "0.1em", marginBottom: 5 }}>YOUR ACTION</div>
            <div style={{ fontSize: 11, color: T.text, lineHeight: 1.7, fontWeight: 500 }}>{verdict.action}</div>
          </div>
          {/* Timing note */}
          {timingNote && (
            <div style={{ padding: "0 14px 10px" }}>
              <div style={{ fontSize: 10, color: T.textDim, fontStyle: "italic" }}>⏱ {timingNote}</div>
            </div>
          )}
          {/* Risk flags */}
          {flags.length > 0 && (
            <div style={{ padding: "8px 14px 10px", borderTop: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: warnColor, letterSpacing: "0.1em", marginBottom: 5 }}>⚠ RISK FLAGS</div>
              {flags.map((f, i) => (
                <div key={i} style={{ fontSize: 10, color: T.textSub, marginBottom: 3, lineHeight: 1.6 }}>
                  <span style={{ color: warnColor, fontWeight: 700 }}>• {f.label}:</span> {f.desc}
                </div>
              ))}
            </div>
          )}
          {/* AI hint */}
          {!hasAI && (
            <div style={{ padding: "6px 14px 8px", borderTop: `1px solid ${T.border}`, fontSize: 10, color: T.textDim, fontStyle: "italic" }}>
              💡 Run ✦ AI Analysis to enhance this verdict with market context
            </div>
          )}
        </div>
      )}
    </span>
  );
}

function HoverTip({ content, children, maxWidth = 300, delayMs = 300 }) {
  const [show, setShow] = React.useState(false);
  const [pos, setPos] = React.useState({ top: 0, left: 0, flip: false });
  const ref = React.useRef(null);
  const tRef = React.useRef(null);

  const hide = () => { if (tRef.current) clearTimeout(tRef.current); tRef.current = null; setShow(false); };
  const scheduleShow = () => {
    if (!content) return;
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => {
      try {
        const r = ref.current?.getBoundingClientRect?.();
        if (!r) return;
        const preferAbove = r.top > 110;
        const flip = !preferAbove;
        const top = preferAbove ? (r.top - 10) : (r.bottom + 10);
        const left = Math.min(r.left, window.innerWidth - (maxWidth + 18));
        setPos({ top, left, flip });
        setShow(true);
      } catch {}
    }, delayMs);
  };

  // Mobile: long-press to show, tap to dismiss
  const pressRef = React.useRef(null);
  const onTouchStart = () => {
    if (!content) return;
    if (pressRef.current) clearTimeout(pressRef.current);
    pressRef.current = setTimeout(() => scheduleShow(), 450);
  };
  const onTouchEnd = () => { if (pressRef.current) clearTimeout(pressRef.current); pressRef.current = null; };

  return (
    <span
      ref={ref}
      style={{ display: "inline-flex", position: "relative" }}
      onMouseEnter={scheduleShow}
      onMouseLeave={hide}
      onClick={hide}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {children}
      {show && content && (
        <div
          style={{
            position: "fixed",
            zIndex: 9999,
            maxWidth,
            background: "#0d0d1a",
            color: "#fff",
            border: "1px solid rgba(245,158,11,0.25)",
            borderRadius: 10,
            boxShadow: "0 10px 34px rgba(0,0,0,0.65)",
            padding: "10px 12px",
            fontSize: 11,
            lineHeight: 1.55,
            pointerEvents: "none",
            top: pos.flip ? pos.top : (pos.top - 0),
            left: pos.left,
            transform: pos.flip ? "translateY(0)" : "translateY(-100%)",
            opacity: 1,
            transition: "opacity 0.12s ease, transform 0.12s ease",
            whiteSpace: "pre-line",
          }}
        >
          <div
            style={{
              position: "absolute",
              width: 10,
              height: 10,
              background: "#0d0d1a",
              borderLeft: "1px solid rgba(245,158,11,0.25)",
              borderTop: "1px solid rgba(245,158,11,0.25)",
              transform: "rotate(45deg)",
              left: 18,
              top: pos.flip ? -6 : undefined,
              bottom: pos.flip ? undefined : -6,
            }}
          />
          {content}
        </div>
      )}
    </span>
  );
}

function Badge({ text, color, bg }) {
  const col = color || signalColor(text);
  const u = (text || "").toUpperCase();
  const shape = u.includes("BUY") || u.includes("BULL") || u.includes("GO") || u.includes("RISING") || u.includes("CROSS") && col === T.green ? "▲ " :
                u.includes("SELL") || u.includes("BEAR") || u.includes("DEATH") || u.includes("200 SMA") || u.includes("TRIM") ? "▼ " :
                u.includes("SQUEEZE") || u.includes("CAUTION") || u.includes("WAIT") || u.includes("NOT YET") ? "◆ " : "";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", fontSize: F.sm, fontWeight: 600, padding: "3px 10px", borderRadius: 6, background: bg || `${col}22`, color: col, border: `1px solid ${col}44`, whiteSpace: "nowrap" }}>
      {shape}{text}
    </span>
  );
}

function Pill({ active, label }) {
  return <span title={label} style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: active ? T.red : T.border, boxShadow: active ? `0 0 6px ${T.red}` : "none", marginRight: 4 }} />;
}

function ScoreBar({ score, max = 9 }) {
  const pct = Math.min(Math.abs(score) / max * 100, 100);
  const col = score >= 2 ? T.green : score <= -2 ? T.red : T.yellow;
  return (
    <div style={{ height: 4, background: T.border, borderRadius: 2, marginTop: 4, overflow: "hidden" }}>
      <div style={{ height: "100%", width: `${pct}%`, background: col, borderRadius: 2, transition: "width 0.4s" }} />
    </div>
  );
}

function RangeBar({ score }) {
  if (score == null) return null;
  const col = score <= 20 ? T.green : score >= 80 ? T.red : score >= 65 ? T.orange : score <= 35 ? T.cyan : T.textSub;
  return (
    <div style={{ width: 60 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ fontSize: F.xs, color: col, fontWeight: 700 }}>{score}</span>
        <span style={{ fontSize: F.xs, color: T.textDim }}>/100</span>
      </div>
      <div style={{ height: 4, background: T.border, borderRadius: 2, position: "relative" }}>
        <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: "100%", background: `linear-gradient(90deg, ${T.green}, ${T.yellow}, ${T.red})`, opacity: 0.3, borderRadius: 2 }} />
        <div style={{ position: "absolute", top: "50%", transform: "translate(-50%,-50%)", left: `${score}%`, width: 8, height: 8, borderRadius: "50%", background: col, border: `2px solid ${T.bg}` }} />
      </div>
    </div>
  );
}

// ─── VIX TRANSLATOR ───────────────────────────────────────────────────────────
const VIX_LEVELS = [
  {
    range: "Below 12",
    min: 0, max: 12,
    label: "COMPLACENCY",
    color: "#4ade80",
    bg: "#4ade8010",
    mood: "Market is extremely calm. Investors are not worried about anything.",
    options: "Options are CHEAP. Best time to buy calls or puts — low premiums.",
    stocks: "Good for swing longs. Low volatility = steady trending moves.",
    watch: "Complacency can precede sharp selloffs. Don't get too comfortable.",
    actions: ["✓ Buy calls or puts — cheapest premiums of the cycle", "✓ Swing trade shares — clean trends", "✓ Ideal time to set up positions before a move", "⚠ Watch for sudden VIX spike — the calm before the storm"],
  },
  {
    range: "12 – 16",
    min: 12, max: 16,
    label: "CALM",
    color: "#86efac",
    bg: "#86efac10",
    mood: "Normal, healthy market. Mild background anxiety. This is where the market spends most of its time in bull markets.",
    options: "Options premiums are fair. Reasonable time to buy directional calls/puts.",
    stocks: "Best environment for swing trading shares. Clean signals, low noise.",
    watch: "Nothing alarming. Stay disciplined with your entry gate and stops.",
    actions: ["✓ Buy directional calls/puts at fair prices", "✓ Swing trade shares — ideal conditions", "✓ Follow your entry gate signals normally", "✓ Covered calls on extended positions work well here"],
  },
  {
    range: "16 – 20",
    min: 16, max: 20,
    label: "MILD CONCERN",
    color: "#fbbf24",
    bg: "#fbbf2410",
    mood: "Market is starting to get nervous. Elevated but not extreme. Often seen during earnings seasons or macro uncertainty.",
    options: "Premiums getting expensive. Consider selling options (covered calls, cash-secured puts) instead of buying.",
    stocks: "Still tradeable but expect choppier price action. Tighter stops recommended.",
    watch: "If VIX is rising (not just elevated), that's more concerning than the level itself. Watch the direction.",
    actions: ["~ Buy options only on the highest conviction setups", "✓ Selling covered calls generates more premium income", "✓ Shares still fine — tighten stops to 1×ATR", "⚠ Watch whether VIX is rising or falling — direction matters more than level"],
  },
  {
    range: "20 – 25",
    min: 20, max: 25,
    label: "ELEVATED FEAR",
    color: "#fb923c",
    bg: "#fb923c10",
    mood: "Market is genuinely worried. Institutional hedging is happening. This level is seen during corrections and before Fed announcements.",
    options: "Premiums are expensive. Buying options here risks getting crushed even if you're right on direction (IV crush).",
    stocks: "Volatile swings. Only the cleanest setups with wide stops. Reduce position sizes.",
    watch: "If VIX spikes to 25 and starts falling (VIX crush), that's actually a great time to buy calls — fear is peaking.",
    actions: ["✗ Avoid buying calls/puts unless VIX is actively falling", "✓ VIX falling from 25 → buy calls aggressively (fear peak)", "✓ Shares only on STRONG BUY signals with score 5+", "✓ Smaller position sizes — market is unpredictable", "⚠ Reduce overall exposure — wait for clarity"],
  },
  {
    range: "25 – 35",
    min: 25, max: 35,
    label: "HIGH FEAR",
    color: "#ef4444",
    bg: "#ef444410",
    mood: "Significant market stress. Seen during major selloffs, banking crises, geopolitical events. Retail is panicking.",
    options: "Options premiums are very expensive. DO NOT buy options — you will get IV crushed. Sell premium instead.",
    stocks: "Only trade if you have very high conviction. Use tight stops. Cash is a position.",
    watch: "Contrarian signal: when VIX hits 30-35 and retail sentiment is at extreme fear, markets often bounce hard. The panic is the signal.",
    actions: ["✗ Do NOT buy calls or puts — IV will crush you even if right", "✓ SELL premium: covered calls, cash-secured puts = collect the fear", "⚠ Look for VIX reversal — when it peaks and turns down, buy aggressively", "~ If holding shares, check your stops — this is when stops get hit", "✓ Extreme fear at 52w lows + RSI <30 = historical best buying opportunities"],
  },
  {
    range: "Above 35",
    min: 35, max: 100,
    label: "EXTREME PANIC",
    color: "#dc2626",
    bg: "#dc262610",
    mood: "Market crisis. Seen during COVID crash (VIX hit 85), 2008 financial crisis (80+), major black swan events. Everyone is terrified.",
    options: "Options are insanely expensive. Buying is almost always wrong. Selling premium generates massive income but has unlimited risk on naked positions.",
    stocks: "Either the best buying opportunity of the decade, or the beginning of a prolonged bear market. Requires macro context.",
    watch: "The VIX above 40 historically marks major bottoms. Not always, but statistically this is where long-term buyers step in. Warren Buffett buys here.",
    actions: ["✗ Never buy options — premiums are 3-5× normal price", "✓ If you have conviction: buy quality shares in tranches", "✓ VIX above 40 historically = generational buying opportunities", "⚠ Only invest what you can leave untouched for 6-12 months", "✓ Sell cash-secured puts on stocks you want to own at lower prices"],
  },
];

function VixTranslator() {
  const [vixInput, setVixInput] = useState("");
  const [direction, setDirection] = useState(null); // "rising" | "falling" | null

  const vixNum = parseFloat(vixInput);
  const level = !isNaN(vixNum) && vixNum > 0
    ? VIX_LEVELS.find(l => vixNum >= l.min && vixNum < l.max) || VIX_LEVELS[VIX_LEVELS.length - 1]
    : null;

  return (
    <div style={{ background: T.bg, borderRadius: 10, padding: 18, border: `1px solid ${T.border}` }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: F.sm, fontWeight: 700, color: T.text }}>📊 VIX Translator</div>
          <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 2 }}>Check VIX at cnbc.com/quotes/VIX or TradingView ($VIX)</div>
        </div>
        <a href="https://finance.yahoo.com/quote/%5EVIX/" target="_blank" rel="noreferrer"
          style={{ fontSize: F.xs, color: T.cyan, textDecoration: "none", padding: "4px 10px", border: `1px solid ${T.cyan}44`, borderRadius: 6 }}>
          Check VIX ↗
        </a>
      </div>

      {/* Input row */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 4 }}>CURRENT VIX LEVEL</div>
          <input
            type="number" placeholder="e.g. 18.5" value={vixInput}
            onChange={e => setVixInput(e.target.value)}
            style={{ width: "100%", background: T.surface, border: `1px solid ${T.border}`, color: T.text, padding: "8px 12px", fontSize: F.md, fontFamily: "'JetBrains Mono', monospace", borderRadius: 8, fontWeight: 700 }}
          />
        </div>
        <div>
          <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 4 }}>DIRECTION</div>
          <div style={{ display: "flex", gap: 6 }}>
            {[["↑ Rising", "rising", T.red], ["↓ Falling", "falling", T.green]].map(([lbl, val, col]) => (
              <button key={val} onClick={() => setDirection(d => d === val ? null : val)}
                style={{ padding: "8px 14px", background: direction === val ? `${col}22` : T.surface, border: `1px solid ${direction === val ? col : T.border}`, color: direction === val ? col : T.textSub, fontSize: F.sm, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", borderRadius: 8 }}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* VIX Scale bar */}
      <div style={{ marginBottom: level ? 16 : 0 }}>
        <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginBottom: 4 }}>
          {VIX_LEVELS.map((l, i) => (
            <div key={i} style={{ flex: 1, background: l.color, opacity: level?.label === l.label ? 1 : 0.25, transition: "opacity 0.3s" }} />
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: F.xs, color: T.textDim }}>
          <span>0 — Calm</span><span>16</span><span>20</span><span>25</span><span>35</span><span>Panic — 80+</span>
        </div>
      </div>

      {/* Result */}
      {level && (
        <div style={{ background: level.bg, border: `1px solid ${level.color}44`, borderRadius: 10, padding: 16, animation: "fadeIn 0.2s ease" }}>
          {/* Level header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: F.xl, fontWeight: 700, color: level.color }}>
                VIX {ff(vixNum, 1)} — {level.label}
              </div>
              <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 2 }}>Range: {level.range}</div>
              {(() => {
                const im = vixImpliedDailyMove(vixNum);
                if (!im) return null;
                return (
                  <div style={{ fontSize: F.sm, color: T.textSub, marginTop: 8, lineHeight: 1.5 }}>
                    Implied 1-day move (1σ): <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, color: T.cyan }}>{im.label}</span>
                    <span style={{ color: T.textDim }}> · ÷16 shortcut ≈ {im.ruleOf16Pct.toFixed(2)}%</span>
                  </div>
                );
              })()}
            </div>
            {direction && (
              <div style={{ padding: "6px 14px", background: direction === "falling" ? `${T.green}22` : `${T.red}22`, border: `1px solid ${direction === "falling" ? T.green : T.red}44`, borderRadius: 8, fontSize: F.sm, fontWeight: 700, color: direction === "falling" ? T.green : T.red }}>
                {direction === "falling" ? "↓ VIX FALLING — BULLISH" : "↑ VIX RISING — BEARISH"}
              </div>
            )}
          </div>

          {/* Mood */}
          <div style={{ marginBottom: 12, padding: "10px 14px", background: T.surface, borderRadius: 8 }}>
            <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, marginBottom: 4 }}>MARKET MOOD</div>
            <div style={{ fontSize: F.sm, color: T.text, lineHeight: 1.6 }}>{level.mood}</div>
          </div>

          {/* Direction-specific override */}
          {direction === "falling" && vixNum >= 20 && (
            <div style={{ marginBottom: 12, padding: "10px 14px", background: `${T.green}15`, border: `1px solid ${T.green}44`, borderRadius: 8 }}>
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.green, marginBottom: 4 }}>⚡ VIX CRUSH IN PROGRESS</div>
              <div style={{ fontSize: F.sm, color: T.text, lineHeight: 1.6 }}>
                VIX falling from elevated levels is one of the strongest buy signals that exists. Fear is peaking and unwinding — institutions are buying aggressively. This is the ideal time to enter bullish calls or buy shares on strong setups.
              </div>
            </div>
          )}

          {direction === "rising" && vixNum < 20 && (
            <div style={{ marginBottom: 12, padding: "10px 14px", background: `${T.orange}15`, border: `1px solid ${T.orange}44`, borderRadius: 8 }}>
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.orange, marginBottom: 4 }}>⚠ VIX EXPANDING — CAUTION</div>
              <div style={{ fontSize: F.sm, color: T.text, lineHeight: 1.6 }}>
                VIX rising even from low levels is a warning. Avoid adding new options positions. If holding calls, consider taking partial profits. Rising VIX = rising premiums going forward, but your existing positions may suffer from negative delta.
              </div>
            </div>
          )}

          {/* Three columns */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
            <div style={{ background: T.surface, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, marginBottom: 6 }}>OPTIONS STRATEGY</div>
              <div style={{ fontSize: F.sm, color: T.text, lineHeight: 1.6 }}>{level.options}</div>
            </div>
            <div style={{ background: T.surface, borderRadius: 8, padding: 12 }}>
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, marginBottom: 6 }}>SHARES STRATEGY</div>
              <div style={{ fontSize: F.sm, color: T.text, lineHeight: 1.6 }}>{level.stocks}</div>
            </div>
          </div>

          {/* Watch for */}
          <div style={{ marginBottom: 12, padding: "10px 14px", background: `${T.yellow}10`, border: `1px solid ${T.yellow}33`, borderRadius: 8 }}>
            <div style={{ fontSize: F.xs, fontWeight: 700, color: T.yellow, marginBottom: 4 }}>👁 WATCH FOR</div>
            <div style={{ fontSize: F.sm, color: T.text, lineHeight: 1.6 }}>{level.watch}</div>
          </div>

          {/* Action list */}
          <div>
            <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, marginBottom: 8 }}>RECOMMENDED ACTIONS</div>
            {level.actions.map((a, i) => {
              const col = a.startsWith("✓") ? T.green : a.startsWith("✗") ? T.red : a.startsWith("⚠") ? T.orange : T.textSub;
              return (
                <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: F.sm, color: col, flexShrink: 0, width: 14 }}>{a.slice(0, 1)}</span>
                  <span style={{ fontSize: F.sm, color: T.text, lineHeight: 1.5 }}>{a.slice(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!level && (
        <div style={{ textAlign: "center", padding: "16px 0", fontSize: F.sm, color: T.textDim }}>
          Enter the current VIX level above to see exactly how to interpret it and what trades make sense
        </div>
      )}
    </div>
  );
}

// ─── TRADE TYPE ADVISOR ───────────────────────────────────────────────────────
function calcTradeAdvisor(result, rr) {
  const r = result;
  const bullish = r.buyScore >= 2;
  const bearish = r.buyScore <= -2;
  const strongBull = r.buyScore >= 4;
  const strongBear = r.buyScore <= -4;
  const goldenCross = r.goldenCross;
  const deathCross = r.deathCross;
  const bbSqueeze = r.bb?.isSqueeze;
  const rsiOk = r.rsi != null && r.rsi < 65 && r.rsi > 30;
  const rsiOver = r.rsi != null && r.rsi > 65;
  const rsiUnder = r.rsi != null && r.rsi < 35;
  const cmfPos = r.cmf != null && r.cmf > 0.05;
  const cmfNeg = r.cmf != null && r.cmf < -0.05;
  const aboveVwap = r.vwap && r.price > r.vwap;
  const belowVwap = r.vwap && r.price <= r.vwap;
  const nearLow = r.range52w?.nearLow;
  const nearHigh = r.range52w?.nearHigh;
  const rrGood = rr != null && rr >= 2;
  const rrExcellent = rr != null && rr >= 3;
  const threeDayClear = !r.threeDayRule?.postDrop?.active && !r.threeDayRule?.earningsGap?.active;
  const extended = r.rsi > 65 && r.bb && r.price > r.bb.upper;

  // ── SHARES (swing long) ──
  const sharesMet = [
    [goldenCross, "Golden Cross active"],
    [bullish, "Composite score bullish"],
    [cmfPos, "Institutional accumulation (CMF+)"],
    [aboveVwap, "Price above VWAP"],
    [rrGood, "R:R at least 2:1"],
    [threeDayClear, "3-day rule clear"],
    [!deathCross, "200 SMA structure OK"],
  ];
  const sharesMiss = [
    [!goldenCross, "Wait for golden cross (SMA50 > SMA200)"],
    [!bullish, "Score too low — wait for more signals to align"],
    [!cmfPos, "CMF not positive — institutions not accumulating"],
    [!aboveVwap, "Price below VWAP — institutions not buying yet"],
    [!rrGood, "R:R below 2:1 — risk not justified"],
    [!threeDayClear, "3-day rule active — wait for cooldown"],
    [deathCross, "200 SMA structure bearish — trend is bearish"],
  ];
  const sharesScore = sharesMet.filter(([c]) => c).length;

  // ── CALLS (bullish options) ──
  const callsMet = [
    [strongBull, "Strong bullish score (5+)"],
    [bbSqueeze, "BB Squeeze loading — big move imminent"],
    [rsiOk, "RSI not extended — room to run"],
    [aboveVwap, "Price above VWAP"],
    [rrExcellent, "R:R 3:1+ — leverage justified"],
    [threeDayClear, "3-day rule clear"],
    [!extended, "Price not extended — premiums reasonable"],
  ];
  const callsMiss = [
    [!strongBull, "Score below 5 — not strong enough for leveraged play"],
    [!bbSqueeze, "No BB Squeeze — no confirmed volatility catalyst"],
    [rsiOver, "RSI extended — options premiums inflated, IV crush risk"],
    [belowVwap, "Below VWAP — don't buy calls below institutional average"],
    [!rrExcellent, "R:R below 3:1 — options need better setup"],
    [!threeDayClear, "Post-gap or post-drop — avoid options during repricing"],
    [extended, "Price extended above BB — wait for pullback before calls"],
  ];
  const callsScore = callsMet.filter(([c]) => c).length;

  // ── PUTS (bearish options) ──
  // Core principle: if short qualifies, puts qualify. Same bet, different instrument.
  // Short-level conviction (score ≤ -5, death cross) relaxes CMF — neutral flow on a falling stock is fine
  const shortLevelConv = strongBear && deathCross;
  const inFreefall = r.range52w?.score != null && r.range52w.score <= 30 && deathCross;
  const bearishTrend = nearHigh || inFreefall;
  // CMF condition: strict (negative) for normal puts, relaxed (not actively accumulating) for short-level conviction
  const cmfValidPuts = shortLevelConv ? (r.cmf != null && r.cmf < 0.05) : cmfNeg; // 0.05 = not actively accumulating
  const putsMet = [
    [bearish, "Composite score bearish"],
    [deathCross, "200 SMA structure — macro trend bearish"],
    [cmfValidPuts, shortLevelConv ? "CMF not accumulating (relaxed — short-level conviction)" : "CMF negative — institutional distribution"],
    [belowVwap, "Price below VWAP"],
    [!goldenCross, "No golden cross (trend not bullish)"],
    [bearishTrend, inFreefall ? "Confirmed downtrend — continuation" : "Near 52-week high breaking down"],
    [r.sellCount >= 3 || shortLevelConv, shortLevelConv && r.sellCount < 3 ? `Short-level conviction (200 SMA structure bearish + score ≤ -5) — sell count relaxed` : `${r.sellCount}/5 sell conditions active`],
  ];
  const putsMiss = [
    [!bearish, "Score not bearish enough — weak put case"],
    [!deathCross, "200 SMA structure OK — macro trend not confirmed bearish"],
    [!cmfValidPuts, shortLevelConv ? "CMF shows active accumulation — institutions buying, avoid puts" : "CMF not negative — institutions not distributing"],
    [aboveVwap, "Price above VWAP — bulls still in control"],
    [goldenCross, "Golden cross active — never buy puts against a golden cross"],
    [!bearishTrend, "Neither near 52w high nor in confirmed downtrend"],
    [r.sellCount < 3, "Fewer than 3 sell conditions — not enough confirmation"],
  ];
  const putsScore = putsMet.filter(([c]) => c).length;

  // ── SHORT ──
  const shortMet = [
    [strongBear, "Strong bearish score (-5 or worse)"],
    [deathCross, "200 SMA structure bearish confirmed"],
    [cmfNeg, "Heavy distribution (CMF negative)"],
    [r.sellCount >= 4, "4+ sell conditions active"],
    [nearHigh, "Near 52-week high breaking down"],
    [!goldenCross, "No golden cross blocking short"],
  ];
  const shortMiss = [
    [!strongBear, "Score not strong enough to short (-5 required)"],
    [!deathCross, "200 SMA structure OK — shorting against trend is dangerous"],
    [!cmfNeg, "CMF not negative — no institutional selling confirmed"],
    [goldenCross, "Golden cross active — never short a golden cross"],
    [r.sellCount < 4, "Need 4+ sell conditions for high-conviction short"],
  ];
  const shortScore = shortMet.filter(([c]) => c).length;

  // ── COVERED CALLS (income) ──
  const coveredMet = [
    [rsiOver, "RSI elevated (>65) — stock extended, sell the upside"],
    [r.bb && r.price > r.bb.upper, "Price above BB upper — statistically extended"],
    [nearHigh, "Near 52-week high — less upside likely"],
    [bullish, "You hold shares (bullish position)"],
    [cmfPos, "Still accumulating — not full exit, just income"],
  ];
  const coveredScore = coveredMet.filter(([c]) => c).length;

  const trades = [
    { type: "SHARES", emoji: "📈", desc: "Swing long stock", score: sharesScore, max: 8, met: sharesMet, miss: sharesMiss, color: T.green, when: "Hold 5–20 days. Best for clean trend setups.", viable: sharesScore >= 5 },
    { type: "CALLS", emoji: "⚡", desc: "Bullish options", score: callsScore, max: 8, met: callsMet, miss: callsMiss, color: T.cyan, when: "Hold 3–14 days. Needs BB Squeeze + low VIX.", viable: callsScore >= 5 },
    { type: "PUTS", emoji: "🔻", desc: "Bearish options", score: putsScore, max: 8, met: putsMet, miss: putsMiss, color: T.red,
      when: rsiOver || (r.rsi != null && r.rsi < 38)
        ? (r.rsi != null && r.rsi < 38 ? "⚠ RSI deeply oversold — consider waiting for a relief bounce to 45-50 before entering. Puts bought at the bottom risk theta decay while the stock stabilizes." : "RSI overbought — classic puts setup. Hold 3–14 days. Needs confirmed breakdown.")
        : "Hold 3–14 days. Needs confirmed breakdown. Best entered on a failed bounce, not at the low.",
      viable: putsScore >= 5 },
    { type: "SHORT", emoji: "☠️", desc: "Short selling", score: shortScore, max: 7, met: shortMet, miss: shortMiss, color: T.orange, when: "Requires margin account. High risk. 200 SMA structure bearish essential.", viable: shortScore >= 5 },
    { type: "COV. CALLS", emoji: "💰", desc: "Covered call income", score: coveredScore, max: 5, met: coveredMet, miss: [], color: T.purple, when: "Sell calls against shares you own when stock is extended.", viable: coveredScore >= 3 },
  ];

  // Sort by score descending
  return trades.sort((a, b) => (b.score / b.max) - (a.score / a.max));
}

// ─── NARRATIVE ENGINE ────────────────────────────────────────────────────────
function generateNarrative(result, symbol) {
  const r = result;
  const p = r.price;
  const lines = [];

  // Overall verdict
  const bull = r.buyScore >= 2;
  const bear = r.buyScore <= -2;
  const strong = Math.abs(r.buyScore) >= 4;

  if (r.deathCross) {
    lines.push(`${symbol} is in a macro downtrend — 200 SMA structure is bearish (SMA50 below SMA200), meaning the long-term trend is bearish. This is a critical block on all new long entries until the cross resolves.`);
  } else if (r.goldenCross && strong && bull) {
    lines.push(`${symbol} is showing a strong bullish setup. The Golden Cross is active (SMA50 above SMA200) confirming the long-term trend is up, and ${r.buyScore} of the major indicators are aligned bullishly.`);
  } else if (bull) {
    lines.push(`${symbol} is showing a moderate bullish lean with a composite score of ${r.buyScore > 0 ? "+" : ""}${r.buyScore}. Not all indicators are aligned but the balance of signals favors the bulls.`);
  } else if (bear) {
    lines.push(`${symbol} is showing a bearish lean with a composite score of ${r.buyScore}. Multiple indicators are pointing down and caution is warranted on new longs.`);
  } else {
    lines.push(`${symbol} is in a neutral zone with a composite score of ${r.buyScore > 0 ? "+" : ""}${r.buyScore}. Signals are mixed — this is a wait-and-watch situation rather than an actionable setup.`);
  }

  // RSI interpretation
  if (r.rsi != null) {
    if (r.rsi < 30) lines.push(`RSI is at ${r.rsi.toFixed(1)} — deeply oversold territory. Historically this level marks exhaustion in selling pressure and precedes bounces, especially when CMF starts turning positive.`);
    else if (r.rsi < 40) lines.push(`RSI at ${r.rsi.toFixed(1)} is in the lower range, suggesting the stock has been under selling pressure but may be building a base for recovery.`);
    else if (r.rsi > 70) lines.push(`RSI is at ${r.rsi.toFixed(1)} — overbought. The stock is statistically extended and trimming or tightening stops is prudent. A pullback to the 50–55 RSI range is common before the next leg.`);
    else if (r.rsi > 60) lines.push(`RSI at ${r.rsi.toFixed(1)} is elevated but not extreme. The stock has momentum but is approaching the zone where pullbacks become more likely.`);
    else lines.push(`RSI at ${r.rsi.toFixed(1)} is in the neutral zone — no extreme reading in either direction.`);
    if (r.rsiDiv?.bearishDiv) lines.push(`Importantly, there is a bearish RSI divergence — price has been rising but RSI is declining. This is one of the most reliable early warnings of a trend reversal and deserves serious attention.`);
    if (r.rsiDiv?.bullishDiv) lines.push(`There is a bullish RSI divergence — price has been falling but RSI is rising. This hidden strength often precedes sharp bounces and is a high-conviction setup when combined with oversold RSI.`);
  }

  // MACD
  if (r.macd) {
    if (r.macd.crossover) lines.push(`MACD (chart context): bullish crossover — momentum turning up; confirm with CMF, gate, and R:R before acting.`);
    else if (r.macd.crossunder) lines.push(`MACD (chart context): bearish crossover — momentum turning down; v2 does not auto-trigger exits on MACD alone.`);
    else if (r.macd.bullish) lines.push(`MACD is in bullish territory (above signal line) but has not just crossed — the existing upward momentum continues.`);
    else lines.push(`MACD is bearish (below signal line) — existing downward momentum continues.`);
  }

  // CMF / Volume
  if (r.cmf != null) {
    if (r.cmf > 0.10) lines.push(`Chaikin Money Flow at ${r.cmf.toFixed(3)} shows strong institutional accumulation. Real money is flowing in — this is not just retail buying.`);
    else if (r.cmf > 0.05) lines.push(`CMF at ${r.cmf.toFixed(3)} shows mild institutional buying in the background.`);
    else if (r.cmf < -0.10) lines.push(`CMF at ${r.cmf.toFixed(3)} shows heavy distribution. Institutions are actively exiting this position. This is a serious warning sign — don't fight institutional selling.`);
    else if (r.cmf < -0.05) lines.push(`CMF at ${r.cmf.toFixed(3)} shows mild distribution — some institutional selling is occurring.`);
  }
  if (r.volDiv?.bearish) lines.push(`There is a volume divergence warning — price has been rising but volume is shrinking. This is the classic institutional distribution pattern: retail buyers absorb shares while institutions quietly exit.`);
  if (r.volDiv?.bullish) lines.push(`Volume divergence is bullish — price has been pulling back but volume is rising, suggesting institutions are accumulating on the dip.`);

  // Bollinger
  if (r.bb) {
    if (r.bb.isSqueeze) lines.push(`The Bollinger Bands are in a squeeze — volatility has compressed to its tightest point in months. This coiled spring setup typically precedes an explosive move in either direction. Watch for the first strong directional candle to confirm which way it breaks.`);
    else if (p > r.bb.upper) lines.push(`Price is trading above the upper Bollinger Band — statistically extended. This doesn't mean sell immediately but it does mean the easy money from this move may already be made. Consider trimming.`);
    else if (p < r.bb.lower) lines.push(`Price has broken below the lower Bollinger Band — statistically oversold at the band level. Combined with RSI under 35 this is often a high-quality bounce setup.`);
  }

  // 52w range
  if (r.range52w) {
    if (r.range52w.nearLow) lines.push(`At a 52-week range score of ${r.range52w.score}/100, ${symbol} is near its annual low. Historically this is where the best risk/reward setups form — but only when RSI and CMF confirm accumulation rather than continued breakdown.`);
    else if (r.range52w.nearHigh) lines.push(`At a 52-week range score of ${r.range52w.score}/100, ${symbol} is near its annual high. New highs can continue but require confirmed breakout volume. Without a volume surge, this is a high-risk entry zone.`);
  }

  // ADX
  if (r.adx) {
    if (r.adx.ranging) lines.push(`ADX at ${r.adx.adx.toFixed(1)} indicates the market is in a choppy, ranging environment. In ranging markets, trend-following signals like MACD and moving average crossovers are less reliable and produce more false signals.`);
    else if (r.adx.strong) lines.push(`ADX at ${r.adx.adx.toFixed(1)} confirms a strong trend — signals in this environment carry higher conviction and are worth following more aggressively.`);
  }

  return lines;
}

function generateFlipAnalysis(result) {
  const r = result;
  if (!r) return { flips: [], levels: [] };
  const sig = typeof r.signal === "string" ? r.signal : "";
  const flips = [];

  if (sig.includes("BUY") || sig === "HOLD") {
    // What would flip this to SELL
    flips.push({ type: "SELL TRIGGER", color: T.red, conditions: [
      r.ema21 ? `Price breaks below EMA21 at $${r.ema21.toFixed(2)} — short-term trend broken` : null,
      r.rsi ? `RSI climbs above 70 (currently ${r.rsi.toFixed(1)}) — overbought, time to trim` : null,
      `MACD crosses below signal line — momentum reversal confirmed`,
      r.bb?.upper != null ? `Price closes above BB upper band at $${r.bb.upper.toFixed(2)} — extended, consider trimming` : null,
    ].filter(Boolean) });
  }

  if (sig.includes("SELL") || sig === "HOLD" || r.deathCross) {
    // What would flip this to BUY
    const buyFlips = [];
    if (r.deathCross && r.sm50 && r.sm200) buyFlips.push(`SMA50 ($${r.sm50.toFixed(2)}) needs to cross back above SMA200 ($${r.sm200.toFixed(2)}) — 200 SMA structure must resolve`);
    if (r.ema21) buyFlips.push(`Price reclaims EMA21 at $${r.ema21.toFixed(2)} on volume — short-term trend restored`);
    if (r.rsi && r.rsi > 45) buyFlips.push(`RSI pulls back below 45 (currently ${r.rsi.toFixed(1)}) — momentum resets to neutral`);
    if (r.cmf != null && r.cmf < 0) buyFlips.push(`CMF turns positive — institutional accumulation begins`);
    if (r.vwap) buyFlips.push(`Price closes above VWAP at $${r.vwap.toFixed(2)} — institutional bias flips bullish`);
    if (buyFlips.length) flips.push({ type: "BUY TRIGGER", color: T.green, conditions: buyFlips });
  }

  // Price levels to watch (skip null/NaN — v2 sometimes has bb shell without band prices)
  const pushLevel = (label, price, significance) => {
    if (typeof price !== "number" || !Number.isFinite(price)) return;
    levels.push({ label, price, significance });
  };
  const levels = [];
  pushLevel("EMA21 (key trendline)", r.ema21, "Break above = bullish. Break below = bearish.");
  pushLevel("BB Upper (trim zone)", r.bb?.upper, "Extended above this — consider trimming long positions.");
  pushLevel("BB Lower (bounce zone)", r.bb?.lower, "Oversold at this level — watch for reversal confirmation.");
  pushLevel("Fib 61.8% (golden ratio)", r.fibs?.fib618, "Key institutional support/resistance level.");
  pushLevel("Pivot R1 (resistance)", r.pivots?.r1, "First resistance target above current price.");
  pushLevel("Pivot S1 (support)", r.pivots?.s1, "First support level below current price.");

  return { flips, levels };
}

const RETIRED_MACD_TRIGGERS = new Set(["macd_bullish", "macd_bearish"]);

function filterRetiredTriggers(list) {
  if (!Array.isArray(list)) return { kept: [], hadMacd: false };
  const kept = list.filter((t) => !RETIRED_MACD_TRIGGERS.has(t.type));
  return { kept, hadMacd: kept.length !== list.length };
}

// ─── TRIGGER CHECKER ──────────────────────────────────────────────────────────
function checkTriggers(triggers, result) {
  if (!triggers?.length || !result) return [];
  return triggers.map(t => {
    let fired = false;
    const v = t.value;
    switch (t.type) {
      case "rsi_below":         fired = result.rsi != null && result.rsi < v; break;
      case "rsi_above":         fired = result.rsi != null && result.rsi > v; break;
      case "price_above":       fired = result.price != null && result.price > v; break;
      case "price_below":       fired = result.price != null && result.price < v; break;
      case "macd_bullish":
      case "macd_bearish":
        fired = false;
        break;
      case "golden_cross":      fired = result.goldenCross; break;
      case "death_cross":       fired = result.deathCross; break;
      case "cmf_positive":      fired = result.cmf != null && result.cmf > 0.05; break;
      case "cmf_negative":      fired = result.cmf != null && result.cmf < -0.05; break;
      case "rvol_above":        fired = result.rvol?.ratio != null && result.rvol.ratio > (v || 1.5); break;
      case "adx_above":         fired = result.adx?.adx != null && result.adx.adx > (v || 25); break;
      case "bb_squeeze_fires":  fired = result.bb?.isSqueeze === false && result.bb?.wasSqueezing; break;
      case "above_vwap":        fired = result.price != null && result.vwap != null && result.price > result.vwap; break;
      case "below_vwap":        fired = result.price != null && result.vwap != null && result.price < result.vwap; break;
      case "above_ema21":       fired = result.price != null && result.ema21 != null && result.price > result.ema21; break;
      case "below_ema21":       fired = result.price != null && result.ema21 != null && result.price < result.ema21; break;
      default: fired = false;
    }
    return { ...t, fired };
  });
}

// ─── OPTIONS PLAY CALCULATOR ──────────────────────────────────────────────────
// ─── LOTTO SETUP DETECTOR ────────────────────────────────────────────────────
// Checks for the 3-pillar lotto confluence: Squeeze + RVOL surge + RS positive
// All three must align — any two is just a good trade, not a lotto
function calcLottoSetup(result) {
  if (!result) return null;

  const squeeze      = result.bb?.isSqueeze === true;
  const rvolSurge    = result.rvol?.ratio >= 1.8; // volume pre-loading — unusual vol while flat
  const rsPositive   = result.rs?.outperforming === true;
  const goldenCross  = result.goldenCross === true;
  const ema9above21  = result.ema9AboveEma21 === true;
  const tLineRun     = result.nineEmaEntry === true; // price hugging EMA9, pulling away
  const adxBuilding  = result.adx?.adx != null && result.adx.adx >= 20 && result.adx.adx < 35; // trending but not exhausted
  const rsiNotOB     = result.rsi != null && result.rsi < 70; // not overbought
  const volBreakout  = result.volBreakout?.isBreakout === true;

  // Core 3-pillar check
  const pillars = [squeeze, rvolSurge, rsPositive].filter(Boolean).length;
  const isLotto = pillars >= 3;

  // Bonus confirmations
  const bonusCount = [goldenCross, ema9above21, tLineRun, adxBuilding, rsiNotOB, volBreakout].filter(Boolean).length;

  // Conviction tier
  const conviction = isLotto
    ? bonusCount >= 4 ? "EXTREME" : bonusCount >= 2 ? "HIGH" : "MODERATE"
    : pillars === 2 ? "PARTIAL" : null;

  if (!conviction) return null;

  const signals = [];
  if (squeeze) signals.push({ label: "BB Squeeze", detail: "Bands at multi-week narrowest — volatility coiled for explosive move", pass: true });
  else signals.push({ label: "BB Squeeze", detail: "Bands not yet compressed — coil not set", pass: false });

  if (rvolSurge) signals.push({ label: `Volume Pre-Load (${result.rvol?.ratio?.toFixed(1)}x)`, detail: "Unusual volume while price is still flat — smart money building quietly", pass: true });
  else signals.push({ label: `RVOL (${result.rvol?.ratio?.toFixed(1) ?? "—"}x)`, detail: "Volume not elevated — no pre-loading signal", pass: false });

  if (rsPositive) signals.push({ label: "RS vs SPY: Outperforming", detail: "Stock holding while market is weak — first to run when market turns", pass: true });
  else signals.push({ label: "RS vs SPY: Lagging", detail: "Stock not showing relative strength — lotto setups need leadership", pass: false });

  if (tLineRun) signals.push({ label: "T-Line Entry (EMA9 pullback)", detail: "Price pulling back to EMA9 — momentum is hot, this is the entry trigger", pass: true });
  if (volBreakout) signals.push({ label: "Volume Breakout Confirmed", detail: `${result.volBreakout?.breakType} on ${result.volBreakout?.volRatio?.toFixed(1)}x volume — breakout is real, not a fake`, pass: true });

  return { isLotto, conviction, pillars, bonusCount, signals };
}

function calcOptionsPlay(result, type = "calls") {
  const price = result.price;
  const atr = result.atr || price * 0.02;
  const isSqueeze = result.bb?.isSqueeze;
  const adx = result.adx?.adx || 20;
  const t1 = result.t1;
  const t2 = result.t2;
  const ivRank = result.bb?.ivRank ?? 50;
  const ivLabel = result.bb?.ivLabel ?? "AVERAGE";
  const goodForBuying = result.bb?.goodForBuying ?? true;
  const avoidBuying = result.bb?.avoidBuying ?? false;
  const nineEmaEntry = result.nineEmaEntry;
  const ema9 = result.ema9;
  const ema9AboveEma21 = result.ema9AboveEma21;

  // Lotto setup check
  const lottoSetup = calcLottoSetup(result);

  // Delta guidance based on IV rank
  // Low IV → go slightly deeper (higher delta) for more leverage at cheap premium
  // High IV → stay closer ATM or use spreads to reduce vega exposure
  const deltaAtm  = type === "calls" ? "~0.50" : "~-0.50";
  const deltaOtm  = ivRank <= 30 ? (type === "calls" ? "~0.40" : "~-0.40") : (type === "calls" ? "~0.35" : "~-0.35");
  const deltaDeep = ivRank <= 30 ? (type === "calls" ? "~0.65–0.70" : "~-0.65") : (type === "calls" ? "~0.20" : "~-0.20");

  // Strike rounding — nearest $5 for >$50, nearest $1 for <$50
  const roundStrike = (p) => price > 50 ? Math.round(p / 5) * 5 : Math.round(p);

  // Per-tier expiry helper
  const calcExpiry = (extraWeeks = 0) => {
    const baseWeeks = isSqueeze ? 3 : adx > 30 ? 8 : adx > 20 ? 6 : 5;
    const totalDays = Math.max((baseWeeks + extraWeeks) * 7, 45); // min 45 DTE
    const d = new Date();
    d.setDate(d.getDate() + totalDays);
    const dow = d.getDay();
    d.setDate(d.getDate() + (dow === 5 ? 0 : dow < 5 ? 5 - dow : 6));
    return {
      expStr: d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      dte: Math.round((d - new Date()) / 86400000),
    };
  };

  const exp0 = calcExpiry(0);
  const exp1 = calcExpiry(1);
  const exp2 = calcExpiry(2);
  const expStr = exp0.expStr;
  const dte = exp0.dte;

  // IV environment context
  const ivContext = avoidBuying
    ? { level: "HIGH", note: `IV Rank ${ivRank}/100 — volatility is expensive. You are overpaying for premium. Consider a debit spread instead of a naked ${type === "calls" ? "call" : "put"} to reduce vega exposure.`, color: T.red }
    : goodForBuying
    ? { level: "LOW", note: `IV Rank ${ivRank}/100 — volatility is compressed. Options are on sale. This is the ideal environment to buy naked ${type === "calls" ? "calls" : "puts"} — a volatility expansion will add value even if the stock moves slowly.`, color: T.green }
    : { level: "MODERATE", note: `IV Rank ${ivRank}/100 — volatility is average. Standard sizing applies. Monitor for IV expansion on breakout.`, color: T.yellow };

  // Entry timing — with specific dollar entry zone
  const pctAboveEma9 = ema9 ? ((price - ema9) / ema9) * 100 : 0;
  const entryZoneLow  = ema9 ? (ema9 * 0.995).toFixed(2) : null;
  const entryZoneHigh = ema9 ? (ema9 * 1.005).toFixed(2) : null;
  const atIdealEntry  = ema9 ? Math.abs(pctAboveEma9) <= 1.0 : false;

  const entryTiming = nineEmaEntry || atIdealEntry
    ? { signal: "IDEAL ENTRY", status: "GO", note: `Price is at the EMA9 pullback zone ($${entryZoneLow}–$${entryZoneHigh}). This is the professional entry trigger — buying the dip in a confirmed uptrend. Enter now with a tight stop.`, color: T.green, pctAway: 0, entryZoneLow, entryZoneHigh }
    : ema9AboveEma21
    ? { signal: "WAIT FOR PULLBACK", status: "WAIT", note: `Price is ${pctAboveEma9.toFixed(1)}% above the ideal entry zone ($${entryZoneLow}–$${entryZoneHigh}). Chasing here compresses your risk/reward. Wait for a pullback to EMA9 — the trend is confirmed bullish, the entry isn't ready yet.`, color: T.yellow, pctAway: pctAboveEma9, entryZoneLow, entryZoneHigh }
    : { signal: "NOT YET ALIGNED", status: "WAIT", note: `EMA9 is not yet above EMA21 — short-term momentum hasn't confirmed the move. Best entries happen after EMA9 crosses above EMA21 and price pulls back to test it.`, color: T.orange, pctAway: null, entryZoneLow, entryZoneHigh };

  if (type === "calls") {
    const atmStrike = roundStrike(price);
    const otmStrike = roundStrike(price * (price > 50 ? 1.03 : 1.05));
    const deepStrike = roundStrike(price * (price > 50 ? 0.97 : 0.95)); // ITM for high IV
    const aggressiveStrike = roundStrike(price * (price > 50 ? 1.06 : 1.10));
    const estPremiumAtm = (atr * 1.8).toFixed(2);
    const estPremiumOtm = (atr * 1.1).toFixed(2);
    const estPremiumDeep = (atr * 2.6).toFixed(2);

    const plays = avoidBuying
      ? [ // High IV — recommend ITM (higher delta, less vega) or spread
          { label: "ITM Call (High IV play)", strike: deepStrike, delta: deltaDeep, estPremium: estPremiumDeep, breakeven: (deepStrike + parseFloat(estPremiumDeep)).toFixed(2), target: t1 ? t1.toFixed(2) : "—", expiry: exp0.expStr, dte: exp0.dte, why: `IV is elevated. Going ITM (delta ${deltaDeep}) reduces vega exposure — less of your premium is pure time value that IV crush can destroy.` },
          { label: "ATM Call (Standard)", strike: atmStrike, delta: deltaAtm, estPremium: estPremiumAtm, breakeven: (atmStrike + parseFloat(estPremiumAtm)).toFixed(2), target: t1 ? t1.toFixed(2) : "—", expiry: exp1.expStr, dte: exp1.dte, why: "Standard ATM — most responsive to price move. IV crush risk at current levels — consider a debit spread as alternative." },
        ]
      : [ // Normal / low IV
          { label: "Conservative (ATM)", strike: atmStrike, delta: deltaAtm, estPremium: estPremiumAtm, breakeven: (atmStrike + parseFloat(estPremiumAtm)).toFixed(2), target: t1 ? t1.toFixed(2) : "—", expiry: exp0.expStr, dte: exp0.dte, why: "Highest probability, most responsive to price move. Best for a confirmed setup. Delta ~0.50 moves $0.50 per $1 in the stock." },
          { label: "Aggressive (OTM)", strike: otmStrike, delta: deltaOtm, estPremium: estPremiumOtm, breakeven: (otmStrike + parseFloat(estPremiumOtm)).toFixed(2), target: t2 ? t2.toFixed(2) : t1 ? (t1 * 1.03).toFixed(2) : "—", expiry: exp1.expStr, dte: exp1.dte, why: `Lower cost, higher reward. ${ivRank <= 30 ? "IV is low — this is when OTM options make sense. Volatility expansion adds extra value." : "Needs a bigger move to profit. Extra week of time helps."}` },
          { label: "Lottery (Far OTM)", strike: aggressiveStrike, delta: "~0.20", estPremium: (atr * 0.5).toFixed(2), breakeven: (aggressiveStrike + atr * 0.5).toFixed(2), target: t2 ? (t2 * 1.02).toFixed(2) : "—", expiry: exp2.expStr, dte: exp2.dte, why: "Needs 6-10% move to profit. Small size only. Never skimp on expiry here — buy at least 8-10 extra weeks." },
        ];

    // Inject lotto play when all 3 pillars align
    if (lottoSetup?.isLotto) {
      const lottoExp = new Date();
      lottoExp.setDate(lottoExp.getDate() + 7);
      const dow = lottoExp.getDay();
      lottoExp.setDate(lottoExp.getDate() + (dow === 5 ? 0 : dow < 5 ? 5 - dow : 6));
      const lottoExpStr = lottoExp.toLocaleDateString("en-US", { month: "short", day: "numeric" });
      const lottoStrike = roundStrike(price * (price > 50 ? 1.10 : 1.15));
      plays.push({
        label: `🎰 Lotto — ${lottoSetup.conviction}`,
        strike: lottoStrike,
        delta: "~0.10–0.15",
        estPremium: (atr * 0.25).toFixed(2),
        breakeven: (lottoStrike + atr * 0.25).toFixed(2),
        target: "10x–20x on explosive breakout",
        expiry: lottoExpStr,
        dte: 7,
        why: `All 3 lotto pillars confirmed: Squeeze + Volume Pre-Load (${result.rvol?.ratio?.toFixed(1)}x) + RS Outperforming SPY. Short-dated far-OTM bet on explosive breakout. HOUSE MONEY ONLY — 0.25–0.5% of portfolio max. Treat as 100% risk.`,
        isLotto: true,
      });
    }

    return {
      type: "CALLS", color: T.green, expiry: expStr, dte, ivContext, entryTiming, lottoSetup,
      reason: isSqueeze ? `BB Squeeze firing — ${exp0.dte} DTE minimum (squeeze can take 5-10 days to break)` : adx > 30 ? `Strong trend (ADX ${adx.toFixed(0)}) — ${exp0.dte} DTE to ride the move` : `Standard swing — ${exp0.dte} DTE, exit before 14 DTE danger zone`,
      plays,
      rules: [
        "1% Rule: Never risk more than 1–2% of portfolio on a single options trade",
        "50% Profit Target: Sell HALF at 50% gain — make the trade risk-free. Let the rest run.",
        "100% Target: Close the full position at 100% gain. Don't hold for more.",
        "Stop Loss: Cut the position at 25–30% loss. Options decay fast — small losses beat big ones.",
        `Technical Stop: If stock closes below EMA21 ($${result.ema21?.toFixed(2) ?? "—"}) on the daily, close the position.`,
        `DTE Rule: Bought at ${dte} DTE. Plan to EXIT before 21 DTE — theta decay accelerates sharply in the final 3 weeks.`,
        "Morning Rule: Best entries 9:45–10:15 AM EST after initial volatility settles. Avoid chasing the open.",
        "Never hold through earnings — sell into the IV spike BEFORE the announcement.",
      ],
    };
  } else {
    const atmStrike = roundStrike(price);
    const otmStrike = roundStrike(price * (price > 50 ? 0.97 : 0.95));
    const aggressiveStrike = roundStrike(price * (price > 50 ? 0.94 : 0.90));
    const deepStrike = roundStrike(price * (price > 50 ? 1.03 : 1.05)); // ITM put for high IV
    const estPremiumAtm = (atr * 1.8).toFixed(2);
    const estPremiumOtm = (atr * 1.1).toFixed(2);

    const plays = avoidBuying
      ? [
          { label: "ITM Put (High IV play)", strike: deepStrike, delta: "~-0.65", estPremium: (atr * 2.6).toFixed(2), breakeven: (deepStrike - atr * 2.6).toFixed(2), target: (price * 0.95).toFixed(2), expiry: exp0.expStr, dte: exp0.dte, why: `IV is elevated. ITM put reduces vega exposure — less premium at risk of IV crush.` },
          { label: "ATM Put (Standard)", strike: atmStrike, delta: deltaAtm, estPremium: estPremiumAtm, breakeven: (atmStrike - parseFloat(estPremiumAtm)).toFixed(2), target: (price * 0.95).toFixed(2), expiry: exp1.expStr, dte: exp1.dte, why: "Standard ATM put. Consider debit put spread to reduce IV risk." },
        ]
      : [
          { label: "Conservative (ATM)", strike: atmStrike, delta: "~-0.50", estPremium: estPremiumAtm, breakeven: (atmStrike - parseFloat(estPremiumAtm)).toFixed(2), target: (price * 0.95).toFixed(2), expiry: exp0.expStr, dte: exp0.dte, why: "Highest probability put. Most responsive to downside move." },
          { label: "Aggressive (OTM)", strike: otmStrike, delta: deltaOtm, estPremium: estPremiumOtm, breakeven: (otmStrike - parseFloat(estPremiumOtm)).toFixed(2), target: (price * 0.92).toFixed(2), expiry: exp1.expStr, dte: exp1.dte, why: `Cheaper premium, needs bigger breakdown. ${ivRank <= 30 ? "Low IV makes this a good risk/reward trade." : "Extra week gives the thesis time to develop."}` },
        ];

    return {
      type: "PUTS", color: T.red, expiry: expStr, dte, ivContext, entryTiming,
      reason: isSqueeze ? `BB Squeeze — ${exp0.dte} DTE min, breakdown expected` : adx > 30 ? `Strong downtrend (ADX ${adx.toFixed(0)}) — ${exp0.dte} DTE to ride it` : `Standard bearish — ${exp0.dte} DTE, exit before 14 DTE`,
      plays,
      rules: [
        "1% Rule: Never risk more than 1–2% of portfolio on a single options trade",
        "50% Profit Target: Sell HALF at 50% gain — lock in profit immediately.",
        "100% Target: Close the full position at 100% gain. Greed is the fastest way to lose.",
        "Stop Loss: Cut at 25–30% loss — if the stock reverses hard, close immediately.",
        `Technical Stop: If stock closes back ABOVE EMA21 ($${result.ema21?.toFixed(2) ?? "—"}), the bearish thesis is broken — exit.`,
        `DTE Rule: Bought at ${dte} DTE. Plan to EXIT before 21 DTE — theta accelerates in the final 3 weeks.`,
        "Confirm the breakdown first — never buy puts on a stock sitting at major support.",
        "Never hold puts through earnings — IV crush destroys put value after the announcement.",
      ],
    };
  }
}


// ─── OPTIONS PLAY PANEL ───────────────────────────────────────────────────────
function OptionsPlayPanel({ result, symbol, type = "calls" }) {
  const play = calcOptionsPlay(result, type);
  const c = play.color;
  const mono = { fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div style={{ padding: "16px 20px", background: T.surfaceHi, borderBottom: `1px solid ${T.border}` }}>

      {/* Earnings advisory — not a block, but contextual guidance with adjusted parameters */}
      {result?.earnings?.soon && (
        <div style={{ marginBottom: 14, padding: "14px 16px", background: `${T.yellow}0d`, border: `1px solid ${T.yellow}55`, borderRadius: 10 }}>
          <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
            <div style={{ fontSize: 20, flexShrink: 0 }}>⚠</div>
            <div>
              <div style={{ fontSize: F.sm, fontWeight: 700, color: T.yellow }}>
                EARNINGS IN {result.earnings.daysAway} DAY{result.earnings.daysAway === 1 ? "" : "S"} — {result.earnings.date}
              </div>
              <div style={{ fontSize: F.xs, color: T.textSub, marginTop: 4, lineHeight: 1.6 }}>
                Entering this options play is still valid if the technical setup is strong — but the rules change significantly around earnings.
                IV is elevated right now, which means you are paying inflated premium. After the announcement IV collapses 30-60% regardless of which direction the stock moves.
                The play is to capture the move <strong style={{ color: T.yellow }}>before</strong> earnings, not hold through it.
              </div>
            </div>
          </div>

          <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12 }}>
            <div style={{ fontSize: F.xs, fontWeight: 700, color: T.yellow, letterSpacing: "0.08em", marginBottom: 10 }}>
              ADJUSTED PARAMETERS FOR EARNINGS WINDOW
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                { label: "Position Size", value: "25-50% of normal", note: "IV crush risk is real — size down" },
                { label: "Strike", value: "ATM only (0.50 delta)", note: "OTM needs too much movement to overcome IV crush" },
                { label: "Expiry to Buy", value: `After ${result.earnings.date} — 35+ DTE`, note: "Gives room after the announcement" },
                { label: "When to Exit", value: `BEFORE ${result.earnings.date}`, note: "Sell into the IV spike, don't hold through" },
                { label: "Best Case", value: "Stock moves 3-5% your way before earnings", note: "Sell into strength and capture the IV premium" },
                { label: "Alternative", value: "Wait until post-earnings", note: "Enter after reaction settles — cleaner setup, no IV risk" },
              ].map(({ label, value, note }) => (
                <div key={label} style={{ background: T.bg, borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.yellow, marginBottom: 3 }}>{label.toUpperCase()}</div>
                  <div style={{ fontSize: F.xs, fontWeight: 700, color: T.text, marginBottom: 2 }}>{value}</div>
                  <div style={{ fontSize: 10, color: T.textDim }}>{note}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── ENTRY STATUS — dominant, always first ── */}
      {play.entryTiming && (
        <div style={{ marginBottom: 14, borderRadius: 12, overflow: "hidden", border: `1px solid ${play.entryTiming.color}55` }}>
          {/* Status header bar */}
          <div style={{ padding: "12px 16px", background: `${play.entryTiming.color}18`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>{play.entryTiming.status === "GO" ? "🎯" : "⏳"}</span>
              <div>
                <div style={{ fontSize: F.base, fontWeight: 800, color: play.entryTiming.color, letterSpacing: "0.04em" }}>
                  {play.entryTiming.status === "GO" ? "ENTER NOW" : "SIGNAL ACTIVE — WAIT FOR ENTRY"}
                </div>
                <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 1 }}>
                  {play.entryTiming.status === "GO" ? "Price is in the ideal entry zone" : `Price is ${play.entryTiming.pctAway?.toFixed(1)}% above ideal entry`}
                </div>
              </div>
            </div>
            {/* Entry zone pill */}
            {play.entryTiming.entryZoneLow && (
              <div style={{ textAlign: "right", background: T.bg, borderRadius: 8, padding: "8px 12px", flexShrink: 0 }}>
                <div style={{ fontSize: 9, color: T.textDim, marginBottom: 2, letterSpacing: "0.06em" }}>TARGET ENTRY ZONE</div>
                <div style={{ fontSize: F.sm, fontWeight: 800, color: play.entryTiming.color, fontFamily: "'JetBrains Mono', monospace" }}>
                  ${play.entryTiming.entryZoneLow} – ${play.entryTiming.entryZoneHigh}
                </div>
              </div>
            )}
          </div>
          {/* Detail */}
          <div style={{ padding: "10px 16px", background: T.bg }}>
            <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.7 }}>{play.entryTiming.note}</div>
            {play.entryTiming.status === "WAIT" && play.entryTiming.pctAway != null && (
              <div style={{ marginTop: 8, padding: "6px 10px", background: `${T.yellow}0d`, borderRadius: 6, fontSize: F.xs, color: T.yellow, fontWeight: 600 }}>
                ⚠ Entering {play.entryTiming.pctAway.toFixed(1)}% above EMA9 compresses your risk/reward significantly. The signal is real — the timing isn't right yet. Set a price alert at ${play.entryTiming.entryZoneLow} and wait.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── IV ENVIRONMENT ── */}
      {play.ivContext && (
        <div style={{ marginBottom: 14, padding: "10px 14px", background: `${play.ivContext.color}0d`, border: `1px solid ${play.ivContext.color}44`, borderRadius: 10, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <div style={{ fontSize: 16, flexShrink: 0 }}>{play.ivContext.level === "LOW" ? "✅" : play.ivContext.level === "HIGH" ? "⚠️" : "ℹ️"}</div>
          <div>
            <div style={{ fontSize: F.xs, fontWeight: 800, color: play.ivContext.color, letterSpacing: "0.06em", marginBottom: 3 }}>IV ENVIRONMENT: {play.ivContext.level}</div>
            <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>{play.ivContext.note}</div>
          </div>
        </div>
      )}

      {/* ── LOTTO SETUP BANNER ── */}
      {play.lottoSetup?.isLotto && (
        <div style={{ marginBottom: 14, padding: "12px 14px", background: "linear-gradient(135deg, #1a0533, #0f1a00)", border: `1px solid #a855f766`, borderRadius: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 18 }}>🎰</span>
            <div style={{ fontSize: F.xs, fontWeight: 800, color: "#a855f7", letterSpacing: "0.08em" }}>
              LOTTO SETUP DETECTED — {play.lottoSetup.conviction} CONVICTION
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 8 }}>
            {play.lottoSetup.signals.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 5, padding: "6px 8px", background: `${s.pass ? T.green : T.border}12`, borderRadius: 6, border: `1px solid ${s.pass ? T.green : T.border}33` }}>
                <span style={{ fontSize: 10, color: s.pass ? T.green : T.textDim, flexShrink: 0, marginTop: 1 }}>{s.pass ? "✓" : "✗"}</span>
                <div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: s.pass ? T.green : T.textDim }}>{s.label}</div>
                  <div style={{ fontSize: 9, color: T.textDim, lineHeight: 1.4, fontFamily: "sans-serif", marginTop: 2 }}>{s.detail}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: F.xs, color: T.textDim, lineHeight: 1.6, padding: "7px 10px", background: "#ffffff08", borderRadius: 6, fontFamily: "sans-serif" }}>
            ⚠ Lotto plays require all 3 pillars. Treat as 100% risk — use house money only (0.25–0.5% of portfolio).
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: F.xs, fontWeight: 700, color: c, letterSpacing: "0.1em", marginBottom: 3 }}>
            {type === "calls" ? "⚡" : "🔻"} {play.type} PLAY — {symbol}
          </div>
          <div style={{ fontSize: F.base, fontWeight: 700, color: T.text }}>Recommended Strike & Expiration</div>
          <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 2 }}>{play.reason}</div>
        </div>
        <div style={{ textAlign: "right", background: `${c}15`, border: `1px solid ${c}44`, borderRadius: 10, padding: "10px 16px" }}>
          <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 2 }}>EARLIEST EXPIRY</div>
          <div style={{ ...mono, fontSize: F.lg, fontWeight: 700, color: c }}>{play.expiry}</div>
          <div style={{ fontSize: F.xs, color: T.textDim }}>{play.dte} DTE · each tier varies</div>
        </div>
      </div>

      {/* Strike options */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${play.plays.length}, 1fr)`, gap: 10, marginBottom: 16 }}>
        {play.plays.map((p, i) => (
          <div key={i} style={{
            background: p.isLotto ? "linear-gradient(135deg, #1a0533, #0f1a00)" : i === 0 ? `${c}12` : T.bg,
            border: `1px solid ${p.isLotto ? "#a855f766" : i === 0 ? c + "44" : T.border}`,
            borderRadius: 10, padding: "12px 14px"
          }}>
            <div style={{ fontSize: F.xs, fontWeight: 700, color: p.isLotto ? "#a855f7" : i === 0 ? c : T.textDim, marginBottom: 8, letterSpacing: "0.06em" }}>
              {p.label} {i === 0 && !p.isLotto ? "★ RECOMMENDED" : ""}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              {[["Strike", `$${p.strike}`], ["Delta", p.delta], ["Expiry", p.expiry], [`DTE`, `${p.dte} days`], ["Est. Premium", `$${p.estPremium}`], ["Breakeven", `$${p.breakeven}`], ["Target", `$${p.target}`]].map(([l, v]) => (
                <div key={l}>
                  <div style={{ fontSize: 10, color: T.textDim, marginBottom: 2 }}>{l}</div>
                  <div style={{ ...mono, fontSize: F.sm, fontWeight: 600, color: T.text }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.5, borderTop: `1px solid ${T.border}`, paddingTop: 8 }}>{p.why}</div>
          </div>
        ))}
      </div>

      {/* Rules */}
      <div style={{ background: T.bg, borderRadius: 8, padding: "10px 14px" }}>
        <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 6 }}>OPTIONS RULES</div>
        {play.rules.map((r, i) => (
          <div key={i} style={{ fontSize: F.xs, color: T.textSub, marginBottom: 3, display: "flex", gap: 6 }}>
            <span style={{ color: c, flexShrink: 0 }}>•</span>{r}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 8, fontSize: F.xs, color: T.textDim, fontStyle: "italic" }}>
        ⚠ Premiums are estimates based on ATR. Verify actual bid/ask in your broker before placing. Strike prices rounded to nearest standard increment.
      </div>
    </div>
  );
}

// ─── LIVE QUOTE (intraday refresh via Alpaca snapshot) ────────────────────────
function LiveQuote({ symbol, lastClose }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchQuote = async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`/api/quote?symbol=${encodeURIComponent(symbol)}`);
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const q = await res.json();
      if (!q.price) throw new Error("No quote data");
      const prevClose = q.prevClose || lastClose;
      const current = q.price;
      const changePct = prevClose ? ((current - prevClose) / prevClose) * 100 : 0;
      setQuote({
        price: current,
        prevClose,
        changePct,
        changeAmt: current - prevClose,
        gapFromScan: lastClose ? ((current - lastClose) / lastClose) * 100 : 0,
        time: new Date().toLocaleTimeString(),
        volume: q.volume,
        vwap: q.vwap,
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const gapAlert = quote && Math.abs(quote.gapFromScan) > 2;
  const gapColor = quote?.gapFromScan > 0 ? T.green : T.red;

  return (
    <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em" }}>⚡ LIVE QUOTE</div>
        <button onClick={fetchQuote} disabled={loading}
          style={{ padding: "4px 12px", background: `${T.cyan}18`, border: `1px solid ${T.cyan}44`, color: T.cyan, fontSize: F.xs, fontWeight: 600, fontFamily: "inherit", cursor: loading ? "not-allowed" : "pointer", borderRadius: 6 }}>
          {loading ? "Loading..." : "↻ Refresh"}
        </button>
      </div>

      {!quote && !error && (
        <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 6 }}>Click Refresh for current intraday price — checks for gaps since last daily scan.</div>
      )}

      {error && <div style={{ fontSize: F.xs, color: T.red, marginTop: 6 }}>{error}</div>}

      {quote && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 2 }}>CURRENT</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: F.xl, fontWeight: 700, color: T.text }}>${quote.price?.toFixed(2)}</div>
            </div>
            <div>
              <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 2 }}>VS PREV CLOSE</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: F.lg, fontWeight: 700, color: quote.changePct >= 0 ? T.green : T.red }}>
                {quote.changePct >= 0 ? "+" : ""}{quote.changePct?.toFixed(2)}% (${quote.changeAmt >= 0 ? "+" : ""}{quote.changeAmt?.toFixed(2)})
              </div>
            </div>
            <div>
              <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 2 }}>VS LAST SCAN</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: F.lg, fontWeight: 700, color: gapAlert ? gapColor : T.textSub }}>
                {quote.gapFromScan >= 0 ? "+" : ""}{quote.gapFromScan?.toFixed(2)}%
              </div>
            </div>
            {quote.vwap && (
              <div>
                <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 2 }}>VWAP</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: F.base, fontWeight: 600, color: quote.price > quote.vwap ? T.green : T.red }}>${quote.vwap?.toFixed(2)}</div>
              </div>
            )}
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <div style={{ fontSize: F.xs, color: T.textDim }}>as of {quote.time}</div>
            </div>
          </div>

          {gapAlert && (
            <div style={{ marginTop: 10, padding: "8px 12px", background: `${gapColor}15`, border: `1px solid ${gapColor}44`, borderRadius: 8, fontSize: F.xs, color: gapColor, fontWeight: 600 }}>
              {quote.gapFromScan > 0
                ? `⚡ Up ${quote.gapFromScan.toFixed(1)}% since scan — entry may be extended. Check RSI before entering.`
                : `⚠ Down ${Math.abs(quote.gapFromScan).toFixed(1)}% since scan — review signal validity before entering.`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SUMMARY PANEL (tabbed: Analysis + Calls Play + Puts Play) ───────────────

// ─── TRADEFLOW PANEL ──────────────────────────────────────────────────────────
function TradeFlowPanel({ result, symbol, isDark }) {
  if (!result) return null;

  const { cmf, cmfDiv, obv, rvol, vwap, price, adx, psar, trendStructure, volDiv, stoch, williamsR, volBreakout,
          range52w, rs, rsVsSector, weeklyTrend, macd, rsiDiv, rsi, deathCross, goldenCross } = result;

  // ── MONEY FLOW NARRATIVE ──────────────────────────────────────────────────
  const moneyFlowNarrative = (() => {
    const signals = [];
    if (cmf != null) {
      if (cmf > 0.15) signals.push({ label: "Strong institutional accumulation", detail: `CMF ${cmf.toFixed(3)} — heavy net buying pressure. Institutions are aggressively accumulating.`, color: T.green, weight: 3 });
      else if (cmf > 0.05) signals.push({ label: "Institutional accumulation", detail: `CMF ${cmf.toFixed(3)} — net buying. More volume is closing near highs than lows.`, color: T.green, weight: 2 });
      else if (cmf > -0.02) signals.push({ label: "Neutral money flow", detail: `CMF ${cmf.toFixed(3)} — no clear institutional bias. Balanced buying and selling pressure.`, color: T.textSub, weight: 0 });
      else if (cmf > -0.08) signals.push({ label: "Mild distribution", detail: `CMF ${cmf.toFixed(3)} — slight net selling. Institutions may be quietly reducing exposure.`, color: T.orange, weight: -1 });
      else signals.push({ label: "Active distribution", detail: `CMF ${cmf.toFixed(3)} — heavy net selling. Institutional sellers are in control.`, color: T.red, weight: -2 });
    }
    if (obv) {
      if (obv.bullishDiv) signals.push({ label: "OBV bullish divergence", detail: "Price declining but OBV rising — smart money buying while price falls. Often precedes a reversal.", color: T.green, weight: 2 });
      else if (obv.bearishDiv) signals.push({ label: "OBV bearish divergence", detail: "Price rising but OBV falling — price move is not supported by volume. Warning sign for bulls.", color: T.red, weight: -2 });
      else if (obv.confirming && obv.rising) signals.push({ label: "OBV confirming uptrend", detail: "Volume is rising with price — institutional participation validates the move.", color: T.green, weight: 1 });
      else if (!obv.rising) signals.push({ label: "OBV falling", detail: "On-balance volume declining — more volume on down days than up days.", color: T.orange, weight: -1 });
    }
    if (cmfDiv?.bullishDiv) {
      signals.push({ label: "CMF bullish divergence", detail: cmfDiv.label, color: T.green, weight: 2 });
    } else if (cmfDiv?.bearishDiv) {
      signals.push({ label: "CMF bearish divergence", detail: cmfDiv.label, color: T.red, weight: -2 });
    }
    if (williamsR) {
      signals.push({
        label: "Williams %R: " + (williamsR.label ?? ''),
        detail: williamsR.tooltip ?? `Williams %R ${williamsR.value}${williamsR.bullCross ? " — Mean reversion buy signal" : williamsR.bearCross ? " — Distribution signal" : ""}`,
        color: williamsR.color ?? T.textSub,
        weight: williamsR.bullCross ? 2 : williamsR.bearCross ? -2 : 0,
      });
    }
    if (volBreakout?.isBreakout || volBreakout?.volSurgeNoBreakout) {
      signals.push({
        label: "Vol Breakout: " + (volBreakout.label ?? ''),
        detail: volBreakout.breakType ? `${volBreakout.breakType} · ${volBreakout.volRatio?.toFixed(1)}x vol` : `${volBreakout.volRatio?.toFixed(1)}x avg volume`,
        color: volBreakout.color ?? T.cyan,
        weight: 2,
      });
    }
    if (rvol) {
      if (rvol.ratio >= 2.0) signals.push({ label: `High relative volume (${rvol.ratio.toFixed(1)}x)`, detail: "Volume is more than double average. Institutional participation likely — this move has conviction behind it.", color: T.cyan, weight: 2 });
      else if (rvol.ratio >= 1.5) signals.push({ label: `Above-average volume (${rvol.ratio.toFixed(1)}x)`, detail: "Volume elevated above average. Enough participation to sustain a directional move.", color: T.cyan, weight: 1 });
      else if (rvol.ratio < 0.7) signals.push({ label: `Low volume (${rvol.ratio.toFixed(1)}x)`, detail: "Volume well below average. Moves on thin volume are unreliable — potential for reversal.", color: T.yellow, weight: -1 });
    }
    if (volDiv?.bullish) signals.push({ label: "Volume dry on down days", detail: "Selling volume is decreasing — sellers are exhausting themselves. Bullish for near-term stability.", color: T.green, weight: 1 });
    if (volDiv?.bearish) signals.push({ label: "Volume dry on up days", detail: "Buying volume is weak on rallies — price rising without participation. Potential exhaustion.", color: T.orange, weight: -1 });
    return signals;
  })();

  // ── TREND CONVICTION ─────────────────────────────────────────────────────
  const trendSignals = (() => {
    const t = [];
    if (adx?.adx != null) {
      const a = adx.adx;
      if (a >= 40) t.push({ label: "Very strong trend", detail: `ADX ${a.toFixed(0)} — powerful directional move. High follow-through probability.`, color: T.green });
      else if (a >= 25) t.push({ label: "Trending", detail: `ADX ${a.toFixed(0)} — stock is in a clear trend. Trade with it, not against it.`, color: T.cyan });
      else if (a >= 20) t.push({ label: "Weakly trending", detail: `ADX ${a.toFixed(0)} — trend is developing but not yet strong. Requires confirmation.`, color: T.yellow });
      else t.push({ label: "No trend (choppy)", detail: `ADX ${a.toFixed(0)} — directionless. Range-bound behavior likely. Avoid breakout plays.`, color: T.orange });
    }
    if (vwap && price) {
      const pctFromVwap = ((price - vwap) / vwap) * 100;
      if (pctFromVwap > 1.5) t.push({ label: `${pctFromVwap.toFixed(1)}% above VWAP`, detail: `$${price.toFixed(2)} vs VWAP $${vwap.toFixed(2)} — price extended above institutional fair value. Buyers have been aggressive.`, color: T.green });
      else if (pctFromVwap > -1.5) t.push({ label: "Trading at VWAP", detail: `$${price.toFixed(2)} near VWAP $${vwap.toFixed(2)} — price near institutional fair value. Key level to watch.`, color: T.textSub });
      else t.push({ label: `${Math.abs(pctFromVwap).toFixed(1)}% below VWAP`, detail: `$${price.toFixed(2)} vs VWAP $${vwap.toFixed(2)} — price below institutional fair value. Sellers in control.`, color: T.red });
    }
    if (psar?.bullish != null) {
      t.push({ label: psar.bullish ? "Parabolic SAR: Uptrend" : "Parabolic SAR: Downtrend",
        detail: psar.bullish ? "SAR dots below price — trend direction is UP. Dot level acts as a trailing stop reference." : "SAR dots above price — trend direction is DOWN. Dot level acts as a resistance reference.",
        color: psar.bullish ? T.green : T.red });
    }
    if (trendStructure?.structure) {
      const s = trendStructure.structure;
      const isUp = s.includes("Uptrend") || s.includes("HH");
      const isDown = s.includes("Downtrend") || s.includes("LL");
      t.push({ label: `Price structure: ${s}`, detail: isUp ? "Higher highs and higher lows — classic uptrend structure intact." : isDown ? "Lower lows and lower highs — classic downtrend structure. Do not buy against this." : "Rangebound — no clear directional structure. Wait for a breakout.",
        color: isUp ? T.green : isDown ? T.red : T.yellow });
    }
    return t;
  })();

  // ── RELATIVE STRENGTH ─────────────────────────────────────────────────────
  const relStrength = (() => {
    const r = [];
    if (rs) {
      r.push({ label: rs.label || (rs.outperforming ? "Outperforming SPY" : "Lagging SPY"),
        detail: rs.spread != null ? `${rs.outperforming ? "+" : ""}${rs.spread.toFixed(1)}% vs SPY over 63 days. ${rs.outperforming ? "Leadership — stock is stronger than the market." : "Lagging — this stock is weaker than the index. Headwind for swing longs."}` : "",
        color: rs.outperforming ? T.green : T.red });
    }
    if (rsVsSector) {
      r.push({ label: `${rsVsSector.label} (vs ${rsVsSector.etf})`,
        detail: `${rsVsSector.spread >= 0 ? "+" : ""}${rsVsSector.spread.toFixed(1)}% vs sector ETF. ${rsVsSector.outperforming ? "Stock is stronger than its own sector — leadership within sector." : "Stock lags its own sector — either stock-specific weakness or sector rotation pressure."}`,
        color: rsVsSector.outperforming ? T.green : rsVsSector.spread < -8 ? T.red : T.orange });
    }
    if (weeklyTrend) {
      r.push({ label: `Weekly trend: ${weeklyTrend.label || "N/A"}`,
        detail: weeklyTrend.strongUptrend ? "Weekly EMA aligned bullishly — daily setup is trading WITH the larger trend. Highest follow-through probability." : weeklyTrend.downtrend ? "Weekly trend is DOWN — daily setup is fighting the larger trend. Expect lower follow-through." : "Weekly trend neutral — mixed signals on the larger timeframe.",
        color: weeklyTrend.strongUptrend ? T.green : weeklyTrend.downtrend ? T.red : T.yellow });
    }
    return r;
  })();

  // ── OVERALL FLOW VERDICT ──────────────────────────────────────────────────
  const totalWeight = moneyFlowNarrative.reduce((s, x) => s + (x.weight || 0), 0);
  const flowVerdict = totalWeight >= 3 ? { label: "STRONG BUY FLOW", color: T.green, bg: `${T.green}12` }
    : totalWeight >= 1 ? { label: "MILD BUY FLOW", color: "#4ade80", bg: `${T.green}08` }
    : totalWeight <= -3 ? { label: "STRONG SELL FLOW", color: T.red, bg: `${T.red}12` }
    : totalWeight <= -1 ? { label: "MILD SELL FLOW", color: T.orange, bg: `${T.orange}10` }
    : { label: "NEUTRAL FLOW", color: T.textSub, bg: T.surfaceHi };

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  );

  const FlowRow = ({ label, detail, color }) => (
    <div style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: `1px solid ${T.border}` }}>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0, marginTop: 5 }} />
      <div>
        <div style={{ fontSize: F.sm, fontWeight: 600, color, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>{detail}</div>
      </div>
    </div>
  );

  const stochOB = stoch?.k != null && stoch.k > 80;
  const stochOS = stoch?.k != null && stoch.k < 20;

  return (
    <div style={{ padding: "16px 20px" }}>
      {/* Overall flow verdict */}
      <div style={{ padding: "12px 16px", background: flowVerdict.bg, border: `1px solid ${flowVerdict.color}33`, borderRadius: 10, marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 4 }}>TRADEFLOW VERDICT</div>
          <div style={{ fontSize: F.xl, fontWeight: 800, color: flowVerdict.color }}>{flowVerdict.label}</div>
          <div style={{ fontSize: F.xs, color: T.textSub, marginTop: 4 }}>
            Based on CMF, OBV, RVOL, VWAP, volume divergence. Independent of entry score.
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 28, fontWeight: 900, color: flowVerdict.color }}>{totalWeight > 0 ? "+" : ""}{totalWeight}</div>
          <div style={{ fontSize: F.xs, color: T.textDim }}>flow score</div>
        </div>
      </div>

      <Section title="MONEY FLOW — WHERE IS THE SMART MONEY GOING?">
        {moneyFlowNarrative.length > 0
          ? moneyFlowNarrative.map((s, i) => <FlowRow key={i} {...s} />)
          : <div style={{ fontSize: F.sm, color: T.textDim }}>Insufficient data</div>}
      </Section>

      <Section title="TREND CONVICTION — HOW STRONG IS THE MOVE?">
        {trendSignals.length > 0
          ? trendSignals.map((s, i) => <FlowRow key={i} {...s} />)
          : <div style={{ fontSize: F.sm, color: T.textDim }}>Insufficient data</div>}
        {stoch?.k != null && (stochOB || stochOS) && (
          <FlowRow
            label={stochOB ? `Stochastic overbought (${stoch.k.toFixed(0)})` : `Stochastic oversold (${stoch.k.toFixed(0)})`}
            detail={stochOB ? "Short-term momentum is stretched — potential for pullback even in an uptrend." : "Short-term momentum is depressed — potential for bounce even in a downtrend."}
            color={stochOB ? T.orange : T.cyan}
          />
        )}
        {(rsiDiv?.bullish || rsiDiv?.bearish) && (
          <FlowRow
            label={rsiDiv?.bullish ? "✅ RSI Bullish divergence" : "⚠ RSI Bearish divergence"}
            detail={rsiDiv?.bullish
              ? "Price lower low + RSI higher low (20-bar swings). Accumulation signal — +2 score."
              : "Price higher high + RSI lower high (20-bar swings). Distribution signal — −2 score."}
            color={rsiDiv?.bullish ? T.green : T.red}
          />
        )}
      </Section>

      <Section title="RELATIVE STRENGTH — LEADERSHIP OR LAGGARD?">
        {relStrength.length > 0
          ? relStrength.map((s, i) => <FlowRow key={i} {...s} />)
          : <div style={{ fontSize: F.sm, color: T.textDim }}>Run a scan first to load RS data</div>}
        <div style={{ padding: "8px 12px", background: T.bg, borderRadius: 8, marginTop: 8, fontSize: F.xs, color: T.textDim, lineHeight: 1.7 }}>
          ⓘ  Relative strength uses 63-day (quarterly) returns. A stock that underperforms SPY and its sector simultaneously is a high-risk long regardless of technical setup quality.
        </div>
      </Section>

      {/* ── CALMAR RATIO ── */}
      {(() => {
        // Need closes array — stored on result._closes if available from scan
        // Calculate from result data we have: price, range52w, atr, rs
        // Approximate Calmar from 63-day return and 52W drawdown
        const annualReturn63 = result.rs?.stockReturn63 != null
          ? result.rs.stockReturn63 * (252 / 63)  // annualize the 63-day return
          : null;
        const maxDrawdown52w = result.range52w
          ? result.range52w.distFromHigh  // % below 52W high = proxy for max drawdown
          : null;
        const calmar63 = annualReturn63 != null && maxDrawdown52w != null && maxDrawdown52w > 0
          ? Math.abs(annualReturn63 / maxDrawdown52w)
          : null;

        // Also compute a volatility-adjusted version using ATR
        const atrPct = result.atr && result.price ? (result.atr / result.price) * 100 : null;

        if (calmar63 == null && atrPct == null) return null;

        const calmarColor = calmar63 == null ? T.textSub
          : calmar63 >= 3 ? T.green
          : calmar63 >= 1 ? "#86efac"
          : calmar63 >= 0.5 ? T.yellow
          : T.red;

        const calmarLabel = calmar63 == null ? "—"
          : calmar63 >= 3 ? "Strong"
          : calmar63 >= 1 ? "Acceptable"
          : calmar63 >= 0.5 ? "Weak"
          : "Poor";

        const calmarInterp = calmar63 == null ? ""
          : calmar63 >= 3
            ? `${calmarLabel} — This stock has generated strong returns relative to its drawdown. High risk-adjusted efficiency.`
            : calmar63 >= 1
            ? `${calmarLabel} — Returns are proportionate to the drawdown risk taken. Acceptable risk-adjusted profile.`
            : calmar63 >= 0.5
            ? `${calmarLabel} — Returns are modest relative to drawdown experienced. Below average risk-adjusted performance.`
            : `${calmarLabel} — The stock has experienced significant drawdown relative to its return. Poor risk-adjusted profile.`;

        return (
          <Section title="CALMAR RATIO — RISK-ADJUSTED PERFORMANCE">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div style={{ padding: "12px 14px", background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 4 }}>CALMAR (63-DAY, ANNUALIZED)</div>
                <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: calmarColor }}>
                  {calmar63 != null ? calmar63.toFixed(2) : "—"}
                </div>
                <div style={{ fontSize: F.xs, color: calmarColor, fontWeight: 600, marginTop: 2 }}>{calmarLabel}</div>
              </div>
              <div style={{ padding: "12px 14px", background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 4 }}>MAX DRAWDOWN (52W PROXY)</div>
                <div style={{ fontSize: 22, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: maxDrawdown52w > 30 ? T.red : maxDrawdown52w > 15 ? T.orange : T.textSub }}>
                  {maxDrawdown52w != null ? `-${maxDrawdown52w.toFixed(1)}%` : "—"}
                </div>
                <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 2 }}>below 52W high</div>
              </div>
            </div>
            {calmarInterp && (
              <div style={{ padding: "8px 12px", background: `${calmarColor}12`, border: `1px solid ${calmarColor}33`, borderRadius: 8, marginBottom: 8, fontSize: F.xs, color: T.textSub, lineHeight: 1.7 }}>
                {calmarInterp}
              </div>
            )}
            {atrPct != null && (
              <FlowRow
                label={`Daily volatility (ATR%): ${atrPct.toFixed(2)}%`}
                detail={`Average true range is ${atrPct.toFixed(2)}% of price per day. ${atrPct > 4 ? "High volatility — wider stops required, smaller position size." : atrPct > 2 ? "Moderate volatility — standard swing trade sizing applies." : "Low volatility — tighter setups, can use closer stops."}`}
                color={atrPct > 4 ? T.orange : atrPct > 2 ? T.yellow : T.green}
              />
            )}
            <div style={{ padding: "8px 12px", background: T.bg, borderRadius: 8, marginTop: 8, fontSize: F.xs, color: T.textDim, lineHeight: 1.7 }}>
              ⓘ Calmar Ratio = Annualized Return ÷ Max Drawdown. Above 1.0 = returns exceed drawdown (acceptable). Above 3.0 = strong risk-adjusted performance. Calculated using 63-day annualized return vs 52W drawdown as a proxy. Not a buy/sell signal — use as a tiebreaker between setups with similar scores.
            </div>
          </Section>
        );
      })()}

      <div style={{ padding: "10px 14px", background: `${T.purple}10`, border: `1px solid ${T.purple}22`, borderRadius: 8, fontSize: F.xs, color: T.textSub, lineHeight: 1.7 }}>
        <strong style={{ color: T.purple }}>What this is:</strong> Tradeflow analysis synthesizes money flow, trend conviction, relative strength, and risk-adjusted performance into a comprehensive read on a ticker. It does not affect the Auctus Score or Entry Gate — use it as a confirmation layer. A great score with strong flow and a healthy Calmar ratio is a high-conviction trade.
      </div>
    </div>
  );
}


// ─── WATCHLIST DETAIL DRAWER ─────────────────────────────────────────────────
// 460px right-side drawer — replaces inline expand. Shows full Layer 1/2/3 with
// warnings (SPEC/CMF conflict) at top, all existing panels in tabs.
// ─── SIZING TAB CONTENT ──────────────────────────────────────────────────────
function SizingTabContent({ bp, result, portfolioSize, onSavePortfolioSize, symbol, inPosition }) {
          const buyPrice = parseFloat(bp?.buyPrice) || null;
          const shares   = parseFloat(bp?.shares)   || null;
          const dollarAmt= parseFloat(bp?.dollarAmt) || null;
          const stopPct  = parseFloat(bp?.stopPct)   || 8;
          const dollarIn = shares && buyPrice ? shares * buyPrice : dollarAmt;
          const atrStop  = result.atr && buyPrice ? Math.min(buyPrice - 2 * result.atr, buyPrice * (1 - stopPct / 100)) : buyPrice ? buyPrice * (1 - stopPct / 100) : null;
          const retStop  = buyPrice && atrStop ? ((atrStop - buyPrice) / buyPrice * 100) : null;
          const retT1    = buyPrice && result.t1 ? ((result.t1 - buyPrice) / buyPrice * 100) : null;
          const retT2    = buyPrice && result.t2 ? ((result.t2 - buyPrice) / buyPrice * 100) : null;
          const rr       = retT1 && retStop ? Math.abs(retT1 / retStop) : null;
          const portSize = portfolioSize;
          const posPct   = portSize && dollarIn ? (dollarIn / portSize) * 100 : null;
          const maxDollar= portSize ? portSize * 0.03 : null;
          const overSized= posPct && posPct > 3;
          const atrPct   = result.atr && result.price ? (result.atr / result.price) * 100 : null;
          const volTier  = atrPct ? (atrPct >= 4 ? "SPEC" : atrPct >= 2.5 ? "HIGH" : atrPct >= 1.5 ? "NORMAL" : "LOW") : null;
          const volMultiplier = volTier === "SPEC" ? 0.5 : volTier === "HIGH" ? 0.75 : 1;
          const earningsSoon14 = !!(result?.earnings?.soon && result.earnings.daysAway != null && result.earnings.daysAway <= 14);
          const earningsMultiplier = earningsSoon14 ? 0.6 : 1; // HARD RULE: reduce size 40% in earnings window
          const earningsAdjMaxDollar = maxDollar ? maxDollar * earningsMultiplier : null;
          const adjMaxDollar = maxDollar ? maxDollar * volMultiplier * earningsMultiplier : null;
          const [portEdit, setPortEdit] = React.useState(false);
          const [portInput, setPortInput] = React.useState(portSize ? String(portSize) : "");
          React.useEffect(() => { if (portSize) setPortInput(String(portSize)); }, [portSize]);
          return (
            <div style={{ padding: "16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
              <Module4RecommendationCard result={result} T={T} />
              {result && (
                <div style={{ padding: "12px 14px", borderRadius: 12, background: T.surface, border: `1px solid ${T.border}`, boxShadow: "0 8px 24px rgba(0,0,0,0.25)" }}>
                  {(() => {
                    const livePrice = result?.price ?? null;
                    const display = resolveDisplayVerdict(result, { inPosition: !!inPosition, activeSignal: result?.activeSignal });
                    const ui = calcActionSignalUi({
                      symbol: symbol || "",
                      item: { inPosition: !!inPosition },
                      result,
                      price: livePrice,
                      bp,
                      atrStop,
                    });
                    const label = display.actionText || ui?.label || "—";
                    const reason = ui?.reason || `${display.verdict} — ${display.subtitle}`;
                    const col = ui?.color || T.textDim;
                    const bg = ui?.bg || `${T.border}22`;
                    const br = ui?.border || T.border;
                    const setupLine = result.activeSetupType && result.activeSetupType !== "MOMENTUM" ? result.activeSetupType.replace(/_/g, " ") : "SETUP BUILDING";
                    const storyOneLine = (result.priceStoryParagraph || "").split("\n").filter(Boolean)[0] || "";
                    return (
                      <div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                          <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.1em" }}>ACTION PANEL</div>
                          <span style={{ padding: "4px 10px", borderRadius: 999, background: bg, border: `1px solid ${br}`, color: col, fontSize: 10, fontWeight: 900, letterSpacing: "0.03em" }}>
                            {label}
                          </span>
                        </div>
                        <div style={{ marginTop: 8, fontSize: 11, color: T.textSub, lineHeight: 1.6 }}>
                          <div><strong style={{ color: T.text }}>Reason:</strong> {reason}</div>
                          <div><strong style={{ color: T.text }}>Setup:</strong> {setupLine}</div>
                          {storyOneLine && <div><strong style={{ color: T.text }}>Why now:</strong> {storyOneLine}</div>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 3, height: 16, borderRadius: 2, background: T.cyan, flexShrink: 0 }} />
                  <div style={{ fontSize: 10, fontWeight: 800, color: T.cyan, letterSpacing: "0.08em" }}>PORTFOLIO SIZE</div>
                </div>
                <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
                  {!portEdit ? (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>
                        {portSize ? `$${portSize >= 1000000 ? (portSize/1000000).toFixed(2)+"M" : portSize >= 1000 ? (portSize/1000).toFixed(0)+"K" : portSize}` : "Not set"}
                      </span>
                      <button onClick={() => setPortEdit(true)} style={{ fontSize: 11, color: T.cyan, background: "none", border: `1px solid ${T.cyan}44`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontFamily: "inherit" }}>
                        {portSize ? "✎ Edit" : "+ Set Size"}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: T.textDim }}>$</span>
                      <input autoFocus type="number" value={portInput} onChange={e => setPortInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { onSavePortfolioSize(portInput); setPortEdit(false); } if (e.key === "Escape") setPortEdit(false); }}
                        placeholder="e.g. 50000"
                        style={{ flex: 1, background: T.surface, border: `1px solid ${T.cyan}`, color: T.text, padding: "7px 10px", fontSize: 13, borderRadius: 7, fontFamily: "'JetBrains Mono', monospace" }} />
                      <button onClick={() => { onSavePortfolioSize(portInput); setPortEdit(false); }} style={{ padding: "7px 14px", background: T.cyan, border: "none", color: "#000", fontSize: 12, fontWeight: 700, borderRadius: 7, cursor: "pointer", fontFamily: "inherit" }}>Save</button>
                      <button onClick={() => setPortEdit(false)} style={{ padding: "7px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textDim, fontSize: 12, borderRadius: 7, cursor: "pointer", fontFamily: "inherit" }}>✕</button>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 3, height: 16, borderRadius: 2, background: rr >= 2 ? T.green : T.orange, flexShrink: 0 }} />
                  <div style={{ fontSize: 10, fontWeight: 800, color: rr >= 2 ? T.green : T.orange, letterSpacing: "0.08em" }}>RISK / REWARD</div>
                </div>
                <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
                  {buyPrice ? (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {[
                        { l: "Entry", v: `$${buyPrice.toFixed(2)}`, c: T.text },
                        { l: "ATR Stop", v: atrStop ? `$${atrStop.toFixed(2)}` : "—", c: T.red },
                        { l: "Stop %", v: retStop ? `${retStop.toFixed(1)}%` : "—", c: T.red },
                        { l: "R:R Ratio", v: rr ? `${rr.toFixed(1)} : 1` : "—", c: rr >= 3 ? T.green : rr >= 2 ? T.yellow : T.red },
                        { l: "Target 1", v: result.t1 ? `$${result.t1.toFixed(2)}` : "—", c: T.green },
                        { l: "T1 Return", v: retT1 ? `+${retT1.toFixed(1)}%` : "—", c: T.green },
                        { l: "Target 2", v: result.t2 ? `$${result.t2.toFixed(2)}` : "—", c: T.green },
                        { l: "T2 Return", v: retT2 ? `+${retT2.toFixed(1)}%` : "—", c: T.green },
                      ].map(({ l, v, c }) => (
                        <div key={l} style={{ background: T.surface, borderRadius: 8, padding: "9px 11px" }}>
                          <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>{l}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: T.textDim }}>Enter a buy price in the watchlist row to calculate R:R.</div>
                  )}
                </div>
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 3, height: 16, borderRadius: 2, background: overSized ? T.orange : T.green, flexShrink: 0 }} />
                  <div style={{ fontSize: 10, fontWeight: 800, color: overSized ? T.orange : T.green, letterSpacing: "0.08em" }}>POSITION SIZING</div>
                  {volTier && <div style={{ fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 4, background: volTier === "SPEC" ? `${T.red}22` : volTier === "HIGH" ? `${T.orange}22` : `${T.green}18`, color: volTier === "SPEC" ? T.red : volTier === "HIGH" ? T.orange : T.green }}>{volTier}</div>}
                </div>
                <div style={{ background: T.bg, border: `1px solid ${overSized ? T.orange : T.border}`, borderRadius: 10, padding: 12 }}>
                  {portSize ? (
                    <>
                      {earningsSoon14 && (
                        <div style={{ marginBottom: 10, padding: "8px 10px", background: `${T.orange}12`, border: `1px solid ${T.orange}44`, borderRadius: 7, fontSize: 11, color: T.orange }}>
                          ⚠ Earnings within 14 days — reduce size
                        </div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: overSized ? 8 : 0 }}>
                        {[
                          { l: "Current Position", v: dollarIn ? `$${dollarIn.toFixed(0)}` : "Not set", c: T.text },
                          { l: "% of Portfolio", v: posPct ? `${posPct.toFixed(1)}%` : "—", c: overSized ? T.orange : T.green },
                          { l: "Standard Max (3%)", v: `$${maxDollar.toFixed(0)}`, c: T.textSub },
                          { l: earningsSoon14 ? "Earnings-Adj Max (60%)" : "Earnings-Adj Max", v: earningsAdjMaxDollar ? `$${earningsAdjMaxDollar.toFixed(0)}` : "—", c: earningsSoon14 ? T.orange : T.textSub },
                          { l: volTier === "SPEC" ? "Vol-Adj Max (50%)" : volTier === "HIGH" ? "Vol-Adj Max (75%)" : "Vol-Adj Max", v: adjMaxDollar ? `$${adjMaxDollar.toFixed(0)}` : "—", c: volTier === "SPEC" ? T.red : volTier === "HIGH" ? T.orange : earningsSoon14 ? T.orange : T.green },
                        ].map(({ l, v, c }) => (
                          <div key={l} style={{ background: T.surface, borderRadius: 8, padding: "9px 11px" }}>
                            <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>{l}</div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
                          </div>
                        ))}
                      </div>
                      {overSized && <div style={{ padding: "8px 10px", background: `${T.orange}12`, border: `1px solid ${T.orange}44`, borderRadius: 7, fontSize: 11, color: T.orange }}>⚠ Position exceeds 3% guideline. Consider reducing to ${maxDollar?.toFixed(0)}{volTier === "SPEC" || volTier === "HIGH" ? ` or ${adjMaxDollar?.toFixed(0)} (vol-adjusted)` : ""}.</div>}
                      {volTier === "SPEC" && <div style={{ marginTop: 8, padding: "8px 10px", background: `${T.red}10`, border: `1px solid ${T.red}33`, borderRadius: 7, fontSize: 11, color: T.red }}>🔴 ATR {atrPct?.toFixed(1)}%/day — speculative. Max recommended: 50% of standard (1.5% of portfolio).</div>}
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: T.textDim }}>Set your portfolio size above to calculate recommended position limits.</div>
                  )}
                </div>
              </div>
            </div>
          );
}

function PositionExitTabContent({ result, position, currentPrice, symbol }) {
  const checklist = buildExitChecklist(result, position, currentPrice);
  const healthData = calcPositionHealth({ result, position });
  if (!checklist) {
    return (
      <div style={{ padding: 16, color: T.textDim, fontSize: 12 }}>
        Set a buy price in Sizing to enable exit tracking.
      </div>
    );
  }

  const { entryPrice, pctMove, atr, atr2x, rows } = checklist;
  const healthColor = healthData?.healthColor ?? T.textDim;
  const healthLabel = healthData?.health === "green" ? "GREEN"
    : healthData?.health === "yellow" ? "YELLOW"
    : healthData?.health === "orange" ? "ORANGE"
    : healthData?.health === "red" ? "RED"
    : "—";

  return (
    <div style={{ padding: "16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 3, height: 16, borderRadius: 2, background: T.cyan, flexShrink: 0 }} />
          <div style={{ fontSize: 10, fontWeight: 800, color: T.cyan, letterSpacing: "0.08em" }}>POSITION STATUS</div>
        </div>
        <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, fontSize: 11, lineHeight: 1.8, fontFamily: "'JetBrains Mono', monospace" }}>
          <div><span style={{ color: T.textDim, display: "inline-block", width: 72 }}>Entry:</span> <span style={{ color: T.text }}>${entryPrice.toFixed(2)}</span></div>
          <div><span style={{ color: T.textDim, display: "inline-block", width: 72 }}>Current:</span> <span style={{ color: T.text }}>${currentPrice.toFixed(2)}</span> <span style={{ color: pctMove >= 0 ? T.green : T.red }}>({pctMove >= 0 ? "+" : ""}{pctMove.toFixed(2)}%)</span></div>
          <div><span style={{ color: T.textDim, display: "inline-block", width: 72 }}>ATR:</span> <span style={{ color: T.text }}>${atr.toFixed(2)}</span></div>
          <div><span style={{ color: T.textDim, display: "inline-block", width: 72 }}>2×ATR:</span> <span style={{ color: T.text }}>${atr2x.toFixed(2)}</span></div>
        </div>
      </div>

      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ width: 3, height: 16, borderRadius: 2, background: T.orange, flexShrink: 0 }} />
          <div style={{ fontSize: 10, fontWeight: 800, color: T.orange, letterSpacing: "0.08em" }}>EXIT CHECKLIST</div>
        </div>
        <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
          {rows.map((row) => (
            <div key={row.key} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <span style={{ fontSize: 13, color: row.iconColor, flexShrink: 0, width: 16 }}>{row.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{row.label}</div>
                <div style={{ fontSize: 10, color: T.textDim, marginTop: 2, lineHeight: 1.5 }}>{row.detail}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ padding: "10px 12px", background: `${healthColor}12`, border: `1px solid ${healthColor}44`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textSub }}>Position Health</span>
        <span style={{ fontSize: 12, fontWeight: 900, color: healthColor, letterSpacing: "0.06em" }}>{healthLabel}</span>
      </div>
    </div>
  );
}

class DrawerPanelErrorBoundary extends React.Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("[DrawerPanelErrorBoundary]", error, info?.componentStack);
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 16, background: `${T.red}10`, border: `1px solid ${T.red}44`, borderRadius: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.red, marginBottom: 8 }}>This tab hit an error</div>
          <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.6, marginBottom: 12 }}>{String(this.state.error?.message || this.state.error)}</div>
          <button type="button" onClick={() => this.setState({ error: null })}
            style={{ padding: "8px 14px", background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 8, color: T.text, fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}>
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function WatchlistDetailDrawer({ item, onClose, anthropicKey, openaiKey, geminiKey, aiProvider, isDark, proMode, hintsOn, liveQuote, portfolioSize, onSavePortfolioSize, news, onScan, scanning, onTogglePosition }) {
  const { symbol, result: rawResult, position: bp } = item;
  const result = React.useMemo(() => {
    if (!rawResult) return null;
    if (!item.inPosition && !rawResult.inPosition) return rawResult;
    return refreshPostMoveAssessment(rawResult, {
      inPosition: item.inPosition,
      price: liveQuote?.price || rawResult.price,
    });
  }, [rawResult, item.inPosition, liveQuote?.price]);
  const [tab, setTab] = useState("levels");
  const [optionsView, setOptionsView] = useState("calls");
  React.useEffect(() => {
    if (result?.putsSignal?.fullSignal && !result?.callsSignal?.fullSignal && !result?.callsSignal?.bouncePlayOnly) setOptionsView("puts");
    else setOptionsView("calls");
  }, [result?.callsSignal?.fullSignal, result?.callsSignal?.bouncePlayOnly, result?.putsSignal?.fullSignal]);
  const [drawerW, setDrawerW] = useState(() => {
    try { return parseInt(localStorage.getItem("watchlistDrawerW") || "460"); } catch { return 460; }
  });
  const dragRef = useRef(false);

  const startDrag = (e) => {
    e.preventDefault();
    dragRef.current = true;
    const startX = e.clientX;
    const startW = drawerW;
    const onMove = (e) => {
      if (!dragRef.current) return;
      const delta = startX - e.clientX; // dragging left increases width
      const next = Math.min(900, Math.max(320, startW + delta));
      setDrawerW(next);
    };
    const onUp = () => {
      dragRef.current = false;
      setDrawerW(w => { try { localStorage.setItem("watchlistDrawerW", String(w)); } catch {} return w; });
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  const verdict = useVerdictForTicker(result, symbol, {
    inPosition: item.inPosition,
    position: bp,
    currentPrice: liveQuote?.price || result?.price,
  });
  const col = verdict ? (isDark ? verdict.darkColor : verdict.color) : T.textDim;
  const coverCalls = result && (result.callsSignal?.fullSignal || result.callsSignal?.partialSignal) ? calcCoverCalls(result, symbol) : null;
  const coverPuts  = result && (result.putsSignal?.fullSignal  || result.putsSignal?.partialSignal)  ? calcCoverPuts(result, symbol) : null;
  const hasCallsPlay = callsSignalDisplayActive(result);
  const hasPutsPlay  = result?.putsSignal?.fullSignal  || result?.putsSignal?.partialSignal;
  const calmar = result ? calcCalmar(result) : null;
  const isPrime = result && verdict ? isPrimeSetup(result, verdict) : false;

  // Warning flags
  const showVolWarning = verdict && result && (verdict.layer === "buy" || verdict.layer === "watch" || verdict.layer === "wait" || verdict.layer === "recov") && (verdict.volTier === "SPEC" || verdict.volTier === "HIGH");
  const showCmfWarning = verdict && verdict.cmfConflict && verdict.layer === "buy";

  const tabs = [
    ...(item.inPosition ? [{ id: "position", label: "📍 Position" }] : []),
    { id: "levels",    label: "📊 Levels" },
    { id: "chart",     label: "📈 Chart" },
    { id: "analysis",  label: "📋 Analysis" },
    { id: "tradeflow", label: "🌊 Tradeflow" },
    { id: "ai",        label: "✦ AI" },
    { id: "sizing",    label: "💰 Sizing" },
    { id: "options",   label: hasCallsPlay ? "⚡ Options" : hasPutsPlay ? "🔻 Options" : "⚡ Options" },
  ];

  if (!result) return (
    <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: drawerW, background: T.surface, borderLeft: `1px solid ${T.border}`, zIndex: 200, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.5)" }}>
      {/* Drag handle — visible pill on left edge */}
      <div onMouseDown={startDrag}
        style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 10, cursor: "col-resize", zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center" }}
        title="Drag to resize panel">
        <div style={{ width: 3, height: 40, borderRadius: 2, background: T.border, pointerEvents: "none" }} />
      </div>
      <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onClose} style={{ padding: "5px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.text, borderRadius: 7, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>×</button>
        <span style={{ fontSize: 18, fontWeight: 900, color: T.text }}>{symbol}</span>
      </div>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 12 }}>
        <div style={{ color: T.textDim, fontSize: F.base }}>No data — scan this ticker first</div>
        <button onClick={() => onScan(symbol)} disabled={scanning}
          style={{ padding: "8px 20px", background: `${T.cyan}22`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 8, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
          ↻ Scan {symbol}
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: drawerW, background: T.surface, borderLeft: `1px solid ${T.border}`, zIndex: 200, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.5)" }}>

      {/* Drag handle — visible pill on left edge */}
      <div onMouseDown={startDrag}
        style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 10, cursor: "col-resize", zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center" }}
        title="Drag to resize panel">
        <div style={{ width: 3, height: 40, borderRadius: 2, background: T.border, pointerEvents: "none", transition: "background 0.15s" }} />
      </div>

      {/* ── Header ── */}
      <div style={{ padding: "12px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0, background: T.surface }}>
        <button onClick={onClose} style={{ padding: "5px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.text, borderRadius: 7, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>×</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 20, fontWeight: 900, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>{symbol}</div>
          <div style={{ fontSize: 11, color: T.textSub, fontFamily: "'JetBrains Mono', monospace" }}>
            {(() => {
              const displayPrice = liveQuote?.price || result.price;
              const displayPct = liveQuote?.changePct ?? result.pctChange;
              const isLive = !!liveQuote?.price;
              return (<>
                <span style={{ color: T.text }}>${displayPrice?.toFixed(2)}</span>
                {displayPct != null && <span style={{ color: displayPct >= 0 ? T.green : T.red, marginLeft: 6 }}>{displayPct >= 0 ? "+" : ""}{displayPct.toFixed(2)}%</span>}
                {isLive && <span style={{ color: T.cyan, marginLeft: 6, fontSize: 9 }}>● LIVE</span>}
              </>);
            })()}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {verdict && (
            <div
              title={verdict.label}
              style={{
                padding: "4px 10px",
                borderRadius: 7,
                background: isDark ? verdict.darkBg : verdict.bg,
                border: `1px solid ${col}55`,
                color: col,
                fontSize: 11,
                fontWeight: 800,
                textAlign: "center",
                maxWidth: 220,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {verdict.label}
              {verdict.preExitVerdictLabel && (
                <div style={{ fontSize: 9, color: T.textDim, fontWeight: 500, marginTop: 2 }}>Was: {verdict.preExitVerdictLabel}</div>
              )}
              {isPrime && <div style={{ fontSize: 9, color: "#f59e0b" }}>★ PRIME</div>}
            </div>
          )}
          {result?.breakout52w?.active && (
            <div
              title="52W BREAKOUT ALERT — all breakout confirmations met"
              style={{
                padding: "3px 9px",
                borderRadius: 999,
                background: "linear-gradient(135deg, #16a34a22, #22c55e22)",
                border: "1px solid #22c55e77",
                color: "#22c55e",
                fontSize: 10,
                fontWeight: 900,
                letterSpacing: "0.05em",
              }}
            >
              🚀 BREAKOUT SETUP
            </div>
          )}
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 900, color: result.buyScore >= 7 ? T.green : result.buyScore >= 4 ? "#86efac" : result.buyScore <= -7 ? T.red : result.buyScore <= -4 ? "#fca5a5" : T.textSub }}>
            {result.buyScore > 0 ? "+" : ""}{result.buyScore}
          </div>
        </div>
        <button onClick={() => onScan(symbol)} disabled={scanning}
          title="Rescan this ticker"
          style={{ padding: "5px 9px", background: `${T.cyan}18`, border: `1px solid ${T.cyan}33`, color: T.cyan, borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: scanning ? "not-allowed" : "pointer" }}>
          {scanning ? "…" : "↻"}
        </button>
        {/* In Position toggle */}
        <button onClick={() => onTogglePosition && onTogglePosition(symbol)}
          title={item.inPosition ? "Mark as not in position" : "Mark as in position"}
          style={{ padding: "5px 9px", background: item.inPosition ? `${T.cyan}22` : T.surfaceHi, border: `1px solid ${item.inPosition ? T.cyan : T.border}`, color: item.inPosition ? T.cyan : T.textDim, borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: "pointer", fontWeight: item.inPosition ? 700 : 400, whiteSpace: "nowrap" }}>
          {item.inPosition ? "📍 In Position" : "📍"}
        </button>
      </div>

      {/* ── Position Mode Banner ── */}
      {item.inPosition && (
        <div style={{ flexShrink: 0, padding: "8px 14px", background: `${T.cyan}0d`, borderBottom: `1px solid ${T.cyan}33`, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 14 }}>📍</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.cyan, letterSpacing: "0.06em", marginBottom: 2 }}>POSITION MODE — MANAGING ACTIVE TRADE</div>
            <div style={{ fontSize: 10, color: T.textDim, lineHeight: 1.5 }}>
              Score changes are normal — focus on Position Health and EMA21 stop (${result.ema21?.toFixed(2) ?? "—"}). Hold while thesis intact.
            </div>
          </div>
          {(() => {
            const flags = [];
            if (result?.ema21 && result.price < result.ema21) flags.push("below EMA21");
            if (result?.cmf != null && result.cmf < -0.05) flags.push("CMF dist.");
            const health = flags.length === 0 ? "HOLD" : flags.length === 1 ? "WATCH" : "CAUTION";
            const hc = health === "HOLD" ? T.green : health === "WATCH" ? T.yellow : T.red;
            return (
              <div style={{ textAlign: "center", padding: "5px 10px", background: `${hc}18`, borderRadius: 7, border: `1px solid ${hc}44` }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: hc }}>{health}</div>
                <div style={{ fontSize: 9, color: T.textDim, marginTop: 1 }}>{flags.length === 0 ? "thesis intact" : flags[0]}</div>
              </div>
            );
          })()}
        </div>
      )}
      {(showVolWarning || showCmfWarning) && (
        <div style={{ flexShrink: 0, borderBottom: `1px solid ${T.border}`, padding: "5px 14px", display: "flex", gap: 6, flexWrap: "wrap", background: T.surface }}>
          {showVolWarning && (
            <span title={verdict.volTier === "SPEC"
              ? `ATR ${verdict.atrPct?.toFixed(1)}%/day — speculative. Gap-downs of 15–20% overnight are normal. Size down significantly.`
              : `ATR ${verdict.atrPct?.toFixed(1)}%/day — above-average swings.${verdict.extendedAndVolatile ? " Extended near 52W high. Highest risk zone." : ""} Size smaller, tighten stop.`}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: verdict.volTier === "SPEC" ? `${T.red}18` : `${T.orange}18`, border: `1px solid ${verdict.volTier === "SPEC" ? T.red + "44" : T.orange + "44"}`, fontSize: 10, fontWeight: 800, color: verdict.volTier === "SPEC" ? T.red : T.orange, cursor: "help", letterSpacing: "0.03em" }}>
              ⚠ {verdict.volTier === "SPEC" ? "SPEC · Size way down" : `High Vol${verdict.extendedAndVolatile ? " · Extended" : ""} · Size smaller`}
            </span>
          )}
          {showCmfWarning && (
            <span title={`CMF ${result.cmf?.toFixed(3)} — institutions distributing despite bullish signal. Enter reduced size or wait for CMF to cross positive.`}
              style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${T.orange}18`, border: `1px solid ${T.orange}44`, fontSize: 10, fontWeight: 800, color: T.orange, cursor: "help", letterSpacing: "0.03em" }}>
              ⚠ CMF Conflict · Reduce size
            </span>
          )}
        </div>
      )}

      <DrawerOverviewStack result={result} T={T} />
      <PostMoveAssessmentPanel result={result} T={T} signalLabel={resolveDisplayVerdict(result, { inPosition: !!item.inPosition, activeSignal: result?.activeSignal }).verdict} />

      {/* ── Tab bar ── */}
      <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, background: T.surface, flexShrink: 0, overflowX: "auto" }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: "8px 14px", background: "none", border: "none", borderBottom: `2px solid ${tab === t.id ? (t.id === "calls" ? T.green : t.id === "puts" ? T.red : t.id === "tradeflow" ? T.cyan : T.purple) : "transparent"}`, color: tab === t.id ? T.text : T.textDim, fontSize: F.sm, fontWeight: tab === t.id ? 700 : 400, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Scrollable content ── */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>

        {/* ── POSITION TAB ── */}
        {tab === "position" && item.inPosition && (
          <PositionExitTabContent
            result={result}
            position={bp}
            currentPrice={liveQuote?.price || result?.price}
            symbol={symbol}
          />
        )}

        {/* ── LEVELS TAB ── */}
        {tab === "levels" && (
          <div style={{ padding: "16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>

            {/* KEY PRICE LEVELS */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{ width: 3, height: 16, borderRadius: 2, background: T.cyan, flexShrink: 0 }} />
                <div style={{ fontSize: 10, fontWeight: 800, color: T.cyan, letterSpacing: "0.08em" }}>KEY PRICE LEVELS</div>
              </div>
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
                {(() => {
                  const FIB_NEAR_GOLD = "#fbbf24";
                  const fibNearLabel = { "38.2%": "Fib 38.2%", "50%": "Fib 50%", "61.8%": "Fib 61.8%", "78.6%": "Fib 78.6%" };
                  const fibColor = (label, defaultC) =>
                    (result.fibLevel && fibNearLabel[result.fibLevel] === label ? FIB_NEAR_GOLD : defaultC);
                  return [
                  { l: "EMA 50", v: result.ma?.ema50 ?? result.sm50, c: result.goldenCross ? T.green : T.red },
                  { l: "EMA 200", v: result.ma?.ema200 ?? result.sm200, c: result.goldenCross ? T.green : T.red },
                  { l: "EMA 20", v: result.ma?.ema20 ?? result.ema20, c: result.price > (result.ma?.ema20 ?? result.ema20) ? T.green : T.red },
                  { l: result.ema21SlopeLabel || "EMA 21", v: result.ema21, c: result.price > result.ema21 ? T.green : T.red },
                  { l: "VWAP", v: result.vwap, c: result.vwap && result.price > result.vwap ? T.green : T.red },
                  { l: "Fib 38.2%", v: result.fibs?.fib382, c: fibColor("Fib 38.2%", T.textSub) },
                  { l: "Fib 50%", v: result.fibs?.fib500, c: fibColor("Fib 50%", T.yellow) },
                  { l: "Fib 61.8%", v: result.fibs?.fib618, c: fibColor("Fib 61.8%", T.yellow) },
                  { l: "Fib 78.6%", v: result.fibs?.fib786, c: fibColor("Fib 78.6%", T.textSub) },
                  { l: "Pivot", v: result.pivots?.pivot, c: T.blue },
                  { l: "R1", v: result.pivots?.r1, c: T.orange },
                  { l: "S1", v: result.pivots?.s1, c: T.cyan },
                  { l: "52W High", v: result.recentHigh, c: T.textSub },
                  { l: "ATR (14)", v: result.atr, c: T.purple },
                ].filter(x => x.v != null).map(({ l, v, c }, i, arr) => {
                  const isNearFib = result.fibLevel && fibNearLabel[result.fibLevel] === l;
                  return (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 4px", borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none", background: isNearFib ? `${FIB_NEAR_GOLD}12` : "transparent", borderRadius: isNearFib ? 6 : 0 }}>
                    <span style={{ fontSize: 11, color: isNearFib ? FIB_NEAR_GOLD : T.textSub, fontWeight: isNearFib ? 800 : 400 }}>{l}{isNearFib ? " ◆" : ""}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>${typeof v === "number" ? v.toFixed(2) : v}</span>
                  </div>
                ); });
                })()}
              </div>
            </div>

            {/* 52W RANGE */}
            {result.range52w && (() => {
              const rng = result.range52w;
              const col = rng.score <= 20 ? T.green : rng.score >= 80 ? T.red : rng.score >= 65 ? T.orange : rng.score <= 35 ? T.cyan : T.textSub;
              return (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <div style={{ width: 3, height: 16, borderRadius: 2, background: col, flexShrink: 0 }} />
                    <div style={{ fontSize: 10, fontWeight: 800, color: col, letterSpacing: "0.08em" }}>52-WEEK RANGE</div>
                  </div>
                  <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textDim, marginBottom: 8 }}>
                      <span>LOW ${rng.low?.toFixed(2)}</span>
                      <span style={{ fontSize: 20, fontWeight: 700, color: col }}>{rng.score}<span style={{ fontSize: 11, color: T.textDim }}>/100</span></span>
                      <span>HIGH ${rng.high?.toFixed(2)}</span>
                    </div>
                    <div style={{ height: 8, background: T.border, borderRadius: 4, position: "relative", marginBottom: 10 }}>
                      <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: "100%", background: `linear-gradient(90deg, ${T.green}66, ${T.yellow}66, ${T.red}66)`, borderRadius: 4 }} />
                      <div style={{ position: "absolute", top: "50%", transform: "translate(-50%,-50%)", left: `${rng.score}%`, width: 14, height: 14, borderRadius: "50%", background: col, border: `3px solid ${T.bg}`, boxShadow: `0 0 8px ${col}` }} />
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: T.textDim }}>
                      <span>From low: <span style={{ color: T.green, fontWeight: 700 }}>+{rng.distFromLow?.toFixed(1)}%</span></span>
                      <span>From high: <span style={{ color: rng.distFromHigh > -10 ? T.red : T.textSub, fontWeight: 700 }}>{rng.distFromHigh?.toFixed(1)}%</span></span>
                    </div>
                    {rng.nearLow && <div style={{ marginTop: 8, padding: "6px 10px", background: `${T.green}18`, border: `1px solid ${T.green}44`, borderRadius: 7, fontSize: 10, color: T.green }}>✓ Near 52w low — potential floor. Confirm with RSI + CMF before entry.</div>}
                    {rng.nearHigh && <div style={{ marginTop: 8, padding: "6px 10px", background: `${T.orange}18`, border: `1px solid ${T.orange}44`, borderRadius: 7, fontSize: 10, color: T.orange }}>⚠ Near 52w high — only enter on confirmed volume breakout.</div>}
                  </div>
                </div>
              );
            })()}

            {/* 3-DAY RULE */}
            {result.threeDayRule && (
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 3, height: 16, borderRadius: 2, background: T.yellow, flexShrink: 0 }} />
                  <div style={{ fontSize: 10, fontWeight: 800, color: T.yellow, letterSpacing: "0.08em" }}>3-DAY RULE</div>
                </div>
                <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                  {[
                    { label: "Post-Drop Cooldown", sub: "4%+ drop → wait 3 sessions", active: result.threeDayRule.postDrop?.active, msg: result.threeDayRule.postDrop?.active ? `Drop of ${result.threeDayRule.postDrop.dropPct?.toFixed(1)}% — ${result.threeDayRule.postDrop.cooldownLeft} session(s) remaining` : null, okText: "CLEAR", badText: "COOLING", badColor: T.red },
                    { label: "Breakout Confirmation", sub: "Need 3 closes above resistance", active: false, badge: result.threeDayRule.breakout ? (result.threeDayRule.breakout.confirmed ? "CONFIRMED" : `DAY ${result.threeDayRule.breakout.streak}/3`) : "N/A", badgeColor: result.threeDayRule.breakout?.confirmed ? T.green : result.threeDayRule.breakout ? T.yellow : T.textDim },
                    { label: "Earnings Gap", sub: "5%+ gap → wait 3 sessions", active: result.threeDayRule.earningsGap?.active, msg: result.threeDayRule.earningsGap?.active ? `Gap of ${result.threeDayRule.earningsGap.pct?.toFixed(1)}% — ${result.threeDayRule.earningsGap.stabilLeft} day(s) to stabilize` : null, okText: "CLEAR", badText: "SETTLING", badColor: T.orange },
                    { label: "Gap Fill Watch", sub: "Bullish gap 5%+ → drift window", active: false, badge: result.threeDayRule.gapFillWatch?.active ? "ACTIVE" : result.threeDayRule.gapFillWatch?.windowOpen ? "EXPIRING" : "NONE", badgeColor: result.threeDayRule.gapFillWatch?.active ? T.green : result.threeDayRule.gapFillWatch?.windowOpen ? T.yellow : T.textDim, msg: result.threeDayRule.gapFillWatch?.active ? `+${result.threeDayRule.gapFillWatch.gapPct?.toFixed(1)}% gap ${result.threeDayRule.gapFillWatch.daysAgo}d ago — stocks drift higher 3-5 sessions post beat. Support: $${result.threeDayRule.gapFillWatch.gapPrice?.toFixed(2)}` : null },
                  ].map(({ label, sub, active, msg, okText, badText, badColor, badge, badgeColor }) => (
                    <div key={label}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: msg ? 6 : 0 }}>
                        <div>
                          <div style={{ fontSize: 11, fontWeight: 600, color: T.text }}>{label}</div>
                          <div style={{ fontSize: 10, color: T.textDim }}>{sub}</div>
                        </div>
                        <div style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 4, background: badge ? `${badgeColor}22` : active ? `${badColor}22` : `${T.green}18`, color: badge ? badgeColor : active ? badColor : T.green, border: `1px solid ${badge ? badgeColor : active ? badColor : T.green}44` }}>
                          {badge || (active ? badText : okText)}
                        </div>
                      </div>
                      {msg && <div style={{ fontSize: 10, color: badColor, padding: "5px 8px", background: `${badColor}12`, borderRadius: 6 }}>{msg}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* VIX TRANSLATOR */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{ width: 3, height: 16, borderRadius: 2, background: T.purple, flexShrink: 0 }} />
                <div style={{ fontSize: 10, fontWeight: 800, color: T.purple, letterSpacing: "0.08em" }}>VIX TRANSLATOR</div>
              </div>
              <VixTranslator />
            </div>

            <SignalReasonsSection result={result} T={T} />

          </div>
        )}

        {tab === "chart" && (
          <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.cyan, letterSpacing: "0.08em" }}>DAILY CHART</div>
              <MaStackPills result={result} T={T} />
            </div>
            <TickerMaChart symbol={symbol} result={result} T={T} height={Math.min(420, Math.max(280, drawerW - 48))} />
          </div>
        )}

        {/* ── ANALYSIS TAB ── */}
        {tab === "analysis" && (
          <div style={{ padding: "16px 16px", display: "flex", flexDirection: "column", gap: 16 }}>
            <AnalysisTabPanel result={result} T={T} inPosition={!!item.inPosition} activeSignal={result?.activeSignal} />
          </div>
        )}

        {/* ── AI TAB ── */}
        {tab === "ai" && (
          <div style={{ padding: "16px 16px" }}>
            <DrawerPanelErrorBoundary key={`ai-${symbol}`}>
              <AnalystPanel
                result={result} symbol={symbol}
                anthropicKey={anthropicKey} openaiKey={openaiKey}
                geminiKey={geminiKey} aiProvider={aiProvider}
                isDark={isDark} bp={bp} news={news}
                inPosition={!!item.inPosition}
                dollarIn={bp ? (() => { const shares = parseFloat(bp.shares) || null; const buyPrice = parseFloat(bp.buyPrice) || null; return shares && buyPrice ? shares * buyPrice : (parseFloat(bp.dollarAmt) || null); })() : null}
                portfolioSize={portfolioSize}
                onSavePortfolioSize={onSavePortfolioSize}
              />
            </DrawerPanelErrorBoundary>
          </div>
        )}

        {/* ── SIZING TAB ── */}
        {tab === "sizing" && <SizingTabContent bp={bp} result={result} portfolioSize={portfolioSize} onSavePortfolioSize={onSavePortfolioSize} symbol={symbol} inPosition={!!bp && ((parseFloat(bp.buyPrice) || 0) > 0 || (parseFloat(bp.shares) || 0) > 0 || (parseFloat(bp.dollarAmt) || 0) > 0)} />}

        {/* ── TRADEFLOW TAB ── */}
        {tab === "tradeflow" && (
          result
            ? <div style={{ padding: "16px 16px" }}><SignalTimelinePanel result={result} T={T} /><OrderFlowSummary result={result} T={T} /><TradeFlowPanel result={result} symbol={symbol} isDark={isDark} /></div>
            : <div style={{ padding: "40px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 24, marginBottom: 12 }}>🌊</div>
                <div style={{ fontSize: F.sm, fontWeight: 700, color: T.textSub, marginBottom: 8 }}>No Tradeflow data yet</div>
                <div style={{ fontSize: F.xs, color: T.textDim, lineHeight: 1.7, maxWidth: 280, margin: "0 auto" }}>
                  Scan this ticker to see institutional money flow, trend conviction, relative strength, and divergence signals.
                </div>
                <button onClick={() => onScan(symbol)} disabled={scanning}
                  style={{ marginTop: 16, padding: "8px 20px", background: `${T.cyan}22`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 8, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                  ↻ Scan {symbol}
                </button>
              </div>
        )}

        {/* ── OPTIONS TAB — always visible ── */}
        {tab === "options" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <OptionsSignalPanel result={result} T={T} />
            <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 4 }}>
            {/* Sub-tab toggle — Calls / Puts */}
            {/* Sub-tab toggle */}
            {(() => {
              // Compute entry timing status for badge color
              const ema9 = result?.ema9;
              const price = result?.price;
              const pctAbove = ema9 && price ? ((price - ema9) / ema9) * 100 : null;
              const callsAtEntry = hasCallsPlay && pctAbove != null && Math.abs(pctAbove) <= 1.0;
              const callsBadgeColor = callsAtEntry ? T.green : hasCallsPlay ? T.yellow : T.textDim;
              const callsBadgeLabel = callsAtEntry ? "⚡ ENTER" : hasCallsPlay ? "⏳ WAIT" : null;
              return (
                <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, background: T.bg, flexShrink: 0 }}>
                  <button onClick={() => setOptionsView("calls")}
                    style={{ flex: 1, padding: "10px", background: "none", border: "none", borderBottom: `2px solid ${optionsView === "calls" ? (callsAtEntry ? T.green : hasCallsPlay ? T.yellow : T.green) : "transparent"}`, color: optionsView === "calls" ? T.text : T.textDim, fontSize: F.sm, fontWeight: optionsView === "calls" ? 700 : 400, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                    ⚡ Calls
                    {callsBadgeLabel && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: `${callsBadgeColor}22`, color: callsBadgeColor, fontWeight: 700, border: `1px solid ${callsBadgeColor}44` }}>{callsBadgeLabel}</span>}
                  </button>
                  <button onClick={() => setOptionsView("puts")}
                    style={{ flex: 1, padding: "10px", background: "none", border: "none", borderBottom: `2px solid ${optionsView === "puts" ? T.red : "transparent"}`, color: optionsView === "puts" ? T.text : T.textDim, fontSize: F.sm, fontWeight: optionsView === "puts" ? 700 : 400, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                    🔻 Puts
                    {hasPutsPlay && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: `${T.red}22`, color: T.red, fontWeight: 700, border: `1px solid ${T.red}44` }}>SIGNAL</span>}
                  </button>
                </div>
              );
            })()}
                  {optionsView === "calls" && (
                    <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                      {!hasCallsPlay && (
                        <div style={{ padding: "14px 16px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, textAlign: "center" }}>
                          <div style={{ fontSize: 20, marginBottom: 8 }}>⚡</div>
                          <div style={{ fontSize: F.sm, fontWeight: 700, color: T.textSub, marginBottom: 6 }}>No active calls signal</div>
                          <div style={{ fontSize: F.xs, color: T.textDim, lineHeight: 1.7, maxWidth: 320, margin: "0 auto" }}>
                            Calls signal fires when BB Squeeze + MACD bullish + RSI ideal + CMF positive + Gate GO all align.
                          </div>
                        </div>
                      )}
                      {coverCalls && (
                        <div style={{ padding: "10px 12px", background: `${T.yellow}10`, border: `1px solid ${T.yellow}44`, borderRadius: 8 }}>
                          <div style={{ fontSize: F.xs, fontWeight: 800, color: T.yellow, marginBottom: 4 }}>{coverCalls.level === "COVER" ? "⚠ COVER YOUR CALLS — MOVE MAY BE EXHAUSTED" : "⚠ CONSIDER PARTIAL PROFIT ON CALLS"}</div>
                          <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.7 }}>{coverCalls.reasons.map((r, i) => <div key={i}>• {r}</div>)}</div>
                        </div>
                      )}
                      <OptionsPlayPanel result={result} symbol={symbol} type="calls" />
                    </div>
                  )}
                  {optionsView === "puts" && (
                    <div style={{ padding: "12px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                      {!hasPutsPlay && (
                        <div style={{ padding: "14px 16px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, textAlign: "center" }}>
                          <div style={{ fontSize: 20, marginBottom: 8 }}>🔻</div>
                          <div style={{ fontSize: F.sm, fontWeight: 700, color: T.textSub, marginBottom: 6 }}>No active puts signal</div>
                          <div style={{ fontSize: F.xs, color: T.textDim, lineHeight: 1.7, maxWidth: 320, margin: "0 auto" }}>
                            Puts signal fires when 200 SMA structure bearish + CMF negative + score below -5 + institutional distribution detected.
                          </div>
                        </div>
                      )}
                      {coverPuts && (
                        <div style={{ padding: "10px 12px", background: `${T.yellow}10`, border: `1px solid ${T.yellow}44`, borderRadius: 8 }}>
                          <div style={{ fontSize: F.xs, fontWeight: 800, color: T.yellow, marginBottom: 4 }}>{coverPuts.level === "COVER" ? "⚠ COVER YOUR PUTS — MOVE MAY BE EXHAUSTED" : "⚠ CONSIDER PARTIAL PROFIT ON PUTS"}</div>
                          <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.7 }}>{coverPuts.reasons.map((r, i) => <div key={i}>• {r}</div>)}</div>
                        </div>
                      )}
                      <OptionsPlayPanel result={result} symbol={symbol} type="puts" />
                    </div>
                  )}

            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryPanel({ result, symbol, anthropicKey, openaiKey, geminiKey, aiProvider, isDark, bp, dollarIn, news, portfolioSize, onSavePortfolioSize }) {
  const hasCallsPlay = callsSignalDisplayActive(result);
  const hasPutsPlay  = result?.putsSignal?.fullSignal  || result?.putsSignal?.partialSignal;
  const coverPuts = hasPutsPlay ? calcCoverPuts(result, symbol) : null;
  const coverCalls = (result?.callsSignal?.fullSignal || result?.callsSignal?.partialSignal) ? calcCoverCalls(result, symbol) : null;

  const defaultTab = "analysis";
  const [tab, setTab] = useState(defaultTab);
  const [optionsView, setOptionsView] = useState(hasCallsPlay ? "calls" : hasPutsPlay ? "puts" : "calls");

  const tabs = [
    { id: "analysis",  label: "📋 Analysis" },
    { id: "tradeflow", label: "🌊 Tradeflow" },
    { id: "options",   label: hasCallsPlay ? "⚡ Options" : hasPutsPlay ? "🔻 Options" : "⚡ Options" },
  ];

  return (
    <div>
      {/* Tab bar — only show if there's more than one tab */}
      {true && (
        <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, paddingLeft: 20 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{
                padding: "8px 16px", background: "none", border: "none",
                borderBottom: tab === t.id ? `2px solid ${t.id === "calls" ? T.green : t.id === "puts" ? T.red : t.id === "tradeflow" ? T.cyan : T.purple}` : "2px solid transparent",
                color: tab === t.id ? T.text : T.textDim,
                fontSize: F.sm, fontWeight: tab === t.id ? 700 : 400,
                fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap",
              }}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab content */}
      <div style={{ padding: tab === "tradeflow" ? "0" : "16px 20px" }}>
        {tab === "analysis" && (
          <>
            <LiveQuote symbol={symbol} lastClose={result?.price} />
            <AnalystPanel result={result} symbol={symbol}
              anthropicKey={anthropicKey} openaiKey={openaiKey}
              geminiKey={geminiKey} aiProvider={aiProvider}
              isDark={isDark} bp={bp} news={news}
              inPosition={!!bp && ((parseFloat(bp.buyPrice) || 0) > 0 || (parseFloat(bp.shares) || 0) > 0 || (parseFloat(bp.dollarAmt) || 0) > 0)} />
          </>
        )}
        {tab === "tradeflow" && (
          result
            ? <TradeFlowPanel result={result} symbol={symbol} isDark={isDark} />
            : <div style={{ padding: "40px 20px", textAlign: "center" }}>
                <div style={{ fontSize: 24, marginBottom: 12 }}>🌊</div>
                <div style={{ fontSize: F.sm, fontWeight: 700, color: T.textSub, marginBottom: 8 }}>No Tradeflow data yet</div>
                <div style={{ fontSize: F.xs, color: T.textDim, lineHeight: 1.7, maxWidth: 280, margin: "0 auto" }}>Scan this ticker to see money flow, trend conviction, and divergence signals.</div>
              </div>
        )}
        {tab === "options" && (
            <div>
              {(() => {
                const ema9 = result?.ema9;
                const price = result?.price;
                const pctAbove = ema9 && price ? ((price - ema9) / ema9) * 100 : null;
                const callsAtEntry = hasCallsPlay && pctAbove != null && Math.abs(pctAbove) <= 1.0;
                const callsBadgeColor = callsAtEntry ? T.green : hasCallsPlay ? T.yellow : T.textDim;
                const callsBadgeLabel = callsAtEntry ? "⚡ ENTER" : hasCallsPlay ? "⏳ WAIT" : null;
                return (
                  <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, background: T.bg }}>
                    <button onClick={() => setOptionsView("calls")}
                      style={{ flex: 1, padding: "10px", background: "none", border: "none", borderBottom: `2px solid ${optionsView === "calls" ? (callsAtEntry ? T.green : hasCallsPlay ? T.yellow : T.green) : "transparent"}`, color: optionsView === "calls" ? T.text : T.textDim, fontSize: F.sm, fontWeight: optionsView === "calls" ? 700 : 400, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                      ⚡ Calls
                      {callsBadgeLabel && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: `${callsBadgeColor}22`, color: callsBadgeColor, fontWeight: 700, border: `1px solid ${callsBadgeColor}44` }}>{callsBadgeLabel}</span>}
                    </button>
                    <button onClick={() => setOptionsView("puts")}
                      style={{ flex: 1, padding: "10px", background: "none", border: "none", borderBottom: `2px solid ${optionsView === "puts" ? T.red : "transparent"}`, color: optionsView === "puts" ? T.text : T.textDim, fontSize: F.sm, fontWeight: optionsView === "puts" ? 700 : 400, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                      🔻 Puts
                      {hasPutsPlay && <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: `${T.red}22`, color: T.red, fontWeight: 700, border: `1px solid ${T.red}44` }}>SIGNAL</span>}
                    </button>
                  </div>
                );
              })()}
              {optionsView === "calls" && (
                <>
                  {!hasCallsPlay && (
                    <div style={{ margin: "12px 20px", padding: "12px 16px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, textAlign: "center" }}>
                      <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textSub, marginBottom: 4 }}>No active calls signal</div>
                      <div style={{ fontSize: F.xs, color: T.textDim, lineHeight: 1.7 }}>Calls signal fires when BB Squeeze + MACD bullish + RSI ideal + CMF positive + Gate GO all align.</div>
                    </div>
                  )}
                  {coverCalls && (
                    <div style={{ margin: "10px 20px 0", padding: "10px 14px", background: `${T.yellow}10`, border: `1px solid ${T.yellow}44`, borderRadius: 8 }}>
                      <div style={{ fontSize: F.xs, fontWeight: 800, color: T.yellow, marginBottom: 4 }}>{coverCalls.level === "COVER" ? "⚠ COVER YOUR CALLS — MOVE MAY BE EXHAUSTED" : "⚠ CONSIDER PARTIAL PROFIT ON CALLS"}</div>
                      <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.7 }}>{coverCalls.reasons.map((r, i) => <div key={i}>• {r}</div>)}</div>
                    </div>
                  )}
                  <OptionsPlayPanel result={result} symbol={symbol} type="calls" />
                </>
              )}
              {optionsView === "puts" && (
                <>
                  {!hasPutsPlay && (
                    <div style={{ margin: "12px 20px", padding: "12px 16px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, textAlign: "center" }}>
                      <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textSub, marginBottom: 4 }}>No active puts signal</div>
                      <div style={{ fontSize: F.xs, color: T.textDim, lineHeight: 1.7 }}>Puts signal fires when 200 SMA structure bearish + CMF negative + score below -5 + institutional distribution detected.</div>
                    </div>
                  )}
                  {coverPuts && (
                    <div style={{ margin: "10px 20px 0", padding: "10px 14px", background: `${T.yellow}10`, border: `1px solid ${T.yellow}44`, borderRadius: 8 }}>
                      <div style={{ fontSize: F.xs, fontWeight: 800, color: T.yellow, marginBottom: 4 }}>{coverPuts.level === "COVER" ? "⚠ COVER YOUR PUTS — MOVE MAY BE EXHAUSTED" : "⚠ CONSIDER PARTIAL PROFIT ON PUTS"}</div>
                      <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.7 }}>{coverPuts.reasons.map((r, i) => <div key={i}>• {r}</div>)}</div>
                    </div>
                  )}
                  <OptionsPlayPanel result={result} symbol={symbol} type="puts" />
                </>
              )}
            </div>
        )}
      </div>
    </div>
  );
}

// ─── /api/ai proxy — reference payload (see AnalystPanel.runAI below; drawer call must stay in sync) ───
// Request: POST /api/ai
//   headers: { "Content-Type": "application/json" }
//   body: JSON.stringify({
//     prompt: string,
//     model: "claude-sonnet-4-20250514",
//     max_tokens: number,
//     ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
//   })
// Response handling: const data = await res.json();
//   if (data.error) throw new Error(data.error.message || data.error || "AI error");
//   const text = data.content?.[0]?.text || "No response";
// Server-side retries (529 / overloaded) are implemented in api/ai.js — client must POST JSON as above.

// ─── ANALYST PANEL (narrative + AI) ──────────────────────────────────────────
function AnalystPanel({ result, symbol, anthropicKey, openaiKey, geminiKey, aiProvider = "anthropic", isDark, bp, news, dollarIn = null, portfolioSize = null, onSavePortfolioSize = null, inPosition = false }) {
  const [editingPort, setEditingPort] = useState(false);
  const [portInput, setPortInput] = useState(portfolioSize ? String(portfolioSize) : "");
  React.useEffect(() => { if (portfolioSize) setPortInput(String(portfolioSize)); }, [portfolioSize]);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiDate, setAiDate] = useState(null);
  const [aiUsedProvider, setAiUsedProvider] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [localProvider, setLocalProvider] = useState(aiProvider || "anthropic");

  const [triggers, setTriggers] = useState([]);
  const [triggersLoading, setTriggersLoading] = useState(false);
  const [macdTriggerRetiredNotice, setMacdTriggerRetiredNotice] = useState(false);

  const [aiCautionLocal, setAiCautionLocal] = useState(false);

  const clearLocalAiCache = async () => {
    await clearTickerAiCache(symbol);
    setAiAnalysis(null);
    setAiDate(null);
    setAiUsedProvider(null);
    setTriggers([]);
    setAiError(null);
    setAiCautionLocal(false);
    setMacdTriggerRetiredNotice(false);
  };

  // Load cached summary + triggers for this ticker on mount
  useEffect(() => {
    (async () => {
      try {
        const cached = await load(`aiSummary_${symbol}`);
        const text =
          typeof cached?.text === "string"
            ? cached.text
            : typeof cached === "string"
            ? cached
            : null;
        if (text) {
          setAiAnalysis(text);
          setAiDate(typeof cached?.date === "string" ? cached.date : null);
          setAiUsedProvider(cached?.provider ?? null);
          const lower = text.toLowerCase();
          const cautionWords = ["do not buy", "don't buy", "avoid", "wait for", "not yet", "overbought", "extended", "caution", "pullback first", "risky entry"];
          setAiCautionLocal(cautionWords.some(w => lower.includes(w)));
        }
        const cachedVerdict = await load(`aiVerdict_${symbol}`);
        if (cachedVerdict?.caution) setAiCautionLocal(true);
        const cachedTriggers = await load(`aiTriggers_${symbol}`);
        let triggerList = cachedTriggers;
        if (triggerList && typeof triggerList === "object" && !Array.isArray(triggerList) && Array.isArray(triggerList.triggers)) {
          triggerList = triggerList.triggers;
        }
        if (triggerList) {
          const normalized = Array.isArray(triggerList) ? triggerList.filter(Boolean) : [];
          const { kept, hadMacd } = filterRetiredTriggers(normalized);
          setTriggers(kept);
          if (hadMacd && !localStorage.getItem("auctus_macd_trigger_retired_notice")) {
            setMacdTriggerRetiredNotice(true);
            localStorage.setItem("auctus_macd_trigger_retired_notice", "1");
          }
        }
      } catch (e) {
        console.warn("[AnalystPanel] cache load:", e?.message || e);
      }
    })();
  }, [symbol]);

  // Extract structured triggers from the AI summary using a second small call
  const extractTriggers = async (summaryText) => {
    setTriggersLoading(true);
    try {
      const triggerPrompt = `Based on this trading analysis for ${symbol}, extract up to 3 specific watch conditions AND a verdict. Return ONLY valid JSON:

${summaryText}

Trigger types (use only — do NOT use macd_bullish or macd_bearish; MACD is display-only in v2): rsi_below, rsi_above, price_above, price_below, golden_cross, death_cross, cmf_positive, cmf_negative, rvol_above, adx_above, bb_squeeze_fires, above_vwap, below_vwap, above_ema21, below_ema21

Return format (no other text):
{"triggers":[{"type":"one_of_the_above_types","value":number_or_null,"label":"plain English description","action":"BUY|SELL|WATCH"}],"verdict":{"action":"BUY_NOW|BUY|WATCH|WAIT|BEARISH|NEUTRAL","caution":true_or_false,"reason":"one sentence max","confidence":"high|medium|low"}}`;
      let raw = "";
      // Use server-side AI proxy (same request/response contract as AnalystPanel.runAI)
      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: triggerPrompt,
          model: "claude-sonnet-4-20250514",
          max_tokens: 300,
          ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      raw = data.content?.[0]?.text || "";
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      if (parsed.triggers?.length) {
        const { kept, hadMacd } = filterRetiredTriggers(parsed.triggers.slice(0, 3));
        setTriggers(kept);
        save(`aiTriggers_${symbol}`, kept);
        if (hadMacd && !localStorage.getItem("auctus_macd_trigger_retired_notice")) {
          setMacdTriggerRetiredNotice(true);
          localStorage.setItem("auctus_macd_trigger_retired_notice", "1");
        }
      }
      if (parsed.verdict) {
        save(`aiVerdict_${symbol}`, parsed.verdict);
      }
    } catch (e) { console.warn("Trigger extraction failed:", e.message); }
    finally { setTriggersLoading(false); }
  };

  // Available providers = only those with keys connected
  // AI is always available — runs server-side via /api/ai
  const connectedProviders = [{ id: "anthropic", name: "Claude (Anthropic)", color: "#f97316" }];

  const flipAnalysis = React.useMemo(() => {
    try {
      return generateFlipAnalysis(result);
    } catch (e) {
      console.warn("[AnalystPanel] flip analysis:", e?.message || e);
      return { flips: [], levels: [] };
    }
  }, [result]);

  const runAI = async () => {
    setAiLoading(true); setAiError(null); setAiAnalysis(null);
    try {
      // Compute verdict for AI context
      const aiVerdictCtx = calcVerdict({ result, aiCaution: false });
      const isAccumZone = aiVerdictCtx?.layer === "accum";
      const isBubble = aiVerdictCtx?.bubbleEntry;

      const summary = `
Ticker: ${symbol}
Price: $${result.price?.toFixed(2)}
Auctus Verdict: ${aiVerdictCtx?.label || result.signal}${isAccumZone ? " — LONG-TERM ACCUMULATION ZONE (not a swing trade)" : isBubble ? " — MARGINAL ENTRY (risk-off market conditions)" : ""}
Signal: ${result.signal}
Score: ${result.buyScore}
RSI: ${result.rsi?.toFixed(1)}
MACD: ${result.macd?.bullish ? "Bullish" : "Bearish"}${result.macd?.crossover ? " (fresh crossover)" : result.macd?.crossunder ? " (fresh crossunder)" : ""}
Bollinger: ${result.bb?.isSqueeze ? "SQUEEZE" : result.price > result.bb?.upper ? "Above upper band" : result.price < result.bb?.lower ? "Below lower band" : "Mid band"}
CMF: ${result.cmf?.toFixed(3)}
VWAP: ${result.vwap?.toFixed(2)} (price is ${result.price > result.vwap ? "above" : "below"})
Golden Cross: ${result.goldenCross ? "YES" : "NO"}
200 SMA Structure: ${result.deathCross ? "YES" : "NO"}
ADX: ${result.adx?.adx?.toFixed(1)} (${result.adx?.label})
52W Range Score: ${result.range52w?.score}/100 (${result.range52w?.nearLow ? "near low" : result.range52w?.nearHigh ? "near high" : "mid range"})
RVOL: ${result.rvol?.ratio?.toFixed(2)}x average
OBV: ${result.obv?.bearishDiv ? "Bearish divergence" : result.obv?.bullishDiv ? "Bullish divergence" : result.obv?.confirming ? "Confirming" : "Diverging"}
Parabolic SAR: ${result.psar?.bullish ? "Bullish" : "Bearish"}
Trend Structure: ${result.trendStructure?.structure}
Entry Gate: ${result.gate?.label} (${result.gate?.passed}/${result.gate?.total})
Weekly Trend: ${result.weeklyTrend?.label || "N/A"} (weekly EMA21 ${result.weeklyTrend?.aboveEma21 ? "above" : "below"} price)
MACD Histogram: ${result.macd?.histAccelBull ? "Accelerating bullish" : result.macd?.histFadingBull ? "Fading bullish — losing steam" : result.macd?.histAccelBear ? "Accelerating bearish" : result.macd?.histFadingBear ? "Fading bearish — potential reversal" : "Neutral"}
IV Rank (BB proxy): ${result.bb?.ivRank ?? "N/A"}/100 — ${result.bb?.ivLabel ?? "N/A"} (${result.bb?.goodForBuying ? "cheap options — ideal to buy premium" : result.bb?.avoidBuying ? "expensive options — consider spreads over naked calls/puts" : "average IV"})
EMA9: $${result.ema9?.toFixed(2) ?? "N/A"} | EMA9>EMA21: ${result.ema9AboveEma21 ? "YES" : "NO"} | Price at EMA9 pullback: ${result.nineEmaEntry ? "YES — ideal entry timing" : "NO"}
CMF Divergence: ${result.cmfDiv?.bullishDiv ? "BULLISH" : result.cmfDiv?.bearishDiv ? "BEARISH" : "None"}
      `.trim();

      const phaseUiForAi = result ? derivePhaseUi(result) : null;
      const setupTypeForAi = (() => {
        const t = result?.activeSetupType;
        const ts = result?.tradeSetups || {};
        if (t === "VCP") return ts.vcp?.breakout_confirmed ? "VCP_BREAKOUT" : "VCP_FORMING";
        if (t === "BULL_FLAG") return "BULL_FLAG";
        if (t === "RANGE_BREAKOUT") return "RANGE_BREAKOUT";
        if (t === "SUPPORT_BOUNCE") return "SUPPORT_BOUNCE";
        if (t === "EARNINGS_DRIFT") return "EARNINGS_DRIFT";
        if (t === "GAP_AND_GO") return "GAP_AND_GO";
        if (t === "GAP_FILL") return "GAP_FILL";
        if (t === "DEAD_CAT_BOUNCE") return "DEAD_CAT_BOUNCE";
        return t || "—";
      })();
      const daysToEarnings = result.earningsState?.daysAway ?? result.scanEarningsDaysAway ?? null;
      const tideScore = typeof result.marketTide?.breadthScore === "number" ? result.marketTide.breadthScore : null;
      const stopPrice = (result.price != null && result.atr != null) ? (result.price - 2 * result.atr) : null;

      const tradePlanFields = `

TRADE PLAN INPUTS (use these exact values):
tradeRecommendation: ${result.tradeRecommendation ? JSON.stringify(result.tradeRecommendation) : "—"}
estimatedTimeframe: ${result.estimatedTimeframe ?? "—"}
estimatedDaysToTarget: ${result.estimatedDaysToTarget ?? "—"}
optionsDTE: ${result.optionsDTE ?? "—"}
optionsStrike: ${result.optionsStrike ?? "—"}
setup_type: ${setupTypeForAi}
phase: ${phaseUiForAi?.label || "—"}
days_to_earnings: ${daysToEarnings ?? "—"}
market_tide_score: ${tideScore ?? "—"}
sector_rs_label: ${result.rsVsSector?.label ?? "—"}
t1Price: ${result.t1 != null ? `$${result.t1.toFixed(2)}` : "—"}
t2Price: ${result.t2 != null ? `$${result.t2.toFixed(2)}` : "—"}
stopPrice: ${stopPrice != null ? `$${stopPrice.toFixed(2)}` : "—"}
atr: ${result.atr != null ? result.atr.toFixed(2) : "—"}
      `.trim();

      // Inject recent news headlines if available (drawer may pass [] when empty)
      const newsPack = news && typeof news === "object" && !Array.isArray(news) ? news : null;
      const newsSection = newsPack?.articles?.length
        ? `\n\nRECENT NEWS (last 72h):\n` + newsPack.articles.slice(0, 5).map(a => `• [${a.time}] ${a.title} (${a.source})`).join("\n") + `\nOverall news sentiment: ${newsPack.label ?? "—"}`
        : "";

      const prompt = isAccumZone
        ? `You are a senior investment analyst specializing in identifying long-term accumulation zones. This is NOT a swing trade setup — the Auctus system has flagged this as a potential long-term accumulation zone for a quality stock at a depressed price. Give clear, direct analysis in plain English. No disclaimers. Talk like a seasoned analyst to a patient long-term investor. 3-4 paragraphs max.\n\nAnalyze these technical signals for ${symbol} and give your honest assessment: Is the selling genuinely slowing? Is this a real long-term buy zone or a falling knife? What are the key signals that would confirm accumulation is happening (look at CMF trend, RSI divergence, volume on down days)? What price level or CMF reading would give higher conviction for adding? If news is provided, does the fundamental story support a long-term thesis?\n\n${summary}${newsSection}`
        : isBubble
        ? `You are a professional swing trader and technical analyst. This setup meets minimum entry criteria but is flagged as MARGINAL — the score is just above threshold and market conditions are risk-off. Be direct and honest about whether this trade is worth taking given current conditions. 3-4 paragraphs max.\n\nAnalyze these technical signals for ${symbol}. Focus on: is this setup strong enough to overcome the risk-off macro environment? What would make this a high-conviction entry vs what could cause it to fail? Sizing recommendation?\n\n${summary}${newsSection}`
        : `You are a professional swing trader and technical analyst. Give clear, direct, actionable analysis in plain English. No fluff. No disclaimers. Talk like a trader to a trader.\n\nAnalyze these technical signals for ${symbol} and give me your honest assessment.\n\n${summary}\n\n${tradePlanFields}${newsSection}`;

      const analysisData = Object.fromEntries(
        Object.entries({
          signal: result.signal,
          signalStrength: result.signalStrength,
          signalReasons: result.signalReasons,
          action: result.action,
          module1: result.module1,
          module2: result.module2,
          module3: result.module3,
          module4: result.module4,
          ema8: result.ema8,
          ema20: result.ema20,
          consolidation: result.consolidation,
          relativeStrength: result.relativeStrength,
          sectorStage: result.sectorStage,
          sectorName: result.sectorName,
          t1_price: result.t1Price,
          t2_price: result.t2Price,
          stop_price: result.stopPrice,
          market_tide_score: result.marketTide?.breadthScore,
          days_to_earnings: getEarningsDays(result, symbol).days,
          earnings_context: earningsContextForAi(result, symbol),
          cmf: result.cmf,
          rsi: result.rsi,
          price: result.price,
          score: result.score,
          sector: result.sector,
          move1d: result.move1d,
          move5d: result.move5d,
          move21d: result.move21d,
          move63d: result.move63d,
          riskLevel: result.postMoveAssessment?.riskLevel,
          flags: result.postMoveAssessment?.flags,
          entryGuidance: result.postMoveAssessment?.entryGuidance,
          guidanceText: result.postMoveAssessment?.guidanceText,
        }).filter(([, v]) => v != null)
      );

      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          analysisData,
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      const responseText = data.content?.[0]?.text || "No response";

      setAiAnalysis(responseText);
      const now = new Date().toLocaleString();
      setAiDate(now);
      setAiUsedProvider("anthropic");
      save(`aiSummary_${symbol}`, { text: responseText, date: now, provider: "anthropic", signal: resolveDisplayVerdict(result, { inPosition: !!inPosition, activeSignal: result?.activeSignal }).actionText ?? result.signal, score: result.buyScore });
      extractTriggers(responseText);
    } catch (e) {
      setAiError(e.message);
    } finally { setAiLoading(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      <PriceStorySection result={result} T={T} />

      {/* ── 1. AUCTUS ANALYSIS CTA ── */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 12 }}>✦ AUCTUS ANALYSIS — SENIOR ANALYST</div>
        {result?.module1 != null && (
          <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.6, marginBottom: 12, padding: "8px 10px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8 }}>
            Analysis uses the 4-module signal data including {safeDisplayVerdict(result, { inPosition: !!inPosition, activeSignal: result?.activeSignal }).verdict} detection and {result?.module4?.strategy ? String(result.module4.strategy).replace(/_/g, " ") : "directional"} options recommendation.
          </div>
        )}
        <button onClick={runAI} disabled={aiLoading}
          style={{ width: "100%", padding: "13px 20px", background: aiLoading ? T.surfaceHi : `linear-gradient(135deg, #7c3aed, #d946ef)`, border: "none", color: aiLoading ? T.textDim : "#fff", fontSize: 14, fontWeight: 800, fontFamily: "inherit", cursor: aiLoading ? "not-allowed" : "pointer", borderRadius: 10, letterSpacing: "0.03em", marginBottom: aiAnalysis ? 14 : 0 }}>
          {aiLoading ? "⏳  Analyzing..." : aiAnalysis ? "↻  Refresh Analysis" : "✦  Run Auctus Analysis"}
        </button>

        {aiAnalysis && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.purple, letterSpacing: "0.08em" }}>✦ AUCTUS ANALYSIS — SENIOR ANALYST</div>
                {aiDate && <div style={{ fontSize: 10, color: T.textDim }}>· {aiDate}</div>}
              </div>
            </div>
            <div style={{ fontSize: 13, color: T.text, lineHeight: 1.85, whiteSpace: "pre-wrap", padding: "14px 16px", background: `${T.purple}0a`, border: `1px solid ${T.purple}33`, borderRadius: 10 }}>{aiAnalysis}</div>
          </div>
        )}

        {!aiAnalysis && !aiLoading && (
          <div style={{ marginTop: 10, padding: "10px 14px", background: T.bg, border: `1px dashed ${T.border}`, borderRadius: 8, textAlign: "center" }}>
            <div style={{ fontSize: 12, color: T.textDim, marginBottom: 4 }}>No analysis run yet for {symbol}</div>
            <div style={{ fontSize: 10, color: T.textDim }}>Senior analyst commentary — factors in all 22 indicators, news context, and market regime. Takes ~5 seconds.</div>
          </div>
        )}

        {aiError && (
          <div style={{ marginTop: 10, padding: "10px 14px", background: `${T.red}10`, border: `1px solid ${T.red}44`, borderRadius: 8, fontSize: 12, color: T.red }}>✗ {aiError}</div>
        )}
      </div>

      {/* ── 2. WATCH TRIGGERS ── */}
      {macdTriggerRetiredNotice && (
        <div style={{ padding: "10px 12px", background: `${T.yellow}15`, border: `1px solid ${T.yellow}55`, borderRadius: 10, fontSize: 11, color: T.textSub, marginBottom: 8 }}>
          MACD watch triggers are retired in scoring v2 (display-only). Your saved MACD triggers were removed — use RSI, CMF, crosses, or price levels instead.
        </div>
      )}
      {(triggers.length > 0 || triggersLoading) && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: T.yellow, letterSpacing: "0.08em", marginBottom: 12 }}>
            🎯 WATCH TRIGGERS {triggersLoading && <span style={{ color: T.textDim, fontWeight: 400 }}>— extracting...</span>}
          </div>
          {checkTriggers(triggers, result).map((t, i) => {
            const fired = t.fired;
            const actionColor = t.action === "BUY" ? T.green : t.action === "SELL" ? T.red : T.yellow;
            return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", marginBottom: 6, background: fired ? `${actionColor}15` : T.bg, border: `1px solid ${fired ? actionColor + "66" : T.border}`, borderRadius: 8 }}>
                <div style={{ fontSize: 16, flexShrink: 0 }}>{fired ? "✅" : "⏳"}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: fired ? actionColor : T.text, fontWeight: fired ? 700 : 400 }}>{t.label}</div>
                  {fired && <div style={{ fontSize: 10, color: actionColor, marginTop: 2, fontWeight: 700 }}>⚡ TRIGGERED — Consider {t.action} action</div>}
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: `${actionColor}22`, color: actionColor, flexShrink: 0 }}>{t.action}</div>
              </div>
            );
          })}
          <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>Auto-checks on every scan. ✅ = condition met now.</div>
        </div>
      )}

      {/* ── 3. SIGNAL FLIP ANALYSIS ── */}
      {(() => {
        const { flips } = flipAnalysis;
        if (!flips.length) return null;
        return (
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 12 }}>🔄 SIGNAL FLIP ANALYSIS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {flips.map((flip, i) => (
                <div key={i}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: flip.color, letterSpacing: "0.06em", marginBottom: 6 }}>
                    {flip.type === "SELL TRIGGER" ? "📉 WHAT WOULD FLIP THIS TO SELL" : "📈 WHAT WOULD FLIP THIS TO BUY"}
                  </div>
                  {(Array.isArray(flip.conditions) ? flip.conditions : []).map((c, j) => (
                    <div key={j} style={{ display: "flex", gap: 8, marginBottom: 5, alignItems: "flex-start" }}>
                      <div style={{ width: 5, height: 5, borderRadius: "50%", background: flip.color, marginTop: 5, flexShrink: 0 }} />
                      <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.55 }}>{c}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── 4. KEY PRICE LEVELS ── */}
      {(() => {
        const { levels } = flipAnalysis;
        if (!levels.length) return null;
        return (
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.cyan, letterSpacing: "0.08em", marginBottom: 12 }}>🎯 KEY PRICE LEVELS TO WATCH</div>
            {levels.map((l, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: i < levels.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <div>
                  <div style={{ fontSize: 12, color: T.textSub }}>{l.label}</div>
                  <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>{l.significance}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, marginLeft: 12 }}>
                  {typeof l.price === "number" && Number.isFinite(l.price) ? `$${l.price.toFixed(2)}` : "—"}
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      <div style={{ marginTop: 4, paddingTop: 10, borderTop: `1px dashed ${T.border}`, textAlign: "center", fontSize: 10, color: T.textDim, lineHeight: 1.7 }}>
        Still broken after updating?{" "}
        <button type="button" onClick={() => clearLocalAiCache()}
          style={{ background: "none", border: "none", color: T.cyan, cursor: "pointer", fontFamily: "inherit", fontSize: 10, textDecoration: "underline", padding: 0 }}>
          Clear AI cache for {symbol}
        </button>
        {" · "}
        <button type="button" onClick={async () => { await resetAppShellCache(); window.location.reload(); }}
          style={{ background: "none", border: "none", color: T.cyan, cursor: "pointer", fontFamily: "inherit", fontSize: 10, textDecoration: "underline", padding: 0 }}>
          Refresh app shell
        </button>
      </div>

    </div>
  );
}

// ─── POSITION ROW ─────────────────────────────────────────────────────────────
// ─── NEWS POPOVER ─────────────────────────────────────────────────────────────
function NewsPopover({ news, symbol }) {
  const [showPop, setShowPop] = useState(false);
  if (!news || !news.articles?.length) return null;
  return (
    <div style={{ position: "relative", display: "inline-block", marginTop: 4 }}>
      <div
        onClick={e => { e.stopPropagation(); setShowPop(p => !p); }}
        title={`${news.label}\n\n${news.articles.slice(0, 3).map(a => `• ${a.title} — ${a.source} (${a.time})`).join("\n")}`}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "1px 6px", borderRadius: 8, background: `${news.color}15`, border: `1px solid ${news.color}44` }}>
        <span style={{ fontSize: 11, filter: `drop-shadow(0 0 3px ${news.color})` }}>📰</span>
        <span style={{ fontSize: 10, fontWeight: 700, color: news.color }}>{Math.min(news.articles.length, 6)}</span>
      </div>
      {showPop && (
        <div
          onClick={e => e.stopPropagation()}
          style={{ position: "absolute", top: "100%", left: 0, zIndex: 999, width: 320, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.5)", padding: "10px 12px", marginTop: 4 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: news.color, letterSpacing: "0.04em" }}>{symbol} — {news.label}</div>
            <button onClick={e => { e.stopPropagation(); setShowPop(false); }} style={{ background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
          </div>
          {news.articles.slice(0, 5).map((a, i) => (
            <div key={i} style={{ padding: "7px 0", borderBottom: i < Math.min(news.articles.length, 5) - 1 ? `1px solid ${T.border}` : "none" }}>
              <a href={a.url} target="_blank" rel="noopener noreferrer"
                onClick={e => e.stopPropagation()}
                style={{ fontSize: 11, color: T.text, textDecoration: "none", lineHeight: 1.5, display: "block", marginBottom: 4 }}>
                {a.title}
              </a>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: T.purple, background: `${T.purple}18`, padding: "1px 6px", borderRadius: 4 }}>{a.source}</span>
                <span style={{ fontSize: 9, color: T.textDim }}>{a.time}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PhaseBadgeCell({ result, inPosition = false }) {
  const display = resolveDisplayVerdict(result, { inPosition, activeSignal: result?.activeSignal });
  const ui = derivePhaseUi(result);
  if (ui?.kind?.includes("CALLS") && !display.showCallsBadge) return null;
  if (ui?.kind?.includes("PUTS") && !display.showPutsBadge) return null;
  const callsGateReason = hardGatesBlockedReason(result?.callsSignal);
  const putsGateReason = hardGatesBlockedReason(result?.putsSignal);
  // If we already have a visible phase badge, never clutter it with a gate pill.
  if (ui) {
    const isCalls = ui.kind?.includes("CALLS");
    const isPuts = ui.kind?.includes("PUTS");
    const g = result?.gates;
    const soft = [];
    if (isCalls) {
      if (g?.callsContext?.gate2?.pass === false) soft.push({ id: "counter", text: "⚠ Counter-trend setup", color: "#f97316" });
      if (g?.callsContext?.gate3?.pass === false) soft.push({ id: "template", text: "⚠ Below trend template", color: "#f97316" });
      // Gate 4 warning only when OBV declining but CMF not extreme (extremes are hard blocks)
      const cmf = typeof result?.cmf === "number" ? result.cmf : null;
      const g4Detail = g?.callsContext?.gate4?.detail || "";
      if (g?.callsContext?.gate4?.pass === false && cmf != null && cmf >= -0.10 && g4Detail.toLowerCase().includes("obv")) {
        soft.push({ id: "inst", text: "⚠ Weak institutional support", color: "#fbbf24" });
      }
    }
    if (isPuts) {
      if (g?.putsContext?.gate2?.pass === false) soft.push({ id: "counter", text: "⚠ Counter-trend setup", color: "#f97316" });
      if (g?.putsContext?.gate3?.pass === false) soft.push({ id: "template", text: "⚠ Below trend template", color: "#f97316" });
      const cmf = typeof result?.cmf === "number" ? result.cmf : null;
      const g4Detail = g?.putsContext?.gate4?.detail || "";
      if (g?.putsContext?.gate4?.pass === false && cmf != null && cmf <= 0.10 && g4Detail.toLowerCase().includes("obv")) {
        soft.push({ id: "inst", text: "⚠ Weak institutional support", color: "#fbbf24" });
      }
    }
    const topWarn = (() => {
      if (soft.some(x => x.id === "counter")) return soft.find(x => x.id === "counter");
      if (soft.some(x => x.id === "template")) return soft.find(x => x.id === "template");
      if (soft.some(x => x.id === "inst")) return soft.find(x => x.id === "inst");
      return null;
    })();
    const phaseTip = (() => {
      if (ui.kind === "CALLS_WATCH") return `WHAT: Early signs of institutional accumulation — volume building quietly on up days, CMF turning positive, OBV making higher lows while price is flat.\nWHY: Smart money positions 7-14 days before a move. This is the earliest warning signal.\nDO: Add to watchlist, check daily. Wait for CALLS SETUP to develop.`;
      if (ui.kind === "CALLS_SETUP") return `WHAT: Pre-breakout structure confirmed. Stock is coiling — range tightening, volume drying up, institutions loaded.\nWHY: The coiled spring is almost fully wound. Breakout is typically 1-5 days away.\nDO: Set a price alert at the breakout level. Prepare position size in advance.`;
      if (ui.kind === "CALLS_READY") return `WHAT: All conditions aligned. Gates pass, setup confirmed, phase 3 active. The stock has met every institutional-grade filter.\nWHY: This is the highest probability entry point — when technicals, volume, trend, and market tide all agree.\nDO: Enter on current or next candle. Check sizing tab for exact position size.`;
      if (ui.kind === "PUTS_WATCH") return `WHAT: Early distribution signs — volume increasing on down days, CMF turning negative, OBV making lower highs while price is still flat or rising.\nWHY: Institutions are quietly selling before retail traders notice.\nDO: Watch for deterioration. Wait for PUTS SETUP to develop before acting.`;
      if (ui.kind === "PUTS_SETUP") return `WHAT: Bearish structure forming. Stock breaking below support levels, volume picking up on down days, CMF negative.\nWHY: The breakdown is building. Entry conditions for puts typically 1-5 days out.\nDO: Prepare puts position. Wait for PUTS READY confirmation before entering.`;
      if (ui.kind === "PUTS_READY") return `WHAT: Full bearish signal confirmed. All puts gates pass — Stage 4 structure, distribution confirmed, breakdown active.\nWHY: Stock is in institutional distribution with confirmed downtrend structure.\nDO: Enter puts position. Use D1 as first target, D2 as full cover level.`;
      return ui.label;
    })();

    const setupTip = (() => {
      const s = ui.setupSuffix || "";
      if (s === "🔄 VCP FORMING") return `WHAT: Volatility Contraction Pattern — 2-3 progressively tighter consolidations with declining volume.\nWHY: Tightening means sellers are exhausted and supply is being absorbed by strong hands.\nDO: Wait for a break above the pivot on volume >2x average. That's the entry.`;
      if (s === "💥 VCP BREAKOUT") return `WHAT: VCP breakout confirmed — close above pivot on strong volume.\nWHY: Supply absorbed; demand in control. Optimal entry with best risk/reward.\nDO: Buy now or next open. Stop below the tightest contraction low.`;
      if (s === "🚩 BULL FLAG") return `WHAT: Bull flag setup — strong pole then tight flag pullback on declining volume.\nWHY: Proper volume contraction flags often resolve upward with follow-through.\nDO: Wait for a close above the flag channel on volume >1.5x average.`;
      if (s === "🔄 SUPPORT BOUNCE") return `WHAT: Pullback to key support (EMA21 / pivot / base) with institutions holding (CMF supportive).\nWHY: Support pullbacks in uptrends are lower-risk add/entry points.\nDO: Enter on first green candle at support with volume confirmation. Stop below support.`;
      if (s === "💥 RANGE BREAKOUT") return `WHAT: Tight range compression resolved upward with strong volume.\nWHY: Range breakouts with volume confirmation tend to continue.\nDO: Enter on breakout or first pullback to range high. Target = range height projection.`;
      if (s === "📈 EARNINGS DRIFT") return `WHAT: Post-earnings drift — stock beat, gapped, and continues higher as institutions accumulate.\nWHY: PEAD is well-documented; institutions build positions over 5-15 days post-report.\nDO: Enter on pullback to earnings-day open. Exit before next earnings.`;
      if (s === "⬆ GAP AND GO") return `WHAT: Trend-aligned gap up (3%+) on strong volume with institutional support.\nWHY: Quality gaps often continue for 1-3 days when supported by flow.\nDO: Enter near open. Stop below gap open. Target = gap size projected up.`;
      if (s === "↩ GAP FILL") return `WHAT: Weak gap against trend on light volume with no institutional support.\nWHY: Weak gaps against trend often fill back to yesterday's close.\nDO: Puts targeting gap fill level. Stop above today's high.`;
      if (s === "🐱 DEAD CAT BOUNCE") return `WHAT: Oversold snap-back after a severe decline — seller exhaustion + divergence.\nWHY: Short covering + buyers stepping in can create sharp 10-30% bounces.\nDO: Enter on first green candle w/ volume. Target T1/T2 retracements. Exit before earnings.`;
      if (s === "🔄 VCP FORMING" || s === "🔄 VCP FORMING") return null;
      return null;
    })();
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, maxWidth: 280, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
        <HoverTip content={phaseTip}>
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "0.03em",
              padding: "4px 10px",
              borderRadius: 999,
              border: `1px solid ${ui.border}`,
              color: ui.color,
              background: `${ui.color}14`,
              animation: ui.flash ? "pulse 1s infinite" : ui.pulse ? "phasePulse 2.2s ease-in-out infinite" : "none",
              whiteSpace: "normal",
              textAlign: "center",
              lineHeight: 1.25,
              wordBreak: "break-word",
              cursor: "help",
            }}
          >{ui.label}</span>
        </HoverTip>

        {ui.setupSuffix && setupTip && (
          <HoverTip content={setupTip}>
            <span
              style={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: "0.02em",
                padding: "3px 9px",
                borderRadius: 999,
                border: `1px solid ${T.border}`,
                color: T.textSub,
                background: T.surfaceHi,
                cursor: "help",
                whiteSpace: "nowrap",
              }}
            >
              {ui.setupSuffix}
            </span>
          </HoverTip>
        )}
        </div>

        {topWarn && (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "center" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: topWarn.color, lineHeight: 1.1 }}>
              {topWarn.text}
            </div>
          </div>
        )}
      </div>
    );
  }

  // In CHOP / BLOCKED states, Phase should be empty (no setup progression).
  // Also avoid showing "GATED" pills here — advisory/block reasons belong in Action.
  return null;
}

function PositionRow({ item, onUpdate, onRemove, onScan, scanning, onLogTrade, rowIndex, onDragStart, onDragOver, onDragEnd, isDragging, anthropicKey, openaiKey, geminiKey, aiProvider, proMode, isDark, hintsOn, liveQuote, portfolioSize, onSavePortfolioSize, onNewsClick, onOpenDrawer, onTogglePosition, onManualSignalExit, colVis = {}, rowHighlight = false }) {
  const cv = (key) => proMode && (colVis[key] !== false); // default true if not explicitly toggled off
  const { symbol, position, result, status, error, news } = item;
  const [pos, setPos] = useState(position || { buyPrice: "", shares: "", dollarAmt: "", inputMode: "shares", stopPct: 8 });
  const [rowTriggers, setRowTriggers] = useState([]);

  // Check cached AI verdict + triggers (caution handled by useVerdictForTicker)
  const [aiVerdict, setAiVerdict] = useState(null);
  useEffect(() => {
    (async () => {
      const v = await load(`aiVerdict_${symbol}`);
      if (v) setAiVerdict(v);
      const t = await load(`aiTriggers_${symbol}`);
      if (t) setRowTriggers(t);
    })();
  }, [symbol]); // null | "summary" | "full"

  // Compute checked triggers once per render
  const checkedTriggers = result ? checkTriggers(rowTriggers, result) : [];

  // Returns a glow style for a cell if any matching trigger type has fired
  const glowFor = (types) => {
    // Cell-level glow removed — trigger feedback lives in the expanded trigger panel only
    // Avoids confusing green/red cell backgrounds for users without triggers configured
    return {};
  };

  const bp = parseFloat(pos.buyPrice) || null;
  const shares = pos.inputMode === "shares" ? (parseFloat(pos.shares) || null) : (bp && parseFloat(pos.dollarAmt) ? parseFloat(pos.dollarAmt) / bp : null);
  const dollarIn = shares && bp ? shares * bp : (parseFloat(pos.dollarAmt) || null);
  const price = liveQuote?.price || result?.price; // prefer live quote over scan price
  const currentVal = shares && price ? shares * price : null;
  const pnlPct = bp && price ? ((price - bp) / bp) * 100 : null;
  const pnlDollar = dollarIn && currentVal ? currentVal - dollarIn : null;
  const sp = parseFloat(pos.stopPct) || 8;
  const atrStop = result?.atr && bp ? Math.min(bp - 2 * result.atr, bp * (1 - sp / 100)) : bp ? bp * (1 - sp / 100) : null;
  const retT1 = bp && result?.t1 ? ((result.t1 - bp) / bp * 100) : null;
  const retStop = bp && atrStop ? ((atrStop - bp) / bp * 100) : null;
  // Live stop — uses scan price when no buy price entered, so column always shows data
  const scanPrice = result?.price;
  const v2Stop = result?.stopPrice ?? result?.tradeStop ?? null;
  const liveStop = !bp && scanPrice && v2Stop != null
    ? v2Stop
    : !bp && result?.atr && result?.price
      ? result.price - 2 * result.atr
      : null;
  const displayStop = atrStop ?? liveStop;
  const stopIsLive = !atrStop && !!liveStop;
  const rr = retT1 && retStop ? Math.abs(retT1 / retStop) : null;
  // Live R:R — v2 rr1 from runAnalysis, else legacy T1/stop math
  const scanStop = v2Stop ?? (result?.atr && scanPrice ? scanPrice - 2 * result.atr : null);
  const scanT1 = result?.t1;
  const liveRR = result?.rr1 ?? ((!bp && scanPrice && scanStop && scanT1 && scanStop < scanPrice)
    ? Math.abs((scanT1 - scanPrice) / (scanPrice - scanStop))
    : null);
  const displayRR = rr ?? liveRR; // use manual entry R:R if available, else live R:R
  const rrIsLive = !rr && !!liveRR;

  const handlePos = (f, v) => { const u = { ...pos, [f]: v }; setPos(u); onUpdate(symbol, u); };

  const statusDot = { done: T.green, error: T.red, loading: T.cyan, queued: T.yellow, idle: T.textDim }[status] || T.textDim;
  const hasUrgentExit = bp && (result?.exits?.level === "CRITICAL" || result?.exits?.level === "HIGH");

  // Returns a tooltip string only when hints are on
  const hint = (text) => hintsOn ? text : undefined;

  // Synthesized three-layer verdict (AI caution via shared hook)
  const verdict = useVerdictForTicker(result, symbol, {
    inPosition: item.inPosition,
    position: bp,
    currentPrice: liveQuote?.price || result?.price,
  });

  const cell = { padding: "8px 8px", verticalAlign: "middle" };
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  const isPrimeRow = result?.isPrime === true;

  return (
    <>
      <tr
        id={`wl-row-${symbol}`}
        onClick={() => { if (result && onOpenDrawer) onOpenDrawer(item); }}
        data-prime={isPrimeRow ? "true" : undefined}
        style={{ cursor: result ? "pointer" : "default", background: rowHighlight ? `${T.cyan}22` : isDragging ? `${T.cyan}18` : item.inPosition ? `${T.cyan}07` : hasUrgentExit ? `${T.red}0a` : rowIndex % 2 === 0 ? "transparent" : isDark ? "#080808" : "rgba(0,0,0,0.025)", opacity: isDragging ? 0.5 : 1, transition: "background 0.1s, opacity 0.15s", borderLeft: rowHighlight ? `3px solid ${T.cyan}` : item.inPosition ? `3px solid ${T.cyan}` : isPrimeRow ? "2px solid #fbbf24" : bp ? `3px solid ${T.green}` : "3px solid transparent", boxShadow: rowHighlight ? `inset 0 0 0 1px ${T.cyan}55` : item.inPosition ? `inset 0 0 0 1px ${T.cyan}18` : isPrimeRow ? "inset 0 0 0 1px #fbbf2418" : "none" }}
        onMouseEnter={e => { if (!isDragging) e.currentTarget.style.background = isDark ? "#0f0f0f" : "rgba(139,92,246,0.06)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = isDragging ? `${T.cyan}18` : hasUrgentExit ? `${T.red}0a` : rowIndex % 2 === 0 ? "transparent" : isDark ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.025)"; }}
        onDragOver={e => { e.preventDefault(); onDragOver && onDragOver(rowIndex); }}
        onDrop={e => { e.preventDefault(); onDragEnd && onDragEnd(); }}
      >
        {/* DRAG HANDLE */}
        <td style={{ padding: "0 8px", verticalAlign: "middle", cursor: "grab", width: 20, userSelect: "none" }}
          draggable="true"
          onDragStart={e => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", String(rowIndex));
            onDragStart && onDragStart();
          }}
          onDragEnd={() => onDragEnd && onDragEnd()}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 3, padding: "4px 0" }}>
            <div style={{ width: 16, height: 2, background: T.border, borderRadius: 1 }} />
            <div style={{ width: 16, height: 2, background: T.border, borderRadius: 1 }} />
            <div style={{ width: 16, height: 2, background: T.border, borderRadius: 1 }} />
          </div>
        </td>

        {/* TICKER */}
        <td style={{ ...cell, paddingLeft: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: "50%", background: statusDot, boxShadow: status === "loading" ? `0 0 8px ${T.cyan}` : "none", flexShrink: 0 }} />
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ ...mono, fontSize: F.lg, fontWeight: 700, color: T.text, letterSpacing: "0.05em" }}>
                  <TickerLabel symbol={symbol} />
                </div>
                {item.inPosition && (
                  <span
                    onClick={e => { e.stopPropagation(); onTogglePosition && onTogglePosition(symbol); }}
                    title="In Position — click to remove"
                    style={{ fontSize: 9, padding: "2px 6px", borderRadius: 5, background: `${T.cyan}22`, color: T.cyan, fontWeight: 800, border: `1px solid ${T.cyan}44`, cursor: "pointer", letterSpacing: "0.04em" }}>
                    📍 IN
                  </span>
                )}
              </div>
              {item.inPosition && result && (() => {
                const flags = [];
                if (result.ema21 && result.price < result.ema21) flags.push("below EMA21");
                if (result.cmf != null && result.cmf < -0.05) flags.push("CMF dist.");
                if (result.sellCount >= 3) flags.push(`${result.sellCount} sell flags`);
                const health = flags.length === 0 ? "HOLD" : flags.length === 1 ? "WATCH" : "CAUTION";
                const hc = health === "HOLD" ? T.green : health === "WATCH" ? T.yellow : T.red;
                return <div style={{ fontSize: 9, fontWeight: 700, color: hc, marginTop: 2 }}>{health}{flags.length > 0 ? ` · ${flags[0]}` : " · thesis intact"}</div>;
              })()}
              {/* Earnings — keep, genuinely useful alert */}
              {result?.earnings?.soon && (
                <div style={{ fontSize: 10, fontWeight: 700, marginTop: 2, color: T.orange }}>
                  ⚠ Earnings within 14 days — reduce size
                </div>
              )}
            </div>
          </div>
          {news && news.articles?.length > 0 && (
            <NewsPopover news={news} symbol={symbol} />
          )}
          {hasUrgentExit && <div style={{ fontSize: F.xs, color: T.red, marginTop: 4, animation: "pulse 1.5s infinite" }}>⚠ {result.exits.level} EXIT — review your position</div>}
          {status === "error" && <div style={{ fontSize: F.xs, color: T.red, marginTop: 2 }}>{error?.slice(0, 28)}</div>}
        </td>

        {/* PRICE — live quote overrides scan price when available */}
        <td style={{ ...cell, textAlign: "right", ...glowFor(["price_above","price_below"]) }}>
          {(liveQuote?.price || price) ? (() => {
            const displayPrice = liveQuote?.price || price;
            const displayPct = liveQuote?.changePct ?? result?.pctChange;
            const isLive = !!liveQuote?.price;
            const staleSecs = isLive ? Math.floor((Date.now() - liveQuote.ts) / 1000) : null;
            const openPct = (isLive && liveQuote?.open && liveQuote.open > 0)
              ? ((displayPrice - liveQuote.open) / liveQuote.open) * 100
              : null;

            // Extended hours data — show pre OR post market
            // Also show post-market during CLOSED so users see overnight price
            const isPreMarket   = liveQuote?.isPreMarket;
            const isPostMarket  = liveQuote?.isPostMarket;
            const isClosedWithAH = !isPreMarket && !isPostMarket && liveQuote?.postMarketPrice != null;
            const extPrice      = isPreMarket
              ? liveQuote.preMarketPrice
              : (isPostMarket || isClosedWithAH) ? liveQuote.postMarketPrice
              : null;
            const extChangePct  = isPreMarket
              ? liveQuote.preMarketChangePct
              : (isPostMarket || isClosedWithAH) ? liveQuote.postMarketChangePct
              : null;
            const extLabel      = isPreMarket ? "PRE" : (isPostMarket || isClosedWithAH) ? "AH" : null;
            const extColor      = isPreMarket ? T.yellow : T.purple;

            return (
              <>
                {/* Regular session price */}
                <div style={{ ...mono, fontSize: F.lg, fontWeight: 700, color: T.text }}>{fc(displayPrice)}</div>
                <div
                  title={isLive ? `vs prior close · Updated ${staleSecs}s ago` : "vs prior close · Last scan price"}
                  style={{ fontSize: F.sm, color: displayPct >= 0 ? T.green : T.red, fontWeight: 600, cursor: "help", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
                  {fp(displayPct)}
                  <span style={{ fontSize: 8, color: T.textDim, fontWeight: 400 }}>cls</span>
                  {isLive && !extLabel && <span style={{ fontSize: 9, color: T.textDim }}>●</span>}
                </div>

                {/* Extended hours row — pre or post market price */}
                {extLabel && extPrice != null && (
                  <div
                    title={`${extLabel === "PRE" ? "Pre-market" : "After-hours"} price · ${extChangePct != null ? (extChangePct >= 0 ? "+" : "") + extChangePct.toFixed(2) + "% vs prior close" : ""}`}
                    style={{ marginTop: 3, padding: "1px 5px", borderRadius: 4, background: `${extColor}15`, border: `1px solid ${extColor}33`, display: "inline-flex", alignItems: "center", gap: 4, float: "right", clear: "both" }}>
                    <span style={{ fontSize: 8, fontWeight: 800, color: extColor, letterSpacing: "0.04em" }}>{extLabel}</span>
                    <span style={{ fontSize: 10, fontWeight: 700, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>{fc(extPrice)}</span>
                    {extChangePct != null && (
                      <span style={{ fontSize: 9, fontWeight: 600, color: extChangePct >= 0 ? T.green : T.red }}>{extChangePct >= 0 ? "+" : ""}{extChangePct.toFixed(2)}%</span>
                    )}
                  </div>
                )}

                {/* vs today's open */}
                {openPct != null && !extLabel && (
                  <div
                    title={`vs today's open ($${liveQuote.open?.toFixed(2)})`}
                    style={{ fontSize: 10, color: openPct >= 0 ? T.green : T.red, fontWeight: 500, cursor: "help", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3, opacity: 0.8 }}>
                    {fp(openPct)}
                    <span style={{ fontSize: 8, color: T.textDim, fontWeight: 400 }}>open</span>
                  </div>
                )}
              </>
            );
          })() : <span style={{ color: T.textDim }}>—</span>}
        </td>

        {/* Signal — v2 matches Market Scanner verdict badge + explanation */}
        <td style={{ ...cell, textAlign: "left", ...glowFor(["golden_cross","death_cross","macd_bullish","macd_bearish"]), minWidth: 180 }}>
          {result
            ? (() => {
              normalizeScanResult(result);
              const isV2 = isScoringV2Result(result);
              const display = resolveDisplayVerdict(result, { inPosition: !!item.inPosition, activeSignal: result?.activeSignal });
              const badge = isV2 ? getScannerDisplayBadge(result) : null;
              const chip = displayVerdictChipStyle(display.verdictStyle);
              const strength = isV2 ? null : display.signalStrength;
              const cfBridge = !isV2 ? confluenceSignalBridge(result, watchlistConfluenceCell) : null;
              const explanation = isV2
                ? getSetupExplanationLine(result, badge)
                : display.subtitle
                  ? (cfBridge ? `${display.subtitle} · ${cfBridge}` : display.subtitle)
                  : (cfBridge || display.subtitle);
              const verdictTip = hint(
                isV2
                  ? (badge?.tooltip ? `${badge.label} — ${badge.tooltip}` : badge?.label || display.verdict)
                  : display.subtitle
                    ? `${display.verdict}${display.subtitle ? ` — ${display.subtitle}` : ""}`
                    : signalVerdictTooltip(display.verdict)
              );
              const subtitle = explanation;
              const rrWarn = displayRR != null && displayRR < 1 && !bp;
              return (
                <HoverTip content={verdictTip}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4, cursor: verdictTip ? "help" : "default" }}>
                  {isV2 && badge ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                      <span
                        style={{
                          display: "inline-block",
                          fontSize: 10,
                          fontWeight: 800,
                          color: badge.text,
                          background: badge.bg,
                          padding: "3px 6px",
                          borderRadius: 5,
                          lineHeight: 1.25,
                        }}
                      >
                        {badge.label}
                      </span>
                      {isPrimeRow && (
                        <span
                          title={(result.primeReasons || []).join(" · ") || PRIME_STAR_TOOLTIP}
                          style={{
                            display: "inline-block",
                            fontSize: 9,
                            fontWeight: 800,
                            color: "#78350f",
                            background: "linear-gradient(180deg, #fef3c7 0%, #fde68a 100%)",
                            border: "1px solid #f59e0b",
                            padding: "2px 5px",
                            borderRadius: 5,
                            lineHeight: 1.25,
                          }}
                        >
                          ⭐ PRIME
                        </span>
                      )}
                      <EarningsRowPill result={result} symbol={item.symbol} T={T} />
                    </div>
                  ) : (
                  <div style={{ fontSize: 11, fontWeight: 800, color: chip.color, lineHeight: 1.3, display: "flex", flexWrap: "wrap", alignItems: "center", gap: 4 }}>
                    <span>{display.verdict}</span>
                    {isPrimeRow && (
                      <HoverTip content={PRIME_STAR_TOOLTIP}>
                        <span style={{ cursor: "help", fontSize: 11, lineHeight: 1 }} aria-label="Prime setup">⭐</span>
                      </HoverTip>
                    )}
                    {strength != null && <span style={{ fontSize: 10, color: T.textDim, fontWeight: 700 }}>{strength}%</span>}
                  </div>
                  )}
                  <div style={{ fontSize: 9, color: T.textDim, lineHeight: 1.35 }}>{subtitle}</div>
                  {rrWarn && (
                    <div style={{ fontSize: 9, fontWeight: 800, color: T.orange, lineHeight: 1.3 }}>
                      R:R {displayRR.toFixed(1)}:1 — below 1:1 at scan price
                    </div>
                  )}
                  {result?.consolidation?.active && (() => {
                    const badge = consolidationSignalBadge(result.consolidation);
                    if (!badge) return null;
                    return <span style={{ fontSize: 9, fontWeight: 800, color: T.cyan }}>{badge}</span>;
                  })()}
                  {result?.relativeStrength?.rs_line_new_high && (
                    <span style={{ fontSize: 9, fontWeight: 900, color: "#fbbf24" }}>⭐ RS HIGH</span>
                  )}
                  {display.showExitSignal && display.exitLabel && (
                    <div style={{ marginTop: 2 }} onClick={e => e.stopPropagation()}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: T.red }}>{display.exitLabel}</div>
                    </div>
                  )}
                  {display.holdingText && (
                    <div style={{ marginTop: 2 }} onClick={e => e.stopPropagation()}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: T.cyan }}>{display.holdingText}</div>
                    </div>
                  )}
                  <SignalAgeBadge signalAge={result?.signalAge} />
                </div>
                </HoverTip>
              );
            })()
            : "—"}
        </td>

        {/* ACTION — display coordinator */}
        <td style={{ ...cell, textAlign: "center", minWidth: 140 }}>
          {(() => {
            if (!result) return <span style={{ color: T.textDim, fontSize: 10 }}>—</span>;
            const ui = calcSignalActionCombinedUi({ result, inPosition: !!item.inPosition });
            if (!ui) return <span style={{ color: T.textDim, fontSize: 10 }}>—</span>;
            if (ui.isDash && !isPrimeRow) return <span style={{ color: T.textDim, fontSize: 10 }}>—</span>;
            const rotSuffix = getSectorRotationActionSuffix(result);
            const headwind = getSectorRotationHeadwindWarning(result);
            const primeAction = getPrimeWatchlistActionText(result);
            const actionText = primeAction ?? `${ui.text}${rotSuffix}`;
            const actionColor = primeAction ? "#92400e" : ui.color;
            const actionBg = primeAction ? "#fef3c733" : ui.bg;
            const actionBorder = primeAction ? "#f59e0b" : ui.border;
            const pill = ui.showPill && ui.setupLabel && ui.setupPillStyle ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "2px 8px",
                  borderRadius: 999,
                  fontSize: 8,
                  fontWeight: 800,
                  letterSpacing: "0.03em",
                  border: `1px solid ${ui.setupPillStyle.border}`,
                  color: ui.setupPillStyle.color,
                  background: ui.setupPillStyle.bg,
                  whiteSpace: "nowrap",
                  maxWidth: 140,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {ui.setupLabel}
              </span>
            ) : null;
            return (
              <HoverTip content={hint(
                (primeAction
                  ? `${primeAction}\n\n${(result.primeReasons || []).join(" · ") || PRIME_STAR_TOOLTIP}`
                  : (ui.tip || ui.text))
                + (rotSuffix && !primeAction ? `\n\nSector rotation context active.` : "")
                + (headwind ? `\n\n${headwind}` : "")
              )}>
                <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "help" }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 9,
                      fontWeight: 800,
                      letterSpacing: "0.03em",
                      border: `1px solid ${actionBorder || T.border}`,
                      color: actionColor || T.text,
                      background: actionBg || `${T.border}22`,
                      whiteSpace: "normal",
                      textAlign: "center",
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                      maxWidth: 140,
                      animation: ui.flash ? "pulse 0.9s infinite" : ui.pulse ? "phasePulse 2.2s ease-in-out infinite" : "none",
                    }}
                  >
                    {actionText}
                  </span>
                  {headwind && (
                    <span style={{ fontSize: 8, fontWeight: 800, color: T.orange, letterSpacing: "0.03em" }}>
                      {headwind}
                    </span>
                  )}
                  {pill}
                </span>
              </HoverTip>
            );
          })()}
        </td>

        {/* SCORE — dims when position is open, shows entry score context */}
        {(() => {
          const entryScore = bp ? (aiVerdict?.score ?? null) : null;
          const hasPos = !!bp;
          const scoreTip = hasPos
            ? `Score is de-emphasized while you are in a trade. You entered at ${entryScore != null ? (entryScore > 0 ? "+" : "") + entryScore : "an earlier score"}. The score fluctuates daily as individual signals shift — a MACD crossover bonus fires only on the crossover bar, multi-TF bonuses appear and disappear. What matters now is Position Health (EMA21, CMF, MACD, Stop). Focus on exit signals, not entry score.`
            : hint(result ? SCORE_TIP : undefined);
          return (
            <td style={{ ...cell, textAlign: "center", minWidth: 80 }}>
              <HoverTip content={scoreTip}>
                <div style={{ cursor: scoreTip ? "help" : "default" }}>
                  {result ? (
                    <>
                      {(() => {
                        const vd = resolveDisplayVerdict(result, { inPosition: !!item.inPosition, activeSignal: result?.activeSignal });
                        const scoreMuted = hasPos || vd.scoreGrey;
                        return (
                          <>
                            <div style={{ ...mono, fontSize: F.lg, fontWeight: 700,
                              color: scoreMuted ? T.textDim : result.buyScore >= 2 ? T.green : result.buyScore <= -2 ? T.red : T.yellow,
                              opacity: scoreMuted ? 0.55 : 1 }}>
                              {result.buyScore > 0 ? "+" : ""}{result.buyScore}
                              {vd.scoreSuffix && !hasPos ? (
                                <span style={{ display: "block", fontSize: 8, fontWeight: 700, color: T.textDim }}>{vd.scoreSuffix}</span>
                              ) : null}
                            </div>
                            {!hasPos && <ScoreBar score={result.buyScore} />}
                          </>
                        );
                      })()}
                    </>
                  ) : "—"}
                </div>
              </HoverTip>
            </td>
          );
        })()}

        {/* RSI — Pro only */}
        {cv("rsi") && (() => {
          const rsiTip = hint(rsiCellTooltipExtended(result?.rsi, result));
          const sub = rsiWatchlistSubline(result);
          return (
            <td style={{ ...cell, textAlign: "center", ...glowFor(["rsi_below","rsi_above"]) }}>
              <HoverTip content={rsiTip}>
                <div style={{ cursor: rsiTip ? "help" : "default" }}>
                  {result?.rsi != null ? (
                    <>
                      <div style={{ ...mono, fontSize: F.md, fontWeight: 700, color: rsiDisplayColor(result.rsi, T), whiteSpace: "nowrap" }}>
                        {formatRsiWithDivergence(result.rsi, result.rsiDiv, 0)}
                      </div>
                      {sub && <div style={{ fontSize: 8, fontWeight: 700, color: T.textDim, marginTop: 2 }}>{sub}</div>}
                    </>
                  ) : "—"}
                </div>
              </HoverTip>
            </td>
          );
        })()}

        {/* Confluence — Pro only */}
        {cv("confluence") && (() => {
          const cf = watchlistConfluenceCell(result);
          const cfColor =
            cf.tone === "bull" || cf.tone === "bounce" ? T.green
            : cf.tone === "breakout" ? T.cyan
            : cf.tone === "warn" ? T.orange
            : T.textDim;
          const cfTip = hint(confluenceCellTooltip(cf));
          return (
            <td style={{ ...cell, textAlign: "center", minWidth: 72 }}>
              <HoverTip content={cfTip}>
                <div style={{ ...mono, fontSize: F.sm, fontWeight: 800, color: cfColor, whiteSpace: "nowrap", cursor: cfTip ? "help" : "default" }}>
                  {cf.text}
                </div>
              </HoverTip>
            </td>
          );
        })()}

        {/* BB — Pro only */}
        {cv("bb") && <td style={{ ...cell, textAlign: "center", ...glowFor(["bb_squeeze_fires"]) }}
          title={hint(result?.bb ? result.bb.isSqueeze ? BB_TIGHT_BANDS_TIP : price > result.bb.upper ? `Price is above the upper Bollinger Band ▲ — statistically extended. This is a high-risk entry zone. The entry gate blocks entries here. Wait for price to pull back inside the bands before buying.` : price < result.bb.lower ? `Price is below the lower Bollinger Band ▼ — oversold territory. Potential reversal zone. Watch for RSI confirmation before entering.` : `Price is in the middle of the Bollinger Bands. No extreme reading — neutral position within the range.` : undefined)}>
          {result?.bb ? (
            result.bb.isSqueeze
              ? <Badge text={BB_TIGHT_BANDS_LABEL} color={T.yellow} />
              : <div style={{ fontSize: F.lg, fontWeight: 700, color: price > result.bb.upper ? T.red : price < result.bb.lower ? T.green : T.textDim }}>
                  {price > result.bb.upper ? "▲" : price < result.bb.lower ? "▼" : "–"}
                </div>
          ) : "—"}
        </td>}

        {/* CMF — Pro only */}
        {cv("cmf") && (() => {
          const cmfTip = hint(result?.cmf != null ? `${CMF_TIP}${result.rvol ? `\n\nRVOL ${result.rvol.ratio.toFixed(1)}× avg today.` : ""}` : undefined);
          return (
            <td style={{ ...cell, textAlign: "center", ...glowFor(["cmf_positive","cmf_negative","rvol_above","above_vwap","below_vwap"]) }}>
              <HoverTip content={cmfTip}>
                <div style={{ cursor: cmfTip ? "help" : "default" }}>
                  {result?.cmf != null ? (
                    <>
                      <div style={{ ...mono, fontSize: F.sm, fontWeight: 600, color: result.cmf > 0.05 ? T.green : result.cmf < -0.05 ? T.red : T.textSub }}>
                        {ff(result.cmf, 3)}
                      </div>
                      {result.rvol && (
                        <div style={{ fontSize: 9, fontWeight: 700, color: result.rvol.surge ? T.green : result.rvol.high ? T.cyan : result.rvol.low ? T.red : T.textDim, marginTop: 1 }}>
                          {result.rvol.ratio.toFixed(1)}x {result.rvol.surge ? "↑↑" : result.rvol.high ? "↑" : result.rvol.low ? "↓" : ""}
                        </div>
                      )}
                      {result.volDiv?.bearish && <div style={{ fontSize: F.xs, color: T.orange }}>↘ vol div</div>}
                      {result.volDiv?.bullish && <div style={{ fontSize: F.xs, color: T.green }}>↗ accum</div>}
                    </>
                  ) : "—"}
                </div>
              </HoverTip>
            </td>
          );
        })()}

        {/* 52W RANGE — Pro only */}
        {cv("range52w") && <td style={{ ...cell, textAlign: "center" }}
          title={hint(result?.range52w ? `52-Week Range score: ${result.range52w.score}/100. ${result.range52w.score <= 20 ? "Near annual low (0–20) — maximum upside potential. Highest risk/reward for longs. Institutions accumulate at lows." : result.range52w.score >= 80 ? "Near annual high (80–100) — limited upside without a confirmed breakout. Only enter if RVOL is surging on the breakout day." : result.range52w.score <= 40 ? "Lower half of range (20–40) — good entry zone with meaningful upside to the annual high." : "Mid-range — no extreme reading. Upside exists but watch for resistance near the top of range."}` : undefined)}>
          {result?.range52w ? <RangeBar score={result.range52w.score} /> : "—"}
        </td>}

        {/* RS vs SPY — Pro only (10-day vs SPY) */}
        {cv("rs") && (() => {
          const rsTip = hint(result?.rsVsSpy10 != null ? RS_SPY_TIP : "RS vs SPY: scan to calculate relative strength vs the market.");
          return (
            <td style={{ ...cell, textAlign: "center", ...glowFor(["golden_cross"]) }}>
              <HoverTip content={rsTip}>
                <div style={{ cursor: rsTip ? "help" : "default" }}>
          {result?.rsVsSpy10 != null ? (() => {
            const v = result.rsVsSpy10;
            const hist = readActionHistory(symbol);
            const a = hist?.[0]?.rsVsSpy10;
            const b = hist?.[1]?.rsVsSpy10;
            const c = hist?.[2]?.rsVsSpy10;
            const trend = (typeof a === "number" && typeof b === "number" && typeof c === "number")
              ? (a > b && b > c) ? "UP"
              : (a < b && b < c) ? "DOWN"
              : "FLAT"
              : "FLAT";
            const trendIcon = trend === "UP" ? "↑" : trend === "DOWN" ? "↓" : "—";
            const trendCol = trend === "UP" ? T.green : trend === "DOWN" ? T.red : T.textDim;
            const col = v >= 0 ? T.green : T.red;
            return (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: F.sm, fontWeight: 700, color: col }}>
                  {v >= 0 ? "↑" : "↓"}{Math.abs(v).toFixed(1)}%{" "}<span style={{ color: trendCol, fontWeight: 800 }}>{trendIcon}</span>
                </span>
                {result.relativeStrength?.rs_rating != null && (
                  <span style={{
                    fontSize: 8, fontWeight: 800, padding: "1px 5px", borderRadius: 999,
                    color: result.relativeStrength.rs_rating > 70 ? T.green : result.relativeStrength.rs_rating < 30 ? T.red : T.textSub,
                    background: result.relativeStrength.rs_rating > 70 ? `${T.green}18` : result.relativeStrength.rs_rating < 30 ? `${T.red}18` : `${T.border}22`,
                    border: `1px solid ${result.relativeStrength.rs_rating > 70 ? T.green + "44" : result.relativeStrength.rs_rating < 30 ? T.red + "44" : T.border}`,
                  }}>
                    RS {result.relativeStrength.rs_rating}
                  </span>
                )}
                {result.relativeStrength?.rs_line_new_high && (
                  <span style={{ fontSize: 8, fontWeight: 900, color: "#fbbf24" }}>⭐</span>
                )}
              </div>
            );
          })() : "—"}
                </div>
              </HoverTip>
            </td>
          );
        })()}

        {/* Exit Signal column removed (redundant with Action). */}
        {cv("stop") && <td style={{ ...cell, textAlign: "right" }}
          title={hint(displayStop
            ? `ATR Stop: $${displayStop.toFixed(2)}${retStop ? " (" + (retStop >= 0 ? "+" : "") + retStop.toFixed(1) + "% from entry)" : ""}${stopIsLive ? " — based on current scan price (enter buy price for your actual stop)" : ""}. Price minus 2× ATR — volatility-adjusted stop that avoids normal daily noise. If price closes below this, exit rule triggers.`
            : "No scan data yet — scan this ticker to calculate stop level")}>
          {displayStop ? (
            <>
              <div style={{ ...mono, fontSize: F.base, fontWeight: 600, color: T.red }}>{fc(displayStop)}</div>
              {retStop && <div style={{ fontSize: F.xs, color: T.red }}>{fp(retStop)}</div>}
              {stopIsLive && <div style={{ fontSize: 8, color: T.textDim }}>live</div>}
            </>
          ) : "—"}
        </td>}

        {/* T1/T2 or D1/D2 — signal conditional */}
        <td style={{ ...cell, textAlign: "right" }} title={hint((() => {
          if (!result) return undefined;
          const isBull = result.signal?.includes("BUY") || result.signal === "POWER PLAY" || result.signal === "STANDARD LONG" || result.signal === "SPECULATIVE";
          const isBear = result.signal?.includes("SELL") || result.signal === "200 SMA STRUCTURE" || result.signal === "BREAKDOWN" || result.signal === "STANDARD SHORT";
          if (isBull && result.t1) return `T1: ${fc(result.t1)}${result.t1Source ? ` (${result.t1Source})` : ""} — take half off here, trail remainder.${result.t2 ? ` T2: ${fc(result.t2)}${result.t2Source ? ` (${result.t2Source})` : ""} — let rest run with trailing stop.` : ""}`;
          if (isBear && result.d1) return `D1: ${fc(result.d1)} — first downside support.${result.d2 ? ` D2: ${fc(result.d2)} — extended target.` : ""}`;
          return "No targets — signal is neutral. Targets appear on BUY or SELL verdicts.";
        })())}>
          {result ? (() => {
            const isBull = result.signal?.includes("BUY") || result.signal === "POWER PLAY" || result.signal === "STANDARD LONG" || result.signal === "SPECULATIVE";
            const isBear = result.signal?.includes("SELL") || result.signal === "200 SMA STRUCTURE" || result.signal === "BREAKDOWN" || result.signal === "STANDARD SHORT";
            if (isBull && result.t1) return (
              <>
                <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginBottom: 1 }}>
                  <div style={{ fontSize: F.xs, color: T.textDim }}>T1</div>
                  {result.t1Source && <div style={{ fontSize: 8, color: T.textDim, opacity: 0.7 }}>{result.t1Source}</div>}
                </div>
                <div style={{ ...mono, fontSize: F.sm, fontWeight: 600, color: T.green }}>{fc(result.t1)}</div>
                {bp && retT1 && <div style={{ fontSize: 10, color: T.green }}>{fp(retT1)}</div>}
                {result.t2 && <>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 3, marginTop: 3, marginBottom: 1 }}>
                    <div style={{ fontSize: F.xs, color: T.textDim }}>T2</div>
                    {result.t2Source && <div style={{ fontSize: 8, color: T.textDim, opacity: 0.7 }}>{result.t2Source}</div>}
                  </div>
                  <div style={{ ...mono, fontSize: F.sm, fontWeight: 600, color: "#4ade80" }}>{fc(result.t2)}</div>
                </>}
              </>
            );
            if (isBear && result.d1) return (
              <>
                <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 1 }}>D1</div>
                <div style={{ ...mono, fontSize: F.sm, fontWeight: 600, color: T.red }}>{fc(result.d1)}</div>
                {result.d2 && <>
                  <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 3 }}>D2</div>
                  <div style={{ ...mono, fontSize: F.sm, fontWeight: 600, color: "#f87171" }}>{fc(result.d2)}</div>
                </>}
              </>
            );
            return <span style={{ color: T.textDim }}>—</span>;
          })() : "—"}
        </td>

        {cv("earnings") && <td style={{ ...cell, width: 70, textAlign: "center" }}>
          {(() => {
            const rawDate = result?.nextEarningsDate || result?.earningsDate || result?.earnings?.date;
            if (!rawDate) return <span style={{ color: T.textDim }}>—</span>;
            const next = new Date(rawDate);
            if (isNaN(next.getTime())) return <span style={{ color: T.textDim }}>—</span>;
            const base = result?.scanTime ? new Date(result.scanTime) : new Date();
            const days = Math.floor((next - base) / (1000 * 60 * 60 * 24));
            if (!Number.isFinite(days) || days < 0) return <span style={{ color: T.textDim }}>—</span>;
            const mm = String(next.getMonth() + 1).padStart(2, "0");
            const dd = String(next.getDate()).padStart(2, "0");
            const label = `${mm}/${dd} (${days}d)`;
            const color = days <= 7 ? "#fbbf24" : days <= 14 ? "#f97316" : "#4d5570";
            const full = next.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
            const tip = `Next earnings: ${full} (${days} day${days === 1 ? "" : "s"})`;
            const warnTip = days <= 7
              ? `Earnings in ${days} days — high risk.\nIV elevated, binary event.`
              : days <= 14
                ? `Earnings in ${days} days — IV expansion window.\nOptions getting expensive.`
                : null;
            const content = warnTip ? `${tip}\n\n${warnTip}` : tip;
            return (
              <HoverTip content={content}>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 800, color, cursor: "help" }}>
                  {label}
                </span>
              </HoverTip>
            );
          })()}
        </td>}

        {POLYGON_OPTIONS_LIVE && cv("ivrank") && <td style={{ ...cell, width: 55, textAlign: "center", color: T.textDim }}>—</td>}
        {POLYGON_OPTIONS_LIVE && cv("csp") && <td style={{ ...cell, width: 55, textAlign: "center", color: T.textDim }}>—</td>}

        {cv("rr") && <td style={{ ...cell, textAlign: "center" }}
          title={hint(displayRR
            ? `Risk:Reward: ${displayRR.toFixed(1)}:1${rrIsLive ? " (at current scan price $" + scanPrice?.toFixed(2) + " — updates on every scan)" : " (at your entry price)"}. ${displayRR >= 3 ? "Excellent — $" + displayRR.toFixed(1) + " gained per $1 risked." : displayRR >= 2 ? "Good — meets the 2:1 minimum threshold." : "Low R:R — below 2:1 minimum."} Formula: (T1 − Entry) ÷ (Entry − Stop).`
            : "R:R calculates automatically from scan price. Enter a buy price for your actual entry R:R.")}>
          {displayRR ? (
            <>
              <div style={{ ...mono, fontSize: F.lg, fontWeight: 700, color: displayRR >= 3 ? T.green : displayRR >= 2 ? T.yellow : T.red }}>{ff(displayRR, 1)}:1</div>
              {rrIsLive && <div style={{ fontSize: 8, color: T.textDim, marginTop: 1 }}>live</div>}
              {!rrIsLive && displayRR < 2 && <div style={{ fontSize: F.xs, color: T.red }}>⚠ low</div>}
            </>
          ) : "—"}
        </td>}

        {cv("spark") && <td style={{ ...cell, width: 70, textAlign: "center" }}>
          {(() => {
            const closes = result?.closes;
            if (!Array.isArray(closes) || closes.length < 3) return <span style={{ color: T.textDim }}>—</span>;
            const pts = closes.slice(0, 10).filter(v => typeof v === "number" && Number.isFinite(v));
            if (pts.length < 3) return <span style={{ color: T.textDim }}>—</span>;
            const first = pts[pts.length - 1];
            const last = pts[0];
            const chg = first ? ((last - first) / first) * 100 : 0;
            const stroke = Math.abs(chg) < 0.5 ? "#4d5570" : (last > first ? "#22c55e" : "#ef4444");

            const w = 64, h = 24;
            const top = 4, bot = 20;
            const min = Math.min(...pts);
            const max = Math.max(...pts);
            const flat = Math.abs(max - min) < 1e-9;
            const yFor = (v) => {
              if (flat) return 12;
              const t = (v - min) / (max - min); // 0..1
              return bot - t * (bot - top);
            };
            const xFor = (i) => (pts.length === 1 ? 0 : (i / (pts.length - 1)) * (w - 2)) + 1;
            // pts is newest->older? In this app closes[0]=today, so pts[0]=today. Sparkline should flow left->right oldest->newest.
            const chron = [...pts].reverse();
            const d = chron.map((v, i) => `${xFor(i).toFixed(2)},${yFor(v).toFixed(2)}`).join(" ");
            return (
              <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: "inline-block" }}>
                <polyline
                  points={d}
                  stroke={stroke}
                  strokeWidth={1.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            );
          })()}
        </td>}

        {/* ACTIONS */}
        <td style={{ ...cell, paddingRight: 12 }} onClick={e => e.stopPropagation()}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {result && (<>
              <button onClick={() => onLogTrade && onLogTrade({ symbol, result, pos })}
                style={{ padding: "6px 11px", background: `${T.green}12`, border: `1px solid ${T.green}44`, color: T.green, fontSize: F.xs, cursor: "pointer", borderRadius: 6, fontFamily: "inherit", fontWeight: 700 }}>
                + Log
              </button>
            </>)}
            <button onClick={() => onScan(symbol)} disabled={scanning}
              style={{ padding: "6px 10px", background: T.surface, border: `1px solid ${T.border}`, color: T.cyan, fontSize: F.sm, cursor: scanning ? "not-allowed" : "pointer", borderRadius: 6, fontFamily: "inherit", fontWeight: 600 }}>↻</button>
            <button onClick={() => onRemove(symbol)}
              style={{ padding: "6px 10px", background: T.surface, border: `1px solid ${T.border}`, color: T.red, fontSize: F.sm, cursor: "pointer", borderRadius: 6, fontFamily: "inherit" }}>✕</button>
          </div>
        </td>
      </tr>

    </>
  );
}

// ─── HELP / GUIDE PANEL ───────────────────────────────────────────────────────
const HELP_TABS = [
  {
    id: "framework", label: "🧠 How It Works", icon: "🧠",
    sections: [
      {
        title: "The Three-Layer Framework",
        color: T.cyan,
        steps: [
          "Datum reads every ticker through three distinct layers. Understanding the difference between them is the most important thing to know about this tool.",
          "LAYER 1 — Setup Quality: The composite buyScore (capped ±15) reflects a 6-core scoring engine plus phase/setup bonuses. The indicator checklist (22+ rows in Pro mode) shows how many signals are bullish vs bearish for context — it is not a simple sum. A high score means strong technical bones. It does NOT mean buy right now.",
          "LAYER 2 — Entry Timing: The Entry Gate and the AI summary answer whether right now is the right moment to act. A stock can score +8 and still be a bad entry today because it has run too far too fast (extended RSI, price above BB upper) or because the AI sees macro context the score misses. This is the most important layer for your actual trade decision.",
          "ACCUMULATION ZONE means the stock is near its 52-week low with oversold RSI and distribution slowing. This is a long-term watch signal for patient investors — NOT a swing trade entry. Think MSFT, NVDA, or AAPL at major bottoms. LAYER 3 — Position Management: Exit alerts only matter if you already own the stock. If IBM shows a HIGH EXIT condition and you don't own IBM, that information is irrelevant to you. If you do own it, that's a signal to review your position — not a reason to avoid buying.",
          "The Verdict pill synthesizes all three layers into one clear recommendation. BUY NOW means all three layers align — act. BUY NOW ⚠ means gate is open but CMF conflict exists — enter half size only. WATCH — EXTENDED means strong setup but price is extended, wait for pullback. WATCH — CMF CONFLICT means institutions aren't confirming — wait for CMF to cross positive. BLOCKED means a critical gate condition is preventing entry regardless of score. CLOSE means all critical conditions pass and you are 1-2 advisory conditions from GO — scan daily. WR RECOVERY means Williams %R mean reversion signal with full 5-factor confirmation — shorter duration bounce trade. OVERSOLD RECOVERY is a new signal for quality names (Calmar ≥ 1) that have temporarily dipped with RSI oversold — mean reversion setup, not a momentum entry. EMERGING RECOVERY fires when a stock with bearish 200 SMA structure has a strong score and positive CMF — institutions buying before the golden cross confirms. STRONG SHORT requires negative CMF and bearish MACD — a conflicting signal (like PLTR with positive CMF) correctly shows BEARISH instead. Hover any verdict to see the full reasoning.",
        ],
      },
      {
        title: "Why the Score and Verdict Can Disagree",
        color: T.purple,
        steps: [
          "Example: AVGO shows Score +7, Verdict WATCH — VERIFY ENTRY. This is not a contradiction. The score says: most indicators are bullish. The verdict says: yes, but RSI is 74 and price is extended above the Bollinger upper band — chasing this entry is risky.",
          "The score is backward-looking evidence. The verdict is a forward-looking action recommendation. They answer different questions.",
          "When the AI summary has been run and it flags caution, that gets incorporated into the verdict. The AI sees the same data but reasons about it holistically — if it disagrees with a mechanical buy signal, the verdict upgrades from BUY to WATCH.",
          "The highest conviction trade in Datum is when all three layers agree: Score ≥ 5, Gate = GO, AI summary confirms, no extension. That combination shows as BUY NOW in green. These setups are rare and that's exactly why they're worth acting on.",
        ],
      },
      {
        title: "Easy Mode vs Pro Mode",
        color: T.green,
        steps: [
          "Easy Mode (default) leads with the Verdict badge as the primary signal. The table is clean — Ticker, Price, Verdict, Score, Calls/Puts, Targets. One clear answer per row. Hover the Verdict to see why.",
          "Pro Mode shows all 20 indicator columns so you can see the raw evidence behind each verdict. Use Pro Mode when you want to challenge a verdict or understand exactly what is and isn't firing.",
          "In both modes, clicking any row opens the Summary panel which shows the full breakdown: all 20 signal checks, the AI narrative, watch triggers, and the options play if applicable.",
          "The verdict updates automatically every scan. If the AI summary has been run, its caution assessment is included. If not, the verdict is based on the mechanical indicators alone — still useful, just without the narrative layer.",
        ],
      },
    ],
  },
  {
    id: "quickstart", label: "🚀 Quick Start", icon: "🚀",
    sections: [
      {
        title: "Read This First — The Honest Truth About Signal Trading",
        color: T.orange,
        steps: [
          "A well-built technical analysis system like this, used correctly, probably gets you to a 55-62% win rate on swing trades over time. That sounds low but it is actually good — most professional traders operate at 55-60% and make consistent money because they manage R:R properly.",
          "The bigger variable is not the signal accuracy. It is you.",
          "The app will correctly identify setups. The failures will come from: entering on a GO signal when VIX is 28 (the app tells you to check VIX but most people skip it), holding through earnings (the app flags the risk but does not stop you), sizing too large (taking a 10% portfolio position on a +7 score because it looks perfect), not exiting at 50% profit on options (watching a winner turn into a loser), and paper trading for 2 weeks instead of 4-6 (not enough sample size to calibrate your own behavior).",
          "The honest reality: technical analysis works until the market goes macro. A bearish 200 SMA structure on NVDA means nothing if the Fed announces a surprise rate hike or a tariff war starts. This is a swing trading tool for trending markets. In choppy or macro-driven markets, the ADX filter helps but it will not catch everything.",
          "What to do before deploying real capital: Paper trade 30+ trades minimum. Track not just whether you won or lost but WHY — was the signal right and you mismanaged it, or was the signal wrong? After 30 trades you will know if the system is working and more importantly you will know where your specific weaknesses are. That data is worth more than any indicator.",
          "The app gives you an edge. The edge only pays off if your execution and position sizing do not give it back.",
        ],
      },
      {
        title: "Why You Can Still Lose Money — Read This",
        color: T.red,
        steps: [
          "1. THE MARKET ENVIRONMENT OVERRIDES EVERYTHING. The biggest single factor. A technically perfect setup in a bear market will fail more often than the same setup in a bull regime. Institutions don't care that a stock has a perfect score — if they're reducing equity exposure across the board, it goes down with everything. The Regime Indicator at the top of the app tells you the environment. Take it seriously.",
          "2. ENTRY TIMING WITHIN THE SIGNAL. The signal fires when conditions align, but it does not tell you where within today's range to buy. Buying a BUY NOW at the high of the day vs. waiting for a pullback to EMA21 is the difference between a winning trade and getting stopped out immediately. The tool gives the green light — it does not tell you to wait for the 10:30am dip. Patience after the signal is your job.",
          "3. POSITION SIZING. This is the most overlooked factor. If you take 6 BUY NOW signals and put 10% of your portfolio in each, one bad macro event wipes you out regardless of signal quality. The Portfolio Calculator tab exists for exactly this reason — use it. Max 3% per single stock. Risk only 1-2% of your total portfolio per trade.",
          "4. HOLDING TOO LONG. A trader who enters on BUY NOW and holds through a SELL NOW is giving back all the gains. The exit signal column exists for a reason. Entry signals are generated well by this system. Exit discipline is entirely on the trader — and most traders are worse at exiting than entering. Set your exit plan before you enter.",
          "5. IGNORING YOUR STOP LOSS. The ATR stop is calculated and shown in every row. Most traders ignore it or widen it emotionally after a loss. If you own a stock and it closes below its stop level, exit. No exceptions. The stop exists because the trade thesis is broken — holding longer hoping for a recovery is how small losses become large ones.",
          "6. OVERTRADING THE SCANNER. The scanner surfaces 360+ tickers. A trader who acts on every BUY and BUY NOW is taking 20-30 simultaneous positions. At that scale, correlation risk is massive — they all go down together in a down day. Focus on the 3-5 cleanest setups. Concentration in the best ideas beats spreading thin across 20 marginal ones.",
          "7. CONFUSING SIGNAL QUALITY WITH OUTCOME CERTAINTY. A 55-65% win rate is what elite professional traders achieve. That means 35-45% of clean, correct signals still result in losses. TWLO can be a perfect BUY NOW and drop 8% tomorrow because the semiconductor sector has a bad day. A losing trade on a good signal is not a system failure — it is the normal cost of doing business. Manage it with position sizing, not by questioning every signal.",
          "8. WHAT THIS TOOL CANNOT SEE. Earnings announcements 2 days out. Fed decisions. Geopolitical events. Sector-specific news breaking after close. Institutional block trades happening in real time. All of these can invalidate a technically perfect setup instantly. The 3-day rule and earnings proximity flag help, but no technical system catches everything. Always know what is on the calendar for your positions.",
        ],
      },
      {
        title: "Step 1 — Connect Your Data Source",
        color: T.cyan,
        steps: [
          "Market data is pulled automatically from Yahoo Finance — no account or API keys needed",
          "Navigate to Paper Trading → API Keys → Generate New Keys",
          "Click ⚙ Settings in the top right → Data Source tab → paste your Key ID and Secret Key",
          "Click Test Connection to verify, then Save & Close",
          "Unlimited scans — no daily limit, no rate limit, no cost",
        ],
      },
      {
        title: "Step 2 — Build Your Watchlist",
        color: T.green,
        steps: [
          "Type tickers in the ADD TICKERS box: NVDA, TSLA, SPY, QQQ — hit Enter",
          "OR click 📊 Sectors to add an entire sector at once (AI/Semis, ETFs, etc.)",
          "OR click 🔥 Top Movers to pull today's top 30 gainers/losers/active in one call",
          "Always add SPY and QQQ first — they tell you what the broad market is doing",
          "Invalid tickers are automatically detected and removed",
        ],
      },
      {
        title: "Step 3 — Scan",
        color: T.yellow,
        steps: [
          "Click ⟳ Scan All to scan your entire watchlist",
          "The scanner runs in parallel — a full 15-ticker scan takes about 10 seconds",
          "Use the 🔭 Market Scanner tab to scan 360+ stocks across 16 sectors for the best setups",
          "OR configure ⏰ Scheduler to auto-scan at market open, power hour, and close",
          "Windows notifications fire automatically when signals change",
        ],
      },
      {
        title: "Step 4 — Read the Results",
        color: T.purple,
        steps: [
          "The Signal column is the whole story at a glance. Each row shows: a Verdict pill (▲ BUY NOW, ↩ OB RECOV, ▼ STRONG SHORT, etc.) on the first line, then an info line below it.",
          "The info line shows: BULL count in green (bullish signals active), BEAR count in red (bearish signals active), neutral count in gray, then any context badges — ● SPEC (high-beta warning), ≠ CMF (flow conflict), ↑↑ MTF (multi-timeframe confirmed), and ◎ AI (click to run or view AI analysis).",
          "Example: '▲ BUY NOW / BULL 9  BEAR 1  — 2  |  ● SPEC  ≠ CMF  ◎ AI' — a strong bullish signal on a speculative stock with an institutional flow conflict. The SPEC and CMF badges tell you exactly what to be careful about before entering.",
          "If a ticker is SPEC (ATR > 5% daily) or has a CMF conflict on a buy signal, a red or orange caution banner appears automatically below the row — no click required. It explains the risk and what to do.",
          "Click any row to open Summary — full three-layer breakdown: all 20 indicator checks, AI narrative, targets, and options play. The ◎ AI button on the info line opens this directly.",
          "Use filter tabs — BUY, CALLS / PUTS with 📊 Analysis, SQUEEZE, OB RECOV — to focus on actionable setups.",
        ],
      },
    ],
  },
  {
    id: "operations", label: "📋 Order of Operations", icon: "📋",
    sections: [
      {
        title: "Before You Enter Any Trade — Run This Checklist",
        color: T.green,
        steps: [
          "1. CHECK THE BROAD MARKET — Is SPY/QQQ trending up today? If the market is in a confirmed downtrend, no new longs regardless of individual signals.",
          "2. CHECK THE SECTOR — Is the sector ETF (SOXX for semis, XLK for tech) healthy? A great signal on a stock in a sick sector is usually a trap.",
          "3. CHECK VIX — Open the VIX Translator (in expanded row). Is VIX below 20? Good. Above 25? Be very cautious with options. Falling VIX = buy signal. Rising VIX = reduce exposure.",
          "4. CHECK THE VERDICT — Does the row show BUY NOW or BUY? If it shows WATCH, WAIT, or STRONG SETUP — WAIT, do not enter yet. Hover the badge to read exactly why.",
          "5. CHECK THE 3-DAY RULE — Is there a post-drop cooldown or earnings gap active? If yes, wait for it to clear.",
          "6. CHECK R:R — Is the Risk:Reward at least 2:1? If not, skip the trade. Wait for a better setup.",
          "7. WAIT UNTIL 10 AM ET — Never enter a new position in the first 30 minutes of trading. Let overnight flow settle.",
          "8. CONFIRM PRE-MARKET NEWS — Check Bloomberg/Yahoo for any catalysts, earnings surprises, or gap-down risk.",
          "9. CHECK RVOL — Is today's volume above average? Low volume = weak signal. 1.5x+ = institutional conviction.",
          "10. CHECK VOLATILITY TIER — Does the signal column show ● SPEC or ● HIGH VOL? If yes, the caution banner below the row explains the risk. Take a smaller-than-typical position on speculative/high-beta names regardless of how strong the signal looks.",
          "11. CHECK CMF CONFLICT — Does the info line show ≠ CMF? This means institutional money flow is not confirming the bullish signal. Either wait for CMF to turn positive, or enter at reduced size. When CMF crosses above 0, that's your confirmation trigger.",
          "12. ONLY THEN — enter your position with your planned size, stop level, and target.",
        ],
      },
      {
        title: "While You're In a Trade — Monitor These",
        color: T.yellow,
        steps: [
          "Watch the SELL SIGNAL column — 3+ red dots = start preparing to exit",
          "CRITICAL or HIGH exit alert = exit immediately, don't wait for confirmation",
          "Check ATR Stop level — if price breaks below it, that's your exit rule, period",
          "If RSI crosses above 70 AND price is above BB upper — consider trimming half",
          "If MACD crosses down — momentum is reversing, have your finger on the exit",
          "Never average down into a losing position without re-running the full gate checklist",
          "If down more than 8% from entry with no clear reversal signal — reassess immediately",
        ],
      },
      {
        title: "Daily Routine",
        color: T.cyan,
        steps: [
          "Pre-Market (8:00 AM) — Auto-scan fires. Check notifications for overnight signal changes.",
          "9:30–10:00 AM — Do NOT enter new positions. Watch the open, let the dust settle.",
          "10:00 AM — Review BUY filter. Any BUY NOW verdict with R:R 3:1+? These are your candidates. Check verdict hover for reasoning before entering.",
          "Midday (12:00 PM) — Auto-scan fires. Check for any signal deterioration on open positions.",
          "Power Hour (3:00–4:00 PM) — Auto-scan fires. Review SELL and ALERTS filters.",
          "After Close (4:15 PM) — Final scan. Plan tomorrow. Add new candidates from Top Movers.",
        ],
      },
      {
        title: "Weekly Routine",
        color: T.orange,
        steps: [
          "Sunday night — Run a full sector sweep. Add all 7 sectors, scan overnight.",
          "Monday morning — Filter by BUY. Review every setup across the full market.",
          "Add high-conviction setups to your active watchlist, remove the rest.",
          "Review any open positions against the Signal Playbook tier. Are they still in the right tier?",
          "Check if any positions have hit T1 — consider selling half and moving stop to breakeven.",
        ],
      },
    ],
  },
  {
    id: "indicators", label: "📊 Indicator Guide", icon: "📊",
    sections: [
      {
        title: "Trend Indicators",
        color: T.green,
        steps: [
          "GOLDEN CROSS — SMA50 crosses above SMA200. Major long-term bullish signal. One of the most reliable trend confirmation signals in all of technical analysis.",
          "200 SMA STRUCTURE — SMA50 crosses below SMA200. Macro bearish. The app BLOCKS all entries when this is active. Never fight a bearish 200 SMA structure.",
          "EMA21 — The short-term trendline. Price above = swing trend intact. Price below = trend at risk. Use as a trailing stop reference.",
          "PARABOLIC SAR — Trailing stop dots. Flips above price when trend reverses. The SAR level is shown as a hard stop reference.",
          "TREND STRUCTURE (HH/HL) — Higher Highs + Higher Lows = confirmed uptrend. Lower Highs + Lower Lows = confirmed downtrend. Most reliable at the daily timeframe.",
        ],
      },
      {
        title: "Momentum Indicators",
        color: T.cyan,
        steps: [
          "RSI (14) — Below 30 = oversold (potential buy). Above 70 = overbought (trim/sell). The 40-60 zone is neutral. Sweet spot for swing entries: 35–55.",
          "RSI DIVERGENCE — The most powerful RSI signal. BULLISH DIVERGENCE: price making lower lows but RSI making higher lows — momentum is improving before price catches up. +2 score, strong mean-reversion signal. BEARISH DIVERGENCE: price making higher highs but RSI making lower highs — momentum is weakening into the move. -2 score and triggers a WATCH verdict even on strong setups. This is one of the most reliable early reversal warnings in all of TA.",
          "MACD (12/26/9) — Crossover above signal line = bullish momentum starting (+3 score). Crossunder = bearish (-3 score). Histogram direction shows acceleration. Fresh crossovers are the most actionable signal.",
          "STOCHASTIC (14,3,3) — Confirms RSI. %K crossing above %D below 20 = bullish. Above 80 = overbought. Used as an early momentum signal before RSI catches up.",
          "ADX — Measures trend STRENGTH not direction. Below 20 = choppy/ranging market, signals are unreliable. Above 25 = strong trend. Above 40 = very strong. Never swing trade with ADX below 20.",
          "WILLIAMS %R (14) — Ranges from -100 to 0. Below -80 = oversold. Above -20 = overbought. The most powerful signal is the TRANSITION: when %R crosses back above -80 from deeply oversold, mean reversion is underway — +1.5 score. When %R crosses below -20 from overbought, distribution is starting — -1.5 score. Williams %R leads RSI by 1-3 bars, making it an early warning system for turns.",
        ],
      },
      {
        title: "Volume Indicators",
        color: T.yellow,
        steps: [
          "CMF (Chaikin Money Flow) — Above +0.05 = institutional accumulation. Below -0.05 = distribution. Above +0.15 = strong institutional buying. The best confirmation that real money is behind a move.",
          "OBV (On Balance Volume) — Cumulative volume. OBV rising with price = confirmed move. OBV falling while price rises = bearish divergence, weak move likely to fail.",
          "RVOL (Relative Volume) — Today's volume vs 20-day average. 1.5x+ = above average conviction. 2x+ = volume surge, institutional participation. Below 0.7x = weak move, be skeptical.",
          "VOLUME DIVERGENCE — Price rising on falling volume = institutions are not buying, retail is pumping. One of the most reliable early warning signs of a reversal.",
          "VWAP — Volume Weighted Average Price. The institutional average cost. Price above = bulls in control. Never buy calls with price below VWAP.",
          "VOLUME BREAKOUT CONFIRMATION — Validates whether a price breakout (above EMA21, BB upper, or 20-day high) has real institutional participation. CONFIRMED = breakout with 1.5x+ average volume (+1.5 score) — institutions are buying the move. FAKEOUT RISK = breakout on below-average volume (<0.85x, -1.5 score) — historically fails 60-70% of the time. The rule: a breakout that everyone sees but nobody buys is not a breakout. VOL SURGE without breakout = possible stealth accumulation or news catalyst, watch for next move.",
          "VOLUME CLIMAX — One of the highest-conviction reversal signals in Auctus. Fires when a stock closes red on 3x+ average volume. The logic: retail investors panic-sell en masse, and institutional buyers absorb all of that supply at the low. When the climax day holds above prior lows (institutions defended a level) AND RSI is oversold (≥2 points), this is historically one of the best long setups in all of technical analysis. Strength 0–3: each of these adds conviction — (1) RSI below 35, (2) price holds above prior 3-day low, (3) volume is 5x+ average. CLIMAX — HIGH CONVICTION (strength ≥2) = +2 score. CLIMAX — WATCH (strength 1) = +1 score. The next-day close above the climax day high is the entry trigger.",
        ],
      },
      {
        title: "Volatility & Support/Resistance",
        color: T.orange,
        steps: [
          "BOLLINGER BANDS — Price above upper band = statistically extended. Price below lower band = oversold. BB Squeeze (bands tightest in 6 months) = explosive move loading. Play the direction after the break.",
          "ATR (Average True Range) — Daily volatility range. Used to calculate smart stops (2×ATR). Volatile stocks need wider stops. This prevents getting stopped out by normal daily noise.",
          "FIBONACCI — 61.8% retracement (the Golden Ratio) is where institutions place limit orders. The best swing entries come from bouncing off Fib levels with RSI confirming.",
          "PIVOT POINTS — Calculated support/resistance from prior 20-day range. R1/R2 = resistance targets. S1/S2 = support floors. Price near a level = watch for reaction.",
          "52-WEEK RANGE (0–100 score) — 0–20 near annual low (highest R:R for longs). 80–100 near annual high (only enter on breakout with volume). Mid-range = no extreme signal.",
        ],
      },
      {
        title: "Relative Strength",
        color: T.purple,
        steps: [
          "RS vs SPY — Compares the stock's 3-month return against SPY. Outperforming = stock has relative strength vs the broad market — worth +1 score. Underperforming = drag, even if the setup is bullish — -1 score. A strong setup on a stock lagging SPY by 8%+ has a lower follow-through probability.",
          "SECTOR RS — Compares the stock's 3-month return against its own sector ETF (e.g. NVDA vs SOXX, AAPL vs XLK). LEADING SECTOR = +1 score — the stock is outperforming even its sector peers, indicating stock-specific strength beyond a macro tailwind. LAGGING SECTOR = -1 score — the stock underperforms even within its own sector, a red flag even when the sector is healthy. This is a more surgical signal than RS vs SPY because it filters out the broad market effect.",
          "WHY IT MATTERS — A stock can outperform SPY simply because its sector is hot. Sector RS strips out the sector tailwind and asks: is THIS stock actually leading? When RS vs SPY and Sector RS are both positive, you have genuine stock-specific strength — the best setups.",
        ],
      },
      {
        color: T.purple,
        steps: [
          "WHAT IT IS — The Calmar Ratio measures how much return a stock has generated relative to its maximum drawdown. Formula: Annualized Return ÷ Max Drawdown. A Calmar of 2.0 means the stock returned twice what it lost at its worst point.",
          "HOW TO READ IT — Above 3.0 = strong risk-adjusted performance. The stock has delivered meaningful gains without requiring investors to stomach devastating drawdowns. 1.0–3.0 = acceptable. Below 1.0 = poor — you are taking more drawdown risk than you are getting in return. Below 0.5 = avoid.",
          "CALMAR vs RAW RETURN — A stock up 80% sounds great. But if it dropped 70% to get there, the Calmar is terrible (80/70 = 1.14). Compare this to a stock up 40% with a 10% max drawdown (Calmar 4.0) — far better risk-adjusted outcome with far less stress.",
          "IN AUCTUS — Calmar appears in the 🌊 Tradeflow tab. It is calculated from the 63-day annualized return and the 52-week drawdown as a proxy. It does NOT affect the Auctus Score. Use it as a tiebreaker: when two setups have similar scores, favor the one with the higher Calmar.",
          "LIMITATIONS — Calmar is backward-looking. A high historical Calmar does not guarantee future performance. A stock that trended cleanly for 6 months may have an excellent Calmar and still be setting up for a reversal. Always combine with forward-looking signals.",
          "PROFESSIONAL CONTEXT — Hedge funds use Calmar to evaluate their own track records across 3-5 year periods. Applying it to a single ticker over 63 days is a reasonable approximation but treat it as directional evidence, not a precise measurement.",
        ],
      },
    ],
  },
  {
    id: "scoring", label: "🎯 Signal Scoring", icon: "🎯",
    sections: [
      {
        title: "How the Composite Score Works",
        color: T.cyan,
        steps: [
          "The buyScore is capped at ±15. It is built in layers — not by summing the 22-row indicator checklist (that checklist is for display and bull/bear counts only).",
          "LAYER A — 6-core engine (before weekly/tide): MACD (−3…+3), RSI (−2…+2), RS vs SPY (−2…+2), Bollinger squeeze/expansion (−3…+3), CMF (−2…+2), Sector rotation 3d vs SPY (−2…+2).",
          "LAYER A — Additives: RSI divergence (±2), key level proximity (up to +1.5), 52W breakout score (−1…+3), OBV divergence (±2). Subtotal = buyScoreBeforeWeekly.",
          "LAYER B — Phase system: early-warning phase score (−9…+9) + optional +1 when Phase 1→2→3 aligns with full calls/puts signal.",
          "LAYER C — Multipliers: weekly bearish cut (×0.5–1, tide-aware override) then market tide (×0.5–1.25 on directional scores).",
          "LAYER D — Setup bonuses: dead cat bounce (+2/+3/+4) and trade setup winner (VCP, flag, drift, etc. — up to +10). Hard RSI/CMF penalties applied last.",
          "Score +7 and above = strongly bullish setup. Score −7 and below = strongly bearish. The score is Layer 1 evidence — the Verdict badge synthesizes score + entry gate + timing into the action recommendation.",
        ],
      },
      {
        title: "Verdicts — STARTER (Phase D)",
        color: "#06b6d4",
        steps: [
          "STARTER — 25% ACCUMULATION (Phase D)",
          "Most retail traders enter setups AFTER the breakout confirms — by which point the easy money is gone. The STARTER verdict catches institutional accumulation BEFORE the technical score reflects it.",
          "Trigger: Phase 1 (institutional accumulation cluster — CMF rising, OBV building, volume favoring up days, OR RS leader vs SPY) is detected, Phase 2 (pre-breakout coil) and Phase 3 (breakout confirmation) have not yet fired, the underlying technical score is in the -2 to +4 range (where the regular cascade would produce CHOP or WATCH), and no critical gate blocks are active.",
          "What to do:",
          "  • Enter at 25% of your normal position size NOW",
          "  • Stop: 2×ATR below entry",
          "  • Add the remaining 75% when Phase 2 or Phase 3 fires (the regular cascade will then route to POWER PLAY, STANDARD LONG, or SPECULATIVE based on technical confirmation)",
          "  • If Phase 1 fades without progressing to Phase 2 within 10 trading sessions, exit the starter — institutional interest didn't follow through, free up the capital",
          "Why a separate verdict tier: Phase 1 detection currently boosts the technical score by +2. For a stock with a mediocre -1 base score, that's only enough to reach +1 — still in CHOP range, so the user never sees it as actionable. STARTER bypasses score thresholds entirely and surfaces the institutional signal directly.",
          "STARTER SHORT — 25% DISTRIBUTION is the bearish mirror — same logic applied to Phase 1 distribution detection.",
          "Layered context: STARTER respects all other modifiers. Sector headwind, earnings window, signal staleness, and AI caution all apply to STARTER the same way they apply to STANDARD LONG. The fully-stacked label can read: STARTER — 25% ACCUMULATION — SECTOR HEADWIND — AI CAUTION.",
        ],
      },
      {
        title: "Verdicts — EXIT (Phase E)",
        color: "#dc2626",
        steps: [
          "When the user owns a position (In Position toggle ON), the verdict switches from entry-side (BUY / WATCH / WAIT) to exit-side. The exit verdict tells you what to do with the position — not whether to enter a new one.",
          "",
          "Five exit verdicts in priority order (first match wins):",
          "",
          "  1. STOP LOSS HIT — EXIT (red)",
          "     Price closed below entry − 2×ATR. Hard exit rule. No averaging down. The 2×ATR stop is the discipline that keeps small losses from becoming catastrophic ones.",
          "",
          "  2. MOMENTUM LOSS — EXIT (orange)",
          "     Three concurrent technical losses: MACD histogram negative, price below EMA21, CMF turned negative. The setup that brought you in is no longer valid. Exit regardless of P&L.",
          "",
          "  3. T2 HIT — FULL EXIT OR TRAIL (emerald)",
          "     Price hit entry + 3×ATR — a successful swing. Either close the remaining position or convert to a trailing stop at EMA21. Don't let a winner turn into a loser.",
          "",
          "  4. TRAILING STOP — EXIT (amber)",
          "     Position ran 2×ATR+ in profit but is now closing below EMA21. Momentum is rolling over. Lock in the gain.",
          "",
          "  5. T1 HIT — TAKE 50% (yellow)",
          "     Price hit entry + 1.5×ATR profit. Sell 50%, move stop on the remaining half to breakeven. Let the runner go toward T2 with no downside risk.",
          "",
          "Catch-all when no exit triggers fire:",
          "  • HOLD — POSITION HEALTHY (emerald) when calcPositionHealth is green",
          "  • HOLD — MONITOR (gray) otherwise",
          "",
          "Why exits are their own verdict tier: Most retail traders lose money on exits, not entries. The position-management decision is fundamentally different from the entry decision — you already own it, the question is what to do now. Promoting exits to first-class verdicts forces the decision to the surface instead of hiding it behind position health colors and column data.",
          "",
          "Other modifiers (sector tide, earnings, signal age, AI caution) do NOT apply to exit verdicts. When the stop has been hit, sector context doesn't matter — you exit. The original verdict (what was showing pre-exit) is preserved as preExitVerdictLabel for context.",
          "",
          "ATR caveat: T1/T2/stop levels use current ATR as a proxy for entry ATR (acknowledged limitation — see future enhancement to track entry_atr at position open time).",
        ],
      },
      {
        title: "Entry Gate — The 14-Point Checklist",
        color: T.green,
        steps: [
          "The gate runs 14 checks. 6 are CRITICAL (hard blocks if failed). 8 are advisory.",
          "CRITICAL BLOCK 1: 200 SMA structure bearish — SMA50 < SMA200. No long entries allowed.",
          "CRITICAL BLOCK 2: Price extended — RSI > 65 AND above BB upper simultaneously. Chasing a stretched move.",
          "CRITICAL BLOCK 3: Post-drop cooldown — 4%+ drop in last 3 sessions. Institutional selling often continues.",
          "CRITICAL BLOCK 4: Score deeply bearish (≤ -3) — too many bearish signals to justify a long.",
          "CRITICAL BLOCK 5: Near 52W high with CMF not confirming — institutions not supporting the level.",
          "CRITICAL BLOCK 6: Fakeout breakout — breakout detected but volume < 0.85x average. Fails 65%+ of the time.",
          "Advisory checks (8): RSI not overbought, CMF positive, price above VWAP, no MACD crossunder, no bearish volume divergence, earnings gap settled, weekly trend not down, price above ATR stop.",
          "Gate label: GO = all critical pass + 12+ advisory pass. CLOSE = all critical pass, 1-2 advisory failing. NEAR = all critical pass, 3+ advisory failing. BLOCKED = any critical fails.",
          "",
          "SECTOR TIDE GATE (Phase A)",
          "Sector tide measures the 5-day price change of the ticker's sector ETF (XLK, XLF, XLE, etc.) — falls back to 3-day when 5-day is unavailable — and modifies the verdict based on whether the sector is supporting or fighting the setup.",
          "Bands: NEUTRAL (-3% to +3%) = no modification.",
          "HEADWIND (-3% to -5%): bullish momentum verdicts (POWER PLAY, STANDARD LONG, SPECULATIVE) downgrade to half-size with tighter stop. Mean-reversion setups (WR RECOVERY, OB RECOVERY, ACCUMULATION, EMERGING RECOVERY) are NOT downgraded — sector weakness can be the setup catalyst.",
          "SEVERE_HEADWIND (≤ -5%): all bullish setups downgrade to WATCH — SECTOR HEADWIND. Wait for sector stabilization.",
          "TAILWIND_AGAINST_SHORTS (+3% to +5%): bearish verdicts downgrade to half-size puts with tighter stop.",
          "SEVERE_TAILWIND_AGAINST_SHORTS (≥ +5%): all bearish setups downgrade to WATCH — SECTOR TAILWIND AGAINST.",
          "Skipped for: index ETFs (SPY, QQQ, DIA, IWM, etc.), crypto, and any ticker without a sector mapping.",
          "Note: layered on top of the score-based Sector Rotation component (±2 in core scoring). Sector Rotation gives a small nudge; Sector Tide is the structural override when sector weakness is severe.",
          "",
          "EARNINGS WINDOW GATE (Phase B)",
          "Earnings is a binary event. Holding a swing position through an earnings print is gambling on the reaction, not trading a technical setup. Both longs and shorts are exposed — earnings can gap a short violently against you just as easily as a long.",
          "Trigger: ticker reports earnings within 7 trading sessions (daysAway >= 0 AND <= 7).",
          "Bullish momentum (POWER PLAY, STANDARD LONG, SPECULATIVE): downgraded to WAIT — EARNINGS WINDOW. Wait for earnings to pass.",
          "Bullish mean reversion (WR RECOVERY, OB RECOVERY, ACCUMULATION ZONE, EMERGING RECOVERY): downgraded to WAIT — EARNINGS WINDOW. Mean reversion doesn't survive earnings either — bottoms get re-tested or invalidated by the print.",
          "Bearish setups (STRONG SHORT, BREAKDOWN, BEARISH): downgraded to WAIT — EARNINGS WINDOW. Short thesis is exposed to binary upside risk on the print.",
          "Existing positions (HOLD): verdict NOT changed, but an \"Earnings in {N} sessions — consider trimming\" warning is added to the action text. Decision is yours: trim to 25–50% size, or accept the binary risk for the thesis.",
          "Non-actionable layers (CHOP, WATCH, BLOCKED, NEARLY GO, SETUP — WAIT): not modified — they were already not actionable.",
          "Skipped when: earnings data unavailable (lenient default — don't gate on missing data), or earnings already reported (daysAway < 0).",
          "Note: this is a hard 7-session gate. The existing 14-day \"earnings soon\" badge and the −40% sizing adjustment still apply in the 8–14 day window — those are the softer warnings before the hard gate.",
          "The original verdict (what it would have been without the earnings gate) is preserved as preEarningsVerdictLabel — visible in the drawer Levels tab for context.",
          "",
          "AI CAUTION MODIFIER",
          "When the cached AI summary for a ticker contains caution keywords (\"avoid\", \"wait for\", \"extended\", etc.), the verdict is modified rather than suppressed. Previously, AI caution silently dropped actionable verdicts to HOLD — MONITOR fallback — losing the technical signal entirely.",
          "Effect:",
          "  • Actionable bullish verdicts (POWER PLAY, STANDARD LONG, SPECULATIVE, WR RECOVERY, OB RECOVERY, ACCUMULATION ZONE, EMERGING RECOVERY): label appended with \"— AI CAUTION\", action text adds AI caveat. Underlying setup is preserved.",
          "  • Actionable bearish verdicts (STRONG SHORT, BREAKDOWN, BEARISH): same treatment — short setup preserved, AI flag appended.",
          "  • Non-actionable verdicts (CHOP, WATCH, BLOCKED, HOLD, NEARLY GO, or anything already downgraded by sector tide / earnings): not modified — AI caution would be redundant on a verdict that's already in the wait layer.",
          "Order of evaluation: Sector Tide (Phase A) → Earnings Window (Phase B) → AI Caution. The first two may replace the verdict with a WATCH or WAIT tier, in which case AI caution does not fire (its non-actionable check excludes those).",
          "This change ensures the row and drawer always show the same verdict for a given ticker — previously the row passed AI caution to calcVerdict() while the drawer hardcoded false, causing labels to diverge silently.",
          "",
          "TIME STOPS / STALE MODIFIER (Phase C)",
          "Swing trade setups have an implicit expiration. A signal that fires and then sits without progress for 7+ trading sessions is statistically stale — the conditions that originally triggered it have decayed, and capital tied up in it is losing opportunity cost vs fresher setups.",
          "Trigger: existing OPEN signal_history row for this ticker with first_fired_at >= 7 trading sessions ago AND no 3%+ move in the expected direction since the trigger price.",
          "Bands:",
          "  • MILD STALE (7–9 sessions, no 3%+ move): Bullish and bearish actionable setups get \"— STALE\" appended. Action text recommends tightening stop to 1×ATR and reducing size by 25%. If no progress in 3 more sessions, exit.",
          "  • SEVERE STALE (10+ sessions, no 3%+ move): Verdict is replaced with WATCH — STALE SETUP (gray badge). Setup is structurally dead — wait for fresh conditions or remove from active watch.",
          "  • CONFIRMED MOVE (any age, 3%+ in expected direction): Not stale — the signal proved out. No modification.",
          "  • HOLD positions on a stale signal: Verdict stays HOLD. A \"⏱ STALE POSITION\" warning is added to the action text suggesting trim + redeploy.",
          "Time calculation uses trading days (skipping weekends and US market holidays) — not calendar days.",
          "Order of evaluation: Sector Tide (Phase A) → Earnings Window (Phase B) → STALE (Phase C) → AI Caution. If sector or earnings already moved the verdict to a non-actionable layer (WATCH / WAIT), STALE doesn't add noise on top. AI Caution can still layer over a STALE-suffixed verdict: e.g. \"STANDARD LONG — STALE — AI CAUTION.\"",
          "Watchlist row: a clock badge (⏱5d / ⏱8d / ⏱STALE) appears next to the verdict badge once the signal is at least 5 sessions old, scaling in color from gray (informational) to amber (warning) to red (severe).",
        ],
      },
      {
        title: "Exit Alerts — When to Get Out",
        color: T.red,
        steps: [
          "CRITICAL: Price broke ATR stop level. This is a hard exit rule — no exceptions.",
          "CRITICAL: 4 or more of 5 sell conditions active simultaneously.",
          "HIGH: RSI bearish divergence or heavy distribution — momentum weakening (MACD shown in UI only, not exit triggers).",
          "HIGH: RSI bearish divergence — price rising but momentum declining.",
          "HIGH: Volume divergence on a position — institutions are quietly exiting.",
          "HIGH: Gap down 3%+ — momentum break, reassess the thesis.",
          "MEDIUM: Price extended above BB + RSI > 68 — consider trimming half.",
          "MEDIUM: CMF below -0.10 — heavy distribution in progress.",
          "Rule: CRITICAL = exit immediately. HIGH = seriously consider exiting. MEDIUM = trim or tighten stop.",
        ],
      },
      {
        title: "The 3-Day Rule — Three Versions",
        color: T.yellow,
        steps: [
          "VERSION 1 — Post-Drop Cooldown: Any single day with a 4%+ drop triggers a 3-session wait. Institutional selling rarely happens in one day — there are usually 2-3 more red days before the real floor.",
          "VERSION 2 — Breakout Confirmation: When price breaks above resistance, wait for 3 consecutive closes above that level. A single-day breakout that doesn't hold is called a fakeout — it triggers stops, fills buyers, then reverses.",
          "VERSION 3 — Earnings/Gap Stabilization: Any 5%+ single-day move (earnings reaction, news gap) triggers a 3-day wait. Stock is repricing — entering on day 1 or 2 is gambling on the direction of that repricing.",
          "When any 3-day rule is active, the Entry Gate blocks entry and shows exactly how many sessions remain.",
        ],
      },
    ],
  },
  {
    id: "trades", label: "💡 Trade Type Guide", icon: "💡",
    sections: [
      {
        title: "When to Buy Shares (Swing Long)",
        color: T.green,
        steps: [
          "Golden cross active + score +4 or higher",
          "CMF positive — institutions are accumulating",
          "ADX above 20 — there's a real trend to ride",
          "Price above VWAP and EMA21",
          "Entry Gate shows GO or CAUTION (not NOT YET)",
          "Hold time: 5–20 trading days",
          "Best setup: Near 52w low + RSI recovering + BB Squeeze loading",
        ],
      },
      {
        title: "When to Buy Calls (Bullish Options)",
        color: T.cyan,
        steps: [
          "Score +7 or higher (stronger signal needed for leverage)",
          "BB Squeeze detected — confirms a big move is loading",
          "RSI between 35–65 — not extended, room to run",
          "VIX below 20 — premiums are cheap enough to justify buying",
          "No earnings within 2 weeks — IV crush will kill you even if right",
          "R:R at least 3:1 — options need better setups than shares",
          "Hold time: 3–14 days. Pick expiration 35–45 days out minimum so you have a full exit window before the 14 DTE danger zone.",
        ],
      },
      {
        title: "When to Buy Puts (Bearish Options)",
        color: T.red,
        steps: [
          "200 SMA structure bearish — macro trend confirmed bearish",
          "Score -3 or lower with 3+ sell conditions active",
          "CMF negative — institutional distribution confirmed",
          "Price below VWAP and EMA21",
          "VIX below 20 — cheap premiums to buy protection",
          "Never buy puts against a golden cross — you're fighting the trend",
          "Best setup: Near 52w high + MACD bearish cross + OBV diverging",
        ],
      },
      {
        title: "When to Sell Covered Calls (Income)",
        color: T.purple,
        steps: [
          "You already own shares and the stock is extended",
          "RSI above 65 AND price above BB upper band",
          "Near 52-week high — limited upside remaining",
          "VIX elevated (16+) — you collect more premium",
          "Sell calls 1–2 strikes above current price, 2–3 weeks out",
          "This generates income while you wait for pullback to buy more",
          "NOT for selling when you expect a large continued move — you cap your upside",
        ],
      },
    ],
  },
  {
    id: "faq", label: "❓ FAQ", icon: "❓",
    sections: [
      {
        title: "After You Enter — What to Expect",
        color: T.green,
        steps: [
          "This platform is built for swing trading. The indicators — RSI, MACD, CMF, ADX, Bollinger Bands — all operate on daily timeframes. They measure momentum and trend over days to weeks, not hours.",
          "A BUY NOW verdict means conditions are aligned today. It does not mean the stock will be up tomorrow. Give every trade a minimum of 5 trading days before questioning the thesis — unless your stop is hit first.",
          "Days 1-3 — Confirmation window: The riskiest period. The stock may chop, dip, or even test your stop before moving. This is normal. If it triggers your stop in day 1-2 that is not a bad signal — that is risk management working correctly.",
          "Days 3-10 — Where most of the move happens: If the thesis is right, this is where momentum carries the trade. MACD crossovers, RSI expansion, volume confirmation all resolve in this window.",
          "Days 10-20 — Extension phase: Stronger setups with high ADX and clean trend structure can run longer. This is where you manage the position — trailing stop, partial exit at T1, holding the runner to T2.",
          "Expected hold time: 1 to 3 weeks for most setups. Exceptional setups can run 4 to 6 weeks.",
          "The mindset shift: Most retail traders lose because they exit winners too early and hold losers too long. The exit signals in this platform tell you when the original thesis has broken down — not when the stock has a bad day. If price has not hit your stop, the trade is still valid. Don't check the price every hour.",
        ],
      },
      {
        title: "Common Questions",
        color: T.textSub,
        steps: [
          "Q: Why does a ticker show an error? — Either the ticker symbol is wrong (app auto-removes invalid ones), or there was a data fetch issue. Try scanning again.",
          "Q: The signal says BUY but the stock is clearly going down — Check the Entry Gate. If it says NOT YET or WAIT there is a specific reason shown in the Full analysis panel.",
          "Q: Should I trust every BUY signal? — No. The signal is a starting point. At minimum check: VIX, SPY/QQQ direction, ADX, and R:R before entering.",
          "Q: What is the best setup to look for? — Score +5 or higher + BB Squeeze + RSI under 50 + CMF positive + Entry Gate GO + R:R 3:1+. When all of those align it is the highest probability setup.",
          "Q: Why does it say WAIT — EXTENDED? — RSI is above 65 AND price is above the BB upper band. The stock is overbought. Wait for a pullback to EMA21 before entering.",
          "Q: Why does the Verdict say WATCH when the Score is +7? — The Score is Layer 1 evidence from the 6-core engine (+ phase/setup layers), capped ±15. A score of +7 means strong technical bones. But the Verdict also checks Layer 2 — entry timing. If RSI is 74 and price is above the BB upper band, the stock is extended and chasing it carries real risk. WATCH means the setup is real but the timing is poor. Wait for RSI to reset or a pullback to EMA21.",
          "Q: What is the difference between Score, Gate, and Verdict? — Score = quality of the technical setup (Layer 1). Gate = specific entry conditions checklist (part of Layer 2). Verdict = synthesized conclusion combining all three layers. The Verdict is the one thing to act on. Score and Gate are supporting evidence.",
          "Q: What does NOT YET mean on the Gate? — A critical condition failed. Do not enter. The specific reason is shown in the Full analysis panel.",
          "Q: Why does a ticker show GO gate but have negative CMF? — CMF is an advisory condition, not a critical gate block. The gate has two tiers: critical blocks (bearish 200 SMA structure, RSI extended above BB upper, 4%+ recent drop, score below -3) and advisory conditions (CMF, VWAP, MACD, etc.). A slightly negative CMF like -0.02 or -0.05 does not hard-block entry — it reduces conviction. The CMF conflict badge flags this when CMF is below -0.10. Think of GO as: all hard rules pass. Whether you enter is still your judgment call — check CMF, VIX, and the broader market before acting.",
          "Q: Can a stock show ACCUM and a BLOCKED gate at the same time? — Yes, and it is expected. ACCUM means the stock is building a long-term base near its 52-week low — a signal for patient, slow accumulation over weeks. BLOCKED means a critical short-term swing entry condition failed right now — usually a bearish 200 SMA structure or post-drop cooldown. They answer different questions. ACCUM + BLOCKED means: this stock is on your radar for long-term accumulation, but do not chase it today. Wait for the gate to open before adding size.",
          "Q: Can I use this for day trading? — This app uses daily close data. It is designed for swing trading with typical hold times of 1-3 weeks.",
          "Q: How often should I scan? — Once daily is usually enough for swing trading. Morning before market open to plan your day, or after close to review.",
          "Q: Why are some cells glowing? — Those cells match an AI Watch Trigger that has fired. The condition the AI told you to watch for has been met. Click Summary to see details.",
          "Q: The options strike prices look off — Premiums are estimated from ATR, not live options chain data. Always verify the actual bid/ask in your broker before placing any options trade.",
        ],
      },
      {
        title: "Limitations to Know",
        color: T.yellow,
        steps: [
          "Data source: Real-time daily bars via Yahoo Finance server-side. Prices reflect the most recent trading session.",
          "Options premiums: Estimated from ATR volatility, not live options chain. Always verify in your broker.",
          "No earnings dates: Check earnings calendars separately. Be cautious entering positions within 5 days of a scheduled earnings report.",
          "AI triggers cover 17 measurable conditions. Complex qualitative conditions like sector rotation or news catalysts cannot be auto-checked.",
          "Paper trade first: Every signal system has periods where it underperforms. Paper trade for 4-6 weeks before using real capital.",
        ],
      },
    ],
  },
  {
    id: "features", label: "⚡ Features", icon: "⚡",
    sections: [
      {
        title: "Three-Layer Verdict System",
        color: T.cyan,
        steps: [
          "Every ticker gets a synthesized Verdict pill — BUY NOW, BUY, WATCH, WAIT, OVERSOLD RECOVERY, EMERGING RECOVERY, ACCUMULATION ZONE, STRONG SHORT, BEARISH. Below the pill, an info line shows BULL/BEAR/neutral counts and context badges. This is the complete signal at a glance.",
          "Layer 1 — Setup Quality: The buyScore (±15 cap) from the 6-core engine + phase/setup layers tells you technical structure. The indicator checklist shows bull/bear counts for context. High score = strong bones. This is evidence, not a recommendation.",
          "Layer 2 — Entry Timing: Gate + extension check + AI caution determines whether NOW is the right moment to enter. A great setup at the wrong time shows as WATCH or WAIT.",
          "Layer 3 — Position Management: Exit alerts only show when you have a buy price entered. They answer what to do with a position you already own — completely separate from the entry decision.",
          "Hover any Verdict badge to see the exact reasoning in plain English. The Summary panel shows all three layers in structured order when you click a row.",
          "Run AI Analysis to enhance the Verdict — the AI adds context the mechanical indicators cannot capture, upgrading WATCH verdicts when AI confirms caution.",
        ],
      },
      {
        title: "Market Scanner",
        color: T.cyan,
        steps: [
          "Access via the Scanner tab in the bottom panel.",
          "Scans 360+ stocks across 16 sectors: AI/Semis, Mega Cap, High Growth, Cloud/SaaS, Fintech, Financials, Biotech, Healthcare, EV/Auto, ETFs, Energy, Consumer, Media, Industrials, Real Estate, and Crypto.",
          "Toggle sectors on/off to narrow your scan. Run just AI/Semis in 30 seconds or all 16 sectors in 3-4 minutes. No API keys required.",
          "Filters: ALL / GO / BUY / ★ PRIME / CALLS / PUTS / SQUEEZE / SHORTS / ACCUM / EMERGING / ↩ OB RECOV. Min Score slider removes weak setups.",
          "★ PRIME filter is the highest-conviction filter in the platform. It requires all four to be true simultaneously: Verdict = BUY or BUY NOW, Gate = GO, R:R ≥ 2:1, and Calmar ≥ 1.5. These setups are rare — when they appear, they represent the best risk-adjusted entry the system can identify. In a bull market (VIX < 20): highest conviction entry. In a risk-off market (VIX ≥ 20): valid signal but expect a longer hold (3–5 weeks vs 1–2) and more chop — give the thesis room before cutting.",
          "PUTS and CALLS have split buttons — click the main button to filter the table, click 📊 Analysis for the full breakdown: metric cards, conflict flags, AI narrative, and trade idea cards with strike/expiry/stop/target estimates.",
          "SHORTS filter surfaces stocks with score -7 or lower + bearish 200 SMA structure + negative CMF — all three required for a valid short setup. Positive CMF on a bearish score correctly shows BEARISH instead of STRONG SHORT.",
          "OB RECOV filter surfaces Oversold Recovery candidates — quality names (Calmar ≥ 1) that are temporarily oversold with no bearish 200 SMA structure. Mean reversion setups on Mag 7 and large-cap names.",
          "Click + Watch on any scanner result to instantly add it to your main watchlist without typing.",
          "Results update in real time as batches complete. Sorted by score descending by default.",
        ],
      },
      {
        title: "AI Watch Triggers",
        color: T.purple,
        steps: [
          "After an AI summary runs, triggers are automatically extracted using a fast second call (Haiku or GPT-4o-mini).",
          "17 trigger types: RSI levels, price levels, MACD crossovers, Golden Cross / 200 SMA structure, BB Squeeze fires, CMF direction, RVOL spike, ADX level, VWAP, and EMA21 breaks.",
          "Triggers save permanently per ticker and survive app restarts.",
          "Every scan automatically checks all saved triggers against fresh data — no extra API calls needed.",
          "When a trigger fires: the matching data cell glows (green = BUY, red = SELL, yellow = WATCH). A pulsing badge appears under the ticker name.",
          "You only pay for the AI call once. Triggers check for free on every subsequent scan.",
        ],
      },
      {
        title: "Calmar Ratio & Tradeflow Panel",
        color: T.purple,
        steps: [
          "WILLIAMS %R (−100 to 0): Measures where price is relative to its recent high-low range. Below −80 = oversold (green). Above −20 = overbought (red). The most powerful signal is the TRANSITION — %R crossing back above −80 from deeply oversold is a mean-reversion buy signal. This is especially powerful when RSI is also recovering from oversold. %R crossing below −20 from overbought signals distribution and potential reversal. Adds ±1.5 to score on transition signals.",
          "VOLUME BREAKOUT CONFIRMATION: Validates whether a price breakout is real or a fakeout. CONFIRMED = breakout with 1.5x+ average volume — institutions are participating. FAKEOUT RISK = breakout on below-average volume (< 0.85x) — historically fails 60-70% of the time. VOL SURGE without breakout = possible accumulation or news catalyst. Adds +1.5 to score for confirmed breakouts, −1.5 for fakeouts.",
          "CALMAR RATIO shows in the scanner columns. Formula: Annualized 2-year return ÷ maximum peak-to-trough drawdown over the same 2-year period. This is the proper Calmar calculation — not a 63-day proxy. Standard Calmar uses 3-5 years; we use up to 2 years of data which is what Yahoo Finance provides.",
          "Scale: 3+ = Strong (green), 1–3 = Acceptable (lime), 0.5–1 = Weak (yellow), below 0.5 = Poor (red). Capped at ±9.9. Shows — when drawdown is less than 3% (stock near all-time high with no meaningful pullback to measure). Real-world range for a healthy stock is typically 0.5–4.",
          "Use Calmar as a quality tiebreaker: same Auctus Score but Calmar 2.4 vs 0.3 means the first stock has compounded gains efficiently while controlling drawdowns. The 0.3 stock has either underperformed or had large drawdowns relative to its returns.",
          "Click any ticker row → Summary → 🌊 Tradeflow tab for the full Calmar breakdown with interpretation.",
          "TRADEFLOW VERDICT — Synthesizes CMF, OBV, RVOL, and volume divergence into a flow score (STRONG BUY FLOW to STRONG SELL FLOW) that is completely independent of the Auctus Score.",
          "MONEY FLOW section tells you where institutional money is moving — accumulation, distribution, or neutral.",
          "TREND CONVICTION section shows ADX strength, VWAP position, Parabolic SAR direction, price structure, and RSI divergence.",
          "RELATIVE STRENGTH section shows 63-day performance vs SPY and vs the stock's sector ETF. A stock lagging both SPY and its own sector is a high-risk long regardless of technical score.",
        ],
      },
      {
        title: "Options Play Recommendations",
        color: T.green,
        steps: [
          "When a ticker shows a pulsing CALLS (green) or PUTS (red) full signal, click it to open the Options Play panel.",
          "Three strike tiers shown side by side: Conservative ATM at ~0.50 delta, Aggressive OTM at ~0.35 delta, and Lottery far OTM at ~0.20 delta.",
          "Each tier shows: strike price, estimated premium, breakeven level, and price target.",
          "Expiration auto-calculated: BB Squeeze = 2-3 weeks, Strong trend ADX>30 = 6-8 weeks, Standard = 4-5 weeks. Always lands on a Friday.",
          "Premiums are estimated from ATR — guidance only. Always verify actual bid/ask in your broker.",
          "Five hard rules shown: 50% profit exit, 50% loss cut, 1-2% max risk, always buy with 21+ DTE minimum and exit before 14 DTE, avoid options into earnings.",
        ],
      },
      {
        title: "My Trades — Real Brokerage Journal",
        color: T.yellow,
        steps: [
          "Access via the My Trades tab in the bottom panel. This is your real brokerage trade log — not paper trading.",
          "Click + Log on any scanned ticker row to open the trade entry form. Entry price, stop, targets, Auctus verdict, score, RSI, MACD, CMF, and calls/puts signal are all pre-filled from the scan.",
          "Four trade types: Stock Long, Stock Short, Call Option, Put Option. P&L calculates correctly for each — shorts profit when price falls, options use (exit premium − entry premium) × contracts × 100.",
          "When you exit, click Close Position and enter your exit price and reason. P&L calculates automatically.",
          "Signal Insights tab shows your personal win rate broken down by Auctus verdict label — after 3+ closed trades you see which signals are actually producing wins for YOU.",
          "Tracks: win rate, total P&L, avg win%, avg loss%, R:R. After 20-30 trades you will know which Auctus signals work best with your execution style.",
        ],
      },
      {
        title: "AI Analysis",
        color: "#a855f7",
        steps: [
          "Click Summary on any scanned ticker, then click Ask AI to generate plain-English analysis.",
          "Supports Claude (Anthropic), ChatGPT (OpenAI), and Gemini (Google). Add keys in Settings > AI Provider tab.",
          "If multiple providers are connected, toggle pills appear next to the button so you can switch and compare.",
          "AI summaries save permanently per ticker. Reopening the panel loads the cached summary instantly — you never pay twice.",
          "Date and provider are shown on every saved summary so you know how fresh it is.",
          "Claude costs ~$0.013 per analysis. ChatGPT ~$0.005. Gemini has a free tier.",
        ],
      },
      {
        title: "Keyboard Shortcuts and Power Features",
        color: T.orange,
        steps: [
          "S — Scan All from anywhere (when not typing in an input field)",
          "Escape — Close any open modal (Settings, Guide, Scheduler, Top Movers)",
          "1 = ALL filter, 2 = BUY, 3 = SELL, 4 = SQUEEZE, 5 = ALERTS",
          "Drag the handle on the left of any ticker row to reorder your watchlist. Order saves automatically.",
          "Drag the divider bar between the ticker table and the bottom panel to resize both sections.",
          "Stale data: tickers show a clock icon when data is over 6 hours old. Header shows how long ago the last scan ran.",
          "Default watchlist of 15 tickers loads on first boot. Delete any you do not want — they will not come back.",
        ],
      },
    ],
  },
  {
    id: "options", label: "🎯 Options Guide", icon: "🎯",
    sections: [
      {
        title: "What Are Options — Plain English",
        color: T.green,
        steps: [
          "A call option gives you the RIGHT to buy 100 shares at a fixed price (the strike) before a fixed date (expiration). You pay a premium for that right.",
          "A put option gives you the RIGHT to sell 100 shares at a fixed price before expiration. Puts profit when the stock goes down.",
          "You never have to exercise the option. Most traders just buy the option and sell it later when it's worth more — like trading the contract itself.",
          "Example: NVDA is at $900. You buy a $920 call for $10 (= $1,000 total for 100 shares). If NVDA goes to $960, your call might be worth $45 — you made $3,500 on a $1,000 investment.",
          "The flip side: if NVDA stays at $900 or goes down, your call expires worthless and you lose the $1,000 premium. That is your max loss.",
          "Options move faster than stocks — a 5% move in the stock can mean 50-100% gain or loss on the option. This is why position sizing matters enormously.",
        ],
      },
      {
        title: "Key Terms You Need to Know",
        color: T.cyan,
        steps: [
          "STRIKE PRICE — The price at which you can buy (call) or sell (put) the shares. A $920 call on NVDA means you can buy at $920 regardless of where the stock is.",
          "EXPIRATION DATE — The date the option expires. After this date it is worthless if not in the money. Always buy more time than you think you need.",
          "PREMIUM — The price you pay for the option. If a call costs $10, you pay $1,000 (10 x 100 shares). This is your total risk.",
          "IN THE MONEY (ITM) — Call is ITM when stock price is ABOVE the strike. Put is ITM when stock price is BELOW the strike. ITM options have real intrinsic value.",
          "OUT OF THE MONEY (OTM) — Call is OTM when stock is BELOW the strike. OTM options are cheaper but need a bigger move to profit.",
          "DELTA — How much the option moves per $1 move in the stock. A 0.50 delta call gains $0.50 for every $1 the stock goes up. ATM options are ~0.50 delta.",
          "DTE (Days to Expiration) — How many days until the option expires. The less time, the faster premium decays. Always buy with 21 DTE minimum. Plan to EXIT before 14 DTE — that is your exit trigger, not your entry point.",
          "IV (Implied Volatility) — How expensive the option is. High IV = expensive premium. Low IV = cheap premium. Buy options when IV is LOW, not when it is spiking.",
          "THETA (Time Decay) — Every day that passes, the option loses value even if the stock does not move. This works against you as the buyer. It is why you need the stock to move quickly.",
        ],
      },
      {
        title: "When the App Shows a CALLS Signal",
        color: T.green,
        steps: [
          "A FULL Calls signal means 5 or more of these 8 conditions are met simultaneously — this is a high-probability setup for a call option.",
          "Condition 1 — BB Squeeze active OR price breaking above BB upper band. Squeeze = compressed volatility about to explode. Breakout = momentum confirmation.",
          "Condition 2 — RSI between 40-70 and not overbought. Momentum is building but not exhausted. RSI above 75 = too late, the move may already be priced in.",
          "Condition 3 — MACD bullish (above signal line) or fresh bullish crossover. Momentum is positive and accelerating.",
          "Condition 4 — CMF positive (above 0). Institutional money is flowing IN. Smart money is accumulating, not distributing.",
          "Condition 5 — Golden Cross active (SMA50 above SMA200). Long-term trend is up. You are trading with the tide, not against it.",
          "Condition 6 — ADX above 20. There is actual trend strength behind the move. Weak ADX = choppy market where calls are unreliable.",
          "Condition 7 — Price above VWAP. Institutional bias is bullish. Market participants are paying up for shares.",
          "Condition 8 — RVOL above 1.0. Volume is confirming the move. Price moving up on low volume is suspicious — volume confirmation is required.",
          "PARTIAL signal = 3-4 conditions met. Worth watching but not a full entry. Wait for more confirmation.",
        ],
      },
      {
        title: "When the App Shows a PUTS Signal",
        color: T.red,
        steps: [
          "A FULL Puts signal means 5 or more of these 8 conditions are met — a high-probability setup for a put option or short position.",
          "Condition 1 — 200 SMA structure bearish (SMA50 below SMA200). Long-term trend is down. You are trading with the bearish tide.",
          "Condition 2 — RSI above 60 and falling OR below 40 and continuing lower. Either overbought and reversing, or already in a downtrend.",
          "Condition 3 — MACD bearish (below signal line) or fresh bearish crossunder. Downward momentum is confirmed.",
          "Condition 4 — CMF negative (below 0). Institutional money is flowing OUT. Smart money is distributing into any bounce.",
          "Condition 5 — Price below VWAP. Institutions are selling. Market participants are not willing to pay up.",
          "Condition 6 — ADX above 20 with bearish trend. The downtrend has real strength behind it.",
          "Condition 7 — Price below EMA21. Short-term trend is down. Each bounce is getting sold.",
          "Condition 8 — RVOL above 1.0 on down days. High volume selling confirms distribution, not just a quiet drift lower.",
        ],
      },
      {
        title: "How Strike and Expiration Are Chosen",
        color: T.yellow,
        steps: [
          "Click any ticker row showing a CALLS or PUTS signal, then click the Calls Play or Puts Play tab in the panel that opens.",
          "THREE STRIKE TIERS are shown: Conservative (ATM ~0.50 delta), Aggressive (OTM ~0.35 delta), and Lottery (far OTM ~0.20 delta).",
          "CONSERVATIVE (ATM) — Strike is at or just above the current price. Highest probability of profit. Best for new options traders. Recommended in most cases.",
          "AGGRESSIVE (OTM) — Strike is 3-6% above current price. Cheaper premium, needs a bigger move, but better reward if the stock rips. Use when you have high conviction.",
          "LOTTERY (far OTM) — Strike is 6-10% above current price. Very cheap, very low probability. Only use a tiny position — treat it as a bonus, not a core trade.",
          "EXPIRATION is calculated from volatility: BB Squeeze active = 2-3 weeks (fast explosive move expected). Strong trend with ADX above 30 = 6-8 weeks. Standard setup = 4-5 weeks.",
          "Always buy options that expire on a FRIDAY — the app auto-selects the nearest Friday for the calculated timeframe.",
          "NOTE: Premiums shown are estimated from ATR (volatility measure). They are order-of-magnitude guidance. Always check the actual bid/ask in your broker before placing.",
        ],
      },
      {
        title: "The 5 Rules — Never Break These",
        color: T.orange,
        steps: [
          "RULE 1 — Never risk more than 1-2% of your portfolio on a single options trade. If you have a $50,000 account, max $500-1,000 per trade. Options can go to zero.",
          "RULE 2 — Exit at 50% profit. Do not hold trying to squeeze every last dollar. A 50% gain on options is exceptional. Take it and move on.",
          "RULE 3 — Cut losses at 50% of premium paid. If you paid $1,000 and it drops to $500 — close it. Do not let a $500 loss become a $1,000 loss.",
          "RULE 4 — Always buy with at least 21 DTE minimum. Plan to EXIT before 14 DTE — that is when theta decay accelerates and eats your premium fast. The 14 DTE line is your exit trigger, not your entry point. For swing trades targeting 5-15 days, buy 35-45 DTE so you have a full exit window.",
          "RULE 5 — Earnings require adjusted parameters, not avoidance. IV spikes before earnings inflating premiums, then collapses after (IV crush). If the setup is strong enough to still enter: cut position size to 25-50% of normal, buy deep ITM (0.70+ delta) to minimize vega exposure, or wait until after the announcement and enter on the confirmed directional move with 35+ DTE. Never buy OTM options into earnings — IV crush will destroy them.",
        ],
      },
      {
        title: "Common Mistakes to Avoid",
        color: T.red,
        steps: [
          "Buying calls on a stock that is already extended (RSI above 75, price far above BB upper). You are buying at the top. Wait for a reset.",
          "Buying far OTM options because they are cheap. Cheap options are cheap for a reason — the market does not think the stock will get there. Stick to ATM or slightly OTM.",
          "Holding through earnings when you bought options for a technical setup. Close before the earnings date. The IV crush after earnings will destroy the premium regardless of the move.",
          "Averaging down on a losing options position. Unlike stocks, options expire. Adding to a loser accelerates your losses as expiration approaches.",
          "Not checking VIX before buying options. When VIX is above 25, premiums are inflated. You are overpaying. Wait for VIX to calm down before entering.",
          "Ignoring the Entry Gate. The app shows NOT YET or WAIT on the Gate for a reason. A calls signal with a NOT YET gate means the setup is not fully confirmed — wait.",
        ],
      },
      {
        title: "Score vs Options Signal — Why They Can Disagree",
        color: T.yellow,
        steps: [
          "Q: The score is +6 or higher but there is no Calls signal. Does that mean I should buy calls? — Not automatically. The composite score and the calls signal measure different things.",
          "The SCORE answers: how many of 22 technical indicators are bullish? It weighs trend, momentum, volume, and relative strength. A high score means the stock is technically strong overall.",
          "The CALLS SIGNAL answers: are the 8 specific conditions for a high-probability options trade aligned? These are stricter — BB Squeeze or breakout, RSI in the right zone (35-65), MACD bullish, CMF positive, price above VWAP, Golden Cross, ADX confirming, and RVOL surging.",
          "A stock can score +7 (very bullish) but still have no calls signal because the BB Squeeze has not fired yet, RSI is 72 (overbought), or VWAP is not confirmed. The score says the stock is strong. The calls signal says the options conditions are not perfect yet.",
          "What to do when score is high but no calls signal: the stock is on your radar as bullish. Watch it. When the BB Squeeze fires or RSI resets to 50-55, the calls signal will likely appear. That is the better entry — not chasing a high score before the conditions align.",
          "Q: The score is -6 or lower but there is no Puts signal. Does that mean I should buy puts? — Same logic applies in reverse. The score is broadly bearish but the 8 specific puts conditions are not fully met.",
          "The PUTS SIGNAL requires: bearish 200 SMA structure, MACD bearish crossunder, CMF negative (institutions selling), price below VWAP, ADX confirming downtrend strength, RSI behavior, price below EMA21, and RVOL on down days.",
          "A stock can be deeply bearish (score -7) but still lack a puts signal because the MACD has not crossed under yet, CMF is still slightly positive, or volume is not confirming the breakdown. The score says trouble. The puts signal says wait for the breakdown to fully confirm.",
          "The app flags this mismatch on the ticker row. When you see Bearish · no puts signal yet or Bullish · no calls signal yet, it means the score and the options-specific conditions are not yet aligned. The score tells you direction. The signal tells you when the options trade is ready.",
          "Bottom line: never buy options based on score alone. Wait for the matching options signal to confirm. The score is your early warning. The signal is your green light.",
        ],
      },
    ],
  },
  {
    id: "crypto", label: "₿ Crypto Guide", icon: "₿",
    sections: [
      {
        title: "How Crypto Scanning Differs From Stocks",
        color: "#f59e0b",
        steps: [
          "The ₿ Crypto tab is a separate scanner built specifically for digital assets. It uses the same 20-indicator engine as the stock scanner but with recalibrated thresholds — because crypto behaves very differently from equities.",
          "RSI thresholds: Oversold on crypto is below 25 (not 30 like stocks). Overbought is above 80 (not 70). Crypto regularly hits RSI 15 in bear markets and RSI 90 in bull runs — using stock thresholds would generate constant false signals. When you see RSI 22 on ETH highlighted in green, that is a genuine oversold read at crypto scale.",
          "ATR (volatility) thresholds: SPEC territory on a stock is ATR above 5% per day. On crypto, that is normal. SPEC on crypto means ATR above 12%. A 4% daily move on BTC is a calm day — a 4% move on AAPL is a major event. Size your crypto positions accordingly.",
          "Options signals are disabled for pure crypto coins. The calls/puts system is built around equity options chains — BTC-USD does not have the same options structure. The Crypto Equities tab (COIN, MARA, RIOT, MSTR) uses full stock signal logic including options signals since these are actual stocks.",
        ],
      },
      {
        title: "The BTC Regime Banner — Read This First Every Session",
        color: "#f59e0b",
        steps: [
          "The BTC Regime banner at the top of the Crypto tab shows BTC's current score, RSI, CMF, and regime label (BULL / BEAR / NEUTRAL). This is the single most important thing to check before acting on any crypto signal.",
          "BULL regime: BTC has a golden cross with positive score. Long setups on altcoins have a macro tailwind. BEAR regime: BTC has a bearish 200 SMA structure with negative score — every long carries extra risk, institutions are distributing. NEUTRAL: choppy conditions, lower win rates, smallest sizes.",
          "The most reliable crypto setups occur when: BTC regime is BULL + the coin shows positive CMF (institutional accumulation) + RSI is recovering from oversold (not extended) + Gate is GO. That four-factor alignment is your highest-conviction crypto entry.",
        ],
      },
      {
        title: "Coins vs Equities vs Star Watchlist",
        color: T.cyan,
        steps: [
          "🪙 Coins tab: Pure spot crypto — BTC, ETH, SOL, AVAX, LINK, etc. Crypto-calibrated RSI thresholds (25/80). The 'vs BTC' column shows each coin's 3-month performance relative to Bitcoin — positive means outperforming BTC (relative strength leader).",
          "📈 Equities tab: Stocks that move like crypto — COIN, MARA, RIOT, MSTR, HUT, IBIT, FBTC. Standard stock signal logic applies including options signals. Use these for crypto exposure through a regular brokerage.",
          "★ Watchlist tab: Your personal crypto watchlist. Click '+ Watch' on any coin to add it here. Watched coins show as full cards with live score, RSI, CMF, and gate. Completely separate from the main stock watchlist — crypto thresholds would be misapplied there.",
          "The detail drawer opens on any row click. Shows full indicator grid, targets/stops (wider ATR multiplier for crypto volatility), and the ✦ AI Analysis button which gives a crypto-specific assessment including BTC correlation risk.",
        ],
      },
      {
        title: "Crypto Position Sizing",
        color: T.orange,
        steps: [
          "Crypto is not stocks. No single crypto position should exceed 2-3% of your total portfolio. For high-beta altcoins (AVAX, SOL, smaller caps), cap at 1-1.5%. Even with a perfect signal stack, crypto's sensitivity to macro events means black swans are more frequent than in equities.",
          "The SPEC label means ATR is above 12% per day. At that volatility, a 2x ATR stop means your stop is 24% below entry. On a 3% portfolio position that is 0.72% of total capital at risk — manageable. On a 10% position it is 2.4% — far too much.",
          "BTC and ETH are the only two crypto assets where larger allocation makes sense — deepest liquidity, most institutional support, lowest individual project risk. Everything else is higher risk by definition.",
          "Never hold through earnings on crypto equities (COIN, MARA, RIOT). These are stocks and they gap hard on quarterly reports. Always check earnings dates before entering crypto equity positions.",
        ],
      },
    ],
  },
  {
    id: "bull", label: "🐂 Bull Market", icon: "🐂",
    sections: [
      {
        title: "How to Know You're In a Bull Market",
        color: T.green,
        steps: [
          "The single most reliable indicator: SPY and QQQ both have a golden cross (SMA50 above SMA200) AND are trading above EMA21. When both are true simultaneously, you are in a bull market and should be looking for long entries aggressively.",
          "Auctus signals this in the Regime Banner at the top of your watchlist. BULL regime means the macro trend is up — institutions are accumulating, not distributing. Every long trade has a tailwind.",
          "Secondary confirmation: VIX below 20 and falling, CMF positive on SPY, Fear & Greed above 50, and more than 60% of your watchlist showing golden crosses.",
          "Bull markets are not straight lines — they have corrections of 5-15% along the way. These are buying opportunities, not reasons to exit. The key is distinguishing a normal pullback from a trend reversal. A pullback holds above the SMA50. A reversal breaks it.",
          "Bearish 200 SMA structure on SPY is your exit signal. Do not hold longs through it — institutions are telling you the trend has changed.",
        ],
      },
      {
        title: "Making the Most of a Bull Market",
        color: T.green,
        steps: [
          "SIZE UP. Bull markets are when you make most of your annual returns. This is not the time for 1% positions. Move toward your maximum allocation (3-5% per position) on high-conviction setups.",
          "BUY PULLBACKS TO EMA21. In a strong bull market, price repeatedly bounces off EMA21. Every dip to EMA21 with positive CMF and RSI between 40-55 is a textbook entry. These are the setups Auctus flags as BUY or BUY NOW.",
          "HOLD WINNERS LONGER. The biggest mistake in bull markets is selling too early. A stock with a golden cross, positive CMF, and rising ADX can trend for weeks or months. Use a trailing stop at 2xATR instead of a fixed target.",
          "ADD TO WINNERS. When a position is up 10-15% and the signal is still BUY NOW with gate GO, consider adding to the position. Averaging up in a bull market is a professional strategy — it is the opposite of averaging down in a bear market.",
          "ROTATE INTO LEADERS. Bull markets have leadership sectors. Use the sector rotation banner to identify which sectors are outperforming. Put your capital in the leaders, not the laggards. If XLK is running and XLU is flat, your money belongs in tech.",
        ],
      },
      {
        title: "Bull Market Trade Strategy",
        color: T.cyan,
        steps: [
          "SHARES — Ideal in bull markets. Full 3-5% position size. Hold for 2-6 weeks on strong setups. Use 2xATR trailing stop to ride the trend. Do not exit just because RSI touches 65 — in a bull market RSI can stay elevated for weeks.",
          "CALLS — Best in bull markets when VIX is below 20. Premiums are cheap and stocks move fast. Score +7 or higher + BB Squeeze + Gate GO = highest conviction call setup. Pick expiry 35-45 DTE. Take profit at 50-100% gain, do not let winners become losers.",
          "COVERED CALLS — Sell calls on positions that have run 20%+ and stalled. Collect premium while you wait for the next leg up. Best when RSI is above 70 and price is above BB upper — the stock needs to rest before continuing.",
          "THE BEST BULL MARKET SETUP: Golden cross + score +7 or higher + BB Squeeze loading + RSI between 40-55 (not extended) + CMF above +0.10 + Gate GO + VIX below 18. This combination historically has the highest follow-through rate of any setup Auctus detects.",
        ],
      },
      {
        title: "Key Auctus Signals in a Bull Market",
        color: T.purple,
        steps: [
          "REGIME BANNER — BULL + LOW FEAR means maximum aggression. Every BUY NOW signal in this environment should be acted on at full size.",
          "GOLDEN CROSS — The primary bull market signal. When this fires on a stock, Auctus opens all long entry gates. A golden cross with positive CMF and score +5 or higher is the highest-conviction long setup.",
          "BB SQUEEZE — Bollinger Band squeeze means volatility is coiling for a big move. In a bull market, squeezes resolve upward. The ⚡ SQUEEZE badge in the scanner is your early warning that a breakout is coming.",
          "CMF ABOVE +0.10 — Strong institutional accumulation. Smart money is buying aggressively at current levels. This is the fuel behind sustained trends. When CMF is above +0.15, a move is likely imminent.",
          "EMERGING RECOVERY verdict — Fires when a stock has bearish 200 SMA structure but score is turning positive and CMF is turning green. In a bull market, these are early entries before the golden cross confirms. Institutions start buying before the moving averages cross.",
          "PRIME SETUP badge — The rarest signal in Auctus. Fires only when score, gate, CMF, ADX, and R:R all align simultaneously. In a bull market, PRIME setups are the highest probability trades you will see.",
          "ACCUMULATION verdict — Fires on quality names near 52-week lows with positive CMF. In a bull market these are deep value opportunities — names that got left behind in the rally. When the golden cross fires on an ACCUM setup, it can be a 30-50% mover.",
        ],
      },
      {
        title: "What to Avoid in a Bull Market",
        color: T.orange,
        steps: [
          "DO NOT short in a bull market. Even weak stocks drift higher when the market is rising. Short squeezes are violent and frequent. Save your puts and shorts for bear market conditions.",
          "DO NOT wait for the perfect entry. In a bull market, good enough is good enough. Waiting for RSI to pull back from 60 to 45 means missing a 10% move. Enter on the first GO signal and manage risk with your stop.",
          "DO NOT ignore earnings dates. Bull markets amplify earnings gaps — a miss in a bull market can still drop a stock 15-20%. The app shows earnings proximity warnings. Always check before entering options positions.",
          "DO NOT over-diversify. A bull market rewards concentration. 6-8 positions in the strongest sectors beats 20 positions spread thin. More positions means more to monitor and less conviction per trade.",
          "DO NOT sell winners too early. The most common bull market mistake. A stock showing BUY NOW with a fresh MACD crossover, positive CMF, and golden cross can run for weeks. Let the trailing stop do the work — not your emotions.",
        ],
      },
      {
        title: "Bull Market Checklist — Before Every Trade",
        color: T.yellow,
        steps: [
          "1. Check Regime Banner. If BULL, move to full position sizes (3-5%).",
          "2. Check VIX. Below 18 = ideal. Below 20 = good. Above 25 = reduce size even in a bull market.",
          "3. Check SPY and QQQ. Both should be above EMA21 and CMF positive before taking aggressive longs.",
          "4. For calls: score +7 or higher + BB Squeeze or MACD crossover + RSI 35-65 + Gate GO + VIX below 20.",
          "5. For shares: golden cross + score +4 or higher + CMF positive + Gate GO or CLOSE.",
          "6. Set a trailing stop at 2xATR — not a fixed stop. Let winners run in a bull market.",
          "7. Check earnings dates. Never hold options through earnings even in a bull market.",
          "8. After entry, give the trade 5 trading days minimum before questioning the thesis — unless your stop is hit.",
        ],
      },
    ],
  },
  {
    id: "bear", label: "🐻 Bear Market", icon: "🐻",
    sections: [
      {
        title: "How to Know You're In a Bear Market",
        color: T.red,
        steps: [
          "The single most reliable indicator: SPY and QQQ both have bearish 200 SMA structure (SMA50 below SMA200) AND are trading below EMA21. When both of these are true simultaneously, you are in a bear market until proven otherwise.",
          "Auctus signals this clearly in the Regime Banner at the top of your watchlist. BEAR regime means the macro trend is down — institutions are distributing, not accumulating. Every long trade carries extra risk in this environment.",
          "Secondary confirmation: VIX above 25 consistently (not just a spike), CMF negative on SPY, and more than 60% of your watchlist showing bearish 200 SMA structure.",
          "Bear markets are not crashes — they are sustained downtrends that can last months. The S&P has historically lost 30-50% in bear markets. The most dangerous move is averaging down into a falling market without a macro reversal signal.",
          "The golden cross on SPY is your all-clear signal. Do not declare the bear market over until SPY reclaims its SMA50, then SMA200, with positive CMF. Early reversals fail more often than they succeed.",
        ],
      },
      {
        title: "Surviving a Bear Market — What Not to Do",
        color: T.orange,
        steps: [
          "DO NOT average down on positions just because a stock looks cheap. In a bear market, cheap gets cheaper. A stock down 40% can go down another 40%. Price alone is not a reason to buy.",
          "DO NOT chase oversold bounces without macro confirmation. RSI below 30 in a bear market is not a buy signal — it is a warning that selling is intense. Bounces from oversold levels fail 60-70% of the time in sustained downtrends.",
          "DO NOT hold options through a bear market expecting a quick recovery. Options decay daily. A bear market rally is not the same as a trend reversal. Options need direction AND timing.",
          "DO NOT ignore your stops. Bear markets are where most retail traders blow up their accounts — not by being wrong on direction, but by holding losers too long hoping for recovery. Honor your stops.",
          "DO NOT go to 100% cash and feel safe. Inflation erodes cash in a prolonged downturn. The goal is capital preservation with selective deployment, not total inaction.",
          "DO reduce position sizes. In a bear market, take 25-50% of your normal position size on any long. You will be wrong more often — smaller size keeps you alive.",
        ],
      },
      {
        title: "Making Money in a Bear Market",
        color: T.cyan,
        steps: [
          "PUTS on weak stocks: Bear markets create sustained put opportunities. Look for bearish 200 SMA structure + score below -5 + CMF negative + price below EMA21. Auctus surfaces these with the PUTS filter in the scanner. Never buy puts against a golden cross — even in a bear market, strong stocks fight the tape.",
          "SHORT SELLING: Auctus flags STRONG SHORT when score is -9 or below with bearish 200 SMA structure confirmed. These are the highest-conviction short setups. Keep position sizes small — short squeezes in bear markets can be violent.",
          "SECTOR ROTATION into defensives: Bear markets do not hurt all sectors equally. Utilities (XLU), consumer staples (XLP), healthcare (XLV), and gold (GLD) historically outperform. Look for green regime signals in these sectors even when tech is red.",
          "CASH IS A POSITION: Sitting 60-70% cash in a bear market and waiting for a reversal signal is a valid and often superior strategy. Preserving capital means you have dry powder when the golden cross fires again.",
          "ACCUMULATION ZONE entries: Auctus flags ACCUM on high-quality names that have reached their 52-week low with institutional buying showing (positive CMF). These are long-term entry opportunities — not swing trades. Scale in slowly, expect 3-6 month holds.",
          "COVERED CALLS on existing positions: If you hold shares that are sideways or slowly declining, selling covered calls 1-2 strikes above current price collects premium while you wait. Best when VIX is elevated — higher VIX means higher premiums.",
        ],
      },
      {
        title: "Key Auctus Signals to Watch in a Bear Market",
        color: T.purple,
        steps: [
          "REGIME BANNER — Check this first every session. BEAR + HIGH FEAR means maximum caution. Any BUY NOW signal in this regime should be treated as a half-size trade at best.",
          "200 SMA STRUCTURE (BEARISH) — This is the primary bear market indicator in Auctus. When it fires on a stock, Auctus blocks all long entry gates. Do not fight this signal. A stock with bearish 200 SMA structure can still bounce intraday but the primary trend is down.",
          "CMF BELOW -0.10 — This is Auctus's institutional distribution signal. Negative CMF means the smart money is selling into any strength. Combine with bearish 200 SMA structure and you have a confirmed put setup.",
          "STRONG SHORT verdict — This is Auctus's highest conviction bearish signal. Requires: bearish 200 SMA structure + score -9 or below + CMF negative + price below EMA21. These appear in the scanner under the PUTS filter.",
          "VIX SPIKE — When VIX spikes above 30, Auctus's regime banner goes to HIGH FEAR. This is historically not the time to sell — it is often the time to start watching for reversal. Extreme fear at 52W lows + RSI below 25 = the setups that define generational buying opportunities.",
          "EMERGING RECOVERY verdict — This fires when a stock has bearish 200 SMA structure but score is turning positive and CMF is turning green. This is Auctus's early warning that institutions are buying before the golden cross. Small entry only — confirmation required.",
          "CALMAR RATIO — Visible in the scanner. High Calmar (above 1.5) means the stock has historically recovered well from drawdowns. In a bear market, these are the names to accumulate at 52W lows — they have the track record to recover.",
        ],
      },
      {
        title: "Bear Market Checklist — Before Every Trade",
        color: T.yellow,
        steps: [
          "1. Check the Regime Banner. If it says BEAR, reduce all position sizes by 50%.",
          "2. Check VIX. Above 25 = reduce size. Above 35 = longs only at 52W lows with positive CMF.",
          "3. Check SPY and QQQ in your watchlist. Both need positive CMF and price above EMA21 before taking any aggressive long.",
          "4. For puts: bearish 200 SMA structure confirmed + CMF below -0.10 + score below -5. All three required.",
          "5. For longs: golden cross OR ACCUM signal + CMF positive + gate GO. No exceptions in a bear market.",
          "6. Set your stop before you enter. In a bear market use 1x ATR stop instead of 2x — tighter stops, smaller sizes.",
          "7. Have an exit plan for both directions. Know at what price you are wrong. Know at what price you take profit.",
          "8. Check earnings dates. Bear markets amplify earnings reactions — gaps of 15-20% are common. Never hold options through earnings in a bear market.",
        ],
      },
    ],
  },
];

function HelpPanel({ onClose, initialTab = "quickstart" }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const active = HELP_TABS.find(t => t.id === activeTab);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 300, display: "flex", alignItems: "stretch", justifyContent: "flex-end" }}>
      {/* Click outside to close */}
      <div style={{ flex: 1 }} onClick={onClose} />

      {/* Panel */}
      <div style={{ width: "min(720px, 95vw)", background: T.surface, borderLeft: `2px solid ${T.border}`, display: "flex", flexDirection: "column", animation: "slideIn 0.25s ease", overflow: "hidden" }}>
        <style>{`@keyframes slideIn { from{transform:translateX(100%)} to{transform:translateX(0)} }`}</style>

        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, background: T.surfaceHi }}>
          <div>
            <div style={{ fontSize: F.xs, fontWeight: 700, color: T.purple, letterSpacing: "0.1em", marginBottom: 4 }}>USER GUIDE</div>
            <h2 style={{ fontSize: F.xl, fontWeight: 700, color: T.text }}>📖 Trade Signal Terminal</h2>
            <div style={{ fontSize: F.sm, color: T.textDim, marginTop: 2 }}>Complete reference guide — from setup to trade execution</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textSub, fontSize: F.xl, cursor: "pointer", padding: 8 }}>✕</button>
        </div>

        {/* Tab bar */}
        <div style={{ display: "flex", gap: 2, padding: "10px 12px", borderBottom: `1px solid ${T.border}`, flexShrink: 0, background: T.bg, overflowX: "auto" }}>
          {HELP_TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ padding: "8px 14px", background: activeTab === tab.id ? `${T.purple}22` : "none", border: `1px solid ${activeTab === tab.id ? T.purple : "transparent"}`, color: activeTab === tab.id ? T.purple : T.textSub, fontSize: F.sm, fontWeight: activeTab === tab.id ? 700 : 400, fontFamily: "inherit", cursor: "pointer", borderRadius: 8, whiteSpace: "nowrap", transition: "all 0.15s" }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {active?.sections.map((section, si) => (
            <div key={si} style={{ marginBottom: 28 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ width: 4, height: 20, borderRadius: 2, background: section.color, flexShrink: 0 }} />
                <h3 style={{ fontSize: F.md, fontWeight: 700, color: T.text }}>{section.title}</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {section.steps.map((step, i) => {
                  const isQ = step.startsWith("Q:");
                  const isNum = /^\d+\./.test(step);
                  return (
                    <div key={i} style={{ display: "flex", gap: 12, padding: "10px 14px", background: T.bg, borderRadius: 8, border: `1px solid ${T.border}`, alignItems: "flex-start" }}>
                      {!isQ && !isNum && (
                        <div style={{ width: 8, height: 8, borderRadius: "50%", background: section.color, marginTop: 5, flexShrink: 0, boxShadow: `0 0 4px ${section.color}44` }} />
                      )}
                      <div style={{ fontSize: F.base, color: isQ ? T.text : T.textSub, lineHeight: 1.6, flex: 1 }}>
                        {isQ ? (
                          <>
                            <span style={{ fontWeight: 700, color: section.color }}>{step.split(" — ")[0]} — </span>
                            <span style={{ color: T.textSub }}>{step.split(" — ").slice(1).join(" — ")}</span>
                          </>
                        ) : step}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Footer */}
          <div style={{ marginTop: 8, padding: "14px 16px", background: `${T.purple}10`, border: `1px solid ${T.purple}33`, borderRadius: 10, fontSize: F.sm, color: T.textDim, lineHeight: 1.7 }}>
            <div style={{ fontWeight: 700, color: T.purple, marginBottom: 6 }}>⚠ Disclaimer</div>
            Trade Signal Terminal is an educational and research tool. It does not provide financial advice. All trading involves risk. Past signal performance does not guarantee future results. Always paper trade before using real capital. The developer is not responsible for trading losses.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── SETTINGS PANEL ───────────────────────────────────────────────────────────
const AI_PROVIDERS = [
  {
    id: "anthropic",
    name: "Claude (Anthropic)",
    color: "#a855f7",
    placeholder: "sk-ant-••••••••••••••••••••••",
    prefix: "sk-ant-",
    url: "console.anthropic.com",
    cost: "~$0.013 per analysis",
    description: "Best reasoning and market analysis. Powers the AI summary button.",
    model: "claude-sonnet-4-20250514",
  },
  {
    id: "openai",
    name: "ChatGPT (OpenAI)",
    color: "#10b981",
    placeholder: "sk-••••••••••••••••••••••••••",
    prefix: "sk-",
    url: "platform.openai.com/api-keys",
    cost: "~$0.005 per analysis",
    description: "Alternative AI provider using GPT-4o for analysis.",
    model: "gpt-4o",
  },
  {
    id: "gemini",
    name: "Gemini (Google)",
    color: "#3b82f6",
    placeholder: "AIza••••••••••••••••••••••••",
    prefix: "AIza",
    url: "aistudio.google.com/app/apikey",
    cost: "Free tier available",
    description: "Google's Gemini Pro model for market analysis.",
    model: "gemini-1.5-pro",
  },
];

// ─── ONBOARDING MODAL ────────────────────────────────────────────────────────
// ─── LOGIN SCREEN ─────────────────────────────────────────────────────────────
// ─── DISCLAIMER MODAL ─────────────────────────────────────────────────────────
function DisclaimerModal({ onAccept }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 20, width: 540, maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 32px 80px rgba(0,0,0,0.5)" }}>
        <div style={{ padding: "28px 32px 24px", borderBottom: `1px solid ${T.border}`, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 6 }}>Important Disclaimer</div>
          <div style={{ fontSize: 13, color: T.textDim }}>Please read and acknowledge before using Auctus Analytics</div>
        </div>
        <div style={{ padding: "24px 32px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginBottom: 24 }}>
            {[
              ["📊 For informational purposes only", "Auctus Analytics is an educational and informational tool. Nothing on this platform constitutes financial advice, investment advice, trading advice, or any other form of professional advice."],
              ["💰 Not financial advice", "The signals, verdicts, entry zones, and AI analysis provided are algorithmic outputs based on technical indicators. They are not personalized recommendations. You are solely responsible for your own investment and trading decisions."],
              ["📉 Past performance disclaimer", "Past performance of any indicator, signal, or pattern does not guarantee future results. Markets are inherently unpredictable. You may lose some or all of your invested capital."],
              ["🔢 Entry zones are estimates", "Recommended entry zones are calculated algorithmically from price data and volatility metrics. They are not guaranteed entry or exit points. Always verify with your broker and use your own judgment before placing any order."],
              ["🤖 AI analysis limitations", "AI-generated analysis is based on technical data only. It does not account for news events, earnings, macroeconomic conditions, or other factors that may materially affect stock prices."],
              ["👨‍💼 Consult a professional", "Always consult a qualified and licensed financial advisor before making investment decisions, especially with significant capital. This platform is not a substitute for professional financial advice."],
              ["📋 Options risk", "Options trading involves significant risk and is not suitable for all investors. Options can expire worthless, resulting in a total loss of premium paid. Always understand the full risk before trading options."],
            ].map(([title, desc]) => (
              <div key={title} style={{ display: "flex", gap: 12, padding: "12px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                <div style={{ minWidth: 24, fontSize: 16 }}>{title.split(" ")[0]}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>{title.split(" ").slice(1).join(" ")}</div>
                  <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.5 }}>{desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ padding: "12px 16px", background: `${T.orange}10`, border: `1px solid ${T.orange}33`, borderRadius: 10, marginBottom: 20, fontSize: 12, color: T.textDim, lineHeight: 1.6 }}>
            By clicking "I Understand & Agree" you acknowledge that you have read this disclaimer, understand the risks involved in trading and investing, and agree that Auctus Analytics and its operators are not liable for any financial losses you may incur.
          </div>
          <button onClick={onAccept}
            style={{ width: "100%", padding: "14px", background: "linear-gradient(135deg, #7c3aed, #d946ef)", border: "none", color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 12 }}>
            I Understand & Agree — Enter Auctus Analytics
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── APP SCREENSHOTS CAROUSEL ────────────────────────────────────────────────
function AppScreensCarousel() {
  const carouselRef = React.useRef(null);
  React.useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    let frame; let pos = 0; const speed = 0.4;
    const step = () => { pos += speed; if (pos >= el.scrollWidth / 2) pos = 0; el.scrollLeft = pos; frame = requestAnimationFrame(step); };
    frame = requestAnimationFrame(step);
    const pause = () => cancelAnimationFrame(frame);
    const resume = () => { frame = requestAnimationFrame(step); };
    el.addEventListener("mouseenter", pause); el.addEventListener("touchstart", pause);
    el.addEventListener("mouseleave", resume); el.addEventListener("touchend", resume);
    return () => { cancelAnimationFrame(frame); el.removeEventListener("mouseenter", pause); el.removeEventListener("touchstart", pause); el.removeEventListener("mouseleave", resume); el.removeEventListener("touchend", resume); };
  }, []);

  const ScreenCard = ({ title, children }) => (
    <div style={{ flexShrink: 0, width: 300, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden" }}>
      <div style={{ background: T.surfaceHi, padding: "9px 12px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 5 }}>
        {["#ff5f57","#febc2e","#28c840"].map(c => <div key={c} style={{ width: 8, height: 8, borderRadius: "50%", background: c }} />)}
        <span style={{ marginLeft: 6, fontSize: 9, color: T.textDim, fontFamily: "sans-serif" }}>{title}</span>
      </div>
      <div style={{ padding: "12px 14px" }}>{children}</div>
    </div>
  );

  const cards = [
    <ScreenCard key="levels" title="Levels — Entry Gate + Price Levels">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div><div style={{ fontSize: 17, fontWeight: 900, color: T.text }}>AMD</div><div style={{ fontSize: 10, color: T.textDim }}>$217.50 +6.74% ● LIVE</div></div>
        <div style={{ textAlign: "right" }}><div style={{ fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 4, background: `${T.yellow}22`, color: T.yellow, border: `1px solid ${T.yellow}44` }}>WAIT</div><div style={{ fontSize: 13, fontWeight: 900, color: T.green, marginTop: 2 }}>+7.5</div></div>
      </div>
      <div style={{ background: T.bg, borderRadius: 7, padding: "8px 10px", marginBottom: 8 }}>
        <div style={{ fontSize: 8, fontWeight: 800, color: T.orange, letterSpacing: "0.08em", marginBottom: 6 }}>ENTRY GATE — NOT YET · 11/13</div>
        {[["200 SMA structure OK", true], ["Not extended (RSI+BB)", true], ["No post-drop cooldown", false], ["CMF not distributing", true], ["Gap stabilized", false]].map(([l, pass]) => (
          <div key={l} style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 4 }}>
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: pass ? T.green : T.red, flexShrink: 0 }} />
            <span style={{ fontSize: 10, color: pass ? T.textSub : T.red, fontFamily: "sans-serif" }}>{l}</span>
          </div>
        ))}
      </div>
      {[["SMA 50","$214.46",T.cyan],["EMA 21","$204.61",T.cyan],["Fib 61.8%","$233.43",T.textSub],["ATR (14)","$8.65",T.orange]].map(([l,v,c]) => (
        <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 11, color: T.textDim, fontFamily: "sans-serif" }}>{l}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono',monospace" }}>{v}</span>
        </div>
      ))}
    </ScreenCard>,
    <ScreenCard key="analysis" title="Analysis — 22-Indicator Grid + Volume">
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 8 }}>
        {[["Score","+7.5",T.green],["Signals","8▲·4▼",T.textSub],["RSI","52.4",T.text],["MACD","BULL ▲",T.green],["CMF","0.066",T.green],["RVOL","0.47x",T.red],["Weekly","STRONG UP",T.green],["Trend","Golden ▲",T.green]].map(([l,v,c]) => (
          <div key={l} style={{ background: T.bg, borderRadius: 5, padding: "5px 7px" }}>
            <div style={{ fontSize: 8, color: T.textDim, marginBottom: 2, fontFamily: "sans-serif" }}>{l}</div>
            <div style={{ fontSize: 10, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono',monospace" }}>{v}</div>
          </div>
        ))}
      </div>
      <div style={{ background: T.bg, borderRadius: 7, padding: "8px 10px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}><span style={{ fontSize: 10, color: T.textDim, fontFamily: "sans-serif" }}>Relative Volume</span><span style={{ fontSize: 12, fontWeight: 900, color: T.red, fontFamily: "'JetBrains Mono',monospace" }}>0.47x LOW</span></div>
        <div style={{ height: 4, background: T.border, borderRadius: 2, overflow: "hidden" }}><div style={{ height: 4, width: "16%", background: T.red, borderRadius: 2 }} /></div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 5, fontSize: 10, color: T.textDim, fontFamily: "sans-serif" }}><span>Today: 15.29M</span><span>Avg: 32.76M</span></div>
      </div>
    </ScreenCard>,
    <ScreenCard key="ai" title="AI — Senior Analyst Commentary">
      <button style={{ width: "100%", padding: "9px", background: "linear-gradient(135deg,#7c3aed,#d946ef)", border: "none", color: "#fff", fontSize: 11, fontWeight: 700, borderRadius: 7, cursor: "pointer", fontFamily: "inherit", marginBottom: 10 }}>↻ Refresh Analysis</button>
      <div style={{ fontSize: 8, fontWeight: 700, color: T.purple, letterSpacing: "0.08em", marginBottom: 6 }}>✦ AUCTUS ANALYSIS — SENIOR ANALYST</div>
      <div style={{ background: `${T.purple}0a`, border: `1px solid ${T.purple}33`, borderRadius: 7, padding: "8px 10px" }}>
        {["AMD is in a strong uptrend but showing classic overextension signals above the upper Bollinger Band.","OBV bearish divergence with CMF slightly negative — institutions aren't participating in this push.","Watch for a pullback to VWAP around $203 for entry. The trade: wait for the pullback or pass."].map((t,i) => (
          <div key={i} style={{ fontSize: 10, color: T.textSub, lineHeight: 1.6, marginBottom: 5, paddingLeft: 7, borderLeft: `2px solid ${T.purple}44`, fontFamily: "sans-serif" }}>{t}</div>
        ))}
      </div>
    </ScreenCard>,
    <ScreenCard key="scanner" title="Scanner — 884 Tickers · 20 Sectors">
      <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 8 }}>
        {["ALL","BUY","CALLS ⚡","PUTS ↓"].map((f,i) => (
          <span key={f} style={{ fontSize: 9, padding: "2px 7px", borderRadius: 10, background: i===0?`${T.purple}22`:T.bg, border: `1px solid ${i===0?T.purple:T.border}`, color: i===0?T.purple:T.textDim, fontFamily: "sans-serif" }}>{f}</span>
        ))}
      </div>
      {[["NVDA","+11",T.green,"BUY NOW","GO"],["CRDO","+9",T.green,"BUY NOW","GO"],["ALAB","+8",T.green,"BUY NOW","GO"],["PLTR","+6",T.yellow,"WATCH","CLOSE"],["COIN","-11",T.red,"STRONG SHORT","WAIT"]].map(([sym,score,c,verdict,gate]) => (
        <div key={sym} style={{ display: "flex", alignItems: "center", gap: 5, padding: "6px 0", borderBottom: `1px solid ${T.border}` }}>
          <span style={{ fontSize: 11, fontWeight: 900, color: c, width: 40, fontFamily: "'JetBrains Mono',monospace" }}>{sym}</span>
          <span style={{ fontSize: 9, fontWeight: 800, padding: "2px 5px", borderRadius: 3, background: `${c}18`, color: c, border: `1px solid ${c}33`, flex: 1, textAlign: "center" }}>{verdict}</span>
          <span style={{ fontSize: 10, fontWeight: 800, color: c, width: 24, textAlign: "right", fontFamily: "'JetBrains Mono',monospace" }}>{score}</span>
          <span style={{ fontSize: 9, fontWeight: 700, color: gate==="GO"?T.green:gate==="CLOSE"?T.yellow:T.red, width: 30, textAlign: "right" }}>{gate}</span>
        </div>
      ))}
    </ScreenCard>,
    <ScreenCard key="tradeflow" title="Tradeflow — Institutional Money Flow">
      {[["Strong institutional accumulation","CMF +0.142 — heavy net buying. Institutions aggressively accumulating.",T.green],["OBV confirming uptrend","Volume rising with price — institutional participation validates the move.",T.green],["CMF Bullish Divergence","Price lower low but CMF improved — sellers exhausting. Early reversal.",T.green],["Above-average volume (1.8x)","Enough participation to sustain a directional move.",T.cyan]].map(([label,detail,c]) => (
        <div key={label} style={{ padding: "7px 9px", background: T.bg, borderRadius: 6, marginBottom: 5, borderLeft: `2px solid ${c}` }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: c, marginBottom: 2 }}>{label}</div>
          <div style={{ fontSize: 10, color: T.textDim, lineHeight: 1.5, fontFamily: "sans-serif" }}>{detail}</div>
        </div>
      ))}
    </ScreenCard>,
  ];

  return (
    <div ref={carouselRef} style={{ display: "flex", gap: 14, overflowX: "hidden", userSelect: "none" }}>
      {[...cards, ...cards]}
    </div>
  );
}

function LoginScreen() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showLogin, setShowLogin] = useState(false);

  // Inject responsive CSS — must be before any early returns (Rules of Hooks)
  React.useEffect(() => {
    const id = "auctus-landing-css";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent = `
      .lp-hero h1 { font-size: 54px; }
      .lp-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
      .lp-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
      .lp-grid-4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 10px; }
      .lp-grid-4c { display: grid; grid-template-columns: repeat(4,1fr); gap: 10px; }
      .lp-grid-stats { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr 1fr; gap: 20px; }
      .lp-side-by-side { display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }
      .lp-feat-title { font-size: 15px; }
      .lp-feat-desc { font-size: 14px; }
      .lp-body { font-size: 14px; line-height: 1.75; }
      .lp-label { font-size: 12px; }
      .lp-card-val { font-size: 13px; }
      .lp-gate-name { font-size: 12px; }
      .lp-gate-desc { font-size: 11px; }
      .lp-list-item { font-size: 12px; }
      .lp-score-desc { font-size: 14px; }
      .lp-verdict-name { font-size: 11px; width: 110px; }
      .lp-verdict-desc { font-size: 12px; }
      .lp-section-pad { padding: 80px 5%; }
      @media (max-width: 768px) {
        .lp-hero h1 { font-size: 34px !important; }
        .lp-grid-3 { grid-template-columns: 1fr !important; }
        .lp-grid-2 { grid-template-columns: 1fr !important; gap: 16px !important; }
        .lp-grid-4 { grid-template-columns: 1fr 1fr !important; }
        .lp-grid-4c { grid-template-columns: 1fr 1fr !important; }
        .lp-grid-stats { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
        .lp-side-by-side { grid-template-columns: 1fr !important; gap: 24px !important; }
        .lp-feat-title { font-size: 17px !important; }
        .lp-feat-desc { font-size: 15px !important; }
        .lp-body { font-size: 15px !important; }
        .lp-label { font-size: 13px !important; }
        .lp-card-val { font-size: 14px !important; }
        .lp-gate-name { font-size: 14px !important; }
        .lp-gate-desc { font-size: 13px !important; }
        .lp-list-item { font-size: 14px !important; }
        .lp-score-desc { font-size: 15px !important; }
        .lp-verdict-name { font-size: 12px !important; width: 100px !important; }
        .lp-verdict-desc { font-size: 13px !important; }
        .lp-section-pad { padding: 48px 4% !important; }
        .lp-hide-mobile { display: none !important; }
        .lp-cta-row { flex-direction: column !important; align-items: stretch !important; }
        .lp-cta-row button { width: 100% !important; text-align: center !important; }
        .lp-nav-links { display: none !important; }
        .lp-nav-mobile { display: flex !important; }
        .lp-pricing { grid-template-columns: 1fr !important; }
        .lp-verdict-row { flex-wrap: wrap !important; }
        .lp-score-range { grid-template-columns: 1fr 1fr !important; }
        .lp-cta-row button { padding: 12px 20px !important; font-size: 15px !important; }
        .lp-nav-cta { padding: 8px 16px !important; font-size: 13px !important; }
      }
    `;
    document.head.appendChild(style);
    return () => { try { document.head.removeChild(style); } catch {} };
  }, []);

  // Landing page always dark — override theme
  const T = {
    bg: "#07070f", surface: "#0d0d1a", surfaceHi: "#131320",
    border: "#1e1e2e", borderHi: "#2a2a3e",
    text: "#f1f5f9", textSub: "#94a3b8", textDim: "#475569",
    green: "#22c55e", red: "#ef4444", orange: "#f59e0b",
    yellow: "#eab308", cyan: "#06b6d4", purple: "#a78bfa",
    blue: "#3b82f6",
  };

  const signInWithGoogle = async () => {
    setLoading(true); setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin }
    });
    if (error) { setError(error.message); setLoading(false); }
  };

  const Logo = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width="32" height="36" viewBox="0 0 64 68">
        <defs><linearGradient id="lg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#6b21a8"/><stop offset="50%" stopColor="#7c3aed"/><stop offset="100%" stopColor="#d946ef"/></linearGradient></defs>
        <polygon points="4,64 22,8 32,36 18,36 20,42 34,42 44,64" fill="url(#lg)" opacity="0.9"/>
        <polygon points="28,64 42,8 60,64 53,64 42,28 31,64" fill="url(#lg)"/>
      </svg>
      <div>
        <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: "1px", background: "linear-gradient(135deg, #7c3aed, #d946ef)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>AUCTUS</div>
        <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: "3px", color: "#6b7280", marginTop: -2 }}>ANALYTICS</div>
      </div>
    </div>
  );

  // ── Login Modal ──
  if (showLogin) return (
    <div style={{ height: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ width: 420, padding: "48px 40px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 20, textAlign: "center" }}>
        <button onClick={() => setShowLogin(false)} style={{ position: "absolute", marginLeft: -380, marginTop: -30, background: "none", border: "none", color: T.textDim, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>← Back</button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 28 }}><Logo /></div>
        <div style={{ fontSize: 22, fontWeight: 700, color: T.text, marginBottom: 8 }}>Welcome back</div>
        <div style={{ fontSize: 14, color: T.textDim, marginBottom: 32, lineHeight: 1.6 }}>Sign in to access your watchlist, signals, and AI analysis</div>
        <button onClick={signInWithGoogle} disabled={loading}
          style={{ width: "100%", padding: "14px 20px", background: loading ? T.surfaceHi : "#fff", border: `1px solid ${T.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 12, cursor: loading ? "not-allowed" : "pointer", fontSize: 15, fontWeight: 600, color: "#1a1a1a", fontFamily: "inherit" }}>
          <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          {loading ? "Signing in..." : "Continue with Google"}
        </button>
        {error && <div style={{ marginTop: 16, padding: "10px 14px", background: `${T.red}15`, border: `1px solid ${T.red}44`, borderRadius: 8, fontSize: 13, color: T.red }}>{error}</div>}
        <div style={{ marginTop: 24, fontSize: 12, color: T.textDim, lineHeight: 1.7 }}>7-day free trial · No credit card required · Cancel anytime</div>
      </div>
    </div>
  );


  const S = {
    page: { background: T.bg, fontFamily: "'Inter', system-ui, sans-serif", color: T.text, minHeight: "100vh" },
    nav: { position: "sticky", top: 0, zIndex: 100, background: `${T.bg}ee`, backdropFilter: "blur(12px)", borderBottom: `1px solid ${T.border}`, padding: "0 5%", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64 },
    section: { padding: "80px 5%", maxWidth: 1100, margin: "0 auto" },
    h2: { fontSize: 38, fontWeight: 800, color: T.text, marginBottom: 14, lineHeight: 1.2 },
    sub: { fontSize: 18, color: T.textSub, lineHeight: 1.75, maxWidth: 560 },
    card: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "24px 28px" },
    pill: (c) => ({ display: "inline-block", padding: "4px 14px", borderRadius: 20, background: `${c}18`, border: `1px solid ${c}44`, fontSize: 12, fontWeight: 700, color: c, letterSpacing: "0.06em", marginBottom: 16 }),
    ctaPrimary: { padding: "15px 32px", background: "linear-gradient(135deg, #7c3aed, #9333ea)", border: "none", borderRadius: 10, fontSize: 16, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em" },
    ctaSecondary: { padding: "15px 28px", background: "transparent", border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 16, fontWeight: 600, color: T.textSub, cursor: "pointer", fontFamily: "inherit" },
    mono: { fontFamily: "'JetBrains Mono', monospace" },
  };

  const FeatureCard = ({ emoji, title, desc, accent }) => (
    <div style={{ ...S.card, borderTop: `2px solid ${accent}` }}>
      <div style={{ fontSize: 26, marginBottom: 10 }}>{emoji}</div>
      <div className="lp-feat-title" style={{ fontWeight: 700, color: T.text, marginBottom: 8 }}>{title}</div>
      <div className="lp-feat-desc" style={{ color: T.textSub, lineHeight: 1.7 }}>{desc}</div>
    </div>
  );

  const StatBox = ({ val, label }) => (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 34, fontWeight: 900, ...S.mono, background: "linear-gradient(135deg, #7c3aed, #d946ef)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>{val}</div>
      <div style={{ fontSize: 13, color: T.textDim, marginTop: 3, letterSpacing: "0.05em" }}>{label}</div>
    </div>
  );

  const CheckRow = ({ text }) => (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12 }}>
      <div style={{ width: 20, height: 20, borderRadius: "50%", background: `${T.green}22`, border: `1px solid ${T.green}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
        <span style={{ fontSize: 11, color: T.green, fontWeight: 800 }}>✓</span>
      </div>
      <span className="lp-body" style={{ color: T.textSub, lineHeight: 1.6 }}>{text}</span>
    </div>
  );

  const VerdictBadge = ({ label, color }) => (
    <div style={{ padding: "4px 10px", borderRadius: 6, background: `${color}22`, border: `1px solid ${color}55`, fontSize: 11, fontWeight: 800, color, ...S.mono, display: "inline-block" }}>{label}</div>
  );

  const FAQItem = ({ q, a }) => {
    const [open, setOpen] = React.useState(false);
    return (
      <div style={{ borderBottom: `1px solid ${T.border}`, padding: "18px 0" }}>
        <button onClick={() => setOpen(o => !o)} style={{ width: "100%", background: "none", border: "none", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: T.text, textAlign: "left" }}>{q}</span>
          <span style={{ fontSize: 18, color: T.textDim, marginLeft: 16, flexShrink: 0, transform: open ? "rotate(45deg)" : "none", transition: "transform 0.2s" }}>+</span>
        </button>
        {open && <div style={{ fontSize: 15, color: T.textSub, lineHeight: 1.7, marginTop: 12, paddingRight: 32 }}>{a}</div>}
      </div>
    );
  };

  return (
    <div style={S.page}>

      {/* ── NAV ── */}
      <nav style={S.nav}>
        <Logo />
        <div className="lp-nav-links" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => setShowLogin(true)} className="lp-nav-cta" style={S.ctaSecondary}>Sign In</button>
          <button onClick={() => setShowLogin(true)} className="lp-nav-cta" style={S.ctaPrimary}>Start Free Trial →</button>
        </div>
        {/* Mobile — compact sign in only */}
        <button onClick={() => setShowLogin(true)} className="lp-nav-mobile" style={{ display: "none", padding: "8px 16px", background: "#7c3aed", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 700, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Sign In</button>
      </nav>
      <div className="lp-hero" style={{ ...S.section, paddingTop: 100, paddingBottom: 80, textAlign: "center" }}>
        <div style={S.pill(T.purple)}>SWING TRADING INTELLIGENCE</div>
        <h1 style={{ fontWeight: 900, lineHeight: 1.1, marginBottom: 20, maxWidth: 720, margin: "0 auto 20px", color: T.text }}>
          Stop guessing.<br/>
          <span style={{ background: "linear-gradient(135deg, #7c3aed, #d946ef)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Start knowing.</span>
        </h1>
        <p style={{ ...S.sub, margin: "0 auto 40px", textAlign: "center" }}>
          AI-powered swing trading signals for stocks and crypto. A 22-indicator engine reads every ticker and delivers one clear verdict — no noise, no guesswork.
        </p>
        <div className="lp-cta-row" style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
          <button onClick={() => setShowLogin(true)} style={{ ...S.ctaPrimary, fontSize: 17, padding: "17px 36px" }}>Start 7-Day Free Trial →</button>
          <button onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })} style={{ ...S.ctaSecondary, fontSize: 17 }}>See How It Works</button>
        </div>
        <div style={{ marginTop: 16, fontSize: 14, color: T.textDim }}>No credit card required · Cancel anytime · Web + Android</div>

        {/* Mock signal card */}
        <div style={{ maxWidth: 600, margin: "60px auto 0", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "20px 24px", textAlign: "left" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 900, ...S.mono, color: T.text }}>NVDA</div>
              <div style={{ fontSize: 13, color: T.textDim }}>NVIDIA Corporation</div>
            </div>
            <VerdictBadge label="◆ BUY NOW" color={T.green} />
          </div>
          <div className="lp-grid-4c" style={{ gap: 8, marginBottom: 12 }}>
            {[["SCORE", "+11", T.green], ["RSI", "52.4", T.text], ["CMF", "+0.142", T.green], ["GATE", "GO", T.green]].map(([l, v, c]) => (
              <div key={l} style={{ background: T.bg, borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>{l}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: c, ...S.mono }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {["● HIGH", "↑↑ MTF", "⚡ SQZ", "◎ AI ✓"].map(b => (
              <span key={b} style={{ fontSize: 9, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: `${T.purple}18`, border: `1px solid ${T.purple}33`, color: T.purple }}>{b}</span>
            ))}
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: T.textDim }}>T1 $142.80 · T2 $158.40 · Stop $121.50 · R:R 2.4:1</div>
        </div>
      </div>

      {/* ── STATS ── */}
      <div style={{ borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, padding: "40px 5%" }}>
        <div className="lp-grid-stats" style={{ maxWidth: 900, margin: "0 auto" }}>
          <StatBox val="884" label="TICKERS SCANNED" />
          <StatBox val="22" label="INDICATORS PER SIGNAL" />
          <StatBox val="8" label="VERDICT TIERS" />
          <StatBox val="20" label="SECTORS COVERED" />
          <StatBox val="7 day" label="FREE TRIAL" />
        </div>
      </div>

      {/* ── FEATURES ── */}
      <div id="features" style={S.section}>
        <div style={S.pill(T.cyan)}>THE SIGNAL SYSTEM</div>
        <h2 style={S.h2}>Three layers. One verdict.</h2>
        <p style={S.sub}>Most platforms give you 20 indicators and leave you to figure it out. Auctus synthesizes everything into a single, actionable answer.</p>

        <div className="lp-grid-3" style={{ marginTop: 48 }}>
          <FeatureCard emoji="⚡" title="Layer 1 — Setup Quality" desc="22 indicators calculated per ticker: RSI, MACD, Bollinger Bands, CMF, OBV, Williams %R, ADX, PSAR, and more. Each weighted and combined into a composite score from -12 to +12." accent={T.purple} />
          <FeatureCard emoji="🎯" title="Layer 2 — Entry Timing" desc="A 14-point gate system checks 200 SMA structure, RSI extension, volume, CMF distribution, and post-drop cooldown before confirming GO. Good stock, wrong moment? It tells you to wait." accent={T.cyan} />
          <FeatureCard emoji="📊" title="Layer 3 — Position Health" desc="After entry, monitors EMA21, CMF, MACD, and your stop level. Four conditions tracked in real time — shows exactly when to hold, tighten, or exit." accent={T.green} />
        </div>

        {/* Verdict showcase */}
        <div style={{ marginTop: 48, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "28px 32px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 16 }}>8-TIER VERDICT SYSTEM</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {[["BUY NOW", T.green], ["BUY", "#86efac"], ["WR RECOVERY", T.cyan], ["ACCUMULATION", T.purple], ["WATCH", T.yellow], ["HOLD", T.textSub], ["BEARISH", T.orange], ["STRONG SHORT", T.red]].map(([l, c]) => (
              <VerdictBadge key={l} label={l} color={c} />
            ))}
          </div>
          <div style={{ marginTop: 16, fontSize: 12, color: T.textDim, lineHeight: 1.7 }}>Every verdict comes with a plain-English reason and a specific action: what to do, when to enter, where to set your stop, and what would invalidate the thesis.</div>
        </div>
      </div>

      {/* ── SCANNER ── */}
      <div style={{ background: T.surface, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
        <div style={S.section}>
          <div className="lp-side-by-side">
            <div>
              <div style={S.pill(T.orange)}>MARKET SCANNER</div>
              <h2 style={S.h2}>Find the best setup in 884 tickers in seconds.</h2>
              <p style={S.sub}>Scan the entire market across 20 sectors. Filter by signal type — BUY, CALLS, PUTS, SQUEEZE, ACCUMULATION. Sort by score, CMF, RSI, or % gain.</p>
              <div style={{ marginTop: 24 }}>
                {["20 sectors including AI, Healthcare, Energy, REITs, Crypto", "Filter by CALLS or PUTS — options setups surfaced automatically", "BB Squeeze filter — explosive moves before they happen", "CMF divergence — institutional flow vs price action"].map(t => <CheckRow key={t} text={t} />)}
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[["AI / Semis", "40 tickers", T.purple], ["Healthcare", "60 tickers", T.cyan], ["Energy", "40 tickers", T.orange], ["Crypto", "30 tickers", T.yellow], ["Financials", "40 tickers", T.green], ["Industrials", "40 tickers", T.textSub]].map(([s, n, c]) => (
                <div key={s} style={{ ...S.card, padding: "14px 16px" }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: c, marginBottom: 6 }} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{s}</div>
                  <div style={{ fontSize: 11, color: T.textDim }}>{n}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── AI ── */}
      <div style={S.section}>
        <div className="lp-side-by-side">
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, padding: "24px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.purple, letterSpacing: "0.1em", marginBottom: 12 }}>✦ AI ANALYSIS · NVDA</div>
            {["Setup quality: Score +11 with 14 of 22 bullish signals. Golden cross confirmed — SMA50 above SMA200, institutional momentum intact.", "Entry timing: Gate GO with 13/14 conditions met. RSI at 52 — not extended. CMF +0.142 shows strong institutional accumulation.", "Key risk: Earnings in 18 days. Avoid holding options through the event. Consider closing half position before the announcement.", "Action: Full position entry valid. EMA21 at $128.40 is your stop reference. T1 $142.80 on first target, trail the remainder."].map((t, i) => (
              <div key={i} style={{ fontSize: 12, color: T.textSub, lineHeight: 1.75, marginBottom: 10, paddingLeft: 12, borderLeft: `2px solid ${T.purple}44` }}>{t}</div>
            ))}
          </div>
          <div>
            <div style={S.pill(T.purple)}>AI ANALYSIS</div>
            <h2 style={S.h2}>Senior analyst-level insight on every ticker.</h2>
            <p style={S.sub}>Run a full AI analysis on any ticker. Get a clear assessment of setup quality, entry timing, key risk, and the specific action to take. No vague commentary.</p>
            <div style={{ marginTop: 24 }}>
              {["Reads all 22 indicators and synthesizes a narrative", "Daily market briefing — top setups and what to avoid", "Watchlist Intelligence — AI scans all your tickers at once", "Options flow analysis for calls and puts setups"].map(t => <CheckRow key={t} text={t} />)}
            </div>
          </div>
        </div>
      </div>

      {/* ── APP SCREENSHOTS ── */}
      <div style={{ borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, background: T.bg }}>
        <div style={S.section}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={S.pill(T.purple)}>INSIDE THE APP</div>
            <h2 style={{ ...S.h2, textAlign: "center" }}>Everything a serious trader needs.</h2>
            <p style={{ ...S.sub, textAlign: "center", margin: "0 auto" }}>Three tabs per ticker — entry levels, full indicator analysis, and AI commentary.</p>
          </div>
          <AppScreensCarousel />

        </div>
      </div>

      {/* ── CRYPTO + BEAR/BULL ── */}
      <div style={{ background: T.surface, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
        <div style={S.section}>
          <div className="lp-grid-3">
            <FeatureCard emoji="₿" title="Crypto Scanner" desc="40 coins + 25 crypto equities with crypto-calibrated thresholds. BTC Regime Banner shows macro crypto environment. Coin watchlist syncs across web and mobile." accent={T.yellow} />
            <FeatureCard emoji="🐻" title="Bear Market Mode" desc="Auto-detects bear conditions using VIX, Fear & Greed, and bearish 200 SMA percentage. Activates a survival guide with puts strategies, sector rotation, and key signals to watch." accent={T.red} />
            <FeatureCard emoji="🐂" title="Bull Market Mode" desc="Detects confirmed bull conditions — golden crosses dominant, VIX low, F&G positive. Opens a bull playbook: sizing up, buying pullbacks, call strategies, and trend signals." accent={T.green} />
          </div>
        </div>
      </div>

      {/* ── FULL SIGNAL DEPTH ── */}
      <div style={{ borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
        <div style={S.section}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={S.pill(T.purple)}>THE FULL STACK</div>
            <h2 style={{ ...S.h2, textAlign: "center", fontSize: 40 }}>One app. Every signal that matters.</h2>
            <p style={{ ...S.sub, textAlign: "center", margin: "0 auto" }}>Most platforms give you 5 indicators. Auctus runs 22 per ticker, synthesizes them into a score, checks 14 entry conditions, generates AI commentary, and tells you exactly what to do — in one screen.</p>
          </div>

          {/* 22 Indicators grid */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 3, height: 20, background: T.purple, borderRadius: 2 }} />
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text, letterSpacing: "0.04em" }}>22 INDICATORS CALCULATED PER TICKER</div>
              <div style={{ height: 1, flex: 1, background: T.border }} />
            </div>
            <div className="lp-grid-4">
              {[
                { name: "RSI (14)", desc: "Momentum. Below 30 = oversold opportunity. Above 70 = overbought — avoid new entries. Divergence detection included.", color: T.cyan },
                { name: "MACD (12/26/9)", desc: "Trend + momentum. Crossover fires when fast line crosses slow line — the most actionable MACD signal. +2 score on crossover.", color: T.cyan },
                { name: "Bollinger Bands", desc: "Volatility. Squeeze = bands at 6-month narrowest, explosive move loading. Price above upper band = extended. Below lower = oversold.", color: T.cyan },
                { name: "CMF (20)", desc: "Chaikin Money Flow — institutional buying vs selling. Above +0.05 = accumulation. Below -0.05 = distribution. CMF divergence detection.", color: T.green },
                { name: "OBV", desc: "On-Balance Volume. Bullish divergence = price falling but OBV rising — smart money buying. Bearish divergence = price rising on falling volume.", color: T.green },
                { name: "RVOL (20-day)", desc: "Relative Volume vs 20-day average. SURGE = 2x+ (institutional participation). LOW = below 0.7x (unreliable move). Raw volume + today vs avg.", color: T.green },
                { name: "Williams %R", desc: "Mean reversion. The key signal is the TRANSITION — crossing from -80 back toward -20 flags a floor and momentum recovery. +2 score on bull cross.", color: T.yellow },
                { name: "ADX (14)", desc: "Trend strength. Above 25 = confirmed trend (follow it). Below 20 = choppy range (breakouts unreliable). Combined with +DI/-DI for direction.", color: T.yellow },
                { name: "Parabolic SAR", desc: "Trend direction. Dots below price = uptrend confirmed. Dots above = downtrend. Used as trailing stop reference in position health.", color: T.yellow },
                { name: "Stochastic (14,3)", desc: "Momentum oscillator. Oversold below 20, overbought above 80. Cross signals within extreme zones used for confirmation.", color: T.yellow },
                { name: "EMA 21", desc: "Fast trend. Price above EMA21 = short-term bull structure. Price reclaiming EMA21 after pullback = entry signal. Key support level.", color: T.orange },
                { name: "SMA 50 / 200", desc: "Golden cross (SMA50 > SMA200) = primary bull signal. 200 SMA structure bearish = primary bear signal. Highest-weight structural indicator.", color: T.orange },
                { name: "ATR (14)", desc: "Average True Range = volatility measure. Used for stop placement (2x ATR stop), position sizing, target calculation (1.5x and 3x ATR).", color: T.orange },
                { name: "Volume Breakout", desc: "Validates breakouts vs fake-outs. Volume must be 1.5x+ average for a breakout to qualify. Low-volume breakouts fail 65%+ of the time.", color: T.orange },
                { name: "Volume Climax", desc: "3x+ volume red day where price holds above prior lows with RSI in oversold zone. Detects capitulation — institutional buyers stepping in.", color: T.red },
                { name: "CMF Divergence", desc: "Bullish: price lower low but CMF improved — sellers exhausting. Bearish: price higher high but CMF weakened — rally losing fuel. ±1 score.", color: T.red },
                { name: "RSI Divergence", desc: "Bullish: price lower low but RSI higher low — momentum improving before price. One of the earliest reversal signals in the system.", color: T.red },
                { name: "Sector RS", desc: "3-month relative strength vs SPY and vs sector ETF. Outperforming stocks add +1 score. Lagging stocks subtract -1. Money flow tells the story.", color: T.red },
                { name: "Weekly Trend", desc: "Higher timeframe structure. Strong Uptrend or Downtrend adds ±1 to score. Multi-timeframe alignment (daily + weekly) adds ↑↑ MTF badge.", color: T.purple },
                { name: "Trend Structure", desc: "Detects HH/HL (uptrend), LL/LH (downtrend), or range-bound via recent swing analysis. Confirms or contradicts the moving average signals.", color: T.purple },
                { name: "52-Week Range", desc: "Score 0-100. Where price sits in its annual range. Near annual low (0-20) = maximum upside. Near annual high (80-100) = limited upside warning.", color: T.purple },
                { name: "VWAP", desc: "Volume Weighted Average Price = institutional fair value. Price above = buyers aggressive. Price below = sellers in control. Used in gate + sizing.", color: T.purple },
              ].map(({ name, desc, color }) => (
                <div key={name} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 16px", borderTop: `2px solid ${color}44` }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color, marginBottom: 5, fontFamily: "'JetBrains Mono', monospace" }}>{name}</div>
                  <div style={{ fontSize: 11, color: T.textDim, lineHeight: 1.6, fontFamily: "sans-serif" }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Score system */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 3, height: 20, background: T.green, borderRadius: 2 }} />
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text, letterSpacing: "0.04em" }}>THE SCORING SYSTEM</div>
              <div style={{ height: 1, flex: 1, background: T.border }} />
            </div>
            <div className="lp-grid-2" style={{ gap: 14 }}>
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "20px 24px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 14 }}>HOW THE SCORE WORKS</div>
                <p style={{ fontSize: 13, color: T.textSub, lineHeight: 1.8, marginBottom: 16, fontFamily: "sans-serif" }}>All 22 indicators are run through a proprietary weighting engine. Every signal is evaluated, weighted by conviction level, and aggregated into a single composite score.</p>
                <p style={{ fontSize: 13, color: T.textSub, lineHeight: 1.8, marginBottom: 16, fontFamily: "sans-serif" }}>The score has no fixed ceiling — when multiple high-conviction signals fire simultaneously, the score reflects that. A score of +9 means the evidence is overwhelmingly bullish. A score of -9 means the opposite.</p>
                <p style={{ fontSize: 13, color: T.textSub, lineHeight: 1.8, fontFamily: "sans-serif" }}>The score is Layer 1 — setup quality. It tells you <em>how good the stock looks</em>. The gate is Layer 2 — it tells you <em>whether now is the right time to enter</em>. Both must align before Auctus gives you a BUY NOW.</p>
                <div style={{ marginTop: 20, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {[["+9 or above", T.green, "Overwhelmingly bullish"], ["+5 to +8", "#86efac", "Strong setup"], ["+1 to +4", T.textSub, "Building"], ["-1 to -4", T.yellow, "Caution"], ["-5 to -8", T.orange, "Bearish"], ["-9 or below", T.red, "Strong short"]].map(([range, color, label]) => (
                    <div key={range} style={{ padding: "8px 12px", background: T.bg, borderRadius: 8, borderLeft: `3px solid ${color}` }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color, fontFamily: "'JetBrains Mono', monospace" }}>{range}</div>
                      <div style={{ fontSize: 10, color: T.textDim, fontFamily: "sans-serif", marginTop: 2 }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "20px 24px", marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 14 }}>8-TIER VERDICT SYSTEM</div>
                  <p style={{ fontSize: 12, color: T.textDim, lineHeight: 1.6, marginBottom: 14, fontFamily: "sans-serif" }}>The score feeds into one of eight verdicts. Each verdict comes with a plain-English reason and a specific recommended action — not just a label.</p>
                  {[
                    ["BUY NOW", "Highest conviction. All signals aligned. Gate GO.", T.green],
                    ["BUY", "Strong setup. Confirmed entry timing.", "#86efac"],
                    ["WR RECOVERY", "Mean-reversion setup. Floor detected.", T.cyan],
                    ["ACCUMULATION", "Institutional buying at 52W low.", T.purple],
                    ["WATCH", "Good setup. Gate not yet open — monitor.", T.yellow],
                    ["HOLD", "In a position. Thesis intact, no new entry.", T.textSub],
                    ["BEARISH", "Avoid longs. Puts setup possible.", T.orange],
                    ["STRONG SHORT", "Highest conviction bearish. Puts confirmed.", T.red],
                  ].map(([v, d, c]) => (
                    <div key={v} style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: `1px solid ${T.border}` }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: c, fontFamily: "'JetBrains Mono', monospace", width: 110, flexShrink: 0 }}>{v}</span>
                      <span style={{ fontSize: 11, color: T.textDim, fontFamily: "sans-serif", lineHeight: 1.5 }}>{d}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Gate system */}
          <div style={{ marginBottom: 56 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
              <div style={{ width: 3, height: 20, background: T.cyan, borderRadius: 2 }} />
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text, letterSpacing: "0.04em" }}>14-POINT ENTRY GATE SYSTEM</div>
              <div style={{ height: 1, flex: 1, background: T.border }} />
            </div>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "20px 24px" }}>
              <p style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7, marginBottom: 16, fontFamily: "sans-serif" }}>The gate is separate from the score. A stock can have a great setup (high score) but wrong timing (gate not GO). The gate prevents you from buying into overextended stocks, post-drop situations, or against institutional distribution. All 14 conditions must pass for a GO.</p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                {[
                  ["200 SMA structure OK", "SMA50 above SMA200 — macro structure is bullish", "critical"],
                  ["RSI Not Extended", "RSI below 78 and not above upper Bollinger Band simultaneously", "critical"],
                  ["Above Stop Level", "Price above 2xATR stop — not already broken down", "critical"],
                  ["Score Not Deeply Bearish", "Composite score above -3 — not overwhelmingly bearish", "standard"],
                  ["No Post-Drop Cooldown", "No 4%+ single-day drop in last 3 sessions — volatility cooling", "critical"],
                  ["No Bearish Vol Divergence", "Volume not declining on price advance — move has participation", "standard"],
                  ["RSI Not Overbought", "RSI below 70 — room to run without being extended", "standard"],
                  ["CMF Not Distributing", "CMF above -0.10 — institutions not actively exiting", "standard"],
                  ["Price Above VWAP", "Price above institutional fair value — buyers in control", "standard"],
                  ["MACD Not Crossing Down", "MACD not in a bearish crossunder — momentum not reversing", "standard"],
                  ["Gap Stabilized", "No recent gap up requiring 3-session stabilization", "standard"],
                  ["Weekly Trend Not Bearish", "Higher timeframe not in confirmed downtrend", "standard"],
                  ["Not Extended + Distribution", "Not near 52W high with negative CMF simultaneously", "critical"],
                  ["No Fakeout Breakout", "No pattern of price breaking out and failing — trap detection", "standard"],
                ].map(({ 0: name, 1: desc, 2: type }) => (
                  <div key={name} style={{ background: T.bg, borderRadius: 8, padding: "10px 12px", borderLeft: `2px solid ${type === "critical" ? T.red : T.green}44` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 3 }}>
                      <div style={{ width: 5, height: 5, borderRadius: "50%", background: type === "critical" ? T.red : T.green, flexShrink: 0 }} />
                      <span style={{ fontSize: 11, fontWeight: 700, color: T.text }}>{name}</span>
                      {type === "critical" && <span style={{ marginLeft: "auto", fontSize: 8, fontWeight: 700, color: T.red, background: `${T.red}18`, padding: "1px 5px", borderRadius: 3 }}>CRITICAL</span>}
                    </div>
                    <div style={{ fontSize: 10, color: T.textDim, lineHeight: 1.5, fontFamily: "sans-serif" }}>{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Price levels + market data */}
          <div className="lp-grid-3" style={{ gap: 14 }}>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "20px 24px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 14 }}>PRICE LEVELS CALCULATED</div>
              {["SMA 50 — Medium-term trend", "SMA 200 — Long-term trend", "EMA 21 — Short-term momentum", "VWAP — Institutional fair value", "Fib 23.6% — First target", "Fib 38.2% — Second target", "Fib 50% — Mid retracement", "Fib 61.8% — Golden ratio level", "Fib 78.6% — Deep retracement", "Pivot point — Daily S/R", "R1, R2 — Resistance levels", "S1, S2 — Support levels", "52-Week High / Low", "ATR-based stop (2x)", "Target 1 / Target 2", "D1 / D2 (bearish targets)"].map(l => (
                <div key={l} style={{ fontSize: 11, color: T.textSub, padding: "4px 0", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 6, fontFamily: "sans-serif" }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: T.cyan, flexShrink: 0 }} />{l}
                </div>
              ))}
            </div>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "20px 24px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 14 }}>MACRO + MARKET CONTEXT</div>
              {["VIX — Fear index with level classification", "Fear & Greed Index (CNN)", "Market Regime (Bull/Bear/Mixed)", "Bear market detection (2-of-3)", "Bull market detection (2-of-3)", "BTC Regime Banner for crypto", "SPY + QQQ trend monitoring", "Sector rotation analysis", "200 SMA / golden cross % of watchlist", "Sector concentration warnings", "Earnings proximity warnings (14-day)", "Calls/puts opportunity banners", "Reversal Watch — recovery signals", "Daily AI market briefing", "Signal of the Day highlight", "VIX Translator — plain English"].map(l => (
                <div key={l} style={{ fontSize: 11, color: T.textSub, padding: "4px 0", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 6, fontFamily: "sans-serif" }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: T.orange, flexShrink: 0 }} />{l}
                </div>
              ))}
            </div>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "20px 24px" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 14 }}>ADDITIONAL OUTPUTS</div>
              {["Signal Confidence Score", "R:R ratio per setup", "Calmar Ratio (risk-adjusted)", "Position Health Monitor (4-point)", "Best Play (shares vs calls vs puts)", "Calls signal — full options thesis", "Puts signal — full options thesis", "Watch Triggers — entry/exit alerts", "Trade Advisor — viability score", "CMF divergence (bull + bear)", "RSI divergence (bull + bear)", "OBV divergence (bull + bear)", "Volume Climax detection", "BB Squeeze detector", "Multi-timeframe (MTF) alignment", "PRIME setup detection"].map(l => (
                <div key={l} style={{ fontSize: 11, color: T.textSub, padding: "4px 0", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 6, fontFamily: "sans-serif" }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: T.purple, flexShrink: 0 }} />{l}
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 32, textAlign: "center", padding: "28px 32px", background: T.surface, borderRadius: 14, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: T.text, marginBottom: 8 }}>
              22 indicators · 8 verdicts · 14-gate entry check · 16 price levels · Full AI synthesis
            </div>
            <div style={{ fontSize: 14, color: T.textDim, fontFamily: "sans-serif", marginBottom: 20 }}>Everything runs on every scan. One button. Every signal that matters.</div>
            <button onClick={() => setShowLogin(true)} style={{ ...S.ctaPrimary, fontSize: 15, padding: "14px 36px" }}>Start Free Trial — See It Live →</button>
          </div>
        </div>
      </div>

      {/* ── PRICING ── */}
      <div style={S.section}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={S.pill(T.green)}>PRICING</div>
          <h2 style={{ ...S.h2, textAlign: "center" }}>One good trade pays for a year.</h2>
          <p style={{ ...S.sub, textAlign: "center", margin: "0 auto" }}>7-day free trial. Full access from day one. No credit card required.</p>
        </div>
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          {/* Pro card — centered, single option */}
          <div style={{ ...S.card, border: `2px solid ${T.purple}66`, position: "relative", marginBottom: 16 }}>
            <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: "linear-gradient(135deg, #7c3aed, #9333ea)", padding: "4px 20px", borderRadius: 20, fontSize: 12, fontWeight: 700, color: "#fff", whiteSpace: "nowrap" }}>7-DAY FREE TRIAL</div>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 6, marginTop: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.purple, marginBottom: 6 }}>Auctus Pro</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <div style={{ fontSize: 42, fontWeight: 900, color: T.text }}>$24.99</div>
                  <div style={{ fontSize: 14, color: T.textDim }}>/month</div>
                </div>
                <div style={{ fontSize: 13, color: T.textDim, marginTop: 2 }}>or $199/year — save 34%</div>
              </div>
              <div style={{ textAlign: "right", paddingTop: 8 }}>
                <div style={{ fontSize: 12, color: T.textDim, marginBottom: 4, fontFamily: "sans-serif" }}>First month promo</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: T.green }}>$9.99</div>
                <div style={{ fontSize: 11, color: T.textDim, fontFamily: "sans-serif" }}>then $24.99/mo</div>
              </div>
            </div>
            <div style={{ height: 1, background: T.border, margin: "16px 0" }} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 20 }}>
              {[
                "Unlimited watchlist",
                "Full 22-indicator engine",
                "884-ticker market scanner",
                "AI analysis per ticker",
                "Calls + puts signals",
                "Daily AI market briefing",
                "Bear + bull market guides",
                "Web + Android app",
              ].map(f => (
                <div key={f} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: `${T.green}22`, border: `1px solid ${T.green}44`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                    <span style={{ fontSize: 9, color: T.green, fontWeight: 800 }}>✓</span>
                  </div>
                  <span style={{ fontSize: 13, color: T.textSub, lineHeight: 1.5, fontFamily: "sans-serif" }}>{f}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setShowLogin(true)} style={{ ...S.ctaPrimary, width: "100%", fontSize: 16, padding: "16px" }}>Start 7-Day Free Trial →</button>
            <div style={{ marginTop: 10, textAlign: "center", fontSize: 12, color: T.textDim, fontFamily: "sans-serif" }}>No credit card required · Cancel anytime · Full access from day one</div>
          </div>
          {/* Promo note */}
          <div style={{ background: `${T.green}0a`, border: `1px solid ${T.green}22`, borderRadius: 10, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20 }}>🎁</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.green, marginBottom: 3 }}>Launch promo — first month $9.99</div>
              <div style={{ fontSize: 12, color: T.textDim, fontFamily: "sans-serif" }}>Use code <span style={{ color: T.green, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>AUCTUS10</span> at checkout. Renews at $24.99/month after first month.</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <div style={{ background: T.surface, borderTop: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ ...S.section, maxWidth: 720 }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={S.pill(T.textDim)}>FAQ</div>
            <h2 style={{ ...S.h2, textAlign: "center" }}>Common questions</h2>
          </div>
          <FAQItem q="Is this financial advice?" a="No. Auctus Analytics is an educational and informational tool. Nothing on this platform constitutes financial advice or investment advice. All trading involves risk. Past signal performance does not guarantee future results. Always consult a qualified financial advisor before making investment decisions." />
          <FAQItem q="How accurate are the signals?" a="Auctus uses a 22-indicator composite engine with institutional money flow, multi-timeframe confirmation, and a 14-point entry gate. No signal system is 100% accurate — markets are probabilistic. The goal is to improve your odds on each trade by only entering when multiple conditions align. Signals with score +9 or above and gate GO have historically higher follow-through rates." />
          <FAQItem q="What data sources do you use?" a="Daily price bars, volume, and fundamental data via Yahoo Finance server-side API. AI analysis powered by Anthropic Claude. Fear & Greed index from CNN. BTC data from Yahoo Finance. Data is refreshed on each scan — historical bars go back 200+ days for moving average and indicator calculations." />
          <FAQItem q="Does it work for options trading?" a="Yes. Auctus has a dedicated calls and puts signal system. The calls signal fires when BB Squeeze is active, MACD is bullish, RSI is in the ideal zone, and gate is GO. The puts signal fires when bearish 200 SMA structure is confirmed, CMF is negative, and institutional distribution is detected. Each signal card includes suggested strike, expiration, estimated premium, and stop level." />
          <FAQItem q="Is there a mobile app?" a="Yes — Android app available now. iOS coming soon. Your watchlist, scan results, and settings sync automatically between the web app and Android app." />
          <FAQItem q="Can I cancel anytime?" a="Yes. Cancel from your account settings at any time. Your subscription stays active until the end of your current billing period. No penalties, no questions asked." />
        </div>
      </div>

      {/* ── FINAL CTA ── */}
      <div style={{ ...S.section, textAlign: "center", paddingTop: 80, paddingBottom: 80 }}>
        <h2 style={{ ...S.h2, textAlign: "center", fontSize: 42 }}>Ready to trade with an edge?</h2>
        <p style={{ ...S.sub, textAlign: "center", margin: "0 auto 36px" }}>Join traders using Auctus to find high-probability setups every day.</p>
        <button onClick={() => setShowLogin(true)} style={{ ...S.ctaPrimary, fontSize: 16, padding: "16px 40px" }}>Start 7-Day Free Trial →</button>
        <div style={{ marginTop: 12, fontSize: 12, color: T.textDim }}>No credit card required · 7-day free trial · Cancel anytime</div>
      </div>

      {/* ── FOOTER ── */}
      <div style={{ borderTop: `1px solid ${T.border}`, padding: "28px 5%", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <Logo />
        <div style={{ fontSize: 11, color: T.textDim, maxWidth: 500, lineHeight: 1.6 }}>
          Auctus Analytics is for educational purposes only. Not financial advice. All trading involves substantial risk of loss. Past signal performance does not guarantee future results.
        </div>
        <div style={{ display: "flex", gap: 20 }}>
          <button onClick={() => setShowLogin(true)} style={{ background: "none", border: "none", color: T.textDim, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Sign In</button>
          <button onClick={() => setShowLogin(true)} style={{ background: "none", border: "none", color: T.purple, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Start Free Trial</button>
        </div>
      </div>

    </div>
  );
}


// ─── PRODUCT TOUR OVERLAY ────────────────────────────────────────────────────
const TOUR_STEPS = [
  {
    target: "tour-add-ticker",
    title: "Add Any Ticker",
    body: "Type a stock symbol like NVDA or TSLA and hit + Add or press Enter. Add as many as you want — crypto, ETFs, and stocks all work.",
    position: "below",
    pulse: true,
  },
  {
    target: "tour-scan-all",
    title: "Scan All",
    body: "Hit Scan to pull live data on every ticker in your list. This runs 22 technical indicators on each one and generates your BUY / WATCH / WAIT verdicts.",
    position: "below",
    pulse: true,
  },
  {
    target: null, // center modal — no target element
    title: "Click Any Row to Expand",
    body: "After scanning, click any ticker row to open the full analysis — three-layer verdict, entry zone, position tracker, and the AI summary. That's where the real detail lives.",
    position: "center",
    pulse: false,
  },
  {
    target: "tour-scanner",
    title: "🔭 Market Scanner",
    body: "Scans 400+ tickers across 16 sectors to find the best setups right now. Run it when you want ideas — it surfaces stocks you'd never think to add manually.",
    position: "above",
    pulse: true,
  },
  {
    target: "tour-playbook",
    title: "📚 Playbook",
    body: "Every signal explained in plain English — what it means, why it matters, and what to do with it. Read this before you trade so you understand what the app is telling you.",
    position: "above",
    pulse: true,
  },
  {
    target: "tour-guide",
    title: "📖 Full Trading Guide",
    body: "Rules, position sizing, entry timing, exit strategy, and a full FAQ. This is the operating manual. Bookmark it — you'll come back to it.",
    position: "below-left",
    pulse: true,
  },
];

function TourOverlay({ step, total, onNext, onSkip }) {
  const [rect, setRect] = React.useState(null);
  const cfg = TOUR_STEPS[step];

  React.useEffect(() => {
    if (!cfg?.target) { setRect(null); return; }
    const update = () => {
      const el = document.getElementById(cfg.target);
      if (el) setRect(el.getBoundingClientRect());
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [step, cfg]);

  if (!cfg) return null;

  const PAD = 12;
  const TW = 320; // tooltip width
  const TH = 160; // tooltip approx height

  let tooltipStyle = {};
  if (!cfg.target || !rect) {
    // Center modal
    tooltipStyle = {
      position: "fixed",
      top: "50%", left: "50%",
      transform: "translate(-50%, -50%)",
      width: TW,
    };
  } else {
    const pos = cfg.position || "below";
    // Clamp left so tooltip never goes off screen right edge or left edge
    const clampedLeft = Math.min(Math.max(rect.left, 16), window.innerWidth - TW - 16);
    if (pos === "below") {
      tooltipStyle = { position: "fixed", top: rect.bottom + PAD, left: clampedLeft, width: TW };
    } else if (pos === "below-left") {
      // Anchor to right edge of target, but clamp so it doesn't go off left edge
      const rightAnchored = window.innerWidth - rect.right;
      tooltipStyle = { position: "fixed", top: rect.bottom + PAD, right: Math.max(rightAnchored, 16), width: TW };
    } else if (pos === "above") {
      // For right-side buttons, anchor tooltip to right edge so it doesn't overflow
      const wouldOverflow = rect.left + TW > window.innerWidth - 16;
      if (wouldOverflow) {
        tooltipStyle = { position: "fixed", bottom: window.innerHeight - rect.top + PAD, right: Math.max(window.innerWidth - rect.right, 16), width: TW };
      } else {
        tooltipStyle = { position: "fixed", bottom: window.innerHeight - rect.top + PAD, left: clampedLeft, width: TW };
      }
    }
  }

  const highlightStyle = rect ? {
    position: "fixed",
    top: rect.top - 6, left: rect.left - 6,
    width: rect.width + 12, height: rect.height + 12,
    borderRadius: 10,
    boxShadow: "0 0 0 4px #a855f7, 0 0 0 8px rgba(168,85,247,0.25), 0 0 30px rgba(168,85,247,0.4)",
    pointerEvents: "none",
    zIndex: 9999,
    animation: "tourPulse 1.5s ease-in-out infinite",
  } : null;

  return (
    <>
      {/* Dimmed backdrop */}
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9998, pointerEvents: "none" }} />
      {/* Highlight ring around target */}
      {highlightStyle && <div style={highlightStyle} />}
      {/* Tooltip card */}
      <div style={{ ...tooltipStyle, zIndex: 10000, background: "#0d0d0d", border: "1px solid #a855f7", borderRadius: 14, padding: "18px 20px", boxShadow: "0 16px 48px rgba(0,0,0,0.7), 0 0 0 1px rgba(168,85,247,0.3)" }}>
        {/* Step indicator */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 5 }}>
            {Array.from({ length: total }).map((_, i) => (
              <div key={i} style={{ width: i === step ? 18 : 6, height: 6, borderRadius: 3, background: i === step ? "#a855f7" : i < step ? "#7c3aed" : "#222222", transition: "all 0.3s" }} />
            ))}
          </div>
          <span style={{ fontSize: 10, color: "#5a6284" }}>{step + 1} of {total}</span>
        </div>
        {/* Content */}
        <div style={{ fontSize: 14, fontWeight: 700, color: "#f0f2ff", marginBottom: 8 }}>{cfg.title}</div>
        <div style={{ fontSize: 12, color: "#9ba3c4", lineHeight: 1.6, marginBottom: 16 }}>{cfg.body}</div>
        {/* Buttons */}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onSkip} style={{ padding: "7px 14px", background: "none", border: "1px solid #2a2d3e", color: "#5a6284", fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>
            Skip tour
          </button>
          <button onClick={onNext} style={{ padding: "7px 18px", background: "linear-gradient(135deg, #7c3aed, #d946ef)", border: "none", color: "#fff", fontSize: 12, fontWeight: 700, borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>
            {step === total - 1 ? "Done ✓" : "Next →"}
          </button>
        </div>
      </div>
      <style>{`@keyframes tourPulse { 0%,100% { box-shadow: 0 0 0 4px #a855f7, 0 0 0 8px rgba(168,85,247,0.25), 0 0 30px rgba(168,85,247,0.4); } 50% { box-shadow: 0 0 0 4px #d946ef, 0 0 0 12px rgba(217,70,239,0.3), 0 0 40px rgba(217,70,239,0.5); } }`}</style>
    </>
  );
}

function OnboardingModal({ onComplete, user }) {
  const [aiKey, setAiKey] = useState("");
  const [aiProvider, setAiProvider] = useState("anthropic");
  const [showAiSetup, setShowAiSetup] = useState(false);
  const firstName = user?.user_metadata?.full_name?.split(" ")[0] || "there";

  const launch = () => onComplete({ aiKey: aiKey.trim(), aiProvider });

  const providers = [
    { id: "anthropic", label: "Claude", color: "#f97316" },
    { id: "openai",    label: "ChatGPT", color: "#10b981" },
    { id: "gemini",    label: "Gemini", color: "#3b82f6" },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 20, width: 600, maxWidth: "100%", maxHeight: "95vh", overflowY: "auto", boxShadow: "0 32px 80px rgba(0,0,0,0.5)" }}>

        {/* Hero */}
        <div style={{ padding: "36px 36px 24px", background: `linear-gradient(160deg, ${T.bg} 0%, ${T.surface} 100%)`, borderBottom: `1px solid ${T.border}`, textAlign: "center" }}>
          <svg width="52" height="56" viewBox="0 0 64 68" style={{ marginBottom: 16 }}>
            <defs>
              <linearGradient id="ob-g" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6b21a8"/>
                <stop offset="50%" stopColor="#7c3aed"/>
                <stop offset="100%" stopColor="#d946ef"/>
              </linearGradient>
            </defs>
            <polygon points="4,64 22,8 32,36 18,36 20,42 34,42 44,64" fill="url(#ob-g)" opacity="0.9"/>
            <polygon points="28,64 42,8 60,64 53,64 42,28 31,64" fill="url(#ob-g)"/>
          </svg>
          <div style={{ fontSize: 26, fontWeight: 800, color: T.text, marginBottom: 6 }}>
            Welcome, {firstName} 👋
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#a855f7", letterSpacing: "0.08em", marginBottom: 20, textTransform: "uppercase" }}>
            Thank you for joining Auctus Analytics
          </div>
          {/* Auctus meaning */}
          <div style={{ display: "inline-block", padding: "10px 20px", background: `#7c3aed15`, border: `1px solid #7c3aed33`, borderRadius: 10, marginBottom: 20 }}>
            <span style={{ fontSize: 12, color: T.textDim }}>
              <em style={{ color: "#a855f7", fontStyle: "normal", fontWeight: 700 }}>Auctus</em> — Latin for <em style={{ color: T.text, fontStyle: "normal", fontWeight: 600 }}>"growth" and "increase"</em>. Your journey to smarter investing starts here.
            </span>
          </div>
          {/* Mission statement */}
          <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.8, maxWidth: 480, margin: "0 auto" }}>
            Built for retail traders and everyday investors — the swing trader looking for high-conviction setups, the person who DCA's into NVDA every month, the 401k self-manager who wants to time their buys better. Anyone who's ever bought something and watched it immediately drop.<br/><br/>
            Auctus aggregates 22 technical signals into one clear directive — so you know <span style={{ color: T.text, fontWeight: 600 }}>when</span> conditions are truly aligned before you buy. No more noise. No more buyer's remorse.
          </div>
        </div>

        <div style={{ padding: "28px 36px" }}>

          {/* What to do first */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 14 }}>YOUR FIRST 60 SECONDS</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                ["1", "#7c3aed", "Watch the auto-scan finish", "Your watchlist scans automatically on every load — always fresh prices when you open the app."],
                ["2", "#7c3aed", "Hover any verdict badge", "Each ticker shows BUY NOW · WATCH · WAIT. Hover the badge to see exactly why — the full three-layer reasoning in plain English."],
                ["3", "#7c3aed", "Click any ticker row", "Opens the full analysis panel — 22 indicators, entry timing, your position layer, and AI commentary."],
                ["4", "#d946ef", "Ask Claude on your best setup", "Click any row → Summary → Ask Claude. You get a senior analyst narrative in seconds — context the indicators alone can't give you."],
              ].map(([num, col, title, desc]) => (
                <div key={num} style={{ display: "flex", gap: 14, padding: "12px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: `${col}22`, border: `1px solid ${col}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: col, flexShrink: 0, marginTop: 1 }}>{num}</div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 2 }}>{title}</div>
                    <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.5 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div style={{ padding: "14px 16px", background: `#7c3aed12`, border: `1px solid #7c3aed33`, borderRadius: 12, marginBottom: 24 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#a855f7", marginBottom: 10, letterSpacing: "0.05em" }}>💡 THREE THINGS TO KNOW</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                ["💡 Hints are ON", "Hover any column header to learn what it means and why it matters. Turn them off with the Hints button in the controls bar once you know the system."],
                ["📖 Read the Guide", "Tap Guide in the header. The 'How It Works' tab explains the three-layer verdict system in 2 minutes. It's the most important thing to understand."],
                ["🔭 Find new setups", "Use the Scanner button to scan 360+ stocks across 16 sectors. Filter by BUY NOW, CALLS, PUTS, or SQUEEZE to find actionable setups fast."],
              ].map(([title, desc]) => (
                <div key={title} style={{ display: "flex", gap: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.text, minWidth: 120, flexShrink: 0 }}>{title}</div>
                  <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.5 }}>{desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* AI key — optional */}
          <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, marginBottom: 20, overflow: "hidden" }}>
            <button onClick={() => setShowAiSetup(s => !s)}
              style={{ width: "100%", padding: "13px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", background: showAiSetup ? `#7c3aed18` : T.bg, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 18 }}>🤖</span>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Add your own AI key <span style={{ color: T.textDim, fontWeight: 400 }}>— optional</span></div>
                  <div style={{ fontSize: 11, color: T.textDim }}>AI is included free — your own key gives you unlimited usage</div>
                </div>
              </div>
              <span style={{ color: T.textDim, fontSize: 11 }}>{showAiSetup ? "▲ hide" : "▼ expand"}</span>
            </button>
            {showAiSetup && (
              <div style={{ padding: "16px", borderTop: `1px solid ${T.border}`, background: T.surface }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                  {providers.map(p => (
                    <button key={p.id} onClick={() => setAiProvider(p.id)}
                      style={{ flex: 1, padding: "8px", background: aiProvider === p.id ? `${p.color}22` : T.bg, border: `1px solid ${aiProvider === p.id ? p.color : T.border}`, borderRadius: 8, color: aiProvider === p.id ? p.color : T.textDim, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                      {p.label}
                    </button>
                  ))}
                </div>
                <input value={aiKey} onChange={e => setAiKey(e.target.value)}
                  type="password" placeholder="Paste your API key here"
                  style={{ width: "100%", padding: "10px 14px", background: T.bg, border: `1px solid ${aiKey ? "#7c3aed" : T.border}`, borderRadius: 8, color: T.text, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                <div style={{ fontSize: 11, color: T.textDim, marginTop: 8 }}>You can always add this later in ⚙ Settings → AI Provider</div>
              </div>
            )}
          </div>

          {/* CTAs */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={launch}
              style={{ width: "100%", padding: "15px", background: "linear-gradient(135deg, #7c3aed, #d946ef)", border: "none", color: "#fff", fontSize: 15, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 12, letterSpacing: "0.02em" }}>
              Launch Auctus Analytics →
            </button>
            <label style={{ width: "100%", padding: "11px", background: T.bg, border: `1px solid ${T.border}`, color: T.textSub, fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", borderRadius: 12, textAlign: "center", display: "block", boxSizing: "border-box" }}>
              📥 Restore from a previous backup
              <input type="file" accept=".json" style={{ display: "none" }} onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const text = await file.text();
                  const data = JSON.parse(text);
                  if (!data.keys || data._version !== 1) throw new Error("Invalid backup file");
                  for (const [key, val] of Object.entries(data.keys)) await save(key, val);
                  onComplete({
                    aiKey: data.keys.anthropicKey || data.keys.openaiKey || "",
                    aiProvider: data.keys.aiProvider || "anthropic",
                  });
                } catch(err) { alert("Could not restore: " + err.message); }
              }} />
            </label>
          </div>

        </div>
      </div>
    </div>
  );
}

const BACKUP_KEYS = [
  "watchlist", "lastScanResults", "lastScanTime",
  "scannerResults", "scannerResultsTime", "auctus_last_scanner_results",
  "cryptoScanCoins", "cryptoScanEquities", "cryptoScanBTC", "cryptoScanTime", "cryptoCoinWatchlist",
  "anthropicKey", "openaiKey", "geminiKey", "aiProvider",
  "tradeJournal", "signalLog", "schedulerConfig",
  "datum_theme", "datum_mode", "datum_hints",
];

function DataBackupTab({ onClose }) {
  const [status, setStatus] = useState(null);
  const [importing, setImporting] = useState(false);

  const doExport = async () => {
    try {
      const data = { _version: 1, _exported: new Date().toISOString(), keys: {} };
      for (const key of BACKUP_KEYS) {
        const val = await load(key);
        if (val !== null && val !== undefined) data.keys[key] = val;
      }
      // Also grab per-ticker AI summaries, triggers, verdicts
      const watchlist = await load("watchlist") || [];
      for (const item of watchlist) {
        const sym = item.symbol;
        for (const suffix of ["aiSummary_", "aiTriggers_", "aiVerdict_"]) {
          const val = await load(`${suffix}${sym}`);
          if (val) data.keys[`${suffix}${sym}`] = val;
        }
      }
      const json = JSON.stringify(data, null, 2);
      const blob = new Blob([json], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `auctus-backup-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setStatus({ ok: true, msg: `✓ Exported ${Object.keys(data.keys).length} keys to file` });
    } catch (e) {
      setStatus({ ok: false, msg: `✗ Export failed: ${e.message}` });
    }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setStatus(null);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.keys || data._version !== 1) throw new Error("Invalid backup file format");
      let count = 0;
      for (const [key, val] of Object.entries(data.keys)) {
        await save(key, val);
        count++;
      }
      setStatus({ ok: true, msg: `✓ Restored ${count} keys — restart or re-scan to see changes` });
    } catch (e) {
      setStatus({ ok: false, msg: `✗ Import failed: ${e.message}` });
    } finally { setImporting(false); e.target.value = ""; }
  };

  return (
    <div>
      <div style={{ padding: "12px 16px", background: `${T.blue}10`, border: `1px solid ${T.blue}33`, borderRadius: 10, marginBottom: 20, lineHeight: 1.7 }}>
        <div style={{ fontWeight: 700, color: T.blue, marginBottom: 4, fontSize: F.base }}>💡 Your data is safe when you update the app</div>
        <div style={{ fontSize: F.sm, color: T.textSub }}>Watchlist, scan results, journal, and settings are stored in electron-store on your disk — separate from the app files. Updating <code>main.js</code> or <code>App.jsx</code> never touches this data. Use backup/restore to move between machines or as an extra safety net.</div>
      </div>

      {/* EXPORT */}
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, color: T.text, marginBottom: 6 }}>📤 Export Backup</div>
        <div style={{ fontSize: F.sm, color: T.textSub, marginBottom: 14 }}>Downloads a <code>.json</code> file with your full watchlist, scan results, trade journal, performance log, AI summaries, and all settings.</div>
        <button onClick={doExport} style={{ padding: "9px 20px", background: `linear-gradient(135deg, ${T.cyan}, ${T.blue})`, border: "none", color: "#fff", fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 8 }}>
          Download Backup
        </button>
      </div>

      {/* IMPORT */}
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, marginBottom: 14 }}>
        <div style={{ fontWeight: 700, color: T.text, marginBottom: 6 }}>📥 Restore from Backup</div>
        <div style={{ fontSize: F.sm, color: T.textSub, marginBottom: 14 }}>Select a previously exported <code>auctus-backup-*.json</code> file. This overwrites current data — export first if needed.</div>
        <label style={{ display: "inline-block", padding: "9px 20px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.text, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 8 }}>
          {importing ? "⏳ Restoring..." : "Choose Backup File"}
          <input type="file" accept=".json" onChange={doImport} style={{ display: "none" }} disabled={importing} />
        </label>
      </div>

      {/* WHAT'S INCLUDED */}
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18 }}>
        <div style={{ fontWeight: 700, color: T.text, marginBottom: 10 }}>📋 What's included in a backup</div>
        {[
          ["Watchlist", "All tickers and buy prices"],
          ["Scan results", "Last scan data for every ticker"],
          ["AI summaries", "Cached Claude/GPT analysis per ticker"],
          ["AI triggers", "Watch conditions extracted by AI"],
          ["Trade journal", "All logged trades"],
          ["Performance log", "Signal history and outcomes"],
          ["AI Provider", "Anthropic (Claude), OpenAI, Gemini"],
          ["Preferences", "Theme, Easy/Pro mode, hints, scheduler"],
        ].map(([label, desc]) => (
          <div key={label} style={{ display: "flex", gap: 10, marginBottom: 8 }}>
            <span style={{ color: T.green, flexShrink: 0 }}>✓</span>
            <div><span style={{ fontWeight: 600, color: T.text }}>{label}</span> <span style={{ color: T.textDim, fontSize: F.sm }}>— {desc}</span></div>
          </div>
        ))}
      </div>

      {status && (
        <div style={{ marginTop: 14, padding: "10px 14px", background: status.ok ? `${T.green}15` : `${T.red}15`, border: `1px solid ${status.ok ? T.green : T.red}44`, borderRadius: 8, fontSize: F.sm, color: status.ok ? T.green : T.red, fontWeight: 600 }}>
          {status.msg}
        </div>
      )}
    </div>
  );
}

function SettingsPanel({ anthropicKey: initialAnthropicKey, openaiKey: initialOpenaiKey, geminiKey: initialGeminiKey, aiProvider: initialAiProvider, onSave, onClose }) {
  const [tab, setTab] = useState("ai");
  const [aiKeys, setAiKeys] = useState({
    anthropic: initialAnthropicKey || "",
    openai: initialOpenaiKey || "",
    gemini: initialGeminiKey || "",
  });
  const [showKey, setShowKey] = useState({});
  const [activeProvider, setActiveProvider] = useState(initialAiProvider || "anthropic");
  const canSave = true; // Yahoo Finance needs no keys — always saveable

  const tabBtn = (id, label) => (
    <button key={id} onClick={() => setTab(id)}
      style={{ padding: "8px 18px", background: "none", border: "none", borderBottom: tab === id ? `2px solid ${T.cyan}` : "2px solid transparent", color: tab === id ? T.text : T.textDim, fontSize: F.base, fontWeight: tab === id ? 700 : 400, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap" }}>
      {label}
    </button>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, width: 560, maxWidth: "95vw", maxHeight: "90vh", display: "flex", flexDirection: "column", animation: "fadeIn 0.2s ease", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ padding: "20px 24px 0", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 3 }}>CONFIGURATION</div>
              <h2 style={{ fontSize: F.xl, fontWeight: 700, color: T.text }}>⚙ Settings</h2>
            </div>
            <button onClick={onClose} style={{ background: "none", border: "none", color: T.textSub, fontSize: F.xl, cursor: "pointer" }}>✕</button>
          </div>
          <div style={{ display: "flex", gap: 0 }}>
            {tabBtn("ai", "🤖 AI Provider")}
            {tabBtn("data", "💾 Backup & Restore")}
          </div>
        </div>

        {/* Tab content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>

          {/* ── BACKUP TAB ── */}
          {tab === "data" && (
            <DataBackupTab onClose={onClose} />
          )}



          {/* ── AI PROVIDER TAB ── */}
          {tab === "ai" && (
            <>
              <div style={{ fontSize: F.sm, color: T.textSub, marginBottom: 16, lineHeight: 1.6 }}>
                Connect an AI provider to power the <strong style={{ color: T.purple }}>🤖 Ask AI</strong> analysis button. All optional — the plain English summary always works without any AI key.
              </div>

              {/* Provider selector */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>ACTIVE PROVIDER</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {AI_PROVIDERS.map(p => (
                    <div key={p.id} onClick={() => setActiveProvider(p.id)}
                      style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 16px", background: activeProvider === p.id ? `${p.color}15` : T.bg, border: `1px solid ${activeProvider === p.id ? p.color + "66" : T.border}`, borderRadius: 10, cursor: "pointer", transition: "all 0.15s" }}>
                      <div style={{ width: 10, height: 10, borderRadius: "50%", background: p.color, boxShadow: activeProvider === p.id ? `0 0 8px ${p.color}` : "none", flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: F.base, fontWeight: activeProvider === p.id ? 700 : 400, color: activeProvider === p.id ? p.color : T.text }}>{p.name}</div>
                        <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 1 }}>{p.description}</div>
                      </div>
                      <div style={{ fontSize: F.xs, color: T.textDim, textAlign: "right" }}>
                        <div style={{ color: aiKeys[p.id] ? T.green : T.textDim }}>{aiKeys[p.id] ? "✓ Key saved" : "No key"}</div>
                        <div>{p.cost}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Key input for each provider */}
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>API KEYS</div>
              {AI_PROVIDERS.map(p => (
                <div key={p.id} style={{ marginBottom: 14 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <div style={{ fontSize: F.sm, fontWeight: 600, color: p.color }}>{p.name}</div>
                    <button onClick={() => { const u = `https://${p.url}`; window.open?.(u) || eAPI?.openExternal(u); }}
                      style={{ fontSize: F.xs, color: T.textDim, background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
                      Get key ↗
                    </button>
                  </div>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showKey[p.id] ? "text" : "password"}
                      value={aiKeys[p.id]}
                      placeholder={p.placeholder}
                      onChange={e => setAiKeys(k => ({ ...k, [p.id]: e.target.value }))}
                      style={{ width: "100%", background: T.bg, border: `1px solid ${aiKeys[p.id] ? p.color + "44" : T.border}`, color: p.color, padding: "9px 40px 9px 12px", fontSize: F.sm, borderRadius: 8, fontFamily: "'JetBrains Mono', monospace" }}
                    />
                    <button onClick={() => setShowKey(k => ({ ...k, [p.id]: !k[p.id] }))}
                      style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: F.md }}>
                      {showKey[p.id] ? "●" : "○"}
                    </button>
                  </div>
                </div>
              ))}

              <div style={{ padding: "10px 14px", background: T.bg, borderRadius: 8, border: `1px solid ${T.border}`, fontSize: F.xs, color: T.textDim, lineHeight: 1.6, marginTop: 4 }}>
                Keys are saved locally on your machine only. Never shared with anyone.
              </div>
            </>
          )}
        </div>

        {/* Footer save button */}
        <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, background: T.surface }}>
          <button
            onClick={() => onSave(aiKeys.anthropic.trim(), aiKeys.openai.trim(), aiKeys.gemini.trim(), activeProvider)}
            style={{ width: "100%", padding: "12px", background: `linear-gradient(135deg, ${T.green}, ${T.cyan})`, border: "none", color: T.bg, fontSize: F.base, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 10 }}>
            Save & Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SCHEDULER PANEL ─────────────────────────────────────────────────────────
function SchedulerPanel({ onClose }) {
  const [cfg, setCfg] = useState({ enabled: true, times: ["08:00", "09:35", "12:00", "15:00", "16:15"], timezone: "America/New_York", notifyOnBuy: true, notifyOnSell: true, notifyOnSqueeze: true, notifyOnSignalChange: true });
  const [saved, setSaved] = useState(false);
  useEffect(() => { (async () => { const c = await load("schedulerConfig"); if (c) setCfg(c); })(); }, []);
  const doSave = async () => { await save("schedulerConfig", cfg); if (eAPI) await eAPI.setSchedulerConfig(cfg); setSaved(true); setTimeout(() => setSaved(false), 2000); };
  const TIMES = [["08:00", "Pre-Market 8:00 AM"], ["09:35", "Open 9:35 AM"], ["10:30", "Mid-Morning 10:30"], ["12:00", "Midday 12:00 PM"], ["14:00", "Afternoon 2:00 PM"], ["15:00", "Power Hour 3:00 PM"], ["16:15", "After Close 4:15 PM"]];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 32, width: 500, maxWidth: "95vw", animation: "fadeIn 0.2s ease" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 4 }}>CONFIGURATION</div>
            <h2 style={{ fontSize: F.xl, fontWeight: 700, color: T.text }}>Auto-Scan Scheduler</h2>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textSub, fontSize: F.xl, cursor: "pointer", padding: 4 }}>✕</button>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", background: T.bg, borderRadius: 10, marginBottom: 20, border: `1px solid ${T.border}` }}>
          <div>
            <div style={{ fontSize: F.base, fontWeight: 600, color: T.text }}>Auto-Scan Enabled</div>
            <div style={{ fontSize: F.sm, color: T.textDim, marginTop: 2 }}>Scans your watchlist Mon–Fri at selected times (ET)</div>
          </div>
          <button onClick={() => setCfg(c => ({ ...c, enabled: !c.enabled }))}
            style={{ padding: "8px 20px", background: cfg.enabled ? `${T.green}22` : T.surface, border: `2px solid ${cfg.enabled ? T.green : T.border}`, color: cfg.enabled ? T.green : T.textDim, fontSize: F.base, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 8 }}>
            {cfg.enabled ? "ON" : "OFF"}
          </button>
        </div>

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 10 }}>SCAN TIMES (EASTERN TIME)</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {TIMES.map(([t, l]) => {
              const on = cfg.times.includes(t);
              return <button key={t} onClick={() => setCfg(c => ({ ...c, times: on ? c.times.filter(x => x !== t) : [...c.times, t].sort() }))}
                style={{ padding: "10px 14px", background: on ? `${T.cyan}18` : T.bg, border: `1px solid ${on ? T.cyan : T.border}`, color: on ? T.cyan : T.textSub, fontSize: F.base, fontFamily: "inherit", cursor: "pointer", borderRadius: 8, textAlign: "left", fontWeight: on ? 600 : 400 }}>
                {on ? "✓ " : "  "}{l}
              </button>;
            })}
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 10 }}>NOTIFY ME WHEN</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[["notifyOnBuy", "🟢 BUY signal fires", T.green], ["notifyOnSell", "🔴 SELL / EXIT fires", T.red], ["notifyOnSqueeze", `📊 ${BB_TIGHT_BANDS_LABEL} (BB)`, T.yellow], ["notifyOnSignalChange", "🔄 Signal changes", T.cyan]].map(([k, l, col]) => (
              <button key={k} onClick={() => setCfg(c => ({ ...c, [k]: !c[k] }))}
                style={{ padding: "10px 14px", background: cfg[k] ? `${col}18` : T.bg, border: `1px solid ${cfg[k] ? col : T.border}`, color: cfg[k] ? col : T.textSub, fontSize: F.base, fontFamily: "inherit", cursor: "pointer", borderRadius: 8, textAlign: "left", fontWeight: cfg[k] ? 600 : 400 }}>
                {cfg[k] ? "✓ " : "  "}{l}
              </button>
            ))}
          </div>
        </div>

        <button onClick={doSave}
          style={{ width: "100%", padding: "14px", background: saved ? `${T.green}22` : `linear-gradient(135deg, ${T.green}, ${T.cyan})`, border: saved ? `1px solid ${T.green}` : "none", color: saved ? T.green : T.bg, fontSize: F.md, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 10 }}>
          {saved ? "✓ Saved" : "Save Scheduler"}
        </button>
      </div>
    </div>
  );
}

// ─── BACKTESTER ───────────────────────────────────────────────────────────────
function Backtester({ apiKey }) {
  const [ticker, setTicker] = useState("SPY");
  const [mode, setMode] = useState("puts"); // "puts" | "calls"
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const runBacktest = async () => {
    if (!apiKey?.trim()) { setError("API key required — open ⚙ Settings"); return; }
    setLoading(true); setError(""); setResults(null);
    try {
      const url = `https://www.alphavantage.co/query?function=TIME_SERIES_DAILY_ADJUSTED&symbol=${ticker.trim().toUpperCase()}&outputsize=full&apikey=${apiKey.trim()}`;
      const res = await fetch(url); const json = await res.json();
      if (json["Note"]) throw new Error("Rate limit hit — wait 60s");
      if (json["Error Message"]) throw new Error(`Invalid ticker: ${ticker}`);
      if (json["Information"]) throw new Error("Daily limit (25/day) reached");
      const ts = json["Time Series (Daily)"]; if (!ts) throw new Error("No data");
      const dates = Object.keys(ts).sort((a, b) => b.localeCompare(a)).slice(0, 252);
      const closes = dates.map(d => parseFloat(ts[d]["5. adjusted close"]));
      const highs = dates.map(d => parseFloat(ts[d]["2. high"]));
      const lows = dates.map(d => parseFloat(ts[d]["3. low"]));
      const volumes = dates.map(d => parseFloat(ts[d]["6. volume"]));

      const signals = [];
      // Slide a window back through the data — need at least 50 days of history
      for (let i = 0; i < dates.length - 55; i++) {
        // Slice from day i onwards (most recent = index 0)
        const sliceCloses = closes.slice(i);
        const sliceHighs = highs.slice(i);
        const sliceLows = lows.slice(i);
        const sliceVolumes = volumes.slice(i);
        if (sliceCloses.length < 50) break;

        const r = runAnalysis({ closes: sliceCloses, highs: sliceHighs, lows: sliceLows, volumes: sliceVolumes });
        const sig = mode === "puts" ? r.putsSignal : r.callsSignal;

        if (sig?.fullSignal || sig?.partialSignal) {
          // Look forward: what happened in next 5 and 10 days
          const entryPrice = closes[i];
          const price5d = i >= 5 ? closes[i - 5] : null;
          const price10d = i >= 10 ? closes[i - 10] : null;
          const ret5d = price5d ? ((price5d - entryPrice) / entryPrice) * 100 : null;
          const ret10d = price10d ? ((price10d - entryPrice) / entryPrice) * 100 : null;
          // For puts, a win = price went DOWN. For calls, a win = price went UP.
          const win5d = ret5d != null ? (mode === "puts" ? ret5d < -1 : ret5d > 1) : null;
          const win10d = ret10d != null ? (mode === "puts" ? ret10d < -2 : ret10d > 2) : null;

          signals.push({
            date: dates[i],
            price: entryPrice,
            score: r.buyScore,
            signal: mode === "puts" ? r.putsSignal : r.callsSignal,
            ret5d, ret10d, win5d, win10d,
            full: sig.fullSignal,
            passed: sig.passed,
            total: sig.total,
          });
        }
      }

      // Deduplicate — don't count consecutive days as separate signals
      const deduped = [];
      let lastDate = null;
      for (const s of signals) {
        if (!lastDate || daysBetween(s.date, lastDate) >= 3) {
          deduped.push(s);
          lastDate = s.date;
        }
      }

      // Stats
      const fullSignals = deduped.filter(s => s.full);
      const withFwd5 = fullSignals.filter(s => s.win5d != null);
      const withFwd10 = fullSignals.filter(s => s.win10d != null);
      const wins5 = withFwd5.filter(s => s.win5d).length;
      const wins10 = withFwd10.filter(s => s.win10d).length;
      const avgRet5 = withFwd5.length ? withFwd5.reduce((s, x) => s + (x.ret5d || 0), 0) / withFwd5.length : 0;
      const avgRet10 = withFwd10.length ? withFwd10.reduce((s, x) => s + (x.ret10d || 0), 0) / withFwd10.length : 0;

      setResults({ signals: deduped, fullSignals, wins5, wins10, withFwd5, withFwd10, avgRet5, avgRet10, ticker: ticker.trim().toUpperCase(), mode });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  function daysBetween(d1, d2) {
    return Math.abs(new Date(d1) - new Date(d2)) / (1000 * 60 * 60 * 24);
  }

  const signalColor = mode === "puts" ? T.red : T.green;
  const signalLabel = mode === "puts" ? "🔻 PUTS" : "⚡ CALLS";

  return (
    <div style={{ padding: "16px 24px 16px 28px", height: "100%", overflowY: "auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 2 }}>SIGNAL BACKTESTER</div>
          <div style={{ fontSize: F.base, fontWeight: 700, color: T.text }}>How many times would this signal have worked?</div>
          <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 2 }}>Runs your signal conditions backwards through 252 days of data. Uses 1 API call.</div>
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 4 }}>TICKER</div>
          <input value={ticker} onChange={e => setTicker(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === "Enter" && runBacktest()}
            style={{ width: 100, background: T.bg, border: `1px solid ${T.border}`, color: T.cyan, padding: "7px 12px", fontSize: F.base, fontFamily: "'JetBrains Mono', monospace", borderRadius: 8, fontWeight: 700 }} />
        </div>
        <div>
          <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 4 }}>SIGNAL TYPE</div>
          <div style={{ display: "flex", gap: 6 }}>
            {[["puts", "🔻 Puts", T.red], ["calls", "⚡ Calls", T.green]].map(([val, lbl, col]) => (
              <button key={val} onClick={() => setMode(val)}
                style={{ padding: "7px 14px", background: mode === val ? `${col}22` : T.bg, border: `1px solid ${mode === val ? col : T.border}`, color: mode === val ? col : T.textSub, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 8 }}>
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <button onClick={runBacktest} disabled={loading}
          style={{ padding: "7px 20px", background: loading ? T.surfaceHi : `linear-gradient(135deg, ${signalColor}, ${mode === "puts" ? T.orange : T.cyan})`, border: "none", color: loading ? T.textDim : T.bg, fontSize: F.base, fontWeight: 700, fontFamily: "inherit", cursor: loading ? "not-allowed" : "pointer", borderRadius: 8 }}>
          {loading ? "Running..." : "▶ Run Backtest"}
        </button>
      </div>

      {error && <div style={{ padding: "8px 14px", background: `${T.red}15`, border: `1px solid ${T.red}44`, borderRadius: 8, fontSize: F.sm, color: T.red, marginBottom: 12 }}>{error}</div>}

      {results && (
        <div>
          {/* Summary stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 10, marginBottom: 16 }}>
            {[
              { label: "Total Signals", value: results.fullSignals.length, sub: "full conditions met", col: signalColor },
              { label: "Win Rate 5d", value: results.withFwd5.length ? `${Math.round(results.wins5 / results.withFwd5.length * 100)}%` : "—", sub: `${results.wins5}/${results.withFwd5.length} trades`, col: results.wins5 / (results.withFwd5.length || 1) >= 0.6 ? T.green : T.red },
              { label: "Win Rate 10d", value: results.withFwd10.length ? `${Math.round(results.wins10 / results.withFwd10.length * 100)}%` : "—", sub: `${results.wins10}/${results.withFwd10.length} trades`, col: results.wins10 / (results.withFwd10.length || 1) >= 0.6 ? T.green : T.red },
              { label: "Avg Move 5d", value: `${results.avgRet5 >= 0 ? "+" : ""}${results.avgRet5.toFixed(1)}%`, sub: mode === "puts" ? "negative = win" : "positive = win", col: mode === "puts" ? (results.avgRet5 < 0 ? T.green : T.red) : (results.avgRet5 > 0 ? T.green : T.red) },
              { label: "Avg Move 10d", value: `${results.avgRet10 >= 0 ? "+" : ""}${results.avgRet10.toFixed(1)}%`, sub: mode === "puts" ? "negative = win" : "positive = win", col: mode === "puts" ? (results.avgRet10 < 0 ? T.green : T.red) : (results.avgRet10 > 0 ? T.green : T.red) },
            ].map(({ label, value, sub, col }) => (
              <div key={label} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "12px 14px", textAlign: "center" }}>
                <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: F.xl, fontWeight: 700, color: col }}>{value}</div>
                <div style={{ fontSize: 11, color: T.textDim, marginTop: 3 }}>{sub}</div>
              </div>
            ))}
          </div>

          {/* Caveat */}
          <div style={{ padding: "8px 12px", background: `${T.yellow}10`, border: `1px solid ${T.yellow}33`, borderRadius: 8, fontSize: F.xs, color: T.yellow, marginBottom: 12 }}>
            ⚠ Past performance ≠ future results. This uses daily close data — actual entry/exit prices in live trading will differ. Does not account for VIX, earnings gaps, or transaction costs. Use as a directional guide only.
          </div>

          {/* Signal list */}
          {results.fullSignals.length > 0 && (
            <div>
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 8 }}>
                FULL {signalLabel} SIGNALS — {results.ticker} ({results.fullSignals.length} occurrences in last 252 days)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 8 }}>
                {results.fullSignals.slice(0, 20).map((s, i) => (
                  <div key={i} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: F.sm, fontWeight: 700, color: T.text }}>{s.date}</div>
                      <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 2 }}>Score: {s.score > 0 ? "+" : ""}{s.score} · Entry: ${s.price.toFixed(2)}</div>
                    </div>
                    <div style={{ textAlign: "right", display: "flex", gap: 10 }}>
                      {s.ret5d != null && (
                        <div>
                          <div style={{ fontSize: 10, color: T.textDim }}>5d</div>
                          <div style={{ fontSize: F.sm, fontWeight: 700, color: s.win5d ? T.green : T.red }}>{s.ret5d >= 0 ? "+" : ""}{s.ret5d.toFixed(1)}%</div>
                        </div>
                      )}
                      {s.ret10d != null && (
                        <div>
                          <div style={{ fontSize: 10, color: T.textDim }}>10d</div>
                          <div style={{ fontSize: F.sm, fontWeight: 700, color: s.win10d ? T.green : T.red }}>{s.ret10d >= 0 ? "+" : ""}{s.ret10d.toFixed(1)}%</div>
                        </div>
                      )}
                      {s.ret5d == null && <div style={{ fontSize: F.xs, color: T.textDim }}>Recent —<br/>no fwd data</div>}
                    </div>
                  </div>
                ))}
              </div>
              {results.fullSignals.length > 20 && <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 8 }}>Showing 20 of {results.fullSignals.length} signals</div>}
            </div>
          )}

          {results.fullSignals.length === 0 && (
            <div style={{ padding: "20px", textAlign: "center", color: T.textDim, fontSize: F.base }}>
              No full {signalLabel} signals found for {results.ticker} in the last 252 days.<br />
              <span style={{ fontSize: F.sm }}>Try relaxing the criteria or checking a different ticker.</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── TRADE JOURNAL ────────────────────────────────────────────────────────────
function MyTrades({ pendingTrade, onClearPending }) {
  const [trades, setTrades] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingExit, setEditingExit] = useState(null);
  const [exitForm, setExitForm] = useState({ price: "", date: new Date().toLocaleDateString(), reason: "" });
  const [activeTab, setActiveTab] = useState("open"); // open | closed | insights
  const [form, setForm] = useState({});

  useEffect(() => {
    (async () => {
      const saved = await load("myTrades");
      // Migrate from old tradeJournal key
      if (!saved) {
        const old = await load("tradeJournal");
        if (old && Array.isArray(old)) { setTrades(old); save("myTrades", old); return; }
      }
      if (saved && Array.isArray(saved)) setTrades(saved);
    })();
  }, []);

  useEffect(() => {
    if (trades.length > 0) save("myTrades", trades);
  }, [trades]);

  // Pre-fill form from watchlist Log Trade button
  useEffect(() => {
    if (!pendingTrade) return;
    const { symbol, result, pos } = pendingTrade;
    const verdict = result ? calcVerdict({ result, aiCaution: false }) : null;
    const atrStop = result?.atr ? (result.price - 2 * result.atr).toFixed(2) : "";
    setForm({
      symbol,
      tradeType: "STOCK",           // STOCK | CALL | PUT | SHORT
      direction: "LONG",
      entryDate: new Date().toLocaleDateString(),
      entryPrice: result?.price?.toFixed(2) || "",
      shares: "",
      stopPrice: atrStop,
      target1: result?.t1?.toFixed(2) || "",
      target2: result?.t2?.toFixed(2) || "",
      // Options fields
      strike: "",
      expDate: "",
      contracts: "",
      premium: "",
      // Signal snapshot
      score: result?.buyScore,
      verdict: verdict?.label || result?.signal || "",
      verdictLayer: verdict?.layer || "",
      rsi: result?.rsi?.toFixed(1),
      macd: result?.macd?.crossover ? "Bullish Cross" : result?.macd?.bullish ? "Bullish" : result?.macd?.crossunder ? "Bearish Cross" : "Bearish",
      cmf: result?.cmf?.toFixed(3),
      gate: result?.gate?.label,
      adx: result?.adx?.adx?.toFixed(1),
      bbSqueeze: result?.bb?.isSqueeze ? "YES" : "NO",
      rvol: result?.rvol?.ratio?.toFixed(2),
      callsSignal: result?.callsSignal?.bouncePlayOnly ? "BOUNCE" : result?.callsSignal?.fullSignal ? "FULL" : result?.callsSignal?.partialSignal ? "PARTIAL" : "NONE",
      putsSignal: result?.putsSignal?.fullSignal ? "FULL" : result?.putsSignal?.partialSignal ? "PARTIAL" : "NONE",
      notes: "",
    });
    setShowForm(true);
    onClearPending();
  }, [pendingTrade]);

  const isOptions = form.tradeType === "CALL" || form.tradeType === "PUT";

  const saveTrade = () => {
    if (!form.symbol || !form.entryPrice) return;
    const entryPrice = parseFloat(form.entryPrice);
    const shares = parseFloat(form.shares) || 0;
    const contracts = parseFloat(form.contracts) || 0;
    const premium = parseFloat(form.premium) || 0;
    const isShort = form.tradeType === "SHORT";
    const cost = isOptions ? contracts * premium * 100 : shares * entryPrice;
    const trade = {
      id: Date.now(),
      ...form,
      entryPrice,
      shares,
      contracts,
      premium,
      cost: parseFloat(cost.toFixed(2)),
      stopPrice: parseFloat(form.stopPrice) || null,
      target1: parseFloat(form.target1) || null,
      target2: parseFloat(form.target2) || null,
      strike: parseFloat(form.strike) || null,
      status: "OPEN",
      exitPrice: null,
      exitDate: null,
      exitReason: "",
      pnlDollar: null,
      pnlPct: null,
    };
    setTrades(t => [trade, ...t]);
    setShowForm(false);
    setForm({});
  };

  const closeTrade = (id) => {
    const ep = parseFloat(exitForm.price);
    if (!ep) return;
    setTrades(t => t.map(tr => {
      if (tr.id !== id) return tr;
      let pnlDollar, pnlPct;
      if (tr.tradeType === "CALL") {
        // Call: profit when exit premium > entry premium
        pnlDollar = (ep - tr.premium) * tr.contracts * 100;
        pnlPct = tr.premium > 0 ? ((ep - tr.premium) / tr.premium) * 100 : 0;
      } else if (tr.tradeType === "PUT") {
        // Put: profit when exit premium > entry premium (bought to open)
        pnlDollar = (ep - tr.premium) * tr.contracts * 100;
        pnlPct = tr.premium > 0 ? ((ep - tr.premium) / tr.premium) * 100 : 0;
      } else if (tr.tradeType === "SHORT") {
        // Short: profit when exit price < entry price
        // e.g. short at $300, close at $290 = +$10/share
        pnlDollar = (tr.entryPrice - ep) * tr.shares;
        pnlPct = tr.entryPrice > 0 ? ((tr.entryPrice - ep) / tr.entryPrice) * 100 : 0;
      } else {
        // Stock long: profit when exit price > entry price
        pnlDollar = (ep - tr.entryPrice) * tr.shares;
        pnlPct = tr.entryPrice > 0 ? ((ep - tr.entryPrice) / tr.entryPrice) * 100 : 0;
      }
      return { ...tr, exitPrice: ep, exitDate: exitForm.date, exitReason: exitForm.reason, pnlDollar: parseFloat(pnlDollar.toFixed(2)), pnlPct: parseFloat(pnlPct.toFixed(2)), status: "CLOSED" };
    }));
    setEditingExit(null);
    setExitForm({ price: "", date: new Date().toLocaleDateString(), reason: "" });
  };

  const openTrades  = trades.filter(t => t.status === "OPEN");
  const closedTrades = trades.filter(t => t.status === "CLOSED");
  const wins    = closedTrades.filter(t => (t.pnlDollar || 0) > 0);
  const losses  = closedTrades.filter(t => (t.pnlDollar || 0) <= 0);
  const winRate = closedTrades.length ? (wins.length / closedTrades.length * 100) : null;
  const totalPnl = closedTrades.reduce((s, t) => s + (t.pnlDollar || 0), 0);
  const avgWinPct = wins.length ? wins.reduce((s, t) => s + t.pnlPct, 0) / wins.length : 0;
  const avgLossPct = losses.length ? losses.reduce((s, t) => s + t.pnlPct, 0) / losses.length : 0;
  const rr = avgLossPct < 0 ? Math.abs(avgWinPct / avgLossPct) : 0;

  // Signal correlation insights
  const signalStats = React.useMemo(() => {
    const map = {};
    closedTrades.forEach(t => {
      const key = t.verdict || t.signal || "Unknown";
      if (!map[key]) map[key] = { verdict: key, wins: 0, losses: 0, totalPnl: 0 };
      if ((t.pnlDollar || 0) > 0) map[key].wins++;
      else map[key].losses++;
      map[key].totalPnl += t.pnlDollar || 0;
    });
    return Object.values(map)
      .map(s => ({ ...s, total: s.wins + s.losses, winRate: (s.wins / (s.wins + s.losses)) * 100 }))
      .sort((a, b) => b.total - a.total);
  }, [closedTrades]);

  const tradeTypeStats = React.useMemo(() => {
    const map = { STOCK: { wins: 0, losses: 0, pnl: 0 }, CALL: { wins: 0, losses: 0, pnl: 0 }, PUT: { wins: 0, losses: 0, pnl: 0 }, SHORT: { wins: 0, losses: 0, pnl: 0 } };
    closedTrades.forEach(t => {
      const key = t.tradeType || "STOCK";
      if (!map[key]) map[key] = { wins: 0, losses: 0, pnl: 0 };
      if ((t.pnlDollar || 0) > 0) map[key].wins++; else map[key].losses++;
      map[key].pnl += t.pnlDollar || 0;
    });
    return map;
  }, [closedTrades]);

  const exportTradesCsv = () => {
    const esc = (v) => {
      if (v == null || v === "") return "";
      const s = String(v);
      if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const headers = ["Symbol", "Date", "ADX", "CMF", "Score", "Verdict", "Entry Price", "Exit Price", "P&L", "Notes"];
    const rows = trades.map((t) => [
      t.symbol,
      t.entryDate,
      t.adx ?? "",
      t.cmf ?? "",
      t.score != null && t.score !== "" ? t.score : "",
      t.verdict || t.signal || "",
      t.entryPrice != null && t.entryPrice !== "" ? t.entryPrice : "",
      t.exitPrice != null && t.exitPrice !== "" ? t.exitPrice : "",
      t.pnlDollar != null ? Number(t.pnlDollar).toFixed(2) : "",
      t.notes ?? "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map(esc).join(","))].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    a.href = url;
    a.download = `trades-journal-${y}-${m}-${d}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const inp = (key, label, type = "text", placeholder = "") => (
    <div>
      <label style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 4, display: "block" }}>{label}</label>
      <input type={type} value={form[key] || ""} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder}
        style={{ background: T.bg, border: `1px solid ${T.border}`, color: T.text, padding: "8px 12px", fontSize: F.base, borderRadius: 8, fontFamily: "inherit", width: "100%", boxSizing: "border-box" }} />
    </div>
  );

  const TradeCard = ({ t }) => {
    const isOpt = t.tradeType === "CALL" || t.tradeType === "PUT";
    const isShort = t.direction === "SHORT" || t.tradeType === "SHORT";
    const typeColor = t.tradeType === "CALL" ? T.green : t.tradeType === "PUT" ? T.red : t.tradeType === "SHORT" ? T.orange : T.cyan;
    const pnlColor = (t.pnlDollar || 0) > 0 ? T.green : (t.pnlDollar || 0) < 0 ? T.red : T.textSub;
    return (
      <div style={{ background: T.bg, border: `1px solid ${t.status === "CLOSED" ? ((t.pnlDollar || 0) > 0 ? T.green + "44" : T.red + "33") : T.border}`, borderRadius: 10, padding: 14, marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: F.md, fontWeight: 900, color: T.text }}>{t.symbol}</span>
          <span style={{ padding: "2px 7px", borderRadius: 4, background: typeColor + "18", border: `1px solid ${typeColor}44`, fontSize: F.xs, fontWeight: 800, color: typeColor }}>
            {t.tradeType}{isOpt ? ` ${t.strike} ${t.expDate}` : ""}
          </span>
          <span style={{ padding: "2px 7px", borderRadius: 4, background: T.surfaceHi, fontSize: F.xs, color: T.textDim }}>{t.verdict || t.signal}</span>
          <span style={{ fontSize: F.xs, color: T.textDim, marginLeft: "auto" }}>{t.entryDate}</span>
        </div>

        {/* Score + signal snapshot */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          {[
            ["Score", `${t.score > 0 ? "+" : ""}${t.score}`, t.score >= 4 ? T.green : t.score <= -4 ? T.red : T.yellow],
            ["RSI", t.rsi, T.textSub],
            ["MACD", t.macd, t.macd?.includes("Bull") ? T.green : T.red],
            ["CMF", t.cmf, parseFloat(t.cmf) > 0 ? T.green : T.red],
            ["Gate", t.gate, t.gate === "GO" ? T.green : T.red],
            ["ADX", t.adx, T.textSub],
          ].filter(([,v]) => v).map(([l, v, c]) => (
            <div key={l} style={{ padding: "3px 8px", background: T.surface, borderRadius: 5, fontSize: 10 }}>
              <span style={{ color: T.textDim }}>{l}: </span>
              <span style={{ color: c, fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Price info */}
        <div style={{ display: "flex", gap: 16, marginBottom: 8, flexWrap: "wrap" }}>
          {isOpt ? (
            <>
              <div><div style={{ fontSize: 9, color: T.textDim }}>PREMIUM PAID</div><div style={{ fontSize: F.sm, fontWeight: 700, fontFamily: "monospace", color: T.text }}>${t.premium} × {t.contracts} contracts</div></div>
              <div><div style={{ fontSize: 9, color: T.textDim }}>COST BASIS</div><div style={{ fontSize: F.sm, fontWeight: 700, fontFamily: "monospace", color: T.text }}>${t.cost?.toFixed(2)}</div></div>
              <div><div style={{ fontSize: 9, color: T.textDim }}>EXPIRES</div><div style={{ fontSize: F.sm, fontWeight: 700, color: T.text }}>{t.expDate}</div></div>
            </>
          ) : (
            <>
              <div><div style={{ fontSize: 9, color: T.textDim }}>ENTRY</div><div style={{ fontSize: F.sm, fontWeight: 700, fontFamily: "monospace", color: T.text }}>${t.entryPrice} × {t.shares} shares</div></div>
              {t.stopPrice && <div><div style={{ fontSize: 9, color: T.red }}>STOP</div><div style={{ fontSize: F.sm, fontWeight: 700, fontFamily: "monospace", color: T.red }}>${t.stopPrice}</div></div>}
              {t.target1 && <div><div style={{ fontSize: 9, color: T.green }}>T1</div><div style={{ fontSize: F.sm, fontWeight: 700, fontFamily: "monospace", color: T.green }}>${t.target1}</div></div>}
              {t.target2 && <div><div style={{ fontSize: 9, color: T.cyan }}>T2</div><div style={{ fontSize: F.sm, fontWeight: 700, fontFamily: "monospace", color: T.cyan }}>${t.target2}</div></div>}
            </>
          )}
          {t.status === "CLOSED" && (
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <div style={{ fontSize: F.md, fontWeight: 900, fontFamily: "monospace", color: pnlColor }}>{(t.pnlDollar || 0) >= 0 ? "+" : ""}${t.pnlDollar?.toFixed(2)}</div>
              <div style={{ fontSize: F.xs, color: pnlColor }}>{(t.pnlPct || 0) >= 0 ? "+" : ""}{t.pnlPct?.toFixed(2)}%</div>
              {t.exitReason && <div style={{ fontSize: 9, color: T.textDim }}>{t.exitReason}</div>}
            </div>
          )}
        </div>

        {t.notes && <div style={{ fontSize: F.xs, color: T.textSub, fontStyle: "italic", padding: "6px 10px", background: T.surface, borderRadius: 6, marginBottom: 8 }}>"{t.notes}"</div>}

        {/* Actions */}
        {t.status === "OPEN" && (
          editingExit === t.id ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input placeholder={t.tradeType === "SHORT" ? "Cover price (lower = profit)" : t.tradeType === "CALL" || t.tradeType === "PUT" ? "Exit premium ($)" : "Exit price"} value={exitForm.price} onChange={e => setExitForm(f => ({ ...f, price: e.target.value }))} type="number"
                style={{ padding: "6px 10px", background: T.bg, border: `1px solid ${T.border}`, color: T.text, borderRadius: 6, fontSize: F.sm, fontFamily: "inherit", width: 110 }} />
              <input placeholder="Date" value={exitForm.date} onChange={e => setExitForm(f => ({ ...f, date: e.target.value }))}
                style={{ padding: "6px 10px", background: T.bg, border: `1px solid ${T.border}`, color: T.text, borderRadius: 6, fontSize: F.sm, fontFamily: "inherit", width: 110 }} />
              <select value={exitForm.reason} onChange={e => setExitForm(f => ({ ...f, reason: e.target.value }))}
                style={{ padding: "6px 10px", background: T.bg, border: `1px solid ${T.border}`, color: T.text, borderRadius: 6, fontSize: F.sm, fontFamily: "inherit" }}>
                <option value="">Reason...</option>
                <option>Target hit</option><option>Stop hit</option><option>Signal reversed</option>
                <option>Trailing stop</option><option>Expiration</option><option>Manual exit</option>
              </select>
              <button onClick={() => closeTrade(t.id)} style={{ padding: "6px 14px", background: `${T.green}18`, border: `1px solid ${T.green}44`, color: T.green, borderRadius: 6, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>Close Trade</button>
              <button onClick={() => setEditingExit(null)} style={{ padding: "6px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, fontSize: F.sm, fontFamily: "inherit", cursor: "pointer" }}>Cancel</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setEditingExit(t.id)} style={{ padding: "5px 12px", background: `${T.cyan}18`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 6, fontSize: F.xs, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>Close Position</button>
              <button onClick={() => setTrades(tr => tr.filter(x => x.id !== t.id))} style={{ padding: "5px 10px", background: `${T.red}12`, border: `1px solid ${T.red}33`, color: T.red, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer" }}>Delete</button>
            </div>
          )
        )}
        {t.status === "CLOSED" && (
          <button onClick={() => setTrades(tr => tr.filter(x => x.id !== t.id))} style={{ padding: "4px 10px", background: `${T.red}12`, border: `1px solid ${T.red}33`, color: T.red, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer" }}>Delete</button>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <div style={{ padding: "14px 20px 14px 28px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em" }}>MY TRADES</div>
          <div style={{ fontSize: F.sm, color: T.textSub }}>Real brokerage trade log · Signal performance tracking</div>
        </div>
        <button type="button" onClick={exportTradesCsv}
          style={{ padding: "7px 16px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 8, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
          Export CSV
        </button>
        <button onClick={() => { setForm({ tradeType: "STOCK", direction: "LONG", entryDate: new Date().toLocaleDateString() }); setShowForm(true); }}
          style={{ padding: "7px 16px", background: `${T.green}18`, border: `1px solid ${T.green}44`, color: T.green, borderRadius: 8, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
          + Log Trade
        </button>
      </div>

      {/* Stats bar */}
      {closedTrades.length > 0 && (
        <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}`, flexShrink: 0, overflowX: "auto" }}>
          {[
            ["WIN RATE", winRate != null ? `${winRate.toFixed(0)}%` : "—", winRate >= 55 ? T.green : winRate >= 45 ? T.yellow : T.red],
            ["TOTAL P&L", `${totalPnl >= 0 ? "+" : ""}$${totalPnl.toFixed(2)}`, totalPnl >= 0 ? T.green : T.red],
            ["AVG WIN", `+${avgWinPct.toFixed(1)}%`, T.green],
            ["AVG LOSS", `${avgLossPct.toFixed(1)}%`, T.red],
            ["R:R", rr > 0 ? `${rr.toFixed(2)}:1` : "—", rr >= 1.5 ? T.green : rr >= 1 ? T.yellow : T.red],
            ["TRADES", `${wins.length}W / ${losses.length}L`, T.textSub],
          ].map(([l, v, c]) => (
            <div key={l} style={{ padding: "10px 16px", borderRight: `1px solid ${T.border}`, flexShrink: 0 }}>
              <div style={{ fontSize: 9, color: T.textDim, marginBottom: 3 }}>{l}</div>
              <div style={{ fontSize: F.sm, fontWeight: 800, fontFamily: "monospace", color: c }}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Sub tabs */}
      <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, flexShrink: 0, paddingLeft: 12 }}>
        {[["open", `📂 Open (${openTrades.length})`], ["closed", `✓ Closed (${closedTrades.length})`], ["insights", "💡 Signal Insights"]].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            style={{ padding: "8px 16px", background: "none", border: "none", borderBottom: `2px solid ${activeTab === id ? T.purple : "transparent"}`, color: activeTab === id ? T.purple : T.textDim, fontSize: F.sm, fontWeight: activeTab === id ? 700 : 400, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap" }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>
        {/* Open trades */}
        {activeTab === "open" && (
          openTrades.length === 0
            ? <div style={{ textAlign: "center", padding: "40px 0", color: T.textDim }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
                <div style={{ fontSize: F.sm, color: T.textSub, marginBottom: 6 }}>No open trades</div>
                <div style={{ fontSize: F.xs, color: T.textDim }}>Click ✚ Log Trade above or use the "Log Trade" button on any watchlist ticker row</div>
              </div>
            : openTrades.map(t => <TradeCard key={t.id} t={t} />)
        )}

        {/* Closed trades */}
        {activeTab === "closed" && (
          closedTrades.length === 0
            ? <div style={{ textAlign: "center", padding: "40px 0", color: T.textDim }}>No closed trades yet</div>
            : [...closedTrades].reverse().map(t => <TradeCard key={t.id} t={t} />)
        )}

        {/* Signal insights */}
        {activeTab === "insights" && (
          <div>
            {closedTrades.length < 3 ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: T.textDim }}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>💡</div>
                <div style={{ fontSize: F.sm, color: T.textSub }}>Close at least 3 trades to see signal performance insights</div>
              </div>
            ) : (
              <>
                {/* Trade type breakdown */}
                <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 10 }}>PERFORMANCE BY TRADE TYPE</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 20 }}>
                  {Object.entries(tradeTypeStats).filter(([, v]) => v.wins + v.losses > 0).map(([type, s]) => {
                    const wr = ((s.wins / (s.wins + s.losses)) * 100);
                    const typeColor = type === "CALL" ? T.green : type === "PUT" ? T.red : type === "SHORT" ? T.orange : T.cyan;
                    return (
                      <div key={type} style={{ padding: "12px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8 }}>
                        <div style={{ fontSize: F.xs, fontWeight: 800, color: typeColor, marginBottom: 4 }}>{type}</div>
                        <div style={{ fontSize: F.lg, fontWeight: 800, color: wr >= 55 ? T.green : wr >= 45 ? T.yellow : T.red }}>{wr.toFixed(0)}%</div>
                        <div style={{ fontSize: 9, color: T.textDim }}>{s.wins}W / {s.losses}L</div>
                        <div style={{ fontSize: 10, color: s.pnl >= 0 ? T.green : T.red, fontWeight: 600 }}>{s.pnl >= 0 ? "+" : ""}${s.pnl.toFixed(0)}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Signal correlation */}
                <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 10 }}>WIN RATE BY AUCTUS SIGNAL</div>
                {signalStats.map(s => {
                  const wr = s.winRate;
                  const barColor = wr >= 60 ? T.green : wr >= 50 ? T.yellow : T.red;
                  return (
                    <div key={s.verdict} style={{ marginBottom: 8, padding: "10px 12px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontSize: F.sm, fontWeight: 600, color: T.text, flex: 1 }}>{s.verdict}</span>
                        <span style={{ fontSize: F.sm, fontWeight: 800, color: barColor }}>{wr.toFixed(0)}%</span>
                        <span style={{ fontSize: F.xs, color: T.textDim }}>{s.wins}W / {s.losses}L</span>
                        <span style={{ fontSize: F.xs, color: (s.totalPnl || 0) >= 0 ? T.green : T.red, fontWeight: 600 }}>{(s.totalPnl || 0) >= 0 ? "+" : ""}${s.totalPnl?.toFixed(0)}</span>
                      </div>
                      <div style={{ height: 4, background: T.border, borderRadius: 2 }}>
                        <div style={{ height: "100%", width: `${Math.min(wr, 100)}%`, background: barColor, borderRadius: 2, transition: "width 0.5s ease" }} />
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>

      {/* Log Trade Modal */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 28, width: "min(680px, 95vw)", maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.2s ease" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: F.xs, fontWeight: 700, color: T.green, letterSpacing: "0.1em", marginBottom: 4 }}>MY TRADES</div>
                <h2 style={{ fontSize: F.xl, fontWeight: 700, color: T.text }}>Log Trade{form.symbol ? ` — ${form.symbol}` : ""}</h2>
              </div>
              <button onClick={() => setShowForm(false)} style={{ background: "none", border: "none", color: T.textSub, fontSize: F.xl, cursor: "pointer" }}>✕</button>
            </div>

            {/* Trade type selector */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 8, display: "block" }}>TRADE TYPE</label>
              <div style={{ display: "flex", gap: 6 }}>
                {[["STOCK", "📈 Stock Long", T.cyan], ["SHORT", "📉 Stock Short", T.orange], ["CALL", "⚡ Call Option", T.green], ["PUT", "🔻 Put Option", T.red]].map(([type, label, color]) => (
                  <button key={type} onClick={() => setForm(f => ({ ...f, tradeType: type }))}
                    style={{ flex: 1, padding: "8px 4px", background: form.tradeType === type ? `${color}22` : T.bg, border: `2px solid ${form.tradeType === type ? color : T.border}`, color: form.tradeType === type ? color : T.textSub, borderRadius: 8, fontSize: 11, fontWeight: form.tradeType === type ? 800 : 400, fontFamily: "inherit", cursor: "pointer" }}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Signal snapshot (if pre-filled from watchlist) */}
            {form.score != null && (
              <div style={{ background: T.bg, borderRadius: 10, padding: 12, marginBottom: 20 }}>
                <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, marginBottom: 8 }}>AUCTUS SIGNAL AT ENTRY</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {[["Verdict", form.verdict, T.purple], ["Score", `${form.score > 0 ? "+" : ""}${form.score}`, form.score >= 4 ? T.green : form.score <= -4 ? T.red : T.yellow], ["RSI", form.rsi, T.text], ["MACD", form.macd, form.macd?.includes("Bull") ? T.green : T.red], ["CMF", form.cmf, parseFloat(form.cmf) > 0 ? T.green : T.red], ["Gate", form.gate, form.gate === "GO" ? T.green : T.red], ["Calls", form.callsSignal, form.callsSignal === "FULL" ? T.green : T.textDim], ["Puts", form.putsSignal, form.putsSignal === "FULL" ? T.red : T.textDim]].map(([l, v, c]) => v ? (
                    <div key={l} style={{ padding: "4px 10px", background: T.surface, borderRadius: 5, fontSize: 11 }}>
                      <span style={{ color: T.textDim }}>{l}: </span><span style={{ color: c, fontWeight: 700 }}>{v}</span>
                    </div>
                  ) : null)}
                </div>
              </div>
            )}

            {/* Form fields */}
            {/* Short-specific guidance */}
            {form.tradeType === "SHORT" && (
              <div style={{ padding: "10px 14px", background: `${T.orange}10`, border: `1px solid ${T.orange}33`, borderRadius: 8, marginBottom: 16, fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>
                <strong style={{ color: T.orange }}>SHORT POSITION:</strong> Enter the price you shorted at. When you close, enter the lower price you bought back at. P&L = (Entry − Exit) × Shares. Example: Short at $300, close at $290 = +$10/share.
              </div>
            )}
            {(form.tradeType === "CALL" || form.tradeType === "PUT") && (
              <div style={{ padding: "10px 14px", background: `${form.tradeType === "CALL" ? T.green : T.red}10`, border: `1px solid ${form.tradeType === "CALL" ? T.green : T.red}33`, borderRadius: 8, marginBottom: 16, fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>
                <strong style={{ color: form.tradeType === "CALL" ? T.green : T.red }}>{form.tradeType} OPTION:</strong> Enter the premium you paid per contract (e.g. $2.50). P&L = (Exit Premium − Entry Premium) × Contracts × 100. Example: Buy call at $2.50, sell at $5.00 = +$250/contract.
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {inp("symbol", "TICKER *", "text", "e.g. NVDA")}
              {inp("entryDate", "ENTRY DATE", "text", new Date().toLocaleDateString())}
              {isOptions ? (
                <>
                  {inp("premium", "PREMIUM PAID PER CONTRACT ($)", "number", "e.g. 2.50")}
                  {inp("contracts", "NUMBER OF CONTRACTS", "number", "e.g. 3")}
                  {inp("strike", "STRIKE PRICE ($)", "number", "e.g. 300")}
                  {inp("expDate", "EXPIRATION DATE", "text", "e.g. 4/18/2026")}
                </>
              ) : (
                <>
                  {inp("entryPrice", form.tradeType === "SHORT" ? "SHORT ENTRY PRICE ($)" : "ENTRY PRICE ($)", "number", "0.00")}
                  {inp("shares", "SHARES", "number", "0")}
                  {inp("stopPrice", form.tradeType === "SHORT" ? "STOP PRICE (above entry for shorts)" : "STOP PRICE ($)", "number", "0.00")}
                  {inp("target1", form.tradeType === "SHORT" ? "TARGET (price to cover)" : "TARGET 1 ($)", "number", "0.00")}
                </>
              )}
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 4, display: "block" }}>NOTES</label>
                <textarea value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Why did you take this trade? What signal triggered it?"
                  style={{ background: T.bg, border: `1px solid ${T.border}`, color: T.text, padding: "8px 12px", fontSize: F.base, borderRadius: 8, fontFamily: "inherit", width: "100%", resize: "vertical", boxSizing: "border-box" }} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
              <button onClick={() => setShowForm(false)} style={{ padding: "10px 20px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 8, fontSize: F.base, fontFamily: "inherit", cursor: "pointer" }}>Cancel</button>
              <button onClick={saveTrade} style={{ padding: "10px 24px", background: `${T.green}18`, border: `1px solid ${T.green}44`, color: T.green, borderRadius: 8, fontSize: F.base, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>Save Trade</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── MARKET SCANNER ───────────────────────────────────────────────────────────
const SCANNER_UNIVERSE = {
  "AI / Semiconductors": [
    "NVDA","AMD","AVGO","ARM","QCOM","MRVL","INTC","TSM","AMAT","KLAC",
    "LRCX","ASML","TXN","NXPI","ON","MCHP","SMCI","MU","WDC","MPWR",
    "ENTG","ONTO","WOLF","SWKS","QRVO","TER","ACLS","AMBA","CRUS","SOUN",
    "COHR","ALGM","SLAB","FORM","ICHR","AMKR","OLED","MXIM","SITM","POWI",
    "ALAB","CRDO","AXTI","IPHI","RMBS","CAMT","MTSI","DIOD","SIMO","AOSL",
    "NVTS","IMOS","KLIC","UCTT","CCMP","MKSI","STRL","PI","SKYW","PLXS"
  ],
  "Mega Cap Tech": [
    "AAPL","MSFT","GOOGL","GOOG","META","AMZN","TSLA","NFLX","ORCL","CRM",
    "ADBE","INTU","NOW","UBER","ABNB","DASH","SHOP","SNOW","PLTR","DELL",
    "IBM","HPQ","SNPS","CDNS","ANSS","CTSH","EPAM","GLOB","INFY","WIT",
    "SAP","ASML","ERIC","NOK","SONY","BIDU","JD","PDD","BABA","SE",
    "GRAB","MELI","CPNG","TCEHY","NTES","WB","TIGR","FUTU","LPSN","ALKS"
  ],
  "Cloud / SaaS": [
    "NET","DDOG","ZS","CRWD","PANW","FTNT","OKTA","GTLB","MDB","ESTC",
    "TEAM","DOCU","ZM","BOX","TWLO","HUBS","BILL","SMAR","CFLT","DOCN",
    "VEEV","WDAY","PCTY","PAYC","BRZE","PATH","AI","MNDY","TOST","TTD",
    "CWAN","ASAN","DOMO","SPRK","SPSC","NCNO","AVPT","ALRM","EXLS","PLUS",
    "SEMR","FROG","SQSP","WIX","WEAV","KVYO","RDDT","RAMP","TASK","CLOU",
    "S","TENB","QLYS","RPD","VRNS","APPN","PEGA","ALTR","DSGX","EVBG"
  ],
  "Fintech / Payments": [
    "V","MA","PYPL","SQ","COIN","HOOD","SOFI","AFRM","UPST","LC",
    "MSTR","NU","PAYO","WEX","GPN","FIS","FISV","AXP","COF","DFS",
    "MQ","FICO","WU","RELY","DAVE","NRDS","FLYW","RPAY","PRCT","EVRI",
    "LMND","ROOT","HIPPO","OPEN","INSU","ACNB","NRDS","IIIV","KFRC","COOP",
    "TREE","NMIH","ESNT","PFSI","GHLD","UWM","LDI","RKT","UWMC","HMPT"
  ],
  "Financials / Banks": [
    "JPM","GS","MS","BAC","WFC","C","BLK","SCHW","RJF","IBKR",
    "USB","PNC","TFC","MTB","CFG","FITB","HBAN","KEY","RF","ZION",
    "BX","KKR","APO","CG","ARES","STT","AMP","TROW","BEN","IVZ",
    "ALLY","SYF","OMF","CACC","WRLD","ESNT","NMI","PFSI","COOP","MC",
    "ICE","CME","CBOE","NDAQ","MKTX","VIRT","LPLA","RJF","SF","EVR",
    "LAZ","HLNE","TPG","STEP","GCMG","HLNE","AMG","WDR","CNS","ROME"
  ],
  "Healthcare / Biotech": [
    "LLY","UNH","JNJ","PFE","ABBV","MRK","AMGN","GILD","REGN","VRTX",
    "MRNA","BNTX","BIIB","ILMN","IDXX","DXCM","ISRG","SYK","BSX","ZBH",
    "TMO","DHR","MDT","EW","BDX","PODD","INSP","ALNY","SRPT","RARE",
    "HUM","CVS","CI","MOH","ELV","ABT","BAX","BIO","HOLX","NTRA",
    "RXRX","NVAX","CRSP","EDIT","NTLA","BEAM","FATE","BLUE","IONS","ARGX",
    "EXAS","NVCR","SEER","PACB","ONEM","PRGO","JAZZ","HALO","INVA","ITCI",
    "ACAD","SAGE","AXSM","CPRX","SUPN","ADMA","CNMD","PDCO","PRCT","SWAV"
  ],
  "Consumer / Retail": [
    "WMT","TGT","COST","HD","LOW","TJX","ROST","LULU","NKE","SBUX",
    "MCD","YUM","DPZ","CMG","WING","ULTA","ELF","ONON","DECK","CROX",
    "DG","DLTR","BURL","CHWY","W","ETSY","CPNG","RH","TPR","RL",
    "PVH","HBI","COLM","SKX","VFC","WEBR","PRPL","HELE","LEVI","OXM",
    "RACE","F","GM","FCAU","HOG","BC","FOXF","YETI","PTON","NFIT",
    "CELH","MNST","KO","PEP","KHC","GIS","CPB","CAG","SJM","MKC"
  ],
  "Travel / Leisure": [
    "ABNB","BKNG","EXPE","TRIP","UBER","LYFT","MAR","HLT","H","IHG",
    "CCL","RCL","NCLH","AAL","DAL","UAL","LUV","ALK","JBLU","SAVE",
    "LVS","MGM","WYNN","CZR","PENN","DKNG","MPSA","MTN","VAIL","SIX",
    "GENI","ISPO","TNL","VAC","HGV","PLYA","SOND","VCNX","SEAT","EVEX"
  ],
  "Media / Entertainment": [
    "NFLX","DIS","CMCSA","WBD","PARA","SPOT","SNAP","PINS","RDDT","MTCH",
    "FOXA","NYT","LYV","IMAX","RBLX","TTWO","EA","ATVI","ZNGA","SKLZ",
    "WWE","TKO","MSGS","EDR","AMCX","IHRT","NWSA","IPG","OMC","WPP",
    "BMBL","GRINDR","DUOL","UDMY","COUR","MGAM","GFAI","WEBR","NERD","GAMB"
  ],
  "Energy": [
    "XOM","CVX","COP","EOG","MPC","PSX","VLO","OXY","DVN","FANG",
    "HAL","SLB","BKR","MRO","APA","CTRA","SM","RRC","EQT","AR",
    "TRGP","KMI","WMB","ET","MPLX","EPD","LNG","CQP","NFE","GLNG",
    "CCJ","UEC","NXE","DNN","UUUU","UR","PDN","LEU","BWXT","NNE",
    "HES","PXD","MTDR","VTLE","CHRD","NOG","BATL","ESTE","MGOL","PBF",
    "DKL","PARR","CLMT","CAPL","CALUMET","FRO","TK","DHT","INSW","STNG"
  ],
  "EV / Clean Energy": [
    "TSLA","RIVN","LCID","F","GM","NIO","XPEV","LI","BLNK","CHPT",
    "EVGO","ENPH","FSLR","RUN","ARRY","SEDG","PLUG","BE","STEM","NOVA",
    "RKLB","ASTS","JOBY","ACHR","LILM","KTOS","AVAV","BLDP","HYLN","WKHS",
    "NKLA","FSR","GOEV","RIDE","SOLO","AYRO","ZAPP","NIU","SOLO","IDEX",
    "NOVA","MAXN","CSIQ","JKS","DQ","SPWR","SHLS","FLUX","HASI","CWEN"
  ],
  "ETFs": [
    "SPY","QQQ","IWM","DIA","VTI","VOO","SOXX","SMH","XLK","XLF",
    "XLE","XLV","XLY","XLI","XLC","ARKK","GLD","SLV","TLT","HYG",
    "SQQQ","TQQQ","SOXL","SOXS","UVXY","XBI","IBB","GDX","GDXJ","USO",
    "BOTZ","ROBO","DRIV","ICLN","QCLN","LIT","COPX","SIL","PAVE","CIBR",
    "XLP","XLU","XLRE","XLB","ARKW","ARKG","ARKF","ARKQ","ARKX","IZRL",
    "KWEB","EEM","EFA","VEA","VWO","BND","AGG","LQD","BITO","GBTC"
  ],
  "Real Estate / REITs": [
    "AMT","PLD","EQIX","CCI","SBAC","DLR","PSA","EXR","AVB","EQR",
    "MAA","UDR","VTR","WELL","O","NNN","SPG","BXP","VNO","HST",
    "RHP","IRM","CUBE","LSI","ELS","SUI","KIM","REG","BRX","UDR",
    "VICI","MPW","OHI","PEAK","CTRE","SBRA","GMRE","AHR","INVH","AMH",
    "TRNO","FR","EGP","REXR","STAG","LXP","PLYM","GTY","ADC","EPRT"
  ],
  "Industrials / Defense": [
    "CAT","DE","HON","GE","BA","RTX","LMT","NOC","GD","LHX",
    "MMM","EMR","ETN","PH","ROK","AME","CARR","OTIS","TT","IR",
    "AXON","LDOS","SAIC","CACI","HII","BWXT","HEI","TDG","HEICO","CW",
    "PWR","MYR","FIX","IESC","WMS","AEYE","ACMR","RCAT","JOBY","RDW",
    "UBER","XPO","SAIA","ODFL","JBHT","KNX","WERN","CHRW","EXPD","GXO",
    "UPS","FDX","AMZN","DHLGY","ZTO","YMM","GRAB","FWRD","LSTR","ECHO"
  ],
  "Crypto / Web3": [
    "COIN","MSTR","MARA","RIOT","CLSK","HUT","BTBT","CIFR","IREN","CORZ",
    "IBIT","FBTC","GBTC","BITB","SQ","HOOD","PYPL","BKKT","MGNI","ACMR",
    "WULF","BRRR","HODL","EZBC","ARKB","DEFI","BITQ","SATO","HIVE","BTBT"
  ],
  "Small Cap / High Beta": [
    "APP","DUOL","CELH","RDDT","SOUN","RXRX","DOCS","GRAB","SE","NU",
    "HIMS","LMND","ROOT","OPEN","RDFN","OPFI","CMAX","PSFE","CLNE","MKFG",
    "IONQ","QBTS","RGTI","QUBT","ARQQ","DFNS","BBAI","SYM","NVTS","AIOT",
    "LUNR","RDW","RKLB","DNMR","OUST","AEVA","LIDR","MVIS","INVZ","LAZR",
    "CLOV","GREE","CIFR","MARA","RIOT","BTBT","CLSK","CORZ","HUT","IREN"
  ],
  "Commodities / Materials": [
    "FCX","NEM","GOLD","AEM","WPM","PAAS","AG","CDE","HL","USAS",
    "CLF","X","NUE","STLD","CMC","RS","AA","CENX","KALU","ARNC",
    "MOS","CF","NTR","IPI","SMG","FMC","CC","OLN","LYB","HUN",
    "RIO","BHP","VALE","MT","PKX","SID","SCCO","TECK","HBM","CDE",
    "MP","NOVN","AMMO","POWW","ATI","TS","TRQ","SBSW","SAND","MAG"
  ],
  "Utilities": [
    "NEE","SO","DUK","AEP","EXC","XEL","PEG","ES","FE","ETR",
    "WEC","CMS","DTE","PPL","AEE","NI","EVRG","OGE","PNW","AVA",
    "AGR","CLNE","BEP","CWEN","HASI","NEP","AY","PEGI","TERP","RUN"
  ],
  "Retail Favorites / Meme": [
    "GME","AMC","PLTR","SOFI","MSTR","RIVN","NIO","SPCE","BB","WISH",
    "SNAP","PINS","RBLX","UBER","SHOP","NET","DKNG","PENN","HOOD","LCID",
    "BBBY","MULN","CLOV","WKHS","NKLA","RIDE","GOEV","SPCE","OPEN","OFIR",
    "NERD","GFAI","EBET","GRPN","CODA","PHUN","VERB","ILUS","GMVD","BACK"
  ],
};


const ALL_SCANNER_TICKERS = [...new Set(Object.values(SCANNER_UNIVERSE).flat())];
const SCANNER_RESULTS_CACHE_KEY = "auctus_last_scanner_results";

function readScannerResultsCache() {
  try {
    const raw = localStorage.getItem(SCANNER_RESULTS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return { results: parsed, completedAt: null };
    }
    if (parsed?.results?.length) {
      return { results: parsed.results, completedAt: parsed.completedAt ?? null };
    }
  } catch {}
  return null;
}

function writeScannerResultsCache(results, completedAt) {
  try {
    localStorage.setItem(SCANNER_RESULTS_CACHE_KEY, JSON.stringify({
      results,
      completedAt,
      savedAt: Date.now(),
    }));
  } catch {}
}

async function fetchTickerForScanner(sym, priorBuyScore = null) {
  try {
    const sectorEtf = TICKER_SECTOR_MAP[sym];
    let scanUrl = `/api/scan?symbol=${encodeURIComponent(sym)}&includePhase=1${sectorEtf ? `&sectorEtf=${encodeURIComponent(sectorEtf)}` : ""}`;
    if (priorBuyScore != null && Number.isFinite(Number(priorBuyScore))) {
      scanUrl += `&priorBuyScore=${encodeURIComponent(priorBuyScore)}`;
    }
    const res = await fetch(scanUrl);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.closes || data.closes.length < 20) return null;
    return data;
  } catch { return null; }
}

// ─── AUTO TRADER PANEL ────────────────────────────────────────────────────────
function OptionsAnalysisPanel({ candidates, type, onClose, onAddToWatchlist, watchlistSymbols = [], anthropicKey }) {
  const [tableFilter, setTableFilter] = React.useState("ALL");
  const [aiSummary, setAiSummary] = React.useState(null);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [selectedTicker, setSelectedTicker] = React.useState(null); // { symbol, result }
  const accentColor = type === "PUTS" ? T.red : T.green;
  const accentBg = type === "PUTS" ? `${T.red}12` : `${T.green}12`;

  // Derived stats
  const total = candidates.length;
  const gateTriggered = candidates.filter(c => c.result?.gate?.label === "GO").length;
  const oversoldRSI = candidates.filter(c => c.result?.rsi != null && c.result.rsi < 30).length;
  const highConviction = type === "PUTS"
    ? candidates.filter(c => c.result?.buyScore <= -8.5 && c.result?.rsi >= 30 && c.result?.cmf != null && c.result.cmf < -0.05).length
    : candidates.filter(c => c.result?.buyScore >= 8.5 && c.result?.rsi <= 70 && c.result?.cmf != null && c.result.cmf > 0.05).length;
  const positiveCMF = type === "PUTS"
    ? candidates.filter(c => c.result?.cmf != null && c.result.cmf > 0).length
    : candidates.filter(c => c.result?.cmf != null && c.result.cmf < 0).length;
  const outperfSPY = type === "PUTS"
    ? candidates.filter(c => c.result?.rs?.outperforming === true).length
    : candidates.filter(c => c.result?.rs?.outperforming === false).length;

  // Conflict flags per ticker
  const withFlags = candidates.map(c => {
    const r = c.result;
    const flags = [];
    if (type === "PUTS") {
      if (r.rsi != null && r.rsi < 30)       flags.push({ label: "RSI <30", type: "bounce" });
      if (r.cmf != null && r.cmf > 0)        flags.push({ label: "CMF+", type: "conflict" });
      if (r.rs?.outperforming)               flags.push({ label: "RS+SPY", type: "conflict" });
      if (r.buyScore <= -8.5 && r.rsi >= 30 && r.cmf != null && r.cmf < -0.05) flags.push({ label: "HIGH CONV", type: "strong" });
    } else {
      if (r.rsi != null && r.rsi > 70)       flags.push({ label: "RSI >70", type: "bounce" });
      if (r.cmf != null && r.cmf < 0)        flags.push({ label: "CMF-", type: "conflict" });
      if (r.rs?.outperforming === false)      flags.push({ label: "RS-SPY", type: "conflict" });
      if (r.buyScore >= 8.5 && r.rsi <= 70 && r.cmf != null && r.cmf > 0.05) flags.push({ label: "HIGH CONV", type: "strong" });
    }
    return { ...c, flags };
  });

  // Table filter
  const tableFiltered = withFlags.filter(c => {
    if (tableFilter === "ALL") return true;
    if (tableFilter === "HIGH_CONV") return c.flags.some(f => f.type === "strong");
    if (tableFilter === "OVERSOLD") return c.flags.some(f => f.type === "bounce");
    if (tableFilter === "CONFLICTED") return c.flags.some(f => f.type === "conflict");
    if (tableFilter === "CMF_CONFLICT") return c.flags.some(f => f.label === "CMF+" || f.label === "CMF-");
    if (tableFilter === "RS_CONFLICT") return c.flags.some(f => f.label === "RS+SPY" || f.label === "RS-SPY");
    return true;
  }).sort((a, b) => (a.result?.buyScore ?? 0) - (b.result?.buyScore ?? 0) * (type === "PUTS" ? 1 : -1));

  // AI narrative
  const runAI = async () => {
    setAiLoading(true);
    try {
      const top5 = withFlags
        .filter(c => c.flags.some(f => f.type === "strong"))
        .slice(0, 5)
        .map(c => `${c.symbol} (score ${c.result.buyScore}, RSI ${c.result.rsi?.toFixed(1)}, CMF ${c.result.cmf?.toFixed(3)})`)
        .join(", ");
      const conflicted = withFlags
        .filter(c => c.flags.some(f => f.type === "conflict" || f.type === "bounce"))
        .slice(0, 5)
        .map(c => `${c.symbol} (${c.flags.map(f => f.label).join(", ")})`)
        .join(", ");
      const prompt = `You are a professional options trader and technical analyst. Analyze this ${type} signal landscape from the Auctus scanner.

Total ${type} candidates: ${total}
Gate triggered (entry ready): ${gateTriggered}
${type === "PUTS" ? "Oversold RSI <30 (bounce risk):" : "Overbought RSI >70 (pullback risk):"} ${oversoldRSI}
High conviction (score ${type === "PUTS" ? "≤ −8.5" : "≥ +8.5"}): ${highConviction}
${type === "PUTS" ? "Positive CMF conflicts:" : "Negative CMF conflicts:"} ${positiveCMF}
Outperforming SPY conflicts: ${outperfSPY}

Top high-conviction setups: ${top5 || "none"}
Conflicted/questionable signals: ${conflicted || "none"}

Write 2-3 paragraphs covering: (1) overall ${type} landscape and macro context, (2) top setups worth watching and why, (3) signals to avoid or approach with caution. Be direct and actionable. No disclaimers.`;

      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          model: "claude-sonnet-4-20250514",
          max_tokens: 600,
          ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      const responseText = data.content?.[0]?.text || "No response";
      setAiSummary(responseText);
    } catch (e) { setAiSummary(e.message); }
    setAiLoading(false);
  };

  const flagColor = (type) =>
    type === "strong" ? T.green : type === "bounce" ? T.red : T.orange;
  const flagBg = (type) =>
    type === "strong" ? `${T.green}18` : type === "bounce" ? `${T.red}18` : `${T.orange}18`;

  // Signal detail modal component — inline for direct T/F access
  const SignalModal = ({ item, onClose: closeModal }) => {
    const r = item.result;
    const verdict = calcVerdict({ result: r, aiCaution: false });
    const verdictColor = verdict ? verdict.darkColor || verdict.color : T.textSub;
    const mono = { fontFamily: "'JetBrains Mono', monospace" };
    const row = (label, value, color) => (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${T.border}` }}>
        <span style={{ fontSize: F.xs, color: T.textDim }}>{label}</span>
        <span style={{ fontSize: F.sm, fontWeight: 700, color: color || T.text, ...mono }}>{value}</span>
      </div>
    );
    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        onClick={closeModal}>
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 14, width: "min(520px, 95vw)", maxHeight: "85vh", overflowY: "auto", animation: "fadeIn 0.15s ease" }}
          onClick={e => e.stopPropagation()}>
          {/* Modal header */}
          <div style={{ padding: "14px 20px 14px 28px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: F.xl, fontWeight: 900, ...mono, color: T.text }}>{item.symbol}</span>
            <span style={{ fontSize: F.sm, color: T.textSub, ...mono }}>${r.price?.toFixed(2)}</span>
            {verdict && (
              <span style={{ padding: "3px 10px", borderRadius: 5, background: (verdict.darkBg || verdict.bg), border: `1px solid ${verdictColor}44`, fontSize: F.xs, fontWeight: 700, color: verdictColor }}>
                {verdict.layer === "buy" ? "▲" : verdict.layer === "bearish" ? "▼" : verdict.layer === "watch" ? "◆" : "◈"} {verdict.label}
              </span>
            )}
            <button onClick={closeModal} style={{ marginLeft: "auto", background: "none", border: "none", color: T.textSub, fontSize: F.lg, cursor: "pointer" }}>✕</button>
          </div>

          <div style={{ padding: "16px 20px" }}>
            {/* Score bar */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em" }}>COMPOSITE SCORE</span>
                <span style={{ fontSize: F.lg, fontWeight: 900, ...mono, color: r.buyScore >= 5 ? T.green : r.buyScore <= -5 ? T.red : T.yellow }}>
                  {r.buyScore > 0 ? "+" : ""}{r.buyScore}
                </span>
              </div>
              <div style={{ height: 6, background: T.border, borderRadius: 3 }}>
                <div style={{ height: "100%", width: `${Math.min(Math.abs(r.buyScore) / 14 * 100, 100)}%`, background: r.buyScore >= 5 ? T.green : r.buyScore <= -5 ? T.red : T.yellow, borderRadius: 3 }} />
              </div>
            </div>

            {/* Verdict reason */}
            {verdict?.reason && (
              <div style={{ padding: "10px 12px", background: T.bg, borderRadius: 8, marginBottom: 14, fontSize: F.xs, color: T.textSub, lineHeight: 1.7, borderLeft: `3px solid ${verdictColor}` }}>
                {verdict.reason}
              </div>
            )}

            {/* Core indicators */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 8 }}>INDICATORS</div>
              {row("RSI (14)", formatRsiWithDivergence(r.rsi, r.rsiDiv, 0), rsiDisplayColor(r.rsi, T))}
              {row("RSI Divergence", r.rsiDiv?.bullish ? "✅ Bullish" : r.rsiDiv?.bearish ? "⚠ Bearish" : "—", r.rsiDiv?.bullish ? T.green : r.rsiDiv?.bearish ? T.orange : T.textDim)}
              {row("MACD", r.macd?.crossover ? "Bullish Cross ⤴" : r.macd?.crossunder ? "Bearish Cross ⤵" : r.macd?.bullish ? "Bullish ▲" : "Bearish ▼", r.macd?.crossover || r.macd?.bullish ? T.green : T.red)}
              {row("CMF", r.cmf?.toFixed(3), r.cmf > 0.05 ? T.green : r.cmf < -0.05 ? T.red : T.textSub)}
              {row("Gate", r.gate?.label || "—", r.gate?.label === "GO" ? T.green : T.red)}
              {row("52W Range", r.range52w?.score != null ? `${r.range52w.score}/100` : "—", r.range52w?.score < 25 ? T.red : r.range52w?.score > 75 ? T.green : T.textSub)}
              {row("RS vs SPY", r.rs?.spread != null ? `${r.rs.spread > 0 ? "+" : ""}${r.rs.spread.toFixed(1)}%` : "—", r.rs?.outperforming ? T.green : T.red)}
              {row("ADX", r.adx?.adx?.toFixed(1), r.adx?.trending ? T.yellow : T.textDim)}
              {row("BB width", r.bb?.isSqueeze ? BB_TIGHT_BANDS_LABEL : "Normal", r.bb?.isSqueeze ? T.yellow : T.textDim)}
              {row("Bull / Bear signals", `${r.bullCount ?? "—"} / ${r.bearCount ?? "—"}`, T.textSub)}
            </div>

            {/* Targets */}
            {(r.t1 || r.t2 || r.d1 || r.d2) && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 8 }}>PRICE TARGETS</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {r.t1 && <div style={{ flex: 1, padding: "8px 10px", background: `${T.green}10`, border: `1px solid ${T.green}33`, borderRadius: 7 }}>
                    <div style={{ fontSize: 9, color: T.green, marginBottom: 2 }}>T1</div>
                    <div style={{ fontSize: F.md, fontWeight: 800, ...mono, color: T.green }}>${r.t1?.toFixed(2)}</div>
                  </div>}
                  {r.t2 && <div style={{ flex: 1, padding: "8px 10px", background: `${T.green}08`, border: `1px solid ${T.green}22`, borderRadius: 7 }}>
                    <div style={{ fontSize: 9, color: T.green, marginBottom: 2 }}>T2</div>
                    <div style={{ fontSize: F.md, fontWeight: 800, ...mono, color: "#4ade80" }}>${r.t2?.toFixed(2)}</div>
                  </div>}
                  {r.d1 && <div style={{ flex: 1, padding: "8px 10px", background: `${T.red}10`, border: `1px solid ${T.red}33`, borderRadius: 7 }}>
                    <div style={{ fontSize: 9, color: T.red, marginBottom: 2 }}>D1</div>
                    <div style={{ fontSize: F.md, fontWeight: 800, ...mono, color: T.red }}>${r.d1?.toFixed(2)}</div>
                  </div>}
                  {r.d2 && <div style={{ flex: 1, padding: "8px 10px", background: `${T.red}08`, border: `1px solid ${T.red}22`, borderRadius: 7 }}>
                    <div style={{ fontSize: 9, color: T.red, marginBottom: 2 }}>D2</div>
                    <div style={{ fontSize: F.md, fontWeight: 800, ...mono, color: "#f87171" }}>${r.d2?.toFixed(2)}</div>
                  </div>}
                </div>
              </div>
            )}

            {/* Action guidance */}
            {verdict?.action && (
              <div style={{ padding: "10px 12px", background: `${T.purple}10`, border: `1px solid ${T.purple}33`, borderRadius: 8, fontSize: F.xs, color: T.textSub, lineHeight: 1.7 }}>
                <strong style={{ color: T.purple }}>ACTION: </strong>{verdict.action}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ── Export functions ────────────────────────────────────────────────────────
  const [exportMsg, setExportMsg] = React.useState(null);

  const exportCSV = () => {
    const headers = ["Symbol","Score","Signal","Verdict","RSI","CMF","MACD","Gate","BB_Squeeze","RVOL","OBV","Williams_R","ADX","Weekly_Trend","RS_vs_SPY","52W_Range","IV_Rank","EMA9_Above_EMA21","Price_At_EMA9","Conviction_Flags"];
    const rows = withFlags.map(c => {
      const r = c.result;
      return [
        c.symbol,
        r.buyScore ?? "",
        r.signal ?? "",
        calcVerdict({ result: r, aiCaution: false })?.short ?? "",
        r.rsi?.toFixed(1) ?? "",
        r.cmf?.toFixed(3) ?? "",
        r.macd?.bullish ? "BULL" : r.macd?.crossunder ? "CROSS_DOWN" : "BEAR",
        r.gate?.label ?? "",
        r.bb?.isSqueeze ? "YES" : "NO",
        r.rvol?.ratio?.toFixed(2) ?? "",
        r.obv?.bullishDiv ? "BULL_DIV" : r.obv?.bearishDiv ? "BEAR_DIV" : r.obv?.confirming ? "CONFIRMING" : "NEUTRAL",
        r.williamsR?.value?.toFixed(1) ?? "",
        r.adx?.adx?.toFixed(1) ?? "",
        r.weeklyTrend?.label ?? "",
        r.rs?.outperforming ? "OUTPERFORM" : r.rs?.outperforming === false ? "LAGGING" : "",
        r.range52w?.score ?? "",
        r.bb?.ivRank ?? "",
        r.ema9AboveEma21 ? "YES" : "NO",
        r.nineEmaEntry ? "YES" : "NO",
        c.flags.map(f => f.label).join("|"),
      ].map(v => `"${v}"`).join(",");
    });
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `auctus_${type.toLowerCase()}_signals_${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    setExportMsg("CSV downloaded"); setTimeout(() => setExportMsg(null), 2500);
  };

  const exportAIPrompt = () => {
    const rows = withFlags.map(c => {
      const r = c.result;
      const v = calcVerdict({ result: r, aiCaution: false });
      return `${c.symbol}: score=${r.buyScore > 0 ? "+" : ""}${r.buyScore}, RSI=${r.rsi?.toFixed(1)}, CMF=${r.cmf?.toFixed(3)}, gate=${r.gate?.label}, MACD=${r.macd?.bullish ? "bull" : "bear"}, squeeze=${r.bb?.isSqueeze ? "YES" : "no"}, RVOL=${r.rvol?.ratio?.toFixed(1)}x, RS=${r.rs?.outperforming ? "outperform" : "lag"}, IV=${r.bb?.ivRank ?? "?"}/100, EMA9_entry=${r.nineEmaEntry ? "YES" : "no"}, flags=[${c.flags.map(f => f.label).join(", ") || "clean"}]`;
    });
    const prompt = `You are a professional options trader. Below is a dataset of ${total} ${type} signal candidates from the Auctus Analytics scanner. Each ticker shows key technical metrics. Your task:

1. Identify the 3-5 highest conviction ${type} setups and explain why
2. Flag any tickers with conflicting signals worth avoiding
3. Note any macro patterns across the dataset (sector concentration, IV environment, etc.)
4. Give a clear recommended action for the top setups: strike zone, expiry range, entry timing

DATASET (${new Date().toLocaleDateString()}):
${rows.join("\n")}

CONTEXT:
- Score range: -12 to +12. ${type === "CALLS" ? "+7 or above with gate GO = high conviction calls setup." : "-7 or below with gate GO = high conviction puts setup."}
- Gate GO = all 14 entry conditions met. NOT YET = waiting on 1-2 conditions.
- IV Rank: 0-35 = cheap options (good to buy). 70+ = expensive (consider spreads).
- EMA9_entry YES = price pulling back to 9-day EMA — ideal entry timing.
- Flags: HIGH CONV = all signals aligned. CMF- = distribution conflict on calls. RSI >70 = overbought.`;
    navigator.clipboard.writeText(prompt).then(() => {
      setExportMsg("Prompt copied to clipboard"); setTimeout(() => setExportMsg(null), 2500);
    }).catch(() => {
      const blob = new Blob([prompt], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `auctus_${type.toLowerCase()}_ai_prompt.txt`;
      a.click(); URL.revokeObjectURL(url);
      setExportMsg("Prompt downloaded"); setTimeout(() => setExportMsg(null), 2500);
    });
  };

  return (
    <div style={{ background: T.surface, borderBottom: `1px solid ${T.border}`, marginBottom: 0, maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
      {/* Ticker detail modal */}
      {selectedTicker && <SignalModal item={selectedTicker} onClose={() => setSelectedTicker(null)} />}
      {/* Header */}
      <div style={{ padding: "12px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: F.md, fontWeight: 800, color: accentColor }}>
          {type === "PUTS" ? "🔻" : "⚡"} {type} SIGNAL ANALYSIS
        </span>
        <span style={{ fontSize: F.xs, color: T.textDim }}>
          {total} candidates · Scan data
        </span>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          {exportMsg && <span style={{ fontSize: F.xs, color: T.green, fontWeight: 600 }}>✓ {exportMsg}</span>}
          <button onClick={exportCSV}
            title="Download full dataset as CSV — import into Excel, Google Sheets, or any AI tool"
            style={{ padding: "4px 10px", background: `${T.cyan}18`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer", fontWeight: 600 }}>
            ↓ CSV
          </button>
          <button onClick={exportAIPrompt}
            title="Copy a formatted prompt with the full dataset — paste into ChatGPT, Gemini, or any AI for independent analysis"
            style={{ padding: "4px 10px", background: `${T.purple}18`, border: `1px solid ${T.purple}44`, color: T.purple, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer", fontWeight: 600 }}>
            ✦ Copy AI Prompt
          </button>
          <button onClick={onClose}
            style={{ padding: "4px 12px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer" }}>
            ✕ Close
          </button>
        </div>
      </div>

      <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 16 }}>
        {/* Summary metric cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
          {[
            { label: "TOTAL CANDIDATES", value: total, color: T.textSub, sub: `${type} signals found` },
            { label: "GATE TRIGGERED", value: gateTriggered, color: gateTriggered > 0 ? accentColor : T.textDim, sub: "entry conditions met" },
            { label: type === "PUTS" ? "OVERSOLD RSI <30" : "OVERBOUGHT RSI >70", value: oversoldRSI, color: T.orange, sub: type === "PUTS" ? "bounce risk on puts" : "pullback risk on calls" },
            { label: "HIGH CONVICTION", value: highConviction, color: T.green, sub: `score ${type === "PUTS" ? "≤ −8" : "≥ +8"}, clean signals` },
          ].map(({ label, value, color, sub }) => (
            <div key={label} style={{ padding: "12px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 28, fontWeight: 900, fontFamily: "'JetBrains Mono', monospace", color, lineHeight: 1 }}>{value}</div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>{sub}</div>
            </div>
          ))}
        </div>

        {/* Conflict flag cards — 2x2 grid, clickable as filters */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          {[
            {
              filterKey: "OVERSOLD",
              label: type === "PUTS" ? "RSI Below 30 — Bounce Risk" : "RSI Above 70 — Pullback Risk",
              count: oversoldRSI, total,
              color: T.red, bg: `${T.red}10`, border: `${T.red}33`,
              desc: type === "PUTS" ? "Oversold stocks can snap back violently. Puts entered here risk being whipsawed even when the bearish thesis is correct." : "Overbought — momentum may already be exhausted.",
              tickers: withFlags.filter(c => c.flags.some(f => f.type === "bounce")).map(c => c.symbol),
            },
            {
              filterKey: "CMF_CONFLICT",
              label: type === "PUTS" ? "Positive CMF — Money Flowing In" : "Negative CMF — Distribution",
              count: positiveCMF, total,
              color: T.orange, bg: `${T.orange}10`, border: `${T.orange}33`,
              desc: type === "PUTS" ? "CMF positive means institutions are buying at current levels. A puts signal fighting institutional accumulation is lower conviction." : "CMF negative despite bullish setup — smart money may be distributing into the rally.",
              tickers: withFlags.filter(c => c.flags.some(f => f.label === "CMF+" || f.label === "CMF-")).map(c => c.symbol),
            },
            {
              filterKey: "RS_CONFLICT",
              label: type === "PUTS" ? "Outperforming SPY — Relative Strength Conflict" : "Underperforming SPY — Weak RS",
              count: outperfSPY, total,
              color: T.orange, bg: `${T.orange}10`, border: `${T.orange}33`,
              desc: type === "PUTS" ? "Stock outperforming the index while showing put signals — relative strength is a headwind for the bearish thesis." : "Lagging SPY despite bullish signals — money is rotating away from this name.",
              tickers: withFlags.filter(c => c.flags.some(f => f.label === "RS+SPY" || f.label === "RS-SPY")).map(c => c.symbol),
            },
            {
              filterKey: "HIGH_CONV",
              label: "High Conviction — Clean Signal Alignment",
              count: highConviction, total,
              color: T.green, bg: `${T.green}10`, border: `${T.green}33`,
              desc: type === "PUTS" ? "Score ≤ −8.5, RSI above 30 (no bounce risk), negative CMF (no accumulation), underperforming SPY. All signals aligned — highest probability setups." : "Score ≥ +8.5, RSI below 70, CMF > 0.05 (institutional accumulation confirmed). All three aligned — highest probability call setups.",
              tickers: withFlags.filter(c => c.flags.some(f => f.type === "strong")).map(c => c.symbol),
            },
          ].map(card => {
            const isActive = tableFilter === card.filterKey;
            return (
              <div key={card.label}
                onClick={() => setTableFilter(isActive ? "ALL" : card.filterKey)}
                style={{ padding: "12px 14px", background: isActive ? card.bg.replace("10", "22") : card.bg, border: `2px solid ${isActive ? card.color : card.border}`, borderRadius: 8, cursor: "pointer", transition: "border 0.15s, background 0.15s", position: "relative" }}>
                {/* Active indicator */}
                {isActive && (
                  <div style={{ position: "absolute", top: 8, right: 10, fontSize: 10, fontWeight: 700, color: card.color, background: `${card.color}22`, padding: "1px 7px", borderRadius: 4 }}>
                    ● FILTERING
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: F.md, fontWeight: 800, color: card.color }}>{card.count}</span>
                  <span style={{ fontSize: F.xs, fontWeight: 700, color: card.color }}>{card.label}</span>
                </div>
                <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.6, marginBottom: 8 }}>{card.desc}</div>
                {card.tickers.length > 0 && (
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {card.tickers.map(sym => {
                      const item = candidates.find(c => c.symbol === sym);
                      return (
                        <span key={sym}
                          onClick={e => { e.stopPropagation(); item && setSelectedTicker(item); }}
                          style={{ padding: "2px 7px", borderRadius: 4, background: `${card.color}18`, border: `1px solid ${card.color}33`, fontSize: 10, fontWeight: 700, color: card.color, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}>
                          {sym}
                        </span>
                      );
                    })}
                  </div>
                )}
                <div style={{ fontSize: 9, color: T.textDim, marginTop: 8 }}>
                  {isActive ? "Click to show all" : "Click to filter below"}
                </div>
              </div>
            );
          })}
        </div>

        {/* AI Narrative */}
        <div style={{ background: T.bg, border: `1px solid ${T.purple}33`, borderRadius: 8, padding: "14px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: aiSummary ? 12 : 0 }}>
            <span style={{ fontSize: F.xs, fontWeight: 700, color: T.purple, letterSpacing: "0.08em" }}>✦ AUCTUS AI — {type} LANDSCAPE ANALYSIS</span>
            {!aiSummary && !aiLoading && (
              <button onClick={runAI}
                style={{ marginLeft: "auto", padding: "5px 14px", background: `${T.purple}22`, border: `1px solid ${T.purple}44`, color: T.purple, borderRadius: 7, fontSize: F.xs, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                ✦ Generate Analysis
              </button>
            )}
            {aiLoading && <span style={{ marginLeft: "auto", fontSize: F.xs, color: T.purple, animation: "pulse 1s infinite" }}>⏳ Analyzing {total} candidates...</span>}
            {aiSummary && (
              <button onClick={() => { setAiSummary(null); }}
                style={{ marginLeft: "auto", padding: "3px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textDim, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer" }}>
                ↻ Refresh
              </button>
            )}
          </div>
          {aiSummary && (
            <div style={{ fontSize: F.sm, color: T.textSub, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{aiSummary}</div>
          )}
          {!aiSummary && !aiLoading && (
            <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 6 }}>
              Click Generate to get a written analysis of the current {type.toLowerCase()} landscape — top setups, signals to avoid, and macro context.
            </div>
          )}
        </div>

        {/* ── TRADE IDEAS — one card per high conviction candidate ── */}
        {tableFiltered.length > 0 && (() => {
          // Helper: get next monthly expiry 30-45 DTE from today
          const getExpiry = () => {
            const today = new Date();
            const target = new Date(today);
            target.setDate(target.getDate() + 38); // aim for ~38 DTE center
            // Roll to 3rd Friday of that month
            const yr = target.getFullYear();
            const mo = target.getMonth();
            const first = new Date(yr, mo, 1);
            const dayOfWeek = first.getDay();
            const firstFriday = 1 + ((5 - dayOfWeek + 7) % 7);
            const thirdFriday = firstFriday + 14;
            const expDate = new Date(yr, mo, thirdFriday);
            // If that's less than 25 DTE, use next month
            if ((expDate - today) / 86400000 < 25) {
              const nm = new Date(yr, mo + 1, 1);
              const nDow = nm.getDay();
              const nFirst = 1 + ((5 - nDow + 7) % 7);
              return new Date(yr, mo + 1, nFirst + 14);
            }
            return expDate;
          };
          const expDate = getExpiry();
          const expStr = `${expDate.getMonth() + 1}/${expDate.getDate()}/${expDate.getFullYear()}`;
          const dte = Math.round((expDate - new Date()) / 86400000);

          return (
            <div>
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>
                TRADE IDEAS — {tableFiltered.length} SETUP{tableFiltered.length !== 1 ? "S" : ""}
              </div>
              {tableFiltered.map(({ symbol, result, flags }) => {
                const p = result.price;
                const atr = result.atr;
                if (!p || !atr) return null;

                // Strike: round to nearest $2.50 below for puts, above for calls
                const interval = p < 20 ? 0.5 : p < 50 ? 1 : p < 100 ? 2.5 : p < 200 ? 5 : 10;
                const putStrike  = Math.floor((p * 0.97) / interval) * interval; // ~3% OTM
                const callStrike = Math.ceil((p * 1.03) / interval) * interval;
                const strike = type === "PUTS" ? putStrike : callStrike;

                // Estimated premium: rough ATR-based approximation
                const estPremium = (atr * 2.2 * (dte / 30) * 0.5).toFixed(2);

                // Stop: stock price invalidation level
                const putStop  = parseFloat((p + 1.5 * atr).toFixed(2));  // puts invalid if stock rallies here
                const callStop = parseFloat((p - 1.5 * atr).toFixed(2));  // calls invalid if stock drops here
                const stopLevel = type === "PUTS" ? putStop : callStop;

                // Targets: stock price levels
                const t1 = type === "PUTS"
                  ? parseFloat((p - 1.5 * atr).toFixed(2))
                  : parseFloat((p + 1.5 * atr).toFixed(2));
                const t2 = type === "PUTS"
                  ? parseFloat((p - 3 * atr).toFixed(2))
                  : parseFloat((p + 3 * atr).toFixed(2));

                const stopPct   = Math.abs((stopLevel - p) / p * 100).toFixed(1);
                const t1Pct     = Math.abs((t1 - p) / p * 100).toFixed(1);
                const t2Pct     = Math.abs((t2 - p) / p * 100).toFixed(1);

                // IV Rank proxy using BB width percentile (0-100)
                // Low = volatility compressed = cheap options = good to buy
                // High = volatility expanded = expensive options = consider spreads
                const ivRank = result.bb?.ivRank ?? 50;
                const ivGood    = ivRank <= 35;   // on sale
                const ivAvoid   = ivRank >= 70;   // too expensive
                const ivModerate = ivRank > 35 && ivRank < 70;

                // Delta guidance based on strike distance
                const strikeDistPct = Math.abs((strike - p) / p * 100);
                const estDelta = strikeDistPct < 2 ? "~0.50 ATM" : strikeDistPct < 4 ? "~0.35–0.45" : strikeDistPct < 7 ? "~0.25–0.35" : "~0.15–0.25 (high leverage, low prob)";
                const deltaWarning = strikeDistPct >= 7;

                // 9/21 EMA pullback entry signal
                const nineEmaEntry = result.nineEmaEntry;
                const ema9AboveEma21 = result.ema9AboveEma21;

                // Auto thesis from signal data
                const deathCross = result.deathCross;
                const rsLabel = result.rs?.spread != null ? `${result.rs.spread > 0 ? "+" : ""}${result.rs.spread.toFixed(1)}%` : null;
                const thesis = type === "PUTS" ? [
                  deathCross ? "200 SMA structure bearish — SMA50 below SMA200, institutional momentum is bearish." : `Score ${result.buyScore} with bearish structure.`,
                  result.cmf < -0.05 ? `CMF ${result.cmf.toFixed(3)} shows active institutional distribution — smart money is exiting.` : "Selling pressure building in money flow.",
                  rsLabel ? `Lagging SPY by ${Math.abs(result.rs.spread).toFixed(1)}% over 63 days — money is rotating out of this name.` : "",
                  result.rsi > 50 ? `RSI ${result.rsi.toFixed(1)} still above 50 — room to fall before oversold.` : result.rsi > 30 ? `RSI ${result.rsi.toFixed(1)} — not yet oversold, momentum still has room to drop.` : "",
                ].filter(Boolean).slice(0, 2).join(" ") : [
                  result.goldenCross ? "Golden cross confirmed — SMA50 above SMA200, institutional momentum is bullish." : `Score +${result.buyScore} with bullish structure.`,
                  result.cmf > 0.08 ? `CMF ${result.cmf.toFixed(3)} — strong institutional accumulation. Real money is flowing in.` : "Buying pressure building.",
                  nineEmaEntry ? "Price pulling back to EMA9 while EMA9 is above EMA21 — textbook calls entry. This is the pullback-to-the-mean setup professionals wait for." : ema9AboveEma21 ? "EMA9 above EMA21 — short-term momentum confirmed bullish." : "",
                  rsLabel && result.rs?.outperforming ? `Outperforming SPY by ${result.rs.spread.toFixed(1)}% — leadership.` : "",
                  result.macd?.crossover ? "Fresh MACD crossover — momentum just confirmed bullish." : "",
                ].filter(Boolean).slice(0, 3).join(" ");

                const isHighConv = flags.some(f => f.type === "strong");
                const hasBounceRisk = flags.some(f => f.type === "bounce");
                const hasConflict = flags.some(f => f.type === "conflict");
                const cardBorderColor = isHighConv ? T.green : hasConflict ? T.orange : hasBounceRisk ? T.red : T.border;

                return (
                  <div key={symbol} style={{ background: T.bg, border: `1px solid ${cardBorderColor}44`, borderRadius: 10, padding: "14px 16px", marginBottom: 10 }}>
                    {/* Header */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <span onClick={() => setSelectedTicker({ symbol, result })}
                        style={{ fontSize: F.lg, fontWeight: 900, fontFamily: "'JetBrains Mono', monospace", color: T.cyan, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}>{symbol}</span>
                      <span style={{ fontSize: F.sm, color: T.textSub, fontFamily: "'JetBrains Mono', monospace" }}>${p.toFixed(2)}</span>
                      <span style={{ padding: "2px 8px", borderRadius: 4, background: accentBg, border: `1px solid ${accentColor}44`, fontSize: F.xs, fontWeight: 800, color: accentColor }}>
                        {type === "PUTS" ? "🔻 PUT" : "⚡ CALL"}
                      </span>
                      {flags.map((f, i) => (
                        <span key={i} style={{ padding: "2px 6px", borderRadius: 4, background: flagBg(f.type), border: `1px solid ${flagColor(f.type)}44`, fontSize: 9, fontWeight: 700, color: flagColor(f.type) }}>
                          {f.label}
                        </span>
                      ))}
                      <span style={{ marginLeft: "auto", fontSize: F.xs, fontWeight: 700, color: result.buyScore <= -8 || result.buyScore >= 8 ? T.green : T.textSub }}>
                        Score: {result.buyScore > 0 ? "+" : ""}{result.buyScore}
                      </span>
                      {onAddToWatchlist && (
                        <button
                          onClick={() => onAddToWatchlist(symbol)}
                          disabled={watchlistSymbols.includes(symbol)}
                          style={{ padding: "3px 10px", background: watchlistSymbols.includes(symbol) ? "transparent" : `${T.cyan}18`, border: `1px solid ${watchlistSymbols.includes(symbol) ? T.border : T.cyan + "66"}`, color: watchlistSymbols.includes(symbol) ? T.textDim : T.cyan, borderRadius: 6, fontSize: F.xs, fontWeight: 700, fontFamily: "inherit", cursor: watchlistSymbols.includes(symbol) ? "default" : "pointer", flexShrink: 0 }}>
                          {watchlistSymbols.includes(symbol) ? "✓ Watching" : "+ Watch"}
                        </button>
                      )}
                    </div>

                    {/* Thesis */}
                    <div style={{ fontSize: F.sm, color: T.textSub, lineHeight: 1.7, marginBottom: 12, padding: "8px 10px", background: T.surface, borderRadius: 6, borderLeft: `3px solid ${accentColor}` }}>
                      {thesis || "Bearish signal alignment — see metrics below."}
                    </div>

                    {/* Trade parameters grid */}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 8, marginBottom: 12 }}>
                      {[
                        { label: type === "PUTS" ? "PUT STRIKE" : "CALL STRIKE", value: `$${strike}`, sub: "~3% OTM", color: accentColor },
                        { label: "EXPIRATION", value: expStr, sub: `${dte} DTE`, color: T.cyan },
                        { label: "EST. PREMIUM", value: `~$${estPremium}`, sub: "per contract ×100", color: T.textSub },
                        { label: type === "PUTS" ? "STOP (stock)" : "STOP (stock)", value: `$${stopLevel}`, sub: `+${stopPct}% from here`, color: T.red },
                        { label: "T1 TARGET", value: `$${t1}`, sub: `${t1Pct}% move`, color: T.green },
                        (() => { const c = calcCalmar(result); return { label: "CALMAR", value: c != null ? c.toFixed(2) : "—", sub: c != null ? calmarLabel(c) : "no data", color: calmarColor(c) || T.textDim }; })(),
                      ].map(({ label, value, sub, color }) => (
                        <div key={label} style={{ padding: "8px 10px", background: T.surface, borderRadius: 7 }}>
                          <div style={{ fontSize: 9, color: T.textDim, marginBottom: 3 }}>{label}</div>
                          <div style={{ fontSize: F.sm, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color }}>{value}</div>
                          <div style={{ fontSize: 9, color: T.textDim, marginTop: 2 }}>{sub}</div>
                        </div>
                      ))}
                    </div>

                    {/* T2 and stop explanation */}
                    <div style={{ display: "flex", gap: 8, marginBottom: 12, fontSize: F.xs, color: T.textDim, flexWrap: "wrap" }}>
                      <span>T2 target: <strong style={{ color: T.green }}>${t2}</strong> ({t2Pct}% move)</span>
                      <span style={{ color: T.border }}>·</span>
                      <span>Stop: stock {type === "PUTS" ? "rallies" : "drops"} ${Math.abs(stopLevel - p).toFixed(2)} ({stopPct}%) invalidates thesis</span>
                    </div>

                    {/* IV Rank + Delta + Exit Rules + Entry Timing */}
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>

                      {/* IV Rank — the most important upgrade */}
                      <div style={{ padding: "8px 12px", background: ivGood ? `${T.green}10` : ivAvoid ? `${T.red}10` : `${T.orange}10`, border: `1px solid ${ivGood ? T.green : ivAvoid ? T.red : T.orange}33`, borderRadius: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                          <strong style={{ fontSize: F.xs, color: ivGood ? T.green : ivAvoid ? T.red : T.orange }}>
                            {ivGood ? "✓ IV RANK LOW — GOOD TIME TO BUY" : ivAvoid ? "⚠ IV RANK HIGH — PREMIUM EXPENSIVE" : "~ IV RANK MODERATE"}
                          </strong>
                          <span style={{ fontSize: 10, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: result.bb?.ivColor || T.textSub }}>
                            {ivRank}/100 — {result.bb?.ivLabel}
                          </span>
                        </div>
                        <div style={{ fontSize: F.xs, color: T.textSub, lineHeight: 1.6, fontFamily: "sans-serif" }}>
                          {ivGood
                            ? `Volatility is compressed (IV rank ${ivRank}/100). Options are relatively cheap right now — you're not overpaying for premium. A volatility expansion alone could increase your option's value even before the stock moves. This is the optimal buying environment.`
                            : ivAvoid
                            ? `Volatility is elevated (IV rank ${ivRank}/100). Options premiums are expensive. Even if the stock moves in your direction, a "vega crush" (IV dropping) can offset your gains. Consider a debit spread (buy one strike, sell another further OTM) to reduce premium cost while keeping directional exposure.`
                            : `Volatility is moderate (IV rank ${ivRank}/100). Premium is neither cheap nor expensive. Proceed with normal sizing — no specific IV advantage or headwind here.`}
                        </div>
                      </div>

                      {/* Delta guidance */}
                      <div style={{ padding: "7px 12px", background: `${T.cyan}08`, border: `1px solid ${T.cyan}22`, borderRadius: 6, fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>
                        <strong style={{ color: T.cyan }}>Δ Delta estimate: {estDelta}</strong>
                        {deltaWarning
                          ? ` — This strike is far OTM. Higher leverage but lower probability of profit. Requires a larger move to be profitable. Only consider this if you have high conviction in a fast, strong move.`
                          : strikeDistPct < 4
                          ? ` — Near-the-money strike. Balanced probability and leverage. Good all-around choice for most setups.`
                          : ` — Slightly OTM. Good balance of cost and leverage. Standard swing trade strike.`}
                      </div>

                      {/* 9/21 EMA entry timing */}
                      {type === "CALLS" && (
                        <div style={{ padding: "7px 12px", background: nineEmaEntry ? `${T.green}10` : `${T.purple}08`, border: `1px solid ${nineEmaEntry ? T.green : T.purple}22`, borderRadius: 6, fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>
                          <strong style={{ color: nineEmaEntry ? T.green : T.purple }}>
                            {nineEmaEntry ? "✓ IDEAL ENTRY — Price at EMA9 pullback" : ema9AboveEma21 ? "⏳ ENTRY TIMING — Wait for EMA9 pullback" : "~ EMA9/21 — Not yet aligned"}
                          </strong>
                          {nineEmaEntry
                            ? ` Price is pulling back to the 9-day EMA ($${result.ema9?.toFixed(2)}) while EMA9 is above EMA21. This is the professional "pullback to the mean" entry — buying the dip in a confirmed uptrend. Optimal entry window.`
                            : ema9AboveEma21
                            ? ` EMA9 ($${result.ema9?.toFixed(2)}) is above EMA21 ($${result.ema21?.toFixed(2)}) — trend is bullish. Wait for price to pull back to touch EMA9 for a tighter risk entry. Don't chase if extended.`
                            : ` EMA9 is not above EMA21 — short-term momentum not confirmed. Wait for alignment before entering calls.`}
                        </div>
                      )}

                      {/* Entry timing note */}
                      <div style={{ padding: "7px 12px", background: `${T.yellow}08`, border: `1px solid ${T.yellow}22`, borderRadius: 6, fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>
                        <strong style={{ color: T.yellow }}>⏰ Intraday timing:</strong> Best entries occur 9:45–10:15 AM after the open gap settles, or 3:00–4:00 PM if the stock is closing at highs on heavy volume. Avoid entering during 12–2 PM lunchtime — volume dries up and theta decay accelerates with no price movement.
                      </div>

                      {/* Explicit exit rules */}
                      <div style={{ padding: "8px 12px", background: `${T.purple}08`, border: `1px solid ${T.purple}22`, borderRadius: 6, fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>
                        <strong style={{ color: T.purple }}>📋 Exit rules:</strong>
                        {" "}Sell half at <strong style={{ color: T.green }}>+50% gain</strong> — makes the trade risk-free.
                        {" "}Exit the remainder at <strong style={{ color: T.green }}>+100% gain</strong> or at T2 target.
                        {" "}Stop-loss: exit if the option loses <strong style={{ color: T.red }}>20-25%</strong> of its value, or if the stock closes below its 8-day EMA.
                        {" "}Never hold through earnings — IV crush will destroy value regardless of direction.
                      </div>

                      {hasBounceRisk && (
                        <div style={{ padding: "7px 10px", background: `${T.red}10`, border: `1px solid ${T.red}33`, borderRadius: 6, fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>
                          <strong style={{ color: T.red }}>🔄 Oversold bounce risk:</strong> RSI {result.rsi?.toFixed(1)} is below 30 — technically oversold. Vulnerable to a snap-back rally. Size down or wait for RSI to recover above 35 before entering puts.
                        </div>
                      )}
                      {hasConflict && (
                        <div style={{ padding: "7px 10px", background: `${T.orange}10`, border: `1px solid ${T.orange}33`, borderRadius: 6, fontSize: F.xs, color: T.textSub, lineHeight: 1.6 }}>
                          <strong style={{ color: T.orange }}>⚡ Conflicting signal:</strong> {flags.filter(f => f.type === "conflict").map(f => f.label).join(", ")} — {
                            flags.some(f => f.label === "CMF+" || f.label === "CMF-")
                              ? "Institutional money flow not confirming directional thesis. Smart money may be positioned opposite."
                              : "Relative strength conflicts with thesis. Consider waiting for alignment."
                          }
                        </div>
                      )}
                      <div style={{ fontSize: 10, color: T.textDim, fontStyle: "italic" }}>
                        ⓘ IV rank is a proxy using BB width percentile (126-day lookback). True IV rank requires options chain data. Strike and premium are ATR-based estimates. Always verify in your broker. Not financial advice.
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Filterable signal table */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em" }}>SIGNAL TABLE</span>
            <span style={{ fontSize: F.xs, color: T.textDim }}>{tableFiltered.length} shown</span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 5 }}>
              {[["ALL", "All"], ["HIGH_CONV", "High Conviction"], ["OVERSOLD", type === "PUTS" ? "Oversold RSI" : "Overbought RSI"], ["CONFLICTED", "Conflicted"]].map(([id, label]) => (
                <button key={id} onClick={() => setTableFilter(id)}
                  style={{ padding: "4px 10px", background: tableFilter === id ? `${accentColor}22` : T.surfaceHi, border: `1px solid ${tableFilter === id ? accentColor : T.border}`, color: tableFilter === id ? accentColor : T.textDim, borderRadius: 6, fontSize: F.xs, fontWeight: tableFilter === id ? 700 : 400, fontFamily: "inherit", cursor: "pointer" }}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: F.sm }}>
              <thead>
                <tr>
                  {["Ticker", "Score", "RSI", "CMF", "RS vs SPY", "Calmar", "52W %", "Bears/Bulls", "Gate", "Flags"].map(h => (
                    <th key={h} style={{ padding: "6px 10px", textAlign: h === "Ticker" ? "left" : "center", fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: `2px solid ${T.border}`, background: T.surface, whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableFiltered.map(({ symbol, result, flags }) => {
                  const scoreCol = scoreDisplayColor(result.buyScore, result.gate?.label);
                  const scorePct = Math.min(Math.abs(result.buyScore) / 14 * 100, 100);
                  return (
                    <tr key={symbol} style={{ borderBottom: `1px solid ${T.border}` }}
                      onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "7px 10px", fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: T.cyan, cursor: "pointer", textDecoration: "underline", textDecorationStyle: "dotted" }}
                        onClick={() => setSelectedTicker({ symbol, result })}>{symbol}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ width: 40, height: 4, background: T.border, borderRadius: 2, flexShrink: 0 }}>
                            <div style={{ height: "100%", width: `${scorePct}%`, background: scoreCol, borderRadius: 2 }} />
                          </div>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: scoreCol, minWidth: 32 }}>
                            {result.buyScore > 0 ? "+" : ""}{result.buyScore}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: rsiDisplayColor(result.rsi, T), whiteSpace: "nowrap" }} title={rsiDisplayTooltip(result.rsi, result.rsiDiv)}>{formatRsiWithDivergence(result.rsi, result.rsiDiv, 0)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", color: result.cmf > 0.05 ? T.green : result.cmf < -0.05 ? T.red : T.textSub }}>{result.cmf?.toFixed(3)}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center", fontWeight: 700, color: result.rs?.outperforming ? T.green : T.red }}>
                        {result.rs?.spread != null ? `${result.rs.spread > 0 ? "+" : ""}${result.rs.spread.toFixed(1)}%` : "—"}
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        {(() => { const c = calcCalmar(result); const col = calmarColor(c); return c != null ? <span style={{ fontSize: F.xs, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: col }}>{c.toFixed(2)}</span> : <span style={{ color: T.textDim }}>—</span>; })()}
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "center", color: T.textSub }}>{result.range52w?.score != null ? `${result.range52w.score}/100` : "—"}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center", color: T.textSub }}>{result.bearCount ?? "—"}/{result.bullCount ?? "—"}</td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        <span style={{ padding: "2px 7px", borderRadius: 4, background: result.gate?.label === "GO" ? `${T.green}18` : `${T.red}12`, border: `1px solid ${result.gate?.label === "GO" ? T.green + "44" : T.red + "33"}`, fontSize: 10, fontWeight: 700, color: result.gate?.label === "GO" ? T.green : T.red }}>
                          {result.gate?.label || "—"}
                        </span>
                      </td>
                      <td style={{ padding: "7px 10px", textAlign: "center" }}>
                        <div style={{ display: "flex", gap: 4, justifyContent: "center", flexWrap: "wrap" }}>
                          {flags.length === 0 && <span style={{ color: T.textDim, fontSize: 10 }}>—</span>}
                          {flags.map((f, i) => (
                            <span key={i} style={{ padding: "2px 6px", borderRadius: 4, background: flagBg(f.type), border: `1px solid ${flagColor(f.type)}44`, fontSize: 9, fontWeight: 700, color: flagColor(f.type), whiteSpace: "nowrap" }}>
                              {f.label}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}


function MarketScanner({ onAddToWatchlist, watchlistSymbols, onAnalyze, scannerPreset, onPresetConsumed, onScanComplete }) {
  const cachedOnInit = readScannerResultsCache();
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [results, setResults] = useState(() => cachedOnInit?.results ?? []);
  const [scannerLoaded, setScannerLoaded] = useState(false);
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_SCANNER_FILTERS }));
  const [filtersCollapsed, setFiltersCollapsed] = useState(true);
  const [sortCol, setSortCol] = useState("rank");
  const [sortDir, setSortDir] = useState(-1);
  const [scanFailures, setScanFailures] = useState(0);
  const stopRef = useRef(false);
  const runScanRef = useRef(null);
  const runScanStaleRef = useRef(null);
  const [scanCompletedAt, setScanCompletedAt] = useState(() => cachedOnInit?.completedAt ?? null);
  const [marketBanners, setMarketBanners] = useState([]);
  const [scannerLaneTab, setScannerLaneTab] = useState("swing");

  useEffect(() => {
    (async () => {
      if (results.length > 0) {
        setScannerLoaded(true);
        return;
      }
      try {
        const saved = await load(SCANNER_RESULTS_CACHE_KEY);
        if (saved?.results?.length) {
          setResults(saved.results);
          if (saved.completedAt) setScanCompletedAt(saved.completedAt);
        } else {
          const legacy = await load("scannerResults");
          if (Array.isArray(legacy) && legacy.length > 0) {
            setResults(legacy);
            const legacyTime = await load("scannerResultsTime");
            if (legacyTime) setScanCompletedAt(legacyTime);
            writeScannerResultsCache(legacy, legacyTime || null);
          }
        }
      } catch {}
      setScannerLoaded(true);
    })();
  }, []);
  useEffect(() => {
    if (!scanCompletedAt && results.length === 0) {
      load("scannerResultsTime").then(t => { if (t) setScanCompletedAt(t); }).catch(() => {});
    }
  }, []);

  useEffect(() => {
    if (results.length > 0 && scannerLoaded) {
      writeScannerResultsCache(results, scanCompletedAt);
      save(SCANNER_RESULTS_CACHE_KEY, { results, completedAt: scanCompletedAt }).catch(() => {});
    }
  }, [results, scanCompletedAt, scannerLoaded]);

  useEffect(() => {
    if (filters.quickFilter === "PRIME") {
      setSortCol("confluencePrime");
      setSortDir(-1);
    }
  }, [filters.quickFilter]);

  useEffect(() => {
    if (!scannerPreset) return;
    setFilters(f => ({
      ...f,
      ...scannerPreset,
      validation: { ...f.validation, ...(scannerPreset.validation || {}) },
      context: { ...f.context, ...(scannerPreset.context || {}) },
    }));
    if (scannerPreset.autoRun) {
      const t = setTimeout(() => {
        runScanRef.current?.();
        onPresetConsumed?.();
      }, 100);
      return () => clearTimeout(t);
    }
    onPresetConsumed?.();
  }, [scannerPreset]);

  const fullUniverseTickers = [...new Set(Object.values(SCANNER_UNIVERSE).flat())];
  const tickers = resolveScannerTickerList({
    mode: filters.universeMode === "full" ? "full" : "core",
    theme: filters.theme || "All Themes",
    rotationSector: filters.sector || "All Sectors",
    fullUniverse: fullUniverseTickers,
    scannerUniverse: SCANNER_UNIVERSE,
  });
  const themeOptions = getScannerThemeOptions(SCANNER_UNIVERSE);
  const coreCount = buildScannerCoreTickers().length;

  const runScan = async (refreshMode = "full") => {
    setScanning(true);
    stopRef.current = false;
    const priorList = refreshMode === "stale"
      ? (results.length > 0 ? results : (readScannerResultsCache()?.results ?? []))
      : [];
    const syms = refreshMode === "stale"
      ? symbolsNeedingRescan(tickers, priorList)
      : [...tickers];
    if (syms.length === 0) {
      setScanning(false);
      return;
    }
    if (refreshMode === "full") setResults([]);
    setScanFailures(0);
    setProgress({ done: 0, total: syms.length, current: "" });

    let spyReturn = null;
    let spyCloses = null;
    let marketCtx = buildMarketContext({});
    try {
      const spyData = await fetchTickerForScanner("SPY");
      spyCloses = spyData?.closes ?? null;
      if (spyData?.closes?.[63]) spyReturn = ((spyData.closes[0] - spyData.closes[63]) / spyData.closes[63]) * 100;
      marketCtx = buildMarketContext({ spyCloses });
    } catch {}

    let tide = null;
    try {
      const r = await fetch("/api/scan?marketTide=1");
      if (r.ok) {
        const j = await r.json();
        tide = j?.marketTide ?? null;
      }
    } catch {}

    const batchSize = 24;
    const bySym = new Map(priorList.map((r) => [String(r.symbol).toUpperCase(), r]));
    let failures = 0;
    const minPrice = filters.context?.minPrice5 !== false;

    for (let i = 0; i < syms.length; i += batchSize) {
      if (stopRef.current) break;
      const batch = syms.slice(i, i + batchSize);
      const batchResults = await Promise.all(batch.map(async (sym) => {
        try {
          const prior = bySym.get(String(sym).toUpperCase())?.result?.buyScore;
          const data = await fetchTickerForScanner(sym, prior);
          if (!data) { failures += 1; return null; }
          const result = runAnalysis(data, spyReturn, isCrypto(sym), sym, tide, marketCtx);
          if (!result) { failures += 1; return null; }
          if (minPrice && result.price != null && result.price < 5) return null;
          result.scanTime = Date.now();
          return { symbol: sym, result };
        } catch {
          failures += 1;
          return null;
        }
      }));
      batchResults.filter(Boolean).forEach((r) => bySym.set(String(r.symbol).toUpperCase(), r));
      const merged = [...bySym.values()];
      setProgress({ done: Math.min(i + batchSize, syms.length), total: syms.length, current: batch.join(", ") });
      setResults(sortScannerResults(merged, sortCol, sortDir, {}));
      if (i + batchSize < syms.length) await new Promise((r) => setTimeout(r, 50));
    }

    setScanning(false);
    setScanFailures(failures);
    setProgress((p) => ({ ...p, current: "" }));
    const scanTime = new Date().toLocaleTimeString();
    const merged = [...bySym.values()];
    const sectorLabelsFinal = Object.fromEntries(
      merged.map(({ symbol }) => {
        const info = getTickerRotationSectorInfo(symbol);
        return [symbol, info.sector !== "Unknown" ? info.sector : "—"];
      })
    );
    const breadthPct20 = computeBreadthPct20(merged.map((r) => r.result?.closes).filter(Boolean));
    marketCtx = buildMarketContext({ spyCloses, breadthPct20 });
    for (const row of merged) {
      if (!row?.result) continue;
      row.result.marketCtx = marketCtx;
      attachScanLanes(row.result, { marketCtx, tickerSectorMap: TICKER_SECTOR_MAP });
      attachScannerAction(row.result);
    }
    setMarketBanners(marketContextBanners(marketCtx));

    const finalResults = sortScannerResults(merged, sortCol, sortDir, sectorLabelsFinal);
    setResults(finalResults);
    setScanCompletedAt(scanTime);
    writeScannerResultsCache(finalResults, scanTime);
    save(SCANNER_RESULTS_CACHE_KEY, { results: finalResults, completedAt: scanTime }).catch(() => {});

    const topPicks = pickTopScannerPicks(finalResults, 5, watchlistSymbols);
    onScanComplete?.(topPicks);
  };
  runScanRef.current = () => runScan("full");
  runScanStaleRef.current = () => runScan("stale");

  const sectorLabelFor = (symbol) => {
    const info = getTickerRotationSectorInfo(symbol);
    return info.sector !== "Unknown" ? info.sector : "—";
  };

  const sectorLabels = Object.fromEntries(
    results.map(({ symbol }) => [symbol, sectorLabelFor(symbol)])
  );

  const filtered = sortScannerResults(
    applyQuickFilter(
      results.filter(r => matchesScannerFilters(r.result, filters, { symbol: r.symbol })),
      filters.quickFilter,
      watchlistSymbols
    ),
    sortCol,
    sortDir,
    sectorLabels
  );
  const signalCounts = countBySignalType(filtered);
  const companyNames = useScannerCompanyNames(filtered.map(r => r.symbol));
  const displayNames = Object.fromEntries(
    filtered.map(({ symbol }) => [symbol, companyNames[symbol] || COMPANY_NAMES[symbol] || symbol])
  );
  const scrollResetKey = `${filters.quickFilter}|${filters.minStrength}|${filters.sector}|${sortCol}|${sortDir}|${filtered.length}`;

  const handleSortChange = (col, dir) => {
    setSortCol(col);
    setSortDir(dir);
  };

  return (
    <div style={{ padding: "16px 16px 16px 24px", minHeight: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap", flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <ScannerCompactFilterBar
            filters={filters}
            setFilters={setFilters}
            sortCol={sortCol}
            onSortChange={handleSortChange}
            themeOptions={themeOptions}
            T={T}
            F={F}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginLeft: "auto" }}>
          {!scanning && scanCompletedAt && results.length > 0 && (
            <>
              <span style={{ fontSize: F.xs, color: T.textDim, whiteSpace: "nowrap" }}>
                Last scan: <span style={{ color: T.green }}>{scanCompletedAt}</span> · {results.length} results
              </span>
              <button
                title="Export scanner results to CSV"
                onClick={() => {
                  const rows = [
                    ["Ticker","Verdict","Gate","R:R","Confluence","module_signal","action_resolved","scoringVersion","Score","RSI","CMF","T1","T2","Stop","ema20","ema50","ema200","pctFrom20","pctFrom50","pctFrom200","slope20","slope50","slope200","lastCross","earnings_days","iv_hv_ratio","options_flag","target_basis","setup_lane","dip_status","rsDuringDrop","lt_status","lt_quality","lt_discount_reasons","lt_tranches"].join(","),
                    ...filtered.map(({ symbol, result: r }) => {
                      const stop = r.stopPrice ?? r.tradeStop ?? (r.price && r.atr ? r.price - 2 * r.atr : null);
                      const verdict = scannerDisplayVerdict(r).verdict ?? "";
                      const gate = r.gate?.label ?? "";
                      const conf = watchlistConfluenceCell(r)?.text ?? "";
                      return [
                        symbol,
                        verdict.replace(/,/g, ";"),
                        gate,
                        r.rr1 != null ? r.rr1.toFixed(2) : "",
                        conf.replace(/,/g, ";"),
                        (r.module_signal ?? r.action ?? "").replace(/,/g, ";"),
                        (r.actionResolved ?? r.scannerAction?.label ?? "").replace(/,/g, ";"),
                        r.scoringVersion ?? "v2",
                        r.buyScore ?? "",
                        r.rsi?.toFixed(1) ?? "",
                        r.cmf?.toFixed(3) ?? "",
                        r.t1?.toFixed(2) ?? "",
                        r.t2?.toFixed(2) ?? "",
                        stop?.toFixed(2) ?? "",
                        r.ma?.ema20?.toFixed(2) ?? "",
                        r.ma?.ema50?.toFixed(2) ?? "",
                        r.ma?.ema200?.toFixed(2) ?? "",
                        r.ma?.pctFrom20?.toFixed(2) ?? "",
                        r.ma?.pctFrom50?.toFixed(2) ?? "",
                        r.ma?.pctFrom200?.toFixed(2) ?? "",
                        r.ma?.slope20?.toFixed(2) ?? "",
                        r.ma?.slope50?.toFixed(2) ?? "",
                        r.ma?.slope200?.toFixed(2) ?? "",
                        r.ma?.lastCross ? `${r.ma.lastCross.type}@${r.ma.lastCross.date ?? ""}` : "",
                        r.earnings_days ?? r.earningsDays ?? "",
                        r.iv_hv_ratio ?? r.ivHvRatio ?? "",
                        r.options_flag ?? r.optionsFlag ?? "",
                        r.target_basis ?? "",
                        r.setup_lane ?? "MOMENTUM",
                        r.dip_status ?? r.dipChecklist?.status ?? "",
                        r.rsDuringDrop != null ? r.rsDuringDrop.toFixed(2) : "",
                        r.lt_status ?? r.longTerm?.status ?? "",
                        r.lt_quality ?? "",
                        (r.lt_discount_reasons ?? r.longTerm?.discountReasons ?? []).join("|"),
                        (r.lt_tranches ?? r.longTerm?.tranches ?? []).map(t => `${t.id}:${t.price?.toFixed(2)}`).join("|"),
                      ].join(",");
                    })
                  ].join("\n");
                  const blob = new Blob([rows], { type: "text/csv" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `auctus-scanner-${new Date().toISOString().slice(0,10)}.csv`;
                  a.click(); URL.revokeObjectURL(url);
                }}
                style={{ padding: "5px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, fontSize: 11, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", borderRadius: 6 }}>
                ⬇ CSV
              </button>
            </>
          )}
          {scanning && (
            <button
              onClick={() => { stopRef.current = true; }}
              style={{ padding: "7px 12px", background: `${T.red}18`, border: `1px solid ${T.red}44`, color: T.red, fontSize: F.sm, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", borderRadius: 8 }}
            >
              ⏹ Stop
            </button>
          )}
          {!scanning && results.length > 0 && (
            <button
              type="button"
              onClick={() => runScanStaleRef.current?.()}
              style={{ padding: "7px 12px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}
              title="Rescan symbols older than 24h in current universe"
            >
              Refresh stale
            </button>
          )}
          {filtered.length > 0 && !scanning && (
            <button
              type="button"
              onClick={() => {
                const top = filtered.slice(0, 5).map((r) => r.symbol);
                top.forEach((s) => onAddToWatchlist?.(s));
              }}
              style={{ padding: "7px 12px", background: `${T.green}14`, border: `1px solid ${T.green}44`, color: T.green, borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}
            >
              + Add top 5
            </button>
          )}
          <button
            onClick={() => runScanRef.current?.()}
            disabled={scanning}
            style={{
              padding: "7px 18px",
              background: "#F5C518",
              border: "none",
              color: "#111827",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 800,
              fontFamily: "inherit",
              cursor: scanning ? "not-allowed" : "pointer",
              opacity: scanning ? 0.65 : 1,
              whiteSpace: "nowrap",
            }}
          >
            {scanning
              ? `Scanning ${progress.done}/${progress.total}...`
              : `Scan ${tickers.length} (${filters.universeMode === "full" ? "full" : `core ~${coreCount}`})`}
          </button>
        </div>
      </div>

      <ScannerFilterPanel
        filters={filters}
        setFilters={setFilters}
        T={T}
        F={F}
        collapsed={filtersCollapsed}
        onToggleCollapse={() => setFiltersCollapsed(c => !c)}
      />

      <MarketContextBanners banners={marketBanners} T={T} />

      {results.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          {[
            { id: "swing", label: "Swing scanner" },
            { id: "longterm", label: "Long-Term" },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setScannerLaneTab(tab.id)}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 700,
                fontFamily: "inherit",
                cursor: "pointer",
                border: `1px solid ${scannerLaneTab === tab.id ? T.cyan : T.border}`,
                background: scannerLaneTab === tab.id ? `${T.cyan}18` : T.surfaceHi,
                color: scannerLaneTab === tab.id ? T.cyan : T.textSub,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {scanning && (
        <div style={{ flexShrink: 0 }}>
          <div style={{ height: 4, background: T.border, borderRadius: 2, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${(progress.done / progress.total) * 100}%`, background: `linear-gradient(90deg, ${T.cyan}, ${T.green})`, transition: "width 0.3s" }} />
          </div>
          <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 4 }}>
            Scanning: {progress.current} — {progress.done}/{progress.total} done · {filtered.length} matching setups so far
            {scanFailures > 0 ? ` · ${scanFailures} failed` : ""}
          </div>
        </div>
      )}

      {!scanning && results.length === 0 && (
        <div style={{ flex: 1, overflowY: "auto", padding: "24px 20px" }}>
          <div style={{ maxWidth: 520, margin: "0 auto" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🔭</div>
              <div style={{ fontSize: F.lg, fontWeight: 700, color: T.text, marginBottom: 6 }}>Market Scanner</div>
              <div style={{ fontSize: F.sm, color: T.textDim, lineHeight: 1.6 }}>
                Scan the core rotation universe (~{coreCount} names) or full curated list ({fullUniverseTickers.length}). Filters use the same verdicts and score as your watchlist.
              </div>
            </div>
            <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: "16px 20px", marginBottom: 16 }}>
              <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 12 }}>📋 4-MODULE SIGNAL SCANNER</div>
              <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.6 }}>
                Use presets (Swing longs, Breakouts, Puts, Prime) or quick filters aligned with watchlist verdicts.
                Extended names are hidden by default; stocks under $5 are excluded when the price filter is on.
              </div>
            </div>
            <div style={{ padding: "12px 16px", background: `#7c3aed10`, border: `1px solid #7c3aed30`, borderRadius: 10, fontSize: F.xs, color: T.textDim, lineHeight: 1.6 }}>
              <strong style={{ color: "#a855f7" }}>💡 Tip:</strong> Use Sector Rotation&apos;s &quot;Scan All [Sector]&quot; to auto-fill swing long + bottom filters for hot sectors.
            </div>
          </div>
        </div>
      )}

      {!scanning && results.length > 0 && filtered.length === 0 && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
          <div style={{ textAlign: "center", maxWidth: 420 }}>
            <div style={{ fontSize: F.md, fontWeight: 700, color: T.text, marginBottom: 8 }}>No setups found matching your criteria.</div>
            <div style={{ fontSize: F.sm, color: T.textDim }}>Try adjusting the filters above.</div>
          </div>
        </div>
      )}

      {filtered.length > 0 && scannerLaneTab === "swing" && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8, minHeight: 0 }}>
          <YesterdayMoversPanel T={T} F={F} />
          <ScannerResultsBar
            total={filtered.length}
            counts={signalCounts}
            T={T}
            F={F}
          />
          <ScannerResultsTable
            rows={filtered}
            companyNames={displayNames}
            getSectorLabel={sectorLabelFor}
            watchlistSymbols={watchlistSymbols}
            onAddToWatchlist={onAddToWatchlist}
            onRowClick={onAnalyze}
            sortCol={sortCol}
            sortDir={sortDir}
            onSortChange={handleSortChange}
            totalCount={filtered.length}
            scrollResetKey={scrollResetKey}
            T={T}
            F={F}
          />
        </div>
      )}

      {results.length > 0 && scannerLaneTab === "longterm" && (
        <LongTermScannerTable
          rows={results.filter((r) => r.result?.longTerm?.status && r.result.longTerm.status !== "NONE")}
          sectorLabels={sectorLabels}
          T={T}
          onRowClick={onAnalyze}
        />
      )}
    </div>
  );
}

// ─── PERFORMANCE TRACKER ──────────────────────────────────────────────────────
function CryptoScanner({ onAddToWatchlist, watchlistSymbols, isDark, anthropicKey, openaiKey, aiProvider }) {
  // Standalone view — accessed via ₿ Crypto in the sidebar
  const [scanning, setScanning] = useState(false);
  const [cryptoTab, setCryptoTab] = useState("coins"); // "coins" | "equities"
  const [coinsResults, setCoinsResults] = useState([]);
  const [equitiesResults, setEquitiesResults] = useState([]);
  const [btcData, setBtcData] = useState(null);
  const [scanCompletedAt, setScanCompletedAt] = useState(null);
  const [cryptoLoaded, setCryptoLoaded] = useState(false);

  // Load all crypto scan data on mount
  useEffect(() => {
    (async () => {
      try {
        const [coins, equities, btc, t] = await Promise.all([
          load("cryptoScanCoins"),
          load("cryptoScanEquities"),
          load("cryptoScanBTC"),
          load("cryptoScanTime"),
        ]);
        // Restore coins — any valid scan result is fine
        if (Array.isArray(coins) && coins.length > 0) setCoinsResults(coins);
        if (Array.isArray(equities) && equities.length > 0) setEquitiesResults(equities);
        if (btc) setBtcData(btc);
        if (t) setScanCompletedAt(t);
      } catch {}
      setCryptoLoaded(true);
    })();
  }, []);

  const [progress, setProgress] = useState({ done: 0, total: 0, current: "" });
  const [sortCol, setSortCol] = useState("buyScore");
  const [sortDir, setSortDir] = useState(-1);
  const [filter, setFilter] = useState("ALL");
  const stopRef = useRef(false);
  const btcResultRef = useRef(null); // holds BTC result synchronously during scan
  const [selectedItem, setSelectedItem] = useState(null); // { symbol, result } for detail drawer
  const [aiAnalysis, setAiAnalysis] = useState({}); // keyed by symbol
  const [aiLoading, setAiLoading] = useState(null); // symbol currently loading
  const [coinWatchlist, setCoinWatchlist] = useState([]);
  useEffect(() => {
    load("cryptoCoinWatchlist").then(v => { if (Array.isArray(v) && v.length > 0) setCoinWatchlist(v); }).catch(() => {});
  }, []);
  const [showCoinWatchlist, setShowCoinWatchlist] = useState(false);
  const [showCryptoAI, setShowCryptoAI] = useState(false);
  const [cryptoAI, setCryptoAI] = useState(null);
  const [cryptoAILoading, setCryptoAILoading] = useState(false);

  const toggleCoinWatch = (sym) => {
    setCoinWatchlist(prev => {
      const next = prev.includes(sym) ? prev.filter(s => s !== sym) : [...prev, sym];
      save("cryptoCoinWatchlist", next).catch(() => {});
      return next;
    });
  };

  const [cryptoDrawerW, setCryptoDrawerW] = useState(() => {
    try { return parseInt(localStorage.getItem("cryptoDrawerW") || "420"); } catch { return 420; }
  });
  const cryptoDragRef = useRef(false);
  const startCryptoDrag = (e) => {
    e.preventDefault();
    cryptoDragRef.current = true;
    const startX = e.clientX;
    const startW = cryptoDrawerW;
    const onMove = (ev) => {
      if (!cryptoDragRef.current) return;
      const next = Math.min(900, Math.max(320, startW + (startX - ev.clientX)));
      setCryptoDrawerW(next);
    };
    const onUp = () => {
      cryptoDragRef.current = false;
      setCryptoDrawerW(w => { try { localStorage.setItem("cryptoDrawerW", String(w)); } catch {} return w; });
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const runCryptoScan = async (mode = "both") => {
    setScanning(true); stopRef.current = false;

    // Scan coins — BTC included in the loop so it always gets a fresh price
    if (mode === "both" || mode === "coins") {
      const coins = PURE_CRYPTO_COINS; // includes BTC-USD
      const coinResults = [];
      const batchSize = 3;
      setProgress({ done: 0, total: coins.length, current: "" });
      for (let i = 0; i < coins.length; i += batchSize) {
        if (stopRef.current) break;
        const batch = coins.slice(i, i + batchSize);
        const res = await Promise.all(batch.map(async sym => {
          for (let attempt = 0; attempt < 2; attempt++) {
            try {
              if (attempt > 0) await new Promise(r => setTimeout(r, 600 + Math.random() * 400));
              const data = await fetchTickerForScanner(sym);
              if (!data) continue;
              const result = runAnalysis(data, null, true, sym);
              if (!result) continue;
              result.scanTime = Date.now();
              // Keep btcData in sync for regime banner
              if (sym === "BTC-USD") {
                setBtcData(result);
                save("cryptoScanBTC", result).catch(() => {});
              }
              return { symbol: sym, result };
            } catch {}
          }
          return null;
        }));
        res.filter(Boolean).forEach(r => coinResults.push(r));
        setProgress({ done: Math.min(i + batchSize, coins.length), total: coins.length, current: batch.join(", ") });
        if (coinResults.length > 0) {
          const allSorted = [...coinResults].sort((a, b) => (b.result?.buyScore || 0) - (a.result?.buyScore || 0));
          setCoinsResults(allSorted);
          try { localStorage.setItem("cryptoScanCoins", JSON.stringify(allSorted)); } catch {}
        }
        if (i + batchSize < coins.length) await new Promise(r => setTimeout(r, 350));
      }
    }

    // Scan equities — stocks, can handle slightly larger batches
    if (mode === "both" || mode === "equities") {
      const equities = CRYPTO_EQUITIES;
      const eqResults = [];
      setProgress({ done: 0, total: equities.length, current: "" });
      for (let i = 0; i < equities.length; i += 5) {
        if (stopRef.current) break;
        const batch = equities.slice(i, i + 5);
        const res = await Promise.all(batch.map(async sym => {
          try {
            const data = await fetchTickerForScanner(sym);
            if (!data) return null;
            const result = runAnalysis(data, null, isCrypto(sym), sym);
            if (!result) return null;
            result.scanTime = Date.now();
            return { symbol: sym, result };
          } catch { return null; }
        }));
        res.filter(Boolean).forEach(r => eqResults.push(r));
        setProgress(p => ({ ...p, done: Math.min(i + 5, equities.length), total: equities.length }));
        const sortedEq = [...eqResults].sort((a, b) => (b.result?.buyScore || 0) - (a.result?.buyScore || 0));
        setEquitiesResults(sortedEq);
        try { localStorage.setItem("cryptoScanEquities", JSON.stringify(sortedEq)); } catch {}
        if (i + 5 < equities.length) await new Promise(r => setTimeout(r, 200));
      }
    }

    setScanning(false);
    setProgress(p => ({ ...p, current: "" }));
    const scanTime = new Date().toLocaleTimeString();
    setScanCompletedAt(scanTime);
    save("cryptoScanTime", scanTime).catch(() => {});
  };

  // coinsResults now always includes BTC from the scan loop
  const activeResults = cryptoTab === "coins" ? coinsResults : equitiesResults;
  const filtered = activeResults.filter(r => {
    if (filter === "BUY") return r.result.buyScore >= 4;
    if (filter === "BEAR") return r.result.buyScore <= -4;
    if (filter === "SQUEEZE") return r.result.bb?.isSqueeze;
    return true;
  });
  const sorted = [...filtered].sort((a, b) => {
    const av = a.result?.[sortCol] ?? -99;
    const bv = b.result?.[sortCol] ?? -99;
    return (bv - av) * sortDir;
  });

  // BTC regime
  const btcRegime = btcData ? (btcData.goldenCross ? "BULL" : btcData.deathCross ? "BEAR" : "NEUTRAL") : null;
  const btcRegimeColor = btcRegime === "BULL" ? T.green : btcRegime === "BEAR" ? T.red : T.yellow;

  const runAI = async (symbol, result) => {
    if (!result) return;
    setAiLoading(symbol);
    try {
      const verdict = calcVerdict({ result, aiCaution: false });
      const isPureCoin = isPureCrypto(symbol);
      const prompt = `You are a professional crypto swing trader. Give clear, direct, actionable analysis. No fluff. Talk like a trader. 3-4 paragraphs max.

Asset: ${symbol.replace("-USD", "")} (${COMPANY_NAMES[symbol] || symbol})
Type: ${isPureCoin ? "Spot cryptocurrency" : "Crypto-related equity"}
Price: $${result.price?.toFixed(2)} (${(result.pctChange ?? 0) >= 0 ? "+" : ""}${result.pctChange?.toFixed(2)}%)
Score: ${result.buyScore > 0 ? "+" : ""}${result.buyScore} | Verdict: ${verdict?.label || result.signal}
RSI: ${result.rsi?.toFixed(1)} (${isPureCoin ? "crypto thresholds: oversold <25, overbought >80" : "standard thresholds"})
MACD: ${result.macd?.crossover ? "BULLISH CROSSOVER ▲" : result.macd?.bullish ? "Bullish ▲" : result.macd?.crossunder ? "BEARISH CROSSUNDER ▼" : "Bearish ▼"}
CMF: ${result.cmf?.toFixed(3)} | BB: ${result.bb?.isSqueeze ? "SQUEEZE ⚡" : "Normal"}
Trend: ${result.goldenCross ? "Golden Cross ▲" : result.deathCross ? "200 SMA Structure ▼" : "No major cross"}
Gate: ${result.gate?.label} (${result.gate?.passed}/${result.gate?.total} conditions)
${isPureCoin ? `BTC Context: Check BTC regime before acting on altcoin signals.` : `Equity correlation: This stock moves with BTC — check BTC trend before entering.`}

What is this ${isPureCoin ? "coin" : "stock"} doing right now? What's the key signal? What should I watch? What's the trade setup if any?`;
      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      const responseText = data.content?.[0]?.text || "No response";
      setAiAnalysis(prev => ({ ...prev, [symbol]: { text: responseText, date: new Date().toLocaleString() } }));
    } catch (e) {
      setAiAnalysis(prev => ({ ...prev, [symbol]: { text: e.message, date: "" } }));
    } finally {
      setAiLoading(null);
    }
  };

  // CSV export
  const exportCSV = () => {
    const rows = (cryptoTab === "watchlist"
      ? coinWatchlist.map(sym => [...coinsResults, ...equitiesResults].find(r => r.symbol === sym)).filter(Boolean)
      : cryptoTab === "coins" ? coinsResults : equitiesResults
    );
    if (!rows.length) return;
    const headers = ["Symbol","Name","Price","Change%","Score","Signal","RSI","CMF","MACD","Trend","Gate","vs BTC","BB Squeeze","Scan Time"];
    const lines = rows.map(({ symbol, result: r }) => {
      const verdict = calcVerdict({ result: r, aiCaution: false });
      const scanDate = r.scanTime ? new Date(r.scanTime).toLocaleTimeString() : "";
      return [
        symbol.replace("-USD",""),
        COMPANY_NAMES[symbol] || symbol,
        r.price > 1 ? r.price.toFixed(2) : r.price.toFixed(4),
        r.pctChange != null ? (r.pctChange >= 0 ? "+" : "") + r.pctChange.toFixed(2) + "%" : "",
        (r.buyScore > 0 ? "+" : "") + r.buyScore,
        verdict?.short || r.signal || "",
        r.rsi?.toFixed(1) || "",
        r.cmf?.toFixed(3) || "",
        r.macd?.crossover ? "CROSS▲" : r.macd?.bullish ? "BULL▲" : r.macd?.crossunder ? "CROSS▼" : "BEAR▼",
        r.goldenCross ? "GOLDEN▲" : r.deathCross ? "200 SMA▼" : "—",
        r.gate?.label || "",
        r.rs?.spread != null ? (r.rs.spread > 0 ? "+" : "") + r.rs.spread.toFixed(1) + "%" : "",
        r.bb?.isSqueeze ? "YES" : "NO",
        scanDate,
      ].map(v => `"${String(v).replace(/"/g,'""')}"`).join(",");
    });
    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `auctus_crypto_${cryptoTab}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Tooltip component — inline hover explanation
  const Tip = ({ text, children }) => {
    const [show, setShow] = React.useState(false);
    return (
      <span style={{ position: "relative", display: "inline-block" }}
        onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
        {children}
        {show && (
          <div style={{ position: "fixed", width: 210, background: "#1a1a2e", border: `1px solid ${T.border}`, borderRadius: 7, padding: "8px 11px", fontSize: 10, color: "#ccc", lineHeight: 1.7, zIndex: 9999, pointerEvents: "none", whiteSpace: "normal", boxShadow: "0 6px 24px rgba(0,0,0,0.7)", marginTop: 28 }}>
            <span style={{ fontWeight: 700, color: "#fff", display: "block", marginBottom: 3 }}>{children}</span>
            {text}
          </div>
        )}
      </span>
    );
  };

  const TH = ({ label, col, tip }) => (
    <th onClick={() => { if (sortCol === col) setSortDir(d => -d); else { setSortCol(col); setSortDir(-1); } }}
      style={{ padding: "7px 10px", fontSize: F.sm, fontWeight: 700, color: sortCol === col ? T.cyan : T.textDim, letterSpacing: "0.06em", textAlign: "center", cursor: "pointer", whiteSpace: "nowrap", borderBottom: `2px solid ${T.border}`, background: T.surface, userSelect: "none" }}>
      {tip ? <Tip text={tip}><span style={{ borderBottom: `1px dotted ${T.textDim}` }}>{label}</span></Tip> : label}
      {sortCol === col ? (sortDir === -1 ? " ▼" : " ▲") : ""}
    </th>
  );

  const TH2 = ({ label, tip }) => (
    <th style={{ padding: "7px 10px", fontSize: F.sm, fontWeight: 700, color: T.textDim, letterSpacing: "0.06em", textAlign: "center", borderBottom: `2px solid ${T.border}`, background: T.surface }}>
      {tip ? <Tip text={tip}><span style={{ borderBottom: `1px dotted ${T.textDim}` }}>{label}</span></Tip> : label}
    </th>
  );

  return (
    <>
    <div style={{ padding: "16px 16px 16px 24px", flex: 1, display: "flex", flexDirection: "column", gap: 12, minHeight: 0, overflow: "hidden" }}>

      {/* BTC Regime banner */}
      {btcData && (
        <div style={{ padding: "8px 14px", background: `${btcRegimeColor}10`, border: `1px solid ${btcRegimeColor}44`, borderRadius: 8, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{ fontSize: F.xs, fontWeight: 800, color: btcRegimeColor }}>₿ BTC REGIME: {btcRegime}</div>
          <div style={{ fontSize: F.xs, color: T.textDim }}>
            RSI <span style={{ color: rsiDisplayColor(btcData.rsi, T), fontWeight: 700 }}>{btcData.rsi != null ? formatRsiWithDivergence(btcData.rsi, btcData.rsiDiv, 0) : "—"}</span>
            {" · "}CMF <span style={{ color: btcData.cmf > 0 ? T.green : T.red, fontWeight: 700 }}>{btcData.cmf?.toFixed(3)}</span>
            {" · "}Score <span style={{ color: btcData.buyScore >= 4 ? T.green : btcData.buyScore <= -4 ? T.red : T.yellow, fontWeight: 700 }}>{btcData.buyScore > 0 ? "+" : ""}{btcData.buyScore}</span>
            {" · "}${btcData.price?.toLocaleString()}
          </div>
          {btcRegime === "BEAR" && <div style={{ fontSize: F.xs, color: T.orange, fontWeight: 700 }}>⚠ Bear regime — approach all crypto longs with caution</div>}
          {btcRegime === "BULL" && <div style={{ fontSize: F.xs, color: T.green }}>✓ Bull regime — long setups have macro tailwind</div>}
        </div>
      )}

      {/* Controls */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", width: "100%" }}>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {["ALL","BUY","BEAR","SQUEEZE"].map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ padding: "4px 10px", background: filter === f ? `${T.cyan}22` : "transparent", border: `1px solid ${filter === f ? T.cyan : "transparent"}`, color: filter === f ? T.cyan : T.textDim, fontSize: F.xs, borderRadius: 5, cursor: "pointer", fontFamily: "inherit" }}>{f}</button>
          ))}
        </div>
        {scanCompletedAt && !scanning && (
          <span style={{ fontSize: F.xs, color: T.textDim }}>
            Last scan: <span style={{ color: T.green }}>{scanCompletedAt}</span> · {coinsResults.length + equitiesResults.length} results
          </span>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "center", marginLeft: "auto", flexWrap: "wrap" }}>
          {(coinsResults.length > 0 || equitiesResults.length > 0) && !scanning && (
            <button onClick={exportCSV}
              title={`Export ${cryptoTab} as CSV`}
              style={{ padding: "4px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer" }}>
              ⬇ CSV
            </button>
          )}
          {scanning && (
            <button onClick={() => { stopRef.current = true; }} style={{ padding: "7px 12px", background: `${T.red}18`, border: `1px solid ${T.red}44`, color: T.red, borderRadius: 8, fontSize: F.sm, fontFamily: "inherit", cursor: "pointer" }}>
              ⬜ Stop
            </button>
          )}
          <button onClick={() => runCryptoScan("both")} disabled={scanning}
            style={{ padding: "7px 16px", background: scanning ? T.surfaceHi : `linear-gradient(135deg, #f59e0b, #f97316)`, border: "none", color: scanning ? T.textDim : "#fff", borderRadius: 8, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: scanning ? "not-allowed" : "pointer" }}>
            {scanning ? `⟳ Scanning ${progress.done}/${progress.total}…` : `₿ Scan All Crypto`}
          </button>
        </div>
      </div>

      {/* Coins / Equities / Watchlist sub-tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        {[
          ["coins",     `🪙 Coins`,           coinsResults.length > 0 ? `${coinsResults.length}/${PURE_CRYPTO_COINS.length}` : null],
          ["equities",  `📈 Equities`,        equitiesResults.length > 0 ? `${equitiesResults.length}/${CRYPTO_EQUITIES.length}` : null],
          ["watchlist", `★ Watchlist`,        coinWatchlist.length > 0 ? String(coinWatchlist.length) : null],
        ].map(([id, label, badge]) => (
          <button key={id} onClick={() => setCryptoTab(id)}
            style={{ padding: "8px 14px", background: "none", border: "none", borderBottom: `2px solid ${cryptoTab === id ? "#f59e0b" : "transparent"}`, color: cryptoTab === id ? "#f59e0b" : T.textDim, fontSize: F.xs, fontWeight: cryptoTab === id ? 700 : 400, fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
            {label}
            {badge && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 8, background: id === "watchlist" ? "#f59e0b22" : `${T.cyan}18`, color: id === "watchlist" ? "#f59e0b" : T.cyan, fontWeight: 700 }}>{badge}</span>}
          </button>
        ))}
        {cryptoTab === "watchlist" && coinWatchlist.length > 0 && (
          <button onClick={() => setShowCryptoAI(v => !v)}
            style={{ marginLeft: "auto", padding: "4px 10px", background: showCryptoAI ? `${T.orange}33` : `${T.orange}18`, border: `1px solid ${T.orange}55`, color: "#f59e0b", fontSize: 10, fontWeight: 700, borderRadius: 5, cursor: "pointer", fontFamily: "inherit", alignSelf: "center" }}>
            ✦ Analyze
          </button>
        )}
        {cryptoTab === "coins" && (
          <div style={{ marginLeft: "auto", fontSize: 9, color: T.textDim, display: "flex", alignItems: "center", paddingRight: 8 }}>
            RSI oversold &lt;25 · overbought &gt;80
          </div>
        )}
      </div>

      {/* Results table — hidden on watchlist tab */}
      {cryptoTab !== "watchlist" && sorted.length > 0 ? (
        <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", minHeight: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: F.xs }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
              <tr>
                <TH label="Ticker" col="symbol" tip="Asset name. Coins end in -USD. Click row to open full detail." />
                <TH label="Price" col="price" tip="Last traded price with daily % change." />
                <TH label="Score" col="buyScore" tip="Composite of 22 technical indicators. +9 or above = strongly bullish. -9 or below = strongly bearish. Not a buy signal alone — check Gate." />
                <TH2 label="Signal" tip="Synthesized verdict combining score, gate, and momentum context. BUY NOW means all conditions align. WATCH means setup is building. STRONG SHORT means sell-side pressure." />
                <TH label="RSI" col="rsi" tip={cryptoTab === "coins" ? "Relative Strength Index. Crypto thresholds: below 25 = oversold (green), above 80 = overbought (red). Much wider than stocks — crypto regularly hits these extremes." : RSI_COLUMN_HEADER_TIP} />
                <TH label="CMF" col="cmf" tip="Chaikin Money Flow. Measures institutional buying vs selling. Above +0.05 = accumulation (green). Below -0.05 = distribution (red). Most important flow indicator — institutions move before price." />
                <TH2 label="MACD" tip="Moving Average Convergence Divergence. CROSS ▲ = fresh bullish crossover (strongest signal). BULL ▲ = bullish but no fresh cross. CROSS ▼ = fresh bearish cross. BEAR ▼ = bearish." />
                <TH2 label="Trend" tip="50/200 day SMA relationship. Golden Cross = 50 crossed above 200 (bull market structure). 200 SMA Structure = 50 crossed below 200 (bear market structure). Crypto bearish 200 SMA structures can be prolonged — use with BTC regime context." />
                <TH2 label="Gate" tip="Entry conditions checklist. GO = all critical conditions met, entry confirmed. WAIT = close but not ready. Shows X/Y conditions passed. Click row for full breakdown." />
                {cryptoTab === "coins" && <TH label="vs BTC" col="rsSpread" tip="3-month performance vs Bitcoin. Positive = this coin is outperforming BTC (relative strength). Negative = underperforming BTC. Look for coins with positive RS during BTC bull regimes." />}
                <th style={{ padding: "7px 10px", fontSize: F.sm, fontWeight: 700, color: T.textDim, letterSpacing: "0.06em", textAlign: "center", borderBottom: `2px solid ${T.border}`, background: T.surface }}></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(({ symbol, result }) => {
                const verdict = calcVerdict({ result, aiCaution: false });
                const col = verdict ? (isDark ? verdict.darkColor : verdict.color) : T.textDim;
                const bg = verdict ? (isDark ? verdict.darkBg : verdict.bg) : T.surface;
                const inWatchlist = watchlistSymbols?.includes(symbol);
                const inCoinWatch = coinWatchlist.includes(symbol);
                const rsiOvs = cryptoTab === "coins" ? 25 : 30;
                const rsiOvb = cryptoTab === "coins" ? 80 : 70;
                const rsiColor = cryptoTab === "coins"
                  ? (result.rsi < rsiOvs ? T.green : result.rsi > rsiOvb ? T.red : result.rsi < 40 ? "#86efac" : T.textSub)
                  : rsiDisplayColor(result.rsi, T);
                // For coins show % change clearly
                const pct = result.pctChange;
                return (
                  <tr key={symbol}
                    onClick={() => setSelectedItem(selectedItem?.symbol === symbol ? null : { symbol, result })}
                    style={{ borderBottom: `1px solid ${T.border}`, background: selectedItem?.symbol === symbol ? `${T.cyan}12` : "transparent", cursor: "pointer", transition: "background 0.1s" }}
                    onMouseEnter={e => { if (selectedItem?.symbol !== symbol) e.currentTarget.style.background = T.surfaceHi; }}
                    onMouseLeave={e => { e.currentTarget.style.background = selectedItem?.symbol === symbol ? `${T.cyan}12` : "transparent"; }}>
                    <td style={{ padding: "9px 10px", fontWeight: 700, color: T.text, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap", fontSize: 13 }}>
                      {symbol.replace("-USD", "")}
                      {cryptoTab === "coins" && <div style={{ fontSize: 10, color: T.textDim, fontFamily: "inherit", marginTop: 1 }}>{COMPANY_NAMES[symbol] || ""}</div>}
                    </td>
                    <td style={{ padding: "9px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace" }}>
                      <div style={{ color: T.text, fontSize: 12 }}>{result.price > 1 ? `$${result.price?.toFixed(2)}` : `$${result.price?.toFixed(4)}`}</div>
                      {pct != null && <div style={{ fontSize: 11, color: pct >= 0 ? T.green : T.red }}>{pct >= 0 ? "+" : ""}{pct?.toFixed(2)}%</div>}
                    </td>
                    <td style={{ padding: "8px 6px", textAlign: "center" }}>
                      <span style={{ fontSize: 15, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", color: result.buyScore >= 7 ? T.green : result.buyScore >= 4 ? "#86efac" : result.buyScore <= -7 ? T.red : result.buyScore <= -4 ? "#fca5a5" : T.textSub }}>
                        {result.buyScore > 0 ? "+" : ""}{result.buyScore}
                      </span>
                    </td>
                    <td style={{ padding: "9px 8px", textAlign: "center" }}>
                      {verdict && <span style={{ padding: "3px 9px", borderRadius: 5, background: bg, border: `1px solid ${col}44`, color: col, fontSize: 11, fontWeight: 800 }}>{verdict.short}</span>}
                    </td>
                    <td style={{ padding: "9px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: rsiColor, fontWeight: 700, whiteSpace: "nowrap" }}
                      title={cryptoTab === "coins" ? undefined : rsiDisplayTooltip(result.rsi, result.rsiDiv)}>
                      {cryptoTab === "coins" ? result.rsi?.toFixed(0) : formatRsiWithDivergence(result.rsi, result.rsiDiv, 0)}
                    </td>
                    <td style={{ padding: "9px 8px", textAlign: "center", fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: result.cmf > 0.05 ? T.green : result.cmf < -0.05 ? T.red : T.textSub }}>
                      {result.cmf?.toFixed(3)}
                    </td>
                    <td style={{ padding: "9px 8px", textAlign: "center", fontSize: 11, fontWeight: 700, color: result.macd?.crossover ? T.green : result.macd?.bullish ? "#86efac" : result.macd?.crossunder ? T.red : T.textDim }}>
                      {result.macd?.crossover ? "CROSS ▲" : result.macd?.bullish ? "BULL ▲" : result.macd?.crossunder ? "CROSS ▼" : "BEAR ▼"}
                    </td>
                    <td style={{ padding: "9px 8px", textAlign: "center", fontSize: 11, fontWeight: 700, color: result.goldenCross ? T.green : result.deathCross ? T.red : T.textDim }}>
                      {result.goldenCross ? "GOLDEN ▲" : result.deathCross ? "200 SMA ▼" : "—"}
                    </td>
                    <td style={{ padding: "9px 8px", textAlign: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: result.gate?.label === "GO" ? T.green : result.gate?.label === "WAIT" ? T.yellow : T.textDim }}>{result.gate?.label}</span>
                      <div style={{ fontSize: 10, color: T.textDim }}>{result.gate?.passed}/{result.gate?.total}</div>
                    </td>
                    {cryptoTab === "coins" && (
                      <td style={{ padding: "9px 8px", textAlign: "center", fontSize: 12, fontWeight: 700, color: result.rs?.outperforming ? T.green : T.textDim }}>
                        {result.rs?.spread != null ? `${result.rs.spread > 0 ? "+" : ""}${result.rs.spread.toFixed(1)}%` : "—"}
                      </td>
                    )}
                    <td style={{ padding: "8px 6px", textAlign: "center" }}>
                      {cryptoTab === "coins" ? (
                        <button onClick={(e) => { e.stopPropagation(); toggleCoinWatch(symbol); }}
                          style={{ padding: "4px 10px", background: coinWatchlist.includes(symbol) ? `${T.yellow}22` : T.surfaceHi, border: `1px solid ${coinWatchlist.includes(symbol) ? "#f59e0b66" : T.border}`, color: coinWatchlist.includes(symbol) ? "#f59e0b" : T.cyan, borderRadius: 5, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>
                          {coinWatchlist.includes(symbol) ? "★ Watching" : "+ Watch"}
                        </button>
                      ) : (
                        !inWatchlist ? (
                          <button onClick={(e) => { e.stopPropagation(); onAddToWatchlist(symbol); }}
                            style={{ padding: "4px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.cyan, borderRadius: 5, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>+ Watchlist</button>
                        ) : (
                          <span style={{ fontSize: 11, color: T.green }}>✓ Added</span>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : cryptoTab === "watchlist" ? null : (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.textDim, fontSize: F.sm, flexDirection: "column", gap: 8 }}>
          {scanning ? `Scanning ${progress.current}…` : (
            <>
              <div>Click ₿ Scan All Crypto to start</div>
              {(coinsResults.length > 0 || equitiesResults.length > 0) && <div style={{ fontSize: F.xs, color: T.textDim }}>Showing last scan results</div>}
            </>
          )}
        </div>
      )}

      {/* ── WATCHLIST TAB ── */}
      {cryptoTab === "watchlist" && (
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "12px 4px" }}>
          {coinWatchlist.length > 0 && coinsResults.length === 0 && (
            <div style={{ margin: "0 0 10px", padding: "8px 12px", background: `${T.yellow}10`, border: `1px solid #f59e0b33`, borderRadius: 8, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, color: T.textSub, flex: 1 }}>Run a scan to populate your coin data</span>
              <button onClick={() => { runCryptoScan("coins"); setCryptoTab("coins"); }} disabled={scanning}
                style={{ padding: "5px 14px", background: `linear-gradient(135deg, #f59e0b, #f97316)`, border: "none", color: "#fff", borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                ₿ Scan Coins
              </button>
            </div>
          )}
          {coinWatchlist.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: T.textDim, fontSize: F.sm }}>
              <div style={{ fontSize: 24, marginBottom: 8 }}>★</div>
              <div>No coins watched yet</div>
              <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 6 }}>Click "+ Watch" on any coin in the Coins tab to add it here</div>
            </div>
          ) : (coinsResults.length === 0 && !btcData) ? (
            <div style={{ textAlign: "center", padding: "40px 20px", color: T.textDim, fontSize: F.sm, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <div>Your watched coins need data — run a scan first</div>
              <button onClick={() => runCryptoScan("coins")} disabled={scanning}
                style={{ padding: "8px 20px", background: `linear-gradient(135deg, #f59e0b, #f97316)`, border: "none", color: "#fff", borderRadius: 8, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                ₿ Scan Coins Now
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {coinWatchlist.map(sym => {
                const item = [...coinsResults, ...equitiesResults].find(r => r.symbol === sym)
                  || (sym === "BTC-USD" && btcData ? { symbol: "BTC-USD", result: btcData } : null);
                const r = item?.result;
                const verdict = r ? calcVerdict({ result: r, aiCaution: false }) : null;
                const col = verdict ? (isDark ? verdict.darkColor : verdict.color) : T.textDim;
                const bg = verdict ? (isDark ? verdict.darkBg : verdict.bg) : T.surface;
                const rsiOvs = isPureCrypto(sym) ? 25 : 30;
                const rsiOvb = isPureCrypto(sym) ? 80 : 70;
                return (
                  <div key={sym} onClick={() => item && setSelectedItem(item)}
                    style={{ padding: "10px 14px", background: T.surface, border: `1px solid ${r ? col + "33" : T.border}`, borderRadius: 10, cursor: item ? "pointer" : "default", display: "flex", alignItems: "center", gap: 12, transition: "background 0.1s" }}
                    onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
                    onMouseLeave={e => e.currentTarget.style.background = T.surface}>
                    {/* Symbol */}
                    <div style={{ minWidth: 80 }}>
                      <div style={{ fontSize: 14, fontWeight: 900, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>{sym.replace("-USD","")}</div>
                      <div style={{ fontSize: 9, color: T.textDim }}>{COMPANY_NAMES[sym] || sym}</div>
                    </div>
                    {/* Price */}
                    {r ? (
                      <>
                        <div style={{ minWidth: 80, textAlign: "right" }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: "monospace" }}>${r.price > 1 ? r.price.toFixed(2) : r.price.toFixed(4)}</div>
                          <div style={{ fontSize: 10, color: (r.pctChange ?? 0) >= 0 ? T.green : T.red }}>{(r.pctChange ?? 0) >= 0 ? "+" : ""}{r.pctChange?.toFixed(2)}%</div>
                        </div>
                        <div style={{ flex: 1, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          {verdict && <span style={{ padding: "3px 8px", borderRadius: 5, background: bg, border: `1px solid ${col}44`, color: col, fontSize: 10, fontWeight: 800 }}>{verdict.short}</span>}
                          <span style={{ fontSize: 14, fontWeight: 900, color: r.buyScore >= 7 ? T.green : r.buyScore >= 4 ? "#86efac" : r.buyScore <= -7 ? T.red : r.buyScore <= -4 ? "#fca5a5" : T.textSub, fontFamily: "monospace" }}>{r.buyScore > 0 ? "+" : ""}{r.buyScore}</span>
                          <span style={{ fontSize: 10, color: T.textDim }}>RSI <span style={{ color: r.rsi < rsiOvs ? T.green : r.rsi > rsiOvb ? T.red : T.textSub, fontWeight: 700 }}>{r.rsi?.toFixed(0)}</span></span>
                          <span style={{ fontSize: 10, color: T.textDim }}>CMF <span style={{ color: r.cmf > 0.05 ? T.green : r.cmf < -0.05 ? T.red : T.textSub, fontWeight: 700 }}>{r.cmf?.toFixed(3)}</span></span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: r.gate?.label === "GO" ? T.green : T.textDim }}>{r.gate?.label}</span>
                        </div>
                      </>
                    ) : (
                      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: F.xs, color: T.textDim, fontStyle: "italic" }}>Not yet scanned</span>
                        <button onClick={e => { e.stopPropagation(); runCryptoScan("coins"); }} disabled={scanning}
                          style={{ padding: "3px 10px", background: `${T.yellow}22`, border: `1px solid #f59e0b44`, color: "#f59e0b", borderRadius: 5, fontSize: 9, fontFamily: "inherit", cursor: "pointer" }}>
                          ₿ Scan
                        </button>
                      </div>
                    )}
                    {/* Remove */}
                    <button onClick={e => { e.stopPropagation(); toggleCoinWatch(sym); }}
                      style={{ padding: "4px 8px", background: "transparent", border: `1px solid ${T.border}`, color: T.textDim, borderRadius: 5, fontSize: 11, cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>✕</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Detail drawer — slides in when row is clicked */}
      {selectedItem && (() => {
        const { symbol, result } = selectedItem;
        const verdict = calcVerdict({ result, aiCaution: false });
        const col = verdict ? (isDark ? verdict.darkColor : verdict.color) : T.textDim;
        const ai = aiAnalysis[symbol];
        const isPureCoin = isPureCrypto(symbol);
        const rsiOvs = isPureCoin ? 25 : 30;
        const rsiOvb = isPureCoin ? 80 : 70;
        return (
          <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: cryptoDrawerW, background: T.surface, borderLeft: `1px solid ${T.border}`, zIndex: 100, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.4)" }}>
            {/* Drag handle */}
            <div onMouseDown={startCryptoDrag}
              style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 8, cursor: "col-resize", zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center" }}
              title="Drag to resize">
              <div style={{ width: 3, height: 40, borderRadius: 2, background: T.border, pointerEvents: "none" }} />
            </div>
            {/* Scrollable body */}
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column" }}>
            {/* Header */}
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <button onClick={() => setSelectedItem(null)} style={{ padding: "5px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.text, borderRadius: 7, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>←</button>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>{symbol.replace("-USD", "")}</div>
                <div style={{ fontSize: 11, color: T.textSub }}>{COMPANY_NAMES[symbol] || symbol} · {isPureCoin ? "Spot Crypto" : "Crypto Equity"}</div>
              </div>
              {verdict && (
                <div style={{ padding: "5px 10px", borderRadius: 7, background: isDark ? verdict.darkBg : verdict.bg, border: `1px solid ${col}55`, color: col, fontSize: 11, fontWeight: 800 }}>{verdict.short}</div>
              )}
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 22, fontWeight: 900, color: result.buyScore >= 4 ? T.green : result.buyScore <= -4 ? T.red : T.textSub }}>
                {result.buyScore > 0 ? "+" : ""}{result.buyScore}
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Price */}
              <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
                <span style={{ fontSize: 28, fontWeight: 800, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>${result.price > 1 ? result.price?.toFixed(2) : result.price?.toFixed(4)}</span>
                <span style={{ fontSize: 14, color: (result.pctChange ?? 0) >= 0 ? T.green : T.red, fontWeight: 700 }}>{(result.pctChange ?? 0) >= 0 ? "+" : ""}{result.pctChange?.toFixed(2)}%</span>
              </div>

              {/* Verdict */}
              {verdict?.reason && (
                <div style={{ background: T.bg, border: `1px solid ${col}33`, borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 6 }}>VERDICT</div>
                  <div style={{ fontSize: 13, color: col, fontWeight: 700, marginBottom: 5 }}>{verdict.label}</div>
                  <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7, marginBottom: 6 }}>{verdict.reason}</div>
                  <div style={{ fontSize: 12, color: T.text, padding: "6px 8px", background: `${col}10`, borderRadius: 6 }}>→ {verdict.action}</div>
                </div>
              )}

              {/* Key indicators */}
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 8 }}>KEY INDICATORS {isPureCoin ? `· RSI oversold <${rsiOvs} overbought >${rsiOvb}` : ""}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                  {[
                    { l: "RSI", v: result.rsi?.toFixed(0), c: result.rsi < rsiOvs ? T.green : result.rsi > rsiOvb ? T.red : result.rsi < 40 ? "#86efac" : T.textSub },
                    { l: "MACD", v: result.macd?.crossover ? "CROSS ▲" : result.macd?.bullish ? "BULL ▲" : result.macd?.crossunder ? "CROSS ▼" : "BEAR ▼", c: result.macd?.bullish ? T.green : T.red },
                    { l: "CMF", v: result.cmf?.toFixed(3), c: result.cmf > 0.05 ? T.green : result.cmf < -0.05 ? T.red : T.textSub },
                    { l: "Trend", v: result.goldenCross ? "Golden ▲" : result.deathCross ? "200 SMA ▼" : "—", c: result.goldenCross ? T.green : result.deathCross ? T.red : T.textDim },
                    { l: "Gate", v: `${result.gate?.label} ${result.gate?.passed}/${result.gate?.total}`, c: result.gate?.label === "GO" ? T.green : result.gate?.label === "WAIT" ? T.yellow : T.textDim },
                    { l: "BB", v: result.bb?.isSqueeze ? BB_TIGHT_BANDS_LABEL : "Normal", c: result.bb?.isSqueeze ? T.yellow : T.textSub },
                    { l: "52W", v: result.range52w ? `${result.range52w.score}/100` : "—", c: result.range52w?.nearLow ? T.green : result.range52w?.nearHigh ? T.orange : T.textSub },
                    { l: isPureCoin ? "vs BTC" : "vs SPY", v: result.rs?.spread != null ? `${result.rs.spread > 0 ? "+" : ""}${result.rs.spread.toFixed(1)}%` : "—", c: result.rs?.outperforming ? T.green : T.textDim },
                    { l: "RVOL", v: result.rvol?.ratio ? `${result.rvol.ratio.toFixed(1)}x` : "—", c: result.rvol?.surge ? T.green : T.textSub },
                  ].map(({ l, v, c }) => (
                    <div key={l} style={{ background: T.surface, borderRadius: 7, padding: "7px 8px" }}>
                      <div style={{ fontSize: 9, color: T.textDim, marginBottom: 2 }}>{l}</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Targets */}
              {(result.t1 || result.d1) && (
                <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 8 }}>TARGETS & STOPS</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                    {[
                      { l: "Target 1", v: result.t1 ? `$${result.t1.toFixed(2)}` : null, c: T.green },
                      { l: "Target 2", v: result.t2 ? `$${result.t2.toFixed(2)}` : null, c: T.green },
                      { l: "ATR Stop", v: result.atr ? `$${(result.price - 2 * result.atr).toFixed(2)}` : null, c: T.red },
                      { l: "D1 Target", v: result.d1 ? `$${result.d1.toFixed(2)}` : null, c: T.red },
                      { l: result.ema21SlopeLabel || "EMA21", v: result.ema21 ? `$${result.ema21.toFixed(2)}` : null, c: T.cyan },
                    ].filter(x => x.v).map(({ l, v, c }) => (
                      <div key={l} style={{ background: T.surface, borderRadius: 7, padding: "7px 8px" }}>
                        <div style={{ fontSize: 9, color: T.textDim, marginBottom: 2 }}>{l}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: 8 }}>
                {isPureCrypto(symbol) ? (
                  <button onClick={() => toggleCoinWatch(symbol)}
                    style={{ flex: 1, padding: "8px", background: coinWatchlist.includes(symbol) ? `${T.yellow}22` : `${T.cyan}22`, border: `1px solid ${coinWatchlist.includes(symbol) ? "#f59e0b44" : T.cyan + "44"}`, color: coinWatchlist.includes(symbol) ? "#f59e0b" : T.cyan, borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                    {coinWatchlist.includes(symbol) ? "★ In Coin Watchlist" : "★ Watch Coin"}
                  </button>
                ) : !watchlistSymbols?.includes(symbol) ? (
                  <button onClick={() => onAddToWatchlist(symbol)} style={{ flex: 1, padding: "8px", background: `${T.cyan}22`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                    + Add to Watchlist
                  </button>
                ) : null}
                {watchlistSymbols?.includes(symbol) && (
                  <div style={{ flex: 1, padding: "8px", background: `${T.green}10`, border: `1px solid ${T.green}33`, borderRadius: 8, fontSize: 12, color: T.green, fontWeight: 700, textAlign: "center" }}>✓ In Watchlist</div>
                )}
              </div>

              {/* AI Analysis */}
              <div style={{ background: T.bg, border: `1px solid ${T.purple}44`, borderRadius: 10, padding: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.purple, letterSpacing: "0.1em", marginBottom: 8 }}>✦ AUCTUS AI ANALYSIS</div>
                {!ai && aiLoading !== symbol && (
                  <button onClick={() => runAI(symbol, result)}
                    style={{ width: "100%", padding: 10, background: `${T.purple}22`, border: `1px solid ${T.purple}44`, color: T.purple, borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                    ✦ Run AI Analysis
                  </button>
                )}
                {aiLoading === symbol && (
                  <div style={{ textAlign: "center", padding: "12px 0", color: T.purple, fontSize: 12, animation: "pulse 1s infinite" }}>⏳ Analyzing {symbol.replace("-USD", "")}…</div>
                )}
                {ai && aiLoading !== symbol && (
                  <>
                    <div style={{ fontSize: 12, color: T.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{ai.text}</div>
                    <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                      <button onClick={() => runAI(symbol, result)} style={{ padding: "5px 12px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>↻ Refresh</button>
                      <span style={{ fontSize: 10, color: T.textDim }}>{ai.date}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            </div> {/* end scrollable body */}
          </div>
        );
      })()}
    </div>

    {/* CRYPTO WATCHLIST AI DRAWER */}
    {showCryptoAI && (
      <CryptoAIPanel
        coinWatchlist={coinWatchlist}
        coinsResults={coinsResults}
        equitiesResults={equitiesResults}
        btcData={btcData}
        anthropicKey={anthropicKey}
        cached={cryptoAI}
        onResult={r => setCryptoAI(r)}
        loading={cryptoAILoading}
        setLoading={setCryptoAILoading}
        onClose={() => setShowCryptoAI(false)}
        onScan={() => runCryptoScan("both")}
        scanning={scanning}
      />
    )}
    </>
  );
}


// ─── PERFORMANCE TRACKER ─────────────────────────────────────────────────────
function PerformanceTracker() {
  const [signals, setSignals] = useState([]);
  const [checking, setChecking] = useState(false);
  const [checkProgress, setCheckProgress] = useState(0);
  const [filter, setFilter] = useState("ALL");
  const [sortCol, setSortCol] = useState("logDate");
  const [sortDir, setSortDir] = useState(-1);
  const [activeView, setActiveView] = useState("mySignals");
  const [accuracyStats, setAccuracyStats] = useState(null);
  const [accuracyLoading, setAccuracyLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const saved = await load("signalLog");
      if (saved) setSignals(saved);
    })();
  }, []);

  const loadAccuracyStats = async () => {
    setAccuracyLoading(true);
    try {
      const { data } = await supabase
        .from("signal_accuracy")
        .select("symbol, score, verdict, verdict_layer, verdict_short, outcome, outcome_pct, logged_at, window_days")
        .neq("outcome", "OPEN")
        .order("logged_at", { ascending: false })
        .limit(2000);
      if (!data?.length) { setAccuracyStats({ empty: true }); return; }
      const byLayer = {};
      ["buy","recov","accum","wait","bearish"].forEach(l => { byLayer[l] = { total: 0, wins: 0, t2: 0, stopped: 0, winPcts: [] }; });
      for (const row of data) {
        const l = row.verdict_layer;
        if (!byLayer[l]) continue;
        byLayer[l].total++;
        const isWin = row.outcome?.includes("HIT") || row.outcome === "EXPIRED — POSITIVE";
        const isT2 = row.outcome === "T2 HIT ✓✓" || row.outcome === "D2 HIT ✓✓";
        if (isWin) { byLayer[l].wins++; if (row.outcome_pct) byLayer[l].winPcts.push(row.outcome_pct); }
        if (row.outcome?.includes("STOPPED")) byLayer[l].stopped++;
        if (isT2) byLayer[l].t2++;
      }
      const byVerdict = {};
      for (const row of data) {
        const k = row.verdict_short || row.verdict_layer;
        if (!byVerdict[k]) byVerdict[k] = { layer: row.verdict_layer, total: 0, wins: 0, stopped: 0, pcts: [] };
        byVerdict[k].total++;
        if (row.outcome?.includes("HIT") || row.outcome === "EXPIRED — POSITIVE") byVerdict[k].wins++;
        if (row.outcome?.includes("STOPPED")) byVerdict[k].stopped++;
        if (row.outcome_pct != null) byVerdict[k].pcts.push(row.outcome_pct);
      }
      const openCount = await supabase.from("signal_accuracy").select("id", { count: "exact", head: true }).eq("outcome", "OPEN");
      setAccuracyStats({ byLayer, byVerdict, total: data.length, openCount: openCount.count || 0, rows: data });
    } catch (e) { setAccuracyStats({ error: e.message }); }
    finally { setAccuracyLoading(false); }
  };

  useEffect(() => { if (activeView === "accuracy" && !accuracyStats) loadAccuracyStats(); }, [activeView]);

  const checkOutcomes = async () => {
    if (!signals.length) return;
    setChecking(true); setCheckProgress(0);
    const updated = [...signals];
    const open = updated.filter(s => s.outcome === "OPEN" || !s.outcome);
    for (let i = 0; i < open.length; i++) {
      const s = open[i];
      try {
        const res = await fetch(`/api/quote?symbol=${encodeURIComponent(s.symbol)}`);
        if (!res.ok) continue;
        const q = await res.json();
        if (!q.price) continue;
        const p = q.price;
        let outcome = "OPEN", outcomePrice = p;
        let outcomePct = s.entryPrice ? ((p - s.entryPrice) / s.entryPrice * 100) : null;
        if (s.stop && p <= s.stop) { outcome = "STOPPED OUT"; outcomePrice = s.stop; outcomePct = s.entryPrice ? ((s.stop - s.entryPrice) / s.entryPrice * 100) : null; }
        else if (s.t2 && p >= s.t2) { outcome = "T2 HIT ✓✓"; outcomePrice = s.t2; outcomePct = s.entryPrice ? ((s.t2 - s.entryPrice) / s.entryPrice * 100) : null; }
        else if (s.t1 && p >= s.t1) { outcome = "T1 HIT ✓"; outcomePrice = s.t1; outcomePct = s.entryPrice ? ((s.t1 - s.entryPrice) / s.entryPrice * 100) : null; }
        const idx = updated.findIndex(u => u.id === s.id);
        if (idx >= 0) updated[idx] = { ...updated[idx], outcome, outcomePrice, outcomePct, currentPrice: p, lastChecked: new Date().toLocaleDateString() };
      } catch {}
      setCheckProgress(Math.round(((i + 1) / open.length) * 100));
      await new Promise(r => setTimeout(r, 150));
    }
    setSignals(updated); save("signalLog", updated); setChecking(false);
  };

  const deleteSignal = (id) => { const next = signals.filter(s => s.id !== id); setSignals(next); save("signalLog", next); };
  const clearAll = () => { setSignals([]); save("signalLog", []); };

  const closed = signals.filter(s => s.outcome && s.outcome !== "OPEN");
  const wins = closed.filter(s => s.outcome?.includes("HIT"));
  const losses = closed.filter(s => s.outcome?.includes("STOPPED"));
  const winRate = closed.length ? (wins.length / closed.length * 100).toFixed(0) : null;
  const avgWinPct = wins.length ? (wins.reduce((s, t) => s + (t.outcomePct || 0), 0) / wins.length).toFixed(1) : null;
  const avgLossPct = losses.length ? (losses.reduce((s, t) => s + (t.outcomePct || 0), 0) / losses.length).toFixed(1) : null;
  const filtered = signals
    .filter(s => filter === "ALL" ? true : filter === "OPEN" ? (!s.outcome || s.outcome === "OPEN") : filter === "WINS" ? s.outcome?.includes("HIT") : filter === "LOSSES" ? s.outcome?.includes("STOPPED") : true)
    .sort((a, b) => { const av = a[sortCol] ?? ""; const bv = b[sortCol] ?? ""; return typeof av === "number" ? (av - bv) * sortDir : String(av).localeCompare(String(bv)) * sortDir; });
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  const layerMeta = {
    buy: { label: "BUY NOW", color: T.green, desc: "45-day window" }, recov: { label: "RECOVERY", color: T.cyan, desc: "45-day window" },
    accum: { label: "ACCUMULATION", color: T.purple, desc: "365-day window" }, wait: { label: "STRONG SETUP", color: T.orange, desc: "45-day window" },
    bearish: { label: "SHORT/BEARISH", color: T.red, desc: "45-day window" },
  };

  const exportAccuracyCsv = () => {
    const rows = accuracyStats?.rows;
    if (!rows?.length) return;
    const esc = (v) => {
      if (v == null || v === "") return "";
      const s = String(v);
      if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const headers = ["Symbol", "Date", "Score", "Verdict", "Signal Type", "Outcome", "Accuracy %"];
    const csv = [headers.join(","), ...rows.map((row) => [
      row.symbol,
      row.logged_at ? new Date(row.logged_at).toISOString().slice(0, 10) : "",
      row.score != null && row.score !== "" ? row.score : "",
      row.verdict_short || row.verdict || "",
      layerMeta[row.verdict_layer]?.label || row.verdict_layer || "",
      row.outcome || "",
      row.outcome_pct != null ? Number(row.outcome_pct).toFixed(1) : "",
    ].map(esc).join(","))].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    a.href = url;
    a.download = `signal-accuracy-${y}-${m}-${d}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: "16px 16px 16px 24px", minHeight: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
        {[["mySignals", "📋 My Signals"], ["accuracy", "📊 Signal Accuracy"]].map(([id, label]) => (
          <button key={id} onClick={() => setActiveView(id)}
            style={{ padding: "7px 16px", background: activeView === id ? `${T.purple}22` : T.surfaceHi, border: `1px solid ${activeView === id ? T.purple : T.border}`, color: activeView === id ? T.purple : T.textDim, fontSize: F.sm, fontWeight: activeView === id ? 700 : 400, fontFamily: "inherit", cursor: "pointer", borderRadius: 8 }}>
            {label}
          </button>
        ))}
      </div>

      {activeView === "accuracy" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>System Signal Accuracy</div>
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>Anonymous · all users · swing window 45d · accumulation 365d.</div>
            </div>
            <button type="button" onClick={exportAccuracyCsv} disabled={accuracyLoading || !accuracyStats?.rows?.length}
              style={{ padding: "6px 14px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, fontSize: F.xs, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 7 }}>
              Export CSV
            </button>
            <button onClick={loadAccuracyStats} disabled={accuracyLoading}
              style={{ padding: "6px 14px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, fontSize: F.xs, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", borderRadius: 7 }}>
              {accuracyLoading ? "Loading…" : "↻ Refresh"}
            </button>
          </div>
          {accuracyLoading && <div style={{ textAlign: "center", padding: "40px 0", color: T.purple, fontSize: 13 }}>✦ Loading…</div>}
          {accuracyStats?.error && <div style={{ padding: "10px 14px", background: `${T.red}10`, border: `1px solid ${T.red}44`, borderRadius: 8, fontSize: 12, color: T.red }}>Error: {accuracyStats.error}</div>}
          {accuracyStats?.empty && <div style={{ textAlign: "center", padding: "40px 0", color: T.textDim, fontSize: 13 }}>No completed signals yet.</div>}
          {accuracyStats && !accuracyStats.empty && !accuracyStats.error && !accuracyLoading && (() => (
            <>
              <div style={{ padding: "8px 14px", background: T.bg, border: `1px solid ${T.border}`, borderRadius: 8, display: "flex", gap: 16 }}>
                <div style={{ fontSize: 11, color: T.textDim }}>Resolved: <span style={{ color: T.text, fontWeight: 700 }}>{accuracyStats.total.toLocaleString()}</span></div>
                <div style={{ fontSize: 11, color: T.textDim }}>Open: <span style={{ color: T.cyan, fontWeight: 700 }}>{accuracyStats.openCount.toLocaleString()}</span></div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
                {["buy","recov","wait","accum","bearish"].map(layer => {
                  const s = accuracyStats.byLayer[layer]; const meta = layerMeta[layer];
                  if (!s || s.total === 0) return null;
                  const wr = s.total ? ((s.wins / s.total) * 100).toFixed(0) : null;
                  const avg = s.winPcts.length ? (s.winPcts.reduce((a,b) => a+b, 0) / s.winPcts.length).toFixed(1) : null;
                  const wrc = wr >= 60 ? T.green : wr >= 45 ? T.yellow : T.red;
                  return (
                    <div key={layer} style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                        <div><div style={{ fontSize: 11, fontWeight: 800, color: meta.color }}>{meta.label}</div><div style={{ fontSize: 10, color: T.textDim }}>{meta.desc} · {s.total}</div></div>
                        <div style={{ fontSize: 22, fontWeight: 900, color: wrc, fontFamily: "'JetBrains Mono', monospace" }}>{wr ?? "—"}%</div>
                      </div>
                      <div style={{ height: 4, background: T.border, borderRadius: 2, marginBottom: 10, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${wr ?? 0}%`, background: wrc }} />
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                        {[["Hits", s.wins, T.green], ["Stopped", s.stopped, T.red], ["Avg Return", avg ? `+${avg}%` : "—", T.green]].map(([l,v,c]) => (
                          <div key={l} style={{ background: T.surface, borderRadius: 6, padding: "6px 8px" }}>
                            <div style={{ fontSize: 9, color: T.textDim, marginBottom: 2 }}>{l}</div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {accuracyStats.byVerdict && (
                <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}`, fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em" }}>BREAKDOWN BY VERDICT TYPE</div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                      <thead><tr style={{ borderBottom: `1px solid ${T.border}` }}>{["Verdict","Signals","Win Rate","Avg Return","Stopped"].map(h => <th key={h} style={{ padding: "7px 12px", textAlign: h === "Verdict" ? "left" : "center", fontSize: 10, fontWeight: 700, color: T.textDim }}>{h}</th>)}</tr></thead>
                      <tbody>
                        {Object.entries(accuracyStats.byVerdict).filter(([,s]) => s.total >= 3).sort((a,b) => b[1].total - a[1].total).map(([verdict, s]) => {
                          const wr = ((s.wins / s.total) * 100).toFixed(0);
                          const avg = s.pcts.length ? (s.pcts.reduce((a,b)=>a+b,0)/s.pcts.length).toFixed(1) : null;
                          const meta = layerMeta[s.layer] || { color: T.textDim };
                          const wrc = wr >= 60 ? T.green : wr >= 45 ? T.yellow : T.red;
                          return (
                            <tr key={verdict} style={{ borderBottom: `1px solid ${T.border}` }} onMouseEnter={e => e.currentTarget.style.background = T.surface} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              <td style={{ padding: "7px 12px" }}><span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, background: `${meta.color}18`, color: meta.color, border: `1px solid ${meta.color}44` }}>{verdict}</span></td>
                              <td style={{ padding: "7px 12px", textAlign: "center", color: T.textSub }}>{s.total}</td>
                              <td style={{ padding: "7px 12px", textAlign: "center", fontWeight: 700, color: wrc, fontFamily: "'JetBrains Mono', monospace" }}>{wr}%</td>
                              <td style={{ padding: "7px 12px", textAlign: "center", color: T.green, fontFamily: "'JetBrains Mono', monospace" }}>{avg ? `+${avg}%` : "—"}</td>
                              <td style={{ padding: "7px 12px", textAlign: "center", color: T.red }}>{s.stopped}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ))()}
        </div>
      )}

      {activeView === "mySignals" && (<>
        {closed.length > 0 && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", flexShrink: 0 }}>
            {[["Signals Logged", signals.length, T.text],["Completed", closed.length, T.text],["Win Rate", winRate ? `${winRate}%` : "—", parseFloat(winRate) >= 55 ? T.green : T.red],["Avg Win", avgWinPct ? `+${avgWinPct}%` : "—", T.green],["Avg Loss", avgLossPct ? `${avgLossPct}%` : "—", T.red],["T2 Hits", signals.filter(s => s.outcome === "T2 HIT ✓✓").length, T.green]].map(([l,v,c]) => (
              <div key={l} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 14px", textAlign: "center" }}>
                <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 3 }}>{l}</div>
                <div style={{ fontSize: F.md, fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}>
          <button onClick={checkOutcomes} disabled={checking || !signals.length}
            style={{ padding: "7px 14px", background: checking ? T.surfaceHi : `linear-gradient(135deg, ${T.cyan}, ${T.green})`, border: "none", color: checking ? T.textDim : T.bg, fontSize: F.sm, fontWeight: 700, fontFamily: "inherit", cursor: checking ? "not-allowed" : "pointer", borderRadius: 8 }}>
            {checking ? `Checking... ${checkProgress}%` : "↻ Check Outcomes"}
          </button>
          <div style={{ display: "flex", gap: 5 }}>
            {["ALL","OPEN","WINS","LOSSES"].map(f => (
              <button key={f} onClick={() => setFilter(f)}
                style={{ padding: "5px 10px", background: filter === f ? T.borderHi : T.surfaceHi, border: `1px solid ${filter === f ? T.borderHi : T.border}`, color: filter === f ? T.text : T.textDim, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer", borderRadius: 6, fontWeight: filter === f ? 700 : 400 }}>
                {f}
              </button>
            ))}
          </div>
          {signals.length > 0 && <button onClick={clearAll} style={{ marginLeft: "auto", padding: "5px 10px", background: "none", border: `1px solid ${T.border}`, color: T.textDim, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer", borderRadius: 6 }}>Clear All</button>}
        </div>
        {filtered.length === 0 ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, gap: 10 }}>
            <div style={{ fontSize: 32 }}>📈</div>
            <div style={{ fontSize: F.base, fontWeight: 600, color: T.text }}>No signals logged yet</div>
            <div style={{ fontSize: F.sm, textAlign: "center", maxWidth: 400, lineHeight: 1.6 }}>Signals are automatically logged on every scan. Run a scan to start building your history.</div>
          </div>
        ) : (
          <div style={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: F.xs }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 5 }}>
                <tr>{[["Date","logDate"],["Ticker","symbol"],["Verdict","verdict"],["Score","score"],["Entry","entryPrice"],["Stop","stop"],["T1","t1"],["T2","t2"],["Outcome","outcome"],["P&L","outcomePct"],[""]].map(([l,col]) => (
                  <th key={l} onClick={() => col && (setSortCol(col), setSortDir(sortCol === col ? -sortDir : 1))}
                    style={{ padding: "6px 8px", fontSize: F.xs, fontWeight: 700, color: sortCol === col ? T.cyan : T.textDim, letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "center", whiteSpace: "nowrap", cursor: col ? "pointer" : "default", borderBottom: `2px solid ${T.border}`, background: T.surface }}>
                    {l}{col && sortCol === col ? (sortDir === -1 ? " ▼" : " ▲") : ""}
                  </th>
                ))}</tr>
              </thead>
              <tbody>
                {filtered.map(s => {
                  const isWin = s.outcome?.includes("HIT"); const isLoss = s.outcome?.includes("STOPPED");
                  const oc = isWin ? T.green : isLoss ? T.red : T.textDim;
                  return (
                    <tr key={s.id} style={{ borderBottom: `1px solid ${T.border}` }} onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "6px 8px", color: T.textDim }}>{s.logDate}</td>
                      <td style={{ padding: "6px 8px", fontWeight: 700, ...mono, color: T.text }}>{s.symbol}</td>
                      <td style={{ padding: "6px 8px", textAlign: "center" }}>
                        {s.verdict ? <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4, color: s.verdictLayer === "buy" ? "#16a34a" : s.verdictLayer === "bearish" ? "#ef4444" : "#94a3b8", background: "#f1f5f922", border: `1px solid #94a3b844` }}>{s.verdict}</span> : <span style={{ color: T.textDim }}>—</span>}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "center", fontWeight: 700, ...mono, color: s.score >= 4 ? T.green : T.yellow }}>{s.score > 0 ? "+" : ""}{s.score}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", ...mono, color: T.text }}>${s.entryPrice?.toFixed(2) || "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", ...mono, color: T.red }}>{s.stop ? `$${s.stop?.toFixed(2)}` : "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", ...mono, color: T.green }}>{s.t1 ? `$${s.t1?.toFixed(2)}` : "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", ...mono, color: "#4ade80" }}>{s.t2 ? `$${s.t2?.toFixed(2)}` : "—"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "center", fontWeight: 700, color: oc }}>{s.outcome || "OPEN"}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, ...mono, color: oc }}>{s.outcomePct != null ? `${s.outcomePct >= 0 ? "+" : ""}${s.outcomePct?.toFixed(1)}%` : "—"}</td>
                      <td style={{ padding: "4px 6px" }}><button onClick={() => deleteSignal(s.id)} style={{ background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: F.sm }}>✕</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </>)}
    </div>
  );
}

// ─── CRYPTO WATCHLIST AI PANEL ───────────────────────────────────────────────
function CryptoAIPanel({ coinWatchlist, coinsResults, equitiesResults, btcData, anthropicKey, cached, onResult, loading, setLoading, onClose, onScan, scanning }) {
  const allResults = [...coinsResults, ...equitiesResults];
  const watchedResults = coinWatchlist.map(sym => allResults.find(r => r.symbol === sym)).filter(Boolean);
  const dataFresh = watchedResults.length > 0 && watchedResults.some(r => r.scanTime && Date.now() - r.scanTime < 5 * 60 * 1000);
  const oldest = watchedResults.length > 0 ? Math.min(...watchedResults.map(r => r.scanTime || 0)) : 0;
  const ageMs = Date.now() - oldest;
  const ageLabel = oldest === 0 ? "not scanned" : ageMs < 60000 ? "just now" : `${Math.floor(ageMs / 60000)}m ago`;
  const ORANGE = "#f59e0b";

  const [drawerW, setDrawerW] = React.useState(() => { try { return parseInt(localStorage.getItem("cryptoAIDrawerW") || "460"); } catch { return 460; } });
  const dragRef = React.useRef(false);
  const startDrag = (e) => {
    e.preventDefault(); dragRef.current = true;
    const startX = e.clientX; const startW = drawerW;
    const onMove = (e) => { if (!dragRef.current) return; setDrawerW(Math.min(900, Math.max(340, startW + (startX - e.clientX)))); };
    const onUp = () => { dragRef.current = false; setDrawerW(w => { try { localStorage.setItem("cryptoAIDrawerW", String(w)); } catch {} return w; }); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };

  const runRef = React.useRef(null);
  const runAfterScan = React.useRef(false);
  React.useEffect(() => { if (runAfterScan.current && !scanning) { runAfterScan.current = false; if (runRef.current) runRef.current(); } }, [scanning]);
  const handleRunClick = () => { if (!dataFresh) { runAfterScan.current = true; onScan(); return; } if (runRef.current) runRef.current(); };

  runRef.current = async () => {
    if (!watchedResults.length) return;
    setLoading(true);
    try {
      const fgScore = _fearGreedData?.score ?? null;
      const fgLabel = fgScore ? (fgScore >= 75 ? "EXTREME GREED" : fgScore >= 55 ? "GREED" : fgScore >= 45 ? "NEUTRAL" : fgScore >= 25 ? "FEAR" : "EXTREME FEAR") : "N/A";
      const btcCtx = btcData ? `BTC: $${btcData.price?.toLocaleString()}, score ${btcData.buyScore > 0 ? "+" : ""}${btcData.buyScore}, RSI ${btcData.rsi?.toFixed(1)}, CMF ${btcData.cmf?.toFixed(3)}, gate ${btcData.gate?.label}` : "BTC not scanned";
      const rows = watchedResults.map(r => {
        const v = calcVerdict({ result: r, aiCaution: false });
        const isCoin = !CRYPTO_EQUITIES?.includes(r.symbol);
        return `${r.symbol}: score ${r.buyScore > 0 ? "+" : ""}${r.buyScore}, verdict ${v?.short || r.signal}, gate ${r.gate?.label}, RSI ${r.rsi?.toFixed(1)}, CMF ${r.cmf?.toFixed(3)}${isCoin && r.rs ? `, vs BTC ${r.rs.outperforming ? "+" : ""}${r.rs.spread?.toFixed(1) ?? "—"}%` : ""}`;
      }).join("\n");
      const prompt = `You are the senior crypto analyst at Auctus Analytics. Direct, no disclaimers.\n\nMARKET CONTEXT:\n${btcCtx}\nFear & Greed: ${fgScore ?? "N/A"}/100 (${fgLabel})\n\nWATCHLIST — ${watchedResults.length} assets:\n${rows}\n\nRespond ONLY with valid JSON:\n{"regime":"2-3 sentence crypto market read","bestTrade":{"symbol":"TICKER","verdict":"verdict","why":"why","entry":"entry","sizing":"sizing"},"takeaways":[{"ticker":"SYM","insight":"insight"}],"altStrength":"one sentence on alt strength vs BTC","avoid":[{"symbol":"SYM","reason":"reason"}],"deploy":"DEPLOY/WAIT/SELECTIVE — directive"}`;
      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          model: "claude-sonnet-4-20250514",
          max_tokens: 1200,
          ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      const responseText = data.content?.[0]?.text || "";
      let parsed = null;
      try { parsed = JSON.parse(responseText.replace(/```json|```/g, "").trim()); } catch {}
      onResult({ parsed, raw: responseText, date: new Date().toLocaleString() });
    } catch (e) { onResult({ parsed: null, raw: e.message, date: new Date().toLocaleString() }); }
    finally { setLoading(false); }
  };

  const p = cached?.parsed;
  const deployColor = p?.deploy?.startsWith("DEPLOY") ? T.green : p?.deploy?.startsWith("WAIT") ? T.red : T.yellow;

  return (
    <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: drawerW, background: T.surface, borderLeft: `1px solid ${T.border}`, zIndex: 200, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.5)" }}>
      <div onMouseDown={startDrag} style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 10, cursor: "col-resize", zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 3, height: 40, borderRadius: 2, background: ORANGE, opacity: 0.5, pointerEvents: "none" }} />
      </div>
      <div style={{ padding: "12px 14px 12px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <button onClick={onClose} style={{ padding: "5px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.text, borderRadius: 7, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>×</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: ORANGE }}>✦ Crypto Intelligence</div>
          <div style={{ fontSize: 10, color: T.textDim }}>{watchedResults.length} watched · {dataFresh ? `fresh (${ageLabel})` : `${ageLabel} — stale`}{cached?.date ? ` · ${cached.date}` : ""}</div>
        </div>
        <button onClick={handleRunClick} disabled={loading || (scanning && runAfterScan.current) || !watchedResults.length}
          style={{ padding: "7px 14px", background: loading || scanning ? T.surfaceHi : `linear-gradient(135deg, #f59e0b, #f97316)`, border: "none", color: loading || scanning ? T.textDim : "#fff", fontSize: 11, fontWeight: 800, fontFamily: "inherit", cursor: loading || scanning || !watchedResults.length ? "not-allowed" : "pointer", borderRadius: 8, whiteSpace: "nowrap", flexShrink: 0 }}>
          {loading ? "⏳ Analyzing…" : scanning && runAfterScan.current ? "⟳ Scanning…" : !dataFresh ? "↻ Scan & Analyze" : cached ? "↻ Re-run" : "✦ Analyze"}
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {!cached && !loading && <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, padding: 24, textAlign: "center" }}>
          <div style={{ fontSize: 32, color: ORANGE }}>₿</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: ORANGE }}>Crypto Intelligence</div>
          <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.7, maxWidth: 300 }}>{dataFresh ? "BTC regime, alt strength, best trade, and deployment guidance." : `Data is ${ageLabel}. Click Scan & Analyze to refresh.`}</div>
        </div>}
        {loading && <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60%", gap: 10 }}>
          <div style={{ fontSize: 24, color: ORANGE }}>✦</div><div style={{ fontSize: 12, color: ORANGE }}>Analyzing {watchedResults.length} assets…</div>
        </div>}
        {cached && !p && !loading && <div style={{ padding: 16 }}><div style={{ fontSize: 12, color: T.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{cached.raw}</div></div>}
        {p && !loading && (
          <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ padding: "10px 14px", background: `${deployColor}12`, border: `1px solid ${deployColor}44`, borderRadius: 10, display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{ fontSize: 18, flexShrink: 0 }}>{p.deploy?.startsWith("DEPLOY") ? "🟢" : p.deploy?.startsWith("WAIT") ? "🔴" : "🟡"}</div>
              <div><div style={{ fontSize: 10, fontWeight: 800, color: deployColor, letterSpacing: "0.08em", marginBottom: 3 }}>CAPITAL DEPLOYMENT</div><div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{p.deploy}</div></div>
            </div>
            <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 8 }}>₿ MARKET REGIME</div>
              <div style={{ fontSize: 12, color: T.text, lineHeight: 1.75 }}>{p.regime}</div>
              {p.altStrength && <div style={{ marginTop: 10, padding: "8px 10px", background: `${ORANGE}10`, border: `1px solid ${ORANGE}33`, borderRadius: 8, fontSize: 11, color: ORANGE }}><span style={{ fontWeight: 700 }}>Alt Strength: </span>{p.altStrength}</div>}
            </div>
            {p.bestTrade && <div style={{ background: T.bg, border: `2px solid ${T.green}55`, borderRadius: 10, padding: 14, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${ORANGE}, #f97316)` }} />
              <div style={{ fontSize: 10, fontWeight: 800, color: ORANGE, letterSpacing: "0.08em", marginBottom: 10 }}>🏆 BEST TRADE</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <div style={{ fontSize: 20, fontWeight: 900, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>{p.bestTrade.symbol}</div>
                <div style={{ padding: "3px 9px", background: `${T.green}22`, border: `1px solid ${T.green}55`, borderRadius: 6, fontSize: 11, fontWeight: 800, color: T.green }}>{p.bestTrade.verdict}</div>
              </div>
              <div style={{ fontSize: 12, color: T.text, lineHeight: 1.7, marginBottom: 8 }}>{p.bestTrade.why}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div style={{ background: T.surface, borderRadius: 8, padding: "8px 10px" }}><div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>ENTRY</div><div style={{ fontSize: 11, fontWeight: 700, color: T.cyan }}>{p.bestTrade.entry}</div></div>
                <div style={{ background: T.surface, borderRadius: 8, padding: "8px 10px" }}><div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>SIZING</div><div style={{ fontSize: 11, fontWeight: 700, color: T.yellow }}>{p.bestTrade.sizing}</div></div>
              </div>
            </div>}
            {p.takeaways?.length > 0 && <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>💡 KEY TAKEAWAYS</div>
              {p.takeaways.map((t, i) => <div key={i} style={{ display: "flex", gap: 10, marginBottom: i < p.takeaways.length - 1 ? 8 : 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: ORANGE, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, minWidth: 44 }}>{t.ticker}</div>
                <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.6 }}>{t.insight}</div>
              </div>)}
            </div>}
            {p.avoid?.length > 0 && <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.red, letterSpacing: "0.08em", marginBottom: 10 }}>🚫 AVOID</div>
              {p.avoid.map((a, i) => <div key={i} style={{ display: "flex", gap: 10, marginBottom: i < p.avoid.length - 1 ? 8 : 0 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.red, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, minWidth: 44 }}>{a.symbol}</div>
                <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.6 }}>{a.reason}</div>
              </div>)}
            </div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PORTFOLIO CALCULATOR ────────────────────────────────────────────────────
function PortfolioCalculator() {
  const [portfolioSize, setPortfolioSize] = React.useState(() => {
    try { const v = localStorage.getItem("auctus_portfolio_size"); return v || ""; } catch { return ""; }
  });
  const [riskPct, setRiskPct] = React.useState("1");
  const [entryPrice, setEntryPrice] = React.useState("");
  const [stopPrice, setStopPrice] = React.useState("");

  const port = parseFloat(portfolioSize) || 0;
  const risk = parseFloat(riskPct) || 1;
  const entry = parseFloat(entryPrice) || 0;
  const stop = parseFloat(stopPrice) || 0;

  const riskDollar = port * (risk / 100);
  const stopDist = entry > stop && stop > 0 ? entry - stop : null;
  const shares = stopDist ? Math.floor(riskDollar / stopDist) : null;
  const positionValue = shares && entry ? shares * entry : null;
  const posPct = positionValue && port ? (positionValue / port) * 100 : null;

  const savePort = (v) => {
    setPortfolioSize(v);
    try { if (v) localStorage.setItem("auctus_portfolio_size", v); else localStorage.removeItem("auctus_portfolio_size"); } catch {}
  };

  const inputStyle = { width: "100%", background: T.bg, border: `1px solid ${T.border}`, color: T.text, padding: "8px 12px", fontSize: F.base, borderRadius: 8, fontFamily: "'JetBrains Mono', monospace", outline: "none" };
  const labelStyle = { fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 6, display: "block" };

  return (
    <div style={{ padding: "24px 24px 24px 28px", maxWidth: 640 }}>
      <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 4 }}>POSITION SIZING CALCULATOR</div>
      <div style={{ fontSize: F.sm, color: T.textSub, marginBottom: 20, lineHeight: 1.6 }}>
        Based on Keith McCullough's position sizing rules: risk 1-2% of portfolio per trade, max 3% per single stock. Enter your details below.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle}>TOTAL PORTFOLIO SIZE ($)</label>
          <input type="number" value={portfolioSize} onChange={e => savePort(e.target.value)}
            placeholder="e.g. 50000" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>RISK PER TRADE (%)</label>
          <div style={{ display: "flex", gap: 6 }}>
            <input type="number" value={riskPct} onChange={e => setRiskPct(e.target.value)}
              placeholder="1" style={{ ...inputStyle, flex: 1 }} />
            <div style={{ display: "flex", gap: 4 }}>
              {["0.5","1","1.5","2"].map(v => (
                <button key={v} onClick={() => setRiskPct(v)}
                  style={{ padding: "8px 10px", background: riskPct === v ? `${T.cyan}33` : T.surfaceHi, border: `1px solid ${riskPct === v ? T.cyan : T.border}`, color: riskPct === v ? T.cyan : T.textSub, fontSize: F.xs, fontWeight: 700, borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>
                  {v}%
                </button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <label style={labelStyle}>ENTRY PRICE ($)</label>
          <input type="number" value={entryPrice} onChange={e => setEntryPrice(e.target.value)}
            placeholder="e.g. 145.50" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>STOP PRICE ($)</label>
          <input type="number" value={stopPrice} onChange={e => setStopPrice(e.target.value)}
            placeholder="e.g. 138.00" style={inputStyle} />
        </div>
      </div>

      {/* Results */}
      {port > 0 && (
        <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 14 }}>POSITION SIZING RESULTS</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 14 }}>
            {[
              { label: "Max Risk $", value: `$${riskDollar.toFixed(0)}`, color: T.text, desc: `${risk}% of $${port.toLocaleString()}` },
              { label: "Shares to Buy", value: shares ? shares.toLocaleString() : "—", color: shares ? T.cyan : T.textDim, desc: stopDist ? `Stop dist: $${stopDist.toFixed(2)}` : "Enter stop price" },
              { label: "Position Value", value: positionValue ? `$${positionValue.toFixed(0)}` : "—", color: positionValue ? T.text : T.textDim, desc: posPct ? `${posPct.toFixed(1)}% of portfolio` : "" },
              { label: "% of Portfolio", value: posPct ? `${posPct.toFixed(1)}%` : "—", color: posPct > 3 ? T.orange : posPct > 0 ? T.green : T.textDim, desc: posPct > 3 ? "⚠ Exceeds 3% guideline" : posPct > 0 ? "✓ Within guideline" : "" },
            ].map(({ label, value, color, desc }) => (
              <div key={label} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ fontSize: F.xs, color: T.textDim, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: F.xl, fontWeight: 800, color, fontFamily: "'JetBrains Mono', monospace" }}>{value}</div>
                {desc && <div style={{ fontSize: 10, color: T.textDim, marginTop: 4 }}>{desc}</div>}
              </div>
            ))}
          </div>
          {posPct > 3 && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: `${T.orange}15`, border: `1px solid ${T.orange}44`, borderRadius: 8, fontSize: F.xs, color: T.orange }}>
              ⚠ Position is {posPct.toFixed(1)}% of portfolio — above the 3% single-stock max. Reduce shares to {Math.floor(port * 0.03 / entry)} shares (${(Math.floor(port * 0.03 / entry) * entry).toFixed(0)}) to stay within guideline.
            </div>
          )}
        </div>
      )}

      {/* McCullough rules reference */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18 }}>
        <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 12 }}>📖 McCULLOUGH POSITION SIZING RULES</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {[
            ["Single Stocks (Long)", "Min 1% — Max 3%"],
            ["Single Stocks (Short)", "Min 0.5% — Max 2%"],
            ["Equities / ETFs", "Min 2% — Max 6%"],
            ["Commodities / Crypto", "Min 1% — Max 4%"],
            ["Risk per trade", "1–2% of portfolio"],
            ["Buy/sell increments", "50–150 basis points"],
            ["Stop loss (max)", "6% after max position"],
            ["Core principle", "Process > conviction"],
          ].map(([label, value]) => (
            <div key={label} style={{ padding: "8px 12px", background: T.bg, borderRadius: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: F.xs, color: T.textSub }}>{label}</span>
              <span style={{ fontSize: F.xs, fontWeight: 700, color: T.cyan }}>{value}</span>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, fontSize: F.xs, color: T.textDim, lineHeight: 1.6 }}>
          Source: Keith McCullough, "Master The Market" — A Hedge Fund Manager's Guide to Process and Profit
        </div>
      </div>
    </div>
  );
}


// ─── MOBILE WATCHLIST VIEW ────────────────────────────────────────────────────

// ─── MOBILE DETAIL SCREEN ─────────────────────────────────────────────────────
function MobileDetailScreen({ item, onClose, isDark, onAddToWatchlist, anthropicKey }) {
  const { symbol, result, verdict } = item;
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiText, setAiText] = React.useState(null);
  const [aiError, setAiError] = React.useState(null);
  const col = verdict ? (isDark ? verdict.darkColor : verdict.color) : T.textDim;
  const bg = verdict ? (isDark ? verdict.darkBg : verdict.bg) : T.surface;
  const cs = result?.callsSignal, ps = result?.putsSignal;
  const ez = result ? calcEntryZone(result, symbol) : null;

  // Load cached AI from web app on mount
  React.useEffect(() => {
    try {
      // Try all key formats: Supabase via localStorage, auctus prefix, datum prefix
      const raw = localStorage.getItem(`aiSummary_${symbol}`)
        || localStorage.getItem(`auctus_aiSummary_${symbol}`)
        || localStorage.getItem(`datum_aiSummary_${symbol}`);
      const cached = JSON.parse(raw || "null");
      if (cached?.text) setAiText(cached.text);
    } catch {}
  }, [symbol]);

  const runAISummary = async () => {
    if (!result) return;
    setAiLoading(true); setAiError(null);
    try {
      const verdictCtx = calcVerdict({ result, aiCaution: false });
      const isAccumZone = verdictCtx?.layer === "accum";
      const isBubble = verdictCtx?.bubbleEntry;
      const summary = `Ticker: ${symbol}
Price: $${result.price?.toFixed(2)}
Auctus Verdict: ${verdictCtx?.label || result.signal}${isAccumZone ? " — LONG-TERM ACCUMULATION ZONE (not a swing trade)" : isBubble ? " — MARGINAL ENTRY (risk-off market conditions)" : ""}
Score: ${result.buyScore} | RSI: ${result.rsi?.toFixed(1)} | MACD: ${result.macd?.bullish ? "Bullish" : "Bearish"}${result.macd?.crossover ? " (fresh crossover)" : result.macd?.crossunder ? " (fresh crossunder)" : ""}
Bollinger: ${result.bb?.isSqueeze ? "SQUEEZE" : result.price > result.bb?.upper ? "Above upper band" : result.price < result.bb?.lower ? "Below lower band" : "Mid band"}
CMF: ${result.cmf?.toFixed(3)} | VWAP: ${result.vwap?.toFixed(2)} (price ${result.price > result.vwap ? "above" : "below"})
Golden Cross: ${result.goldenCross ? "YES" : "NO"} | 200 SMA Structure: ${result.deathCross ? "YES" : "NO"}
ADX: ${result.adx?.adx?.toFixed(1)} (${result.adx?.label}) | 52W Range: ${result.range52w?.score}/100
RVOL: ${result.rvol?.ratio?.toFixed(2)}x | OBV: ${result.obv?.bearishDiv ? "Bearish divergence" : result.obv?.bullishDiv ? "Bullish divergence" : result.obv?.confirming ? "Confirming" : "Diverging"}
Parabolic SAR: ${result.psar?.bullish ? "Bullish" : "Bearish"} | Trend Structure: ${result.trendStructure?.structure}
Entry Gate: ${result.gate?.label} (${result.gate?.passed}/${result.gate?.total})
Weekly Trend: ${result.weeklyTrend?.label || "N/A"} | MACD Histogram: ${result.macd?.histAccelBull ? "Accelerating bullish" : result.macd?.histFadingBull ? "Fading bullish" : result.macd?.histAccelBear ? "Accelerating bearish" : result.macd?.histFadingBear ? "Fading bearish" : "Neutral"}
RS vs SPY: ${result.rs?.label || "N/A"} | Sector RS: ${result.rsVsSector ? result.rsVsSector.label + " vs " + result.rsVsSector.etf : "N/A"}`.trim();
      const prompt = isAccumZone
        ? `You are a senior investment analyst specializing in long-term accumulation zones. This is NOT a swing trade — Auctus flagged ${symbol} as a potential long-term accumulation zone. Give clear, direct analysis. 3-4 paragraphs max.

Is the selling genuinely slowing? Is this a real long-term buy zone or a falling knife? What confirms accumulation? What price or CMF level gives higher conviction?

${summary}`
        : isBubble
        ? `You are a professional swing trader. This setup is flagged MARGINAL — score is just above threshold in risk-off conditions. Be honest about whether the trade is worth taking. 3-4 paragraphs max.

Is this strong enough to overcome risk-off macro? What would make it high conviction vs cause it to fail? Sizing recommendation?

${summary}`
        : `You are a professional swing trader and technical analyst. Give clear, direct, actionable analysis in plain English. No fluff. Talk like a trader to a trader. 3-4 paragraphs max.

Analyze ${symbol}: What is it doing right now? What's the most important signal? What should I watch for? What's the trade?

${summary}`;
      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      const responseText = data.content?.[0]?.text || "No response";
      setAiText(responseText);
      // Cache it for next time
      try { const cacheVal = JSON.stringify({ text: responseText, date: new Date().toLocaleString() }); localStorage.setItem(`aiSummary_${symbol}`, cacheVal); localStorage.setItem(`auctus_aiSummary_${symbol}`, cacheVal); } catch {}
    } catch (e) { setAiError(e.message); }
    setAiLoading(false);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: T.bg, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Header */}
      <div style={{ padding: "12px 16px", background: T.surface, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <button onClick={onClose}
          style={{ padding: "6px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 7, fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>
          ← Back
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.text }}>{symbol}</div>
          <div style={{ fontSize: 11, color: T.textDim }}>${result?.price?.toFixed(2)} {result?.pctChange != null && <span style={{ color: result.pctChange >= 0 ? T.green : T.red }}>{result.pctChange >= 0 ? "+" : ""}{result.pctChange.toFixed(2)}%</span>}</div>
        </div>
        {verdict && (
          <div style={{ padding: "6px 12px", borderRadius: 8, background: bg, border: `1px solid ${col}55`, color: col, fontSize: 11, fontWeight: 800, textAlign: "center" }}>
            {verdict.short}
            {verdict.bubbleEntry && <div style={{ fontSize: 9, color: "#fbbf24" }}>⚡ MARGINAL</div>}
            {verdict.multiTF && <div style={{ fontSize: 9, color: "#22c55e" }}>📈 MULTI-TF</div>}
          </div>
        )}
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 20, fontWeight: 900, fontFamily: "'JetBrains Mono', monospace",
            color: result?.buyScore >= 7 ? T.green : result?.buyScore >= 4 ? T.yellow : result?.buyScore <= -4 ? T.red : T.textSub }}>
            {result?.buyScore > 0 ? "+" : ""}{result?.buyScore}
          </div>
          <div style={{ fontSize: 9, color: T.textDim }}>SCORE</div>
        </div>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "14px 14px" }}>

        {/* Verdict reason */}
        {verdict?.reason && (
          <div style={{ padding: "10px 12px", background: `${col}12`, border: `1px solid ${col}33`, borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: col, marginBottom: 4 }}>{verdict.label}</div>
            <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7 }}>{verdict.reason}</div>
            {verdict.action && <div style={{ fontSize: 12, color: T.text, marginTop: 6, fontWeight: 600 }}>→ {verdict.action}</div>}
          </div>
        )}

        {/* LAYER 1 — 16 Indicators */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 8 }}>LAYER 1 — SETUP QUALITY · {result?.bullCount}/22 bullish</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
            {result?.signalChecks?.map((c, i) => (
              <div key={i} title={c.tooltip || undefined} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", background: T.surface, borderRadius: 7, border: `1px solid ${c.bull ? T.green + "33" : c.bear ? T.red + "33" : T.border}` }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0, background: c.bull ? T.green : c.bear ? T.red : T.border }} />
                <span style={{ fontSize: 10, color: c.bull ? T.green : c.bear ? T.red : T.textDim, fontWeight: c.bull || c.bear ? 700 : 400 }}>{c.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* LAYER 2 — Entry Timing */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 8 }}>LAYER 2 — ENTRY TIMING</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
            {[
              { l: "Gate", v: result?.gate?.label, c: result?.gate?.label === "GO" ? T.green : T.red },
              { l: "RSI", v: formatRsiWithDivergence(result?.rsi, result?.rsiDiv, 1), c: rsiDisplayColor(result?.rsi, T) },
              { l: "RSI Divergence", v: result?.rsiDiv?.bullish ? "✅ Bullish" : result?.rsiDiv?.bearish ? "⚠ Bearish" : "—", c: result?.rsiDiv?.bullish ? T.green : result?.rsiDiv?.bearish ? T.orange : T.textDim },
              { l: "MACD", v: result?.macd?.crossover ? "CROSS▲" : result?.macd?.bullish ? "BULL▲" : result?.macd?.crossunder ? "CROSS▼" : "BEAR▼", c: result?.macd?.bullish ? T.green : T.red },
              { l: "CMF", v: result?.cmf?.toFixed(3), c: result?.cmf > 0.05 ? T.green : result?.cmf < -0.05 ? T.red : T.text },
              { l: "52W", v: `${result?.range52w?.score}/100`, c: result?.range52w?.nearLow ? T.green : result?.range52w?.nearHigh ? T.orange : T.text },
              { l: "Weekly", v: result?.weeklyTrend?.label || "—", c: result?.weeklyTrend?.strongUptrend ? T.green : result?.weeklyTrend?.downtrend ? T.red : T.yellow },
              { l: "BB", v: result?.bb?.isSqueeze ? BB_TIGHT_BANDS_LABEL : "Normal", c: result?.bb?.isSqueeze ? T.yellow : T.textDim },
              { l: "ADX", v: result?.adx ? (result.adx.strong ? "STRONG" : result.adx.trending ? "TREND" : "WEAK") : "—", c: result?.adx?.strong ? T.green : T.textSub },
              { l: "RS vs SPY", v: result?.rs?.label || "—", c: result?.rs?.outperforming ? T.green : result?.rs?.outperforming === false ? T.red : T.textSub },
            ].map(({ l, v, c }) => (
              <div key={l} style={{ background: T.surface, borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 9, color: T.textDim, marginBottom: 2 }}>{l}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
              </div>
            ))}
          </div>

          {/* Entry zone */}
          {ez && (
            <div style={{ marginTop: 8, padding: "10px 12px", background: T.surfaceHi, borderRadius: 8, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, marginBottom: 6 }}>ENTRY ZONE</div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {ez.entryLow && <div><div style={{ fontSize: 9, color: T.textDim }}>Low</div><div style={{ fontSize: 13, fontWeight: 700, color: T.green, fontFamily: "'JetBrains Mono', monospace" }}>${ez.entryLow.toFixed(2)}</div></div>}
                {ez.entryHigh && <div><div style={{ fontSize: 9, color: T.textDim }}>High</div><div style={{ fontSize: 13, fontWeight: 700, color: T.green, fontFamily: "'JetBrains Mono', monospace" }}>${ez.entryHigh.toFixed(2)}</div></div>}
                {result?.t1 && <div><div style={{ fontSize: 9, color: T.textDim }}>T1</div><div style={{ fontSize: 13, fontWeight: 700, color: T.cyan, fontFamily: "'JetBrains Mono', monospace" }}>${result.t1.toFixed(2)}</div></div>}
                {result?.t2 && <div><div style={{ fontSize: 9, color: T.textDim }}>T2</div><div style={{ fontSize: 13, fontWeight: 700, color: T.cyan, fontFamily: "'JetBrains Mono', monospace" }}>${result.t2.toFixed(2)}</div></div>}
              </div>
            </div>
          )}
        </div>

        {/* Calls / Puts */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 8 }}>OPTIONS SIGNALS</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { label: "⚡ CALLS", signal: cs, bullish: true },
              { label: "▼ PUTS", signal: ps, bullish: false },
            ].map(({ label, signal, bullish }) => {
              const c = bullish ? T.green : T.red;
              const status = signal?.fullSignal ? "FULL SIGNAL" : signal?.partialSignal ? `PARTIAL ${signal.passed}/${signal.total}` : "NO SIGNAL";
              const statusColor = signal?.fullSignal ? c : signal?.partialSignal ? T.yellow : T.textDim;
              return (
                <div key={label} style={{ padding: "10px 12px", background: T.surface, borderRadius: 8, border: `1px solid ${signal?.fullSignal ? c + "55" : T.border}`, boxShadow: signal?.fullSignal ? `0 0 12px ${c}22` : "none" }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: c, marginBottom: 4 }}>{label}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: statusColor }}>{status}</div>
                  <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>Conditions: {signal?.passed || 0}/{signal?.total || 8}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Risk flags */}
        {verdict?.riskFlags?.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 8 }}>⚠ RISK FLAGS</div>
            {verdict.riskFlags.map((f, i) => (
              <div key={i} style={{ padding: "8px 10px", background: `${T.orange}10`, border: `1px solid ${T.orange}33`, borderRadius: 7, marginBottom: 6 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: T.orange }}>{f.label}</div>
                <div style={{ fontSize: 11, color: T.textSub, marginTop: 2 }}>{f.desc}</div>
              </div>
            ))}
          </div>
        )}

        {/* AI Summary */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.purple, letterSpacing: "0.1em", marginBottom: 8 }}>✦ AUCTUS AI ANALYSIS</div>
          {aiText ? (
            <div style={{ padding: "12px 14px", background: `${T.purple}10`, border: `1px solid ${T.purple}44`, borderRadius: 10, fontSize: 12, color: T.textSub, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
              {aiText}
            </div>
          ) : (
            <button onClick={runAISummary} disabled={aiLoading}
              style={{ width: "100%", padding: "12px", background: `${T.purple}18`, border: `1px solid ${T.purple}44`, color: T.purple, borderRadius: 10, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: aiLoading ? "not-allowed" : "pointer" }}>
              {aiLoading ? "⏳ Running Auctus AI Analysis…" : "✦ Auctus AI Analysis"}
            </button>
          )}
          {aiError && <div style={{ fontSize: 11, color: T.red, marginTop: 6 }}>{aiError}</div>}
        </div>

        {/* Add to watchlist if from scanner */}
        {onAddToWatchlist && (
          <button onClick={() => { onAddToWatchlist(symbol); onClose(); }}
            style={{ width: "100%", padding: "12px", background: `${T.cyan}18`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 10, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", marginBottom: 8 }}>
            + Add {symbol} to Watchlist
          </button>
        )}

        <div style={{ height: 20 }} />
      </div>
    </div>
  );
}

// ─── MOBILE FULL DETAIL VIEW ──────────────────────────────────────────────────
function MobileDetailView({ item, onClose, onScan, isDark, anthropicKey, openaiKey, geminiKey, aiProvider }) {
  const { symbol, result, status, position: bp, inPosition } = item;
  const [aiAnalysis, setAiAnalysis] = React.useState(null);
  const [aiLoading, setAiLoading] = React.useState(false);
  const verdict = useVerdictForTicker(result, symbol, {
    inPosition: !!inPosition,
    position: bp,
    currentPrice: result?.price,
  });
  const col = verdict ? (isDark ? verdict.darkColor : verdict.color) : T.textDim;
  const cs = result?.callsSignal, ps = result?.putsSignal;

  // Load cached AI analysis from web app on mount
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(`aiSummary_${symbol}`)
        || localStorage.getItem(`auctus_aiSummary_${symbol}`)
        || localStorage.getItem(`datum_aiSummary_${symbol}`);
      const cached = JSON.parse(raw || "null");
      if (cached?.text) setAiAnalysis(cached.text);
    } catch {}
  }, [symbol]);

  const runAI = async () => {
    if (!result) return;
    setAiLoading(true);
    try {
      const verdictCtx = calcVerdict({ result, aiCaution: false });
      const isAccumZone = verdictCtx?.layer === "accum";
      const isBubble = verdictCtx?.bubbleEntry;
      const summary = `Ticker: ${symbol}
Price: $${result.price?.toFixed(2)}
Auctus Verdict: ${verdictCtx?.label || result.signal}${isAccumZone ? " — LONG-TERM ACCUMULATION ZONE (not a swing trade)" : isBubble ? " — MARGINAL ENTRY (risk-off market conditions)" : ""}
Score: ${result.buyScore} | RSI: ${result.rsi?.toFixed(1)} | MACD: ${result.macd?.bullish ? "Bullish" : "Bearish"}${result.macd?.crossover ? " (fresh crossover)" : result.macd?.crossunder ? " (fresh crossunder)" : ""}
Bollinger: ${result.bb?.isSqueeze ? "SQUEEZE" : result.price > result.bb?.upper ? "Above upper band" : result.price < result.bb?.lower ? "Below lower band" : "Mid band"}
CMF: ${result.cmf?.toFixed(3)} | VWAP: ${result.vwap?.toFixed(2)} (price ${result.price > result.vwap ? "above" : "below"})
Golden Cross: ${result.goldenCross ? "YES" : "NO"} | 200 SMA Structure: ${result.deathCross ? "YES" : "NO"}
ADX: ${result.adx?.adx?.toFixed(1)} (${result.adx?.label}) | 52W Range: ${result.range52w?.score}/100
RVOL: ${result.rvol?.ratio?.toFixed(2)}x | OBV: ${result.obv?.bearishDiv ? "Bearish divergence" : result.obv?.bullishDiv ? "Bullish divergence" : result.obv?.confirming ? "Confirming" : "Diverging"}
Parabolic SAR: ${result.psar?.bullish ? "Bullish" : "Bearish"} | Trend Structure: ${result.trendStructure?.structure}
Entry Gate: ${result.gate?.label} (${result.gate?.passed}/${result.gate?.total})
Weekly Trend: ${result.weeklyTrend?.label || "N/A"} | MACD Histogram: ${result.macd?.histAccelBull ? "Accelerating bullish" : result.macd?.histFadingBull ? "Fading bullish" : result.macd?.histAccelBear ? "Accelerating bearish" : result.macd?.histFadingBear ? "Fading bearish" : "Neutral"}
RS vs SPY: ${result.rs?.label || "N/A"} | Sector RS: ${result.rsVsSector ? result.rsVsSector.label + " vs " + result.rsVsSector.etf : "N/A"}`.trim();
      const prompt = isAccumZone
        ? `You are a senior investment analyst specializing in long-term accumulation zones. This is NOT a swing trade — Auctus flagged ${symbol} as a potential long-term accumulation zone. Give clear, direct analysis. 3-4 paragraphs max.

Is the selling genuinely slowing? Is this a real long-term buy zone or a falling knife? What confirms accumulation? What price or CMF level gives higher conviction?

${summary}`
        : isBubble
        ? `You are a professional swing trader. This setup is flagged MARGINAL — score is just above threshold in risk-off conditions. Be honest about whether the trade is worth taking. 3-4 paragraphs max.

Is this strong enough to overcome risk-off macro? What would make it high conviction vs cause it to fail? Sizing recommendation?

${summary}`
        : `You are a professional swing trader and technical analyst. Give clear, direct, actionable analysis in plain English. No fluff. Talk like a trader to a trader. 3-4 paragraphs max.

Analyze ${symbol}: What is it doing right now? What's the most important signal? What should I watch for? What's the trade?

${summary}`;
      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      const responseText = data.content?.[0]?.text || "No response";
      setAiAnalysis(responseText);
      try { const cacheVal = JSON.stringify({ text: responseText, date: new Date().toLocaleString() }); localStorage.setItem(`aiSummary_${symbol}`, cacheVal); localStorage.setItem(`auctus_aiSummary_${symbol}`, cacheVal); } catch {}
    } catch (e) { setAiAnalysis(e.message); }
    setAiLoading(false);
  };

  if (!result) return (
    <div style={{ position: "fixed", inset: 0, background: T.bg, zIndex: 200, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
      <div style={{ fontSize: 32 }}>⏳</div>
      <div style={{ color: T.textDim }}>Scanning {symbol}…</div>
      <button onClick={onClose} style={{ padding: "10px 24px", background: T.surface, border: `1px solid ${T.border}`, color: T.text, borderRadius: 8, fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>← Back</button>
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: T.bg, zIndex: 200, display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
      {/* Header */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, background: T.surface, borderBottom: `1px solid ${T.border}`, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onClose} style={{ padding: "6px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.text, borderRadius: 7, fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.text }}>{symbol}</div>
          {result.price && <div style={{ fontSize: 12, color: T.textSub, fontFamily: "'JetBrains Mono', monospace" }}>${result.price.toFixed(2)} {result.pctChange != null && <span style={{ color: result.pctChange >= 0 ? T.green : T.red }}>{result.pctChange >= 0 ? "+" : ""}{result.pctChange.toFixed(2)}%</span>}</div>}
        </div>
        {verdict && (
          <div
            title={verdict.label}
            style={{
              padding: "6px 12px",
              borderRadius: 8,
              background: isDark ? verdict.darkBg : verdict.bg,
              border: `1px solid ${col}55`,
              color: col,
              fontSize: 12,
              fontWeight: 800,
              textAlign: "center",
              maxWidth: 200,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {verdict.label}
            {verdict.bubbleEntry && <div style={{ fontSize: 9, color: "#fbbf24" }}>⚡ MARGINAL</div>}
            {verdict.multiTF && <div style={{ fontSize: 9, color: "#22c55e" }}>📈 MULTI-TF</div>}
          </div>
        )}
        <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 900,
          color: result.buyScore >= 7 ? T.green : result.buyScore >= 4 ? T.yellow : result.buyScore <= -4 ? T.red : T.textSub }}>
          {result.buyScore > 0 ? "+" : ""}{result.buyScore}
        </div>
      </div>

      <div style={{ padding: "14px 14px", display: "flex", flexDirection: "column", gap: 14 }}>

        {/* LAYER 1 — Verdict */}
        {verdict?.reason && (
          <div style={{ background: T.surface, border: `1px solid ${col}44`, borderRadius: 12, padding: "14px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 8 }}>LAYER 1 — VERDICT</div>
            <div style={{ fontSize: 13, color: col, fontWeight: 700, marginBottom: 6 }}>{verdict.label}</div>
            <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7, marginBottom: 8 }}>{verdict.reason}</div>
            <div style={{ fontSize: 12, color: T.text, lineHeight: 1.6, padding: "8px 10px", background: `${col}10`, borderRadius: 7 }}>→ {verdict.action}</div>
          </div>
        )}

        {/* Key Indicators Grid */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 10 }}>LAYER 1 — KEY INDICATORS</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[
              { l: "RSI", v: formatRsiWithDivergence(result.rsi, result.rsiDiv, 1), c: rsiDisplayColor(result.rsi, T) },
              { l: "MACD", v: result.macd?.crossover ? "CROSS ▲" : result.macd?.bullish ? "BULL ▲" : result.macd?.crossunder ? "CROSS ▼" : "BEAR ▼", c: result.macd?.bullish ? T.green : T.red },
              { l: "CMF", v: result.cmf?.toFixed(3), c: result.cmf > 0.1 ? T.green : result.cmf < -0.05 ? T.red : T.text },
              { l: "52W Range", v: `${result.range52w?.score}/100`, c: result.range52w?.nearLow ? T.green : result.range52w?.nearHigh ? T.orange : T.text },
              { l: "Gate", v: result.gate?.label, c: result.gate?.label === "GO" ? T.green : T.red },
              { l: "BB", v: result.bb?.isSqueeze ? BB_TIGHT_BANDS_LABEL : result.bb ? "Normal" : "—", c: result.bb?.isSqueeze ? T.yellow : T.text },
              { l: "ADX", v: result.adx?.value?.toFixed(1), c: result.adx?.strong ? T.green : T.text },
              { l: "Weekly", v: result.weeklyTrend?.label || "—", c: result.weeklyTrend?.strongUptrend ? T.green : result.weeklyTrend?.downtrend ? T.red : T.yellow },
              { l: "RS vs SPY", v: result.rs?.label, c: result.rs?.outperforming ? T.green : T.red },
              { l: "OBV", v: result.obv?.bullish ? "Bull ▲" : result.obv?.bearish ? "Bear ▼" : "Neutral", c: result.obv?.bullish ? T.green : result.obv?.bearish ? T.red : T.text },
              { l: "RVOL", v: result.rvol?.ratio ? `${result.rvol.ratio.toFixed(1)}x` : "—", c: result.rvol?.surge ? T.green : T.text },
              { l: "Stoch", v: result.stoch?.oversold ? "Oversold" : result.stoch?.overbought ? "Overbought" : "Neutral", c: result.stoch?.oversold ? T.green : result.stoch?.overbought ? T.red : T.text },
              { l: "Calmar", v: (() => { const c = calcCalmar(result); return c != null ? c.toFixed(1) : null; })(), c: (() => { const c = calcCalmar(result); return c != null ? calmarColor(c) : T.text; })() },
            ].map(({ l, v, c }) => v ? (
              <div key={l} style={{ background: T.bg, borderRadius: 8, padding: "8px 10px" }}>
                <div style={{ fontSize: 9, color: T.textDim, marginBottom: 3 }}>{l}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
              </div>
            ) : null)}
          </div>
        </div>

        {/* Entry Zone */}
        {(result.t1 || result.ema21) && (
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 10 }}>LAYER 2 — ENTRY ZONE</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[
                { l: result.ema21SlopeLabel || "EMA21", v: result.ema21?.toFixed(2) ? `$${result.ema21.toFixed(2)}` : null, c: T.cyan },
                { l: "Target 1", v: result.t1?.toFixed(2) ? `$${result.t1.toFixed(2)}` : null, c: T.green },
                { l: "Target 2", v: result.t2?.toFixed(2) ? `$${result.t2.toFixed(2)}` : null, c: T.green },
                { l: "ATR Stop", v: result.atr ? `$${(result.price - 2 * result.atr).toFixed(2)}` : null, c: T.red },
                { l: "Fib 61.8", v: result.fibs?.fib618?.toFixed(2) ? `$${result.fibs.fib618.toFixed(2)}` : null, c: T.yellow },
                { l: "Fib 50", v: result.fibs?.fib500?.toFixed(2) ? `$${result.fibs.fib500.toFixed(2)}` : null, c: T.yellow },
              ].filter(x => x.v).map(({ l, v, c }) => (
                <div key={l} style={{ background: T.bg, borderRadius: 8, padding: "8px 10px" }}>
                  <div style={{ fontSize: 9, color: T.textDim, marginBottom: 3 }}>{l}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: c, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Options Signals */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 10 }}>OPTIONS SIGNALS</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div style={{ padding: "12px", background: cs?.fullSignal ? `${T.green}15` : T.bg, border: `1px solid ${cs?.fullSignal ? T.green : cs?.partialSignal ? T.yellow : T.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: cs?.bouncePlayOnly ? T.cyan : cs?.fullSignal ? T.green : cs?.partialSignal ? T.yellow : T.textDim, marginBottom: 4 }}>⚡ CALLS {cs?.bouncePlayOnly ? "🐱 BOUNCE" : cs?.fullSignal ? "✓ FULL" : cs?.partialSignal ? "PARTIAL" : "—"}</div>
              <div style={{ fontSize: 10, color: T.textDim }}>{cs ? `${cs.passed}/${cs.total} conditions` : "No data"}</div>
            </div>
            <div style={{ padding: "12px", background: ps?.fullSignal ? `${T.red}15` : T.bg, border: `1px solid ${ps?.fullSignal ? T.red : ps?.partialSignal ? T.orange : T.border}`, borderRadius: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: ps?.fullSignal ? T.red : ps?.partialSignal ? T.orange : T.textDim, marginBottom: 4 }}>▼ PUTS {ps?.fullSignal ? "✓ FULL" : ps?.partialSignal ? "PARTIAL" : "—"}</div>
              <div style={{ fontSize: 10, color: T.textDim }}>{ps ? `${ps.passed}/${ps.total} conditions` : "No data"}</div>
            </div>
          </div>
          {/* Analysis buttons — always shown, grayed out when no signal */}
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <div style={{ flex: 1, padding: "10px", background: (cs?.fullSignal || cs?.partialSignal) ? `${T.green}18` : T.surfaceHi, border: `1px solid ${(cs?.fullSignal || cs?.partialSignal) ? T.green + "44" : T.border}`, borderRadius: 8, textAlign: "center", cursor: (cs?.fullSignal || cs?.partialSignal) ? "pointer" : "default" }}
              onClick={() => {}}>
              <div style={{ fontSize: 12, fontWeight: 700, color: cs?.fullSignal ? T.green : cs?.partialSignal ? T.yellow : T.textDim }}>
                ⚡ Calls {cs?.fullSignal ? "— Full Signal" : cs?.partialSignal ? `— ${cs.passed}/${cs.total}` : "— No Signal"}
              </div>
              {(cs?.fullSignal || cs?.partialSignal) && result.t1 && (
                <div style={{ fontSize: 10, color: T.textDim, marginTop: 3 }}>
                  Entry ~${result.price?.toFixed(0)} · Target ${result.t1?.toFixed(2)} · Stop ${result.atr ? (result.price - 2*result.atr).toFixed(2) : "—"}
                </div>
              )}
              {!cs && <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>Scan to check conditions</div>}
            </div>
            <div style={{ flex: 1, padding: "10px", background: (ps?.fullSignal || ps?.partialSignal) ? `${T.red}18` : T.surfaceHi, border: `1px solid ${(ps?.fullSignal || ps?.partialSignal) ? T.red + "44" : T.border}`, borderRadius: 8, textAlign: "center", cursor: (ps?.fullSignal || ps?.partialSignal) ? "pointer" : "default" }}
              onClick={() => {}}>
              <div style={{ fontSize: 12, fontWeight: 700, color: ps?.fullSignal ? T.red : ps?.partialSignal ? T.orange : T.textDim }}>
                ▼ Puts {ps?.fullSignal ? "— Full Signal" : ps?.partialSignal ? `— ${ps.passed}/${ps.total}` : "— No Signal"}
              </div>
              {(ps?.fullSignal || ps?.partialSignal) && result.d1 && (
                <div style={{ fontSize: 10, color: T.textDim, marginTop: 3 }}>
                  Entry ~${result.price?.toFixed(0)} · Target ${result.d1?.toFixed(2)} · Stop ${result.atr ? (result.price + 2*result.atr).toFixed(2) : "—"}
                </div>
              )}
              {!ps && <div style={{ fontSize: 10, color: T.textDim, marginTop: 2 }}>Scan to check conditions</div>}
            </div>
          </div>
          {/* Strike estimates when full signal */}
          {(cs?.fullSignal || ps?.fullSignal) && result.atr && (
            <div style={{ marginTop: 10, padding: "10px 12px", background: T.bg, borderRadius: 8 }}>
              <div style={{ fontSize: 9, color: T.textDim, marginBottom: 6, letterSpacing: "0.06em" }}>ESTIMATED STRIKES</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                {cs?.fullSignal && [
                  { l: "ATM Call", v: `$${(result.price).toFixed(0)}` },
                  { l: "Target", v: result.t1 ? `$${result.t1.toFixed(2)}` : "—" },
                  { l: "Stop", v: result.atr ? `$${(result.price - 2*result.atr).toFixed(2)}` : "—" },
                ].map(({ l, v }) => (
                  <div key={l} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: T.textDim }}>{l}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.green, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
                  </div>
                ))}
                {ps?.fullSignal && !cs?.fullSignal && [
                  { l: "ATM Put", v: `$${(result.price).toFixed(0)}` },
                  { l: "Target", v: result.d1 ? `$${result.d1.toFixed(2)}` : "—" },
                  { l: "Stop", v: result.atr ? `$${(result.price + 2*result.atr).toFixed(2)}` : "—" },
                ].map(({ l, v }) => (
                  <div key={l} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 9, color: T.textDim }}>{l}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T.red, fontFamily: "'JetBrains Mono', monospace" }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Exit Signals */}
        {result.sellCount > 0 && (
          <div style={{ background: T.surface, border: `1px solid ${result.sellCount >= 3 ? T.red : T.orange}44`, borderRadius: 12, padding: "14px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 8 }}>LAYER 3 — EXIT SIGNALS ({result.sellCount}/5)</div>
            {[
              [result.sellFlags.cmfNegative, "CMF negative — distribution"],
              [result.rsiDiv?.bearish, "RSI bearish divergence"],
              [result.sellFlags.belowEma21, "Below EMA21"],
              [result.sellFlags.macdCrossDown, "MACD bearish crossunder"],
              [result.sellFlags.aboveBBUpper, "Above Bollinger upper"],
            ].filter(([f]) => f).map(([, msg]) => (
              <div key={msg} style={{ fontSize: 12, color: T.orange, marginBottom: 4 }}>⚠ {msg}</div>
            ))}
          </div>
        )}

        {/* AI Analysis */}
        <div style={{ background: T.surface, border: `1px solid ${T.purple}44`, borderRadius: 12, padding: "14px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.purple, letterSpacing: "0.1em", marginBottom: 10 }}>✦ AUCTUS AI ANALYSIS</div>
          {aiAnalysis ? (
            <div style={{ fontSize: 12, color: T.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{aiAnalysis}</div>
          ) : (
            <button onClick={runAI} disabled={aiLoading}
              style={{ width: "100%", padding: "12px", background: aiLoading ? `${T.purple}10` : `${T.purple}22`, border: `1px solid ${T.purple}${aiLoading ? "66" : "44"}`, color: T.purple, borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: aiLoading ? "not-allowed" : "pointer", animation: aiLoading ? "pulse 1s infinite" : "none" }}>
              {aiLoading ? "⏳ Running Auctus AI Analysis…" : "✦ Auctus AI Analysis"}
            </button>
          )}
          {aiAnalysis && (
            <button onClick={runAI} disabled={aiLoading}
              style={{ marginTop: 10, padding: "8px 14px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 7, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>
              ↻ Refresh
            </button>
          )}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => onScan(symbol)} style={{ flex: 1, padding: "12px", background: `${T.cyan}18`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>↻ Rescan</button>
          <button onClick={onClose} style={{ flex: 1, padding: "12px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.text, borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>← Back</button>
        </div>

        <div style={{ height: 20 }} />
      </div>
    </div>
  );
}

// ─── MOBILE WATCHLIST VIEW ────────────────────────────────────────────────────

// ─── MOBILE AI ANALYSIS ───────────────────────────────────────────────────────
function MobileAIAnalysis({ symbol, result, anthropicKey, aiProvider }) {
  const [analysis, setAnalysis] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const runAnalysis = async () => {
    if (!result) return;
    setLoading(true);
    setError(null);
    try {
      // Identical to desktop — same verdict context, same full data summary, same prompts
      const verdictCtx = calcVerdict({ result, aiCaution: false });
      const isAccumZone = verdictCtx?.layer === "accum";
      const isBubble = verdictCtx?.bubbleEntry;

      const summary = `
Ticker: ${symbol}
Price: $${result.price?.toFixed(2)}
Auctus Verdict: ${verdictCtx?.label || result.signal}${isAccumZone ? " — LONG-TERM ACCUMULATION ZONE (not a swing trade)" : isBubble ? " — MARGINAL ENTRY (risk-off market conditions)" : ""}
Signal: ${result.signal}
Score: ${result.buyScore}
RSI: ${result.rsi?.toFixed(1)}
MACD: ${result.macd?.bullish ? "Bullish" : "Bearish"}${result.macd?.crossover ? " (fresh crossover)" : result.macd?.crossunder ? " (fresh crossunder)" : ""}
Bollinger: ${result.bb?.isSqueeze ? "SQUEEZE" : result.price > result.bb?.upper ? "Above upper band" : result.price < result.bb?.lower ? "Below lower band" : "Mid band"}
CMF: ${result.cmf?.toFixed(3)}
VWAP: ${result.vwap?.toFixed(2)} (price is ${result.price > result.vwap ? "above" : "below"})
Golden Cross: ${result.goldenCross ? "YES" : "NO"}
200 SMA Structure: ${result.deathCross ? "YES" : "NO"}
ADX: ${result.adx?.adx?.toFixed(1)} (${result.adx?.label})
52W Range Score: ${result.range52w?.score}/100 (${result.range52w?.nearLow ? "near low" : result.range52w?.nearHigh ? "near high" : "mid range"})
RVOL: ${result.rvol?.ratio?.toFixed(2)}x average
OBV: ${result.obv?.bearishDiv ? "Bearish divergence" : result.obv?.bullishDiv ? "Bullish divergence" : result.obv?.confirming ? "Confirming" : "Diverging"}
Parabolic SAR: ${result.psar?.bullish ? "Bullish" : "Bearish"}
Trend Structure: ${result.trendStructure?.structure}
Entry Gate: ${result.gate?.label} (${result.gate?.passed}/${result.gate?.total})
Weekly Trend: ${result.weeklyTrend?.label || "N/A"} (weekly EMA21 ${result.weeklyTrend?.aboveEma21 ? "above" : "below"} price)
MACD Histogram: ${result.macd?.histAccelBull ? "Accelerating bullish" : result.macd?.histFadingBull ? "Fading bullish — losing steam" : result.macd?.histAccelBear ? "Accelerating bearish" : result.macd?.histFadingBear ? "Fading bearish — potential reversal" : "Neutral"}
RS vs SPY: ${result.rs?.label || "N/A"}
Sector RS: ${result.rsVsSector ? result.rsVsSector.label + " vs " + result.rsVsSector.etf : "N/A"}`.trim();

      const prompt = isAccumZone
        ? `You are a senior investment analyst specializing in identifying long-term accumulation zones. This is NOT a swing trade setup — the Auctus system has flagged this as a potential long-term accumulation zone for a quality stock at a depressed price. Give clear, direct analysis in plain English. No disclaimers. Talk like a seasoned analyst to a patient long-term investor. 3-4 paragraphs max.

Analyze these technical signals for ${symbol} and give your honest assessment: Is the selling genuinely slowing? Is this a real long-term buy zone or a falling knife? What are the key signals that would confirm accumulation is happening (look at CMF trend, RSI divergence, volume on down days)? What price level or CMF reading would give higher conviction for adding?

${summary}`
        : isBubble
        ? `You are a professional swing trader and technical analyst. This setup meets minimum entry criteria but is flagged as MARGINAL — the score is just above threshold and market conditions are risk-off. Be direct and honest about whether this trade is worth taking given current conditions. 3-4 paragraphs max.

Analyze these technical signals for ${symbol}. Focus on: is this setup strong enough to overcome the risk-off macro environment? What would make this a high-conviction entry vs what could cause it to fail? Sizing recommendation?

${summary}`
        : `You are a professional swing trader and technical analyst. Give clear, direct, actionable analysis in plain English. No fluff. No disclaimers. Talk like a trader to a trader. 3-4 paragraphs max.

Analyze these technical signals for ${symbol} and give me your honest assessment. What is this stock doing right now, what's the most important signal, what should I watch for, and what's the trade?

${summary}`;

      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      const responseText = data.content?.[0]?.text || "No response";
      setAnalysis(responseText);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ marginTop: 4 }}>
      {!analysis && !loading && (
        <button onClick={e => { e.stopPropagation(); runAnalysis(); }}
          style={{ width: "100%", padding: "9px", background: "linear-gradient(135deg, #7c3aed22, #d946ef22)", border: "1px solid #7c3aed55", color: "#c084fc", borderRadius: 7, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", letterSpacing: "0.02em" }}>
          ✦ Auctus AI Analysis
        </button>
      )}
      {loading && (
        <div style={{ padding: "10px", background: "#7c3aed12", border: "1px solid #7c3aed33", borderRadius: 7, textAlign: "center", fontSize: 12, color: "#c084fc" }}>
          ⏳ Analyzing {symbol}...
        </div>
      )}
      {analysis && (
        <div style={{ padding: "10px 12px", background: "#7c3aed12", border: "1px solid #7c3aed44", borderRadius: 7 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#c084fc", letterSpacing: "0.08em" }}>✦ AUCTUS AI ANALYSIS</span>
            <button onClick={e => { e.stopPropagation(); setAnalysis(null); }}
              style={{ fontSize: 10, color: T.textDim, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>↻ New</button>
          </div>
          <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7 }}>{analysis}</div>
        </div>
      )}
      {error && (
        <div style={{ padding: "8px 10px", background: `${T.red}12`, border: `1px solid ${T.red}33`, borderRadius: 7, fontSize: 11, color: T.red }}>
          {error}
        </div>
      )}
    </div>
  );
}

function MobileWatchlistView({ watchlist, onScan, onScanAll, scanning, onAddTicker, onRemove, isDark, onToggleTheme, anthropicKey, openaiKey, geminiKey, aiProvider, proMode, onTogglePosition, colVis = {}, toggleColVis, COL_DEFS = [], livePrice = {} }) {
  const [input, setInput] = React.useState("");
  const [sortCol, setSortCol] = React.useState("score");
  const [sortDir, setSortDir] = React.useState("desc");
  const [detailItem, setDetailItem] = React.useState(null);
  const [mobileTab, setMobileTab] = React.useState("watchlist");
  const [showMobileAnalysis, setShowMobileAnalysis] = React.useState(null);
  const [scannerResults, setScannerResults] = React.useState(() => {
    try { const s = localStorage.getItem("mobileScannerResults"); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  const [scannerScanning, setScannerScanning] = React.useState(false);
  const [scannerProgress, setScannerProgress] = React.useState({ done: 0, total: 0 });
  const [scannerCompletedAt, setScannerCompletedAt] = React.useState(() => {
    try { return localStorage.getItem("mobileScannerTime") || null; } catch { return null; }
  });
  const [showGuide, setShowGuide] = React.useState(false);
  const [showCalc, setShowCalc] = React.useState(false);
  const [showColSettings, setShowColSettings] = React.useState(false);
  const cv = (key) => proMode && (colVis[key] !== false);
  // Clear legacy mobile col settings
  React.useEffect(() => { try { localStorage.removeItem("mobileColSettings"); } catch {} }, []);
  // Android back button — all state declared before this effect
  const openDetail = React.useCallback((item) => {
    setDetailItem(item);
    window.history.pushState({ auctusDetail: item.symbol }, "");
  }, []);

  React.useEffect(() => {
    const onPop = (e) => {
      if (detailItem) { setDetailItem(null); return; }
      if (showGuide) { setShowGuide(false); return; }
      if (showCalc) { setShowCalc(false); return; }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [detailItem, showGuide, showCalc]);

  const handleAdd = () => {
    const sym = input.trim().toUpperCase();
    if (!sym) return;
    onAddTicker(sym);
    setInput("");
  };

  // When a scanned item updates, refresh detail view if open
  React.useEffect(() => {
    if (detailItem) {
      const updated = watchlist.find(i => i.symbol === detailItem.symbol);
      if (updated) setDetailItem(updated);
    }
  }, [watchlist]);

  const sortedList = [...watchlist].sort((a, b) => {
    // inPosition flag pins to absolute top
    const aInPos = !!(a.inPosition);
    const bInPos = !!(b.inPosition);
    if (aInPos && !bInPos) return -1;
    if (!aInPos && bInPos) return 1;
    // Then positions with buy price entered
    const aHasPos = !!(parseFloat(a.position?.buyPrice));
    const bHasPos = !!(parseFloat(b.position?.buyPrice));
    if (aHasPos && !bHasPos) return -1;
    if (!aHasPos && bHasPos) return 1;
    const av = a.result?.buyScore ?? -99, bv = b.result?.buyScore ?? -99;
    const ap = a.result?.pctChange ?? 0, bp = b.result?.pctChange ?? 0;
    if (sortCol === "score") return sortDir === "desc" ? bv - av : av - bv;
    if (sortCol === "pct") return sortDir === "desc" ? bp - ap : ap - bp;
    if (sortCol === "ticker") return sortDir === "desc" ? b.symbol.localeCompare(a.symbol) : a.symbol.localeCompare(b.symbol);
    return 0;
  });

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "desc" ? "asc" : "desc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const SortArrow = ({ col }) => sortCol === col
    ? <span style={{ color: T.cyan, fontSize: 9 }}>{sortDir === "desc" ? " ▼" : " ▲"}</span>
    : <span style={{ color: T.textDim, fontSize: 9 }}> ⇅</span>;

  const hdr = { fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.06em", padding: "6px 6px", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" };

  const [longPressSym, setLongPressSym] = React.useState(null); // visual feedback during hold
  const touchStartX = React.useRef(0);
  const touchStartY = React.useRef(0);
  const touchStartTime = React.useRef(0);
  const longPressTimer = React.useRef(null);
  const longPressFired = React.useRef(false);
  const LONG_PRESS_MS = 600; // 600ms hold — firm but not frustrating

  const renderRow = (item, onTap) => {
    const { symbol, result, status } = item;
    const isLongPressing = longPressSym === symbol;
    const inPos = !!(item.inPosition);

    // Position health for in-position rows
    const posHealth = inPos && result ? (() => {
      const flags = [];
      if (result.ema21 && result.price < result.ema21) flags.push("below EMA21");
      if (result.cmf != null && result.cmf < -0.05) flags.push("CMF negative");
      if (result.sellCount >= 3) flags.push(`${result.sellCount} sell flags`);
      const health = flags.length === 0 ? "HOLD" : flags.length === 1 ? "WATCH" : flags.length >= 2 ? "CAUTION" : "HOLD";
      const healthColor = health === "HOLD" ? T.green : health === "WATCH" ? T.yellow : T.red;
      return { health, healthColor, flags };
    })() : null;

    return (
      <tr key={symbol}
        onTouchStart={e => {
          touchStartX.current = e.touches[0].clientX;
          touchStartY.current = e.touches[0].clientY;
          touchStartTime.current = Date.now();
          longPressFired.current = false;
          clearTimeout(longPressTimer.current);
          setLongPressSym(symbol);
          longPressTimer.current = setTimeout(() => {
            longPressFired.current = true;
            setLongPressSym(null);
            onTap();
          }, LONG_PRESS_MS);
        }}
        onTouchMove={e => {
          const dx = Math.abs(touchStartX.current - e.touches[0].clientX);
          const dy = Math.abs(touchStartY.current - e.touches[0].clientY);
          if (dx > 8 || dy > 8) { clearTimeout(longPressTimer.current); setLongPressSym(null); }
        }}
        onTouchEnd={e => { clearTimeout(longPressTimer.current); setLongPressSym(null); }}
        style={{
          borderBottom: `1px solid ${T.border}`,
          borderLeft: inPos ? `3px solid ${T.cyan}` : "3px solid transparent",
          background: isLongPressing ? `${T.purple}18` : inPos ? `${T.cyan}06` : "transparent",
          cursor: "pointer", transition: "background 0.15s",
          boxShadow: inPos ? `inset 0 0 0 1px ${T.cyan}18` : "none",
        }}>
        {/* ✕ remove — far left, own narrow cell, hard to accidentally hit */}
        <td style={{ padding: "0 4px 0 6px", width: 28, flexShrink: 0 }}>
          <button
            onClick={e => { e.stopPropagation(); onRemove(symbol); }}
            onTouchStart={e => e.stopPropagation()}
            style={{ padding: "8px 4px", background: "transparent", border: "none", color: T.textDim, fontSize: 14, cursor: "pointer", lineHeight: 1, display: "block", width: "100%" }}>
            ✕
          </button>
        </td>
        <td style={{ padding: "9px 4px 9px 0", minWidth: 90 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
              background: status === "done" ? T.green : status === "loading" ? T.cyan : status === "error" ? T.red : T.border }} />
            <TickerLabel symbol={symbol} />
            {inPos && <span style={{ fontSize: 8, padding: "1px 4px", borderRadius: 4, background: `${T.cyan}22`, color: T.cyan, fontWeight: 800, letterSpacing: "0.04em", border: `1px solid ${T.cyan}44` }}>IN</span>}
          </div>
          {inPos && posHealth && (
            <div style={{ fontSize: 8, fontWeight: 700, color: posHealth.healthColor, marginTop: 2, marginLeft: 11 }}>
              {posHealth.health}{posHealth.flags.length > 0 ? ` · ${posHealth.flags[0]}` : " · thesis intact"}
            </div>
          )}
        </td>
        <td style={{ padding: "9px 6px", textAlign: "right" }}>
          {(() => {
            const live = livePrice[symbol];
            const displayPrice = live?.price || result?.price;
            const displayPct = live?.changePct ?? result?.pctChange;
            const extPrice = live?.isPreMarket ? live.preMarketPrice
              : (live?.isPostMarket || (!live?.isPreMarket && !live?.isRegularHours && live?.postMarketPrice != null)) ? live.postMarketPrice
              : null;
            const extChangePct = live?.isPreMarket ? live.preMarketChangePct
              : (live?.isPostMarket || (!live?.isPreMarket && !live?.isRegularHours && live?.postMarketPrice != null)) ? live.postMarketChangePct
              : null;
            const extLabel = live?.isPreMarket ? "PRE" : extPrice != null ? "AH" : null;
            const extColor = live?.isPreMarket ? T.yellow : T.purple;
            if (!displayPrice) return <span style={{ color: T.textDim, fontSize: 11 }}>—</span>;
            return (
              <>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>${displayPrice.toFixed(2)}</div>
                {displayPct != null && <div style={{ fontSize: 9, color: displayPct >= 0 ? T.green : T.red }}>{displayPct >= 0 ? "+" : ""}{displayPct.toFixed(2)}%</div>}
                {extLabel && extPrice != null && (
                  <div style={{ marginTop: 2, padding: "1px 4px", borderRadius: 3, background: `${extColor}15`, border: `1px solid ${extColor}33`, display: "inline-flex", alignItems: "center", gap: 3 }}>
                    <span style={{ fontSize: 7, fontWeight: 800, color: extColor }}>{extLabel}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>${extPrice.toFixed(2)}</span>
                    {extChangePct != null && <span style={{ fontSize: 8, color: extChangePct >= 0 ? T.green : T.red }}>{extChangePct >= 0 ? "+" : ""}{extChangePct.toFixed(2)}%</span>}
                  </div>
                )}
              </>
            );
          })()}
        </td>
        <td style={{ padding: "9px 4px", textAlign: "center" }}>
          <MobileVerdictCell
            result={result}
            symbol={symbol}
            isDark={isDark}
            inPosition={inPos}
            position={item.position}
            currentPrice={result?.price}
          />
        </td>
        <td style={{ padding: "9px 4px", textAlign: "center" }}>
          {result?.buyScore != null ? <span style={{ fontSize: 14, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
            color: result.buyScore >= 7 ? T.green : result.buyScore >= 4 ? T.yellow : result.buyScore <= -4 ? T.red : T.textSub }}>
            {result.buyScore > 0 ? "+" : ""}{result.buyScore}</span> : <span style={{ color: T.textDim }}>—</span>}
        </td>
        {/* RSI */}
        {cv("rsi") && <td style={{ padding: "9px 4px", textAlign: "center", minWidth: 48 }}
          title={rsiDisplayTooltip(result?.rsi, result?.rsiDiv)}>
          {result?.rsi != null ? <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 700,
            color: rsiDisplayColor(result.rsi, T), whiteSpace: "nowrap" }}>
            {formatRsiWithDivergence(result.rsi, result.rsiDiv, 0)}
          </span> : <span style={{ color: T.textDim, fontSize: 11 }}>—</span>}
        </td>}
        {/* CMF */}
        {cv("cmf") && <td style={{ padding: "9px 4px", textAlign: "center", minWidth: 52 }}>
          {result?.cmf != null ? <span style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace",
            color: result.cmf > 0.05 ? T.green : result.cmf < -0.05 ? T.red : T.textSub }}>
            {result.cmf.toFixed(2)}
          </span> : <span style={{ color: T.textDim, fontSize: 11 }}>—</span>}
        </td>}
        <td style={{ padding: "9px 4px", textAlign: "center", minWidth: 100 }}>
          {result ? <PhaseBadgeCell result={result} inPosition={inPos} /> : null}
        </td>
      </tr>
    );
  };

  return (
    <div style={{ height: "100dvh", display: "flex", flexDirection: "column", background: T.bg, color: T.text, fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden" }}>

      {/* Detail overlay */}
      {detailItem && (
        <MobileDetailView
          item={detailItem}
          onClose={() => setDetailItem(null)}
          onScan={onScan}
          isDark={isDark}
          anthropicKey={anthropicKey}
          openaiKey={openaiKey}
          geminiKey={geminiKey}
          aiProvider={aiProvider}
        />
      )}

      {/* ── HEADER ── */}
      <div style={{ padding: "8px 12px 6px", borderBottom: `1px solid ${T.border}`, background: T.surface, flexShrink: 0 }}>
        {/* Logo row — no scan button here */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 64" fill="none" style={{ height: 36, width: "auto", flexShrink: 0 }}>
            <defs>
              <linearGradient id="mMarkG" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#6b21a8"/>
                <stop offset="50%" stopColor="#7c3aed"/>
                <stop offset="100%" stopColor="#d946ef"/>
              </linearGradient>
              <linearGradient id="mAaGrad" x1="0" y1="0" x2="60" y2="64" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#6b21a8"/>
                <stop offset="50%" stopColor="#7c3aed"/>
                <stop offset="100%" stopColor="#d946ef"/>
              </linearGradient>
            </defs>
            <polygon points="8,56 24,10 32,30 20,30 22,36 34,36 40,56" fill="url(#mAaGrad)" opacity="0.9"/>
            <polygon points="28,56 40,10 56,56 50,56 40,28 30,56" fill="url(#mAaGrad)"/>
            <line x1="72" y1="6" x2="72" y2="58" stroke={isDark ? "#1c1c1c" : "#ddd6fe"} strokeWidth="1.5"/>
            <text x="84" y="40" fontFamily="'-apple-system', 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif" fontSize="28" fontWeight="800" letterSpacing="1" fill="url(#mMarkG)">AUCTUS</text>
            <text x="85" y="57" fontFamily="'-apple-system', 'Helvetica Neue', Arial, sans-serif" fontSize="14" fontWeight="600" letterSpacing="4" fill={isDark ? "#9ca3af" : "#6b7280"}>ANALYTICS</text>
          </svg>
          <div style={{ flex: 1 }} />
          <button onClick={() => setShowCalc(true)}
            style={{ padding: "5px 9px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", marginRight: 3 }}>
            💼
          </button>
          <button onClick={() => setShowGuide(true)}
            style={{ padding: "5px 9px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", marginRight: 3 }}>
            📚
          </button>
          <button onClick={onToggleTheme}
            style={{ padding: "5px 9px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>
            {isDark ? "☀" : "🌙"}
          </button>
        </div>
        {/* Tab bar */}
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          {[["watchlist", "📋 Watch"], ["scanner", "🔭 Scan"]].map(([id, label]) => (
            <button key={id} onClick={() => setMobileTab(id)}
              style={{ flex: 1, padding: "5px 0", background: mobileTab === id ? `${T.cyan}22` : T.surfaceHi, border: `1px solid ${mobileTab === id ? T.cyan : T.border}`, color: mobileTab === id ? T.cyan : T.textSub, borderRadius: 7, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
              {label}
            </button>
          ))}
        </div>
        {/* Watchlist tab: compact add row + scan button */}
        {mobileTab === "watchlist" && (
          <>
          <div style={{ display: "flex", gap: 5 }}>
            <input value={input} onChange={e => setInput(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && handleAdd()}
              placeholder="Add ticker…" maxLength={10}
              style={{ flex: 1, padding: "5px 8px", background: T.bg, border: `1px solid ${T.border}`, color: T.text, borderRadius: 6, fontSize: 12, outline: "none" }} />
            <button onClick={handleAdd}
              style={{ padding: "5px 10px", background: `${T.purple}22`, border: `1px solid ${T.purple}44`, color: T.purple, borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
              + Add
            </button>
            <button onClick={onScanAll}
              disabled={scanning}
              style={{ padding: "5px 10px", background: scanning ? T.surface : `${T.cyan}22`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: scanning ? "not-allowed" : "pointer", whiteSpace: "nowrap" }}>
              {scanning ? "…" : "↻ Scan"}
            </button>
            {proMode && (
              <button onClick={() => setShowColSettings(s => !s)}
                title="Column settings"
                style={{ padding: "5px 8px", background: showColSettings ? `${T.purple}22` : T.surfaceHi, border: `1px solid ${showColSettings ? T.purple : T.border}`, color: showColSettings ? T.purple : T.textDim, borderRadius: 6, fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}>
                ⚙
              </button>
            )}
          </div>
          {/* Column settings panel — pro only */}
          {proMode && showColSettings && (
            <div style={{ marginTop: 6, padding: "10px 12px", background: T.surfaceHi, borderRadius: 8, border: `1px solid ${T.border}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.06em", marginBottom: 8 }}>COLUMNS — tap to toggle</div>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {COL_DEFS.map(({ key, label }) => {
                  const on = colVis[key] !== false;
                  return (
                    <button key={key} onClick={() => toggleColVis && toggleColVis(key)}
                      style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: on ? 700 : 400, fontFamily: "inherit", cursor: "pointer", border: `1px solid ${on ? T.purple + "55" : T.border}`, background: on ? `${T.purple}15` : T.bg, color: on ? T.purple : T.textDim }}>
                      {on ? "✓ " : ""}{label}
                    </button>
                  );
                })}
              </div>
              <div style={{ fontSize: 10, color: T.textDim, marginTop: 8 }}>Always shown: Ticker · Price · Verdict · Score · Phase</div>
            </div>
          )}
          </>
        )}
        {/* Calls / Puts analysis buttons — watchlist only */}
        {mobileTab === "watchlist" && (
          <div style={{ display: "flex", gap: 5, marginTop: 5 }}>
            <button onClick={() => setShowMobileAnalysis(showMobileAnalysis === "CALLS" ? null : "CALLS")}
              style={{ flex: 1, padding: "5px 0", background: showMobileAnalysis === "CALLS" ? `${T.green}33` : `${T.green}12`, border: `1px solid ${showMobileAnalysis === "CALLS" ? T.green : T.green + "55"}`, color: T.green, borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
              ⚡ Calls Analysis {watchlist.filter(i => callsSignalDisplayActive(i.result)).length > 0 ? `(${watchlist.filter(i => callsSignalDisplayActive(i.result)).length})` : ""}
            </button>
            <button onClick={() => setShowMobileAnalysis(showMobileAnalysis === "PUTS" ? null : "PUTS")}
              style={{ flex: 1, padding: "5px 0", background: showMobileAnalysis === "PUTS" ? `${T.red}33` : `${T.red}12`, border: `1px solid ${showMobileAnalysis === "PUTS" ? T.red : T.red + "55"}`, color: T.red, borderRadius: 6, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
              🔻 Puts Analysis {watchlist.filter(i => i.result?.putsSignal?.fullSignal || i.result?.putsSignal?.partialSignal).length > 0 ? `(${watchlist.filter(i => i.result?.putsSignal?.fullSignal || i.result?.putsSignal?.partialSignal).length})` : ""}
            </button>
          </div>
        )}
      </div>

      {/* Mobile Options Analysis Panel */}
      {showMobileAnalysis && mobileTab === "watchlist" && (() => {
        const candidates = watchlist
          .filter(i => i.result && (
            showMobileAnalysis === "CALLS"
              ? callsSignalDisplayActive(i.result)
              : (i.result.putsSignal?.fullSignal || i.result.putsSignal?.partialSignal)
          ))
          .map(i => ({ symbol: i.symbol, result: i.result }));
        return (
          <div style={{ flexShrink: 0, maxHeight: "50vh", overflowY: "auto", borderBottom: `1px solid ${T.border}` }}>
            <OptionsAnalysisPanel
              candidates={candidates}
              type={showMobileAnalysis}
              onClose={() => setShowMobileAnalysis(null)}
              anthropicKey={anthropicKey}
            />
          </div>
        );
      })()}

      {/* Table — header and body in ONE shared scroll container so they move together */}
      <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        {mobileTab === "watchlist" ? (
          <table style={{ borderCollapse: "collapse", minWidth: "100%", width: "max-content" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 5, background: T.surface }}>
              <tr>
                <th style={{ ...hdr, width: 28, borderBottom: `1px solid ${T.border}` }}></th>
                <th style={{ ...hdr, textAlign: "left", minWidth: 90, borderBottom: `1px solid ${T.border}` }} onClick={() => toggleSort("ticker")}>TICKER<SortArrow col="ticker" /></th>
                <th style={{ ...hdr, textAlign: "right", minWidth: 80, borderBottom: `1px solid ${T.border}` }} onClick={() => toggleSort("pct")}>PRICE<SortArrow col="pct" /></th>
                <th style={{ ...hdr, textAlign: "center", minWidth: 100, borderBottom: `1px solid ${T.border}` }}>VERDICT</th>
                <th style={{ ...hdr, textAlign: "center", minWidth: 56, borderBottom: `1px solid ${T.border}` }} onClick={() => toggleSort("score")}>SCORE<SortArrow col="score" /></th>
                {proMode && cv("rsi") && <th style={{ ...hdr, textAlign: "center", minWidth: 48, borderBottom: `1px solid ${T.border}` }}>RSI</th>}
                {proMode && cv("cmf") && <th style={{ ...hdr, textAlign: "center", minWidth: 52, borderBottom: `1px solid ${T.border}` }}>CMF</th>}
                <th style={{ ...hdr, textAlign: "center", minWidth: 100, borderBottom: `1px solid ${T.border}` }}>PHASE</th>
              </tr>
            </thead>
            <tbody>
              {watchlist.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", padding: "40px 16px", color: T.textDim, fontSize: 13 }}>Add a ticker above to start</td></tr>}
              {sortedList.map(item => renderRow(item, () => openDetail(item)))}
            </tbody>
          </table>
        ) : (
          <MobileScannerView
            onAddToWatchlist={onAddTicker}
            isDark={isDark}
            onOpenDetail={item => setDetailItem({ symbol: item.symbol, result: item.result, status: "done" })}
            results={scannerResults}
            setResults={(r) => {
              setScannerResults(r);
              try { localStorage.setItem("mobileScannerResults", JSON.stringify(r)); } catch {}
            }}
            scanning={scannerScanning}
            setScanning={setScannerScanning}
            progress={scannerProgress}
            setProgress={setScannerProgress}
            watchlistSymbols={watchlist.map(i => i.symbol)}
            completedAt={scannerCompletedAt}
            onScanComplete={(time) => {
              setScannerCompletedAt(time);
              try { localStorage.setItem("mobileScannerTime", time); } catch {}
            }}
          />
        )}
      </div>

      <div style={{ flexShrink: 0, padding: "5px 12px", paddingBottom: "calc(5px + env(safe-area-inset-bottom, 0px))", borderTop: `1px solid ${T.border}`, background: T.surface, fontSize: 10, color: T.textDim, textAlign: "center" }}>
        Not financial advice · Data: Yahoo Finance
      </div>

      {/* ── GUIDE MODAL ── */}
      {showGuide && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500, background: T.bg, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <MobileGuideModal onClose={() => setShowGuide(false)} />
        </div>
      )}

      {/* ── POSITION SIZING CALCULATOR MODAL ── */}
      {showCalc && (
        <div style={{ position: "fixed", inset: 0, zIndex: 500, background: T.bg, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ padding: "12px 16px", background: T.surface, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <span style={{ fontSize: 16 }}>💼</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>Position Sizing Calculator</div>
              <div style={{ fontSize: 10, color: T.textDim }}>McCullough risk management framework</div>
            </div>
            <button onClick={() => setShowCalc(false)}
              style={{ padding: "6px 12px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 7, fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>
              ✕
            </button>
          </div>
          {/* Calculator content — reuse existing component */}
          <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
            <PortfolioCalculator />
          </div>
        </div>
      )}
    </div>
  );
}



// ─── MOBILE GUIDE MODAL ───────────────────────────────────────────────────────
// Unified guide — dropdown carousel selector at top, full section content below
const GUIDE_SECTIONS = [
  { id: "playbook",   icon: "📚", label: "Signal Playbook",      type: "playbook" },
  { id: "framework",  icon: "🧠", label: "How It Works",         type: "help" },
  { id: "quickstart", icon: "🚀", label: "Quick Start",          type: "help" },
  { id: "operations", icon: "📋", label: "Order of Operations",  type: "help" },
  { id: "indicators", icon: "📊", label: "Indicator Guide",      type: "help" },
  { id: "scoring",    icon: "🎯", label: "Signal Scoring",       type: "help" },
  { id: "trades",     icon: "💡", label: "Trade Type Guide",     type: "help" },
  { id: "faq",        icon: "❓", label: "FAQ",                  type: "help" },
  { id: "features",   icon: "⚡", label: "Features",             type: "help" },
  { id: "options",    icon: "🔷", label: "Options Guide",        type: "help" },
  { id: "crypto",     icon: "₿",  label: "Crypto Guide",         type: "help" },
];

function MobileGuideModal({ onClose }) {
  const [activeSection, setActiveSection] = React.useState("playbook");
  const [showDropdown, setShowDropdown] = React.useState(false);
  const current = GUIDE_SECTIONS.find(s => s.id === activeSection);

  // Help tab content — from HELP_TABS
  const helpTab = HELP_TABS.find(t => t.id === activeSection);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header with dropdown selector */}
      <div style={{ padding: "10px 14px", background: T.surface, borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Dropdown selector */}
          <div style={{ position: "relative", flex: 1 }}>
            <button
              onClick={() => setShowDropdown(d => !d)}
              style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: T.bg, border: `1px solid ${T.purple}55`, color: T.text, borderRadius: 8, fontSize: 14, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
              <span>{current?.icon}</span>
              <span style={{ flex: 1 }}>{current?.label}</span>
              <span style={{ color: T.textDim, fontSize: 11 }}>{showDropdown ? "▲" : "▼"}</span>
            </button>
            {showDropdown && (
              <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, zIndex: 100, overflow: "hidden", boxShadow: "0 8px 24px rgba(0,0,0,0.3)" }}>
                {GUIDE_SECTIONS.map(sec => (
                  <button key={sec.id}
                    onClick={() => { setActiveSection(sec.id); setShowDropdown(false); }}
                    style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", background: activeSection === sec.id ? `${T.purple}18` : "transparent", border: "none", borderBottom: `1px solid ${T.border}`, color: activeSection === sec.id ? T.purple : T.text, fontSize: 13, fontWeight: activeSection === sec.id ? 700 : 400, fontFamily: "inherit", cursor: "pointer", textAlign: "left" }}>
                    <span style={{ fontSize: 16 }}>{sec.icon}</span>
                    <span>{sec.label}</span>
                    {activeSection === sec.id && <span style={{ marginLeft: "auto", color: T.purple }}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={onClose}
            style={{ padding: "8px 12px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 7, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", flexShrink: 0 }}>
            ✕
          </button>
        </div>
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}
        onClick={() => showDropdown && setShowDropdown(false)}>

        {/* ── SIGNAL PLAYBOOK ── */}
        {activeSection === "playbook" && (
          <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", minHeight: 0 }}>
            <SignalPlaybook />
          </div>
        )}

        {/* ── HELP TABS (all other sections) ── */}
        {activeSection !== "playbook" && helpTab && (
          <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "12px 14px" }}>
            {helpTab.sections.map((section, si) => (
              <div key={si} style={{ marginBottom: 20 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                  <div style={{ width: 3, height: 18, borderRadius: 2, background: section.color, flexShrink: 0 }} />
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{section.title}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {section.steps.map((step, i) => {
                    const isQ = step.startsWith("Q:");
                    return (
                      <div key={i} style={{ display: "flex", gap: 10, padding: "10px 12px", background: T.surface, borderRadius: 8, border: `1px solid ${T.border}`, alignItems: "flex-start" }}>
                        {!isQ && <div style={{ width: 6, height: 6, borderRadius: "50%", background: section.color, marginTop: 6, flexShrink: 0 }} />}
                        <div style={{ fontSize: 12, color: isQ ? T.text : T.textSub, lineHeight: 1.6, flex: 1 }}>
                          {isQ ? (
                            <>
                              <span style={{ fontWeight: 700, color: section.color }}>{step.split(" — ")[0]} — </span>
                              <span>{step.split(" — ").slice(1).join(" — ")}</span>
                            </>
                          ) : step}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <div style={{ padding: "10px 12px", background: `${T.purple}10`, border: `1px solid ${T.purple}33`, borderRadius: 8, fontSize: 11, color: T.textDim, lineHeight: 1.6, marginBottom: 16 }}>
              <span style={{ fontWeight: 700, color: T.purple }}>⚠ Disclaimer: </span>
              Auctus Analytics is an educational and research tool. It does not provide financial advice. All trading involves risk. Past signal performance does not guarantee future results.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function MobileScannerView({ onAddToWatchlist, isDark, onOpenDetail, results, setResults, scanning, setScanning, progress, setProgress, watchlistSymbols, completedAt, onScanComplete }) {
  const [sortCol, setSortCol] = React.useState("score");
  const [sortDir, setSortDir] = React.useState("desc");
  const stopRef = React.useRef(false);

  const runScan = async () => {
    setScanning(true); setResults([]); stopRef.current = false;
    const syms = ALL_SCANNER_TICKERS;
    setProgress({ done: 0, total: syms.length });
    let spyReturn = null;
    try {
      const spyData = await fetchTickerForScanner("SPY");
      if (spyData?.closes[63]) spyReturn = ((spyData.closes[0] - spyData.closes[63]) / spyData.closes[63]) * 100;
    } catch {}
    const allResults = [];
    for (let i = 0; i < syms.length; i += 5) {
      if (stopRef.current) break;
      const batch = syms.slice(i, i + 5);
      const batchRes = await Promise.all(batch.map(async sym => {
        try {
          const data = await fetchTickerForScanner(sym);
          if (!data) return null;
          const result = runAnalysis(data, spyReturn, isCrypto(sym), sym);
          if (!result) return null;
          return { symbol: sym, result };
        } catch { return null; }
      }));
      batchRes.filter(Boolean).forEach(r => allResults.push(r));
      setProgress({ done: Math.min(i + 5, syms.length), total: syms.length });
      setResults([...allResults].sort((a, b) => (b.result?.buyScore || 0) - (a.result?.buyScore || 0)));
      if (i + 5 < syms.length) await new Promise(r => setTimeout(r, 150));
    }
    setScanning(false);
  };

  const sorted = [...results].sort((a, b) => {
    const av = a.result?.buyScore ?? -99, bv = b.result?.buyScore ?? -99;
    return sortDir === "desc" ? bv - av : av - bv;
  });

  const hdr = { fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.06em", padding: "6px 6px", cursor: "pointer", userSelect: "none" };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ padding: "10px 12px", borderBottom: `1px solid ${T.border}`, background: T.surfaceHi, flexShrink: 0, display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: T.textSub, fontWeight: 600 }}>
            {scanning
              ? `Scanning… ${progress.done}/${progress.total}`
              : results.length > 0
                ? <>{results.length} results{completedAt ? <span style={{ color: T.textDim }}> · {completedAt}</span> : null}</>
                : `${ALL_SCANNER_TICKERS.length} tickers ready to scan`}
          </div>
          {scanning && <div style={{ marginTop: 4, height: 3, background: T.border, borderRadius: 2 }}>
            <div style={{ height: "100%", width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`, background: T.cyan, borderRadius: 2, transition: "width 0.3s" }} />
          </div>}
        </div>
        {scanning
          ? <button onClick={() => stopRef.current = true} style={{ padding: "7px 12px", background: `${T.red}22`, border: `1px solid ${T.red}44`, color: T.red, borderRadius: 7, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>■ Stop</button>
          : <button onClick={runScan} style={{ padding: "7px 14px", background: `${T.cyan}22`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 7, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>🔭 Scan</button>
        }
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", background: T.surface, flexShrink: 0 }}>
        <colgroup><col style={{ width: "24%" }} /><col style={{ width: "18%" }} /><col style={{ width: "22%" }} /><col style={{ width: "16%" }} /><col style={{ width: "20%" }} /></colgroup>
        <thead><tr>
          <th style={{ ...hdr, textAlign: "left" }}>TICKER</th>
          <th style={{ ...hdr, textAlign: "right" }}>PRICE</th>
          <th style={{ ...hdr, textAlign: "center" }}>VERDICT</th>
          <th style={{ ...hdr, textAlign: "center", cursor: "pointer" }} onClick={() => setSortDir(d => d === "desc" ? "asc" : "desc")}>
            SCORE {sortDir === "desc" ? "▼" : "▲"}
          </th>
          <th style={{ ...hdr, textAlign: "center" }}>PHASE</th>
        </tr></thead>
      </table>

      <div style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <colgroup><col style={{ width: "24%" }} /><col style={{ width: "18%" }} /><col style={{ width: "22%" }} /><col style={{ width: "16%" }} /><col style={{ width: "20%" }} /></colgroup>
          <tbody>
            {sorted.length === 0 && !scanning && <tr><td colSpan={5} style={{ textAlign: "center", padding: "40px 16px", color: T.textDim, fontSize: 13 }}>Tap Scan to find setups across {ALL_SCANNER_TICKERS.length} tickers</td></tr>}
            {sorted.map(({ symbol, result }) => {
              const display = resolveDisplayVerdict(result, { activeSignal: result?.activeSignal });
              const chip = displayVerdictChipStyle(display.verdictStyle);
              const alreadyAdded = watchlistSymbols?.includes(symbol);
              return (
                <tr key={symbol} style={{ borderBottom: `1px solid ${T.border}`, cursor: "pointer" }}
                  onClick={() => onOpenDetail({ symbol, result })}
                  onTouchEnd={e => {
                    const dy = Math.abs((e.changedTouches[0]?.clientY || 0) - (e.currentTarget._touchY || 0));
                    e.currentTarget._touchY = undefined;
                    if (dy > 8) { e.preventDefault(); return; }
                    onOpenDetail({ symbol, result });
                  }}
                  onTouchStart={e => { e.currentTarget._touchY = e.touches[0].clientY; }}>
                  <td style={{ padding: "7px 8px", fontSize: 13, color: T.text }}>
                    <TickerLabel symbol={symbol} />
                    <button
                      onClick={e => { e.stopPropagation(); if (!alreadyAdded) onAddToWatchlist(symbol); }}
                      style={{ display: "block", marginTop: 3, padding: "2px 8px", background: alreadyAdded ? T.surfaceHi : `${T.purple}22`, border: `1px solid ${alreadyAdded ? T.border : T.purple}44`, color: alreadyAdded ? T.textDim : T.purple, borderRadius: 4, fontSize: 9, fontWeight: 700, fontFamily: "inherit", cursor: alreadyAdded ? "default" : "pointer" }}>
                      {alreadyAdded ? "✓ Added" : "+ Add"}
                    </button>
                  </td>
                  <td style={{ padding: "7px 6px", textAlign: "right", fontSize: 11, fontFamily: "'JetBrains Mono', monospace", color: T.text }}>${result.price?.toFixed(2)}</td>
                  <td style={{ padding: "9px 4px", textAlign: "center" }}>
                    <span style={{ display: "inline-block", padding: "3px 5px", borderRadius: 5, background: chip.bg, border: `1px solid ${chip.border}`, color: chip.color, fontSize: 9, fontWeight: 800 }} title={display.subtitle}>{display.verdict}</span>
                  </td>
                  <td style={{ padding: "9px 4px", textAlign: "center", fontSize: 13, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
                    color: result.buyScore >= 7 ? T.green : result.buyScore >= 4 ? T.yellow : result.buyScore <= -4 ? T.red : T.textSub }}>
                    {result.buyScore > 0 ? "+" : ""}{result.buyScore}
                  </td>
                  <td style={{ padding: "9px 4px", textAlign: "center" }}>
                    <PhaseBadgeCell result={result} inPosition={false} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── NEWS FEED PANEL ──────────────────────────────────────────────────────────
function NewsFeedPanel({ globalFeed, watchlistSymbols, initialFilter, onSetFilter, onAddToWatchlist }) {
  const [filter, setFilter] = React.useState(initialFilter || null);
  const [sourceFilter, setSourceFilter] = React.useState("all");

  React.useEffect(() => { if (initialFilter !== undefined) setFilter(initialFilter); }, [initialFilter]);

  const allSymbols = [...new Set(globalFeed.map(a => a.symbol).filter(Boolean))].sort();

  const filtered = globalFeed.filter(a => {
    if (filter && a.symbol !== filter && a.symbol !== null) return false;
    if (filter && a.symbol === null) return false; // hide general news when filtered to a symbol
    if (sourceFilter !== "all" && a.source_type !== sourceFilter) return false;
    return true;
  });

  const sentimentColor = (score) =>
    score > 0.1 ? T.green : score < -0.1 ? T.red : T.textDim;
  const sentimentLabel = (score) =>
    score > 0.1 ? "Positive" : score < -0.1 ? "Negative" : "Neutral";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Filters */}
      <div style={{ padding: "10px 16px 10px 24px", borderBottom: `1px solid ${T.border}`, flexShrink: 0, background: T.surface }}>
        {/* Source filter */}
        <div style={{ display: "flex", gap: 5, marginBottom: 8, flexWrap: "wrap" }}>
          {[
            { id: "all", label: "All Sources" },
            { id: "finnhub", label: "Finnhub" },
            { id: "yahoo", label: "Yahoo Finance" },
            { id: "reuters", label: "Reuters" },
          ].map(({ id, label }) => (
            <button key={id} onClick={() => setSourceFilter(id)}
              style={{ padding: "3px 10px", background: sourceFilter === id ? `${T.cyan}22` : T.surfaceHi, border: `1px solid ${sourceFilter === id ? T.cyan : T.border}`, color: sourceFilter === id ? T.cyan : T.textDim, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer", fontWeight: sourceFilter === id ? 700 : 400 }}>
              {label}
            </button>
          ))}
        </div>
        {/* Ticker filter */}
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: F.xs, color: T.textDim, flexShrink: 0 }}>Filter:</span>
          <button onClick={() => { setFilter(null); onSetFilter && onSetFilter(null); }}
            style={{ padding: "3px 10px", background: !filter ? `${T.purple}22` : T.surfaceHi, border: `1px solid ${!filter ? T.purple : T.border}`, color: !filter ? T.purple : T.textDim, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer", fontWeight: !filter ? 700 : 400 }}>
            All
          </button>
          {allSymbols.map(sym => (
            <button key={sym} onClick={() => { setFilter(sym); onSetFilter && onSetFilter(sym); }}
              style={{ padding: "3px 10px", background: filter === sym ? `${T.cyan}22` : T.surfaceHi, border: `1px solid ${filter === sym ? T.cyan : T.border}`, color: filter === sym ? T.cyan : T.textDim, borderRadius: 6, fontSize: F.xs, fontFamily: "inherit", cursor: "pointer", fontWeight: filter === sym ? 700 : 400 }}>
              {sym}
            </button>
          ))}
        </div>
      </div>

      {/* Article count */}
      <div style={{ padding: "6px 16px", borderBottom: `1px solid ${T.border}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: F.xs, color: T.textDim }}>{filtered.length} article{filtered.length !== 1 ? "s" : ""}{filter ? ` · ${filter}` : " · all tickers"}</span>
        {filter && <span style={{ fontSize: F.xs, color: T.textDim }}>Showing {filter} only</span>}
      </div>

      {/* Feed */}
      <div style={{ flex: 1, overflowY: "auto" }}>
        {filtered.length === 0 && (
          <div style={{ padding: "40px 20px", textAlign: "center", color: T.textDim }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}>📰</div>
            <div style={{ fontSize: F.sm }}>
              {globalFeed.length === 0
                ? "News loads automatically when you scan tickers. Scan your watchlist to populate the feed."
                : `No ${sourceFilter !== "all" ? sourceFilter + " " : ""}articles${filter ? ` for ${filter}` : ""}.`}
            </div>
          </div>
        )}
        {filtered.map((article, i) => (
          <div key={i} style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, background: i % 2 === 0 ? "transparent" : T.surfaceHi }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              {/* Sentiment dot */}
              <div style={{ width: 7, height: 7, borderRadius: "50%", background: sentimentColor(article.score), marginTop: 5, flexShrink: 0, boxShadow: `0 0 4px ${sentimentColor(article.score)}88` }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Ticker badge + source + time */}
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                  {article.symbol && (
                    <span style={{ padding: "1px 7px", borderRadius: 4, background: `${T.cyan}18`, border: `1px solid ${T.cyan}33`, fontSize: 10, fontWeight: 800, color: T.cyan }}>
                      {article.symbol}
                    </span>
                  )}
                  {article.category && article.category !== "general" && (
                    <span style={{ padding: "1px 7px", borderRadius: 4, background: T.surfaceHi, fontSize: 10, color: T.textDim }}>
                      {article.category}
                    </span>
                  )}
                  <span style={{ fontSize: F.xs, color: T.textDim }}>{article.source}</span>
                  <span style={{ fontSize: F.xs, color: T.textDim }}>·</span>
                  <span style={{ fontSize: F.xs, color: T.textDim }}>{article.time}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: sentimentColor(article.score), marginLeft: "auto" }}>
                    {sentimentLabel(article.score)}
                  </span>
                </div>
                {/* Headline */}
                <a href={article.url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: F.sm, color: T.text, fontWeight: 500, lineHeight: 1.5, textDecoration: "none", display: "block" }}
                  onMouseEnter={e => e.currentTarget.style.color = T.cyan}
                  onMouseLeave={e => e.currentTarget.style.color = T.text}>
                  {article.title}
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: "6px 16px", borderTop: `1px solid ${T.border}`, flexShrink: 0, background: T.surface }}>
        <div style={{ fontSize: 10, color: T.textDim, textAlign: "center" }}>
          Sources: Finnhub · Yahoo Finance · Reuters · Sentiment via keyword analysis · Not financial advice
        </div>
      </div>
    </div>
  );
}


function BottomPanel({ pendingTrade, onClearPending, onAddToWatchlist, watchlistSymbols, activeTab, globalNewsFeed, newsFilter, setNewsFilter, anthropicKey }) {
  const [tab, setTab] = useState(activeTab || "scanner");
  useEffect(() => { if (pendingTrade) setTab("journal"); }, [pendingTrade]);
  useEffect(() => { if (activeTab) setTab(activeTab); }, [activeTab]);
  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", gap: 0, borderBottom: `1px solid ${T.border}`, flexShrink: 0, overflowX: "auto" }}>
        {[["portfolio", "💼 Portfolio"], ["scanner", "🔭 Scanner"], ["performance", "📊 Performance"], ["backtest", "📈 Backtester"], ["journal", "📓 Journal"], ["playbook", "📚 Playbook"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding: "10px 18px", background: tab === id ? T.surface : T.bg, border: "none", borderBottom: tab === id ? `2px solid ${T.cyan}` : "2px solid transparent", color: tab === id ? T.text : T.textDim, fontSize: F.base, fontWeight: tab === id ? 800 : 600, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap", letterSpacing: "0.01em" }}>
            {label}
          </button>
        ))}
      </div>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {tab === "news"      && <NewsFeedPanel globalFeed={globalNewsFeed || []} watchlistSymbols={watchlistSymbols} initialFilter={newsFilter} onSetFilter={setNewsFilter} />}
        {tab === "portfolio" && <PortfolioCalculator />}
        {tab === "playbook" && <SignalPlaybook />}
        {tab === "scanner" && <MarketScanner onAddToWatchlist={onAddToWatchlist} watchlistSymbols={watchlistSymbols} anthropicKey={anthropicKey} />}
        {tab === "performance" && <PerformanceTracker />}
        {tab === "backtest" && (
          <div style={{ padding: 24, color: T.textDim, fontSize: 13 }}>
            <div style={{ color: T.text, fontWeight: 700, marginBottom: 8 }}>Backtester — Coming Q3 2026</div>
            <div>Replay your signals against historical price action. Full 22-indicator backtesting across custom date ranges.</div>
          </div>
        )}
        {tab === "journal" && <MyTrades pendingTrade={pendingTrade} onClearPending={onClearPending} />}
      </div>
    </div>
  );
}

// ─── SIGNAL PLAYBOOK ──────────────────────────────────────────────────────────
function SignalPlaybook() {
  const sectionKeys = ["how", "tiers", "phases", "setups", "indicators", "blocks", "entryexit", "watchlist", "tips"];
  const [open, setOpen] = useState(() => Object.fromEntries(sectionKeys.map((k) => [k, true])));
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  const pb = { fontSize: F.sm, color: T.text, lineHeight: 1.75, marginBottom: 12 };
  const sub = { fontSize: F.xs, fontWeight: 800, color: T.textDim, letterSpacing: "0.06em", marginBottom: 8, marginTop: 4 };

  const Section = ({ k, title, subtitle, accent, children }) => {
    const isOpen = open[k];
    return (
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden", marginBottom: 12, background: T.surfaceHi }}>
        <button
          type="button"
          onClick={() => toggle(k)}
          style={{
            width: "100%",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "14px 16px",
            minHeight: 48,
            background: isOpen ? `${accent}14` : T.surfaceHi,
            border: "none",
            borderBottom: isOpen ? `1px solid ${T.border}` : "none",
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: F.md, fontWeight: 800, color: T.text, lineHeight: 1.35 }}>{title}</div>
            {subtitle ? <div style={{ fontSize: F.xs, color: T.textDim, marginTop: 4, lineHeight: 1.45 }}>{subtitle}</div> : null}
          </div>
          <span style={{ color: accent, fontSize: 13, fontWeight: 800, flexShrink: 0, marginTop: 2 }}>{isOpen ? "▼" : "▶"}</span>
        </button>
        {isOpen && (
          <div style={{ padding: "16px 16px 18px", background: T.bg }}>{children}</div>
        )}
      </div>
    );
  };

  const tier = (emoji, label, range, color, lines) => (
    <div style={{ borderLeft: `3px solid ${color}`, paddingLeft: 12, marginBottom: 16 }}>
      <div style={{ fontSize: F.sm, fontWeight: 800, color: T.text }}>
        {emoji} {label}{" "}
        <span style={{ color: T.textDim, fontWeight: 600 }}>{range}</span>
      </div>
      {lines.map((line, i) => (
        <div key={i} style={{ ...pb, marginBottom: i === lines.length - 1 ? 0 : 8 }}>{line}</div>
      ))}
    </div>
  );

  const phase = (emoji, title, color, desc) => (
    <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${T.border}` }}>
      <div style={{ fontSize: F.sm, fontWeight: 800, color }}>
        {emoji} {title}
      </div>
      <div style={{ ...pb, marginBottom: 0, marginTop: 6, color: T.textSub }}>{desc}</div>
    </div>
  );

  const setup = (emoji, title, body) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: F.sm, fontWeight: 800, color: T.text }}>{emoji} {title}</div>
      <div style={{ ...pb, marginBottom: 0, marginTop: 6, color: T.textSub }}>{body}</div>
    </div>
  );

  return (
    <div style={{ background: T.surface, borderTop: `2px solid ${T.border}`, marginTop: 0 }}>
      <div style={{ padding: "18px clamp(14px, 4vw, 28px) 8px" }}>
        <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.12em", marginBottom: 4 }}>REFERENCE GUIDE</div>
        <div style={{ fontSize: F.xl, fontWeight: 700, color: T.text }}>📚 Signal Playbook</div>
        <div style={{ fontSize: F.sm, color: T.textDim, marginTop: 6, lineHeight: 1.55 }}>
          How Auctus layers macro, market tide, and per-ticker scoring — plus verdict tiers, phases, setups, and rules. Tap a section to expand or collapse.
        </div>
      </div>

      <div style={{ padding: "0 clamp(14px, 4vw, 28px) 32px", maxWidth: 920, margin: "0 auto" }}>
        <Section k="how" title="Section 1 — How Auctus works" subtitle="Three layers of intelligence before a signal" accent={T.cyan}>
          <div style={pb}>
            Auctus analyzes every ticker using <strong style={{ color: T.text }}>3 layers of intelligence</strong> before generating a signal:
          </div>
          <div style={{ ...sub, color: T.blue, marginTop: 0 }}>LAYER 1 — MACRO CONTEXT (TOP BANNER)</div>
          <div style={pb}>What is the overall economy doing? Risk On/Off, Fed direction, Dollar strength, Credit health, Sector rotation.</div>
          <div style={{ ...sub, color: T.purple }}>LAYER 2 — MARKET TIDE (SECOND BANNER)</div>
          <div style={pb}>What is the market doing <strong style={{ color: T.text }}>today</strong>? SPY/QQQ trend, VIX level, Breadth score. Bull Tide amplifies bullish signals. Bear Tide dampens bullish signals.</div>
          <div style={{ ...sub, color: T.green }}>LAYER 3 — INDIVIDUAL STOCK SCORING</div>
          <div style={{ ...pb, marginBottom: 0 }}>
            <strong style={{ color: T.text }}>36 indicators</strong> scored from <strong style={{ color: T.green }}>−15</strong> to <strong style={{ color: T.green }}>+15</strong>. Market context is applied after scoring. Final verdict is generated.
          </div>
        </Section>

        <Section k="tiers" title="Section 2 — Verdict tiers" subtitle="Score bands and how to use them" accent={T.yellow}>
          {tier("⚡", "POWER PLAY", "(+12 to +15)", T.yellow, ["Full size position. All layers aligned.", "Highest conviction setup available."])}
          {tier("✅", "STANDARD LONG", "(+6 to +11)", T.green, ["Normal size. Scale in on EMA21 pullbacks.", "Tide Confirmed = market supporting the move.", "Fighting Bear Tide = proceed with caution."])}
          {tier("⚠", "SPECULATIVE", "(+4 to +5)", T.orange, ["25–50% size only. No options.", "Setup building but not fully confirmed."])}
          {tier("🟦", "STARTER — 25% ACCUMULATION", "(Phase 1, score −2 to +4)", "#06b6d4", ["Enter 25% now when Phase 1 institutional accumulation fires before score confirms.", "Add 75% when Phase 2/3 promotes to STANDARD LONG / POWER PLAY.", "Exit if Phase 1 fades within 10 sessions without Phase 2."])}
          {tier("🟪", "STARTER SHORT — 25% DISTRIBUTION", "(Phase 1 bear, score −4 to +2)", "#a855f7", ["Enter 25% short when Phase 1 distribution fires before score confirms.", "Add 75% when Phase 2/3 puts confirmation fires.", "Blocked when golden cross or post-rally exhaustion is active."])}
          {tier("➡", "CHOP", "(−3 to +3)", T.textDim, ["Stay in cash. No edge present.", "All options signals blocked automatically."])}
          {tier("", "SCALP ONLY", "(−4 to −5)", "#fb923c", ["Short-term puts only.", "Not a full position setup."])}
          {tier("🔻", "STANDARD SHORT", "(−6 to −11)", T.red, ["Puts on failed relief rallies.", "Requires close below VWAP + RVOL ≥ 1.2x."])}
          {tier("💀", "BREAKDOWN", "(−12 to −15)", "#dc2626", ["High conviction puts.", "Multiple bearish layers aligned."])}
        </Section>

        <Section k="phases" title="Section 3 — Signal phase badges" subtitle="Direction-aware labels on the watchlist PHASE column" accent={T.blue}>
          <div style={{ ...pb, marginTop: 0, marginBottom: 14, color: T.textDim }}>Each badge names the direction (CALLS vs PUTS) and the stage (WATCH → SETUP → READY). CHOP / no edge: no phase badge. When a trade setup is active, the setup name appears after the badge — for example: 🚀 CALLS READY · 🚩 BULL FLAG.</div>
          {phase("👁", "CALLS WATCH (blue, static)", T.blue, "Phase 1 bullish. Accumulation or RS leader detected — not a trade yet; put on radar for calls.")}
          {phase("⚡", "CALLS SETUP (yellow, pulse)", T.yellow, "Phase 2 bullish. Pre-breakout coiling or options flow — prepare a calls entry plan.")}
          {phase("🚀", "CALLS READY (green, flashing)", T.green, "Phase 3 bullish. Calls entry conditions met (full or bounce-play). Gate aligned for long-side options.")}
          {phase("👁", "PUTS WATCH (orange, static)", T.orange, "Phase 1 bearish. Distribution — watch for puts development.")}
          {phase("🔻", "PUTS SETUP (red, pulse)", T.red, "Phase 2 bearish. Pre-breakdown / structure weakening — prepare puts.")}
          {phase("💀", "PUTS READY (red, flashing)", "#dc2626", "Phase 3 bearish. Full puts signal — entry conditions met.")}
        </Section>

        <Section k="setups" title="Section 4 — Trade setup types" subtitle="Additive patterns on top of the core score" accent={T.green}>
          {setup("🚀", "GAP AND GO", "Stock gaps up 3%+ at open in direction of trend with strong volume. Continuation play. Entry: Market open or first pullback. Exit: T1 (1× gap size) and T2 (2× gap size).")}
          {setup("↩", "GAP FILL", "Stock gaps up against the trend on weak volume. High probability the gap fills back to pre-gap price. Entry: Puts on failed gap. Exit: D1 (full fill) and D2 (extended fill).")}
          {setup("📈", "EARNINGS DRIFT", "Stock beat earnings and is continuing higher as institutions add to positions post-report. Entry: Any pullback to earnings day open. Exit: T1 and T2 based on earnings day range.")}
          {setup("💥", "RANGE BREAKOUT", "Stock consolidated in tight range 10+ days then broke out with strong volume. Institutional accumulation complete. Entry: Breakout candle or first pullback. Exit: T1 (range height) and T2 (2× range).")}
          {setup("🚩", "BULL FLAG", "Strong move up (pole) followed by tight consolidation (flag) then breakout. Entry: Break above flag high with volume. Exit: T1 (half pole) and T2 (full pole).")}
          {setup("🔄", "SUPPORT BOUNCE", "Healthy stock in uptrend pulled back 8–18% to key support. Institutions still holding. Entry: First green candle at support. Exit: T1 (prior high) and T2 (extended).")}
          {setup("🐱", "DEAD CAT BOUNCE", "Stock beaten down 20%+ with sellers exhausting. Oversold snap-back opportunity. Entry: First green candle with volume after flush. Exit: T1 (38.2% retracement) and T2 (61.8% retracement). ⚠ Higher risk — always check earnings date.")}
        </Section>

        <Section k="indicators" title="Section 5 — The 36 indicators" subtitle="Grouped by role in the model" accent={T.cyan}>
          <div style={{ ...sub, marginTop: 0 }}>STRUCTURAL (HIGHEST WEIGHT)</div>
          <div style={pb}><strong style={{ color: T.text }}>1. CMF — Chaikin Money Flow</strong> — Institutional buying/selling pressure. Positive = accumulating (bullish). Negative = distributing (bearish). <strong style={{ color: T.red }}>HARD RULE:</strong> Negative CMF blocks all calls signals.</div>
          <div style={pb}><strong style={{ color: T.text }}>2. EMA21 + Slope</strong> — 21-day EMA. Price above + slope rising = full bullish score. Price above + flat = reduced. Price below = bearish.</div>
          <div style={pb}><strong style={{ color: T.text }}>3. OBV Divergence</strong> — OBV vs price. OBV rising while price flat = stealth accumulation. OBV falling while price rises = distribution warning.</div>
          <div style={pb}><strong style={{ color: T.text }}>4. 200 SMA Structure</strong> — Price vs 200-day. Healthy zone: 0–15% above. Extended: &gt;15% above (mean reversion risk). Broken: &gt;10% below (structural damage).</div>

          <div style={sub}>MOMENTUM</div>
          <div style={pb}>5. MACD Crossover — trend momentum leader · 6. Williams %R Recovery — de-weighted oscillator · 7. BB Squeeze — volatility compression; squeeze firing = <strong style={{ color: T.text }}>1.5× multiplier</strong> on MACD and ADX</div>

          <div style={sub}>CONFIRMATION</div>
          <div style={pb}>8. RSI — leader oscillator. <strong style={{ color: T.red }}>HARD RULE:</strong> RSI ≥85 blocks calls. RSI ≤20 blocks puts.</div>
          <div style={pb}>9. RVOL · 10. ADX · 11. Stochastic (reduced weight) · 12. PSAR · 13. VWAP — <strong style={{ color: T.red }}>HARD RULE:</strong> Below VWAP AND EMA21 = calls blocked · 14. RS vs SPY · 15. Weekly Trend Multiplier — weekly bearish = 50% score cut; Market Tide breadthScore ≥+3 → ×0.75, ≥+4 → override (×1) · 16. Volume Breakout · 17. RSI Divergence · 18. Volume Climax (context-aware: high vol + RSI &gt;75 = exhaustion bearish; high vol + RSI 45–70 = breakout bullish) · 19. Sector RS — <strong style={{ color: T.text }}>weak sector = −1 score penalty</strong> · 20. Key Level Proximity · 21. Multi-Timeframe Alignment Bonus · 22. Volume Breakout Confirmation</div>

          <div style={sub}>REVERSAL DETECTION</div>
          <div style={pb}>23. Oversold Reversal — RSI recovering from &lt;35 · 24. Volume Reversal — first green after 5+ red days · 25. VWAP Recovery — reclaiming VWAP after 5+ days below</div>

          <div style={sub}>PHASE SYSTEM (EARLY WARNING)</div>
          <div style={{ ...pb, marginBottom: 0 }}>26. Accumulation (Phase 1 bullish) · 27. RS Leader (Phase 1 bullish) · 28. Distribution (Phase 1 bearish) · 29. Pre-Breakout Coiling (Phase 2 bullish) · 30. Options Flow Shift (Phase 2 bullish) · 31. Pre-Breakdown Coiling (Phase 2 bearish) · 32. Sector Leading Indicator (Phase 1/2) · 33–36. Dead Cat Bounce phases (3 phases)</div>
        </Section>

        <Section k="blocks" title="Section 6 — Hard block rules" subtitle="Automatic blocks regardless of headline score" accent={T.red}>
          <ul style={{ margin: 0, paddingLeft: 20, color: T.textSub, fontSize: F.sm, lineHeight: 1.7 }}>
            <li style={{ marginBottom: 8 }}>CMF negative → Calls blocked</li>
            <li style={{ marginBottom: 8 }}>CMF positive → Puts blocked</li>
            <li style={{ marginBottom: 8 }}>RSI ≥ 85 → Calls blocked</li>
            <li style={{ marginBottom: 8 }}>RSI ≤ 20 → Puts blocked</li>
            <li style={{ marginBottom: 8 }}>Score −3 to +3 → All options blocked (CHOP)</li>
            <li style={{ marginBottom: 8 }}>OBV bearish divergence → Calls blocked</li>
            <li style={{ marginBottom: 8 }}>Price below VWAP AND EMA21 → Calls blocked</li>
            <li style={{ marginBottom: 8 }}>Sector RS negative → −1 score penalty</li>
            <li style={{ marginBottom: 8 }}>Standard Short requires close below VWAP + RVOL ≥ 1.2x on rejection bar</li>
            <li style={{ marginBottom: 8 }}>Earnings within 7 days → Bounce/Gap signals blocked</li>
            <li style={{ marginBottom: 8 }}>Gap Fill → Never generates calls signal</li>
            <li style={{ marginBottom: 0 }}>Gap and Go → Never generates puts signal</li>
          </ul>
        </Section>

        <Section k="entryexit" title="Section 7 — Entry and exit rules" subtitle="Discipline by tier and setup" accent={T.green}>
          <div style={{ ...sub, marginTop: 0 }}>ENTRY RULES</div>
          <div style={pb}>⚡ <strong style={{ color: T.text }}>POWER PLAY:</strong> Full size at market open or first green candle</div>
          <div style={pb}>✅ <strong style={{ color: T.text }}>STANDARD LONG:</strong> Scale in on EMA21 pullback with RVOL confirmation</div>
          <div style={pb}>⚠ <strong style={{ color: T.text }}>SPECULATIVE:</strong> Limit order only, never chase, 25–50% normal size</div>
          <div style={pb}>🔻 <strong style={{ color: T.text }}>STANDARD SHORT:</strong> Only on close below VWAP with RVOL ≥ 1.2x rejection bar</div>
          <div style={pb}>Setup plays (flags, bounces, etc.): Entry on confirmation candle with volume</div>
          <div style={{ ...sub }}>EXIT RULES</div>
          <div style={pb}>T1 hit → Take 50% off · T2 hit → Close remainder · Stop hit → Full exit, no averaging down</div>
          <div style={pb}>Score drops below +4 after entry → Reduce to half size</div>
          <div style={{ ...pb, marginBottom: 0 }}>EMA21 break on close → Exit signal · Score drops into CHOP → Exit signal</div>
        </Section>

        <Section k="watchlist" title="Section 8 — Reading the watchlist" subtitle="What each row and drawer tab shows" accent={T.purple}>
          <div style={pb}><strong style={{ color: T.text }}>Ticker row:</strong> Price + % change · Signal label (verdict + context) · Score bar (−15 to +15) · Key indicators: RSI, Confluence, CMF · 52W range position · Calls/Puts signal badges · Phase badge (👁 ⚡ 🚀) · Targets and stop price</div>
          <div style={{ ...pb, marginBottom: 0 }}><strong style={{ color: T.text }}>Click any ticker</strong> for full analysis: <strong style={{ color: T.text }}>Levels</strong> — support/resistance, targets · <strong style={{ color: T.text }}>Analysis</strong> — full 36-indicator breakdown, Price Story (plain English), phase progression · <strong style={{ color: T.text }}>Tradeflow</strong> — volume and momentum · <strong style={{ color: T.text }}>AI</strong> — Claude analysis · <strong style={{ color: T.text }}>Sizing</strong> — position calculator · <strong style={{ color: T.text }}>Options</strong> — calls/puts details</div>
        </Section>

        <Section k="tips" title="Section 9 — Tips for best results" subtitle="Process habits that improve outcomes" accent={T.yellow}>
          <ol style={{ margin: 0, paddingLeft: 22, color: T.textSub, fontSize: F.sm, lineHeight: 1.75 }}>
            <li style={{ marginBottom: 10 }}>Always check the Macro banner first — Risk Off = be more selective</li>
            <li style={{ marginBottom: 10 }}>Check Market Tide before scanning — Strong Bull Tide favors calls; Strong Bear Tide favors puts or cash</li>
            <li style={{ marginBottom: 10 }}>Focus on POWER PLAY and STANDARD LONG with Tide Confirmed — highest probability</li>
            <li style={{ marginBottom: 10 }}>Never fight a hard block — if CMF is negative, do not force calls</li>
            <li style={{ marginBottom: 10 }}>Phase badges matter — 👁 Early Watch today can become 🚀 Entry Ready in 7–14 days</li>
            <li style={{ marginBottom: 10 }}>Read the Price Story in Analysis — plain English setup explanation</li>
            <li style={{ marginBottom: 10 }}>Check earnings — avoid new calls within 7 days of earnings unless it is an Earnings Drift-style play</li>
            <li style={{ marginBottom: 0 }}>Size to verdict tier — POWER PLAY = full size; SPECULATIVE = quarter size; never full size when the system has not fully confirmed</li>
          </ol>
        </Section>
      </div>
    </div>
  );
}
// ─── SIGNAL CONFIDENCE SCORE ─────────────────────────────────────────────────
// Rates how strong a BUY NOW / STRONG SHORT is relative to the minimum threshold
// Returns: { pct: 0-100, label, color, detail }
function calcSignalConfidence(result, verdict) {
  if (!result || !verdict) return null;
  const layer = verdict.layer;
  if (layer !== "buy" && layer !== "bearish" && layer !== "recov" && layer !== "accum") return null;

  const isBull = layer === "buy" || layer === "recov" || layer === "accum";

  // ── Factor 1: Score strength (0-30 pts) ────────────────────────────────────
  const score = result.buyScore ?? 0;
  const scoreMax = isBull ? 12 : -12;
  const scorePts = isBull
    ? Math.min(30, Math.max(0, ((score - 4) / (scoreMax - 4)) * 30))
    : Math.min(30, Math.max(0, ((Math.abs(score) - 4) / (Math.abs(scoreMax) - 4)) * 30));

  // ── Factor 2: Gate quality (0-25 pts) ──────────────────────────────────────
  const gate = result.gate;
  const gateLabel = gate?.label;
  const gatePts = gateLabel === "GO" ? 25 : gateLabel === "CLOSE" ? 15 : gateLabel === "NEAR" ? 8 : 0;

  // ── Factor 3: CMF conviction (0-20 pts) ────────────────────────────────────
  const cmf = result.cmf ?? 0;
  const cmfPts = isBull
    ? cmf > 0.15 ? 20 : cmf > 0.08 ? 14 : cmf > 0.03 ? 8 : cmf > -0.02 ? 4 : 0
    : cmf < -0.15 ? 20 : cmf < -0.08 ? 14 : cmf < -0.03 ? 8 : 0;

  // ── Factor 4: Multi-timeframe alignment (0-15 pts) ─────────────────────────
  const weeklyAligned = result.weeklyTrend?.strongUptrend && isBull;
  const weeklyDown = result.weeklyTrend?.downtrend && !isBull;
  const goldenAligned = (result.goldenCross && isBull) || (result.deathCross && !isBull);
  const mtfPts = (weeklyAligned || weeklyDown ? 8 : 0) + (goldenAligned ? 3 : 0);
  const mtfCapped = Math.min(15, mtfPts);

  // ── Factor 5: Volume confirmation (0-10 pts) ───────────────────────────────
  const volConfirm = result.volBreakout?.volConfirmed;
  const rvolSurge = result.rvol?.surge;
  const obvConf = result.obv?.bullishDiv && isBull || result.obv?.bearishDiv && !isBull;
  const volPts = Math.min(10, (volConfirm ? 6 : 0) + (rvolSurge ? 3 : 0) + (obvConf ? 4 : 0));

  const total = Math.round(scorePts + gatePts + cmfPts + mtfCapped + volPts);
  const pct = Math.min(100, total);

  const label = pct >= 85 ? "EXCEPTIONAL" : pct >= 70 ? "HIGH" : pct >= 55 ? "SOLID" : pct >= 40 ? "MODERATE" : "MINIMUM";
  const color = pct >= 85 ? T.green : pct >= 70 ? "#86efac" : pct >= 55 ? T.cyan : pct >= 40 ? T.yellow : T.orange;

  const detail = [
    `Score: ${scorePts.toFixed(0)}/30`,
    `Gate: ${gatePts}/25`,
    `CMF: ${cmfPts}/20`,
    `Multi-TF: ${mtfCapped}/15`,
    `Volume: ${volPts}/10`,
  ];

  return { pct, label, color, detail, scorePts, gatePts, cmfPts, mtfPts: mtfCapped, volPts };
}

// ─── MARKET REGIME CLASSIFIER ────────────────────────────────────────────────
// Four plain-English regime states derived from VIX + watchlist data
function calcMarketRegime(vix, fearGreed, watchlist) {
  const vixVal = vix?.value ?? null;
  const vixRising = vix?.rising ?? false;
  const vixTrend = vix?.changePct ?? 0;
  const fgScore = fearGreed?.value ?? null;
  const scanned = watchlist?.filter(i => i.result) ?? [];
  const bullCount = scanned.filter(i => (i.result?.buyScore ?? 0) >= 4).length;
  const bearCount = scanned.filter(i => (i.result?.buyScore ?? 0) <= -4).length;
  const total = scanned.length || 1;
  const bullPct = bullCount / total;
  const bearPct = bearCount / total;
  const spyResult = watchlist?.find(i => i.symbol === "SPY")?.result;
  const spyBull = spyResult ? spyResult.goldenCross && (spyResult.cmf ?? 0) > 0 : null;

  if (!vixVal) return null;

  // ── SHOCK: VIX spiking hard ──────────────────────────────────────────────
  if (vixVal >= 35 || (vixVal >= 28 && vixRising && Math.abs(vixTrend) > 5)) {
    return {
      id: "shock", emoji: "⚫", label: "Market Shock",
      color: "#dc2626", bg: "#7f1d1d22",
      headline: "Extreme volatility — protect capital",
      summary: `VIX at ${vixVal?.toFixed(1)} with${vixRising ? " rising" : ""} fear. Markets are in shock territory. Gap-downs, false breakouts, and whipsaws are elevated. This is not a time to open new positions.`,
      actions: [
        "No new long or short positions — volatility makes signals unreliable",
        "If holding positions, review stops and consider reducing exposure",
        "Watch for VIX to peak and begin falling — that's when reversals become tradeable",
        bearPct > 0.5 ? `${Math.round(bearPct * 100)}% of your watchlist is bearish — broad market weakness confirmed` : null,
      ].filter(Boolean),
    };
  }

  // ── HIGH FEAR: Elevated VIX, distribution ────────────────────────────────
  if (vixVal >= 22 || (fgScore != null && fgScore <= 25)) {
    return {
      id: "fear", emoji: "🔴", label: "High Fear — Risk Off",
      color: T.red, bg: `${T.red}15`,
      headline: "Institutions distributing — avoid new longs",
      summary: `VIX ${vixVal?.toFixed(1)}${fgScore != null ? `, Fear & Greed ${fgScore}/100` : ""}. Institutional money is moving to the sidelines. Long signals have lower follow-through in this environment. Short setups are higher probability.`,
      actions: [
        "Approach long entries with extra scrutiny — require Gate GO + CMF positive as minimum",
        "Puts and shorts have a macro tailwind right now",
        bearPct > 0.4 ? `${Math.round(bearPct * 100)}% of your watchlist is in bearish territory` : null,
        "Position sizes should be 50-75% of normal until VIX drops below 20",
        "Watch for VIX to start falling — that's the early signal that fear is peaking",
      ].filter(Boolean),
    };
  }

  // ── CHOPPY: Low ADX, mixed signals ───────────────────────────────────────
  const choppy = bullPct < 0.25 && bearPct < 0.25 && vixVal < 22 && vixVal > 14;
  if (choppy) {
    return {
      id: "choppy", emoji: "🟡", label: "Choppy — No Clear Edge",
      color: T.yellow, bg: `${T.yellow}12`,
      headline: "Mixed signals — reduce size and be selective",
      summary: `VIX ${vixVal?.toFixed(1)}, market in a range. Neither bulls nor bears have conviction. Momentum signals are less reliable when ADX is low. This favors mean-reversion setups over trend-following.`,
      actions: [
        "Only take the highest-confidence setups — EXCEPTIONAL or HIGH confidence rating",
        "Prefer mean-reversion signals (WR RECOVERY, OVERSOLD RECOVERY) over breakout entries",
        "Reduce position sizes to 50-75% of normal",
        bullCount > 0 ? `${bullCount} of ${total} scanned tickers are bullish — selective long opportunities exist` : "Wait for more clarity before deploying capital",
      ].filter(Boolean),
    };
  }

  // ── TRENDING BULL: Clear uptrend ────────────────────────────────────────
  if ((bullPct > 0.4 || spyBull === true) && vixVal < 20) {
    return {
      id: "bull", emoji: "🟢", label: "Trending Bull",
      color: T.green, bg: `${T.green}10`,
      headline: "Conditions favor longs — act on confirmed signals",
      summary: `VIX ${vixVal?.toFixed(1)}${spyBull ? ", SPY in golden cross with positive money flow" : ""}. ${Math.round(bullPct * 100)}% of your scanned watchlist is in bullish territory. Long signals have higher follow-through in this environment.`,
      actions: [
        "BUY NOW signals with HIGH or EXCEPTIONAL confidence are actionable at full size",
        "Trend-following setups (golden cross + CMF positive + Gate GO) are highest probability",
        bullCount > 0 ? `${bullCount} tickers in your watchlist are showing bullish signals` : null,
        vixVal < 15 ? "Low VIX means options are cheaper — favorable for buying calls" : null,
        "Keep stops tight — trending markets can reverse quickly on macro events",
      ].filter(Boolean),
    };
  }

  // ── DEFAULT: Neutral ─────────────────────────────────────────────────────
  return {
    id: "neutral", emoji: "⚪", label: "Neutral",
    color: T.textSub, bg: T.surfaceHi,
    headline: "Mixed conditions — be selective",
    summary: `VIX ${vixVal?.toFixed(1)}. Market is neither clearly bullish nor bearish. Standard signal discipline applies.`,
    actions: [
      "Apply normal signal standards — Gate GO required for entries",
      "Favor setups where CMF confirms the signal direction",
      `${bullCount} bullish, ${bearCount} bearish in your scanned list`,
    ],
  };
}

// ─── POSITION HEALTH MONITOR ─────────────────────────────────────────────────
// For positions the user has entered — is the thesis still intact?
function calcPositionHealth(item) {
  const { result, position } = item;
  if (!result || !position?.buyPrice) return null;
  const bp = parseFloat(position.buyPrice);
  if (!bp || bp <= 0) return null;
  const current = result.price ?? bp;
  const pnlPct = ((current - bp) / bp) * 100;
  const atr = result.atr ?? (bp * 0.02);
  const stopLevel = bp - 2 * atr;
  const belowStop = current < stopLevel;

  // Score each factor
  let score = 0; let flags = []; let positives = [];

  // Price vs stop
  if (belowStop) {
    score -= 3; flags.push({ sev: "critical", msg: "Price broke 2×ATR stop — exit rule triggered" });
  } else if (current < bp * 0.95) {
    score -= 1; flags.push({ sev: "warn", msg: `Down ${Math.abs(pnlPct).toFixed(1)}% from entry — approaching stop` });
  } else if (pnlPct > 5) {
    score += 1; positives.push(`Up ${pnlPct.toFixed(1)}% — position working`);
  }

  // CMF — is institutional money still in?
  if (result.cmf != null) {
    if (result.cmf < -0.08) { score -= 2; flags.push({ sev: "warn", msg: `CMF ${result.cmf.toFixed(3)} — institutions distributing` }); }
    else if (result.cmf < -0.03) { score -= 1; flags.push({ sev: "info", msg: `CMF ${result.cmf.toFixed(3)} — money flow weakening` }); }
    else if (result.cmf > 0.05) { score += 1; positives.push(`CMF ${result.cmf.toFixed(3)} — institutions still accumulating`); }
  }

  // Trend structure
  if (result.deathCross) { score -= 2; flags.push({ sev: "warn", msg: "200 SMA structure bearish — macro trend now bearish" }); }
  else if (result.goldenCross) { score += 1; positives.push("Golden cross intact — macro structure bullish"); }

  // RSI — momentum
  if (result.rsi != null) {
    if (result.rsi > 75) { score -= 1; flags.push({ sev: "info", msg: `RSI ${result.rsi.toFixed(0)} — overbought, consider trimming` }); }
    else if (result.rsi > 55 && result.rsi <= 70) { score += 1; positives.push(`RSI ${result.rsi.toFixed(0)} — healthy momentum`); }
    else if (result.rsi < 40) { flags.push({ sev: "info", msg: `RSI ${result.rsi.toFixed(0)} — momentum fading, watch closely` }); }
  }

  // Exit signals
  const exitCount = result.sellCount ?? 0;
  if (exitCount >= 4) { score -= 2; flags.push({ sev: "critical", msg: `${exitCount}/5 exit signals active — strong trim/exit signal` }); }
  else if (exitCount >= 2) { score -= 1; flags.push({ sev: "warn", msg: `${exitCount}/5 exit signals active — monitor closely` }); }

  // Overall health
  const health = score >= 2 ? "green" : score >= 0 ? "yellow" : score >= -2 ? "orange" : "red";
  const healthColor = health === "green" ? T.green : health === "yellow" ? T.yellow : health === "orange" ? T.orange : T.red;
  const healthLabel = health === "green" ? "✓ Thesis Intact" : health === "yellow" ? "◆ Watch Closely" : health === "orange" ? "⚠ Weakening" : "✗ Exit Signal";
  const healthAction = health === "red" || belowStop
    ? "Stop hit or multiple exit signals active. Review and consider exiting."
    : exitCount >= 3
    ? `${exitCount} exit signals active. Trim 50% and raise your stop.`
    : health === "orange"
    ? "Thesis weakening. Tighten stop to 1×ATR. Do not add."
    : health === "yellow"
    ? "Some flags present. Hold with current stop. Check again tomorrow."
    : "Thesis intact. Hold position with your original stop.";

  return { health, healthColor, healthLabel, healthAction, flags, positives, pnlPct, score, belowStop, stopLevel };
}

// ─── WATCHLIST AI PANEL ───────────────────────────────────────────────────────
function WatchlistAIPanel({ watchlist, anthropicKey, cached, onResult, loading, setLoading, onClose, onScanAll, scanning }) {
  const scanned = watchlist.filter(i => i.result);
  const held    = watchlist.filter(i => i.result && parseFloat(i.position?.buyPrice) > 0);
  const now     = Date.now();
  const oldest  = scanned.length > 0 ? Math.min(...scanned.map(i => i.result.scanTime || 0)) : 0;
  const dataAgeMs = now - oldest;
  const dataFresh = oldest > 0 && dataAgeMs < 5 * 60 * 1000;
  const ageLabel  = oldest === 0 ? "not scanned" : dataAgeMs < 60000 ? "just now" : `${Math.floor(dataAgeMs / 60000)}m ago`;

  const [drawerW, setDrawerW] = React.useState(() => {
    try { return parseInt(localStorage.getItem("watchlistAIDrawerW") || "460"); } catch { return 460; }
  });
  const dragRef = React.useRef(false);
  const startDrag = (e) => {
    e.preventDefault(); dragRef.current = true;
    const startX = e.clientX; const startW = drawerW;
    const onMove = (e) => { if (!dragRef.current) return; setDrawerW(Math.min(900, Math.max(340, startW + (startX - e.clientX)))); };
    const onUp = () => { dragRef.current = false; setDrawerW(w => { try { localStorage.setItem("watchlistAIDrawerW", String(w)); } catch {} return w; }); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };

  const runRef = React.useRef(null);
  const runAfterScan = React.useRef(false);

  React.useEffect(() => {
    if (runAfterScan.current && !scanning) {
      runAfterScan.current = false;
      if (runRef.current) runRef.current();
    }
  }, [scanning]);

  const handleRunClick = () => {
    if (!dataFresh) { runAfterScan.current = true; onScanAll(); return; }
    if (runRef.current) runRef.current();
  };

  runRef.current = async () => {
    if (!scanned.length) return;
    setLoading(true);
    try {
      const spy = scanned.find(i => i.symbol === "SPY");
      const qqq = scanned.find(i => i.symbol === "QQQ");
      const spyCtx = spy ? `SPY: score ${spy.result.buyScore > 0 ? "+" : ""}${spy.result.buyScore}, RSI ${spy.result.rsi?.toFixed(1)}, CMF ${spy.result.cmf?.toFixed(3)}, gate ${spy.result.gate?.label}, MACD ${spy.result.macd?.bullish ? "BULL" : "BEAR"}${spy.result.macd?.crossover ? " (fresh cross)" : ""}` : "SPY not in watchlist";
      const qqqCtx = qqq ? `QQQ: score ${qqq.result.buyScore > 0 ? "+" : ""}${qqq.result.buyScore}, RSI ${qqq.result.rsi?.toFixed(1)}, CMF ${qqq.result.cmf?.toFixed(3)}` : null;
      const heldCtx = held.length > 0 ? `\nOPEN POSITIONS (${held.length}): ${held.map(i => `${i.symbol} @ $${i.position.buyPrice}`).join(", ")}` : "";
      const rows = scanned.filter(i => i.symbol !== "SPY" && i.symbol !== "QQQ").map(i => {
        const r = i.result; const v = calcVerdict({ result: r, aiCaution: false });
        const hasPosition = parseFloat(i.position?.buyPrice) > 0;
        return `${i.symbol}: score ${r.buyScore > 0 ? "+" : ""}${r.buyScore}, verdict ${v?.short || r.signal}, gate ${r.gate?.label}, RSI ${r.rsi?.toFixed(1)}, MACD ${r.macd?.bullish ? "BULL" : "BEAR"}${r.macd?.crossover ? "(CROSS)" : ""}, CMF ${r.cmf?.toFixed(3)}, RVOL ${r.rvol?.ratio?.toFixed(1) ?? "—"}x, 52W ${r.range52w?.score ?? "—"}/100, golden ${r.goldenCross ? "YES" : "NO"}${hasPosition ? `, HELD @ $${i.position.buyPrice}` : ""}`;
      }).join("\n");

      const prompt = `You are the senior analyst at Auctus Analytics. The user just scanned their full watchlist with live data. Be direct. No disclaimers. Trader to trader.

MARKET CONTEXT:
${spyCtx}${qqqCtx ? "\n" + qqqCtx : ""}${heldCtx}

WATCHLIST — ${scanned.filter(i => i.symbol !== "SPY" && i.symbol !== "QQQ").length} tickers (live scan):
${rows}

Respond ONLY with valid JSON — no markdown, no preamble:
{"regime":"2-3 sentence plain English market read and what it means for capital deployment","bestTrade":{"symbol":"TICKER","verdict":"BUY NOW or similar","why":"2-3 sentences citing score/gate/CMF","entry":"specific entry guidance","sizing":"sizing guidance"},"takeaways":[{"ticker":"SYMBOL","insight":"one punchy sentence"},{"ticker":"SYMBOL","insight":"one punchy sentence"},{"ticker":"SYMBOL","insight":"one punchy sentence"}],"positionMoves":[{"symbol":"TICKER","action":"HOLD/TRIM/EXIT/ADD","reason":"one sentence"}],"avoid":[{"symbol":"TICKER","reason":"one sentence"}],"deploy":"DEPLOY/WAIT/SELECTIVE — one sentence directive"}`;

      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          model: "claude-sonnet-4-20250514",
          max_tokens: 1200,
          ...(anthropicKey ? { userApiKey: anthropicKey } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      const responseText = data.content?.[0]?.text || "";
      const clean = responseText.replace(/```json|```/g, "").trim();
      let parsed = null;
      try { parsed = JSON.parse(clean); } catch {}
      onResult({ parsed, raw: responseText, date: new Date().toLocaleString() });
    } catch (e) { onResult({ parsed: null, raw: e.message, date: new Date().toLocaleString() }); }
    finally { setLoading(false); }
  };

  const p = cached?.parsed;
  const deployColor = p?.deploy?.startsWith("DEPLOY") ? T.green : p?.deploy?.startsWith("WAIT") ? T.red : T.yellow;

  return (
    <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: drawerW, background: T.surface, borderLeft: `1px solid ${T.border}`, zIndex: 200, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.5)" }}>
      <div onMouseDown={startDrag} style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 10, cursor: "col-resize", zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center" }} title="Drag to resize">
        <div style={{ width: 3, height: 40, borderRadius: 2, background: T.purple, opacity: 0.5, pointerEvents: "none" }} />
      </div>
      <div style={{ padding: "12px 14px 12px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <button onClick={onClose} style={{ padding: "5px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.text, borderRadius: 7, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>×</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: T.purple }}>✦ Watchlist Intelligence</div>
          <div style={{ fontSize: 10, color: T.textDim }}>{scanned.length} tickers · {dataFresh ? `fresh (${ageLabel})` : `${ageLabel} — stale`}{cached?.date ? ` · analyzed ${cached.date}` : ""}</div>
        </div>
        <button onClick={handleRunClick} disabled={loading || (scanning && runAfterScan.current) || !scanned.length}
          style={{ padding: "7px 14px", background: loading || scanning ? T.surfaceHi : `linear-gradient(135deg, #7c3aed, #d946ef)`, border: "none", color: loading || scanning ? T.textDim : "#fff", fontSize: 11, fontWeight: 800, fontFamily: "inherit", cursor: loading || scanning || !scanned.length ? "not-allowed" : "pointer", borderRadius: 8, whiteSpace: "nowrap", flexShrink: 0 }}>
          {loading ? "⏳ Analyzing…" : scanning && runAfterScan.current ? "⟳ Scanning…" : !dataFresh ? "↻ Scan & Analyze" : cached ? "↻ Re-run" : "✦ Analyze"}
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
        {!cached && !loading && !scanning && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, padding: 24, textAlign: "center" }}>
            <div style={{ fontSize: 32, color: T.purple }}>✦</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.purple }}>Watchlist Intelligence</div>
            <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.7, maxWidth: 300 }}>{dataFresh ? "Click Analyze to get a senior analyst breakdown — best trade, takeaways, position moves, and capital deployment guidance." : `Data is ${ageLabel}. Click Scan & Analyze to refresh prices first, then analysis runs automatically.`}</div>
          </div>
        )}
        {scanning && runAfterScan.current && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60%", gap: 10 }}>
            <div style={{ fontSize: 24, color: T.cyan }}>⟳</div>
            <div style={{ fontSize: 12, color: T.cyan }}>Scanning {scanned.length} tickers…</div>
            <div style={{ fontSize: 11, color: T.textDim }}>Analysis runs automatically when complete.</div>
          </div>
        )}
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60%", gap: 10 }}>
            <div style={{ fontSize: 24, color: T.purple }}>✦</div>
            <div style={{ fontSize: 12, color: T.purple }}>Analyzing {scanned.length} tickers…</div>
          </div>
        )}
        {cached && !p && !loading && (
          <div style={{ padding: 16 }}>
            <div style={{ padding: "10px 14px", background: `${T.red}10`, border: `1px solid ${T.red}44`, borderRadius: 8, fontSize: 12, color: T.red, marginBottom: 12 }}>Parse error — showing raw output.</div>
            <div style={{ fontSize: 12, color: T.text, lineHeight: 1.8, whiteSpace: "pre-wrap" }}>{cached.raw}</div>
          </div>
        )}
        {p && !loading && (
          <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ padding: "10px 14px", background: `${deployColor}12`, border: `1px solid ${deployColor}44`, borderRadius: 10, display: "flex", alignItems: "flex-start", gap: 10 }}>
              <div style={{ fontSize: 18, flexShrink: 0 }}>{p.deploy?.startsWith("DEPLOY") ? "🟢" : p.deploy?.startsWith("WAIT") ? "🔴" : "🟡"}</div>
              <div><div style={{ fontSize: 10, fontWeight: 800, color: deployColor, letterSpacing: "0.08em", marginBottom: 3 }}>CAPITAL DEPLOYMENT</div><div style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>{p.deploy}</div></div>
            </div>
            <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 8 }}>📊 MARKET REGIME</div>
              <div style={{ fontSize: 12, color: T.text, lineHeight: 1.75 }}>{p.regime}</div>
            </div>
            {p.bestTrade && (
              <div style={{ background: T.bg, border: `2px solid ${T.green}55`, borderRadius: 10, padding: 14, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, ${T.green}, ${T.cyan})` }} />
                <div style={{ fontSize: 10, fontWeight: 800, color: T.green, letterSpacing: "0.08em", marginBottom: 10 }}>🏆 BEST TRADE RIGHT NOW</div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: T.text, fontFamily: "'JetBrains Mono', monospace" }}>{p.bestTrade.symbol}</div>
                  <div style={{ padding: "3px 10px", background: `${T.green}22`, border: `1px solid ${T.green}55`, borderRadius: 6, fontSize: 11, fontWeight: 800, color: T.green }}>{p.bestTrade.verdict}</div>
                </div>
                <div style={{ fontSize: 12, color: T.text, lineHeight: 1.7, marginBottom: 10 }}>{p.bestTrade.why}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div style={{ background: T.surface, borderRadius: 8, padding: "8px 10px" }}><div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>ENTRY</div><div style={{ fontSize: 11, fontWeight: 700, color: T.cyan }}>{p.bestTrade.entry}</div></div>
                  <div style={{ background: T.surface, borderRadius: 8, padding: "8px 10px" }}><div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>SIZING</div><div style={{ fontSize: 11, fontWeight: 700, color: T.yellow }}>{p.bestTrade.sizing}</div></div>
                </div>
              </div>
            )}
            {p.takeaways?.length > 0 && (
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>💡 KEY TAKEAWAYS</div>
                {p.takeaways.map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: i < p.takeaways.length - 1 ? 8 : 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: T.purple, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, minWidth: 44 }}>{t.ticker}</div>
                    <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.6 }}>{t.insight}</div>
                  </div>
                ))}
              </div>
            )}
            {p.positionMoves?.length > 0 && (
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>📐 POSITION MOVES</div>
                {p.positionMoves.map((m, i) => {
                  const ac = m.action === "EXIT" ? T.red : m.action === "TRIM" ? T.orange : m.action === "ADD" ? T.green : T.cyan;
                  return (<div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", background: `${ac}0a`, border: `1px solid ${ac}33`, borderRadius: 8, marginBottom: i < p.positionMoves.length - 1 ? 8 : 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 900, color: T.text, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, minWidth: 44 }}>{m.symbol}</div>
                    <div style={{ flex: 1 }}><div style={{ fontSize: 10, fontWeight: 800, color: ac, marginBottom: 3 }}>{m.action}</div><div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.5 }}>{m.reason}</div></div>
                  </div>);
                })}
              </div>
            )}
            {p.avoid?.length > 0 && (
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.red, letterSpacing: "0.08em", marginBottom: 10 }}>🚫 AVOID</div>
                {p.avoid.map((a, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: i < p.avoid.length - 1 ? 8 : 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: T.red, fontFamily: "'JetBrains Mono', monospace", flexShrink: 0, minWidth: 44 }}>{a.symbol}</div>
                    <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.6 }}>{a.reason}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
// ─── REVERSAL WATCH ───────────────────────────────────────────────────────────
// Score display color — reflects BOTH score strength AND gate state
// High score in amber/gray = strong setup but entry NOT confirmed
// Green only when gate is GO or CLOSE
function scoreDisplayColor(buyScore, gateLabel) {
  const gateOpen  = gateLabel === "GO" || gateLabel === "CLOSE";
  const gateNear  = gateLabel === "NEAR";
  const blocked   = gateLabel === "BLOCKED" || gateLabel === "NOT YET";
  if (buyScore >= 9) {
    return gateOpen ? T.green : gateNear ? T.yellow : blocked ? "#888" : T.yellow;
  }
  if (buyScore >= 4) {
    return gateOpen ? "#86efac" : gateNear ? "#fbbf24" : "#888";
  }
  if (buyScore <= -9) return T.red;
  if (buyScore <= -5) return "#fca5a5";
  return T.textSub;
}

function calcReversalWatch(watchlist, vix, fearGreed) {
  const spy = watchlist.find(i => i.symbol === "SPY")?.result;
  const qqq = watchlist.find(i => i.symbol === "QQQ")?.result;
  const results = watchlist.filter(i => i.result);
  if (!results.length) return null;

  const vixElevated  = vix?.value >= 25;
  const vixFalling   = vix && !vix.rising && vix.changePct < -0.5;
  const vixRolling   = vixElevated && vixFalling;
  const extremeFear  = fearGreed?.value != null && fearGreed.value <= 25;
  const spyOversold  = spy?.rsi != null && spy.rsi < 32;
  const spyRsiHooked = spy?.rsi != null && spy.rsi > 28 && spy.rsi < 42;
  const spyCmfRecov  = spy?.cmf != null && spy.cmf > -0.1 && spy.cmf < 0.05;
  const spyCmfPos    = spy?.cmf != null && spy.cmf >= 0.05;
  const spyObvDiv    = spy?.obv?.bullishDiv === true;
  const accumSignal  = spyCmfRecov || spyCmfPos || spyObvDiv;

  // QQQ context — tech proxy adds conviction when aligned with SPY
  const qqqOversold  = qqq?.rsi != null && qqq.rsi < 35;
  const qqqRecov     = qqq?.rsi != null && qqq.rsi > 30 && qqq.rsi < 48;
  const qqqCmfPos    = qqq?.cmf != null && qqq.cmf > 0;
  const qqqAligned   = qqqOversold || (qqqRecov && qqqCmfPos);

  // Leader detection — qualify recovery candidates
  const leaders = results.filter(i => {
    const r = i.result;
    if (i.symbol === "SPY" || i.symbol === "QQQ") return false;
    const cmfPos    = r.cmf != null && r.cmf > 0.05;
    const rsiRecov  = r.rsi != null && r.rsi > 35 && r.rsi < 55;
    const aboveEma  = r.ema21 != null && r.price > r.ema21;
    const rspyStr   = r.rs?.outperforming === true;
    const scoreRecov= r.buyScore >= 2;
    const wrRecov   = r.williamsR != null && r.williamsR > -80 && r.williamsR < -40; // WR mean reversion zone
    const calmarOk  = r.calmar2y == null || r.calmar2y >= 1; // soft filter — prefer quality names
    const rrOk      = r.rr1 != null && r.rr1 >= 1.5;
    const extras = [rrOk, aboveEma, rspyStr, scoreRecov, wrRecov].filter(Boolean).length;
    return cmfPos && rsiRecov && extras >= 1 && calmarOk;
  });

  const squeezeTickers = leaders.filter(i => i.result.bb?.isSqueeze);
  const gateGoLeaders  = leaders.filter(i => i.result.gate?.label === "GO");
  const rrLeaders      = leaders.filter(i => i.result.rr1 != null && i.result.rr1 >= 1.5);
  const wrLeaders      = leaders.filter(i => i.result.williamsR != null && i.result.williamsR > -80 && i.result.williamsR < -40);

  // Score signal count for subtitle display
  let signalCount = 0;
  if (vixRolling) signalCount++;
  if (vixElevated) signalCount++;
  if (spyOversold) signalCount++;
  if (spyRsiHooked) signalCount++;
  if (accumSignal) signalCount++;
  if (extremeFear) signalCount++;
  if (qqqAligned) signalCount++;
  if (gateGoLeaders.length >= 1) signalCount++;
  if (leaders.length >= 2) signalCount++;

  let level = null, reasons = [], actions = [], callsCandidates = [];

  // CALLS READY — full confluence
  if (vixRolling && accumSignal && gateGoLeaders.length >= 2 && leaders.length >= 2) {
    level = "CALLS READY";
    reasons = [
      vixFalling ? `VIX rolling over — ${vix.value.toFixed(1)}, down ${Math.abs(vix.changePct).toFixed(1)}% today` : null,
      spyCmfPos ? `SPY CMF positive (${spy.cmf.toFixed(3)}) — institutions accumulating` : spyCmfRecov ? `SPY CMF recovering (${spy.cmf.toFixed(3)}) — turning positive` : null,
      spyRsiHooked ? `SPY RSI recovering from oversold (${spy.rsi.toFixed(0)}) — hook forming` : null,
      gateGoLeaders.length > 0 ? `${gateGoLeaders.length} ticker${gateGoLeaders.length > 1 ? "s" : ""} with Gate GO: ${gateGoLeaders.map(i => i.symbol).join(", ")}` : null,
      extremeFear ? `Fear & Greed at ${fearGreed.value} — Extreme Fear = contrarian buy zone` : null,
      qqqAligned ? `QQQ ${qqqOversold ? `oversold (RSI ${qqq.rsi.toFixed(0)})` : `recovering (RSI ${qqq.rsi.toFixed(0)}, CMF ${qqq.cmf.toFixed(3)})`} — tech proxy aligned` : null,
      wrLeaders.length > 0 ? `Williams %R mean reversion loading on: ${wrLeaders.map(i => i.symbol).join(", ")}` : null,
    ].filter(Boolean);
    actions = ["Start scaling into calls on leaders showing Gate GO", "Prioritize: positive CMF + R:R ≥ 1.5 + above EMA21", "Use 30-45 DTE options — give the trade room to develop", "Size 50% of normal — wait for SPY to confirm before adding"];
    callsCandidates = gateGoLeaders.length > 0 ? gateGoLeaders : rrLeaders;

  // WATCH — partial confluence, or SPY hooked + leaders present
  } else if ((vixRolling || spyOversold || spyRsiHooked) && (accumSignal || leaders.length >= 1)) {
    level = "WATCH";
    reasons = [
      vixRolling ? `VIX elevated and falling (${vix?.value.toFixed(1)}) — potential rollover` : vixElevated ? `VIX elevated at ${vix?.value.toFixed(1)} — not yet falling` : null,
      spyOversold ? `SPY RSI oversold at ${spy?.rsi.toFixed(0)} — exhaustion zone` : spyRsiHooked ? `SPY RSI hooking up (${spy?.rsi.toFixed(0)}) — momentum shifting` : null,
      spyCmfRecov ? `SPY CMF turning (${spy?.cmf.toFixed(3)}) — watching for positive cross` : null,
      leaders.length > 0 ? `${leaders.length} ticker${leaders.length > 1 ? "s" : ""} showing early recovery: ${leaders.slice(0, 4).map(i => i.symbol).join(", ")}` : null,
      squeezeTickers.length > 0 ? `BB Squeeze loading on: ${squeezeTickers.map(i => i.symbol).join(", ")}` : null,
      qqqAligned ? `QQQ ${qqqOversold ? `oversold (RSI ${qqq.rsi.toFixed(0)})` : "recovering"} — tech proxy confirming` : null,
      wrLeaders.length > 0 ? `WR mean reversion on: ${wrLeaders.map(i => i.symbol).join(", ")}` : null,
    ].filter(Boolean);
    actions = ["Do NOT buy calls yet — wait for Gate GO on 2+ tickers", "Watch for VIX to close below its 5-day high", "Entry trigger: SPY CMF crosses above 0 on a daily close", "Leaders to watch: " + (leaders.slice(0, 3).map(i => i.symbol).join(", ") || "scan your list")];
    callsCandidates = leaders;

  // MONITOR — early warning, not actionable yet
  } else if (vixElevated || spyOversold || extremeFear) {
    level = "MONITOR";
    reasons = [
      vixElevated ? `VIX at ${vix?.value.toFixed(1)} — elevated but not yet rolling over` : null,
      spyOversold ? `SPY RSI oversold at ${spy?.rsi?.toFixed(0)} — selloff may continue` : null,
      extremeFear ? `Fear & Greed at ${fearGreed?.value} — Extreme Fear` : null,
      "No institutional accumulation signal yet",
    ].filter(Boolean);
    actions = ["Stay in cash or puts", "Watch VIX direction daily — falling VIX changes everything", "RSI needs to stop making new lows before calls are worth considering"];
    callsCandidates = [];
  }

  if (!level) return null;
  return { level, score: signalCount, reasons, actions, callsCandidates, leaders, squeezeTickers, spy, qqq, vix, checkedAt: new Date().toLocaleTimeString() };
}

function ReversalWatchPanel({ watchlist, vix, fearGreed, onClose }) {
  const reversal = calcReversalWatch(watchlist, vix, fearGreed);
  const scanned = watchlist.filter(i => i.result);
  const levelColor = reversal?.level === "CALLS READY" ? T.green : reversal?.level === "WATCH" ? T.yellow : reversal?.level === "MONITOR" ? T.orange : T.textDim;

  const [drawerW, setDrawerW] = React.useState(() => { try { return parseInt(localStorage.getItem("reversalDrawerW") || "460"); } catch { return 460; } });
  const dragRef = React.useRef(false);
  const startDrag = (e) => {
    e.preventDefault(); dragRef.current = true;
    const startX = e.clientX; const startW = drawerW;
    const onMove = (e) => { if (!dragRef.current) return; setDrawerW(Math.min(900, Math.max(320, startW + (startX - e.clientX)))); };
    const onUp = () => { dragRef.current = false; setDrawerW(w => { try { localStorage.setItem("reversalDrawerW", String(w)); } catch {} return w; }); window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove); window.addEventListener("mouseup", onUp);
  };

  return (
    <div style={{ position: "fixed", right: 0, top: 0, bottom: 0, width: drawerW, background: T.surface, borderLeft: `1px solid ${T.border}`, zIndex: 200, display: "flex", flexDirection: "column", boxShadow: "-8px 0 32px rgba(0,0,0,0.5)" }}>
      {/* Drag handle */}
      <div onMouseDown={startDrag} style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 10, cursor: "col-resize", zIndex: 10, display: "flex", alignItems: "center", justifyContent: "center" }} title="Drag to resize">
        <div style={{ width: 3, height: 40, borderRadius: 2, background: levelColor, opacity: 0.5, pointerEvents: "none" }} />
      </div>

      {/* Header */}
      <div style={{ padding: "12px 14px 12px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        <button onClick={onClose} style={{ padding: "5px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.text, borderRadius: 7, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>×</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 900, color: levelColor }}>📈 Reversal Watch</div>
          <div style={{ fontSize: 10, color: T.textDim }}>{scanned.length} tickers scanned{reversal ? ` · ${reversal.score} signal${reversal.score !== 1 ? "s" : ""} detected` : " · no alerts"}{reversal?.checkedAt ? ` · ${reversal.checkedAt}` : ""}</div>
        </div>
        {reversal && (
          <div style={{ padding: "3px 10px", background: `${levelColor}22`, border: `1px solid ${levelColor}55`, borderRadius: 6, fontSize: 11, fontWeight: 800, color: levelColor, flexShrink: 0 }}>{reversal.level}</div>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "16px 16px" }}>
        {!reversal ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, textAlign: "center", color: T.textDim }}>
            <div style={{ fontSize: 32 }}>📈</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>No Reversal Signals</div>
            <div style={{ fontSize: 12, lineHeight: 1.7, maxWidth: 300 }}>
              {scanned.length === 0 ? "Scan your watchlist first — include SPY for market context." : "VIX normal, market not in alert zone. No reversal conditions met."}
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Signals detected */}
            <div style={{ background: T.bg, border: `1px solid ${levelColor}33`, borderRadius: 10, padding: 14, borderLeft: `3px solid ${levelColor}` }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: levelColor, letterSpacing: "0.08em", marginBottom: 10 }}>SIGNALS DETECTED</div>
              {reversal.reasons.map((r, i) => (
                <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: levelColor, marginTop: 5, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: T.text, lineHeight: 1.65 }}>{r}</span>
                </div>
              ))}
            </div>

            {/* Action plan */}
            <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>
                {reversal.level === "CALLS READY" ? "✓ ACTION PLAN" : "→ WAIT FOR"}
              </div>
              {reversal.actions.map((a, i) => (
                <div key={i} style={{ fontSize: 12, color: reversal.level === "CALLS READY" ? T.green : T.textSub, marginBottom: 5, lineHeight: 1.65 }}>
                  {reversal.level === "CALLS READY" ? `✓ ${a}` : `→ ${a}`}
                </div>
              ))}
            </div>

            {(reversal.spy || reversal.qqq) && (
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>MARKET CONTEXT</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                  {reversal.spy && (<>
                    <div style={{ background: T.surface, borderRadius: 8, padding: "9px 10px" }}>
                      <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>SPY RSI</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: reversal.spy.rsi < 32 ? T.green : T.text, fontFamily: "'JetBrains Mono', monospace" }}>{reversal.spy.rsi?.toFixed(0)}</div>
                    </div>
                    <div style={{ background: T.surface, borderRadius: 8, padding: "9px 10px" }}>
                      <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>SPY CMF</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: reversal.spy.cmf > 0 ? T.green : reversal.spy.cmf > -0.05 ? T.yellow : T.red, fontFamily: "'JetBrains Mono', monospace" }}>{reversal.spy.cmf?.toFixed(3)}</div>
                    </div>
                  </>)}
                  {reversal.vix && (
                    <div style={{ background: T.surface, borderRadius: 8, padding: "9px 10px" }}>
                      <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>VIX</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: reversal.vix.value >= 30 ? T.red : T.orange, fontFamily: "'JetBrains Mono', monospace" }}>
                        {reversal.vix.value?.toFixed(1)} <span style={{ fontSize: 10, color: !reversal.vix.rising ? T.green : T.red }}>{!reversal.vix.rising ? "▼" : "▲"}</span>
                      </div>
                    </div>
                  )}
                  {reversal.qqq && (<>
                    <div style={{ background: T.surface, borderRadius: 8, padding: "9px 10px" }}>
                      <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>QQQ RSI</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: reversal.qqq.rsi < 35 ? T.green : T.text, fontFamily: "'JetBrains Mono', monospace" }}>{reversal.qqq.rsi?.toFixed(0)}</div>
                    </div>
                    <div style={{ background: T.surface, borderRadius: 8, padding: "9px 10px" }}>
                      <div style={{ fontSize: 10, color: T.textDim, marginBottom: 3 }}>QQQ CMF</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: reversal.qqq.cmf > 0 ? T.green : reversal.qqq.cmf > -0.05 ? T.yellow : T.red, fontFamily: "'JetBrains Mono', monospace" }}>{reversal.qqq.cmf?.toFixed(3)}</div>
                    </div>
                  </>)}
                </div>
              </div>
            )}

            {/* Calls candidates */}
            {reversal.callsCandidates.length > 0 && (
              <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: reversal.level === "CALLS READY" ? T.green : T.textDim, letterSpacing: "0.08em", marginBottom: 10 }}>
                  {reversal.level === "CALLS READY" ? "⚡ CALLS CANDIDATES" : "RECOVERY LEADERS TO WATCH"}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {reversal.callsCandidates.map(item => {
                    const r = item.result;
                    const gateGo = r.gate?.label === "GO";
                    const hasSqueeze = r.bb?.isSqueeze;
                    const edgeColor = gateGo ? T.green : hasSqueeze ? T.yellow : T.border;
                    return (
                      <div key={item.symbol} style={{ padding: "10px 12px", background: T.surface, borderRadius: 9, border: `1px solid ${edgeColor}55`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 800, color: gateGo ? T.green : T.text, fontFamily: "'JetBrains Mono', monospace" }}>{item.symbol}</div>
                          <div style={{ fontSize: 10, color: T.textDim, marginTop: 3 }}>
                            RSI {r.rsi?.toFixed(0)} · CMF {r.cmf?.toFixed(2)}
                            {hasSqueeze ? " · ⚡ SQZ" : ""}
                            {r.macd?.crossover ? " · MACD ▲" : ""}
                          </div>
                        </div>
                        {gateGo && <div style={{ fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 5, background: `${T.green}22`, color: T.green, border: `1px solid ${T.green}44` }}>GO</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
}

// ─── SECTOR ROTATION BANNER ───────────────────────────────────────────────────
function calcSectorRotation() {
  const etfs = Object.keys(_sectorETFData);
  if (etfs.length < 3) return null;
  const ranked = etfs
    .map(etf => {
      const d = _sectorETFData[etf];
      if (!d?.price || !d?.prevClose) return null;
      const pctChange = ((d.price - d.prevClose) / d.prevClose) * 100;
      const names = { XLK:"Tech", XLY:"Cons.Disc", XLF:"Financials", XLE:"Energy", XLV:"Healthcare", XLI:"Industrials", XLU:"Utilities", XLP:"Cons.Staples", XLC:"Comm.Svcs", XLB:"Materials", XLRE:"Real Estate", IGV:"Software", SMH:"Semiconductors" };
      return { etf, pctChange, name: names[etf] || etf };
    })
    .filter(Boolean)
    .sort((a, b) => b.pctChange - a.pctChange);
  if (ranked.length < 2) return null;
  const leading = ranked.slice(0, 2);
  const lagging = ranked.slice(-2).reverse();
  return { leading, lagging, all: ranked };
}

function SectorRotationBanner({ watchlist }) {
  const rotation = calcSectorRotation();
  if (!rotation) return null;
  // Only show if there's meaningful spread between leaders and laggards
  const spread = (rotation.leading[0]?.pctChange ?? 0) - (rotation.lagging[0]?.pctChange ?? 0);
  if (spread < 0.5) return null;
  // Check if user has concentrated exposure in leading/lagging sectors
  const watchedEtfs = watchlist.filter(i => i.result).map(i => TICKER_SECTOR_MAP[i.symbol]).filter(Boolean);
  const leadingExposure = rotation.leading.filter(l => watchedEtfs.includes(l.etf));
  const laggingExposure = rotation.lagging.filter(l => watchedEtfs.includes(l.etf));
  return (
    <div style={{ margin: "0 0 0 0", padding: "6px 20px", background: T.bg, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", flexShrink: 0 }}>
      <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.08em", flexShrink: 0 }}>SECTOR ROTATION</div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ fontSize: 10, color: T.textDim }}>Leading:</span>
        {rotation.leading.map(s => (
          <span key={s.etf} style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: `${T.green}18`, border: `1px solid ${T.green}44`, color: T.green }}>
            {s.name || s.etf} <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.pctChange >= 0 ? "+" : ""}{s.pctChange.toFixed(2)}%</span>
          </span>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ fontSize: 10, color: T.textDim }}>Lagging:</span>
        {rotation.lagging.map(s => (
          <span key={s.etf} style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 5, background: `${T.red}18`, border: `1px solid ${T.red}44`, color: T.red }}>
            {s.name || s.etf} <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{s.pctChange >= 0 ? "+" : ""}{s.pctChange.toFixed(2)}%</span>
          </span>
        ))}
      </div>
      {leadingExposure.length > 0 && (
        <span style={{ fontSize: 10, color: T.green, marginLeft: "auto" }}>✓ You hold {leadingExposure.map(l => l.name || l.etf).join(", ")} names</span>
      )}
      {laggingExposure.length > 0 && !leadingExposure.length && (
        <span style={{ fontSize: 10, color: T.orange, marginLeft: "auto" }}>⚠ Exposure in lagging sector{laggingExposure.length > 1 ? "s" : ""}: {laggingExposure.map(l => l.name || l.etf).join(", ")}</span>
      )}
    </div>
  );
}

let _sectorDashboardCache = { ts: 0, byEtf: {}, spyData: null };

function SectorSparkline({ closes, color = T.cyan }) {
  const pts = (closes || []).filter(v => typeof v === "number" && Number.isFinite(v));
  if (pts.length < 2) return <span style={{ color: T.textDim, fontSize: 10 }}>—</span>;
  const w = 72, h = 28, pad = 2;
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const range = max - min || 1;
  const yFor = (v) => pad + (h - pad * 2) * (1 - (v - min) / range);
  const xFor = (i) => pad + (i / (pts.length - 1)) * (w - pad * 2);
  const d = pts.map((v, i) => `${xFor(i).toFixed(1)},${yFor(v).toFixed(1)}`).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline points={d} stroke={color} strokeWidth={1.5} fill="none" strokeLinecap="round" />
    </svg>
  );
}

function SectorDashboard({
  sectorDashboard,
  rotationAlerts,
  alertBanner,
  loading,
  onRefresh,
  watchlist,
  livePrice,
  onAddToWatchlist,
  onScanAllSector,
  scanning,
}) {
  const [selectedEtf, setSelectedEtf] = React.useState(null);
  const [playQuotes, setPlayQuotes] = React.useState({});
  const [sectorScanning, setSectorScanning] = React.useState(false);

  const sectors = React.useMemo(() => {
    const list = Object.values(sectorDashboard?.byEtf || {}).filter(Boolean);
    return sortSectorsByStage(list);
  }, [sectorDashboard]);

  const watchSet = React.useMemo(() => new Set(watchlist.map(i => i.symbol)), [watchlist]);
  const watchBySym = React.useMemo(() => {
    const m = {};
    watchlist.forEach(i => { if (i.result) m[i.symbol] = i.result; });
    return m;
  }, [watchlist]);

  React.useEffect(() => {
    if (!selectedEtf) return;
    const tickers = SECTOR_TICKER_PLAYS[selectedEtf] || [];
    let cancelled = false;

    const initial = {};
    tickers.forEach(sym => {
      const wr = watchBySym[sym];
      if (watchSet.has(sym) && wr) {
        initial[sym] = {
          loading: false,
          price: wr.price ?? null,
          pctChange: wr.pctChange ?? null,
          score: wr.buyScore ?? null,
          cmf: wr.cmf ?? null,
        };
      } else {
        initial[sym] = { loading: true, price: null, pctChange: null, score: null, cmf: null };
      }
    });
    setPlayQuotes(initial);

    const toFetch = tickers.filter(sym => !(watchSet.has(sym) && watchBySym[sym]));
    if (!toFetch.length) return () => { cancelled = true; };

    (async () => {
      await Promise.all(toFetch.map(async (ticker) => {
        try {
          const res = await fetch("/api/quote?symbol=" + encodeURIComponent(ticker));
          const result = await res.json().catch(() => ({}));
          console.log("[sector quote]", ticker, result);
          if (cancelled) return;
          setPlayQuotes(prev => ({
            ...prev,
            [ticker]: {
              loading: false,
              price: result?.price ?? null,
              pctChange: result?.changePct ?? result?.change_percentage ?? null,
              score: watchBySym[ticker]?.buyScore ?? null,
              cmf: result?.cmf ?? watchBySym[ticker]?.cmf ?? null,
            },
          }));
        } catch (e) {
          console.log("[sector quote]", ticker, { error: e?.message || e });
          if (!cancelled) {
            setPlayQuotes(prev => ({
              ...prev,
              [ticker]: { ...(prev[ticker] || {}), loading: false },
            }));
          }
        }
      }));
    })();

    return () => { cancelled = true; };
  }, [selectedEtf, watchBySym, watchSet]);

  const PlaySpinner = () => (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.textDim, fontSize: 11 }}>
      <div style={{ width: 12, height: 12, borderRadius: "50%", border: `2px solid ${T.border}`, borderTopColor: T.cyan, animation: "spin 0.8s linear infinite" }} />
      Loading…
    </div>
  );

  const selected = selectedEtf ? sectorDashboard?.byEtf?.[selectedEtf] : null;
  const mono = { fontFamily: "'JetBrains Mono', monospace" };

  return (
    <div style={{ flex: 1, overflow: "auto", padding: 16, display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 900, color: T.text }}>Sector Rotation Dashboard</div>
          <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>
            9 sector ETFs · refreshed on every watchlist scan · 15 min cache
          </div>
        </div>
        <button onClick={onRefresh} disabled={loading}
          style={{ padding: "8px 14px", background: `${T.cyan}18`, border: `1px solid ${T.cyan}44`, color: T.cyan, borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: loading ? "default" : "pointer", fontFamily: "inherit", opacity: loading ? 0.6 : 1 }}>
          {loading ? "Scanning sectors…" : "↻ Refresh Sectors"}
        </button>
      </div>

      {alertBanner && (
        <div style={{ padding: "12px 14px", borderRadius: 10, background: `${T.yellow}12`, border: `1px solid ${T.yellow}55`, color: T.text }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: T.yellow, marginBottom: 4 }}>Rotation Alert</div>
          <div style={{ fontSize: 11, color: T.textSub, lineHeight: 1.6, whiteSpace: "pre-line" }}>{alertBanner}</div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {sectors.map(s => (
          <div key={s.etf} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, color: T.text }}>{s.name}</div>
                <div style={{ fontSize: 11, color: T.textDim, ...mono }}>{s.etf}</div>
              </div>
              <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 999, background: `${s.stageColor}18`, border: `1px solid ${s.stageColor}55`, color: s.stageColor, whiteSpace: "nowrap" }}>
                {s.stageLabel}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: T.text, ...mono }}>${s.price?.toFixed(2) ?? "—"}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: (s.pctChange1d ?? 0) >= 0 ? T.green : T.red, ...mono }}>
                  {(s.pctChange1d ?? 0) >= 0 ? "+" : ""}{(s.pctChange1d ?? 0).toFixed(2)}% 1D
                </div>
              </div>
              <SectorSparkline closes={s.sparkCloses} color={s.stageColor} />
            </div>
            <div style={{ display: "flex", gap: 12, fontSize: 11 }}>
              <span style={{ color: (s.perf5vsSpy ?? 0) >= 0 ? T.green : T.red, fontWeight: 700, ...mono }}>
                5D vs SPY {(s.perf5vsSpy ?? 0) >= 0 ? "+" : ""}{(s.perf5vsSpy ?? 0).toFixed(2)}%
              </span>
              <span style={{ color: T.textSub, ...mono }}>
                CMF {s.cmf?.toFixed(3) ?? "—"} {s.cmfRising ? "↑" : s.cmfDeclining ? "↓" : ""}
              </span>
            </div>
            <button onClick={() => setSelectedEtf(s.etf)}
              style={{ marginTop: 4, padding: "7px 10px", background: `${T.purple}14`, border: `1px solid ${T.purple}44`, color: T.purple, borderRadius: 8, fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
              View Plays →
            </button>
          </div>
        ))}
        {!sectors.length && !loading && (
          <div style={{ gridColumn: "1 / -1", padding: 24, textAlign: "center", color: T.textSub, fontSize: 13 }}>
            No sector data yet. Run a watchlist scan to populate sector ETF metrics.
          </div>
        )}
      </div>

      {selected && (
        <div style={{ background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 10, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 900, color: T.text }}>SECTOR: {selected.name} ({selected.etf})</div>
              <div style={{ fontSize: 12, color: selected.stageColor, fontWeight: 800, marginTop: 4 }}>
                {selected.stageLabel} — {selected.stageKey === STAGE_KEYS.ACCUMULATION ? "Early Stage" : selected.stageKey === STAGE_KEYS.ROTATING ? "Building" : selected.stageKey === STAGE_KEYS.HOT ? "Confirmed" : "Status"}
              </div>
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 4 }}>
                Detected: {selected.stageDays ?? 0} day{(selected.stageDays ?? 0) !== 1 ? "s" : ""} ago
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <button
                disabled={scanning || sectorScanning}
                onClick={async () => {
                  const tickers = SECTOR_TICKER_PLAYS[selected.etf] || [];
                  setSectorScanning(true);
                  try {
                    if (onScanAllSector) await onScanAllSector(tickers, selected.name);
                  } finally {
                    setSectorScanning(false);
                  }
                }}
                style={{
                  padding: "7px 12px",
                  background: `${T.green}18`,
                  border: `1px solid ${T.green}55`,
                  color: T.green,
                  borderRadius: 8,
                  fontSize: 11,
                  fontWeight: 800,
                  cursor: scanning || sectorScanning ? "default" : "pointer",
                  fontFamily: "inherit",
                  opacity: scanning || sectorScanning ? 0.6 : 1,
                  whiteSpace: "nowrap",
                }}
              >
                {scanning || sectorScanning ? "Scanning…" : `Scan All ${selected.name} Tickers`}
              </button>
              <button onClick={() => setSelectedEtf(null)} style={{ background: "none", border: "none", color: T.textSub, fontSize: 18, cursor: "pointer" }}>✕</button>
            </div>
          </div>

          <div style={{ fontSize: 11, fontWeight: 800, color: T.textDim, letterSpacing: "0.06em", marginBottom: 8 }}>RECOMMENDED TICKERS TO WATCH</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 8, marginBottom: 14 }}>
            {(SECTOR_TICKER_PLAYS[selected.etf] || []).map(sym => {
              const q = playQuotes[sym] || {};
              const wr = watchBySym[sym];
              const inWl = watchSet.has(sym);
              const isLoading = q.loading === true;
              const score = (inWl && wr) ? wr.buyScore : q.score;
              const cmf = (inWl && wr) ? wr.cmf : q.cmf;
              const price = (inWl && wr) ? wr.price : q.price;
              const pctChange = (inWl && wr) ? wr.pctChange : q.pctChange;
              return (
                <div key={sym} style={{
                  padding: 10,
                  background: T.surface,
                  border: inWl ? `2px solid ${T.green}` : `1px solid ${T.border}`,
                  borderRadius: 8,
                  boxShadow: inWl ? `0 0 0 1px ${T.green}22` : "none",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontWeight: 900, ...mono, color: T.text }}>{sym}</span>
                    {inWl && <span style={{ fontSize: 9, color: T.green, fontWeight: 700 }}>IN WL</span>}
                  </div>
                  {isLoading ? (
                    <div style={{ marginTop: 8 }}><PlaySpinner /></div>
                  ) : (
                    <>
                      <div style={{ fontSize: 11, color: T.textSub, marginTop: 4, ...mono }}>
                        {price != null ? `$${price.toFixed(2)}` : "—"}
                        {pctChange != null && (
                          <span style={{ color: pctChange >= 0 ? T.green : T.red, marginLeft: 6 }}>
                            {pctChange >= 0 ? "+" : ""}{pctChange.toFixed(2)}%
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 10, color: T.textDim, marginTop: 3 }}>
                        Score: {score != null ? `${score > 0 ? "+" : ""}${score}` : "—"} · CMF: {cmf != null ? cmf.toFixed(3) : "—"}
                      </div>
                    </>
                  )}
                  <button
                    disabled={inWl}
                    onClick={() => onAddToWatchlist(sym)}
                    style={{ marginTop: 8, width: "100%", padding: "5px 8px", background: inWl ? T.surfaceHi : `${T.green}14`, border: `1px solid ${inWl ? T.border : T.green + "44"}`, color: inWl ? T.textDim : T.green, borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: inWl ? "default" : "pointer", fontFamily: "inherit" }}>
                    {inWl ? "On Watchlist" : "+ Add to Watchlist"}
                  </button>
                </div>
              );
            })}
          </div>

          <div style={{ padding: 12, background: T.bg, borderRadius: 8, borderLeft: `3px solid ${selected.stageColor}` }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.textDim, letterSpacing: "0.06em", marginBottom: 6 }}>RECOMMENDED TRADE</div>
            <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7, whiteSpace: "pre-line" }}>
              {buildSectorTradeRecommendation(selected)}
            </div>
          </div>
        </div>
      )}

      {rotationAlerts?.length > 0 && (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: T.text, marginBottom: 10 }}>Rotation Alert History</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rotationAlerts.slice(0, 10).map(a => (
              <div key={a.id} style={{ padding: 10, background: T.surfaceHi, borderRadius: 8, fontSize: 11, color: T.textSub, lineHeight: 1.5 }}>
                <div style={{ fontWeight: 800, color: T.text }}>{a.sector} ({a.etf})</div>
                <div>{STAGE_DISPLAY[a.from_stage]?.label || a.from_stage} → {STAGE_DISPLAY[a.to_stage]?.label || a.to_stage}</div>
                <div style={{ color: T.textDim, marginTop: 2 }}>
                  {new Date(a.date).toLocaleString()} · CMF {a.cmf?.toFixed(3)} · 5D vs SPY {(a.performance_vs_spy_5d ?? 0) >= 0 ? "+" : ""}{(a.performance_vs_spy_5d ?? 0).toFixed(2)}%
                </div>
                <div style={{ marginTop: 2 }}>Tickers: {(a.suggested_tickers || []).join(", ")}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── REGIME BANNER COMPONENT ─────────────────────────────────────────────────
function RegimeBanner({ vix, fearGreed, watchlist }) {
  const [expanded, setExpanded] = useState(false);
  const regime = calcMarketRegime(vix, fearGreed, watchlist);
  if (!regime) return null;
  return (
    <div style={{ margin: "0 16px 8px", borderRadius: 10, border: `1px solid ${regime.color}44`, background: regime.bg, overflow: "hidden" }}>
      <div onClick={() => setExpanded(v => !v)}
        style={{ padding: "8px 14px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
        <span style={{ fontSize: 16 }}>{regime.emoji}</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 11, fontWeight: 800, color: regime.color, marginRight: 8 }}>{regime.label}</span>
          <span style={{ fontSize: 11, color: T.textSub }}>{regime.headline}</span>
        </div>
        {vix?.value != null && <span style={{ fontSize: 10, color: T.textDim }}>VIX {vix.value.toFixed(1)}</span>}
        <span style={{ fontSize: 10, color: T.textDim }}>{expanded ? "▲" : "▼"}</span>
      </div>
      {expanded && (
        <div style={{ padding: "0 14px 12px", borderTop: `1px solid ${regime.color}22` }}>
          <p style={{ fontSize: 12, color: T.textSub, lineHeight: 1.7, margin: "10px 0 10px" }}>{regime.summary}</p>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", marginBottom: 6 }}>WHAT TO DO RIGHT NOW</div>
          {regime.actions.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5 }}>
              <span style={{ color: regime.color, fontSize: 11, flexShrink: 0 }}>→</span>
              <span style={{ fontSize: 11, color: T.text, lineHeight: 1.6 }}>{a}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
const SCAN_DELAY = 300; // Small pause between ticker scans to avoid rate limiting Yahoo Finance

export default function App() {
  const [isDark, setIsDark] = useState(() => {
    try { return localStorage.getItem("datum_theme") === "dark"; } catch { return false; }
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  useEffect(() => {
    console.info(`[Auctus] client build ${AUCTUS_CLIENT_BUILD} — AI tab needs this build or newer (not "item is not defined")`);
    window.auctusResetAppShell = async () => {
      await resetAppShellCache();
      window.location.reload();
    };
    window.auctusClearAiCache = (sym) => clearTickerAiCache(sym || "");
    window.auctusClientBuild = AUCTUS_CLIENT_BUILD;
    return () => {
      delete window.auctusResetAppShell;
      delete window.auctusClearAiCache;
      delete window.auctusClientBuild;
    };
  }, []);
  const [portfolioSize, setPortfolioSize] = useState(() => {
    try { const v = localStorage.getItem("auctus_portfolio_size"); return v ? parseFloat(v) : null; } catch { return null; }
  });
  const savePortfolioSize = (val) => {
    const n = parseFloat(val);
    const v = !isNaN(n) && n > 0 ? n : null;
    setPortfolioSize(v);
    try { if (v) localStorage.setItem("auctus_portfolio_size", String(v)); else localStorage.removeItem("auctus_portfolio_size"); } catch {}
  };
  const [proMode, setProMode] = useState(() => {
    try { return localStorage.getItem("datum_mode") === "pro"; } catch { return false; }
  });
  const [hintsOn, setHintsOn] = useState(() => {
    try { return localStorage.getItem("datum_hints") !== "off"; } catch { return true; }
  });
  const toggleHints = () => setHintsOn(h => {
    const next = !h;
    try { localStorage.setItem("datum_hints", next ? "on" : "off"); } catch {}
    return next;
  });
  const toggleMode = () => setProMode(p => {
    const next = !p;
    try { localStorage.setItem("datum_mode", next ? "pro" : "easy"); } catch {}
    return next;
  });

  // Column visibility — pro mode only, user-configurable
  // These are the 11 data columns beyond the always-visible core (Ticker, Price, Verdict, Score, Calls, Puts, Targets)
  const COL_DEFS = [
    { key: "rsi",      label: "RSI",        desc: ">70 momentum · 50–70 bullish · 45–50 neutral · <45 weak · ↑/↓ divergence." },
    { key: "confluence", label: "Confluence", desc: "Tier-1 setup alignment: Fib, Donchian breakout, bounce, or distribution." },
    { key: "bb",       label: "BB / Squeeze", desc: "Bollinger Bands. Squeeze = explosive move loading." },
    { key: "cmf",      label: "CMF + Vol",  desc: "Institutional money flow + relative volume." },
    { key: "gate",     label: "Gate",       desc: "14-point entry gate. GO = all conditions met." },
    { key: "trend",    label: "Trend",      desc: "Weekly trend + multi-timeframe alignment." },
    { key: "range52w", label: "52W Range",  desc: "Where price sits in its annual range (0–100)." },
    { key: "rs",       label: "RS vs SPY",  desc: "3-month relative strength vs S&P 500." },
    { key: "stop",     label: "Stop",       desc: "ATR-based stop level. Enter buy price to calculate." },
    { key: "rr",       label: "R:R",        desc: "Risk:Reward ratio. Enter buy price to calculate." },
    { key: "earnings", label: "Earnings",   desc: "Earnings countdown (days to next report).", defaultVisible: true },
    { key: "ivrank",   label: "IV Rank",    desc: "IV Rank shows how expensive options are relative to the past year. Available after Polygon.io integration.", defaultVisible: false },
    { key: "csp",      label: "CSP %",      desc: "30-day Cash Secured Put yield. Available after Polygon.io integration.", defaultVisible: false },
    { key: "spark",    label: "Trend",      desc: "10-bar sparkline trend (last closes).", defaultVisible: true },
  ];
  const visibleColDefs = COL_DEFS.filter((c) => POLYGON_OPTIONS_LIVE || (c.key !== "ivrank" && c.key !== "csp"));
  const DEFAULT_COLS = Object.fromEntries(COL_DEFS.map(c => [c.key, c.defaultVisible !== false]));
  const [colVis, setColVis] = useState(() => {
    try {
      const saved = localStorage.getItem("auctus_col_vis");
      if (!saved) return DEFAULT_COLS;
      const parsed = JSON.parse(saved);
      if (parsed.macd !== undefined && parsed.confluence === undefined) {
        parsed.confluence = parsed.macd;
        delete parsed.macd;
      }
      if (!POLYGON_OPTIONS_LIVE) {
        parsed.ivrank = false;
        parsed.csp = false;
      }
      return { ...DEFAULT_COLS, ...parsed };
    } catch { return DEFAULT_COLS; }
  });
  const toggleColVis = (key) => {
    setColVis(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem("auctus_col_vis", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const col = (key) => proMode && colVis[key]; // convenience — true if proMode AND column enabled
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mainView, setMainView] = useState("watchlist"); // "watchlist" | "scanner" | "autotrader"
  const [scannerPreset, setScannerPreset] = useState(null);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [signalOfDay, setSignalOfDay] = useState(null);  // top setup from auto-scan
  const [newsFilter, setNewsFilter] = useState(null);    // symbol to filter news panel, null = all
  const [globalNewsFeed, setGlobalNewsFeed] = useState([]); // merged articles from all fetched tickers
  const [drawerTab, setDrawerTab] = useState("scanner");

  const openDrawerTab = (tab) => {
    if (drawerOpen && drawerTab === tab) {
      setDrawerOpen(false); // toggle off if already on this tab
    } else {
      setDrawerTab(tab);
      setDrawerOpen(true);
    }
  };
  // Mutate global T so all components pick up the right theme
  T = THEMES[isDark ? "dark" : "light"];
  const toggleTheme = () => setIsDark(d => {
    const next = !d;
    try { localStorage.setItem("datum_theme", next ? "dark" : "light"); } catch {}
    return next;
  });

  const spyReturnRef = useRef(null); // SPY 63-day return for RS calculation
  const [vix, setVix] = useState(null);
  const [livePrice, setLivePrice] = useState({}); // symbol -> { price, changePct, change, ts }
  const [btcPrice, setBtcPrice] = useState(null);
  const [fearGreed, setFearGreed] = useState(null);
  const [marketTide, setMarketTide] = useState(null); // { breadthScore, spyPct, qqqPct, vix, ... } | null
  const marketTideRef = useRef(null);
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const ADMIN_ID = "88a2dfc1-3bf9-48e8-9d02-00d148ed0cce";
  const isAdmin = (user?.id ?? null) === ADMIN_ID;
  const [anthropicKey, setAnthropicKey] = useState("");
  const anthropicKeyRef = useRef("");
  const [openaiKey, setOpenaiKey] = useState("");
  const openaiKeyRef = useRef("");
  const [geminiKey, setGeminiKey] = useState("");
  const geminiKeyRef = useRef("");
  const [aiProvider, setAiProvider] = useState("anthropic");
  const [showSettings, setShowSettings] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [tourStep, setTourStep] = useState(null); // null = tour not active, 0-N = step index
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showSignalGuide, setShowSignalGuide] = useState(false);
  const [briefingHighlight, setBriefingHighlight] = useState(null);
  const [signalGuideHint, setSignalGuideHint] = useState(() => {
    try { return localStorage.getItem("auctus_signal_guide_hint") !== "1"; } catch { return true; }
  });
  const dismissSignalGuideHint = () => {
    setSignalGuideHint(false);
    try { localStorage.setItem("auctus_signal_guide_hint", "1"); } catch {}
  };
  const scrollToWatchlistTicker = (sym) => {
    const id = sym?.toUpperCase?.();
    if (!id) return;
    setBriefingHighlight(id);
    setTimeout(() => {
      document.getElementById(`wl-row-${id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 80);
    setTimeout(() => setBriefingHighlight(null), 3200);
  };
  const [helpInitialTab, setHelpInitialTab] = useState("quickstart");
  const openHelp = (tab = "quickstart") => { setHelpInitialTab(tab); setShowHelp(true); };
  const [tickerInput, setTickerInput] = useState("");
  const [watchlist, setWatchlist] = useState([]);
  const [scanQueue, setScanQueue] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [sortCol, setSortCol] = useState("buyScore");
  const [sortDir, setSortDir] = useState(-1);
  const [filter, setFilter] = useState("ALL");
  const [readyOnly, setReadyOnly] = useState(false); // READY sub-filter for CALLS/PUTS on watchlist
  const [showWatchlistAnalysis, setShowWatchlistAnalysis] = useState(null); // null | "CALLS" | "PUTS"
  const [watchlistAI, setWatchlistAI] = useState(null); // { text, date } | null
  const [dailyBriefing, setDailyBriefing] = useState(null); // { text, date, regime }
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingCollapsed, setBriefingCollapsed] = useState(() => {
    try { return localStorage.getItem("auctus_briefing_collapsed") === "1"; } catch { return false; }
  });
  const toggleBriefingCollapsed = () => {
    setBriefingCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem("auctus_briefing_collapsed", next ? "1" : "0"); } catch {}
      return next;
    });
  };
  const [watchlistAILoading, setWatchlistAILoading] = useState(false);
  const [showWatchlistAI, setShowWatchlistAI] = useState(false);
  const [showReversalWatch, setShowReversalWatch] = useState(false);
  const [showColPanel, setShowColPanel] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [showSectors, setShowSectors] = useState(false);
  const [sectorDashboard, setSectorDashboard] = useState({ byEtf: {}, ts: 0 });
  const [rotationAlerts, setRotationAlerts] = useState([]);
  const [sectorDashboardLoading, setSectorDashboardLoading] = useState(false);
  const [sectorAlertBanner, setSectorAlertBanner] = useState(null);
  const [sectorAlertTickers, setSectorAlertTickers] = useState([]);
  const [sectorAlertsCollapsed, setSectorAlertsCollapsed] = useState(false);
  const [rotationScanBanner, setRotationScanBanner] = useState(null);
  const [rotationScanBannerVisible, setRotationScanBannerVisible] = useState(false);
  const [marketDataUnavailableBanner, setMarketDataUnavailableBanner] = useState(false);
  const sectorAlertsSectionRef = useRef(null);
  const sectorDashboardRef = useRef({ byEtf: {}, ts: 0 });
  const [showMovers, setShowMovers] = useState(false);
  const [movers, setMovers] = useState(null);
  const [moversLoading, setMoversLoading] = useState(false);
  const [pendingTrade, setPendingTrade] = useState(null);
  const [playbookHeight, setPlaybookHeight] = useState(280);
  const [drawerWidth, setDrawerWidth] = useState(520);
  const [watchlistDrawer, setWatchlistDrawer] = useState(null); // { symbol, result, position }
  const isDragging = useRef(false);
  const dragStartY = useRef(0);
  const dragStartHeight = useRef(0);
  const isDrawerDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [lastScan, setLastScan] = useState(null);
  const cdRef = useRef(null);
  const watchlistRef = useRef(watchlist);
  useEffect(() => { watchlistRef.current = watchlist; }, [watchlist]);

  useEffect(() => {
    if (!rotationScanBannerVisible) return undefined;
    const t = setTimeout(() => setRotationScanBannerVisible(false), 60000);
    return () => clearTimeout(t);
  }, [rotationScanBannerVisible, rotationScanBanner]);

  // Auth state listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      _uid = u?.id ?? null;
      // Small delay ensures _uid is set before dataLoaded effect reads it
      setTimeout(() => setAuthLoading(false), 50);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const prevUid = _uid;
      const u = session?.user ?? null;
      setUser(u);
      _uid = u?.id ?? null;
      // If uid changed from null → real user, reload data from Supabase
      if (!prevUid && _uid) {
        setDataLoaded(false); // triggers re-load
      }
      // If user signed out, reset to defaults
      if (prevUid && !_uid) {
        setWatchlist([]);
        setDataLoaded(false);
        _watchlistLoadedFromSupabase = false;
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Admin overlay gate — hash-driven (#admin-signals)
  useEffect(() => {
    const check = () => {
      const hash = window.location.hash || "";
      const uid = user?.id ?? null;
      if (hash === "#admin-signals") {
        const ok = uid === ADMIN_ID;
        console.log("[admin-signals] hash match", { uid, admin: ADMIN_ID, ok });
        if (ok) {
          setShowAdminDashboard(true);
        } else {
          try { window.location.hash = ""; } catch {}
          setShowAdminDashboard(false);
        }
      } else {
        setShowAdminDashboard(false);
      }
    };
    check();
    window.addEventListener("hashchange", check);
    return () => window.removeEventListener("hashchange", check);
  }, [user?.id]);

  // Load persisted data — runs after auth resolves so _uid is set before Supabase reads
  const [dataLoaded, setDataLoaded] = useState(false);
  useEffect(() => {
    if (authLoading) return; // wait for auth to resolve first
    if (dataLoaded) return; // already loaded — only re-run when explicitly reset
    (async () => {
      // Always fetch market data — all via Yahoo Finance, no API keys needed
      setTimeout(fetchVIX, 300);
      setTimeout(fetchFearGreed, 500);
      setTimeout(fetchBTC, 700);
      setTimeout(fetchGeneralNews, 2000); // load news feed 2s after app init
      setTimeout(() => checkAndUpdateSignalAccuracy(), 10000); // background accuracy check — low priority
      // Show onboarding only on true first visit
      const onboardingDone = await load("onboardingComplete");
      if (!onboardingDone) setShowOnboarding(true);
      const tourDone = await load("tourComplete");
      if (onboardingDone && !tourDone) setTimeout(() => setTourStep(0), 1200); // start tour after initial load
      // Show disclaimer if not yet accepted
      const disclaimerAccepted = await load("disclaimerAccepted");
      if (!disclaimerAccepted) setTimeout(() => setShowDisclaimer(true), 1200);
      const savedAnthropicKey = await load("anthropicKey");
      if (savedAnthropicKey) { setAnthropicKey(savedAnthropicKey); anthropicKeyRef.current = savedAnthropicKey; }
      const savedOpenaiKey = await load("openaiKey");
      if (savedOpenaiKey) { setOpenaiKey(savedOpenaiKey); openaiKeyRef.current = savedOpenaiKey; }
      const savedGeminiKey = await load("geminiKey");
      if (savedGeminiKey) { setGeminiKey(savedGeminiKey); geminiKeyRef.current = savedGeminiKey; }
      const savedProvider = await load("aiProvider");
      if (savedProvider) setAiProvider(savedProvider);
      const savedResults = await load("lastScanResults") || {};
      const signalHistoryForStale = (await load("signal_history")) || [];
      const activeSignalsForLoad = (await load("active_signals")) || [];
      const savedRotationState = (await load("sector_rotation_state")) || {};
      const savedRotationAlerts = (await load("rotation_alerts")) || [];
      if (savedRotationState?.byEtf) {
        const cache = { ts: savedRotationState.updatedAt || Date.now(), byEtf: savedRotationState.byEtf };
        _sectorDashboardCache = cache;
        sectorDashboardRef.current = cache;
        setSectorDashboard(cache);
      }
      if (Array.isArray(savedRotationAlerts)) setRotationAlerts(savedRotationAlerts);
      const enrichedScanResults = enrichResultsWithActiveSignals(
        enrichResultsWithOpenSignals(
          Object.fromEntries(
            Object.entries(savedResults).filter(([, v]) => typeof v?.buyScore === "number" && v?.price != null)
          ),
          signalHistoryForStale
        ),
        activeSignalsForLoad
      );
      const enrichedWithSector = Object.fromEntries(
        Object.entries(enrichedScanResults).map(([sym, r]) => [
          sym,
          attachSectorContextToResult(sym, r, _sectorDashboardCache, TICKER_SECTOR_MAP),
        ])
      );
      // Invalidate cached results missing bullCount (added in latest version)
      const validResults = enrichedWithSector;
      const DEFAULT_POSITION = { buyPrice: "", shares: "", dollarAmt: "", inputMode: "shares", stopPct: 8 };
      const normalizeFromCloud = (arr) => arr.map(i => {
        const sym = typeof i === 'string' ? i : i?.symbol;
        const pos = typeof i === 'string' ? DEFAULT_POSITION : (i?.position || DEFAULT_POSITION);
        if (!sym) return null;
        return { symbol: sym, position: pos, inPosition: !!(i?.inPosition), status: "idle", result: null };
      }).filter(Boolean);
      const normalizeFromLocal = (arr) => arr.map(i => {
        const sym = typeof i === 'string' ? i : i?.symbol;
        const pos = typeof i === 'string' ? DEFAULT_POSITION : (i?.position || DEFAULT_POSITION);
        if (!sym) return null;
        return {
          symbol: sym,
          position: pos,
          inPosition: !!(i?.inPosition),
          status: validResults[sym] ? "done" : "idle",
          result: validResults[sym] || null,
        };
      }).filter(Boolean);
      const { data: { session: wlSession } } = await supabase.auth.getSession();
      const uid = wlSession?.user?.id ?? null;

      if (uid) {
        try {
          const { data, error } = await supabase
            .from("user_data")
            .select("value")
            .eq("user_id", uid)
            .eq("key", "watchlist")
            .maybeSingle();

          console.log("[watchlist load] Supabase user_data:", { uid, key: "watchlist", data, error });

          if (error) console.error("[watchlist load] Supabase query error:", error);

          let cloudRaw = data?.value ?? null;
          if (typeof cloudRaw === "string") {
            try {
              cloudRaw = JSON.parse(cloudRaw);
            } catch (parseErr) {
              console.warn("[watchlist load] value JSON.parse failed:", parseErr);
              cloudRaw = null;
            }
          }

          const cloud = Array.isArray(cloudRaw) ? cloudRaw : null;

          if (cloud !== null) {
            const normalized = normalizeFromCloud(cloud);
            console.log("[watchlist load] applied from Supabase:", { count: normalized.length, normalized });
            setWatchlist(normalized);
            _watchlistLoadedFromSupabase = true;
            try {
              localStorage.setItem("watchlist", JSON.stringify(cloud));
              localStorage.setItem(WATCHLIST_BACKUP_KEY, JSON.stringify(cloud));
            } catch {}
          } else {
            const wl = await load("watchlist");
            console.log("[watchlist load] no client row; load('watchlist') returned:", wl);
            if (wl != null && Array.isArray(wl)) {
              const normalized = normalizeFromLocal(wl);
              console.log("[watchlist load] applied from API/localStorage:", { count: normalized.length, normalized });
              setWatchlist(normalized);
              _watchlistLoadedFromSupabase = true;
            } else {
              let backup = null;
              try {
                const raw = localStorage.getItem(WATCHLIST_BACKUP_KEY);
                backup = raw ? JSON.parse(raw) : null;
              } catch {}
              if (Array.isArray(backup) && backup.length > 0) {
                const normalized = normalizeFromLocal(backup);
                setWatchlist(normalized);
                _watchlistLoadedFromSupabase = true;
                save("watchlist", normalized.map(({ symbol, position, inPosition }) => ({ symbol, position, inPosition })));
                const tid = Date.now();
                setToasts(a => [{ id: tid, msg: "Watchlist restored from local backup", color: T.green }, ...a.slice(0, 3)]);
                setTimeout(() => setToasts(a => a.filter(x => x.id !== tid)), 5000);
              }
            }
          }
        } catch (e) {
          console.error("[watchlist load] exception:", e);
          try {
            const wl = await load("watchlist");
            console.log("[watchlist load] after exception, load('watchlist'):", wl);
            if (wl != null && Array.isArray(wl)) {
              const normalized = normalizeFromLocal(wl);
              console.log("[watchlist load] applied after exception:", { count: normalized.length, normalized });
              setWatchlist(normalized);
              _watchlistLoadedFromSupabase = true;
            }
          } catch {}
        }
      } else {
        const wl = await load("watchlist");
        if (wl && Array.isArray(wl) && wl.length > 0) {
          setWatchlist(normalizeFromLocal(wl));
          _watchlistLoadedFromSupabase = true;
        }
      }
      const ls = await load("lastScanTime");
      if (ls) setLastScan(ls);
      setDataLoaded(true);
    })();
  }, [authLoading]);

  // Auto-scan on page load once watchlist is ready
  const hasAutoScanned = useRef(false);
  const scanAllRef = useRef(null);
  useEffect(() => {
    if (!dataLoaded) return; // wait for Supabase data to load first
    if (hasAutoScanned.current) return;
    if (watchlist.length === 0) return;
    hasAutoScanned.current = true;
    const t = setTimeout(() => {
      if (scanAllRef.current) scanAllRef.current();
    }, 800);
    return () => clearTimeout(t);
  }, [watchlist.length, dataLoaded]);

  // Persist watchlist symbols + positions on every change
  // Guard: only save after dataLoaded OR after user explicitly adds/removes (tracked by saveEnabled ref)
  const saveEnabledRef = useRef(false);
  useEffect(() => {
    if (dataLoaded) saveEnabledRef.current = true;
  }, [dataLoaded]);

  useEffect(() => {
    if (!saveEnabledRef.current) return;
    if (watchlist.length === 0) return;
    const t = setTimeout(() => {
      save("watchlist", watchlist.map(({ symbol, position, inPosition }) => ({ symbol, position, inPosition })));
    }, 600);
    return () => clearTimeout(t);
  }, [watchlist]);

  // Persist scan results separately so they survive app restarts
  useEffect(() => {
    const results = {};
    watchlist.forEach(i => { if (i.result) results[i.symbol] = i.result; });
    if (Object.keys(results).length > 0) {
      const t = setTimeout(() => save("lastScanResults", results), 800);
      return () => clearTimeout(t);
    }
  }, [watchlist]);

  // ── AUTO-REFRESH INTERVALS ───────────────────────────────────────────────────
  // BTC: every 15s | VIX: every 60s | Watchlist quotes: every 5min (staggered)
  // News: 12min market hours (9:30–16:00 ET) | 25min pre/after/weekend
  // All paused when tab is hidden to avoid burning requests in background
  useEffect(() => {
    const isVisible = () => document.visibilityState === "visible";

    // Smart news interval — detects market hours (ET)
    const isMarketHours = () => {
      const now = new Date();
      const et = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const day = et.getDay(); // 0=Sun, 6=Sat
      if (day === 0 || day === 6) return false;
      const h = et.getHours(), m = et.getMinutes();
      const mins = h * 60 + m;
      return mins >= 9 * 60 + 30 && mins < 16 * 60; // 9:30 AM – 4:00 PM ET
    };
    const NEWS_INTERVAL_MARKET = 12 * 60 * 1000;  // 12 minutes
    const NEWS_INTERVAL_OFF    = 25 * 60 * 1000;  // 25 minutes
    let newsTimer = null;
    const scheduleNews = () => {
      if (newsTimer) clearTimeout(newsTimer);
      const interval = isMarketHours() ? NEWS_INTERVAL_MARKET : NEWS_INTERVAL_OFF;
      newsTimer = setTimeout(() => {
        if (isVisible()) fetchGeneralNews();
        scheduleNews(); // reschedule after each run
      }, interval);
    };
    scheduleNews(); // kick off first schedule

    // BTC — 15s
    const btcInt = setInterval(() => { if (isVisible()) fetchBTC(); }, 15000);

    // VIX — 60s
    const vixInt = setInterval(() => { if (isVisible()) fetchVIX(); }, 60000);

    // Watchlist live quotes — stagger fetches across 30s window so we don't
    // hammer Yahoo with all tickers simultaneously
    let quoteTimer = null;
    const scheduleQuotes = () => {
      if (quoteTimer) clearTimeout(quoteTimer);
      const syms = watchlist.filter(i => i.result).map(i => i.symbol);
      if (!syms.length || !isVisible()) { quoteTimer = setTimeout(scheduleQuotes, 300000); return; }
      let delay = 0;
      const gap = Math.max(1500, Math.floor(300000 / syms.length)); // spread evenly over 5 min
      syms.forEach(sym => {
        setTimeout(() => {
          if (!isVisible()) return;
          fetch(`/api/quote?symbol=${encodeURIComponent(sym)}`)
            .then(r => r.ok ? r.json() : null)
            .then(q => {
              if (!q?.price) return;
              setLivePrice(prev => ({ ...prev, [sym]: { price: q.price, changePct: q.changePct, change: q.change, open: q.open || null, prevClose: q.prevClose || null, isPreMarket: q.isPreMarket || false, isPostMarket: q.isPostMarket || false, ts: Date.now() } }));
            })
            .catch(() => {});
        }, delay);
        delay += gap;
      });
      quoteTimer = setTimeout(scheduleQuotes, 300000);
    };
    // Start quote refresh after 5s (let initial scan finish first)
    const quoteStart = setTimeout(scheduleQuotes, 5000);

    return () => {
      clearInterval(btcInt);
      clearInterval(vixInt);
      clearTimeout(quoteTimer);
      clearTimeout(quoteStart);
      clearTimeout(newsTimer);
    };
  }, [watchlist.length]);

  // Draggable divider between ticker pane and bottom pane
  // Also handles horizontal drawer resize
  useEffect(() => {
    const onMove = e => {
      if (isDragging.current) {
        const delta = dragStartY.current - e.clientY;
        const next = Math.min(Math.max(dragStartHeight.current + delta, 80), window.innerHeight - 220);
        setPlaybookHeight(next);
      }
      if (isDrawerDragging.current) {
        const delta = dragStartX.current - e.clientX;
        const next = Math.min(Math.max(dragStartWidth.current + delta, 320), window.innerWidth - 400);
        setDrawerWidth(next);
      }
    };
    const onUp = () => {
      isDragging.current = false;
      isDrawerDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = e => {
      // Don't fire when typing in an input
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.tagName === "SELECT") return;
      if (e.key === "s" || e.key === "S") scanAll();
      if (e.key === "Escape") { setShowSettings(false); setShowScheduler(false); setShowHelp(false); setShowSignalGuide(false); setShowMovers(false); }
      if (e.key === "1") setFilter("ALL");
      if (e.key === "2") setFilter("BUY");
      if (e.key === "3") setFilter("SELL");
      if (e.key === "4") setFilter("SQUEEZE");
      if (e.key === "5") setFilter("ALERTS");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [watchlist]);

  // Electron scan trigger
  useEffect(() => {
    if (!eAPI) return;
    eAPI.onTriggerScan(({ time }) => {
      toast(`⏰ Scheduled scan at ${time || "now"}`, T.cyan);
      const q = watchlist.map(i => i.symbol);
      setScanQueue(q); setWatchlist(w => w.map(i => ({ ...i, status: "queued" }))); processQueue(q);
    });
  }, [watchlist]);

  const toast = (msg, color = T.green) => {
    const id = Date.now();
    setToasts(a => [{ id, msg, color }, ...a.slice(0, 3)]);
    setTimeout(() => setToasts(a => a.filter(x => x.id !== id)), 5000);
  };

  const fetchMovers = async () => {
    setMoversLoading(true); setShowMovers(true); setMovers(null);
    // Use Yahoo Finance via /api/quote to get intraday change for a curated mover universe
    const MOVER_UNIVERSE = ["SPY","QQQ","AAPL","MSFT","NVDA","GOOGL","META","AMZN","TSLA","AVGO",
      "AMD","PLTR","COIN","MSTR","SOFI","SMCI","MU","ARM","MARA","RIOT","HOOD","RBLX","SNAP","UBER","LYFT",
      "NIO","RIVN","LCID","GME","AMC","BBBY","SOXX","GLD","SLV","USO"];
    try {
      const results = await Promise.allSettled(
        MOVER_UNIVERSE.map(sym => fetch(`/api/quote?symbol=${encodeURIComponent(sym)}`).then(r => r.json()).then(q => ({
          ticker: sym, price: q.price?.toFixed(2) || "—",
          change_percentage: q.changePct != null ? q.changePct.toFixed(2) : null,
        })).catch(() => null))
      );
      const withChange = results
        .map(r => r.value)
        .filter(s => s && s.change_percentage != null);
      const gainers = [...withChange].sort((a, b) => parseFloat(b.change_percentage) - parseFloat(a.change_percentage)).slice(0, 10);
      const losers  = [...withChange].sort((a, b) => parseFloat(a.change_percentage) - parseFloat(b.change_percentage)).slice(0, 10);
      const active  = [...withChange].sort((a, b) => Math.abs(parseFloat(b.change_percentage)) - Math.abs(parseFloat(a.change_percentage))).slice(0, 10);
      setMovers({ gainers, losers, active });
    } catch (e) {
      toast(`Top Movers error: ${e.message}`, T.red);
      setShowMovers(false);
    } finally { setMoversLoading(false); }
  };
  const fetchTicker = async (sym, priorBuyScore = null) => {
    const sectorEtf = TICKER_SECTOR_MAP[sym];
    let scanUrl = `/api/scan?symbol=${encodeURIComponent(sym)}&includePhase=1${sectorEtf ? `&sectorEtf=${encodeURIComponent(sectorEtf)}` : ""}`;
    if (priorBuyScore != null && Number.isFinite(Number(priorBuyScore))) {
      scanUrl += `&priorBuyScore=${encodeURIComponent(priorBuyScore)}`;
    }
    const res = await fetch(scanUrl);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      if (res.status === 404) throw new Error(`Invalid ticker 404: ${sym}`);
      throw new Error(err.error || `Error ${res.status} for ${sym}`);
    }
    const data = await res.json();
    if (!data.closes || data.closes.length < 5) throw new Error(`Not enough data for ${sym} — may be too new or delisted`);
    return data;
  };

  const fetchSectorDashboard = useCallback(async (force = false) => {
    if (!force && Date.now() - _sectorDashboardCache.ts < SECTOR_CACHE_MS
      && Object.keys(_sectorDashboardCache.byEtf || {}).length >= 7) {
      setSectorDashboard(_sectorDashboardCache);
      sectorDashboardRef.current = _sectorDashboardCache;
      return _sectorDashboardCache;
    }
    setSectorDashboardLoading(true);
    try {
      const spyRes = await fetch("/api/scan?symbol=SPY");
      if (!spyRes.ok) throw new Error("SPY fetch failed");
      const spyData = await spyRes.json();
      const prevState = (await load("sector_rotation_state")) || {};
      const prevByEtf = prevState.byEtf || {};
      const byEtf = {};
      await Promise.all(SECTOR_DASHBOARD_ETFS.map(async ({ etf, name }) => {
        try {
          const res = await fetch(`/api/scan?symbol=${encodeURIComponent(etf)}`);
          if (!res.ok) return;
          const data = await res.json();
          const metrics = buildSectorEtfMetrics(data, spyData);
          if (!metrics) return;
          const prev = prevByEtf[etf];
          const stageInfo = detectRotationStage(metrics, prev);
          byEtf[etf] = { etf, name, ...metrics, ...stageInfo };
        } catch (e) {
          console.warn("[sectors] ETF fetch failed:", etf, e?.message || e);
        }
      }));
      const newAlerts = buildRotationAlerts(prevByEtf, byEtf);
      if (newAlerts.length) {
        const existing = (await load("rotation_alerts")) || [];
        const merged = [...newAlerts, ...existing].slice(0, 50);
        await save("rotation_alerts", merged);
        setRotationAlerts(merged);
        setSectorAlertBanner(formatStageAlertMessage(newAlerts[0]));
      } else {
        const existing = (await load("rotation_alerts")) || [];
        setRotationAlerts(Array.isArray(existing) ? existing : []);
      }
      await save("sector_rotation_state", { byEtf, updatedAt: Date.now() });
      const cache = { ts: Date.now(), byEtf, spyData };
      _sectorDashboardCache = cache;
      sectorDashboardRef.current = cache;
      setSectorDashboard(cache);
      setWatchlist(w => w.map(i => {
        if (!i.result) return i;
        return { ...i, result: attachSectorContextToResult(i.symbol, { ...i.result }, cache, TICKER_SECTOR_MAP) };
      }));
      return cache;
    } catch (e) {
      console.warn("[sectors] dashboard fetch failed:", e?.message || e);
      return sectorDashboardRef.current;
    } finally {
      setSectorDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (mainView === "sectors" && Object.keys(sectorDashboard?.byEtf || {}).length < 7 && !sectorDashboardLoading) {
      fetchSectorDashboard().catch(() => {});
    }
  }, [mainView, sectorDashboard?.byEtf, sectorDashboardLoading, fetchSectorDashboard]);

  const fetchMarketTide = async () => {
    try {
      console.log("[MarketTide] fetchMarketTide: start");
      const res = await fetch("/api/scan?marketTide=1");
      if (!res.ok) {
        console.log("[MarketTide] fetchMarketTide: non-OK", res.status);
        return null;
      }
      const json = await res.json();
      const tide = json?.marketTide ?? null;
      console.log("[MarketTide] fetchMarketTide: result", tide);
      return tide;
    } catch (e) {
      console.log("[MarketTide] fetchMarketTide: exception", e?.message || e);
      return null;
    }
  };


  const quoteStorageKey = (sym) => `auctus_quote_${String(sym).toUpperCase()}`;

  const readLocalQuoteCache = (sym) => {
    try {
      const raw = localStorage.getItem(quoteStorageKey(sym));
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.price != null ? parsed : null;
    } catch {
      return null;
    }
  };

  const fetchQuoteApi = async (symbol) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const res = await fetch(`/api/quote?symbol=${encodeURIComponent(symbol)}`, {
        signal: controller.signal,
      });
      const q = await res.json().catch(() => null);
      if (q?.marketDataUnavailable) setMarketDataUnavailableBanner(true);
      if (q?.price != null && !q?.error) {
        try {
          localStorage.setItem(quoteStorageKey(q.symbol || symbol), JSON.stringify({ ...q, savedAt: Date.now() }));
        } catch {}
        return q;
      }
      const cached = readLocalQuoteCache(symbol);
      if (cached) {
        setMarketDataUnavailableBanner(true);
        return cached;
      }
      return null;
    } catch (e) {
      console.warn("quote fetch failed:", symbol, e?.message || e);
      const cached = readLocalQuoteCache(symbol);
      if (cached) {
        setMarketDataUnavailableBanner(true);
        return cached;
      }
      return null;
    } finally {
      clearTimeout(timer);
    }
  };

  // Fetch live VIX from Yahoo Finance
  const fetchSectorETFs = async () => {
    try {
      await Promise.all(SECTOR_ETFS.map(async etf => {
        const q = await fetchQuoteApi(etf);
        if (q?.price) _sectorETFData[etf] = { price: q.price, prevClose: q.prevClose || q.price };
      }));
    } catch (e) { console.warn("Sector ETF fetch failed:", e.message); }
  };

  // ── GENERAL NEWS FEED ── fetches Reuters + top market tickers regardless of watchlist
  const fetchGeneralNews = async () => {
    try {
      // General feed — no symbol, gets Reuters RSS + any market-wide news
      const res = await fetch("/api/news");
      if (!res.ok) return;
      const json = await res.json();
      if (!json?.articles?.length) return;
      // Merge into global feed, deduplicated by title
      setGlobalNewsFeed(prev => {
        const seen = new Set(prev.map(a => a.title.slice(0, 60).toLowerCase()));
        const fresh = json.articles.filter(a => !seen.has(a.title.slice(0, 60).toLowerCase()));
        return [...fresh, ...prev].slice(0, 300);
      });
      // Also fetch news for a curated list of high-profile market tickers
      // Stagger these so we don't hit the API all at once
      const CORE_TICKERS = ["NVDA","AAPL","MSFT","TSLA","META","AMZN","GOOGL","AMD","SPY","QQQ"];
      CORE_TICKERS.forEach((sym, i) => {
        setTimeout(async () => {
          if (document.visibilityState !== "visible") return;
          try {
            const r = await fetch(`/api/news?symbol=${sym}`);
            if (!r.ok) return;
            const d = await r.json();
            if (!d?.articles?.length) return;
            setGlobalNewsFeed(prev => {
              const seenSet = new Set(prev.map(a => a.title.slice(0, 60).toLowerCase()));
              const fresh = d.articles
                .filter(a => !seenSet.has(a.title.slice(0, 60).toLowerCase()))
                .map(a => ({ ...a, symbol: sym }));
              return [...fresh, ...prev].slice(0, 300);
            });
          } catch {}
        }, i * 1200); // 1.2s between each to be gentle on the API
      });
    } catch (e) { console.warn("fetchGeneralNews failed:", e.message); }
  };

  const fetchVIX = async () => {
    try {
      const q = await fetchQuoteApi("^VIX");
      if (!q?.price) return;
      const current = q.price;
      const prevClose = q.prevClose || current;
      const change = current - prevClose;
      const changePct = prevClose ? (change / prevClose) * 100 : 0;
      const level = current >= 35 ? "EXTREME FEAR" : current >= 25 ? "HIGH FEAR" : current >= 20 ? "ELEVATED" : current >= 15 ? "NORMAL" : current >= 12 ? "CALM" : "COMPLACENCY";
      const spySpot = marketTideRef.current?.spyPrice ?? null;
      const impliedMove = vixImpliedDailyMove(current, spySpot);
      setVix({
        value: current,
        change,
        changePct,
        rising: change > 0,
        level,
        color: current >= 30 ? T.red : current >= 20 ? T.orange : current >= 15 ? T.yellow : T.green,
        time: new Date().toLocaleTimeString(),
        label: "VIX",
        impliedMove,
      });
      // Track VIX history for trend detection (last 5 readings)
      _vixHistory = [..._vixHistory.slice(-4), current];
      const vixTrend = (() => {
        if (_vixHistory.length < 3) return "unknown";
        const recent = _vixHistory.slice(-3);
        const rising = recent[2] > recent[0];
        const falling = recent[2] < recent[0];
        const magnitude = Math.abs(recent[2] - recent[0]);
        if (magnitude < 0.3) return "flat";
        return rising ? "rising" : "falling";
      })();
      _vixData = { value: current, level, rising: change > 0, trend: vixTrend };
    } catch (e) { console.warn("VIX fetch failed:", e.message); }
  };

  // Auto-fetch VIX on page load/login — never require a click.
  useEffect(() => {
    fetchVIX();
  }, []);

  const fetchBTC = async () => {
    try {
      const q = await fetchQuoteApi("BTC-USD");
      if (!q?.price) return;
      setBtcPrice({
        price: q.price,
        changePct: q.changePct,
        change: q.change,
        time: new Date().toLocaleTimeString(),
      });
      _btcData = { price: q.price, changePct: q.changePct, bullish: (q.changePct || 0) > 0 };
    } catch (e) { console.warn("BTC fetch failed:", e.message); }
  };

  // Fetch VIX on mount and when earnings are fetched
  useEffect(() => { /* VIX now fetched after keys load */ }, []);

  const fetchFearGreed = async () => {
    try {
      const res = await fetch("https://production.dataviz.cnn.io/index/fearandgreed/graphdata", {
        headers: {
          "User-Agent": "Mozilla/5.0",
          "Origin": "https://edition.cnn.com",
          "Referer": "https://edition.cnn.com/",
        }
      });
      if (!res.ok) return;
      const json = await res.json();
      const fg = json?.fear_and_greed;
      if (!fg?.score) return;
      const score = Math.round(fg.score);
      const prev = fg.previous_close ? Math.round(fg.previous_close) : score;
      const change = score - prev;
      const rating = score <= 25 ? "Extreme Fear" : score <= 44 ? "Fear" : score <= 55 ? "Neutral" : score <= 74 ? "Greed" : "Extreme Greed";
      const color = score <= 25 ? T.red : score <= 44 ? T.orange : score <= 55 ? T.yellow : score <= 74 ? "#86efac" : T.green;
      setFearGreed({ score, prev, change, rating, color, time: new Date().toLocaleTimeString() });
      _fearGreedData = { score, rating };
    } catch (e) { console.warn("Fear & Greed fetch failed:", e.message); }
  };
  const fetchEarnings = async (sym, existingState) => {
    try {
      const res = await fetch(`/api/earnings?symbol=${encodeURIComponent(sym)}`);
      if (!res.ok) return null;
      const json = await res.json();
      const earnings = json?.quoteSummary?.result?.[0]?.calendarEvents?.earnings;
      const dates = earnings?.earningsDate;
      if (!dates?.length) return null;
      const nextTs = dates[0]?.raw || dates[0];
      if (!nextTs) return null;
      const nextDate = new Date(nextTs * 1000);
      const daysAwayFromApi = Math.round((nextDate - Date.now()) / (1000 * 60 * 60 * 24));
      if (daysAwayFromApi < EARN_WINDOW_MIN || daysAwayFromApi > EARN_WINDOW_MAX) return null;
      const daysAway = existingState?.daysAway ?? daysAwayFromApi;
      const base = buildEarningsState(daysAway);
      if (base.daysAway == null) return null;
      const enriched = enrichEarningsState(base, {
        date: nextDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
      });
      return earningsStateToLegacyUI(enriched);
    } catch { return null; }
  };

  const fetchNews = async (sym) => {
    try {
      const res = await fetch(`/api/news?symbol=${sym}`);
      if (!res.ok) return null;
      const json = await res.json();
      if (!json?.articles?.length) return null;
      const avgScore = json.score ?? 0;

        let label, color;
        if (avgScore >= 0.15)       { label = "BULLISH";          color = T.green; }
        else if (avgScore >= 0.05)  { label = "SOMEWHAT BULLISH"; color = "#86efac"; }
        else if (avgScore <= -0.15) { label = "BEARISH";          color = T.red; }
        else if (avgScore <= -0.05) { label = "SOMEWHAT BEARISH"; color = T.orange; }
        else                        { label = "NEUTRAL";          color = T.textSub; }

        return { label, color, score: avgScore, articles: json.articles.slice(0, 6) };
    } catch (e) { console.warn("fetchNews failed:", e.message); return null; }
  };

  const scanningRef = useRef(false);
  const setStatus = (sym, patch) => setWatchlist(w => w.map(i => i.symbol === sym ? { ...i, ...patch } : i));

  const processQueue = useCallback(async (queue, options = {}) => {
    if (!queue.length || scanningRef.current) return;
    scanningRef.current = true;
    setScanning(true);
    let q = [...queue];
    const sectorAlertSyms = new Set();
    const sectorAlertMeta = {};
    const sectorAlertAccum = [];
    try {
      // Market Tide: optional market context; fetch once per scan (never blocks indefinitely)
      try {
        const existing = marketTideRef.current;
        const stale = !existing?.asOf || (Date.now() - existing.asOf) > 2 * 60 * 1000;
        if (!existing || stale) {
          console.log("[MarketTide] processQueue: fetching (stale=", stale, ")");
          const tide = await fetchMarketTide();
          marketTideRef.current = tide;
          setMarketTide(tide);
          console.log("[MarketTide] processQueue: setMarketTide", tide);
        }
      } catch {}

      await fetchSectorDashboard().catch(() => {});

      if (options.sectorAlerts) {
        setSectorAlertTickers([]);
        const wlSet = new Set(watchlist.map(i => String(i.symbol).toUpperCase()));
        const picks = pickSectorAlertTickers(
          sectorDashboardRef.current?.byEtf || _sectorDashboardCache?.byEtf || {},
          wlSet,
          readLastScanResultsCache(),
        );
        for (const pick of picks) {
          const sym = String(pick.symbol).toUpperCase();
          if (wlSet.has(sym)) continue;
          sectorAlertMeta[sym] = pick;
          if (!q.includes(sym)) {
            q.push(sym);
            sectorAlertSyms.add(sym);
          }
        }
      }

      let signalHistoryCache = (await load("signal_history")) || [];

      while (q.length) {
        const sym = q.shift(); setScanQueue([...q]);
        const isSectorAlert = sectorAlertSyms.has(sym);
        if (!isSectorAlert) setStatus(sym, { status: "loading" });
        try {
          const wlItem = watchlist.find(i => i.symbol === sym);
          const data = await fetchTicker(sym, wlItem?.result?.buyScore);
          let result = runAnalysis({ ...data, priorBuyScore: wlItem?.result?.buyScore, inPosition: !!wlItem?.inPosition }, spyReturnRef.current, isCrypto(sym), sym, marketTideRef.current);
          if (!result) {
            if (!isSectorAlert) setWatchlist(wl => wl.map(i => i.symbol === sym ? { ...i, status: "error" } : i));
            continue;
          }
          // If this was SPY, store its 63-day return for RS calculations
          if (sym === "SPY" && data.closes[63]) {
            spyReturnRef.current = ((data.closes[0] - data.closes[63]) / data.closes[63]) * 100;
          }
          result.scanTime = Date.now();
          const earningsLegacy = await fetchEarnings(sym, result.earningsState).catch(() => null);
          if (earningsLegacy) {
            result = {
              ...result,
              earnings: earningsLegacy,
              earningsState: enrichEarningsState(result.earningsState, { date: earningsLegacy.date }),
            };
          }
          result.openSignal = findOpenSignalForTicker(signalHistoryCache, sym, result);
          appendActionHistory(sym, result);
          // Auto-log actionable verdicts to performance tracker + anonymous accuracy table
          if (!isSectorAlert) {
            {
              const v = calcVerdict({ result, aiCaution: false });
              const penny = typeof result.price === "number" && result.price < 1;
              const loggable = !penny && v && (v.layer === "buy" || v.layer === "recov" || v.layer === "accum" || v.layer === "bearish" || (v.layer === "wait" && result.buyScore >= 9));
              if (loggable) {
                // Per-user signal log (existing)
                const existing = await load("signalLog") || [];
                const todayStr = new Date().toLocaleDateString();
                const alreadyLogged = existing.some(s => s.symbol === sym && s.logDate === todayStr);
                if (!alreadyLogged) {
                  const entry = {
                    id: Date.now() + Math.random(),
                    symbol: sym,
                    logDate: todayStr,
                    verdict: v.label,
                    verdictLayer: v.layer,
                    signal: result.actionResolved ?? resolveDisplayVerdict(result, { inPosition: !!wlItem?.inPosition, activeSignal: result?.activeSignal }).actionText ?? result.signal,
                    score: result.buyScore,
                    gate: result.gate?.label,
                    entryPrice: result.price,
                    stop: result.stopPrice ?? result.tradeStop ?? (result.atr ? result.price - 2 * result.atr : result.price * 0.92),
                    t1: result.t1,
                    t2: result.t2,
                    rsi: result.rsi,
                    macd: result.macd?.bullish ? "BULL" : "BEAR",
                    cmf: result.cmf,
                    squeeze: result.bb?.isSqueeze,
                    outcome: "OPEN",
                    outcomePct: null,
                    lastChecked: null,
                  };
                  await save("signalLog", [entry, ...existing.slice(0, 199)]);
                }
                // Anonymous aggregate accuracy table (new) — fire and forget
                result.symbol = sym;
                logSignalAccuracy(result, v).catch(() => {});
              }
            }
            fetchNews(sym).then(n => {
              if (n) setStatus(sym, { news: n });
            }).catch(() => {});
          }
          await mergeSignalHistoryAfterScan(sym, result).catch(() => {});
          if (wlItem?.inPosition) result.inPosition = true;
          await mergeScanLedgerAfterScan(sym, result).catch(() => {});
          signalHistoryCache = (await load("signal_history")) || [];
          result.openSignal = findOpenSignalForTicker(signalHistoryCache, sym, result);
          if (isSectorAlert) {
            const meta = sectorAlertMeta[sym] || {};
            sectorAlertAccum.push({
              symbol: sym,
              result,
              sectorName: result.sectorName || meta.sectorName,
              sectorEtf: result.sectorEtf || meta.sectorEtf,
              sectorStage: result.sectorStage || meta.sectorStage,
              sectorEmoji: meta.sectorEmoji || SECTOR_EMOJI_BY_ETF[result.sectorEtf] || "🔄",
            });
            try {
              const cached = JSON.parse(localStorage.getItem("lastScanResults") || "{}");
              cached[sym] = result;
              localStorage.setItem("lastScanResults", JSON.stringify(cached));
            } catch {}
          } else {
            setStatus(sym, { status: "done", result });
            // Keep drawer in sync if this ticker is currently open
            setWatchlistDrawer(prev => prev && prev.symbol === sym ? { ...prev, result } : prev);
            // Immediately write this result to localStorage as a fast local backup
            try {
              const cached = JSON.parse(localStorage.getItem("lastScanResults") || "{}");
              cached[sym] = result;
              localStorage.setItem("lastScanResults", JSON.stringify(cached));
              localStorage.setItem("lastScanTime", new Date().toLocaleTimeString());
            } catch {}
            // Puts snapshot — record RSI/price/CMF when puts signal first fires
            const hasNewPuts = result.putsSignal?.fullSignal || result.putsSignal?.partialSignal;
            const existingPutsSnap = getPutsSnapshot(sym);
            if (hasNewPuts && !existingPutsSnap) {
              setPutsSnapshot(sym, result.rsi, result.price, result.cmf);
            } else if (!hasNewPuts && existingPutsSnap) {
              clearPutsSnapshot(sym);
            }
            // Calls snapshot — record RSI/price/CMF when calls signal first fires
            const hasNewCalls = callsSignalDisplayActive(result);
            const existingCallsSnap = getCallsSnapshot(sym);
            if (hasNewCalls && !existingCallsSnap) {
              setCallsSnapshot(sym, result.rsi, result.price, result.cmf);
            } else if (!hasNewCalls && existingCallsSnap) {
              clearCallsSnapshot(sym);
            }
            // Update livePrice so price display reflects fresh scan immediately
            if (result.price) {
              setLivePrice(prev => ({ ...prev, [sym]: { price: result.price, changePct: result.pctChange, change: result.pctChange ? result.price * result.pctChange / 100 : 0, prevClose: null, ts: Date.now() } }));
            }
            // Auto Trader — check this ticker for exits/price updates on every scan

            // PRIME SETUP toast — fires regardless of eAPI
            {
              const v = calcVerdict({ result, aiCaution: false });
              if (isPrimeSetup(result, v)) {
                const vixLevel = v?.vixLevel ?? 0;
                const riskOff = vixLevel >= 20;
                toast(
                  `★ PRIME: ${sym} — R:R ≥ 2:1, Calmar ≥ 1.5, Gate GO${riskOff ? ` · Risk-off (VIX ${vixLevel.toFixed(0)}) — hold longer` : " · All layers aligned"}`,
                  "#f59e0b"
                );
              }
            }
            // Notifications
            if (eAPI) {
              const prev = await eAPI.getPreviousSignals();
              const sigs = []; let type = "change";
              if (result.signal.includes("BUY") && prev[sym]?.signal !== result.signal) { sigs.push(result.signal); type = "buy"; }
              if (result.sellSignal === "SELL NOW" || result.sellSignal === "TRIM") { sigs.push(result.sellSignal); type = "sell"; }
              if (result.bb?.isSqueeze && !prev[sym]?.squeeze) { sigs.push("BB Squeeze"); type = "squeeze"; }
              if (sigs.length) { eAPI.sendSignalAlert({ symbol: sym, price: result.price.toFixed(2), signals: sigs, type }); toast(`${sym}: ${sigs[0]}`, type === "buy" ? T.green : type === "sell" ? T.red : T.yellow); }
              await eAPI.setPreviousSignals({ ...prev, [sym]: { signal: resolveDisplayVerdict(result, { inPosition: !!wlItem?.inPosition, activeSignal: result?.activeSignal }).actionText ?? result.signal, sellSignal: result.sellSignal, squeeze: result.bb?.isSqueeze } });
            }
          }
        } catch (e) {
          if (!isSectorAlert) {
            if (e.message.includes("Invalid ticker") && e.message.includes("404")) {
              setWatchlist(w => w.filter(i => i.symbol !== sym));
              toast(`"${sym}" not found — removed`, T.red);
            } else {
              setStatus(sym, { status: "error", error: e.message });
              toast(`${sym}: ${e.message.slice(0, 60)}`, T.orange);
            }
          }
        }
        if (q.length) {
          let s = Math.ceil(SCAN_DELAY / 1000); setCountdown(s);
          await new Promise(r => { cdRef.current = setInterval(() => { s--; setCountdown(s); if (s <= 0) { clearInterval(cdRef.current); r(); } }, 1000); });
        }
      }
    } finally {
      scanningRef.current = false;
      const scanTime = new Date().toLocaleTimeString();
      setScanQueue([]); setScanning(false); setCountdown(0);
      setLastScan(scanTime);
      save("lastScanTime", scanTime);
      if (options.sectorAlerts) {
        const sortedAlerts = [...sectorAlertAccum]
          .sort((a, b) => (b.result?.buyScore ?? -999) - (a.result?.buyScore ?? -999))
          .slice(0, 9);
        setSectorAlertTickers(sortedAlerts);
        const banner = buildRotationScanBannerMessage(
          sectorDashboardRef.current?.byEtf || _sectorDashboardCache?.byEtf || {},
          sortedAlerts.length,
        );
        if (banner) {
          setRotationScanBanner(banner);
          setRotationScanBannerVisible(true);
        }
      }
      // Auto-generate briefing after full watchlist scan completes
      setTimeout(() => generateBriefing(watchlistRef.current, vix, fearGreed, { force: true }), 1500);
    }
  }, [fetchSectorDashboard, watchlist]);

  const addTickers = (symbols) => {
    const syms = symbols.filter(s => !watchlist.find(i => i.symbol === s));
    if (!syms.length) return;
    const items = syms.map(s => ({ symbol: s, position: { buyPrice: "", shares: "", dollarAmt: "", inputMode: "shares", stopPct: 8 }, status: "idle", result: null }));
    const next = [...watchlist, ...items];
    setWatchlist(next);
    saveEnabledRef.current = true; // user explicitly added — always save
    save("watchlist", next.map(({ symbol, position, inPosition }) => ({ symbol, position, inPosition })));
    toast(`${syms.length} ticker${syms.length > 1 ? "s" : ""} added — hit Scan All to load data`, T.cyan);
  };

  const addFromInput = () => { const s = tickerInput.toUpperCase().split(/[\s,]+/).map(x => x.trim()).filter(Boolean); addTickers(s); setTickerInput(""); };
  const hasKeys = () => true; // web mode — no Alpaca keys required
  const scanAll = () => {
    scanAllRef.current = scanAll;
    if (scanningRef.current) return;
    fetchVIX(); // refresh VIX at start of every scan
    fetchFearGreed();
    const q = watchlist.map(i => i.symbol);
    setScanQueue(q); setWatchlist(w => w.map(i => ({ ...i, status: "queued" }))); processQueue(q, { sectorAlerts: true });
  };

  const scanSectorPlays = (tickers) => {
    if (scanningRef.current) return;
    fetchVIX();
    fetchFearGreed();
    const syms = tickers.map(t => String(t).toUpperCase());
    const DEFAULT_POSITION = { buyPrice: "", shares: "", dollarAmt: "", inputMode: "shares", stopPct: 8 };
    setWatchlist(w => {
      let next = [...w];
      for (const s of syms) {
        const idx = next.findIndex(i => i.symbol === s);
        if (idx >= 0) next[idx] = { ...next[idx], status: "queued" };
        else next.push({ symbol: s, position: DEFAULT_POSITION, status: "queued", result: null });
      }
      saveEnabledRef.current = true;
      save("watchlist", next.map(({ symbol, position, inPosition }) => ({ symbol, position, inPosition })));
      return next;
    });
    setScanQueue(syms);
    processQueue(syms);
    toast(`Scanning ${syms.length} ${syms.length === 1 ? "ticker" : "tickers"}…`, T.cyan);
  };

  const analyzeFromScanner = (symbol, result) => {
    const sym = String(symbol).toUpperCase();
    const DEFAULT_POSITION = { buyPrice: "", shares: "", dollarAmt: "", inputMode: "shares", stopPct: 8 };
    setWatchlist(w => {
      const idx = w.findIndex(i => i.symbol === sym);
      let next;
      if (idx >= 0) {
        next = [...w];
        next[idx] = { ...next[idx], result, status: "done" };
      } else {
        next = [...w, { symbol: sym, position: DEFAULT_POSITION, status: "done", result }];
      }
      saveEnabledRef.current = true;
      save("watchlist", next.map(({ symbol: s, position, inPosition }) => ({ symbol: s, position, inPosition })));
      return next;
    });
    setWatchlistDrawer({ symbol: sym, result, position: DEFAULT_POSITION });
  };

  const launchSectorScanner = (_tickers, sectorName) => {
    setScannerPreset({
      ...SECTOR_SCAN_PRESET,
      sector: sectorName,
      autoRun: true,
    });
    setMainView("scanner");
    toast(`Scanner preset: ${sectorName} swing long + bottom setups`, T.cyan);
  };
  const marketTideStatusLabel = (tide) => {
    const s = tide?.breadthScore ?? 0;
    if (s >= 4) return "Strong Bull Tide";
    if (s >= 2) return "Bull Tide";
    if (s <= -4) return "Strong Bear Tide";
    if (s <= -2) return "Bear Tide";
    return "Neutral";
  };

  // Generate daily market briefing after watchlist scan
  const generateBriefing = async (wl, vixData, fgData, { force = false } = {}) => {
    const scanned = wl.filter(i => i.result);
    if (scanned.length < 3) return;
    const today = new Date().toLocaleDateString();
    if (!force && dailyBriefing?.date === today) return;
    setBriefingLoading(true);
    try {
      const regime = calcMarketRegime(vixData, fgData, wl);
      const top3Bull = [...scanned].filter(i => i.result.buyScore >= 4).sort((a,b) => b.result.buyScore - a.result.buyScore).slice(0, 3);
      const top3Bear = [...scanned].filter(i => i.result.buyScore <= -4).sort((a,b) => a.result.buyScore - b.result.buyScore).slice(0, 3);
      const prompt = `You are a senior market analyst opening the trading day for a retail trader. Give them a practical, plain-English briefing in 3-4 sentences. No jargon. No disclaimers. Talk like a knowledgeable friend who follows markets.

MARKET CONDITIONS:
- Regime: ${regime?.label ?? "Unknown"} — ${regime?.headline ?? ""}
- VIX: ${vixData?.value?.toFixed(1) ?? "N/A"} (${vixData?.rising ? "rising" : "falling or flat"})
- Fear & Greed: ${fgData?.value ?? "N/A"}/100

WATCHLIST SNAPSHOT (${scanned.length} tickers scanned):
- Bullish setups: ${top3Bull.map(i => `${i.symbol} (+${i.result.buyScore})`).join(", ") || "none"}
- Bearish setups: ${top3Bear.map(i => `${i.symbol} (${i.result.buyScore})`).join(", ") || "none"}
- Overall: ${scanned.filter(i => i.result.buyScore >= 4).length} bullish, ${scanned.filter(i => i.result.buyScore <= -4).length} bearish, ${scanned.filter(i => i.result.buyScore > -4 && i.result.buyScore < 4).length} neutral

Write a 3-sentence briefing covering: (1) what the market is doing right now, (2) what this means for their watchlist specifically, (3) one specific thing they should focus on or watch for today. Be direct and useful.`;
      const res = await fetch("/api/ai", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          ...(anthropicKeyRef.current ? { userApiKey: anthropicKeyRef.current } : {}),
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error.message || data.error || "AI error");
      const responseText = data.content?.[0]?.text || "";
      if (responseText) {
        setDailyBriefing({
          text: responseText,
          date: today,
          regime: regime?.id,
          generatedAt: new Date().toLocaleTimeString(),
          tickerCount: scanned.length,
          tideLabel: marketTideStatusLabel(marketTideRef.current),
        });
      }
    } catch {}
    setBriefingLoading(false);
  };

  const scanOne = async (sym) => {
    // Single ticker refresh — runs independently, doesn't use the shared queue lock
    setWatchlist(w => w.map(i => i.symbol === sym ? { ...i, status: "loading" } : i));
    try {
      const wlItem = watchlist.find(i => i.symbol === sym);
      const data = await fetchTicker(sym, wlItem?.result?.buyScore);
      // Keep tide fresh for manual refresh
      try {
        const tide = await fetchMarketTide();
        marketTideRef.current = tide;
        setMarketTide(tide);
      } catch {}
      let result = runAnalysis({ ...data, priorBuyScore: wlItem?.result?.buyScore, inPosition: !!wlItem?.inPosition }, spyReturnRef.current, isCrypto(sym), sym, marketTideRef.current);
      if (!result) { setWatchlist(w => w.map(i => i.symbol === sym ? { ...i, status: "error", error: "Analysis failed" } : i)); return; }
      result.scanTime = Date.now();
      const earningsLegacy = await fetchEarnings(sym, result.earningsState).catch(() => null);
      if (earningsLegacy) {
        result = {
          ...result,
          earnings: earningsLegacy,
          earningsState: enrichEarningsState(result.earningsState, { date: earningsLegacy.date }),
        };
      }
      await attachOpenSignalToResult(sym, result);
      fetchNews(sym).then(n => { if (n) setWatchlist(w => w.map(i => i.symbol === sym ? { ...i, news: n } : i)); }).catch(() => {});
      await mergeSignalHistoryAfterScan(sym, result).catch(() => {});
      if (wlItem?.inPosition) result.inPosition = true;
      await mergeScanLedgerAfterScan(sym, result).catch(() => {});
      await attachOpenSignalToResult(sym, result);
      setWatchlist(w => w.map(i => i.symbol === sym ? { ...i, result, status: "done", error: null } : i));
      // Update livePrice so the price display reflects the fresh scan immediately
      if (result.price) {
        setLivePrice(prev => ({ ...prev, [sym]: { price: result.price, changePct: result.pctChange, change: result.pctChange ? result.price * result.pctChange / 100 : 0, prevClose: result.closes?.[1] || null, ts: Date.now() } }));
      }
      // Save updated result to lastScanResults
      const prev = await load("lastScanResults") || {};
      await save("lastScanResults", { ...prev, [sym]: result });
      appendActionHistory(sym, result);
      setWatchlistDrawer(prevDrawer => prevDrawer && prevDrawer.symbol === sym ? { ...prevDrawer, result } : prevDrawer);
      // Auto Trader — check this ticker for exits/price updates
    } catch (e) {
      setWatchlist(w => w.map(i => i.symbol === sym ? { ...i, status: "error", error: e.message?.slice(0, 40) } : i));
    }
  };
  // Auto Trader hook — called after every scan result, checks for exits/updates on open positions
  const stopScan = () => {
    scanningRef.current = false;
    if (cdRef.current) { clearInterval(cdRef.current); cdRef.current = null; }
    setScanQueue([]);
    setScanning(false);
    setCountdown(0);
    setWatchlist(w => w.map(i => i.status === "queued" || i.status === "loading" ? { ...i, status: "idle" } : i));
    toast("Scan stopped", T.yellow);
  };
  const removeTicker = sym => setWatchlist(w => w.filter(i => i.symbol !== sym));
  const reorderTicker = (fromSortedIdx, toSortedIdx) => {
    if (fromSortedIdx === toSortedIdx) return;
    setWatchlist(w => {
      // Get the actual symbols from sorted order
      const sortedSymbols = sorted.map(i => i.symbol);
      const fromSymbol = sortedSymbols[fromSortedIdx];
      const toSymbol = sortedSymbols[toSortedIdx];
      if (!fromSymbol || !toSymbol) return w;
      // Reorder in the original watchlist
      const next = [...w];
      const fromIdx = next.findIndex(i => i.symbol === fromSymbol);
      const toIdx = next.findIndex(i => i.symbol === toSymbol);
      if (fromIdx === -1 || toIdx === -1) return w;
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  };
  const updatePos = (sym, pos) => setWatchlist(w => w.map(i => i.symbol === sym ? { ...i, position: pos } : i));
  const toggleInPosition = (sym) => setWatchlist(w => w.map(i => {
    if (i.symbol !== sym) return i;
    const inPosition = !i.inPosition;
    const result = i.result ? refreshPostMoveAssessment(i.result, { inPosition, price: i.result.price }) : i.result;
    return { ...i, inPosition, result };
  }));

  const manualExitActiveSignal = async (sym, reason = "User manually exited") => {
    try {
      const activeRaw = await load("active_signals") || [];
      const ticker = String(sym).toUpperCase();
      const list = Array.isArray(activeRaw) ? activeRaw.map(e => ({ ...e })) : [];
      const idx = list.findIndex(e => e.ticker === ticker);
      if (idx < 0) return;
      list[idx] = { ...list[idx], hard_exit_triggered: true, hard_exit_reason: reason };
      await save("active_signals", list);
      const active = list[idx];
      setWatchlist(w => w.map(i => {
        if (i.symbol !== sym || !i.result) return i;
        return {
          ...i,
          result: {
            ...i.result,
            activeSignal: active,
            activeSignalDisplay: buildActiveSignalDisplay(active, i.result),
          },
        };
      }));
    } catch (e) {
      console.warn("manualExitActiveSignal failed:", e?.message || e);
    }
  };

  const sorted = [...watchlist]
    .filter(i => {
      if (filter === "ALL") return true;
      if (filter === "BUY") return i.result?.signal?.includes("BUY");
      if (filter === "SELL") return i.result?.signal?.includes("SELL") || i.result?.sellSignal?.includes("SELL");
      if (filter === "SQUEEZE") return i.result?.bb?.isSqueeze;
      if (filter === "ALERTS") return i.result?.exits?.level !== "CLEAR" && i.result?.exits?.level != null;
      if (filter === "WATCH") return !!i.result?.earlyWarning?.phase1Any;
      if (filter === "SETUP") return !!i.result?.earlyWarning?.phase2Any;
      if (filter === "READY") {
        const c = i.result?.callsSignal;
        return !!((c?.fullSignal || c?.bouncePlayOnly) || i.result?.putsSignal?.fullSignal);
      }
      if (filter === "CALLS") {
        return matchCallsPhaseFilter(i.result, readyOnly);
      }
      if (filter === "PUTS") {
        return matchPutsPhaseFilter(i.result, readyOnly);
      }
      if (filter === "EMERGING") {
        const v = calcVerdict({ result: item.result, aiCaution: false });
        return v?.short === "EMERGING";
      }
            if (filter === "ACCUM") {
        const res = i.result;
        if (!res) return false;
        const accum52w = res.range52w?.score != null && res.range52w.score <= 25;
        const accumRsi = res.rsi != null && res.rsi <= 38;
        const accumCmf = res.cmf != null && res.cmf >= -0.08;
        const accumScore = res.buyScore >= -6;
        const accumDiv = res.rsiDiv?.bullish === true;
        const accumVol = res.volDiv?.bearish === false;
        return accum52w && accumRsi && accumCmf && accumScore && (accumDiv || accumVol);
      }
      return true;
    })
    .sort((a, b) => {
      // inPosition always pins to absolute top
      const aInPos = !!(a.inPosition), bInPos = !!(b.inPosition);
      if (aInPos && !bInPos) return -1;
      if (!aInPos && bInPos) return 1;
      if (!sortCol) return 0;
      // Special sort keys for calls/puts — full signal > partial > none, then by passed count
      if (sortCol === "callsScore") {
        const rank = (sig) => {
          if (!sig) return 0;
          if (sig.fullSignal) return 1000;
          if (sig.bouncePlayOnly) return 850;
          if (sig.partialSignal) return sig.passed || 0;
          return 0;
        };
        const sa = rank(a.result?.callsSignal);
        const sb = rank(b.result?.callsSignal);
        return (sb - sa) * sortDir;
      }
      if (sortCol === "putsScore") {
        const sa = a.result?.putsSignal?.fullSignal ? 1000 : a.result?.putsSignal?.partialSignal ? (a.result.putsSignal.passed || 0) : 0;
        const sb = b.result?.putsSignal?.fullSignal ? 1000 : b.result?.putsSignal?.partialSignal ? (b.result.putsSignal.passed || 0) : 0;
        return (sb - sa) * sortDir;
      }
      // Price column sorts by % change, not dollar value
      const col = sortCol === "price" ? "pctChange" : sortCol;
      return ((a.result?.[col] ?? -Infinity) - (b.result?.[col] ?? -Infinity)) * sortDir;
    });

  // Sector concentration analysis — warn when 3+ tickers in same sector
  const sectorConcentration = (() => {
    const counts = {};
    watchlist.forEach(i => {
      const etf = TICKER_SECTOR_MAP[i.symbol];
      if (etf) counts[etf] = (counts[etf] || []).concat(i.symbol);
    });
    const concentrated = Object.entries(counts).filter(([, syms]) => syms.length >= 3);
    return concentrated.map(([etf, syms]) => ({ etf, syms, count: syms.length }));
  })();

  const totalInvested = watchlist.reduce((s, i) => { const pos = i.position || {}; const bp = parseFloat(pos.buyPrice) || 0; const sh = pos.inputMode === "shares" ? (parseFloat(pos.shares) || 0) : bp ? (parseFloat(pos.dollarAmt) || 0) / bp : 0; return s + sh * bp; }, 0);
  const totalValue = watchlist.reduce((s, i) => { const pos = i.position || {}; const bp = parseFloat(pos.buyPrice) || 0; const sh = pos.inputMode === "shares" ? (parseFloat(pos.shares) || 0) : bp ? (parseFloat(pos.dollarAmt) || 0) / bp : 0; return s + sh * (i.result?.price || bp); }, 0);
  const totalPnl = totalValue - totalInvested;
  const totalPnlPct = totalInvested > 0 ? (totalPnl / totalInvested) * 100 : 0;

  const TH = ({ label, col, title }) => {
    const activeTitle = hintsOn ? title : undefined;
    return (
    <th title={activeTitle} onClick={() => {
      if (!col) return;
      if (sortCol === col) {
        setSortDir(d => d === 1 ? -1 : 1);
      } else {
        setSortCol(col);
        setSortDir(-1);
      }
    }}
      style={{ padding: "7px 8px", fontSize: F.xs, fontWeight: 700, color: sortCol === col ? T.cyan : T.textDim, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center", whiteSpace: "nowrap", cursor: activeTitle ? "help" : col ? "pointer" : "default", borderBottom: `2px solid ${T.border}`, background: T.surface, userSelect: "none" }}>
      {label}{col && sortCol === col ? (sortDir === -1 ? " ▼" : " ▲") : ""}{hintsOn && title && !col ? <span style={{ opacity: 0.4, fontSize: 9, marginLeft: 2 }}>?</span> : ""}
    </th>
    );
  };

  const btnBase = { fontSize: F.sm, fontFamily: "inherit", cursor: "pointer", borderRadius: 6, fontWeight: 600, border: "none", transition: "opacity 0.15s" };

  // Auth gate — show login screen if not authenticated
  if (authLoading) return (
    <div style={{ height: "100vh", background: T.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 40, height: 40, borderRadius: "50%", border: `3px solid #7c3aed`, borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (!user) return <LoginScreen />;

  return (
    <>
    <style>{makeGlobalCSS(isDark)}</style>
    {marketDataUnavailableBanner && (
      <div style={{
        padding: "8px 16px",
        background: `${T.orange}18`,
        borderBottom: `1px solid ${T.orange}44`,
        color: T.orange,
        fontSize: 12,
        fontWeight: 700,
        textAlign: "center",
        position: "sticky",
        top: 0,
        zIndex: 500,
      }}>
        Market data temporarily unavailable — using cached data
      </div>
    )}
    {isMobile ? (
      <MobileWatchlistView
        watchlist={watchlist}
        onScan={scanOne}
        onScanAll={scanAll}
        scanning={scanning}
        onAddTicker={sym => addTickers([sym])}
        onRemove={removeTicker}
        isDark={isDark}
        onToggleTheme={toggleTheme}
        anthropicKey={anthropicKey}
        openaiKey={openaiKey}
        geminiKey={geminiKey}
        aiProvider={aiProvider}
        proMode={proMode}
        onTogglePosition={toggleInPosition}
        colVis={colVis}
        toggleColVis={toggleColVis}
        COL_DEFS={COL_DEFS}
        livePrice={livePrice}
      />
    ) : (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column", background: T.bg, color: T.text, fontFamily: "'Inter', system-ui, sans-serif", overflow: "hidden", position: "relative" }}>

      {tourStep !== null && (
        <TourOverlay
          step={tourStep}
          total={TOUR_STEPS.length}
          onNext={() => {
            if (tourStep >= TOUR_STEPS.length - 1) {
              setTourStep(null);
              save("tourComplete", true);
            } else {
              setTourStep(s => s + 1);
            }
          }}
          onSkip={() => { setTourStep(null); save("tourComplete", true); }}
        />
      )}
      {showAdminDashboard && isAdmin && (
        <AdminSignalDashboard
          T={T}
          F={F}
          onClose={() => { try { window.location.hash = ""; } catch {} setShowAdminDashboard(false); }}
        />
      )}
      {showScheduler && <SchedulerPanel onClose={() => setShowScheduler(false)} />}

      {showDisclaimer && <DisclaimerModal onAccept={() => { save("disclaimerAccepted", true); setShowDisclaimer(false); }} />}
      {showOnboarding && <OnboardingModal
        user={user}
        onComplete={({ aiKey, aiProvider: prov }) => {
          if (aiKey) {
            if (prov === "anthropic") { setAnthropicKey(aiKey); anthropicKeyRef.current = aiKey; save("anthropicKey", aiKey); }
            if (prov === "openai") { setOpenaiKey(aiKey); openaiKeyRef.current = aiKey; save("openaiKey", aiKey); }
            if (prov === "gemini") { setGeminiKey(aiKey); geminiKeyRef.current = aiKey; save("geminiKey", aiKey); }
            setAiProvider(prov); save("aiProvider", prov);
          }
          save("onboardingComplete", true);
          setShowOnboarding(false);
          setTimeout(() => setTourStep(0), 800); // start tour after onboarding
          setTimeout(fetchVIX, 300);
          setTimeout(fetchFearGreed, 500);
          toast(`Welcome to Auctus Analytics, ${user?.user_metadata?.full_name?.split(" ")[0] || ""}! Scanning your watchlist...`, T.green);
        }}
      />}

      {showSettings && <SettingsPanel
        anthropicKey={anthropicKey}
        openaiKey={openaiKey}
        geminiKey={geminiKey}
        aiProvider={aiProvider}
        onSave={(aKey, oKey, gKey, provider) => {
          if (aKey) { setAnthropicKey(aKey); anthropicKeyRef.current = aKey; save("anthropicKey", aKey); }
          if (oKey) { setOpenaiKey(oKey); openaiKeyRef.current = oKey; save("openaiKey", oKey); }
          if (gKey) { setGeminiKey(gKey); geminiKeyRef.current = gKey; save("geminiKey", gKey); }
          setAiProvider(provider); save("aiProvider", provider);
          setShowSettings(false);
          scanningRef.current = false;
          if (cdRef.current) { clearInterval(cdRef.current); cdRef.current = null; }
          setScanQueue([]); setScanning(false); setCountdown(0);
          setWatchlist(w => w.map(i => i.status === "queued" || i.status === "loading" ? { ...i, status: "idle" } : i));
          toast("Settings saved ✓ — ready to scan", T.green);
        }}
        onClose={() => setShowSettings(false)}
      />}
      {showHelp && <HelpPanel onClose={() => setShowHelp(false)} initialTab={helpInitialTab} />}
      {showSignalGuide && <SignalGuidePanel T={T} onClose={() => setShowSignalGuide(false)} />}

      {/* TOAST NOTIFICATIONS */}
      <div style={{ position: "fixed", top: 20, right: 20, zIndex: 200, display: "flex", flexDirection: "column", gap: 8 }}>
        {toasts.map(t => (
          <div key={t.id} style={{ padding: "12px 18px", background: T.surface, border: `1px solid ${t.color}66`, borderLeft: `4px solid ${t.color}`, borderRadius: 10, fontSize: F.base, color: t.color, maxWidth: 320, animation: "fadeIn 0.2s ease", boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
            {t.msg}
          </div>
        ))}
      </div>

      {/* ── SLIDE-OUT SIDEBAR ── */}
      <div
        onMouseEnter={() => setSidebarExpanded(true)}
        onMouseLeave={() => setSidebarExpanded(false)}
        style={{ position: "fixed", left: 0, top: 0, bottom: 0, zIndex: 100,
          width: sidebarExpanded ? 200 : 48,
          transition: "width 0.2s ease",
          background: T.surface, borderRight: `1px solid ${T.border}`,
          display: "flex", flexDirection: "column", overflow: "hidden",
          boxShadow: sidebarExpanded ? "4px 0 20px rgba(0,0,0,0.15)" : "none",
        }}>

        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 10px", borderBottom: `1px solid ${T.border}`, flexShrink: 0, overflow: "hidden", minHeight: 52 }}>
          <div style={{ width: 28, height: 28, background: `linear-gradient(135deg, #6b21a8, #7c3aed)`, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2 10 L7 3 L12 10" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </div>
          {sidebarExpanded && (
            <div style={{ opacity: sidebarExpanded ? 1 : 0, transition: "opacity 0.15s", whiteSpace: "nowrap" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: T.purple, letterSpacing: "0.06em" }}>AUCTUS</div>
              <div style={{ fontSize: 9, color: T.textDim, letterSpacing: "0.1em" }}>ANALYTICS</div>
            </div>
          )}
        </div>

        {/* Nav items */}
        <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "8px 0" }}>

          {/* Main views */}
          {[
            { id: "watchlist",   icon: "⊞", label: "Watchlist",       badge: watchlist.length || null, badgeColor: T.purple },
            { id: "scanner",     icon: "◎", label: "Scanner",         badge: "LIVE", badgeColor: T.green },
            { id: "crypto",      icon: "₿", label: "Crypto",          badge: "NEW", badgeColor: T.yellow },
            { id: "sectors",     icon: "🔄", label: "Sectors",         badge: Object.values(sectorDashboard?.byEtf || {}).filter(s => s.stageKey === STAGE_KEYS.HOT || s.stageKey === STAGE_KEYS.ROTATING).length || null, badgeColor: T.green },
            { id: "positions",   icon: "📍", label: "Open Positions",  badge: watchlist.filter(i => i.inPosition).length || null, badgeColor: T.cyan },
          ].map(({ id, icon, label, badge, badgeColor }) => (
            <div key={id} onClick={() => setMainView(id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", cursor: "pointer", borderRadius: 6, margin: "1px 4px",
                background: mainView === id ? `${T.purple}18` : "transparent",
                transition: "background 0.1s", overflow: "hidden" }}
              onMouseEnter={e => { if (mainView !== id) e.currentTarget.style.background = T.surfaceHi; }}
              onMouseLeave={e => { e.currentTarget.style.background = mainView === id ? `${T.purple}18` : "transparent"; }}>
              <span style={{ fontSize: 15, flexShrink: 0, color: mainView === id ? T.purple : T.textSub, width: 18, textAlign: "center" }}>{icon}</span>
              {sidebarExpanded && <>
                <span style={{ fontSize: 12, fontWeight: mainView === id ? 700 : 400, color: mainView === id ? T.purple : T.textSub, whiteSpace: "nowrap", flex: 1 }}>{label}</span>
                {badge && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 8, background: `${badgeColor}22`, color: badgeColor, fontWeight: 700, flexShrink: 0 }}>{badge}</span>}
              </>}
            </div>
          ))}

          <div style={{ height: 1, background: T.border, margin: "6px 8px" }} />

          {/* Analysis panels */}
          {[
            { id: "portfolio",   icon: "💼", label: "Portfolio" },
            { id: "news",        icon: "📰", label: "News", badge: globalNewsFeed.length > 0 ? globalNewsFeed.length : null, badgeColor: T.cyan },
            { id: "performance", icon: "📊", label: "Performance" },
            { id: "journal",     icon: "📋", label: "My Trades" },
            { id: "backtest",    icon: "📈", label: "Backtester" },
            { id: "playbook",    icon: "📚", label: "Playbook" },
          ].map(({ id, icon, label, badge, badgeColor }) => (
            <div key={id} onClick={() => openDrawerTab(id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 10px", cursor: "pointer", borderRadius: 6, margin: "1px 4px",
                background: drawerOpen && drawerTab === id ? `${T.cyan}15` : "transparent",
                transition: "background 0.1s", overflow: "hidden" }}
              onMouseEnter={e => { if (!(drawerOpen && drawerTab === id)) e.currentTarget.style.background = T.surfaceHi; }}
              onMouseLeave={e => { e.currentTarget.style.background = drawerOpen && drawerTab === id ? `${T.cyan}15` : "transparent"; }}>
              <span style={{ fontSize: 14, flexShrink: 0, width: 18, textAlign: "center" }}>{icon}</span>
              {sidebarExpanded && <>
                <span style={{ fontSize: 12, color: drawerOpen && drawerTab === id ? T.cyan : T.textSub, whiteSpace: "nowrap", flex: 1 }}>{label}</span>
                {badge && <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 8, background: `${badgeColor}22`, color: badgeColor, fontWeight: 700, flexShrink: 0 }}>{badge}</span>}
              </>}
            </div>
          ))}
        </div>

        {/* Bottom — settings, guide, theme, user */}
        <div style={{ borderTop: `1px solid ${T.border}`, padding: "6px 0", flexShrink: 0 }}>
          <div onClick={toggleTheme} title={isDark ? "Light mode" : "Dark mode"}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", cursor: "pointer", borderRadius: 6, margin: "1px 4px", overflow: "hidden" }}
            onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <span style={{ fontSize: 14, width: 18, textAlign: "center", flexShrink: 0 }}>{isDark ? "☀" : "🌙"}</span>
            {sidebarExpanded && <span style={{ fontSize: 12, color: T.textSub, whiteSpace: "nowrap" }}>{isDark ? "Light mode" : "Dark mode"}</span>}
          </div>
          <div onClick={() => setShowScheduler(true)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", cursor: "pointer", borderRadius: 6, margin: "1px 4px", overflow: "hidden" }}
            onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <span style={{ fontSize: 14, width: 18, textAlign: "center", flexShrink: 0 }}>⏰</span>
            {sidebarExpanded && <span style={{ fontSize: 12, color: T.textSub, whiteSpace: "nowrap" }}>Scheduler</span>}
          </div>
          {isAdmin && (
            <div onClick={() => { window.location.hash = "#admin-signals"; }}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", cursor: "pointer", borderRadius: 6, margin: "1px 4px", overflow: "hidden" }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <span style={{ fontSize: 14, width: 18, textAlign: "center", flexShrink: 0 }}>📊</span>
              {sidebarExpanded && <span style={{ fontSize: 12, color: T.textSub, whiteSpace: "nowrap" }}>Signal Analytics</span>}
            </div>
          )}
          <div onClick={() => setShowSettings(true)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", cursor: "pointer", borderRadius: 6, margin: "1px 4px", overflow: "hidden" }}
            onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <span style={{ fontSize: 14, width: 18, textAlign: "center", flexShrink: 0 }}>⚙</span>
            {sidebarExpanded && <span style={{ fontSize: 12, color: T.textSub, whiteSpace: "nowrap" }}>Settings</span>}
          </div>
          <div onClick={() => setShowSignalGuide(true)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", cursor: "pointer", borderRadius: 6, margin: "1px 4px", overflow: "hidden" }}
            onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <span style={{ fontSize: 14, width: 18, textAlign: "center", flexShrink: 0 }}>📡</span>
            {sidebarExpanded && <span style={{ fontSize: 12, color: T.textSub, whiteSpace: "nowrap" }}>Signal Guide</span>}
          </div>
          <div onClick={() => setShowHelp(true)}
            style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", cursor: "pointer", borderRadius: 6, margin: "1px 4px", overflow: "hidden" }}
            onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
            <span style={{ fontSize: 14, width: 18, textAlign: "center", flexShrink: 0 }}>📖</span>
            {sidebarExpanded && <span style={{ fontSize: 12, color: T.textSub, whiteSpace: "nowrap" }}>Guide</span>}
          </div>
          {user && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", overflow: "hidden", margin: "1px 4px" }}>
              {user.user_metadata?.avatar_url
                ? <img src={user.user_metadata.avatar_url} alt="" style={{ width: 22, height: 22, borderRadius: "50%", border: `1px solid ${T.border}`, flexShrink: 0 }} />
                : <div style={{ width: 22, height: 22, borderRadius: "50%", background: `${T.purple}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700, color: T.purple, flexShrink: 0 }}>
                    {(user.user_metadata?.full_name || user.email || "?")[0].toUpperCase()}
                  </div>
              }
              {sidebarExpanded && (
                <div style={{ flex: 1, overflow: "hidden", minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: T.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user.user_metadata?.full_name?.split(" ")[0] || user.email}</div>
                  <div onClick={() => supabase.auth.signOut()} style={{ fontSize: 10, color: T.red, cursor: "pointer" }}>Sign out</div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── CONTENT AREA — offset by sidebar icon width ── */}
      <div style={{ flexShrink: 0, marginLeft: 48 }}>

        {vix && (() => {
          const scanned = watchlist.filter(i => i.result);
          const deathCrossPct = scanned.length >= 3 ? scanned.filter(i => i.result?.deathCross).length / scanned.length : 0;
          const goldenCrossPct = scanned.length >= 3 ? scanned.filter(i => i.result?.goldenCross).length / scanned.length : 0;
          const bearSignals = [
            vix?.value > 25,
            fearGreed?.score <= 30,
            deathCrossPct > 0.4,
          ].filter(Boolean).length;
          const bullSignals = [
            vix?.value != null && vix.value < 18,
            fearGreed?.score >= 55,
            goldenCrossPct > 0.5,
          ].filter(Boolean).length;
          const isBear = bearSignals >= 2;
          const isBull = bullSignals >= 2 && !isBear;
          if (!isBear && !isBull) return null;
          const bearReasons = [
            vix?.value > 25 ? `VIX ${vix.value.toFixed(1)}` : null,
            fearGreed?.score <= 30 ? `F&G ${fearGreed.score}` : null,
            deathCrossPct > 0.4 ? `${Math.round(deathCrossPct * 100)}% bearish 200 SMA` : null,
          ].filter(Boolean).join(' · ');
          const bullReasons = [
            vix?.value < 18 ? `VIX ${vix.value.toFixed(1)}` : null,
            fearGreed?.score >= 55 ? `F&G ${fearGreed.score}` : null,
            goldenCrossPct > 0.5 ? `${Math.round(goldenCrossPct * 100)}% golden crosses` : null,
          ].filter(Boolean).join(' · ');
          return (
            <div style={{ width: "100%", boxSizing: "border-box", borderBottom: `1px solid ${T.border}`, background: T.surface }}>
              {isBear ? (
                <div onClick={() => openHelp('bear')}
                  title={`Bear market detected: ${bearReasons}. Click for survival guide.`}
                  style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 10, padding: "6px 14px", width: "100%", cursor: "pointer", background: `${T.red}08` }}>
                  <div style={{ fontSize: 12 }}>🐻</div>
                  <div style={{ fontSize: 9, fontWeight: 800, color: T.red, letterSpacing: "0.04em" }}>BEAR</div>
                  <div style={{ fontSize: 8, color: T.red, opacity: 0.7 }}>Guide →</div>
                </div>
              ) : (
                <div onClick={() => openHelp('bull')}
                  title={`Bull market confirmed: ${bullReasons}. Click for bull market playbook.`}
                  style={{ display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center", gap: 10, padding: "6px 14px", width: "100%", cursor: "pointer", background: `${T.green}08` }}>
                  <div style={{ fontSize: 12 }}>🐂</div>
                  <div style={{ fontSize: 9, fontWeight: 800, color: T.green, letterSpacing: "0.04em" }}>BULL</div>
                  <div style={{ fontSize: 8, color: T.green, opacity: 0.7 }}>Guide →</div>
                </div>
              )}
            </div>
          );
        })()}

        {mainView === "watchlist" && (() => {
          console.log("[MarketTide] banner render", marketTide);
          if (!marketTide) {
            return (
              <div style={{ width: "100%", boxSizing: "border-box", borderBottom: `1px solid ${T.border}`, background: T.surface, padding: "6px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <div style={{ padding: "3px 10px", borderRadius: 7, background: `${T.border}22`, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.border}`, color: T.textDim, fontSize: 10, fontWeight: 800, whiteSpace: "nowrap" }}>
                  MARKET DATA UNAVAILABLE
                </div>
                {watchlist.length > 0 && (
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                    {sectorAlertTickers.length > 0 && (
                      <button
                        onClick={() => { setSectorAlertsCollapsed(false); setTimeout(() => sectorAlertsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
                        style={{ padding: "5px 10px", background: `${T.green}18`, border: `1px solid ${T.green}55`, color: T.green, borderRadius: 7, fontSize: 11, fontFamily: "inherit", cursor: "pointer", fontWeight: 800, whiteSpace: "nowrap" }}
                      >
                        🔄 {sectorAlertTickers.length} Sector Alert{sectorAlertTickers.length !== 1 ? "s" : ""}
                      </button>
                    )}
                    <button onClick={() => setShowSettings(true)} style={{ padding: "5px 10px", background: `${T.blue}18`, border: `1px solid ${T.blue}44`, color: T.blue, borderRadius: 7, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>⚙</button>
                    <button onClick={scanAll} disabled={scanning || !watchlist.length}
                      style={{ padding: "7px 18px", background: "#F5C518", border: "none", color: "#111827", borderRadius: 8, fontSize: 13, fontWeight: 800, fontFamily: "inherit", cursor: scanning ? "not-allowed" : "pointer", opacity: scanning ? 0.65 : 1, whiteSpace: "nowrap" }}>
                      {scanning ? `Scanning${countdown > 0 ? ` ${countdown}s` : "..."}` : `Scan (${watchlist.length})`}
                    </button>
                  </div>
                )}
              </div>
            );
          }
          const s = marketTide?.breadthScore ?? 0;
          const label =
            s >= 4 ? "🌊 STRONG BULL TIDE" :
            s >= 2 ? "📈 BULL TIDE" :
            s <= -4 ? "🌊 STRONG BEAR TIDE" :
            s <= -2 ? "📉 BEAR TIDE" :
            "➡ NEUTRAL";
          const c =
            s >= 4 ? T.green :
            s >= 2 ? "#86efac" :
            s <= -4 ? T.red :
            s <= -2 ? "#fca5a5" :
            T.textDim;
          const bg =
            s >= 4 ? `${T.green}18` :
            s >= 2 ? "#86efac22" :
            s <= -4 ? `${T.red}18` :
            s <= -2 ? "#fca5a522" :
            `${T.border}22`;
          const borderLeftColor =
            s >= 4 ? T.green :
            s >= 2 ? "#86efac" :
            s <= -4 ? T.red :
            s <= -2 ? "#fca5a5" :
            T.border;
          return (
            <div style={{ width: "100%", boxSizing: "border-box", borderBottom: `1px solid ${T.border}`, background: T.surface, padding: "6px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <HoverTip content={hintsOn ? (vix?.impliedMove?.tooltip || (marketTide.vix != null ? vixImpliedDailyMove(marketTide.vix)?.tooltip : undefined)) : undefined}>
              <div style={{ marginLeft: 0, padding: "4px 10px", borderRadius: 7, background: bg, border: `1px solid ${c}44`, borderLeft: `3px solid ${borderLeftColor}`, color: c, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap", cursor: vix?.impliedMove ? "help" : "default" }}>
                {label} · SPY {marketTide.spyPct != null ? `${marketTide.spyPct >= 0 ? "+" : ""}${marketTide.spyPct.toFixed(1)}%` : "—"} · QQQ {marketTide.qqqPct != null ? `${marketTide.qqqPct >= 0 ? "+" : ""}${marketTide.qqqPct.toFixed(1)}%` : "—"} · VIX {marketTide.vix != null ? marketTide.vix.toFixed(1) : vix?.value?.toFixed(1) ?? "—"}
                {vix?.impliedMove ? ` · SPY ±${vix.impliedMove.oneSigmaPct.toFixed(2)}% day` : ""} · {s > 0 ? "+" : ""}{s}
              </div>
              </HoverTip>

              {mainView === "watchlist" && watchlist.length > 0 && (
                <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                  {sectorAlertTickers.length > 0 && (
                    <button
                      onClick={() => { setSectorAlertsCollapsed(false); setTimeout(() => sectorAlertsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
                      style={{ padding: "5px 10px", background: `${T.green}18`, border: `1px solid ${T.green}55`, color: T.green, borderRadius: 7, fontSize: 11, fontFamily: "inherit", cursor: "pointer", fontWeight: 800, whiteSpace: "nowrap" }}
                    >
                      🔄 {sectorAlertTickers.length} Sector Alert{sectorAlertTickers.length !== 1 ? "s" : ""}
                    </button>
                  )}
                  <button onClick={() => setShowSettings(true)} style={{ padding: "5px 10px", background: `${T.blue}18`, border: `1px solid ${T.blue}44`, color: T.blue, borderRadius: 7, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>⚙</button>
                  <button onClick={scanAll} disabled={scanning || !watchlist.length}
                    style={{ padding: "7px 18px", background: "#F5C518", border: "none", color: "#111827", borderRadius: 8, fontSize: 13, fontWeight: 800, fontFamily: "inherit", cursor: scanning ? "not-allowed" : "pointer", opacity: scanning ? 0.65 : 1, whiteSpace: "nowrap" }}>
                    {scanning ? `Scanning${countdown > 0 ? ` ${countdown}s` : "..."}` : `Scan (${watchlist.length})`}
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {mainView === "watchlist" && rotationScanBannerVisible && rotationScanBanner && (
          <div style={{
            width: "100%",
            boxSizing: "border-box",
            borderBottom: `1px solid ${T.green}44`,
            background: `${T.green}10`,
            padding: "8px 14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
          }}>
            <div style={{ fontSize: 11, color: T.text, lineHeight: 1.5, fontWeight: 600 }}>
              {rotationScanBanner}
            </div>
            <button
              onClick={() => setRotationScanBannerVisible(false)}
              style={{ background: "none", border: "none", color: T.textSub, fontSize: 16, cursor: "pointer", flexShrink: 0 }}
              title="Dismiss"
            >
              ×
            </button>
          </div>
        )}

        {mainView === "watchlist" && signalGuideHint && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", background: `${T.purple}10`, borderBottom: `1px solid ${T.purple}33`, fontSize: 11, color: T.textSub }}>
            <span>New: hover watchlist cells for signal definitions, or open the <strong style={{ color: T.purple }}>Signal Guide</strong> (sidebar or filter bar).</span>
            <button type="button" onClick={() => { dismissSignalGuideHint(); setShowSignalGuide(true); }} style={{ padding: "4px 10px", background: `${T.purple}22`, border: `1px solid ${T.purple}55`, color: T.purple, borderRadius: 6, fontSize: 10, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>Open guide</button>
            <button type="button" onClick={dismissSignalGuideHint} style={{ marginLeft: "auto", background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: 16 }} aria-label="Dismiss">×</button>
          </div>
        )}

        {/* FILTER BAR — watchlist only */}
        {mainView === "watchlist" && <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", borderBottom: `1px solid ${T.border}`, background: T.surface, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 0, flex: "0 0 auto" }}>
            <input type="text" value={tickerInput} placeholder="Add ticker (e.g. NVDA, TSLA)" className="watchlist-add-ticker-input"
              onChange={e => setTickerInput(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && addFromInput()}
              style={{ width: 247, background: T.bg, border: `1px solid ${T.borderHi}`, borderRight: "none", color: T.cyan, padding: "9px 14px", fontSize: 14, fontFamily: "'JetBrains Mono', monospace", borderRadius: "6px 0 0 6px", letterSpacing: "0.04em", boxShadow: `0 0 0 1px ${T.cyan}22, 0 0 14px ${T.cyan}14` }} />
            <button onClick={addFromInput} style={{ padding: "9px 16px", background: `linear-gradient(135deg, ${T.cyan}, ${T.green})`, color: "#fff", borderRadius: "0 6px 6px 0", fontSize: 13, fontWeight: 600, border: "none", cursor: "pointer" }}>+ Add</button>
          </div>
          <div style={{ position: "relative" }}>
            <button onClick={() => setShowSectors(s => !s)} style={{ padding: "4px 9px", background: showSectors ? `${T.cyan}22` : T.surfaceHi, border: `1px solid ${showSectors ? T.cyan : T.border}`, color: showSectors ? T.cyan : T.textSub, fontSize: 11, borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>+ Discover ▾</button>
            {showSectors && (
              <div style={{ position: "absolute", top: "110%", left: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, zIndex: 50, minWidth: 320, padding: 8, boxShadow: "0 8px 32px rgba(0,0,0,0.5)" }}>
                <div style={{ padding: "6px 10px 8px", fontSize: 10, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em" }}>ADD BY SECTOR</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  {Object.entries(SECTORS).map(([name, tickers]) => (
                    <button key={name} onClick={() => { addTickers(tickers); setShowSectors(false); }} style={{ display: "block", textAlign: "left", padding: "5px 10px", background: "none", border: "none", color: T.text, fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 6 }}
                      onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
                      onMouseLeave={e => e.currentTarget.style.background = "none"}>
                      {name} <span style={{ color: T.textDim }}>({tickers.length})</span>
                    </button>
                  ))}
                </div>
                <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 6, padding: "6px 8px 2px" }}>
                  <button onClick={() => { fetchMovers(); setShowSectors(false); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "5px 10px", background: "none", border: "none", color: T.orange, fontSize: 11, fontFamily: "inherit", cursor: "pointer", borderRadius: 6, fontWeight: 600 }}>🔥 Top Movers</button>
                </div>
              </div>
            )}
          </div>
          <div style={{ width: 1, height: 18, background: T.border }} />
          <div style={{ display: "flex", gap: 3, alignItems: "center" }}>
            {["ALL", "WATCH", "SETUP", "READY", "BUY", "SELL", "SQUEEZE", "ACCUM"].map(f => (
              <button key={f} onClick={() => { setFilter(f); setShowWatchlistAnalysis(null); }} style={{ padding: "3px 8px", background: filter === f ? (f === "ACCUM" ? `${T.cyan}22` : T.borderHi) : "transparent", border: `1px solid ${filter === f ? (f === "ACCUM" ? T.cyan : T.borderHi) : "transparent"}`, color: filter === f ? (f === "ACCUM" ? T.cyan : T.text) : T.textDim, fontSize: 10, borderRadius: 5, cursor: "pointer", fontFamily: "inherit" }}>
                {f === "ACCUM" ? "📦 ACCUM" : f === "WATCH" ? "👁 WATCH" : f === "SETUP" ? "⚡ SETUP" : f === "READY" ? "🚀 READY" : f === "SQUEEZE" ? BB_TIGHT_BANDS_SHORT : f}
              </button>
            ))}
            {/* CALLS split button */}
            <div style={{ display: "flex", gap: 0 }}>
              <button onClick={() => { setFilter("CALLS"); setShowWatchlistAnalysis(null); }}
                style={{ padding: "3px 8px", background: filter === "CALLS" && !showWatchlistAnalysis ? `${T.green}33` : "transparent", border: `1px solid ${filter === "CALLS" && !showWatchlistAnalysis ? T.green : "transparent"}`, borderRight: "none", color: filter === "CALLS" && !showWatchlistAnalysis ? T.green : T.textDim, fontSize: 10, borderRadius: "5px 0 0 5px", cursor: "pointer", fontFamily: "inherit", fontWeight: filter === "CALLS" && !showWatchlistAnalysis ? 700 : 400 }}>
                ⚡ CALLS
              </button>
              <button onClick={() => { setFilter("CALLS"); setShowWatchlistAnalysis("CALLS"); }}
                title="Open Calls Analysis for your watchlist"
                style={{ padding: "3px 7px", background: showWatchlistAnalysis === "CALLS" ? `${T.green}33` : `${T.green}12`, border: `1px solid ${showWatchlistAnalysis === "CALLS" ? T.green : T.green + "55"}`, color: T.green, fontSize: 10, borderRadius: "0 5px 5px 0", cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>
                📊
              </button>
            </div>
            {/* PUTS split button */}
            <div style={{ display: "flex", gap: 0 }}>
              <button onClick={() => { setFilter("PUTS"); setShowWatchlistAnalysis(null); }}
                style={{ padding: "3px 8px", background: filter === "PUTS" && !showWatchlistAnalysis ? `${T.red}33` : "transparent", border: `1px solid ${filter === "PUTS" && !showWatchlistAnalysis ? T.red : "transparent"}`, borderRight: "none", color: filter === "PUTS" && !showWatchlistAnalysis ? T.red : T.textDim, fontSize: 10, borderRadius: "5px 0 0 5px", cursor: "pointer", fontFamily: "inherit", fontWeight: filter === "PUTS" && !showWatchlistAnalysis ? 700 : 400 }}>
                🔻 PUTS
              </button>
              <button onClick={() => { setFilter("PUTS"); setShowWatchlistAnalysis("PUTS"); }}
                title="Open Puts Analysis for your watchlist"
                style={{ padding: "3px 7px", background: showWatchlistAnalysis === "PUTS" ? `${T.red}33` : `${T.red}12`, border: `1px solid ${showWatchlistAnalysis === "PUTS" ? T.red : T.red + "55"}`, color: T.red, fontSize: 10, borderRadius: "0 5px 5px 0", cursor: "pointer", fontFamily: "inherit", fontWeight: 700 }}>
                📊
              </button>
            </div>
            {(filter === "CALLS" || filter === "PUTS") && (
              <button
                onClick={() => setReadyOnly(v => !v)}
                title="READY ONLY — Phase 3 only (🚀 CALLS READY or 💀 PUTS READY)"
                style={{
                  padding: "3px 8px",
                  background: readyOnly ? `${T.cyan}22` : "transparent",
                  border: `1px solid ${readyOnly ? T.cyan : "transparent"}`,
                  color: readyOnly ? T.cyan : T.textDim,
                  fontSize: 10,
                  borderRadius: 5,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontWeight: readyOnly ? 800 : 600,
                  letterSpacing: "0.02em",
                }}
              >
                READY ONLY
              </button>
            )}
          </div>
          <div style={{ marginLeft: 8, fontSize: 10, color: T.textDim, whiteSpace: "nowrap", maxWidth: 420 }}>
            <span style={{ fontWeight: 700, color: T.textSub }}>Phase:</span>{" "}
                <span style={{ color: T.textDim }}>👁 CALLS/PUTS WATCH · ⚡ CALLS/PUTS SETUP · 🚀 CALLS READY / 💀 PUTS READY · Flashing = Phase 3</span>
                <button
                  type="button"
                  onClick={() => setShowSignalGuide(true)}
                  style={{ marginLeft: 8, padding: "2px 8px", background: `${T.purple}14`, border: `1px solid ${T.purple}44`, color: T.purple, borderRadius: 6, fontSize: 10, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
                >
                  📡 Signal Guide
                </button>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center" }}>
            {sectorAlertTickers.length > 0 && (
              <button
                onClick={() => { setSectorAlertsCollapsed(false); setTimeout(() => sectorAlertsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50); }}
                style={{ padding: "3px 8px", background: `${T.green}18`, border: `1px solid ${T.green}55`, color: T.green, fontSize: 10, borderRadius: 5, cursor: "pointer", fontFamily: "inherit", fontWeight: 800, whiteSpace: "nowrap" }}
              >
                🔄 {sectorAlertTickers.length} Sector Alert{sectorAlertTickers.length !== 1 ? "s" : ""}
              </button>
            )}
            {/* Reversal Watch button */}
            {watchlist.some(i => i.result) && (() => {
              const rev = calcReversalWatch(watchlist, vix, fearGreed);
              const revColor = rev?.level === "CALLS READY" ? T.green : rev?.level === "WATCH" ? T.yellow : rev?.level === "MONITOR" ? T.orange : T.textDim;
              return (
                <button
                  onClick={() => setShowReversalWatch(v => !v)}
                  title="Market reversal watch — signals for when to load calls after a selloff"
                  style={{ padding: "3px 9px",
                    background: showReversalWatch ? `${revColor}33` : rev ? `${revColor}18` : T.surfaceHi,
                    border: `1px solid ${rev ? revColor + "55" : T.border}`,
                    color: rev ? revColor : T.textDim,
                    fontSize: 10, fontWeight: 700, borderRadius: 5, cursor: "pointer", fontFamily: "inherit",
                    animation: rev?.level === "CALLS READY" ? "pulse 1.5s infinite" : "none",
                  }}>
                  {rev?.level === "CALLS READY" ? "⚡ CALLS READY" : rev?.level === "WATCH" ? "📈 Watch" : rev?.level === "MONITOR" ? "👁 Monitor" : "📈 Reversal"}
                </button>
              );
            })()}
            {/* Refresh Briefing — manual regenerate without rescanning */}
            {watchlist.some(i => i.result) && (() => {
              const scanned = watchlist.filter(i => i.result);
              return (
                <button
                  onClick={() => generateBriefing(watchlist, vix, fearGreed, { force: true })}
                  disabled={scanned.length < 3 || briefingLoading}
                  title={scanned.length < 3 ? "Scan at least 3 tickers first" : "Regenerate today's briefing from current watchlist data"}
                  style={{ padding: "3px 9px",
                    background: briefingLoading ? `${T.purple}33` : `${T.purple}18`,
                    border: `1px solid ${T.purple}55`,
                    color: T.purple,
                    fontSize: 10, fontWeight: 700, borderRadius: 5,
                    cursor: scanned.length < 3 || briefingLoading ? "not-allowed" : "pointer",
                    fontFamily: "inherit", opacity: scanned.length < 3 ? 0.4 : 1,
                  }}>
                  {briefingLoading ? "⟳ Generating…" : "↻ Refresh Briefing"}
                </button>
              );
            })()}
            {watchlist.some(i => i.result) && (
              <button onClick={() => {
                const rows = [["Ticker","Price","Score","Signal","Verdict","Gate","RSI","MACD","CMF","52W","RS vs SPY","Gate Passed","Bull Count","Bear Count","Scan Time"].join(","), ...watchlist.filter(i => i.result).map(i => { const r = i.result; const v = calcVerdict({ result: r, aiCaution: false }); return [i.symbol,r.price?.toFixed(2)??"",r.buyScore??"",r.signal??"",v?.label??"",r.gate?.label??"",r.rsi?.toFixed(1)??"",r.macd?.crossover?"CROSSOVER":r.macd?.bullish?"BULLISH":r.macd?.crossunder?"CROSSUNDER":"BEARISH",r.cmf?.toFixed(3)??"",r.range52w?.score??"",r.rs?.spread!=null?r.rs.spread.toFixed(1)+"%":"",`${r.gate?.passed??""}/${r.gate?.total??""}`,r.bullCount??"",r.bearCount??"",r.scanTime?new Date(r.scanTime).toLocaleString():""].join(",")})].join("\n");
                const blob = new Blob([rows], { type: "text/csv" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `auctus-${new Date().toISOString().slice(0,10)}.csv`; a.click(); URL.revokeObjectURL(url);
              }} style={{ padding: "3px 9px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, fontSize: 10, borderRadius: 5, cursor: "pointer", fontFamily: "inherit" }}>⬇ CSV</button>
            )}
            <button onClick={toggleMode} style={{ padding: "3px 9px", background: proMode ? `${T.purple}22` : `${T.cyan}18`, border: `1px solid ${proMode ? T.purple : T.cyan}44`, color: proMode ? T.purple : T.cyan, fontSize: 10, fontWeight: 700, borderRadius: 5, cursor: "pointer", fontFamily: "inherit" }}>{proMode ? "PRO ✦" : "EASY ✦"}</button>
            {proMode && <button onClick={() => setShowColPanel(p => !p)} title="Customize visible columns" style={{ padding: "3px 9px", background: showColPanel ? `${T.purple}22` : T.surfaceHi, border: `1px solid ${showColPanel ? T.purple : T.border}`, color: showColPanel ? T.purple : T.textDim, fontSize: 10, fontWeight: showColPanel ? 700 : 400, borderRadius: 5, cursor: "pointer", fontFamily: "inherit" }}>⚙ Columns</button>}
            <button onClick={toggleHints} style={{ padding: "3px 8px", background: hintsOn ? `${T.yellow}15` : "transparent", border: `1px solid ${hintsOn ? T.yellow + "44" : T.border}`, color: hintsOn ? T.yellow : T.textDim, fontSize: 10, fontWeight: hintsOn ? 700 : 400, borderRadius: 5, cursor: "pointer", fontFamily: "inherit" }}>💡</button>
          </div>
        </div>}

        {/* ── COLUMN SETTINGS PANEL — pro only ── */}
        {proMode && showColPanel && mainView === "watchlist" && (
          <div style={{ margin: "0 16px 6px", padding: "12px 16px", background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 10 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.textDim, letterSpacing: "0.06em" }}>COLUMN VISIBILITY — click to toggle</div>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => { const all = Object.fromEntries(COL_DEFS.map(c => [c.key, true])); setColVis(all); try { localStorage.setItem("auctus_col_vis", JSON.stringify(all)); } catch {} }} style={{ padding: "2px 8px", background: T.bg, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 4, fontSize: 9, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>All on</button>
                <button onClick={() => { const none = Object.fromEntries(COL_DEFS.map(c => [c.key, false])); setColVis(none); try { localStorage.setItem("auctus_col_vis", JSON.stringify(none)); } catch {} }} style={{ padding: "2px 8px", background: T.bg, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 4, fontSize: 9, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>All off</button>
              </div>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {visibleColDefs.map(({ key, label, desc }) => {
                const on = colVis[key] !== false;
                return (
                  <button key={key} onClick={() => toggleColVis(key)} title={desc}
                    style={{ padding: "5px 10px", borderRadius: 6, fontSize: 11, fontWeight: on ? 700 : 400, fontFamily: "inherit", cursor: "pointer", border: `1px solid ${on ? T.purple + "55" : T.border}`, background: on ? `${T.purple}15` : T.bg, color: on ? T.purple : T.textDim, display: "flex", alignItems: "center", gap: 5, transition: "all 0.1s" }}>
                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: on ? T.purple : T.border, flexShrink: 0, display: "inline-block" }} />
                    {label}
                  </button>
                );
              })}
            </div>
            <div style={{ marginTop: 8, fontSize: 10, color: T.textDim }}>Always visible: Ticker · Price · Verdict · Score · ⚡ Calls · 🔻 Puts · Targets · Actions. Hover any column button for description.</div>
          </div>
        )}

        {/* ── SIGNAL OF THE DAY CARD — watchlist view only ── */}
        {/* ── DAILY MARKET BRIEFING ── */}
        {mainView === "watchlist" && (dailyBriefing || briefingLoading) && (() => {
          const primeCount = watchlist.filter((i) => i.result?.isPrime === true).length;
          return (
          <div style={{ margin: "0 16px 6px", padding: briefingCollapsed ? "6px 12px" : "10px 14px", background: `${T.purple}08`, border: `1px solid ${T.purple}22`, borderRadius: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: briefingCollapsed ? 0 : 4, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={toggleBriefingCollapsed}
                aria-expanded={!briefingCollapsed}
                title={briefingCollapsed ? "Expand briefing" : "Minimize briefing"}
                style={{
                  padding: "2px 6px",
                  background: T.surfaceHi,
                  border: `1px solid ${T.border}`,
                  color: T.textSub,
                  borderRadius: 5,
                  fontSize: 11,
                  lineHeight: 1,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {briefingCollapsed ? "▸" : "▾"}
              </button>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.purple, letterSpacing: "0.08em" }}>✦ TODAY'S BRIEFING</span>
              {briefingCollapsed && (
                <span style={{ fontSize: 10, color: T.textDim }}>
                  {briefingLoading
                    ? "Generating…"
                    : `${primeCount} prime setup${primeCount === 1 ? "" : "s"} · ${dailyBriefing?.tickerCount ?? "—"} tickers`}
                </span>
              )}
              {!briefingCollapsed && !briefingLoading && watchlist.filter(i=>i.result).length >= 3 && (
                <button onClick={() => generateBriefing(watchlist, vix, fearGreed, { force: true })}
                  style={{ marginLeft: "auto", padding: "2px 7px", background: "transparent", border: `1px solid ${T.border}`, color: T.textDim, borderRadius: 5, fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}>↻</button>
              )}
              {briefingCollapsed && (
                <button
                  type="button"
                  onClick={toggleBriefingCollapsed}
                  style={{ marginLeft: "auto", padding: "2px 8px", background: "transparent", border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 5, fontSize: 9, cursor: "pointer", fontFamily: "inherit" }}
                >
                  Show
                </button>
              )}
            </div>
            {!briefingCollapsed && (
              <>
                <TodaysPrimeSetupsBlock
                  watchlist={watchlist}
                  onTickerClick={scrollToWatchlistTicker}
                  T={T}
                  companyNames={COMPANY_NAMES}
                />
                {(briefingLoading || dailyBriefing?.generatedAt) && (
                  <div style={{ fontSize: 9, color: T.textDim, marginBottom: briefingLoading ? 6 : 6 }}>
                    {briefingLoading
                      ? "Generating…"
                      : `Generated ${dailyBriefing.generatedAt} · ${dailyBriefing.tickerCount} tickers analyzed · Market: ${dailyBriefing.tideLabel}`}
                  </div>
                )}
                {briefingLoading
                  ? <div style={{ fontSize: 11, color: T.purple, fontStyle: "italic", opacity: 0.85 }}>✦ Generating briefing…</div>
                  : (
                    <BriefingParagraph
                      text={dailyBriefing?.text}
                      watchlistSymbols={watchlist.map((i) => i.symbol)}
                      onTickerClick={scrollToWatchlistTicker}
                      T={T}
                    />
                  )
                }
              </>
            )}
          </div>
          );
        })()}

        {/* ── CONSOLIDATED STATUS BAR — regime + sector concentration + VIX ── */}
        {mainView === "watchlist" && (() => {
          const regime = calcMarketRegime(vix, fearGreed, watchlist);
          if (!regime && sectorConcentration.length === 0 && !vix?.value) return null;

          const scanned = watchlist.filter(i => i.result);
          const deathCrossPct = scanned.length >= 3 ? scanned.filter(i => i.result?.deathCross).length / scanned.length : 0;
          const goldenCrossPct = scanned.length >= 3 ? scanned.filter(i => i.result?.goldenCross).length / scanned.length : 0;
          const bearSignals = [vix?.value > 25, fearGreed?.score <= 30, deathCrossPct > 0.4].filter(Boolean).length;
          const bullSignals = [vix?.value != null && vix.value < 18, fearGreed?.score >= 55, goldenCrossPct > 0.5].filter(Boolean).length;
          const isBear = bearSignals >= 2;
          const isBull = bullSignals >= 2 && !isBear;

          return (
            <div style={{ padding: "5px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", background: T.surface, flexShrink: 0 }}>
              {regime && (
                <span title={`${regime.headline} — ${regime.summary?.slice(0, 200)}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${regime.color}15`, border: `1px solid ${regime.color}33`, fontSize: 10, fontWeight: 800, color: regime.color, cursor: "help", fontFamily: "inherit" }}>
                  {regime.emoji} {regime.label}
                </span>
              )}
              {sectorConcentration.map(({ etf, syms }) => (
                <span key={etf} title={`${syms.join(", ")} all in ${etf} — ${syms.length} correlated positions. A single sector downturn affects all.`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${T.orange}15`, border: `1px solid ${T.orange}33`, fontSize: 10, fontWeight: 700, color: T.orange, cursor: "help", fontFamily: "inherit" }}>
                  ⚠ {etf} ×{syms.length}
                </span>
              ))}
              {isBear && (
                <span onClick={() => openHelp('bear')}
                  title={`Bear market conditions active — VIX elevated, fear high, bearish 200 SMA structure dominant. Click for survival guide.`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${T.red}18`, border: `1px solid ${T.red}44`, fontSize: 10, fontWeight: 800, color: T.red, cursor: "pointer", fontFamily: "inherit" }}>
                  🐻 Bear Market Guide
                </span>
              )}
              {isBull && (
                <span onClick={() => openHelp('bull')}
                  title={`Bull market confirmed — VIX low, sentiment positive, golden crosses dominant. Click for bull market playbook.`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${T.green}18`, border: `1px solid ${T.green}44`, fontSize: 10, fontWeight: 800, color: T.green, cursor: "pointer", fontFamily: "inherit" }}>
                  🐂 Bull Market Guide
                </span>
              )}
              <div style={{ flex: 1 }} />
              {vix?.value != null && (
                <span style={{ fontSize: 11, fontWeight: 800, color: vix.value >= 25 ? T.red : vix.value >= 18 ? T.orange : T.textSub, fontFamily: "inherit" }}>
                  VIX {vix.value.toFixed(1)}{vix.value >= 25 ? " ↑" : ""}
                </span>
              )}
            </div>
          );
        })()}

        {/* ── SECTOR ROTATION BANNER ── */}
        {mainView === "watchlist" && (
          <SectorRotationBanner watchlist={watchlist} />
        )}

        {mainView === "watchlist" && signalOfDay && (() => {
          const { symbol, result, verdict, score } = signalOfDay;
          const sodBadge = isScoringV2Result(result) ? getScannerDisplayBadge(result) : null;
          const col = sodBadge?.text ?? (isDark ? verdict.darkColor : verdict.color);
          const bg = sodBadge?.bg ?? (isDark ? verdict.darkBg : verdict.bg);
          const badgeTextColor = sodBadge?.text ?? col;
          return (
            <div style={{ padding: "10px 20px", borderBottom: `1px solid ${T.border}`, background: isDark ? `${T.green}08` : `${T.green}06`, flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ fontSize: 16 }}>🏆</span>
                  <span style={{ fontSize: F.xs, fontWeight: 800, color: T.textDim, letterSpacing: "0.1em" }}>SIGNAL OF THE DAY</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: F.lg, fontWeight: 900, color: T.text }}>{symbol}</span>
                  <span style={{ fontSize: F.md, color: T.textSub, fontFamily: "'JetBrains Mono', monospace" }}>${result.price?.toFixed(2)}</span>
                  <span style={{
                    padding: "3px 8px",
                    borderRadius: 6,
                    background: sodBadge ? bg : bg,
                    border: sodBadge ? "none" : `1px solid ${col}44`,
                    color: badgeTextColor,
                    fontSize: F.xs,
                    fontWeight: 800,
                  }}>{verdict.short}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: F.md, fontWeight: 800, color: T.green }}>{score > 0 ? "+" : ""}{score}</span>
                </div>
                <div style={{ fontSize: F.xs, color: T.textSub, flex: 1, minWidth: 200 }}>
                  {verdict.reason?.slice(0, 120)}{verdict.reason?.length > 120 ? "…" : ""}
                </div>
                <div style={{ display: "flex", gap: 6, marginLeft: "auto" }}>
                  <button onClick={() => { addTickers([symbol]); }} style={{ padding: "4px 12px", background: `${T.green}18`, border: `1px solid ${T.green}44`, color: T.green, borderRadius: 6, fontSize: F.xs, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                    + Add to Watchlist
                  </button>
                  <button onClick={() => setMainView("scanner")} style={{ padding: "4px 12px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, fontSize: F.xs, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                    Open Scanner →
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* SCAN STATUS BAR */}
        {scanning && (
          <div style={{ padding: "5px 20px", background: `${T.cyan}10`, borderBottom: `1px solid ${T.cyan}33`, display: "flex", gap: 20, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: T.cyan, animation: "pulse 1s infinite" }} />
              <span style={{ fontSize: F.sm, fontWeight: 600, color: T.cyan }}>Live Scan Running</span>
            </div>
            {scanQueue.length > 0 && <span style={{ fontSize: F.sm, color: T.textSub }}>Queue: {scanQueue.slice(0, 6).join(", ")}{scanQueue.length > 6 ? ` +${scanQueue.length - 6} more` : ""}</span>}
            {countdown > 0 && <span style={{ fontSize: F.sm, color: T.yellow }}>Cooldown: {countdown}s</span>}
            <span style={{ marginLeft: "auto", fontSize: F.xs, color: T.textDim }}>Powered by Yahoo Finance</span>
          </div>
        )}

        {/* CALLS + PUTS SIGNALS — compact pill bar */}
        {(() => {
          const callsTickers = watchlist.filter(i => i.result?.callsSignal?.fullSignal);
          const bounceCallsTickers = watchlist.filter(i => i.result?.callsSignal?.bouncePlayOnly && !i.result?.callsSignal?.fullSignal);
          const partialCallsTickers = watchlist.filter(i => i.result?.callsSignal?.partialSignal && !i.result?.callsSignal?.fullSignal && !i.result?.callsSignal?.bouncePlayOnly);
          const putsTickers = watchlist.filter(i => i.result?.putsSignal?.fullSignal);
          const partialPutsTickers = watchlist.filter(i => i.result?.putsSignal?.partialSignal && !i.result?.putsSignal?.fullSignal);
          if (callsTickers.length === 0 && bounceCallsTickers.length === 0 && partialCallsTickers.length === 0 && putsTickers.length === 0 && partialPutsTickers.length === 0) return null;
          return (
            <div style={{ padding: "5px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", background: T.surface, flexShrink: 0 }}>
              {callsTickers.length > 0 && (
                <span title={`Full calls signal: ${callsTickers.map(i => i.symbol).join(", ")} — VIX below 20 · No earnings within 14 days · After 10 AM ET`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${T.green}15`, border: `1px solid ${T.green}44`, fontSize: 10, fontWeight: 800, color: T.green, cursor: "help", fontFamily: "inherit" }}>
                  ⚡ Calls: {callsTickers.map(i => i.symbol).join(" · ")}
                </span>
              )}
              {bounceCallsTickers.length > 0 && (
                <span title={bounceCallsTickers.map(i => i.result.callsSignal?.bouncePlayLabel || "Bounce play — not standard momentum").join(" · ")}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${T.cyan}18`, border: `1px solid ${T.cyan}55`, fontSize: 10, fontWeight: 800, color: T.cyan, cursor: "help", fontFamily: "inherit" }}>
                  🐱 Bounce: {bounceCallsTickers.map(i => i.symbol).join(" · ")}
                </span>
              )}
              {partialCallsTickers.length > 0 && (
                <span title={`Near calls: ${partialCallsTickers.map(i => `${i.symbol} (${i.result.callsSignal.passed}/${i.result.callsSignal.total})`).join(", ")}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${T.cyan}12`, border: `1px solid ${T.cyan}33`, fontSize: 10, fontWeight: 700, color: T.cyan, cursor: "help", fontFamily: "inherit" }}>
                  ⚡ Near: {partialCallsTickers.map(i => `${i.symbol} ${i.result.callsSignal.passed}/${i.result.callsSignal.total}`).join(" · ")}
                </span>
              )}
              {putsTickers.length > 0 && (
                <span title={`Full puts signal: ${putsTickers.map(i => i.symbol).join(", ")} — VIX below 20 · No earnings within 14 days · Never put against a golden cross`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${T.red}15`, border: `1px solid ${T.red}44`, fontSize: 10, fontWeight: 800, color: T.red, cursor: "help", fontFamily: "inherit" }}>
                  🔻 Puts: {putsTickers.map(i => i.symbol).join(" · ")}
                </span>
              )}
              {partialPutsTickers.length > 0 && (
                <span title={`Near puts: ${partialPutsTickers.map(i => `${i.symbol} (${i.result.putsSignal.passed}/${i.result.putsSignal.total})`).join(", ")}`}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 5, background: `${T.orange}12`, border: `1px solid ${T.orange}33`, fontSize: 10, fontWeight: 700, color: T.orange, cursor: "help", fontFamily: "inherit" }}>
                  🔻 Near: {partialPutsTickers.map(i => `${i.symbol} ${i.result.putsSignal.passed}/${i.result.putsSignal.total}`).join(" · ")}
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {/* ── MAIN AREA: full-height ticker table + slide-out drawer ── */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0, position: "relative", marginLeft: 48 }}>

        {/* FULL-SCREEN SCANNER VIEW */}
        {mainView === "scanner" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <MarketScanner
              onAddToWatchlist={sym => addTickers([sym])}
              watchlistSymbols={watchlist.map(i => i.symbol)}
              onAnalyze={analyzeFromScanner}
              scannerPreset={scannerPreset}
              onPresetConsumed={() => setScannerPreset(null)}
              onScanComplete={(topPicks) => {
                const sod = topPicks?.[0] ? buildSignalOfDayFromPick(topPicks[0]) : null;
                if (sod) setSignalOfDay(sod);
              }}
            />
          </div>
        )}

        {/* CRYPTO SCANNER VIEW */}
        {mainView === "crypto" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <CryptoScanner
              onAddToWatchlist={sym => addTickers([sym])}
              watchlistSymbols={watchlist.map(i => i.symbol)}
              isDark={isDark}
              anthropicKey={anthropicKey}
              openaiKey={openaiKey}
              aiProvider={aiProvider}
            />
          </div>
        )}

        {mainView === "sectors" && (
          <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
            <SectorDashboard
              sectorDashboard={sectorDashboard}
              rotationAlerts={rotationAlerts}
              alertBanner={sectorAlertBanner}
              loading={sectorDashboardLoading}
              onRefresh={() => fetchSectorDashboard(true)}
              watchlist={watchlist}
              livePrice={livePrice}
              onAddToWatchlist={sym => addTickers([sym])}
              onScanAllSector={(tickers, sectorName) => {
                launchSectorScanner(tickers, sectorName);
              }}
              scanning={scanning}
            />
          </div>
        )}

        {/* OPEN POSITIONS VIEW */}
        {mainView === "positions" && (() => {
          const positions = watchlist.filter(i => i.inPosition);

          // Position health helper
          const getHealth = (result) => {
            if (!result) return { health: "NO DATA", color: T.textDim, flags: [] };
            const flags = [];
            if (result.ema21 && result.price < result.ema21) flags.push("below EMA21");
            if (result.cmf != null && result.cmf < -0.05) flags.push("CMF distributing");
            if (result.sellCount >= 3) flags.push(`${result.sellCount} sell flags`);
            if (result.deathCross) flags.push("200 SMA structure bearish");
            const hasCritical = flags.some(f => f.includes("EMA21") || f.includes("death") || f.includes("200 SMA"));
            const health = flags.length === 0 ? "HOLD" : hasCritical ? "CAUTION" : "WATCH";
            const color = health === "HOLD" ? T.green : health === "WATCH" ? T.yellow : T.red;
            return { health, color, flags };
          };

          // Summary counts
          const holds   = positions.filter(p => getHealth(p.result).health === "HOLD").length;
          const watches  = positions.filter(p => getHealth(p.result).health === "WATCH").length;
          const cautions = positions.filter(p => getHealth(p.result).health === "CAUTION").length;
          const noData   = positions.filter(p => !p.result).length;
          const urgent   = positions.filter(p => getHealth(p.result).health === "CAUTION");

          return (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

              {/* ── Header ── */}
              <div style={{ padding: "10px 20px", borderBottom: `1px solid ${T.border}`, background: T.surface, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>📍 Open Positions</div>
                <button onClick={() => setMainView("watchlist")}
                  style={{ padding: "4px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, borderRadius: 6, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>
                  ← Watchlist
                </button>
              </div>

              {positions.length === 0 ? (
                <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 40, textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>📍</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.textSub, marginBottom: 6 }}>No open positions yet</div>
                  <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.7, maxWidth: 320, marginBottom: 16 }}>
                    Open any ticker in the watchlist and tap the 📍 button in the drawer to track it here.
                  </div>
                  <button onClick={() => setMainView("watchlist")}
                    style={{ padding: "7px 18px", background: `${T.purple}22`, border: `1px solid ${T.purple}44`, color: T.purple, borderRadius: 8, fontSize: 12, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
                    Go to Watchlist →
                  </button>
                </div>
              ) : (
                <>
                  {/* ── Summary bar ── */}
                  <div style={{ padding: "8px 20px", borderBottom: `1px solid ${T.border}`, background: T.bg, flexShrink: 0, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, color: T.textDim }}>{positions.length} position{positions.length > 1 ? "s" : ""}</span>
                    {holds > 0 && <span style={{ fontSize: 11, color: T.green, fontWeight: 600 }}>✓ {holds} HOLD</span>}
                    {watches > 0 && <span style={{ fontSize: 11, color: T.yellow, fontWeight: 600 }}>⏳ {watches} WATCH</span>}
                    {cautions > 0 && <span style={{ fontSize: 11, color: T.red, fontWeight: 600 }}>⚠ {cautions} CAUTION</span>}
                    {noData > 0 && <span style={{ fontSize: 11, color: T.textDim }}>· {noData} unscanned</span>}
                    {cautions > 0 && urgent[0] && (
                      <span style={{ marginLeft: "auto", fontSize: 11, color: T.red, fontWeight: 700 }}>
                        ⚠ {urgent[0].symbol} needs attention
                      </span>
                    )}
                  </div>

                  {/* ── Position cards ── */}
                  <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 8 }}>
                    {positions.map(item => (
                      <PositionCard
                        key={item.symbol}
                        item={item}
                        isDark={isDark}
                        livePrice={livePrice}
                        onOpen={setWatchlistDrawer}
                        getHealth={getHealth}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* WATCHLIST AI ANALYSIS DRAWER */}
        {mainView === "watchlist" && showWatchlistAI && (
          <WatchlistAIPanel
            watchlist={watchlist}
            anthropicKey={anthropicKey}
            cached={watchlistAI}
            onResult={r => setWatchlistAI(r)}
            loading={watchlistAILoading}
            setLoading={setWatchlistAILoading}
            onClose={() => setShowWatchlistAI(false)}
            onScanAll={scanAll}
            scanning={scanning}
          />
        )}

        {/* WATCHLIST OPTIONS ANALYSIS PANEL */}
        {mainView === "watchlist" && showWatchlistAnalysis && (() => {
          const candidates = watchlist
            .filter(i => i.result && (
              showWatchlistAnalysis === "CALLS"
                ? callsSignalDisplayActive(i.result)
                : (i.result.putsSignal?.fullSignal || i.result.putsSignal?.partialSignal)
            ))
            .map(i => ({ symbol: i.symbol, result: i.result }));
          return (
            <OptionsAnalysisPanel
              candidates={candidates}
              type={showWatchlistAnalysis}
              onClose={() => setShowWatchlistAnalysis(null)}
              onAddToWatchlist={(sym) => addTickers([sym])}
              watchlistSymbols={watchlist.map(i => i.symbol)}
              anthropicKey={anthropicKey}
            />
          );
        })()}

        {/* TICKER TABLE — full height when drawer closed */}
        {mainView === "watchlist" && <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column", transition: "margin-right 0.25s ease" }}>
          {sectorAlertTickers.length > 0 && (
            <div ref={sectorAlertsSectionRef} style={{ flexShrink: 0, borderBottom: `1px solid ${T.border}`, background: T.surface }}>
              <button
                onClick={() => setSectorAlertsCollapsed(c => !c)}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 14px", background: `${T.green}08`, border: "none", borderBottom: sectorAlertsCollapsed ? "none" : `1px solid ${T.border}`, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 900, color: T.text }}>🔄 SECTOR ROTATION ALERTS</div>
                  <div style={{ fontSize: 10, color: T.textSub, marginTop: 2 }}>Auto-scanned from hot sectors</div>
                </div>
                <span style={{ fontSize: 12, color: T.textDim }}>{sectorAlertsCollapsed ? "▾" : "▴"}</span>
              </button>
              {!sectorAlertsCollapsed && (
                <div style={{ padding: "8px 12px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                  {sectorAlertTickers.map(alert => {
                    const { symbol, result, sectorName, sectorEmoji, sectorStage } = alert;
                    const ui = result ? calcSignalActionCombinedUi({ result, inPosition: false }) : null;
                    const verdict = result ? resolveDisplayVerdict(result, { inPosition: false, activeSignal: result?.activeSignal }) : null;
                    const actionText = ui?.text && !ui.isDash ? `${ui.text}${getSectorRotationActionSuffix(result)}` : (verdict?.verdict ?? "—");
                    const stageLabel = formatSectorAlertStageLabel(sectorStage);
                    const cf = result ? watchlistConfluenceCell(result) : null;
                    return (
                      <div key={symbol} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 10, fontWeight: 800, padding: "3px 8px", borderRadius: 999, background: `${T.green}18`, border: `1px solid ${T.green}44`, color: T.green, whiteSpace: "nowrap" }}>
                          {sectorEmoji} {String(sectorName || "").toUpperCase()}
                        </span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 900, color: T.text, fontSize: 13 }}>{symbol}</span>
                        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: T.textSub }}>
                          {result?.price != null ? `$${result.price.toFixed(2)}` : "—"}
                        </span>
                        <HoverTip content={hintsOn ? SCORE_TIP : undefined}>
                          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 800, color: (result?.buyScore ?? 0) >= 0 ? T.green : T.red, cursor: hintsOn ? "help" : "default" }}>
                            {result?.buyScore != null ? `${result.buyScore > 0 ? "+" : ""}${result.buyScore}` : "—"}
                          </span>
                        </HoverTip>
                        {cf?.text && cf.text !== "—" && (
                          <HoverTip content={hintsOn ? confluenceCellTooltip(cf) : undefined}>
                            <span style={{ fontSize: 10, fontWeight: 800, color: T.cyan, cursor: hintsOn ? "help" : "default" }}>{cf.text}</span>
                          </HoverTip>
                        )}
                        {result?.rsi != null && (
                          <HoverTip content={hintsOn ? rsiCellTooltipExtended(result.rsi, result) : undefined}>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: rsiDisplayColor(result.rsi, T), cursor: hintsOn ? "help" : "default" }}>
                              {formatRsiWithDivergence(result.rsi, result.rsiDiv, 0)}
                            </span>
                          </HoverTip>
                        )}
                        {result?.cmf != null && (
                          <HoverTip content={hintsOn ? CMF_TIP : undefined}>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: result.cmf > 0.05 ? T.green : T.red, cursor: hintsOn ? "help" : "default" }}>
                              CMF {result.cmf.toFixed(2)}
                            </span>
                          </HoverTip>
                        )}
                        <span style={{ fontSize: 10, fontWeight: 800, color: T.textSub, flex: 1, minWidth: 120 }}>
                          {actionText} · {stageLabel}
                        </span>
                        <button
                          onClick={() => addTickers([symbol])}
                          style={{ padding: "5px 10px", background: `${T.green}14`, border: `1px solid ${T.green}44`, color: T.green, borderRadius: 6, fontSize: 10, fontWeight: 800, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
                        >
                          + Add to Watchlist
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {watchlist.length > 0 ? (
            <div style={{ flex: 1, overflowY: "auto", overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1600, marginLeft: 8 }}>
                <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                  <tr>
                    <th style={{ width: 20, background: T.surface, borderBottom: `2px solid ${T.border}` }} />
                    <TH label="Ticker" title="Stock ticker symbol. Click to open the full analysis drawer. The badges under the ticker show: Verdict (action recommendation), signal count (X/22 indicators bullish), AI triggers (if set), and earnings warning (if within 14 days)." />
                    <TH label="Price" col="price" title="Price column — click to sort by % change (gain/loss), not dollar value. Last scanned price from Yahoo Finance." />
                    <TH label="Signal" col="buyScore" title="Same verdict badge as Market Scanner (v2): label + explanation (e.g. Below VWAP, Near Prime). Legacy rows show strength %." />
                    <TH label="Action" title={ACTION_COLUMN_TIP} />
                    <TH label="Score" col="buyScore" title={SCORE_TIP} />
                    {proMode && colVis.rsi !== false && <TH label="RSI" col="rsi" title={RSI_COLUMN_HEADER_TIP} />}
                    {proMode && colVis.confluence !== false && <TH label="Confluence" title={CONFLUENCE_TIPS["BREAK ⚡"]} />}
                    {proMode && colVis.bb !== false && <TH label="BB" title={`Bollinger Bands. ${BB_TIGHT_BANDS_LABEL} = width near 6-month low (vol compressed).`} />}
                    {proMode && colVis.cmf !== false && <TH label="CMF + Vol" col="cmf" title={CMF_TIP} />}
                    {proMode && colVis.range52w !== false && <TH label="52W Range" title="Where price sits in its annual range (0–100)." />}
                    {proMode && colVis.rs !== false && <TH label="RS vs SPY" title={RS_SPY_TIP} />}
                    {proMode && colVis.stop !== false && <TH label="Stop" title="ATR Stop — calculated as entry price minus 2× ATR (Average True Range). This is a volatility-adjusted stop that accounts for the stock's normal daily range. Prevents being stopped out by noise. Enter your buy price to see your specific stop level." />}
                    <TH label="Targets" title="Price targets anchored to real chart structure. T1 = first target: Pivot R1, Fib 23.6% extension, or 1.5×ATR — the nearest level where sellers are likely to cluster. T2 = extended target: Pivot R2, Fib 38.2% extension, 52W high, or 3×ATR. Take half off at T1, trail the rest to T2. Source label shown under each price (e.g. 'Pivot R1', 'Fib 38.2% ext')." />
                    {proMode && colVis.earnings !== false && (
                      <th style={{ width: 70, padding: "7px 8px", fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center", whiteSpace: "nowrap", borderBottom: `2px solid ${T.border}`, background: T.surface, userSelect: "none" }}>
                        Earnings
                      </th>
                    )}
                    {POLYGON_OPTIONS_LIVE && proMode && colVis.ivrank !== false && (
                      <th style={{ width: 55, padding: "7px 8px", fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center", whiteSpace: "nowrap", borderBottom: `2px solid ${T.border}`, background: T.surface, userSelect: "none" }}>
                        IV Rank
                      </th>
                    )}
                    {POLYGON_OPTIONS_LIVE && proMode && colVis.csp !== false && (
                      <th style={{ width: 55, padding: "7px 8px", fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center", whiteSpace: "nowrap", borderBottom: `2px solid ${T.border}`, background: T.surface, userSelect: "none" }}>
                        CSP %
                      </th>
                    )}
                    {proMode && colVis.rr !== false && <TH label="R:R" title="Risk:Reward ratio — (T1 - Entry) ÷ (Entry - Stop). A ratio of 2.0 means you stand to make $2 for every $1 risked. Only enter trades with R:R of 2:1 or better. R:R below 1.5 = skip and wait for a better setup. Enter your buy price to calculate." />}
                    {proMode && colVis.spark !== false && (
                      <th style={{ width: 70, padding: "7px 8px", fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.08em", textTransform: "uppercase", textAlign: "center", whiteSpace: "nowrap", borderBottom: `2px solid ${T.border}`, background: T.surface, userSelect: "none" }}>
                        Trend
                      </th>
                    )}
                    <TH label="" />
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((item, idx) => (
                    <>
                      {dropIndex === idx && dragIndex !== idx && dragIndex !== idx - 1 && (
                        <tr key={`drop-${idx}`}><td colSpan={23} style={{ padding: 0, height: 3, background: T.cyan, boxShadow: `0 0 8px ${T.cyan}` }} /></tr>
                      )}
                      <PositionRow
                        key={item.symbol}
                        item={item}
                        proMode={proMode}
                        onUpdate={updatePos}
                        onRemove={removeTicker}
                        onScan={scanOne}
                        scanning={scanning}
                        onLogTrade={(trade) => { setPendingTrade(trade); setDrawerTab("journal"); setDrawerOpen(true); }}
                        onOpenDrawer={(item) => { setWatchlistDrawer(item); }}
                        dragIndex={dragIndex}
                        rowIndex={idx}
                        onDragStart={() => { setDragIndex(idx); setDropIndex(idx); }}
                        onDragOver={i => setDropIndex(i)}
                        onDragEnd={() => { if (dragIndex !== null && dropIndex !== null) reorderTicker(dragIndex, dropIndex); setDragIndex(null); setDropIndex(null); }}
                        isDragging={dragIndex === idx}
                        anthropicKey={anthropicKeyRef.current}
                        openaiKey={openaiKeyRef.current}
                        geminiKey={geminiKeyRef.current}
                        aiProvider={aiProvider}
                        proMode={proMode}
                        isDark={isDark}
                        hintsOn={hintsOn}
                        liveQuote={livePrice[item.symbol] || null}
                        portfolioSize={portfolioSize}
                        onSavePortfolioSize={savePortfolioSize}
                        onTogglePosition={toggleInPosition}
                        onManualSignalExit={manualExitActiveSignal}
                        colVis={colVis}
                        rowHighlight={briefingHighlight === item.symbol}
                      />
                    </>
                  ))}
                  {dropIndex === sorted.length && dragIndex !== sorted.length - 1 && (
                    <tr key="drop-end"><td colSpan={23} style={{ padding: 0, height: 3, background: T.cyan, boxShadow: `0 0 8px ${T.cyan}` }} /></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14 }}>
              <div style={{ fontSize: 40 }}>📊</div>
              <div style={{ fontSize: F.xl, fontWeight: 700, color: T.text }}>Your watchlist is empty</div>
              <div style={{ fontSize: F.base, color: T.textSub, textAlign: "center", maxWidth: 420, lineHeight: 1.6 }}>
                Add tickers above, or click <strong style={{ color: T.text }}>Sectors</strong> to add a full sector. Then hit <strong style={{ color: T.text }}>Scan All</strong>.
              </div>
            </div>
          )}
          {watchlist.length > 0 && (
            <div style={{ flexShrink: 0, padding: "5px 20px", borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", background: T.surface }}>
              <span style={{ fontSize: F.xs, color: T.textDim }}>MACD: ⤴⤵ cross · ▲▼ direction · BB: tight bands · ▲▼ band · Click row = Summary · 📊 Full = all indicators · PHASE column = early-warning badge · S = Scan · 1-5 = Filters · Esc = Close panels · {sorted.length} ticker{sorted.length !== 1 ? "s" : ""} shown</span>
              <span style={{ marginLeft: "auto", fontSize: F.xs, color: T.textDim }}>Not financial advice · Data: Yahoo Finance</span>
            </div>
          )}
        </div>}

        {/* SLIDE-OUT INSIGHTS DRAWER — only in watchlist view */}
        {mainView === "watchlist" && <div style={{
          width: drawerOpen ? drawerWidth : 0,
          minWidth: 0,
          overflow: "hidden",
          transition: isDrawerDragging.current ? "none" : "width 0.25s ease",
          borderLeft: drawerOpen ? `1px solid ${T.border}` : "none",
          background: T.surface,
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          position: "relative",
        }}>
          {/* DRAG HANDLE — left edge */}
          {drawerOpen && (
            <div
              onMouseDown={e => {
                isDrawerDragging.current = true;
                dragStartX.current = e.clientX;
                dragStartWidth.current = drawerWidth;
                document.body.style.cursor = "ew-resize";
                document.body.style.userSelect = "none";
              }}
              style={{
                position: "absolute", left: 0, top: 0, bottom: 0, width: 5,
                cursor: "ew-resize", zIndex: 10, background: "transparent",
                transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = T.cyan + "66"}
              onMouseLeave={e => { if (!isDrawerDragging.current) e.currentTarget.style.background = "transparent"; }}
            >
              {/* Visible pill tab centered on edge */}
              <div style={{
                position: "absolute", left: -8, top: "50%", transform: "translateY(-50%)",
                width: 16, height: 48, background: T.surfaceHi,
                border: `1px solid ${T.border}`, borderRadius: 8,
                display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", gap: 3, pointerEvents: "none",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}>
                {[0,1,2].map(i => (
                  <div key={i} style={{ width: 2, height: 2, borderRadius: "50%", background: T.textDim }} />
                ))}
              </div>
            </div>
          )}
          {drawerOpen && (
            <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 16px 8px 20px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
                <span style={{ fontSize: F.sm, fontWeight: 700, color: T.textSub, letterSpacing: "0.06em" }}>
                  {{ news: "📰 NEWS FEED", scanner: "🔭 SCANNER", performance: "📊 PERFORMANCE", backtest: "📈 BACKTESTER", journal: "📓 JOURNAL", playbook: "📚 PLAYBOOK" }[drawerTab] || "INSIGHTS"}
                </span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                  <span style={{ fontSize: 10, color: T.textDim }}>{drawerWidth}px</span>
                  <button onClick={() => setDrawerOpen(false)} style={{ background: "none", border: "none", color: T.textDim, cursor: "pointer", fontSize: F.md, padding: "2px 6px" }}>✕</button>
                </div>
              </div>
              <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                <BottomPanel pendingTrade={pendingTrade} onClearPending={() => setPendingTrade(null)} onAddToWatchlist={sym => addTickers([sym])} watchlistSymbols={watchlist.map(i => i.symbol)} activeTab={drawerTab} globalNewsFeed={globalNewsFeed} newsFilter={newsFilter} setNewsFilter={setNewsFilter} anthropicKey={anthropicKey} />
              </div>
            </div>
          )}
        </div>}

      </div>

      {/* Top Movers modal */}
      {showMovers && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: 28, width: "90%", maxWidth: 900, maxHeight: "80vh", overflow: "auto", animation: "fadeIn 0.2s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.6)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <div>
                <div style={{ fontSize: F.xs, fontWeight: 700, color: T.textDim, letterSpacing: "0.1em", marginBottom: 4 }}>MARKET DISCOVERY</div>
                <h2 style={{ fontSize: F.xl, fontWeight: 700, color: T.text }}>🔥 Today's Top Movers</h2>
                <div style={{ fontSize: F.sm, color: T.textDim, marginTop: 4 }}>Click any ticker to add. Uses 1 of your 25 daily API calls.</div>
              </div>
              <button onClick={() => setShowMovers(false)} style={{ background: "none", border: "none", color: T.textSub, fontSize: F.xl, cursor: "pointer" }}>✕</button>
            </div>
            {moversLoading && <div style={{ textAlign: "center", padding: "40px 0", color: T.textDim }}>⏳ Fetching today's movers...</div>}
            {movers && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }}>
                {[{ title: "📈 Top Gainers", data: movers.gainers, color: T.green }, { title: "📉 Top Losers", data: movers.losers, color: T.red }, { title: "⚡ Most Active", data: movers.active, color: T.cyan }].map(({ title, data, color }) => (
                  <div key={title}>
                    <div style={{ fontSize: F.base, fontWeight: 700, color, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>{title}</div>
                    {data.map(item => {
                      const sym = item.ticker, pct = parseFloat(item.change_percentage) || 0, price = parseFloat(item.price) || 0;
                      const already = watchlist.find(i => i.symbol === sym);
                      return (
                        <div key={sym} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", marginBottom: 6, background: T.bg, borderRadius: 8, border: `1px solid ${already ? color + "44" : T.border}`, cursor: already ? "default" : "pointer" }}
                          onClick={() => { if (!already) { addTickers([sym]); toast(`${sym} added`, color); } }}
                          onMouseEnter={e => { if (!already) e.currentTarget.style.borderColor = color + "88"; }}
                          onMouseLeave={e => { if (!already) e.currentTarget.style.borderColor = already ? color + "44" : T.border; }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: F.md, fontWeight: 700, color: T.text }}>{sym}</span>
                            {already && <span style={{ fontSize: F.xs, color, background: `${color}22`, padding: "2px 8px", borderRadius: 4 }}>✓</span>}
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: F.base, fontWeight: 600, color: T.text }}>${price.toFixed(2)}</div>
                            <div style={{ fontSize: F.sm, fontWeight: 700, color: pct >= 0 ? T.green : T.red }}>{pct >= 0 ? "+" : ""}{pct.toFixed(2)}%</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
            {movers && (
              <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button onClick={() => { addTickers(movers.gainers.map(i => i.ticker)); addTickers(movers.active.map(i => i.ticker)); toast("Gainers + active added", T.green); setShowMovers(false); }} style={{ padding: "10px 18px", background: `${T.green}18`, border: `1px solid ${T.green}44`, color: T.green, fontSize: F.base, fontWeight: 600, borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>+ Add Gainers & Active</button>
                <button onClick={() => { addTickers([...movers.gainers, ...movers.losers, ...movers.active].map(i => i.ticker)); toast("All 30 movers added", T.orange); setShowMovers(false); }} style={{ padding: "10px 18px", background: `${T.orange}18`, border: `1px solid ${T.orange}44`, color: T.orange, fontSize: F.base, fontWeight: 600, borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>+ Add All 30</button>
                <button onClick={() => setShowMovers(false)} style={{ padding: "10px 18px", background: T.surfaceHi, border: `1px solid ${T.border}`, color: T.textSub, fontSize: F.base, fontWeight: 600, borderRadius: 8, cursor: "pointer", fontFamily: "inherit" }}>Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Persistent footer disclaimer */}
      <div style={{ flexShrink: 0, padding: "4px 16px", background: T.bg, borderTop: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 10, color: T.textDim, opacity: 0.6 }}>
          For informational purposes only. Not financial advice. Past performance does not guarantee future results.
        </div>
        <button onClick={() => setShowDisclaimer(true)} style={{ fontSize: 10, color: T.textDim, opacity: 0.6, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", flexShrink: 0, marginLeft: 12 }}>
          View full disclaimer
        </button>
      </div>

    {/* WATCHLIST DETAIL DRAWER — rendered outside overflow:hidden so position:fixed works correctly */}
    {watchlistDrawer && (
      <WatchlistDetailDrawer
        item={watchlistDrawer}
        onClose={() => setWatchlistDrawer(null)}
        anthropicKey={anthropicKey}
        openaiKey={openaiKey}
        geminiKey={geminiKey}
        aiProvider={aiProvider}
        isDark={isDark}
        proMode={proMode}
        hintsOn={hintsOn}
        liveQuote={livePrice[watchlistDrawer.symbol] || null}
        portfolioSize={portfolioSize}
        onSavePortfolioSize={savePortfolioSize}
        news={watchlist.find(i => i.symbol === watchlistDrawer.symbol)?.news ?? null}
        onScan={scanOne}
        scanning={scanning}
        onTogglePosition={toggleInPosition}
      />
    )}

    {/* REVERSAL WATCH DRAWER — outside overflow:hidden so position:fixed works */}
    {mainView === "watchlist" && showReversalWatch && (
      <ReversalWatchPanel
        watchlist={watchlist}
        vix={vix}
        fearGreed={fearGreed}
        onClose={() => setShowReversalWatch(false)}
      />
    )}
    </div>
    )}
    </>
  );
}

